export type Maybe<T> = T | null;
export type InputMaybe<T> = Maybe<T>;
/** All built-in and custom scalars, mapped to their actual values */
export type Scalars = {
  ID: { input: string; output: string; }
  String: { input: string; output: string; }
  Boolean: { input: boolean; output: boolean; }
  Int: { input: number; output: number; }
  Float: { input: number; output: number; }
  AWSDate: { input: string; output: string; }
  AWSDateTime: { input: string; output: string; }
  AWSEmail: { input: string; output: string; }
  AWSIPAddress: { input: string; output: string; }
  AWSJSON: { input: Record<string, unknown>; output: Record<string, unknown>; }
  AWSPhone: { input: string; output: string; }
  AWSTime: { input: string; output: string; }
  AWSTimestamp: { input: unknown; output: unknown; }
  AWSURL: { input: string; output: string; }
};

/**
 * API key (per service_account or user); hashed; scoped; rotatable.
 * Per docs/clearpath-architecture/03-data-model.md tier 6 (`api_keys`).
 */
export type ApiKey = {
  __typename?: 'APIKey';
  createdAt: Scalars['AWSDateTime']['output'];
  createdByUserId?: Maybe<Scalars['ID']['output']>;
  expiresAt?: Maybe<Scalars['AWSDateTime']['output']>;
  id: Scalars['ID']['output'];
  lastUsedAt?: Maybe<Scalars['AWSDateTime']['output']>;
  organizationId: Scalars['ID']['output'];
  /** Key prefix (first 8 chars; safe to display). */
  prefix: Scalars['String']['output'];
  revokedAt?: Maybe<Scalars['AWSDateTime']['output']>;
  /** Scopes this key may exercise. */
  scopes: Array<Scalars['String']['output']>;
  serviceAccount?: Maybe<ServiceAccount>;
  /** Owning service account (mutually exclusive with userId). */
  serviceAccountId?: Maybe<Scalars['ID']['output']>;
  tenantId: Scalars['ID']['output'];
  user?: Maybe<User>;
  /** Owning user (mutually exclusive with serviceAccountId). */
  userId?: Maybe<Scalars['ID']['output']>;
};

/**
 * P1.7 (2026-04-25) — account-deletion lifecycle state for the calling
 * customer. NONE means no request is in flight; REQUESTED means the cooldown
 * window is open and the user can still cancel; SOFT_DELETED + HARD_DELETED
 * are reached by the scheduled worker after cooldown / retention.
 */
export enum AccountDeletionState {
  HardDeleted = 'HARD_DELETED',
  None = 'NONE',
  Requested = 'REQUESTED',
  SoftDeleted = 'SOFT_DELETED'
}

export type AccountDeletionStatus = {
  __typename?: 'AccountDeletionStatus';
  /**
   * When the cooldown grace window expires. NULL when state = NONE. Past
   * this point a scheduled worker will soft-delete the account.
   */
  cooldownEndsAt?: Maybe<Scalars['AWSDateTime']['output']>;
  /** When the user submitted the deletion request. NULL when state = NONE. */
  requestedAt?: Maybe<Scalars['AWSDateTime']['output']>;
  /**
   * When soft-delete is scheduled to run (== cooldownEndsAt while
   * REQUESTED, NULL otherwise). Surfaced as a distinct field so the UI can
   * render the user-facing 'scheduled for' string consistently.
   */
  scheduledFor?: Maybe<Scalars['AWSDateTime']['output']>;
  state: AccountDeletionState;
};

/**
 * How an ACH authorization on file covers future debits from the same
 * payment method. ONE MEMBER TODAY.
 *
 * Every row the current writer inserts
 * (`services/pricing/src/payments/ach_authorizations.ts:121`) is
 * `authorization_type = 'single'` — the database's own definition of `single` is
 * "one debit, this authorization covers that debit only"
 * (`db/migrations/0310_tier7_ach_authorizations.sql:104-106`), which is exactly
 * the per-debit claim `PaymentAuthorizationRecord.authorizationType` must never
 * make. A `single` row therefore resolves that field to `null`, not to a
 * `SINGLE` member — there is no such member to emit, and emitting one would be a
 * schema type error rather than a review question. `RECURRING` is the only
 * value this enum can ever carry, for the day a standing authorization is
 * captured; nothing writes one today.
 */
export enum AchAuthorizationType {
  Recurring = 'RECURRING'
}

/** One ACH debit the receiving bank sent back. ENG-1988 (ACH Phase 5). */
export type AchReturn = {
  __typename?: 'AchReturn';
  /**
   * ALWAYS POSITIVE. It is a reversal, and the settled balance nets it by
   * SUBTRACTING it — the same convention `InvoicePayment.amount` uses.
   */
  amount: Scalars['Float']['output'];
  billingPartyId?: Maybe<Scalars['ID']['output']>;
  billingPartyName?: Maybe<Scalars['String']['output']>;
  currency: Scalars['String']['output'];
  disposition: AchReturnDisposition;
  /** The REVERSAL ledger row's id, not the original charge's. */
  id: Scalars['ID']['output'];
  invoiceId?: Maybe<Scalars['ID']['output']>;
  invoiceNumber?: Maybe<Scalars['String']['output']>;
  /**
   * Null when the return is not attached to an invoice (a statement-level or
   * manual charge).
   */
  invoiceStatus?: Maybe<InvoiceStatus>;
  /**
   * The payment method's CURRENT mandate state, not a fact recorded on the
   * return: true when the saved bank account can no longer be debited. A
   * REVOKES-class return sets it, and so does a payer revoking by hand.
   */
  mandateRevoked: Scalars['Boolean']['output'];
  /** When the return was recorded against the original charge. */
  occurredAt: Scalars['AWSDateTime']['output'];
  paymentMethodId?: Maybe<Scalars['ID']['output']>;
  /**
   * NACHA return-reason code (`R01`, `R02`, …), or `unknown` when the provider
   * reported a return with no readable code.
   */
  returnCode: Scalars['String']['output'];
};

/**
 * What a NACHA return code says about the payer's debit authorization.
 *
 * The classification lives in `@clearpath/shared-types/ach-return-disposition` and
 * is the SAME table that decides whether the write side revoked the payer's
 * mandate, so this label can never disagree with what the system actually did.
 */
export enum AchReturnDisposition {
  /**
   * A funds/timing return (R01 insufficient, R09 uncollected). The
   * authorization survives; the payer can pay again with the same account.
   */
  Retriable = 'RETRIABLE',
  /**
   * The account is closed or invalid, or the payer/their bank says the debit
   * was not authorized. The mandate and the payment method are revoked and nothing
   * may debit that account again.
   */
  Revokes = 'REVOKES',
  /**
   * A code the classifier does not cover — which is MOST of them: 13 of roughly
   * 80 NACHA codes are classified. Nothing automatic was done (no re-present, no
   * revoke) and a human decides. These sort to the top of
   * `ArDashboard.recentAchReturns`.
   */
  Unknown = 'UNKNOWN'
}

export type ActivateJitGrantInput = {
  /**
   * The fully-approved grant to activate. The caller must be the grant's
   * requester (enforced server-side by mintJitSessionToken).
   */
  id: Scalars['ID']['input'];
  /**
   * Fresh WebAuthn step-up reference (mfa:stepup:<ref>). Server-verified for
   * recency (<5 min) and caller-binding before the token is minted.
   */
  stepUpRef: Scalars['String']['input'];
};

/** Actor type for audit entries. */
export enum ActorType {
  Federation = 'FEDERATION',
  PortalCustomer = 'PORTAL_CUSTOMER',
  ServiceAccount = 'SERVICE_ACCOUNT',
  ShareLink = 'SHARE_LINK',
  System = 'SYSTEM',
  User = 'USER'
}

/**
 * Input for addFileLocation (MOL-583) — register an additional physical
 * location for an existing file (NAS sync path, federated share, etc.).
 */
export type AddFileLocationInput = {
  customFields?: InputMaybe<Scalars['AWSJSON']['input']>;
  fileId: Scalars['ID']['input'];
  isPrimary?: InputMaybe<Scalars['Boolean']['input']>;
  locationType: FileLocationType;
  locationUri: Scalars['String']['input'];
};

/** Payload for addFileLocation. */
export type AddFileLocationPayload = {
  __typename?: 'AddFileLocationPayload';
  location: FileLocation;
};

/**
 * Input for addPaymentMethod. The `providerMethodToken` is produced
 * client-side by the provider's secure element (Stripe Elements / equivalent)
 * — raw PAN NEVER crosses this mutation. For ACH/SEPA `mandate` is required;
 * the server persists the mandate row for NACHA / SEPA compliance. MOL-622.
 */
export type AddPaymentMethodInput = {
  /**
   * The billing address the payer CONFIRMED on the form. REQUIRED for ACH and
   * refused if incomplete.
   *
   * BASYS would accept an accountholder name with no street; the completeness
   * requirement is ours, because a name alone is not NACHA authorization evidence
   * and is not a dispute defence. The address is stored on the method AND
   * snapshotted into the immutable `ach_authorizations` row beside the consent
   * text, the server-stamped IP and the timestamp — a snapshot, not a link, so
   * editing a party address later cannot retroactively change what was
   * authorized.
   */
  billingAddress?: InputMaybe<BillingAddressInput>;
  /**
   * Optional customer nickname for this method (stored locally; not sent to
   * the provider).
   */
  label?: InputMaybe<Scalars['String']['input']>;
  /**
   * When true the new method becomes the party's default; any existing
   * default is un-flagged in the same transaction.
   */
  makeDefault?: InputMaybe<Scalars['Boolean']['input']>;
  /** Required for ACH / SEPA — the signed mandate text + payer IP. */
  mandate?: InputMaybe<MandateConsentInput>;
  /**
   * Optional masked PAN from the client tokenizer (e.g. Basys), used as a
   * last4 fallback when the vault attach response omits one. Never a full PAN.
   */
  maskedNumber?: InputMaybe<Scalars['String']['input']>;
  /**
   * Party the method is attached to. For portal viewers this defaults to
   * the viewer's own party; admins can attach on behalf of another party.
   */
  partyId: Scalars['ID']['input'];
  /** Opaque token from the provider's client SDK. */
  providerMethodToken: Scalars['String']['input'];
  /** Method kind (CARD, ACH, SEPA, WIRE). */
  type: PaymentMethodType;
};

export type AddPermissionToRoleInput = {
  permissionKey: Scalars['String']['input'];
  reason?: InputMaybe<Scalars['String']['input']>;
  roleId: Scalars['ID']['input'];
  tenantId: Scalars['ID']['input'];
};

/**
 * Purposes an address can serve. An address with no tags serves every
 * purpose; tags restrict it to the listed ones. Tax resolves through SHIPPING
 * (the ship-to party).
 */
export enum AddressPurpose {
  Billing = 'BILLING',
  Shipping = 'SHIPPING'
}

/** Line-item input for requestCaseAdjustment (ENG-1424). */
export type AdjustmentProductLineInput = {
  /** Free-form notes about what needs adjusting on this specific line. */
  notes?: InputMaybe<Scalars['String']['input']>;
  /** productType id from the source case. Quantity copies from the source line if omitted. */
  productTypeId: Scalars['ID']['input'];
  /** Override quantity. May not exceed the source case's aggregated quantity for this SKU. */
  quantity?: InputMaybe<Scalars['Int']['input']>;
};

/**
 * ENG-231 — which population adminResetUserMfa's target belongs to. Each
 * value lives in a distinct Cognito pool:
 *   CUSTOMER       — the shared molaris-customers pool (portal practitioners,
 *                    customer-org staff/admins, parent-org admins).
 *   TENANT_STAFF   — the target tenant's own per-tenant staff pool. Requires
 *                    AdminResetUserMfaInput.tenantId.
 *   PLATFORM_STAFF — the shared molaris-platform-staff pool (Molaris's own
 *                    employees — the same population that signs in to
 *                    admin.molaris.ai / account.molaris.ai).
 */
export enum AdminMfaResetUserClass {
  Customer = 'CUSTOMER',
  PlatformStaff = 'PLATFORM_STAFF',
  TenantStaff = 'TENANT_STAFF'
}

/**
 * One row from the control-plane permissions table. Rendered in the admin
 * permission registry browser (grouped by productCode + resourceType).
 */
export type AdminPermissionRegistryEntry = {
  __typename?: 'AdminPermissionRegistryEntry';
  action: Scalars['String']['output'];
  createdAt: Scalars['AWSDateTime']['output'];
  deprecatedAt?: Maybe<Scalars['AWSDateTime']['output']>;
  description?: Maybe<Scalars['String']['output']>;
  mfaMaxAgeMinutes?: Maybe<Scalars['Int']['output']>;
  permissionKey: Scalars['String']['output'];
  /** Denormalized product code (e.g. 'clearpath') for client-side grouping. */
  productCode: Scalars['String']['output'];
  productId: Scalars['ID']['output'];
  replacedBy?: Maybe<Scalars['String']['output']>;
  /** Gating feature flag key. Null when the permission is unconditional. */
  requiresFeatureFlagKey?: Maybe<Scalars['String']['output']>;
  requiresStepUpMfa: Scalars['Boolean']['output'];
  resourceType: Scalars['String']['output'];
  updatedAt: Scalars['AWSDateTime']['output'];
};

/** Input for ENG-231 adminResetUserMfa. */
export type AdminResetUserMfaInput = {
  /**
   * Human-readable justification, audit-required (e.g. a support-desk
   * ticket reference).
   */
  reason: Scalars['String']['input'];
  /**
   * Required when userClass = TENANT_STAFF (which per-tenant staff pool to
   * resolve); ignored for every other userClass.
   */
  tenantId?: InputMaybe<Scalars['ID']['input']>;
  userClass: AdminMfaResetUserClass;
  /**
   * Meaning depends on userClass:
   * - CUSTOMER: customer_global_identities.id (mirrors ForceUserReMfaInput's
   *   targeting convention).
   * - TENANT_STAFF / PLATFORM_STAFF: cognito_sub.
   */
  userId: Scalars['ID']['input'];
};

/** Payload for ENG-231 adminResetUserMfa. */
export type AdminResetUserMfaPayload = {
  __typename?: 'AdminResetUserMfaPayload';
  ok: Scalars['Boolean']['output'];
  /**
   * Epoch-seconds watermark — every token issued before this instant is
   * now invalid for the target user.
   */
  tokensInvalidatedAfter: Scalars['AWSTimestamp']['output'];
  /** Echo of the input target class, so callers don't have to round-trip. */
  userClass: AdminMfaResetUserClass;
  /** Echo of the input target id, so callers don't have to round-trip. */
  userId: Scalars['ID']['output'];
};

/**
 * Effective permission set for a tenant role, surfaced in the role builder.
 * Combines directly-granted permission_keys with keys inherited from the
 * parentRoleId chain. `inheritedFrom` carries the path for UI transparency.
 */
export type AdminRoleDefinition = {
  __typename?: 'AdminRoleDefinition';
  createdAt: Scalars['AWSDateTime']['output'];
  description?: Maybe<Scalars['String']['output']>;
  /** The union of permissionKeys + inheritedPermissionKeys — what Cedar sees. */
  effectivePermissionKeys: Array<Scalars['String']['output']>;
  id: Scalars['ID']['output'];
  /** Inherited permission keys sourced from parent role(s). */
  inheritedPermissionKeys: Array<Scalars['String']['output']>;
  isPortalRole: Scalars['Boolean']['output'];
  isSystem: Scalars['Boolean']['output'];
  key: Scalars['String']['output'];
  name: Scalars['String']['output'];
  organizationId?: Maybe<Scalars['ID']['output']>;
  parent?: Maybe<AdminRoleDefinition>;
  parentRoleId?: Maybe<Scalars['ID']['output']>;
  /** Directly-granted permission keys (role_permissions rows). */
  permissionKeys: Array<Scalars['String']['output']>;
  tenantId: Scalars['ID']['output'];
  updatedAt?: Maybe<Scalars['AWSDateTime']['output']>;
};

export type AdvanceNasRolloutInput = {
  canaryPercent?: InputMaybe<Scalars['Int']['input']>;
  cohortPercent?: InputMaybe<Scalars['Int']['input']>;
  id: Scalars['ID']['input'];
  phase?: InputMaybe<NasRolloutPhase>;
};

/**
 * A batched tile's error (ENG-1779), set when `ok` is false. `code` is stable:
 * 'ValidationError' (the tile's query was malformed / referenced an unknown measure
 * / was otherwise invalid) or 'Forbidden' (every requested measure was dropped by
 * server-side gating — e.g. an all-financial tile without clearpath.finance.read).
 * `excludedMeasures` names the gated-out measures for a Forbidden tile. `reason`
 * (ENG-2202) carries the structured refusal code when one applies —
 * `RESULT_TOO_LARGE` (the tile's result exceeded the server's 50,000-row bound and
 * must be narrowed), a k-anon / window / comparison reject code, or
 * `measure_capability_denied` — so a tile renders the same recoverable copy a
 * standalone analyticsQuery does; null otherwise.
 */
export type AnalyticsBatchQueryError = {
  __typename?: 'AnalyticsBatchQueryError';
  code: Scalars['String']['output'];
  excludedMeasures: Array<Scalars['String']['output']>;
  message: Scalars['String']['output'];
  reason?: Maybe<Scalars['String']['output']>;
};

/**
 * One tile in a Query.analyticsQueryBatch request (ENG-1779). `key` is the
 * client's routing key (unique within the batch) so results map back to tiles;
 * `label` is an optional display passthrough. `query` is the serialized
 * SemanticQuery for this tile — the SAME contract as Query.analyticsQuery's
 * `query`. A malformed `query` is reported as THIS tile's error, never a
 * whole-batch failure.
 */
export type AnalyticsBatchQueryItem = {
  key: Scalars['String']['input'];
  label?: InputMaybe<Scalars['String']['input']>;
  query: Scalars['AWSJSON']['input'];
};

/**
 * The result of Query.analyticsQueryBatch (ENG-1779): one item per requested
 * tile, in request order (and each carries its `key` for order-independent
 * map-back).
 */
export type AnalyticsBatchQueryPayload = {
  __typename?: 'AnalyticsBatchQueryPayload';
  items: Array<AnalyticsBatchQueryResultItem>;
};

/**
 * One tile's outcome in a Query.analyticsQueryBatch response (ENG-1779). `key`
 * echoes the request tile's routing key. Exactly one of `result` / `error` is
 * non-null, per `ok`: a successful tile carries its AnalyticsQueryResult (which may
 * still list excludedMeasures for a partial financial drop); a failed tile carries a
 * structured error and does NOT sink the rest of the batch.
 */
export type AnalyticsBatchQueryResultItem = {
  __typename?: 'AnalyticsBatchQueryResultItem';
  error?: Maybe<AnalyticsBatchQueryError>;
  key: Scalars['String']['output'];
  label?: Maybe<Scalars['String']['output']>;
  ok: Scalars['Boolean']['output'];
  result?: Maybe<AnalyticsQueryResult>;
};

/**
 * The served analytics field catalog for the authenticated tenant, split by
 * builder axis. `dimensions` includes the tenant's selectable custom-field
 * projections (entryKind=PROJECTION); `measures` includes the org's servable
 * CALCULATED (derived) measures (entryKind=CALCULATED).
 */
export type AnalyticsCatalog = {
  __typename?: 'AnalyticsCatalog';
  dimensions: Array<AnalyticsCatalogEntry>;
  measures: Array<AnalyticsCatalogEntry>;
};

/**
 * One selectable field in the served analytics catalog. Flat so the MFE reads
 * `entryKind` to place it on the measure vs dimension axis and `financial` to gate
 * money fields (hidden unless the caller holds clearpath.finance.read — the server
 * enforces the same at query time via measure gating).
 */
export type AnalyticsCatalogEntry = {
  __typename?: 'AnalyticsCatalogEntry';
  entryKind: AnalyticsCatalogEntryKind;
  financial: Scalars['Boolean']['output'];
  /**
   * Grouping/source hint: a measure's base relation, or a projection's
   * `base_relation.jsonb_column`. Null for a base dimension.
   */
  group?: Maybe<Scalars['String']['output']>;
  key: Scalars['String']['output'];
  /**
   * For a measure: the MeasureKind (COUNT / MONEY / …). For a dimension: the
   * DimensionKind (time / attribute). For a projection: the scalar value_type.
   */
  kind: Scalars['String']['output'];
  label: Scalars['String']['output'];
  phiClass: AnalyticsFieldPhiClass;
};

/**
 * Which builder axis a catalog entry renders on. PROJECTION is a tenant-opted-in
 * scalar custom field; it rides the dimension axis but keeps its own kind so the
 * client can badge it distinctly from a base dimension. CALCULATED (ENG-1803) is a
 * user-authored derived measure; it rides the measure axis, badged so the client
 * shows it as a calculated field.
 */
export enum AnalyticsCatalogEntryKind {
  Calculated = 'CALCULATED',
  Dimension = 'DIMENSION',
  Measure = 'MEASURE',
  Projection = 'PROJECTION'
}

/**
 * One column in a Query.analyticsQuery result, with its stable output name and
 * role. Names mirror the compiler's aliases (@clearpath/analytics-semantics).
 * `label` is a friendly DISPLAY header, present ONLY for a tenant custom-field
 * projection group-by (whose alias is the opaque `d_p<index>`): it carries the
 * projection's human label so the UI/CSV render e.g. "Region Code" instead of the
 * raw alias. Absent (null) for base measures/dimensions/time — the client falls back
 * to its registry label. `name` (the row key + SQL alias) is unchanged.
 */
export type AnalyticsColumn = {
  __typename?: 'AnalyticsColumn';
  label?: Maybe<Scalars['String']['output']>;
  name: Scalars['String']['output'];
  role: AnalyticsColumnRole;
};

/**
 * The role a Query.analyticsQuery result column plays, so the builder UI can
 * render axes without re-parsing the compiled SQL (ENG-1647 / T6).
 */
export enum AnalyticsColumnRole {
  /** A grouped non-time dimension (column `d_<dimensionKey>`). */
  Dimension = 'DIMENSION',
  /** A computed measure value (column named for the measure key). */
  Measure = 'MEASURE',
  /** The time bucket produced by the query's grain (column `bucket`). */
  Time = 'TIME'
}

/**
 * A user-authored analytics dashboard (name + opaque layout/definition JSON).
 * Owned by its creator (`ownerUserId`); org-isolated, with cross-org read access
 * granted only via shares (a separate surface). Per ENG-1654 / T13.
 */
export type AnalyticsDashboard = {
  __typename?: 'AnalyticsDashboard';
  createdAt: Scalars['AWSDateTime']['output'];
  customFields?: Maybe<Scalars['AWSJSON']['output']>;
  /**
   * Opaque dashboard definition (grid layout, filters, refresh cadence). Shape
   * owned by the analytics client, not the DB. Returned as a raw JSON object.
   */
  definition: Scalars['AWSJSON']['output'];
  id: Scalars['ID']['output'];
  name: Scalars['String']['output'];
  organizationId: Scalars['ID']['output'];
  /**
   * Creator of the dashboard — the owner whose "my dashboards" list it appears
   * on. The owner may always edit; DELETING the whole dashboard and MANAGING its
   * shares stay owner-only (D1/D2). Content edits (rename, definition/layout,
   * widgets) are additionally permitted to same-org editor-sharees (T6/ENG-2117).
   */
  ownerUserId: Scalars['ID']['output'];
  /**
   * A short, capability-gated preview of the dashboard's tiles for the grid card:
   * each tile's viz glyph hint plus its measure LABEL — never a value. Populated ONLY
   * by analyticsDashboards (the caller's own list); null elsewhere, where the client
   * falls back to a generic glyph motif. A label is present ONLY when the caller holds
   * the tile's measure capability (finance/PHI/etc.); an unentitled or unknown measure
   * yields a glyph-only tile, and the tile COUNT is preserved either way so a short
   * preview never reveals a hidden tile. Capped to the card's shape (4 board / 3
   * report). [SEC]
   */
  previewTiles?: Maybe<Array<AnalyticsPreviewTile>>;
  /**
   * The AUTHENTICATED caller's own share permission on this dashboard, populated
   * ONLY by analyticsDashboardsSharedWithMe ('view' | 'edit'). Null on owned lists
   * (analyticsDashboards) — the owner is not a sharee. UX affordance-gating only;
   * the server is authoritative on every write (T6/ENG-2117).
   */
  sharedPermission?: Maybe<Scalars['String']['output']>;
  updatedAt?: Maybe<Scalars['AWSDateTime']['output']>;
};

/**
 * Result of deleting a dashboard. `deletedWidgetCount` reports how many widgets
 * CASCADE-deleted with it (db/migrations/0331 FK ON DELETE CASCADE).
 */
export type AnalyticsDashboardDeletedPayload = {
  __typename?: 'AnalyticsDashboardDeletedPayload';
  deletedId: Scalars['ID']['output'];
  deletedWidgetCount: Scalars['Int']['output'];
};

export type AnalyticsDashboardPayload = {
  __typename?: 'AnalyticsDashboardPayload';
  dashboard: AnalyticsDashboard;
};

/**
 * A grant of access to an analytics dashboard for a named principal. The sharee
 * may be a user in another organization within the tenant; visibility is enforced
 * by RLS, not this row alone. Per ENG-1656 / T15.
 */
export type AnalyticsDashboardShare = {
  __typename?: 'AnalyticsDashboardShare';
  createdAt: Scalars['AWSDateTime']['output'];
  customFields?: Maybe<Scalars['AWSJSON']['output']>;
  dashboardId: Scalars['ID']['output'];
  id: Scalars['ID']['output'];
  /**
   * The OWNING (dashboard's) organization. The sharee reaches the dashboard
   * through the RLS share-visibility lens, not through this column.
   */
  organizationId: Scalars['ID']['output'];
  /** What the grant confers: `view` or `edit`. */
  permission: Scalars['String']['output'];
  /** The principal the dashboard is shared with. */
  sharedWithUserId: Scalars['ID']['output'];
  updatedAt?: Maybe<Scalars['AWSDateTime']['output']>;
};

export type AnalyticsDashboardSharePayload = {
  __typename?: 'AnalyticsDashboardSharePayload';
  share: AnalyticsDashboardShare;
};

/** Result of revoking a share. */
export type AnalyticsDashboardUnsharedPayload = {
  __typename?: 'AnalyticsDashboardUnsharedPayload';
  dashboardId: Scalars['ID']['output'];
  sharedWithUserId: Scalars['ID']['output'];
};

/**
 * The result of Query.analyticsDrillDetail (ENG-2022 M1 B1 + B2 / composite / keyset
 * pagination) `[SEC]`: the caller's OWN order rows behind ONE released aggregate cell.
 * NO-REMAINDER by construction — it carries NO total-cell-count, NO "showing X of N", NO
 * denominator. `drilledCount` is the count of the caller's OWN rows on THIS page; it is
 * `<= cellCount` (the org-wide cell population), which is an EXPECTED response, never an
 * error. `nextCursor` is an OPAQUE keyset cursor that pages ONLY the caller's OWN authorized
 * rows (never a total, never "N more" beyond the lens); it is null when this page ends the
 * caller's own sequence (a page shorter than the requested size also ends it).
 */
export type AnalyticsDrillDetailResult = {
  __typename?: 'AnalyticsDrillDetailResult';
  /** Count of the caller's OWN rows returned on THIS page. `<= cellCount`; NOT a total/remainder. */
  drilledCount: Scalars['Int']['output'];
  /**
   * Opaque keyset cursor for the NEXT page of the caller's OWN authorized rows (pass back
   * as `page.after`). Null when the caller's own sequence has ended. It never implies rows
   * beyond the caller's lens and never encodes a total or remainder (AC6).
   */
  nextCursor?: Maybe<Scalars['String']['output']>;
  /** The caller's own order rows composing the released cell (list-row projection). */
  rows: Array<AnalyticsDrillOrderRow>;
  /**
   * Present ONLY for a UNIT-granular drill (a `product_type` / `product_family` cell,
   * ENG-2022 O4): the returned rows are ORDERS holding >=1 matching unit, so `drilledCount`
   * is an ORDER count and does NOT equal the cell's UNIT-count measure. Null for an
   * order-grain cell, where the count needs no such caveat.
   */
  unitGrainLabel?: Maybe<Scalars['String']['output']>;
};

/**
 * One drilled order row behind an analytics aggregate cell (ENG-2022 M1 / B1) `[SEC]`.
 * The LIST-ROW projection allowlist: operational, non-free-text scalars the caller can
 * already see for their OWN order via Query.orders. NO intake/subject free text, NO
 * totals. Every row is resolved under the CALLER's own session, so RLS re-applies.
 */
export type AnalyticsDrillOrderRow = {
  __typename?: 'AnalyticsDrillOrderRow';
  barcodeValue?: Maybe<Scalars['String']['output']>;
  createdAt?: Maybe<Scalars['AWSDateTime']['output']>;
  customerPartyId?: Maybe<Scalars['ID']['output']>;
  id: Scalars['ID']['output'];
  lifecycleStatus?: Maybe<Scalars['String']['output']>;
  orderType?: Maybe<Scalars['String']['output']>;
  status?: Maybe<Scalars['String']['output']>;
};

/**
 * The BEHAVIORAL (event) funnel (ENG-2032 / E1-5) `[SEC]`: the requested ordered
 * event steps with per-step reach, time-to-convert and conversion/drop vs the prior
 * step, aggregated ORG-WIDE within the caller's tenant over the append-only `events`
 * table. k-anon disclosure control (event-kanon) is applied at the executor tail.
 */
export type AnalyticsEventFunnelResult = {
  __typename?: 'AnalyticsEventFunnelResult';
  /** The breakdown axis (customer_org / region_code), or null for the flat funnel. */
  breakdownBy?: Maybe<Scalars['String']['output']>;
  /** The funnel steps (ascending ordinal; one per step x breakdown value pair). */
  steps: Array<AnalyticsEventFunnelStep>;
  /** The pinned subject_type this funnel counts (never `user`). */
  subjectType: Scalars['String']['output'];
};

/**
 * One step in a BEHAVIORAL (event) funnel (ENG-2032 / E1-5) `[SEC]`. Emitted per
 * requested step in ascending ordinal (0-based), or per (step x breakdown value) pair
 * when the funnel requests a breakdown. `reachedSubjectCount` / `reachedOrderCount`
 * are nullable BY DESIGN: a below-k stage cell is k-anon-SUPPRESSED (counts nulled,
 * `suppressed: true`), DISTINCT from an honest 0. The population floored on is the
 * ORDER-GRAIN `reachedOrderCount` (correct even when the pinned subject is a step_run,
 * which is not itself a k-anon anchor). `avgSecondsFromPrior` is coarsened to a
 * privacy bucket (a precise dwell time re-identifies even inside a >=k cell).
 */
export type AnalyticsEventFunnelStep = {
  __typename?: 'AnalyticsEventFunnelStep';
  /**
   * Average seconds to convert into this step from the prior, coarsened to a privacy
   * bucket. `null` for the first step, an empty cell, or a suppressed cell.
   */
  avgSecondsFromPrior?: Maybe<Scalars['Float']['output']>;
  /**
   * The breakdown value for this cell (customer party id / region code), or null
   * when the funnel requested no breakdown.
   */
  breakdownValue?: Maybe<Scalars['String']['output']>;
  /**
   * Conversion ratio vs the prior step. `null` for the first step and when the
   * prior step's count is null/0 or suppressed.
   */
  conversionFromPrior?: Maybe<Scalars['Float']['output']>;
  /**
   * Absolute subject drop vs the prior step. `null` for the first step or a
   * suppressed cell.
   */
  dropFromPrior?: Maybe<Scalars['Float']['output']>;
  /** The step's event_type predicate (echoed for display). */
  eventType: Scalars['String']['output'];
  /**
   * Distinct OWNING ORDERS that reached this step -- the k-anon population base.
   * `null` when the cell was k-anon-suppressed.
   */
  reachedOrderCount?: Maybe<Scalars['Int']['output']>;
  /**
   * Distinct pinned subjects that reached this step (monotonic reached-or-beyond).
   * `null` when the cell was k-anon-suppressed (below the privacy threshold).
   */
  reachedSubjectCount?: Maybe<Scalars['Int']['output']>;
  /** 0-based ordinal of the step in the requested spine. */
  stepOrdinal: Scalars['Int']['output'];
  /**
   * k-anon SMALL-CELL suppression marker `[SEC]`. `true` when this cell's
   * order-grain population fell below the privacy threshold and its counts/time were
   * nulled -- DISTINCT from an honest empty cell.
   */
  suppressed: Scalars['Boolean']['output'];
};

/**
 * The result of exporting a dashboard (ENG-1657 / T16). `csv` is all sections
 * concatenated into one print / CSV-ready document; `sections` are the clean
 * per-widget CSVs. `excludedMeasures` is the union of financial measures dropped
 * across all widgets because the caller lacks clearpath.finance.read.
 */
export type AnalyticsExportPayload = {
  __typename?: 'AnalyticsExportPayload';
  csv: Scalars['String']['output'];
  dashboardId: Scalars['ID']['output'];
  dashboardName: Scalars['String']['output'];
  excludedMeasures: Array<Scalars['String']['output']>;
  /** The export format — 'CSV' in v1. */
  format: Scalars['String']['output'];
  generatedAt: Scalars['AWSDateTime']['output'];
  /** Total data rows across every exported widget. */
  rowCount: Scalars['Int']['output'];
  sections: Array<AnalyticsExportSection>;
};

/**
 * One exported widget's data as a self-contained CSV section. A dashboard's
 * widgets each run a different query with different columns, so the export is a set
 * of per-widget sections rather than one flat table. `error` is set (and `csv`
 * empty) when the widget's saved query is invalid or was refused because the caller
 * lacks clearpath.finance.read for its financial measures.
 */
export type AnalyticsExportSection = {
  __typename?: 'AnalyticsExportSection';
  /**
   * This widget's data rendered as an RFC-4180 CSV (header row + one row per
   * result row). Empty when `error` is set.
   */
  csv: Scalars['String']['output'];
  error?: Maybe<Scalars['String']['output']>;
  /**
   * Financial (MONEY) measure keys dropped from this widget server-side because
   * the caller lacks clearpath.finance.read.
   */
  excludedMeasures: Array<Scalars['String']['output']>;
  position: Scalars['Int']['output'];
  rowCount: Scalars['Int']['output'];
  title?: Maybe<Scalars['String']['output']>;
  widgetId: Scalars['ID']['output'];
};

/**
 * PHI classification surfaced for a catalog entry. A served entry is only ever
 * SAFE today (phi/blocked never pass the fail-closed filter); carried faithfully so
 * the client can render the real classification once RA-P3-4 admits PHI.
 */
export enum AnalyticsFieldPhiClass {
  Blocked = 'BLOCKED',
  Phi = 'PHI',
  Safe = 'SAFE'
}

/**
 * The case-lifecycle funnel (ENG-1793): the requested stages with per-stage reach,
 * time-in-stage and conversion/drop vs the prior stage, aggregated ORG-WIDE within
 * the caller's tenant over the unit_stage_fact view. Flat by design — the v1 funnel
 * does not break down by a split dimension.
 */
export type AnalyticsFunnelResult = {
  __typename?: 'AnalyticsFunnelResult';
  stages: Array<AnalyticsFunnelStage>;
};

/**
 * One stage in the case-lifecycle funnel (ENG-1793). Emitted per requested stage
 * in canonical spine order (received -> design -> produce -> qc -> ship -> seat).
 * `reachedCount` is nullable BY DESIGN: an unconfigured middle-band stage returns
 * `null` — the fail-closed "not configured" signal, DISTINCT from a legitimate 0 —
 * so a client must read `configured` to tell "no binding" from "no units". Anchor
 * stages (received/ship/seat) are always configured; step-binding stages
 * (design/produce/qc) are configured only when the tenant bound a step to them.
 */
export type AnalyticsFunnelStage = {
  __typename?: 'AnalyticsFunnelStage';
  /**
   * Average time spent in this stage, in seconds. `null` for anchor stages (no
   * duration) and for unconfigured stages.
   */
  avgTimeInStageSeconds?: Maybe<Scalars['Float']['output']>;
  /**
   * Whether the stage is configured for the tenant. `false` → render
   * "not configured", NOT zero.
   */
  configured: Scalars['Boolean']['output'];
  /**
   * Conversion ratio vs the prior stage. `null` for the first stage and when the
   * prior stage's count is `null`/0.
   */
  conversionFromPrior?: Maybe<Scalars['Float']['output']>;
  /** Absolute unit drop vs the prior stage. `null` for the first stage. */
  dropFromPrior?: Maybe<Scalars['Float']['output']>;
  /** Human display label for the stage. */
  label: Scalars['String']['output'];
  /**
   * Units that reached this stage (monotonic reached-or-beyond). `null` for an
   * unconfigured step-binding stage (fail-closed — never a guessed 0).
   */
  reachedCount?: Maybe<Scalars['Int']['output']>;
  /** Canonical spine key: received|design|produce|qc|ship|seat. */
  stage: Scalars['String']['output'];
  /**
   * k-anon SMALL-CELL suppression marker (ENG-1962, [SEC]). `true` when this
   * cell's `count(distinct order_product_id)` population fell below the privacy
   * threshold and `reachedCount` / `avgTimeInStageSeconds` were nulled — DISTINCT
   * from an unconfigured stage (`configured:false`) and from an honest empty window.
   * `false`/`null` when nothing was suppressed. The v1 no-split, org-wide funnel is
   * never floored (a non-QI single-tenant aggregate), so it is `false` here; the
   * field exists so the tile can render a "hidden for privacy" state the moment the
   * splitBy (per-product) breakdown is enabled.
   */
  suppressed?: Maybe<Scalars['Boolean']['output']>;
};

/** Time-series bucketing granularity for throughput. */
export enum AnalyticsGranularity {
  Day = 'DAY',
  Week = 'WEEK'
}

/** The caller's own Overview KPI-board personalization. */
export type AnalyticsOverviewPreferences = {
  __typename?: 'AnalyticsOverviewPreferences';
  organizationId: Scalars['ID']['output'];
  /**
   * Opaque personalization blob { kpiOrder: [String], kpiHidden: [String],
   * kpiTargets: { <kpiKey>: Float } }. Shape owned by the analytics client
   * (apps/analytics/src/overview/overviewPreferences.ts). Returned as a raw JSON
   * object; unknown keys are tolerated so the catalog can evolve.
   */
  prefs: Scalars['AWSJSON']['output'];
  updatedAt?: Maybe<Scalars['AWSDateTime']['output']>;
  /** The user this personalization belongs to (always the authenticated caller). */
  userId: Scalars['ID']['output'];
};

export type AnalyticsOverviewPreferencesPayload = {
  __typename?: 'AnalyticsOverviewPreferencesPayload';
  preferences: AnalyticsOverviewPreferences;
};

/**
 * One capability-gated preview tile on an artifact grid card (viz glyph + measure
 * label). Metadata only — it carries the tile's visualization type and, when the
 * caller is entitled to the tile's measure, its human label. It NEVER carries a
 * computed value or result: the tile-value cache was rejected on capability-leak
 * grounds (a value keyed on org + capabilities + RLS + query could serve a finance
 * number to a base viewer on a single miss). Per ENG preview-labels.
 */
export type AnalyticsPreviewTile = {
  __typename?: 'AnalyticsPreviewTile';
  /**
   * The measure's display label — present ONLY when the caller holds the capability
   * to query that measure. Absent ⇒ the client renders the tile glyph-only. A label
   * naming a measure the viewer cannot query is a disclosure, so it is filtered by the
   * SAME gate as the measure's values.
   */
  label?: Maybe<Scalars['String']['output']>;
  /**
   * The visualization glyph hint (e.g. bar / line / number / table). Decorative;
   * the client maps an unrecognized value to a fallback glyph.
   */
  viz?: Maybe<Scalars['String']['output']>;
};

/**
 * One projection row on the analytics-admin governance review queue (ENG-1764 s5):
 * its row `id` + the governance METADATA a `clearpath.analytics.projection.manage`
 * holder reviews. Carries the stable catalog `key` (shared with the served-catalog
 * twin), the tenant-authored display `label` and stable field identity (`sourceKind` /
 * `sourceRef` = the form field code, `baseRelation` / `jsonbColumn` = where it lives),
 * and the governance flags. DELIBERATELY EXCLUDES json_key VALUES / the custom_fields
 * JSONB blob / any tenant answer — governance is decided over field DEFINITIONS, not
 * field CONTENTS.
 */
export type AnalyticsProjectionGovernanceRow = {
  __typename?: 'AnalyticsProjectionGovernanceRow';
  /** The base relation the field lives on (lower-cased: orders/order_products). */
  baseRelation: Scalars['String']['output'];
  /**
   * Exposure (lower-cased: exposed/pending_review/unclassified). Only `exposed`
   * is catalog-selectable.
   */
  exposure: Scalars['String']['output'];
  /** The projection row id — the handle the s3/s4 governance mutations key off. */
  id: Scalars['ID']['output'];
  /** The JSONB column the field lives in (lower-cased: custom_fields/intake_form_response). */
  jsonbColumn: Scalars['String']['output'];
  /**
   * Stable catalog key (`custom_field.<baseRelation>.<jsonbColumn>.<sourceRef>`),
   * byte-for-byte the key the served-catalog twin carries.
   */
  key: Scalars['String']['output'];
  /** Tenant-authored display label (falls back to the field code when unlabelled). */
  label: Scalars['String']['output'];
  /** PHI class (lower-cased: safe/phi/blocked). phi/blocked is never selectable. */
  phiClass: Scalars['String']['output'];
  /**
   * k-anon QI floor. true (default) ⇒ a group-by is small-cell suppressed
   * (Protected); false ⇒ explicitly opted down to non-QI (Released).
   */
  quasiIdentifier: Scalars['Boolean']['output'];
  /** Review status (lower-cased: unreviewed/classified/approved). */
  reviewStatus: Scalars['String']['output'];
  /** Field identity kind (lower-cased: form_field/free_form). */
  sourceKind: Scalars['String']['output'];
  /**
   * Stable field identity decoupled from form VERSION — the form field code
   * (form_field) or the custom_fields key (free_form). Not a tenant answer value.
   */
  sourceRef: Scalars['String']['output'];
  /** Scalar extraction type (lower-cased: int/bool/string/date/enum). */
  valueType: Scalars['String']['output'];
};

/**
 * A VALIDATED-BUT-UNPERSISTED analytics dashboard/report proposal produced by the
 * assistant's analytics_propose tool (A3a, ENG-2240). Each widget's query has been
 * validated under the CALLER's entitlements + the semantic layer + k-anon (the same
 * gated analyticsQuery pipeline), but NOTHING is written: the model is propose-only.
 * This type mirrors the EXISTING create-mutation inputs 1:1 so the client's accept
 * path is a straight mapping, never a new persistence contract:
 *   name       -> CreateAnalyticsDashboardInput.name
 *   definition -> CreateAnalyticsDashboardInput.definition
 *   widgets[].config    -> CreateAnalyticsWidgetInput.config (byte-for-byte)
 *   widgets[].position  -> CreateAnalyticsWidgetInput.position
 * (the parent dashboardId is intentionally absent — the client fills it from the
 * dashboard it creates on accept).
 */
export type AnalyticsProposal = {
  __typename?: 'AnalyticsProposal';
  /**
   * Maps to CreateAnalyticsDashboardInput.definition (opaque grid/layout object).
   * Null when the assistant proposed no dashboard-level definition.
   */
  definition?: Maybe<Scalars['AWSJSON']['output']>;
  /** Maps to CreateAnalyticsDashboardInput.name. */
  name: Scalars['String']['output'];
  /** One entry per proposed tile, in display order. */
  widgets: Array<AnalyticsProposalWidget>;
};

/**
 * Render-only summary of a proposed tile's validated query (a projection of the
 * tile's composition-v3 config). For the ProposalCard; persistence uses `config`.
 */
export type AnalyticsProposalQuery = {
  __typename?: 'AnalyticsProposalQuery';
  /** Dimension keys the tile groups by (may be empty). */
  dimensions: Array<Scalars['String']['output']>;
  /** How many row filters the tile carries. */
  filterCount: Scalars['Int']['output'];
  /** The tile's time grain (day/week/month/…/snapshot/fiscal_*). */
  grain: Scalars['String']['output'];
  /** Whether the tile carries a comparison period (vs_prior / yoy / qoq / mtd / qtd / ytd). */
  hasComparison: Scalars['Boolean']['output'];
  /** Whether the tile carries a windowed transform (rolling avg / running total / growth%). */
  hasWindow: Scalars['Boolean']['output'];
  /** Measure keys the tile plots. */
  measures: Array<Scalars['String']['output']>;
  /** The tile's visualization type (table/line/bar/area/number/kpi). */
  vizType: Scalars['String']['output'];
};

/**
 * One proposed tile. `config` is the composition-v3 widget config
 * ({ version: 3, source: { kind: "semantic", query }, viz }) and maps DIRECTLY to
 * CreateAnalyticsWidgetInput.config — pass it through verbatim on accept. `position`
 * maps to CreateAnalyticsWidgetInput.position. `config` is the single source of truth
 * for persistence; `query` below is a render-only projection for the ProposalCard.
 */
export type AnalyticsProposalWidget = {
  __typename?: 'AnalyticsProposalWidget';
  /** Byte-for-byte CreateAnalyticsWidgetInput.config (composition-v3 wire config). */
  config: Scalars['AWSJSON']['output'];
  /** Display order; maps to CreateAnalyticsWidgetInput.position. */
  position: Scalars['Int']['output'];
  /**
   * A render-only summary of the tile's query, for the ProposalCard. Projection of
   * `config` — not a persistence contract.
   */
  query: AnalyticsProposalQuery;
  /** Human title for the ProposalCard; also carried inside `config.title`. */
  title?: Maybe<Scalars['String']['output']>;
};

/**
 * The tabular result of Query.analyticsQuery (ENG-1647 / T6). `columns`
 * describes the result schema; `rows` is a JSON array of row objects keyed by
 * column name (time buckets as ISO strings, measures as numbers) — the resolver
 * returns the RAW array and AppSync auto-encodes the AWSJSON scalar (never
 * pre-stringified). `rowCount` is the number of rows. `excludedMeasures` lists any
 * financial (MONEY) measure keys dropped server-side because the caller lacks
 * clearpath.finance.read.
 */
export type AnalyticsQueryResult = {
  __typename?: 'AnalyticsQueryResult';
  columns: Array<AnalyticsColumn>;
  excludedMeasures: Array<Scalars['String']['output']>;
  rowCount: Scalars['Int']['output'];
  rows: Scalars['AWSJSON']['output'];
};

/**
 * An org's goal/target for a single registry measure. Rendered as a chart
 * ReferenceLine + variance badge. Org-visible within the tenant.
 */
export type AnalyticsTarget = {
  __typename?: 'AnalyticsTarget';
  /** Optional display label for the line, e.g. "FY26 goal". */
  comparisonLabel?: Maybe<Scalars['String']['output']>;
  id: Scalars['ID']['output'];
  /**
   * The registry measure this target is for (packages/analytics-semantics
   * MEASURES), e.g. "ordersPerDay".
   */
  measureKey: Scalars['String']['output'];
  organizationId: Scalars['ID']['output'];
  /** The goal value the chart draws as a ReferenceLine. */
  targetValue: Scalars['Float']['output'];
  updatedAt?: Maybe<Scalars['AWSDateTime']['output']>;
};

export type AnalyticsTargetPayload = {
  __typename?: 'AnalyticsTargetPayload';
  target: AnalyticsTarget;
};

/**
 * One row of the customer leaderboard (Query.analyticsTopPractices, ENG-1807).
 * `practiceId` is the REAL customer party id when `inScope` is true (the caller's
 * own visibility lens resolved the name); for an OUT-OF-SCOPE customer it is an
 * OPAQUE, per-response alias (never the real id, not stable across responses) so a
 * customer id the caller may not see never leaves the server. `name` is the
 * customer's name for an in-scope row and null otherwise. `cases` is the whole-period
 * distinct-order count for the customer; it is always at or above the k-anon floor
 * (customers below it are suppressed and never appear). `inScope` records whether the
 * caller's lens resolved the name.
 */
export type AnalyticsTopPracticeRow = {
  __typename?: 'AnalyticsTopPracticeRow';
  cases: Scalars['Int']['output'];
  inScope: Scalars['Boolean']['output'];
  name?: Maybe<Scalars['String']['output']>;
  practiceId: Scalars['String']['output'];
};

/**
 * The result of Query.analyticsTopPractices (ENG-1807): the top-N customers by
 * case (distinct-order) volume for the caller's tenant, ranked descending, behind
 * k-anon small-cell suppression.
 */
export type AnalyticsTopPracticesResult = {
  __typename?: 'AnalyticsTopPracticesResult';
  rows: Array<AnalyticsTopPracticeRow>;
};

/**
 * A tile on an analytics dashboard (config JSON + display position).
 * CASCADE-owned by its dashboard: deleting the dashboard deletes its widgets.
 * Per ENG-1654 / T13.
 */
export type AnalyticsWidget = {
  __typename?: 'AnalyticsWidget';
  /**
   * Opaque widget definition (query/metric ref, visualization type, options).
   * Returned as a raw JSON object.
   */
  config: Scalars['AWSJSON']['output'];
  createdAt: Scalars['AWSDateTime']['output'];
  customFields?: Maybe<Scalars['AWSJSON']['output']>;
  dashboardId: Scalars['ID']['output'];
  id: Scalars['ID']['output'];
  organizationId: Scalars['ID']['output'];
  /** Display order within the dashboard; lower = earlier. */
  position: Scalars['Int']['output'];
  updatedAt?: Maybe<Scalars['AWSDateTime']['output']>;
};

export type AnalyticsWidgetDeletedPayload = {
  __typename?: 'AnalyticsWidgetDeletedPayload';
  deletedId: Scalars['ID']['output'];
};

export type AnalyticsWidgetPayload = {
  __typename?: 'AnalyticsWidgetPayload';
  widget: AnalyticsWidget;
};

/** One payment's outcome in a bulk apply. Never all-or-nothing. */
export type ApplyPaymentAttemptResult = {
  __typename?: 'ApplyPaymentAttemptResult';
  /** What was booked. Null on every refusal. */
  appliedEffect?: Maybe<PaymentProviderEffect>;
  attemptId: Scalars['ID']['output'];
  /**
   * The refusal, VERBATIM from the settlement engine where it came from there.
   * Its own wording names the figures involved and paraphrasing loses them.
   */
  error?: Maybe<Scalars['String']['output']>;
  success: Scalars['Boolean']['output'];
};

/** Book what the provider reports for up to ten payments. */
export type ApplyPaymentAttemptResultsInput = {
  /**
   * Lifecycle ROOT ids, 1..10. The cap is enforced SERVER-side: every item is a
   * provider round trip inside a 30-second AppSync/Lambda budget, and a timeout
   * loses the per-item results for a batch that may already have booked real
   * settlements — strictly worse than a smaller batch. A client with a larger
   * selection chunks it and shows progress.
   */
  attemptIds: Array<Scalars['ID']['input']>;
};

export type ApplyPaymentAttemptResultsPayload = {
  __typename?: 'ApplyPaymentAttemptResultsPayload';
  failureCount: Scalars['Int']['output'];
  results: Array<ApplyPaymentAttemptResult>;
  successCount: Scalars['Int']['output'];
};

/**
 * ENG-3054 Story 3.3 — one atomic save of a staged change set for one product
 * (the same shape the impact preview reviews). Null or omitted lists mean empty;
 * at most 200 grants and 200 revokes; an id may not appear in both; ids compare
 * case-insensitively. At least one grant, revoke or independent operation is
 * required. Unlike the preview, an id that is not a UUID, unknown, in another
 * organization, not an eligible customer, or (for an account manager) outside
 * the caller's book is refused with NotFound, never ignored.
 */
export type ApplyProductTypeCustomerAccessInput = {
  /**
   * The preview's `independentTargeted` for this same change set (exactly 0
   * without an independent operation; any other value is a ValidationError).
   * When the independent offices the operation targets have changed since the
   * preview, the save is refused with a retryable Conflict (reason
   * `independent_targeted_changed`) and nothing is written: preview the change
   * again, never resend the same value. This is a caller-supplied guard against
   * saving from a stale preview, not an authorization control: the server cannot
   * verify that the value came from a preview.
   */
  expectedIndependentTargeted: Scalars['Int']['input'];
  /**
   * The preview's `losingTotal` for this same change set: the most customers
   * the caller agreed to have lose access. Required. This is a caller-supplied
   * guard against saving from a stale preview, not an authorization control:
   * the server cannot verify that the value came from a preview. When the save
   * would make more customers lose access, it is refused with a retryable
   * Conflict (reason `losing_exceeds_expected`) before anything is charged or
   * written: preview the change again, never resend the same value. Fewer is
   * allowed. The server-side bounds are the organization's rolling 24-hour
   * budget (`budget_exceeded`): at most 6000 customers losing access, plus at
   * most 20 independent REVOKE operations that change rows.
   */
  expectedLosing: Scalars['Int']['input'];
  /**
   * Customers to grant: each converges to one open-ended grant that also
   * reaches its active offices (its earliest current start is kept, never
   * later than now; its note is kept).
   */
  grants?: InputMaybe<Array<Scalars['ID']['input']>>;
  /**
   * Grant or revoke every active independent office as one operation. GRANT
   * fills gaps only: it grants exactly the offices that cannot order now.
   * Explicit ids override it in both directions.
   */
  independent?: InputMaybe<CustomerAccessIndependentOp>;
  productTypeId: Scalars['ID']['input'];
  /**
   * Customers whose own direct grants for this product are revoked (an
   * ancestor's grant is never touched).
   */
  revokes?: InputMaybe<Array<Scalars['ID']['input']>>;
};

/**
 * ENG-3054 Story 3.3 — what an applied change set did. `targeted`, `changed`
 * and `unchanged` count PARTIES (targeted = changed + unchanged); `losing`
 * counts CUSTOMERS (a parent organization is one customer plus its active
 * offices) that lost the ability to order, org-wide, the same figure the
 * preview's `losingTotal` reported for the same state.
 */
export type ApplyProductTypeCustomerAccessResult = {
  __typename?: 'ApplyProductTypeCustomerAccessResult';
  /** Targeted parties with at least one grant row changed. */
  changed: Scalars['Int']['output'];
  /** Customers that lost the ability to order (now or at a scheduled end). */
  losing: Scalars['Int']['output'];
  /**
   * Distinct parties the change set targeted (explicit ids plus the
   * independent offices the operation reached).
   */
  targeted: Scalars['Int']['output'];
  /** Targeted parties already in their final state (a repeat changes nothing). */
  unchanged: Scalars['Int']['output'];
};

export type ApplyTenantConfigResult = {
  __typename?: 'ApplyTenantConfigResult';
  appliedAt?: Maybe<Scalars['AWSDateTime']['output']>;
  changesApplied: Scalars['Int']['output'];
  reconciliationId: Scalars['ID']['output'];
  status: Scalars['String']['output'];
};

/**
 * How the owning customer acted on an `AWAITING_CUSTOMER_APPROVAL` hold
 * (ENG-19).
 */
export enum ApprovalDecision {
  /**
   * Sign off — releases the hold via the canonical release path, so production
   * resumes.
   */
  Approve = 'APPROVE',
  /**
   * Reject — records the reason and notifies the lab; the hold is RETAINED so
   * the case cannot advance until it is reworked.
   */
  RequestChanges = 'REQUEST_CHANGES'
}

/**
 * Outcome of submitApprovalDecision. Carries no internal hold fields — the
 * customer-safe hold display refreshes through `Order.customerVisibleHolds`.
 */
export type ApprovalDecisionPayload = {
  __typename?: 'ApprovalDecisionPayload';
  decision: ApprovalDecision;
  holdId: Scalars['ID']['output'];
  /**
   * The affected case, so the client can refetch `customerVisibleHolds` and the
   * approval card without a second round-trip.
   */
  order: Order;
  orderId: Scalars['ID']['output'];
  /**
   * true when the hold was released (APPROVE, production resumes); false when
   * changes were requested and the hold was retained.
   */
  resolved: Scalars['Boolean']['output'];
};

export type ApproveJitGrantInput = {
  id: Scalars['ID']['input'];
  /**
   * Opaque correlation token (post-WebAuthn sessionId) stashed on the
   * jit_access_audit row for cross-reference with the MFA step-up event.
   */
  stepUpRef: Scalars['String']['input'];
};

/**
 * Classify (phiClass) + approve a projection, optionally exposing it. A projection
 * becomes catalog-selectable ONLY when it is SAFE and `exposed: true`; a PHI/BLOCKED
 * projection classifies but is held (never exposed). NotFound if the id is not a
 * projection in the caller's org.
 */
export type ApproveProjectionInput = {
  exposed: Scalars['Boolean']['input'];
  phiClass: ProjectionPhiClass;
  projectionId: Scalars['ID']['input'];
};

/**
 * Outstanding accounts receivable, aged into fixed buckets and computed
 * entirely server-side. FIN-M1-01 (ENG-1446).
 *
 * Two invariants the consumers of this type may rely on:
 *
 *   1. **Every amount is an OUTSTANDING balance**, not a gross invoice total:
 *      invoice total minus settled payment transactions, floored at zero. An
 *      invoice whose balance has already been settled contributes nothing and is
 *      not counted.
 *   2. **Amounts are NEVER summed across currencies.** Currency is part of the
 *      grouping key on both projections, so a caller that renders one number must
 *      pick one currency group (or render one figure per group). There is
 *      deliberately no grand total field for exactly this reason.
 */
export type ArAging = {
  __typename?: 'ArAging';
  /**
   * Business date the buckets were aged against — `filter.asOf` when supplied,
   * otherwise the server's current UTC date. Bucket membership is
   * `asOf - dueAt` in whole days.
   */
  asOf: Scalars['AWSDate']['output'];
  /**
   * One row per currency — the same population rolled up. Metric tiles and
   * the nav badge read this so they never have to aggregate client-side.
   */
  byCurrency: Array<ArAgingCurrencyBucket>;
  /** One row per (currency, billing party). The aging grid reads this. */
  byParty: Array<ArAgingPartyBucket>;
};

/**
 * Outstanding balance split by days past due.
 *
 * Buckets are `asOf - dueAt` in whole days: `current` is not-yet-due (zero or
 * negative days past due) plus anything with no due date at all; `days1To30` is
 * 1..30 days past due (so an invoice ONE day overdue is here, never in
 * `current`); `days31To60` is 31..60; `days61To90` is 61..90; `daysOver90` is 91
 * or more. `total` is the sum of the five and is expressed in the enclosing
 * group's currency.
 */
export type ArAgingBuckets = {
  __typename?: 'ArAgingBuckets';
  current: Scalars['Float']['output'];
  days1To30: Scalars['Float']['output'];
  days31To60: Scalars['Float']['output'];
  days61To90: Scalars['Float']['output'];
  daysOver90: Scalars['Float']['output'];
  total: Scalars['Float']['output'];
};

/** Aged outstanding balance for one currency across every billing party. */
export type ArAgingCurrencyBucket = {
  __typename?: 'ArAgingCurrencyBucket';
  buckets: ArAgingBuckets;
  currency: Scalars['String']['output'];
  invoiceCount: Scalars['Int']['output'];
  oldestDaysPastDue: Scalars['Int']['output'];
  /** Distinct billing parties owing in this currency. */
  partyCount: Scalars['Int']['output'];
};

/** Filter for arAging. FIN-M1-01 (ENG-1446). */
export type ArAgingFilterInput = {
  /**
   * Business date to age against; defaults to the server's current UTC date.
   * Supplying it makes a result reproducible (reports, tests, drill-downs).
   *
   * `asOf` chooses which date the buckets are aged against. It does NOT restate
   * balances as of that date: every amount is the outstanding balance NOW, and a
   * back-dated `asOf` only moves those amounts between buckets. An invoice
   * settled since `asOf` is therefore absent rather than shown as it stood then.
   *
   * That is a deliberate limitation. Historising the balance would historise only
   * half the population — `invoices.status` keeps no history, so an invoice
   * voided or written off since `asOf` would still be excluded — and a
   * half-historical AR figure is more misleading than an honestly current one.
   * A true point-in-time AR needs invoice-status history and is a separate
   * change.
   */
  asOf?: InputMaybe<Scalars['AWSDate']['input']>;
  /**
   * Narrow to these billing parties. Portal callers are ALWAYS additionally
   * narrowed to their own party scope — this filter can only ever subtract.
   */
  billingPartyIds?: InputMaybe<Array<Scalars['ID']['input']>>;
  /** Narrow to these ISO-4217 currency codes. */
  currencies?: InputMaybe<Array<Scalars['String']['input']>>;
};

/**
 * Aged outstanding balance for one billing party in one currency. A party
 * that owes in two currencies produces two rows.
 */
export type ArAgingPartyBucket = {
  __typename?: 'ArAgingPartyBucket';
  billingPartyId: Scalars['ID']['output'];
  /**
   * Display name of the billing party at query time. Flat scalar rather than a
   * nested `ExternalParty` so this projection needs no extra field resolver and
   * can never partially materialize a non-null party field.
   */
  billingPartyName?: Maybe<Scalars['String']['output']>;
  buckets: ArAgingBuckets;
  currency: Scalars['String']['output'];
  /** Invoices with a non-zero outstanding balance in this group. */
  invoiceCount: Scalars['Int']['output'];
  /** Largest days-past-due across this group; 0 when nothing here is overdue. */
  oldestDaysPastDue: Scalars['Int']['output'];
};

/**
 * The CFO's org-wide accounts-receivable and cash view. FIN-M6 slice 4
 * (ENG-1480).
 *
 * ONE POPULATION. Every AR figure in this payload — the per-currency tiles, the
 * days-sales-outstanding numerator, the worst-accounts table — is derived from the
 * SAME row set `Query.arAging` returns, which is embedded whole as `aging`. The
 * tiles and the aging grid therefore cannot disagree, and the bucketing arithmetic
 * exists in exactly one place. Two consequences a consumer must know:
 *
 *   1. That AR definition includes DRAFT invoices (`status NOT IN ('paid','void')`
 *      with a balance outstanding). It is deliberately NOT
 *      `Query.myBillingSummary`'s narrower list: the generator writes drafts with
 *      a due date, so a lab that never calls `sendInvoice` would otherwise report
 *      zero receivable. `myBillingSummary` is a customer-facing "what YOU owe" and
 *      excludes drafts for a different, also-correct reason.
 *   2. **Nothing is ever summed across currencies.** Currency is part of the key on
 *      every list here and there is no grand-total field, matching `arAging`,
 *      `myBillingSummary` and `revenueReport`.
 *
 * Collection Effectiveness Index (CEI) is NOT in this payload. It requires
 * beginning-of-period AR, which requires the M5 period-close snapshot (ENG-1453),
 * and a CEI computed from a current balance would be arithmetically wrong rather
 * than merely imprecise. It arrives in FIN-M6 slice 5 — there is deliberately no
 * null-valued field to bind to in the meantime.
 *
 * Money typing follows one rule: a figure is typed to match the query it must
 * RECONCILE with. AR amounts are `Float` because they come from (and equal) the
 * embedded `ArAging` Floats; the revenue denominators are `String` because they
 * come from (and equal) `revenueReport`'s money-as-string fields.
 */
export type ArDashboard = {
  __typename?: 'ArDashboard';
  /**
   * The full aging grid — byte-for-byte the `Query.arAging` projection for the
   * same `asOf`, embedded rather than recomputed. Read this for the per-party
   * drill-down; `openArByCurrency` is the same money rolled up.
   */
  aging: ArAging;
  /**
   * Business date the dashboard was computed against — the `asOf` argument when
   * supplied, otherwise the server's current UTC date. Same meaning as
   * `ArAging.asOf`: a BUCKETING date, not a restatement.
   */
  asOf: Scalars['AWSDate']['output'];
  /**
   * Instant the aggregate was computed. Distinct from `asOf`, which is a
   * business date: two dashboards with the same `asOf` and different `computedAt`
   * may legitimately disagree if a payment posted in between.
   */
  computedAt: Scalars['AWSDateTime']['output'];
  /**
   * Days sales outstanding per currency, month-to-date. See
   * `ArDsoByCurrency` for what the window is and why the historical figure is not
   * here.
   */
  dsoByCurrency: Array<ArDsoByCurrency>;
  /**
   * Open receivable and the past-due part of it, one row per currency. Empty is
   * a real, expected state (a lab with nothing outstanding).
   */
  openArByCurrency: Array<ArOpenBalance>;
  /**
   * Always LIVE until FIN-M6 slice 5 wires the M5 close snapshot. Label the
   * figures provisional and show `computedAt`.
   */
  periodStatus: RevenuePeriodStatus;
  /**
   * ACH debits the receiving bank sent back in the last 90 days, at most 100,
   * UNKNOWN disposition first and then newest first (ENG-1988).
   *
   * Not derived from the aging population above: a return whose invoice has since
   * been paid again, or voided, is still a return an operator has to see.
   *
   * There is deliberately no retry action attached to these rows. Re-presenting a
   * revoke-class return is a NACHA violation, and most returns classify as UNKNOWN
   * — which is exactly why they sort first. The correct operator action for an
   * unresolvable return is `Mutation.issueInvoiceAdjustment`; a recoverable one is
   * paid again by the payer in the portal.
   */
  recentAchReturns: Array<AchReturn>;
  /**
   * Worst-paying accounts, ranked by PAST-DUE balance descending within each
   * currency, at most 10 per currency. Accounts with nothing past due are absent.
   * Ranked on past-due rather than open balance on purpose: an account with a
   * large not-yet-due balance and one dollar overdue must not outrank an account
   * deep in arrears. The complete, pageable list is `Query.arAging`.
   */
  topOverdueAccounts: Array<ArOverdueAccount>;
};

/**
 * Days sales outstanding for one currency, with every input that produced it,
 * so a CFO can re-derive the figure rather than trust it. FIN-M6 slice 4
 * (ENG-1480).
 *
 *   daysSalesOutstanding = (arEnd / billedInPeriod) x days
 *
 * The window is MONTH-TO-DATE: `periodStart` is the first of `asOf`'s calendar
 * month, `periodEnd` is `asOf`, and `days` is that inclusive day count.
 * `billedInPeriod` is produced by the SAME statement `Query.revenueReport` runs on
 * the ACCRUAL_BILLED basis over that window, so the two screens reconcile by
 * construction — void invoices excluded, credits and settled refunds netted
 * identically.
 *
 * A HISTORICAL, POINT-IN-TIME FIGURE IS NOT AVAILABLE and a back-dated `asOf` does
 * NOT produce one. `arEnd` is the balance outstanding NOW (see `ArAging.asOf`:
 * bucketing, not restatement), so a back-dated `asOf` divides a CURRENT receivable
 * by a HISTORICAL revenue window. The point-in-time version needs the M5
 * period-close AR snapshot and arrives in FIN-M6 slice 5.
 */
export type ArDsoByCurrency = {
  __typename?: 'ArDsoByCurrency';
  /**
   * Open receivable in this currency, the numerator. Equals the matching
   * `ArOpenBalance.openBalance`.
   */
  arEnd: Scalars['Float']['output'];
  currency: Scalars['String']['output'];
  /** Days in the window — the multiplier in the formula above. */
  days: Scalars['Int']['output'];
  /**
   * Days sales outstanding against `grossBilledInPeriod`; NULL when that
   * denominator is not positive — a currency with a receivable but nothing billed
   * in the window has no defined figure, and returning 0 or Infinity would both be
   * lies.
   */
  dsoOnGross?: Maybe<Scalars['Float']['output']>;
  /**
   * The same metric against `netBilledInPeriod`; NULL on the same terms.
   *
   * BOTH are returned because whether the lab's headline revenue is gross or net
   * of credits is an unmade CFO decision — the same refusal to guess that makes
   * `revenueReport` return both `grossBilled` and `netBilled`.
   */
  dsoOnNet?: Maybe<Scalars['Float']['output']>;
  /**
   * Gross revenue billed in the window, per `revenueReport`'s ACCRUAL_BILLED
   * basis. String for the reason given on `ArDashboard`.
   */
  grossBilledInPeriod: Scalars['String']['output'];
  /** The same window net of credits, write-offs and settled refunds. */
  netBilledInPeriod: Scalars['String']['output'];
  /** Last day of the measurement window, inclusive (`asOf`). */
  periodEnd: Scalars['AWSDate']['output'];
  /** First day of the measurement window (the 1st of `asOf`'s month). */
  periodStart: Scalars['AWSDate']['output'];
};

/** One currency's open receivable. FIN-M6 slice 4 (ENG-1480). */
export type ArOpenBalance = {
  __typename?: 'ArOpenBalance';
  currency: Scalars['String']['output'];
  /** Largest days-past-due in this currency; 0 when nothing here is overdue. */
  oldestDaysPastDue: Scalars['Int']['output'];
  /**
   * Total outstanding balance in this currency — identical to the matching
   * `ArAging.byCurrency` bucket total.
   */
  openBalance: Scalars['Float']['output'];
  /** Invoices with a non-zero outstanding balance in this currency. */
  openInvoiceCount: Scalars['Int']['output'];
  /**
   * The part of `openBalance` that is at least one day past due: the aging
   * total minus its `current` bucket. `openBalance - overdueBalance` is what is
   * not yet due (or has no due date at all).
   */
  overdueBalance: Scalars['Float']['output'];
  /** Distinct billing parties owing in this currency. */
  partyCount: Scalars['Int']['output'];
};

/**
 * One overdue account in one currency. A party in arrears in two currencies
 * produces two rows. FIN-M6 slice 4 (ENG-1480).
 */
export type ArOverdueAccount = {
  __typename?: 'ArOverdueAccount';
  billingPartyId: Scalars['ID']['output'];
  /**
   * Display name at query time. Flat scalar rather than a nested party so this
   * projection needs no extra field resolver.
   */
  billingPartyName?: Maybe<Scalars['String']['output']>;
  currency: Scalars['String']['output'];
  /** Largest days-past-due for this party; always >= 1 here. */
  oldestDaysPastDue: Scalars['Int']['output'];
  /** Total outstanding balance for this party in this currency. */
  openBalance: Scalars['Float']['output'];
  /** Invoices with a non-zero outstanding balance for this party. */
  openInvoiceCount: Scalars['Int']['output'];
  /**
   * The part of it at least one day past due. Always > 0 here — an account with
   * nothing overdue is not in this list. This is the ranking key.
   */
  overdueBalance: Scalars['Float']['output'];
};

/** ENG-658 input for archiveFormField. */
export type ArchiveFormFieldInput = {
  id: Scalars['ID']['input'];
};

/**
 * Result of archiveOrgUnit / unarchiveOrgUnit. Returns the full unit so a
 * client can update its cache in place: archivedAt and archivedByUserId are
 * the fields that changed.
 */
export type ArchiveOrgUnitPayload = {
  __typename?: 'ArchiveOrgUnitPayload';
  orgUnit: OrgUnit;
};

/** Input for archivePipeline (P2.4). */
export type ArchivePipelineInput = {
  id: Scalars['ID']['input'];
};

/** Request payload for the general LLM assistant (askAssistant). SDL-01. */
export type AskAssistantInput = {
  conversationId?: InputMaybe<Scalars['ID']['input']>;
  message: Scalars['String']['input'];
};

/**
 * Result of a general LLM assistant turn (askAssistant). Reuses the shared
 * TokenUsage type for per-call accounting. SDL-01.
 */
export type AskAssistantResult = {
  __typename?: 'AskAssistantResult';
  answer: Scalars['String']['output'];
  conversationId: Scalars['ID']['output'];
  /**
   * Present ONLY when the assistant produced an analytics dashboard/report
   * proposal this turn (the analytics_propose tool). PROPOSE-ONLY: nothing is
   * persisted — the client renders it (A4 ProposalCard) and, on the USER's accept,
   * persists it via the EXISTING createAnalyticsDashboard / createAnalyticsWidget
   * mutations. Null when the turn produced no proposal. A3a (ENG-2240).
   */
  proposal?: Maybe<AnalyticsProposal>;
  tokensUsed: TokenUsage;
  toolTrace: Array<AssistantToolCall>;
};

/** Input for assignFeatureFlag (creates a new assignment row). */
export type AssignFlagInput = {
  expiresAt?: InputMaybe<Scalars['AWSDateTime']['input']>;
  flagId: Scalars['ID']['input'];
  scopeId?: InputMaybe<Scalars['String']['input']>;
  scopeType: FlagScopeType;
  value: Scalars['AWSJSON']['input'];
};

/**
 * ENG-658 input for assignPartyOrderForm. There is at most one ACTIVE
 * assignment per party (db unique index) — assigning a new one replaces
 * (revokes then supersedes) any existing active assignment for the same
 * party, it does not add a second one.
 */
export type AssignPartyOrderFormInput = {
  formDefinitionId: Scalars['ID']['input'];
  partyId: Scalars['ID']['input'];
  /**
   * Defaults to PARTY_AND_DESCENDANTS — same reasoning as
   * GrantPartyCatalogAccessInput.scope.
   */
  scope?: InputMaybe<PartyCatalogGrantScope>;
};

/**
 * Input for assignRole.
 *
 * ENG-3498 — a user holds exactly ONE role; their user_roles rows differ only by
 * the org unit the role applies in. `roleId` is therefore optional: omit it to add
 * the user to an org unit (department, team, site) with the role they already
 * hold — that is what every department/site membership surface does. When given,
 * it must be the role the user already holds, or the user must hold none yet;
 * anything else is refused (reason role_change_not_allowed). A role is changed
 * only with changeUserRole.
 *
 * Each call adds one place the role applies (see UserRole): a unit, or the whole
 * lab. The caller needs clearpath.roles.assign over that place — a unit among
 * their own units or below them; only a caller whose role applies to the whole
 * lab may grant the whole lab (reason out_of_scope otherwise). Adding a unit to
 * someone whose role already applies to the whole lab, or the reverse, is not
 * refused; the Roles page saves "where the role applies" as one choice (adds
 * first, then revokeRole on the rows it replaces).
 */
export type AssignRoleInput = {
  expiresAt?: InputMaybe<Scalars['AWSDateTime']['input']>;
  /** The unit the role applies in (and every unit below it). See wholeLab. */
  orgUnitId?: InputMaybe<Scalars['ID']['input']>;
  roleId?: InputMaybe<Scalars['ID']['input']>;
  userId: Scalars['ID']['input'];
  /**
   * ENG-3923 — give a STAFF user's role to the whole lab, on purpose. A staff
   * role applies somewhere chosen: a department or team (`orgUnitId`) or the whole
   * lab (`wholeLab: true`). Omitting both is refused (reason
   * `role_scope_required`) — a blank org unit used to mean whole-lab silently,
   * which no later department assignment could narrow. Setting both is refused
   * (reason `role_scope_conflict`). Ignored for portal users, whose role is
   * derived and carries no org unit.
   */
  wholeLab?: InputMaybe<Scalars['Boolean']['input']>;
};

export type AssignShiftInput = {
  shiftId: Scalars['ID']['input'];
  userIds: Array<Scalars['ID']['input']>;
};

/** Input for assignStepRun. */
export type AssignStepRunInput = {
  /**
   * Override assignee (default: caller). Naming someone else needs
   * clearpath.assignments.write and an assignee in the step's department — see
   * Mutation.assignStepRun.
   */
  assigneeUserId?: InputMaybe<Scalars['ID']['input']>;
  stepRunId: Scalars['ID']['input'];
};

export type AssignTicketInput = {
  /** Null = unassign (returns ticket to the open queue). */
  assigneeUserId?: InputMaybe<Scalars['ID']['input']>;
  ticketId: Scalars['ID']['input'];
};

export type AssignUserSkillInput = {
  certifiedOn?: InputMaybe<Scalars['AWSDateTime']['input']>;
  lastAssessed?: InputMaybe<Scalars['AWSDateTime']['input']>;
  level: Scalars['String']['input'];
  skillDefinitionId: Scalars['ID']['input'];
  userId: Scalars['ID']['input'];
};

/**
 * Assignment record (who's responsible).
 * Per docs/clearpath-architecture/03-data-model.md tier 3 (`assignments`).
 */
export type Assignment = {
  __typename?: 'Assignment';
  acceptedAt?: Maybe<Scalars['AWSDateTime']['output']>;
  assignedAt: Scalars['AWSDateTime']['output'];
  assignedByUserId?: Maybe<Scalars['ID']['output']>;
  assigneeUser?: Maybe<User>;
  /** Assignee user. */
  assigneeUserId: Scalars['ID']['output'];
  id: Scalars['ID']['output'];
  isActive: Scalars['Boolean']['output'];
  orgUnit?: Maybe<OrgUnit>;
  /** Org unit assignment is scoped to. */
  orgUnitId?: Maybe<Scalars['ID']['output']>;
  reassignedFromAssignmentId?: Maybe<Scalars['ID']['output']>;
  releasedAt?: Maybe<Scalars['AWSDateTime']['output']>;
  stepRun?: Maybe<StepRun>;
  stepRunId: Scalars['ID']['output'];
};

export type AssignmentAssignee = {
  __typename?: 'AssignmentAssignee';
  id: Scalars['ID']['output'];
  name?: Maybe<Scalars['String']['output']>;
};

export type AssignmentCandidate = {
  __typename?: 'AssignmentCandidate';
  activeStepRunCount: Scalars['Int']['output'];
  availabilityLabel?: Maybe<Scalars['String']['output']>;
  available: Scalars['Boolean']['output'];
  blockingTimeOffRequestIds: Array<Scalars['ID']['output']>;
  capacitySource: Scalars['String']['output'];
  capacityUnitsPerDay: Scalars['Int']['output'];
  email?: Maybe<Scalars['String']['output']>;
  name: Scalars['String']['output'];
  orgUnitId?: Maybe<Scalars['ID']['output']>;
  releaseLabel?: Maybe<Scalars['String']['output']>;
  releaseNote?: Maybe<Scalars['String']['output']>;
  releasedThisStep: Scalars['Boolean']['output'];
  shiftLabels: Array<Scalars['String']['output']>;
  userId: Scalars['ID']['output'];
};

export type AssignmentDeptFacet = {
  __typename?: 'AssignmentDeptFacet';
  count: Scalars['Int']['output'];
  id: Scalars['ID']['output'];
  name: Scalars['String']['output'];
};

/**
 * Auto-assignment policy on a pipeline step.
 *
 * `MANUAL` — engine leaves step_runs unassigned at ready; operator picks.
 * `ROUND_ROBIN` — engine rotates through the step's team members.
 * `LOWEST_CAPACITY` — engine picks the member with fewest open step_runs.
 */
export enum AssignmentPolicy {
  LowestCapacity = 'LOWEST_CAPACITY',
  Manual = 'MANUAL',
  RoundRobin = 'ROUND_ROBIN'
}

export type AssignmentWorkItem = {
  __typename?: 'AssignmentWorkItem';
  /** Which attempt at this step this item is. 1 for a first pass. */
  attemptNumber: Scalars['Int']['output'];
  currentAssignee?: Maybe<AssignmentAssignee>;
  customerName: Scalars['String']['output'];
  dueDate?: Maybe<Scalars['String']['output']>;
  /**
   * ENG-2293 — this attempt exists because the unit was sent BACK to this
   * step. An `escalate_to_manager` rework arrives here unassigned and would
   * otherwise be indistinguishable from work nobody has picked up yet, which is
   * the difference between "start this" and "somebody already tried this".
   *
   * Derived from `step_runs.loopback_from_step_run_id`, not from
   * `attemptNumber > 1`: a reassignment and a scan retry also raise the attempt
   * number without the unit having gone backwards.
   */
  isRework: Scalars['Boolean']['output'];
  orderBarcode?: Maybe<Scalars['String']['output']>;
  orderId?: Maybe<Scalars['ID']['output']>;
  orgUnitId?: Maybe<Scalars['ID']['output']>;
  orgUnitName?: Maybe<Scalars['String']['output']>;
  patientName: Scalars['String']['output'];
  priority: Scalars['String']['output'];
  slaStatus: Scalars['String']['output'];
  status: Scalars['String']['output'];
  stepName: Scalars['String']['output'];
  stepRunId: Scalars['ID']['output'];
  type: Scalars['String']['output'];
  unitId?: Maybe<Scalars['ID']['output']>;
  unitLabel?: Maybe<Scalars['String']['output']>;
};

export type AssignmentWorkList = {
  __typename?: 'AssignmentWorkList';
  departments: Array<AssignmentDeptFacet>;
  hasMore: Scalars['Boolean']['output'];
  items: Array<AssignmentWorkItem>;
  limit: Scalars['Int']['output'];
  offset: Scalars['Int']['output'];
  total: Scalars['Int']['output'];
};

/**
 * An async assistant job (ENG-2250, Phase 1).
 *
 * ⚠️ SUBSCRIPTION FRAME SIZE: keep the pushed onAssistantResult frame SMALL. The
 * worker's publishAssistantResult call, and the client's onAssistantResult
 * subscription, MUST select only `jobId`, `status` and `hasProposal` (the size
 * hint) — never `proposal` / `answer` / `toolTrace`, which can exceed the
 * WebSocket frame / 8KB WAF cap. The full result is fetched with
 * `assistantJob(jobId)` once the frame signals a terminal status. Selecting the
 * subscription filter key (`jobId`) on the fan-out mutation is MANDATORY or the
 * @aws_subscribe fan-out silently drops the frame — same discipline documented in
 * services/api/src/notifications/push.ts:65-78 for onNotificationCreated.
 */
export type AssistantJob = {
  __typename?: 'AssistantJob';
  /**
   * Final natural-language answer. Null until SUCCEEDED. Poll-delivered via
   * assistantJob — deliberately NOT selected onto the subscription frame (size).
   */
  answer?: Maybe<Scalars['String']['output']>;
  /**
   * Conversation this turn belongs to (echoed from enqueue). Null only before a
   * conversation is resolved.
   */
  conversationId?: Maybe<Scalars['ID']['output']>;
  /**
   * Structured, non-sensitive error/refusal envelope (an encodeErrorEnvelope
   * string: typed code + curated message) when status = FAILED. The worker's
   * try/finally ALWAYS publishes a terminal result — a FAILED job carries this
   * instead of an `answer`, so the client never hangs on a lost turn. Null unless
   * FAILED.
   */
  errorEnvelope?: Maybe<Scalars['String']['output']>;
  /**
   * On-frame size hint: true when a terminal SUCCEEDED job carries an analytics
   * `proposal`. Lets the client decide whether to poll assistantJob(jobId) for the
   * (potentially large) proposal WITHOUT putting the proposal on the WS frame.
   * False while RUNNING.
   */
  hasProposal: Scalars['Boolean']['output'];
  /**
   * Server-minted, unguessable job id. The onAssistantResult subscription
   * filter key — the fan-out mutation MUST select it or the frame silently drops.
   */
  jobId: Scalars['ID']['output'];
  /**
   * Present ONLY when this turn produced an analytics dashboard/report proposal
   * (analytics_propose). PROPOSE-ONLY — nothing is persisted; the client renders it
   * and, on the USER's accept, persists via the existing createAnalyticsDashboard /
   * createAnalyticsWidget mutations. Reuses #1480's AnalyticsProposal type.
   * Potentially large: poll-delivered via assistantJob, never on the subscription
   * frame. See `hasProposal` for the on-frame size hint.
   */
  proposal?: Maybe<AnalyticsProposal>;
  /** In-flight (RUNNING) or terminal (SUCCEEDED / FAILED). */
  status: AssistantJobStatus;
  /** Per-turn model token accounting. Null until terminal. */
  tokensUsed?: Maybe<TokenUsage>;
  /**
   * Redacted trace of the read-only tools the worker invoked. Null until
   * terminal. Poll-delivered, not on the subscription frame.
   */
  toolTrace?: Maybe<Array<AssistantToolCall>>;
  /** Last transition time (enqueue, or terminal publish). */
  updatedAt?: Maybe<Scalars['AWSDateTime']['output']>;
};

/**
 * Status of an async assistant job (ENG-2250). RUNNING is the enqueue-time
 * state; the worker always transitions to a terminal SUCCEEDED or FAILED and
 * publishes it via publishAssistantResult.
 */
export enum AssistantJobStatus {
  Failed = 'FAILED',
  Running = 'RUNNING',
  Succeeded = 'SUCCEEDED'
}

/**
 * One read-only tool invocation performed by the assistant while answering.
 * argsDigest is a redacted/hashed summary of the arguments — never raw args —
 * so the trace is safe to return to the client. SDL-01.
 */
export type AssistantToolCall = {
  __typename?: 'AssistantToolCall';
  argsDigest: Scalars['String']['output'];
  elapsedMs?: Maybe<Scalars['Int']['output']>;
  rowCount?: Maybe<Scalars['Int']['output']>;
  status: Scalars['String']['output'];
  toolName: Scalars['String']['output'];
};

export enum AuditExportFormat {
  Csv = 'CSV',
  Ndjson = 'NDJSON'
}

/**
 * Payload returned by BA.4 requestAuditExport. Bundle URLs expire per
 * the audit-query Lambda's EXPORT_PRESIGN_TTL_SECONDS env (default 24h).
 */
export type AuditExportResponse = {
  __typename?: 'AuditExportResponse';
  exportId: Scalars['ID']['output'];
  presignedCoverUrl: Scalars['AWSURL']['output'];
  presignedDataUrl: Scalars['AWSURL']['output'];
  rowCount: Scalars['Int']['output'];
  sha256: Scalars['String']['output'];
};

/**
 * Append-only audit of all mutations and sensitive reads.
 * Per docs/clearpath-architecture/03-data-model.md tier 8 (`audit_log`).
 */
export type AuditLogEntry = {
  __typename?: 'AuditLogEntry';
  /** Action key (e.g. orders.cancel). */
  action: Scalars['String']['output'];
  actor?: Maybe<User>;
  /** Actor identifier (user ID, service account ID, share-link token). */
  actorRef?: Maybe<Scalars['String']['output']>;
  /** Actor type discriminator. */
  actorType: ActorType;
  /** Acting user (null for system). */
  actorUserId?: Maybe<Scalars['ID']['output']>;
  aggregateId?: Maybe<Scalars['ID']['output']>;
  /** Aggregate kind acted on. */
  aggregateType: Scalars['String']['output'];
  /** Diff or payload (JSONB). */
  changes?: Maybe<Scalars['AWSJSON']['output']>;
  /** Cedar policy decision (allowed, denied). */
  decision: AuthorizationDecision;
  /** Cedar reasons (JSONB). */
  decisionReasons?: Maybe<Scalars['AWSJSON']['output']>;
  id: Scalars['ID']['output'];
  occurredAt: Scalars['AWSDateTime']['output'];
  organizationId?: Maybe<Scalars['ID']['output']>;
  /** Source IP. */
  sourceIp?: Maybe<Scalars['AWSIPAddress']['output']>;
  tenantId: Scalars['ID']['output'];
  userAgent?: Maybe<Scalars['String']['output']>;
};

export type AuditLogEntryConnection = {
  __typename?: 'AuditLogEntryConnection';
  edges: Array<AuditLogEntryEdge>;
  pageInfo: PageInfo;
  totalCount: Scalars['Int']['output'];
};

export type AuditLogEntryEdge = {
  __typename?: 'AuditLogEntryEdge';
  cursor: Scalars['String']['output'];
  node: AuditLogEntry;
};

/** Filter for audit log. */
export type AuditLogFilterInput = {
  action?: InputMaybe<Array<Scalars['String']['input']>>;
  /**
   * Action namespace prefixes (e.g. `[auth., order.]`). Matches any
   * action whose key starts with one of the supplied strings. Used by the
   * admin audit log page's event-type multi-select.
   */
  actionPrefix?: InputMaybe<Array<Scalars['String']['input']>>;
  /**
   * Round 5 (2026-05-02) — exclude rows whose action starts with any of
   * these prefixes. Used by per-order audit panes to suppress
   * `data.read` / `audit_log.` noise that overwhelms the timeline.
   */
  actionPrefixExclude?: InputMaybe<Array<Scalars['String']['input']>>;
  actorUserId?: InputMaybe<Array<Scalars['ID']['input']>>;
  aggregateId?: InputMaybe<Array<Scalars['ID']['input']>>;
  aggregateType?: InputMaybe<Array<Scalars['String']['input']>>;
  decision?: InputMaybe<Array<AuthorizationDecision>>;
  occurredAt?: InputMaybe<DateRangeInput>;
  /**
   * Free-text search across action, aggregate type, aggregate id, and the
   * serialised before/after state JSON. Case-insensitive substring match.
   */
  search?: InputMaybe<Scalars['String']['input']>;
};

/** Input for BA.4 auditQuery. */
export type AuditQueryInput = {
  sql: Scalars['String']['input'];
  tenantId: Scalars['ID']['input'];
};

/** Payload returned by BA.4 auditQuery. */
export type AuditQueryResponse = {
  __typename?: 'AuditQueryResponse';
  executionId: Scalars['ID']['output'];
  presignedResultsUrl: Scalars['AWSURL']['output'];
  rowCount: Scalars['Int']['output'];
};

/** Authorization decision. */
export enum AuthorizationDecision {
  Allowed = 'ALLOWED',
  Denied = 'DENIED',
  NotApplicable = 'NOT_APPLICABLE'
}

export type AuthorizeMachineInput = {
  machineId: Scalars['ID']['input'];
  userId: Scalars['ID']['input'];
};

/**
 * Tenant-defined automation rule.
 * Per docs/clearpath-architecture/03-data-model.md tier 5 (`automation_rules`).
 */
export type AutomationRule = {
  __typename?: 'AutomationRule';
  /** Action to execute (JSONB). */
  action: Scalars['AWSJSON']['output'];
  /**
   * T12/ENG-370 — pipeline step this rule attaches to (attach-by-step_id, O2:
   * survives republish). Null = not step-scoped.
   */
  attachedStepId?: Maybe<Scalars['ID']['output']>;
  /** Optional condition expression (JSONB). */
  condition?: Maybe<Scalars['AWSJSON']['output']>;
  createdAt: Scalars['AWSDateTime']['output'];
  description?: Maybe<Scalars['String']['output']>;
  fireCount: Scalars['Int']['output'];
  id: Scalars['ID']['output'];
  /** Human-readable description of the rule's intent. */
  intent?: Maybe<Scalars['String']['output']>;
  isActive: Scalars['Boolean']['output'];
  /** Last time this rule fired. */
  lastFiredAt?: Maybe<Scalars['AWSDateTime']['output']>;
  name: Scalars['String']['output'];
  organizationId: Scalars['ID']['output'];
  /**
   * T12/ENG-370 — pipeline this rule is attached to for DAG-canvas rendering.
   * Null = organization-wide rule (not pinned to a pipeline).
   */
  pipelineId?: Maybe<Scalars['ID']['output']>;
  publishedAt?: Maybe<Scalars['AWSDateTime']['output']>;
  publishedByUserId?: Maybe<Scalars['ID']['output']>;
  /** P2.4 (2026-04-25) — versioning lifecycle. See PipelineStatus. */
  status: PipelineStatus;
  tenantId: Scalars['ID']['output'];
  /** Event pattern that triggers this rule (JSONB). */
  triggerPattern: Scalars['AWSJSON']['output'];
  updatedAt?: Maybe<Scalars['AWSDateTime']['output']>;
};

export type AutomationRulePayload = {
  __typename?: 'AutomationRulePayload';
  automationRule: AutomationRule;
};

/** Outcome of a dry-run rule evaluation. */
export type AutomationRuleTestResult = {
  __typename?: 'AutomationRuleTestResult';
  /** Validation / evaluation errors surfaced by the engine. Empty on success. */
  errors: Array<Scalars['String']['output']>;
  /** Actions that would execute (serialized JSONB, one per entry). */
  matchedActions: Array<Scalars['AWSJSON']['output']>;
  /**
   * True when the trigger pattern + condition both matched. The actions
   * listed in `matchedActions` would have fired in order.
   */
  wouldFire: Scalars['Boolean']['output'];
};

/** Tenant-supported barcode formats. */
export enum BarcodeFormat {
  /** No check digit, `*` start/stop guards. SKDLA default per ENG-46. */
  Code39 = 'CODE39',
  Code128 = 'CODE128',
  Datamatrix = 'DATAMATRIX',
  Qr = 'QR'
}

/**
 * Append-only scan event log.
 * Per docs/clearpath-architecture/03-data-model.md tier 8 (`barcode_scans`).
 */
export type BarcodeScan = {
  __typename?: 'BarcodeScan';
  atStepRun?: Maybe<StepRun>;
  /** Resolved step run. */
  atStepRunId?: Maybe<Scalars['ID']['output']>;
  /** Scanned barcode value (matches order.barcodeValue). */
  barcodeValue: Scalars['String']['output'];
  id: Scalars['ID']['output'];
  order?: Maybe<Order>;
  /** Resolved order (if matched). */
  orderId?: Maybe<Scalars['ID']['output']>;
  organizationId: Scalars['ID']['output'];
  scanPurpose: ScanPurpose;
  scannedAt: Scalars['AWSDateTime']['output'];
  scannedBy?: Maybe<User>;
  scannedByUserId?: Maybe<Scalars['ID']['output']>;
  scannerDevice?: Maybe<ScannerDevice>;
  scannerDeviceId?: Maybe<Scalars['ID']['output']>;
  tenantId: Scalars['ID']['output'];
};

export type BarcodeScanConnection = {
  __typename?: 'BarcodeScanConnection';
  edges: Array<BarcodeScanEdge>;
  pageInfo: PageInfo;
  totalCount: Scalars['Int']['output'];
};

export type BarcodeScanEdge = {
  __typename?: 'BarcodeScanEdge';
  cursor: Scalars['String']['output'];
  node: BarcodeScan;
};

/** Input for recordBarcodeScan. MOL-563. */
export type BarcodeScanInput = {
  /** Optional location hint captured by the scanner. */
  geoLocation?: InputMaybe<Scalars['AWSJSON']['input']>;
  /** Why the scan happened. Controls whether a step_run is opened/closed. */
  scanPurpose: ScanPurpose;
  /** User who scanned (overrides the authenticated caller when provided). */
  scannedByUserId?: InputMaybe<Scalars['ID']['input']>;
  /** Raw scanned barcode string (typically matches orders.barcode_value). */
  scannedValue: Scalars['String']['input'];
  /** ID of the hardware scanner that captured the scan (if any). */
  scannerDeviceId?: InputMaybe<Scalars['ID']['input']>;
};

/**
 * Result of recordBarcodeScan. When scanPurpose triggered a step transition,
 * stepRun is populated.
 */
export type BarcodeScanPayload = {
  __typename?: 'BarcodeScanPayload';
  /** Resolved order (always present on a successful scan). */
  order?: Maybe<Order>;
  scan: BarcodeScan;
  /**
   * Resolved / newly-inserted step run (present for step_start and
   * step_complete scans when an active step_run exists).
   */
  stepRun?: Maybe<StepRun>;
};

export type BeginMeditConnectionPayload = {
  __typename?: 'BeginMeditConnectionPayload';
  expiresAt: Scalars['AWSDateTime']['output'];
  launchToken: Scalars['String']['output'];
};

export type BeginThreeShapeConnectionPayload = {
  __typename?: 'BeginThreeShapeConnectionPayload';
  expiresAt: Scalars['AWSDateTime']['output'];
  launchToken: Scalars['String']['output'];
};

/**
 * Payer billing address for a saved payment method. Field names are the
 * canonical domain shape, not any provider's key names — the adapter maps to
 * those at send time.
 */
export type BillingAddressInput = {
  /** Street address. Required for ACH — see AddPaymentMethodInput.billingAddress. */
  addressLine1?: InputMaybe<Scalars['String']['input']>;
  addressLine2?: InputMaybe<Scalars['String']['input']>;
  city?: InputMaybe<Scalars['String']['input']>;
  company?: InputMaybe<Scalars['String']['input']>;
  /** ISO-3166 alpha-2 (`US`). */
  country?: InputMaybe<Scalars['String']['input']>;
  email?: InputMaybe<Scalars['String']['input']>;
  /** Accountholder given name. Required by BASYS for an ACH debit. */
  firstName: Scalars['String']['input'];
  /** Accountholder family name. Required by BASYS for an ACH debit. */
  lastName: Scalars['String']['input'];
  phone?: InputMaybe<Scalars['String']['input']>;
  postalCode?: InputMaybe<Scalars['String']['input']>;
  state?: InputMaybe<Scalars['String']['input']>;
};

/**
 * Credit memos, refunds, write-offs.
 * Per docs/clearpath-architecture/03-data-model.md tier 7 (`billing_adjustments`).
 */
export type BillingAdjustment = {
  __typename?: 'BillingAdjustment';
  adjustmentType: BillingEventType;
  amount: Scalars['Float']['output'];
  appliedAt: Scalars['AWSDateTime']['output'];
  appliedByUserId?: Maybe<Scalars['ID']['output']>;
  currency: Scalars['String']['output'];
  id: Scalars['ID']['output'];
  invoiceId?: Maybe<Scalars['ID']['output']>;
  orderId?: Maybe<Scalars['ID']['output']>;
  organizationId: Scalars['ID']['output'];
  reason?: Maybe<Scalars['String']['output']>;
  tenantId: Scalars['ID']['output'];
};

/** Filter for billing adjustments. MOL-620. */
export type BillingAdjustmentFilterInput = {
  adjustmentType?: InputMaybe<Array<BillingEventType>>;
  approvedByUserIds?: InputMaybe<Array<Scalars['ID']['input']>>;
  billingEventIds?: InputMaybe<Array<Scalars['ID']['input']>>;
};

/** Payload envelope for issueInvoiceAdjustment. MOL-622. */
export type BillingAdjustmentPayload = {
  __typename?: 'BillingAdjustmentPayload';
  adjustment: BillingAdjustment;
  /** Invoice post-state — issuing a refund / credit updates amountDue. */
  invoice?: Maybe<Invoice>;
};

/** Cross-tenant billing rollup row. */
export type BillingAggregation = {
  __typename?: 'BillingAggregation';
  createdAt: Scalars['AWSDateTime']['output'];
  currency: Scalars['String']['output'];
  id: Scalars['ID']['output'];
  lastAggregatedAt: Scalars['AWSDateTime']['output'];
  periodEnd: Scalars['AWSDate']['output'];
  periodStart: Scalars['AWSDate']['output'];
  productId: Scalars['ID']['output'];
  sourceEventCount: Scalars['Int']['output'];
  tenantId: Scalars['ID']['output'];
  totalBillableAmount: Scalars['String']['output'];
  updatedAt: Scalars['AWSDateTime']['output'];
};

/**
 * ENG-1422 — whether a case is chargeable. Mirrors the
 * `orders.billing_disposition` CHECK constraint (migration 0299).
 */
export enum BillingDisposition {
  /** Price it normally. */
  Billable = 'BILLABLE',
  /** Waived — the pricing snapshot resolves to zero. */
  NoCharge = 'NO_CHARGE'
}

/**
 * Local billing event record; synced to control plane.
 * Per docs/clearpath-architecture/03-data-model.md tier 7 (`billing_events`).
 */
export type BillingEvent = {
  __typename?: 'BillingEvent';
  amount: Scalars['Float']['output'];
  /** Billable party this event rolls into on generate. */
  billablePartyId?: Maybe<Scalars['ID']['output']>;
  currency: Scalars['String']['output'];
  /** Event type (charge, refund, credit, write_off). */
  eventType: BillingEventType;
  id: Scalars['ID']['output'];
  invoiceId?: Maybe<Scalars['ID']['output']>;
  occurredAt: Scalars['AWSDateTime']['output'];
  /** Barcode of the attributed order (when orderId is set). */
  orderBarcode?: Maybe<Scalars['String']['output']>;
  /** Aggregate this event is attributed to. */
  orderId?: Maybe<Scalars['ID']['output']>;
  organizationId: Scalars['ID']['output'];
  payload?: Maybe<Scalars['AWSJSON']['output']>;
  /** DB lifecycle: pending | invoiced | paid (and similar). */
  status?: Maybe<BillingEventStatus>;
  syncedAt?: Maybe<Scalars['AWSDateTime']['output']>;
  tenantId: Scalars['ID']['output'];
};

export type BillingEventConnection = {
  __typename?: 'BillingEventConnection';
  edges: Array<BillingEventEdge>;
  pageInfo: PageInfo;
  totalCount: Scalars['Int']['output'];
};

export type BillingEventEdge = {
  __typename?: 'BillingEventEdge';
  cursor: Scalars['String']['output'];
  node: BillingEvent;
};

/** Filter for billing events. */
export type BillingEventFilterInput = {
  /** Restrict to these billable parties (lab Generate eligible list). */
  billingPartyIds?: InputMaybe<Array<Scalars['ID']['input']>>;
  eventType?: InputMaybe<Array<BillingEventType>>;
  /**
   * ENG-2572 — when true, `billingPartyIds` also matches every party BENEATH
   * the named ones in the external-party hierarchy, so naming a DSO returns its
   * practices' events too. Defaults to false; staff sessions only (a portal
   * session is already scoped to its own party subtree).
   */
  includeBillingPartyDescendants?: InputMaybe<Scalars['Boolean']['input']>;
  occurredAt?: InputMaybe<DateRangeInput>;
  orderId?: InputMaybe<Array<Scalars['ID']['input']>>;
  status?: InputMaybe<Array<BillingEventStatus>>;
};

export type BillingEventPayload = {
  __typename?: 'BillingEventPayload';
  billingEvent: BillingEvent;
};

/**
 * billing_events.status lifecycle (db/migrations/0007_tier7_pricing_billing.sql
 * CHECK (status IN ('pending','invoiced','paid','disputed','credited','refunded','voided'))).
 */
export enum BillingEventStatus {
  Credited = 'CREDITED',
  Disputed = 'DISPUTED',
  Invoiced = 'INVOICED',
  Paid = 'PAID',
  Pending = 'PENDING',
  Refunded = 'REFUNDED',
  Voided = 'VOIDED'
}

/** Billing event kind. */
export enum BillingEventType {
  Adjustment = 'ADJUSTMENT',
  Charge = 'CHARGE',
  Credit = 'CREDIT',
  Fee = 'FEE',
  Refund = 'REFUND',
  WriteOff = 'WRITE_OFF'
}

/** Billing period (start inclusive, end exclusive) for rollup queries. */
export type BillingPeriodInput = {
  end: Scalars['AWSDate']['input'];
  start: Scalars['AWSDate']['input'];
};

/**
 * Aggregated billing rollup row shown in the cross-tenant billing view.
 * Synthesized from billing_aggregations joined to tenants + products.
 * seats/storageGb/apiCalls come from billing_aggregations.custom_fields
 * (best-effort — present when the billing aggregator populates them).
 */
export type BillingRollupRow = {
  __typename?: 'BillingRollupRow';
  amountUsd: Scalars['Float']['output'];
  apiCalls: Scalars['Int']['output'];
  /** YYYY-MM period string. */
  period: Scalars['String']['output'];
  productCode: Scalars['String']['output'];
  seats: Scalars['Int']['output'];
  storageGb: Scalars['Float']['output'];
  tenantId: Scalars['ID']['output'];
  tenantName: Scalars['String']['output'];
};

/**
 * Summary surface for the portal Billing home view. Aggregates the viewer's
 * open invoices, amount due across all currencies, and the last successful
 * payment. The server auto-scopes to the viewer's billable party closure —
 * no explicit filter is accepted. MOL-622.
 */
export type BillingSummary = {
  __typename?: 'BillingSummary';
  /** Last succeeded payment_transaction total (decimal string). */
  lastPaymentAmount?: Maybe<Scalars['String']['output']>;
  /** When the last payment posted. */
  lastPaymentAt?: Maybe<Scalars['AWSDateTime']['output']>;
  /** Last succeeded payment currency. */
  lastPaymentCurrency?: Maybe<Scalars['String']['output']>;
  /**
   * Sum of amountDue across all open invoices, as a decimal string keyed by
   * currency. Sent as AWSJSON to avoid fixing the key set in the SDL.
   */
  openBalanceByCurrency: Scalars['AWSJSON']['output'];
  /** Count of invoices with status != PAID / VOIDED / WRITTEN_OFF. */
  openInvoiceCount: Scalars['Int']['output'];
  /** Count of invoices whose dueAt is in the past and are still unpaid. */
  overdueInvoiceCount: Scalars['Int']['output'];
  /** Primary currency of the viewer's billable party, if one is configured. */
  primaryCurrency?: Maybe<Scalars['String']['output']>;
};

export type BillingTotals = {
  __typename?: 'BillingTotals';
  amountUsd: Scalars['Float']['output'];
};

/**
 * Round 6 — bulk apply input. Lifts a single tier definition onto a
 * list of parties in one transactional call. Each party gets a fresh
 * agreement with the line embedded.
 */
export type BulkApplyPartyPricingInput = {
  agreementType: PartyAgreementType;
  description?: InputMaybe<Scalars['String']['input']>;
  discountPercent?: InputMaybe<Scalars['Float']['input']>;
  externalPartyIds: Array<Scalars['ID']['input']>;
  name?: InputMaybe<Scalars['String']['input']>;
  overridePrice?: InputMaybe<Scalars['Float']['input']>;
  productTypeId?: InputMaybe<Scalars['ID']['input']>;
  resolutionMode?: InputMaybe<PricingResolutionMode>;
  /** @deprecated ENG-2478: use validFromDay — the server anchors it in the organization's timezone. A raw instant is stored as-is with no day provenance. */
  validFrom?: InputMaybe<Scalars['AWSDateTime']['input']>;
  /**
   * ENG-2478. Calendar day for validFrom, e.g. from a date input. Supplying
   * both this field and its instant counterpart for the same bound is
   * rejected. When only the day is supplied the server anchors it in the
   * organization's timezone. When only the instant is supplied it is stored
   * as-is with a null day (the legacy path).
   */
  validFromDay?: InputMaybe<Scalars['AWSDate']['input']>;
  /** @deprecated ENG-2478: use validUntilDay — the server anchors it in the organization's timezone. A raw instant is stored as-is with no day provenance. */
  validUntil?: InputMaybe<Scalars['AWSDateTime']['input']>;
  /** ENG-2478. Calendar day for validUntil — same rule as validFromDay. */
  validUntilDay?: InputMaybe<Scalars['AWSDate']['input']>;
};

export type BulkApplyPartyPricingPayload = {
  __typename?: 'BulkApplyPartyPricingPayload';
  applied: Array<PartyPricingAgreement>;
  failedExternalPartyIds: Array<Scalars['ID']['output']>;
};

/** Per-CSV-column → ExternalParty-field mapping selected by the import wizard. */
export type BulkImportColumnMapInput = {
  /**
   * Column name from the CSV header row (display/error-message use only —
   * not unique, see csvColumnIndex).
   */
  csvColumn: Scalars['String']['input'];
  /**
   * 0-based position of this column in the CSV header row. BI-04 — the
   * authoritative key: two CSV columns MAY share the same header text (a real
   * PMS export with two "Email" columns), so `csvColumn` alone cannot
   * distinguish them. The resolver reads each row's value by this index, not
   * by name.
   */
  csvColumnIndex: Scalars['Int']['input'];
  /**
   * Target field on ExternalParty / ExternalPartyAddress.
   * Supported values: name, kind, slug, email, phone, phoneExtension,
   * parent.name (resolves by name against an existing party in this org, or
   * another row earlier in the same file, to preserve a parent-org
   * hierarchy), address.street1, address.street2, address.city,
   * address.region, address.postal_code, address.country, or a
   * tenant-defined custom field written to ExternalParty.customFields as
   * `custom.<key>` (e.g. `custom.preferred_milling_center`) — `<key>` must be
   * lowercase letters/digits/underscores, starting with a letter, <= 64
   * chars, and may not collide with a built-in field's storage key. Two
   * columns may not target the same field — see BulkImportRowError / the
   * mutation's own BadInput errors.
   */
  targetField: Scalars['String']['input'];
};

/**
 * Input for bulkImportExternalParties. `csvText` is the full CSV (first
 * row = headers). `columnMap` lists which CSV column maps to which target
 * field. `dryRun: true` returns the would-create preview without writing;
 * `dryRun: false` commits in a single transaction.
 */
export type BulkImportExternalPartiesInput = {
  columnMap: Array<BulkImportColumnMapInput>;
  csvText: Scalars['String']['input'];
  /**
   * Default value applied to every row's `kind` (party_type) when the
   * CSV doesn't carry one. Tenant-extensible; defaults to 'customer'.
   */
  defaultKind?: InputMaybe<Scalars['String']['input']>;
  dryRun: Scalars['Boolean']['input'];
};

export type BulkImportExternalPartiesResult = {
  __typename?: 'BulkImportExternalPartiesResult';
  dryRun: Scalars['Boolean']['output'];
  errors: Array<BulkImportRowError>;
  importedCount: Scalars['Int']['output'];
  preview: Array<BulkImportPreviewRow>;
  totalRows: Scalars['Int']['output'];
};

/**
 * Preview row from a bulk-import dry-run — the resolver shows the
 * caller what *would* be created without actually inserting anything.
 */
export type BulkImportPreviewRow = {
  __typename?: 'BulkImportPreviewRow';
  email?: Maybe<Scalars['String']['output']>;
  kind?: Maybe<Scalars['String']['output']>;
  name: Scalars['String']['output'];
  phone?: Maybe<Scalars['String']['output']>;
  raw?: Maybe<Scalars['AWSJSON']['output']>;
  rowIndex: Scalars['Int']['output'];
};

/** Row-level error from a bulk-import dry-run or commit. */
export type BulkImportRowError = {
  __typename?: 'BulkImportRowError';
  message: Scalars['String']['output'];
  rowIndex: Scalars['Int']['output'];
};

/** Aggregate result of a bulk invoice action. */
export type BulkInvoiceActionPayload = {
  __typename?: 'BulkInvoiceActionPayload';
  failureCount: Scalars['Int']['output'];
  results: Array<BulkInvoiceActionResult>;
  successCount: Scalars['Int']['output'];
};

/**
 * Per-invoice outcome of a bulk invoice action. The batch is NOT
 * all-or-nothing: each invoice reports its own success/failure so a mixed
 * selection (e.g. a void that hits an already-paid invoice) surfaces per row.
 */
export type BulkInvoiceActionResult = {
  __typename?: 'BulkInvoiceActionResult';
  /** Human-readable failure reason when success=false (null on success). */
  error?: Maybe<Scalars['String']['output']>;
  invoiceId: Scalars['ID']['output'];
  success: Scalars['Boolean']['output'];
};

/** Aggregate result of a bulk order action across the requested ids. */
export type BulkOrderActionPayload = {
  __typename?: 'BulkOrderActionPayload';
  failureCount: Scalars['Int']['output'];
  results: Array<BulkOrderActionResult>;
  successCount: Scalars['Int']['output'];
};

/**
 * Outcome of a bulk order action for ONE order id. Bulk actions are NOT
 * all-or-nothing — each order is applied under its own savepoint, so a failure
 * (permission denied for this order, invalid state, unknown id) is reported here
 * without aborting the rest of the batch.
 */
export type BulkOrderActionResult = {
  __typename?: 'BulkOrderActionResult';
  /** Human-readable failure reason when success=false (null on success). */
  error?: Maybe<Scalars['String']['output']>;
  /**
   * The updated order on success — lets the client refresh its cache in place
   * without a round-trip. Null when the action failed.
   */
  order?: Maybe<Order>;
  orderId: Scalars['ID']['output'];
  success: Scalars['Boolean']['output'];
};

/**
 * ENG-482 — the outcome for ONE SKU in a bulk publish. Exactly one of
 * `published: true` (with the updated `productType`) or `published: false`
 * (with a non-empty `reasons`) holds.
 */
export type BulkPublishProductTypeResult = {
  __typename?: 'BulkPublishProductTypeResult';
  /** The updated SKU when published; null when blocked. */
  productType?: Maybe<ProductType>;
  productTypeId: Scalars['ID']['output'];
  published: Scalars['Boolean']['output'];
  /**
   * Why this SKU was not published. Empty iff `published` is true. Uses the
   * SAME `InvalidReason` shape as `ProductType.validity.reasons`, so the admin UI
   * renders a bulk blocker and a detail-page blocker with one component. A SKU
   * that was not found, or was already LIVE, reports that as a reason rather than
   * an empty list.
   */
  reasons: Array<InvalidReason>;
};

/** ENG-482 — ids to publish in one bulk call. Max 200; duplicates collapsed. */
export type BulkPublishProductTypesInput = {
  ids: Array<Scalars['ID']['input']>;
};

/**
 * ENG-482 — partial-success result of bulkPublishProductTypes. A non-zero
 * `blockedCount` is NOT an error: the published SKUs are committed.
 */
export type BulkPublishProductTypesPayload = {
  __typename?: 'BulkPublishProductTypesPayload';
  blockedCount: Scalars['Int']['output'];
  publishedCount: Scalars['Int']['output'];
  results: Array<BulkPublishProductTypeResult>;
};

export type BulkSetLedgerMappingsResult = {
  __typename?: 'BulkSetLedgerMappingsResult';
  /** Mappings removed (a null `accountId` that matched a row). */
  clearedCount: Scalars['Int']['output'];
  /** Mappings created or changed. */
  setCount: Scalars['Int']['output'];
};

/**
 * Input for bulkVoidInvoices. One reason applies to the whole selection
 * (audit-log integrity, Bible §13).
 */
export type BulkVoidInvoicesInput = {
  invoiceIds: Array<Scalars['ID']['input']>;
  reason: Scalars['String']['input'];
};

/**
 * A user-authored CALCULATED (derived) analytics measure: a validated arithmetic
 * formula (`formula`, the ENG-1799 DerivedExpr AST, returned as a raw JSON object)
 * over already-selectable base measures. Org-wide catalog data owned by its author
 * (`ownerUserId`); org-isolated. Per ENG-1803.
 */
export type CalculatedMeasure = {
  __typename?: 'CalculatedMeasure';
  /**
   * When the measure was archived (soft-deleted), or null when it is live. An
   * archived measure is hidden from the authoring picker but STILL RESOLVES in
   * saved reports/dashboards that already reference it (ENG-2106).
   */
  archivedAt?: Maybe<Scalars['AWSDateTime']['output']>;
  createdAt: Scalars['AWSDateTime']['output'];
  /**
   * Optional human-authored note describing what the measure computes and why
   * (free text, staff-visible, not PHI). Null when the author supplied none.
   */
  description?: Maybe<Scalars['String']['output']>;
  /** Display format hint (lower-cased: number/percent/ratio/currency/duration). */
  format: Scalars['String']['output'];
  /**
   * The validated DerivedExpr AST (ENG-1799 grammar), returned as a raw JSON
   * object. Opaque to the client's transport; re-validated server-side on every use.
   */
  formula: Scalars['AWSJSON']['output'];
  id: Scalars['ID']['output'];
  /** Stable output key the measure is named by in a SemanticQuery (unique per org). */
  key: Scalars['String']['output'];
  label: Scalars['String']['output'];
  organizationId: Scalars['ID']['output'];
  /**
   * Author of the measure (provenance). Not an access predicate — a calculated
   * measure is org-wide catalog data, visible to every analytics viewer in the org.
   */
  ownerUserId: Scalars['ID']['output'];
  updatedAt?: Maybe<Scalars['AWSDateTime']['output']>;
};

/**
 * Result of deleteCalculatedMeasure — the row is gone. Mirrors
 * ProductTypeDeletedPayload / OrgUnitDeletedPayload.
 */
export type CalculatedMeasureDeletedPayload = {
  __typename?: 'CalculatedMeasureDeletedPayload';
  deletedId: Scalars['ID']['output'];
};

/**
 * Display format for a calculated measure's value (a rendering hint the builder
 * UI reads). Closed set, validated server-side.
 */
export enum CalculatedMeasureFormat {
  Currency = 'CURRENCY',
  Duration = 'DURATION',
  Number = 'NUMBER',
  Percent = 'PERCENT',
  Ratio = 'RATIO'
}

/** Identify a single calculated measure by id (ENG-2106 archive/restore/delete). */
export type CalculatedMeasureIdInput = {
  id: Scalars['ID']['input'];
};

export type CalculatedMeasurePayload = {
  __typename?: 'CalculatedMeasurePayload';
  calculatedMeasure: CalculatedMeasure;
};

/** Input for cancelOrder. */
export type CancelOrderInput = {
  orderId: Scalars['ID']['input'];
  reason: Scalars['String']['input'];
};

/**
 * Close out a wedged payment, releasing the reservation it holds on its
 * invoice.
 */
export type CancelPaymentAttemptInput = {
  /**
   * ⚠️ REQUIRED for a payment we hold NO provider transaction id for, and
   * REFUSED (`UnnecessaryAcknowledgement`) for one we do.
   *
   * It is NOT a force flag and it overrides nothing: a payment carrying a provider
   * reference is refused outright whatever this says, because the provider can
   * still answer for it and the operator should check first. What this field
   * covers is the opposite case — there is nobody to ask, cancelling ASSERTS that
   * nothing arrived, and the audit record should show that a named human made that
   * assertion rather than the system inferring it from silence. Making it invalid
   * everywhere else is what stops it becoming a checkbox ticked by reflex.
   */
  acknowledgeUnknownOutcome?: InputMaybe<Scalars['Boolean']['input']>;
  /** The lifecycle ROOT id. */
  attemptId: Scalars['ID']['input'];
  /**
   * Required. Recorded on the audit row as the basis for asserting that no
   * debit reached the bank — a stronger claim than voiding an invoice, so
   * "cancelled by operator" is not an acceptable answer.
   */
  reason: Scalars['String']['input'];
};

/**
 * ⚠️ DELIBERATELY DOES NOT RETURN THE UPDATED PAYMENT. `payment_attempts` is
 * append-only, so a cancel adds a row rather than changing one, and the collapsed
 * lifecycle, the invoice balance and the activity list all move with it. Returning
 * a partial snapshot would let a normalizing cache publish some of that and not the
 * rest. Refetch the lifecycle and the activity list.
 */
export type CancelPaymentAttemptPayload = {
  __typename?: 'CancelPaymentAttemptPayload';
  /**
   * True when the payment was ALREADY cancelled and this call wrote nothing.
   * A double-clicked cancel is idempotent, not an error, and it must not report
   * two cancellations of one act.
   */
  alreadyCanceled: Scalars['Boolean']['output'];
  attemptId: Scalars['ID']['output'];
  /** The invoice whose reservation was released. Refetch its balance. */
  invoiceId?: Maybe<Scalars['ID']['output']>;
};

export type CancelTenantInput = {
  /** Optional — when to initiate deprovisioning. Default: now. */
  effectiveAt?: InputMaybe<Scalars['AWSDateTime']['input']>;
  id: Scalars['ID']['input'];
  /** Required for zero-retention cleanup: `DELETE <tenant id>`. */
  immediateDeleteConfirmation?: InputMaybe<Scalars['String']['input']>;
  reason: Scalars['String']['input'];
  /** Optional — retention window for backups (default 30 days). */
  retentionDays?: InputMaybe<Scalars['Int']['input']>;
};

/** On-top card surcharge setting for the organization (ENG-3881). */
export type CardSurchargeConfig = {
  __typename?: 'CardSurchargeConfig';
  /** True only when the surcharge switch is on and a rate is configured. */
  enabled: Scalars['Boolean']['output'];
  /** Rate in basis points (300 = 3.00%). Null unless `enabled`. */
  rateBps?: Maybe<Scalars['Int']['output']>;
};

/**
 * Practitioner-scoped case-health aggregate: counts of the CALLER's own in-flight
 * cases by lifecycle bucket, plus the 30-day seated outcome. Server-computed and
 * org- + party-scoped (orders RLS + customerOrderScopePredicate). ENG-1468.
 */
export type CaseHealthSummary = {
  __typename?: 'CaseHealthSummary';
  asOf: Scalars['AWSDateTime']['output'];
  inDesign: Scalars['Int']['output'];
  inProduction: Scalars['Int']['output'];
  inQc: Scalars['Int']['output'];
  seatedLast30d: Scalars['Int']['output'];
  seatedOnTimePct?: Maybe<Scalars['Float']['output']>;
  seatedPrior30d?: Maybe<Scalars['Int']['output']>;
  shipped: Scalars['Int']['output'];
  totalInFlight: Scalars['Int']['output'];
};

/**
 * ENG-3118 — the case subject of a migrated order: the recorded name of the
 * person the case is for (protected data). Names only, both nullable as
 * recorded. Deliberately no `id` and no encrypted field; see `Order.caseSubject`.
 */
export type CaseSubject = {
  __typename?: 'CaseSubject';
  familyName?: Maybe<Scalars['String']['output']>;
  givenName?: Maybe<Scalars['String']['output']>;
};

/**
 * ENG-2101 — bounded, staff-only product catalog page. Unlike the legacy
 * `productTypes` bare list, this connection reports the complete matching count
 * while returning at most one page of orderable products.
 */
export type CatalogProductTypeConnection = {
  __typename?: 'CatalogProductTypeConnection';
  edges: Array<CatalogProductTypeEdge>;
  /** True when the organization has any product type in any lifecycle state. */
  hasAnyProducts: Scalars['Boolean']['output'];
  pageInfo: PageInfo;
  totalCount: Scalars['Int']['output'];
};

export type CatalogProductTypeEdge = {
  __typename?: 'CatalogProductTypeEdge';
  cursor: Scalars['String']['output'];
  node: ProductType;
};

/**
 * Round 6 — full taxonomy payload for the catalog browser. The whole
 * tree fits in one query; tenants typically have <50 nodes.
 */
export type CatalogTaxonomy = {
  __typename?: 'CatalogTaxonomy';
  nodes: Array<CatalogTaxonomyNode>;
};

/**
 * Round 6 — one node in the tenant catalog taxonomy. Industry-neutral
 * N-level hierarchy. parentId NULL = top-level. productTypeCodes pins
 * specific products to this category.
 */
export type CatalogTaxonomyNode = {
  __typename?: 'CatalogTaxonomyNode';
  id: Scalars['ID']['output'];
  name: Scalars['String']['output'];
  parentId?: Maybe<Scalars['ID']['output']>;
  position: Scalars['Int']['output'];
  productTypeCodes: Array<Scalars['String']['output']>;
};

/** Round 6 — input row for upsertCatalogTaxonomy (full replacement). */
export type CatalogTaxonomyNodeInput = {
  id: Scalars['ID']['input'];
  name: Scalars['String']['input'];
  parentId?: InputMaybe<Scalars['ID']['input']>;
  position: Scalars['Int']['input'];
  productTypeCodes?: InputMaybe<Array<Scalars['String']['input']>>;
};

/** Catalog of certifications a lab tracks (ENG-148). Backing table `certification_definitions`. */
export type CertificationDefinition = {
  __typename?: 'CertificationDefinition';
  id: Scalars['ID']['output'];
  issuer: Scalars['String']['output'];
  name: Scalars['String']['output'];
};

export type CertificationDefinitionDeletedPayload = {
  __typename?: 'CertificationDefinitionDeletedPayload';
  deletedId: Scalars['ID']['output'];
};

export type CertificationDefinitionPayload = {
  __typename?: 'CertificationDefinitionPayload';
  certificationDefinition: CertificationDefinition;
};

/** Input for changeUserRole (ENG-3498). */
export type ChangeUserRoleInput = {
  /** Free-text reason, persisted on the role_assignment_audit rows. */
  reason?: InputMaybe<Scalars['String']['input']>;
  /** The user's new (single) role. */
  roleId: Scalars['ID']['input'];
  userId: Scalars['ID']['input'];
};

/**
 * Payload for changeUserRole: every assignment the user now holds — one role,
 * one row per org unit it applies in (or one tenant-wide row).
 */
export type ChangeUserRolePayload = {
  __typename?: 'ChangeUserRolePayload';
  userRoles: Array<UserRole>;
};

/**
 * ENG-1422 — WHY a rework landed on its disposition. Drives the customer-facing
 * copy, so the portal never has to guess (or hardcode) the reason.
 */
export enum ChargeDispositionReason {
  /** Waived because the original case is still inside its warranty window. */
  InWarranty = 'IN_WARRANTY',
  /**
   * Chargeable because the original case's warranty window has closed (or the
   * case carries no warranty date at all).
   */
  OutOfWarranty = 'OUT_OF_WARRANTY',
  /** The lab always charges for this class of rework. */
  PolicyBillable = 'POLICY_BILLABLE',
  /** The lab never charges for this class of rework. */
  PolicyFree = 'POLICY_FREE'
}

export type ChatAssistantResult = {
  __typename?: 'ChatAssistantResult';
  assistantReply: Scalars['String']['output'];
  conversationId: Scalars['ID']['output'];
  suggestedYamlDiff?: Maybe<Array<YamlDiffBlock>>;
  tokensUsed: TokenUsage;
};

export type ChatWithConfigAssistantInput = {
  conversationId?: InputMaybe<Scalars['ID']['input']>;
  currentConfigDocumentId?: InputMaybe<Scalars['ID']['input']>;
  message: Scalars['String']['input'];
};

/**
 * Who last recorded a step attempt's checklist. See
 * StepRun.checklistAuthorship — identity and timestamp only, never answers.
 */
export type ChecklistAuthorship = {
  __typename?: 'ChecklistAuthorship';
  email?: Maybe<Scalars['String']['output']>;
  fullName?: Maybe<Scalars['String']['output']>;
  /** When the checklist was last written for this attempt. */
  updatedAt?: Maybe<Scalars['AWSDateTime']['output']>;
  /**
   * Null when the author's user row has since been deleted: 0085 nulls
   * `updated_by_user_id` on delete, so the timestamp outlives the identity. An
   * edit whose author is gone is still an edit, and reporting the time with no
   * name is more honest than reporting nothing.
   */
  userId?: Maybe<Scalars['ID']['output']>;
};

/**
 * ENG-3844 — the files a checklist file field may pick from (see
 * StepRun.checklistFileCandidates).
 */
export type ChecklistFileCandidates = {
  __typename?: 'ChecklistFileCandidates';
  /** Current files of that order, newest first (at most 200). */
  files: Array<File>;
  /**
   * The step's owning order. An upload made from the checklist attaches here, so
   * the upload and the validator agree on the order.
   */
  orderId: Scalars['ID']['output'];
};

/**
 * Approve a device's pending pairing and cast one of the caller's dashboards to
 * it. The plaintext pairingCode is shown on the device screen; it is hashed to
 * read-and-burn the device's pre-approval artifact and stored (hashed) as the
 * single-use guard.
 */
export type ClaimProjectionPairingInput = {
  /** The capability ceiling to freeze onto the grant (non-empty). */
  capsAllowlist: Array<Scalars['String']['input']>;
  dashboardId: Scalars['ID']['input'];
  /** Lease length in hours (default 12, max 168). */
  leaseHours?: InputMaybe<Scalars['Int']['input']>;
  /** The plaintext pairing code shown on the device screen. */
  pairingCode: Scalars['String']['input'];
};

export type ClaimProjectionPairingPayload = {
  __typename?: 'ClaimProjectionPairingPayload';
  grant: ProjectionGrant;
};

export type ClearScannerIntakeIdentityMappingInput = {
  ingestId: Scalars['ID']['input'];
};

export type ClearScannerIntakeIdentityMappingPayload = {
  __typename?: 'ClearScannerIntakeIdentityMappingPayload';
  affectedCaseCount: Scalars['Int']['output'];
  mappingId: Scalars['ID']['output'];
};

/**
 * Per-customer (clinic / DSO / doctor) operational analytics — REAL numbers
 * computed from that customer's orders + the append-only step_runs record.
 * Volume, on-time %, rework rate and lead time are measured over the orders the
 * customer CREATED within the requested window; `activeOrderCount` is
 * point-in-time WIP (independent of the window). A customer with no orders in
 * the window returns an all-zero payload (empty state), never an error.
 */
export type ClientAnalytics = {
  __typename?: 'ClientAnalytics';
  /**
   * This customer's orders currently in-flight (not terminal) as of now —
   * point-in-time, independent of the range window.
   */
  activeOrderCount: Scalars['Int']['output'];
  /** Average lead time (created -> done) in hours over completed orders. */
  avgLeadTimeHours: Scalars['Float']['output'];
  /** Of those, how many reached a done state (delivered/seated/closed). */
  completedCount: Scalars['Int']['output'];
  /** Median (p50) lead time in hours over completed orders. */
  medianLeadTimeHours: Scalars['Float']['output'];
  /** Completed orders delivered on/before their ETA. */
  onTimeCount: Scalars['Int']['output'];
  /** Completed orders that carried an ETA (the on-time denominator). */
  onTimeEligibleCount: Scalars['Int']['output'];
  /** onTimeCount / onTimeEligibleCount (0 when no eligible orders). */
  onTimeRate: Scalars['Float']['output'];
  /** Orders this customer created in the window (volume denominator). */
  orderCount: Scalars['Int']['output'];
  /** external_parties.id of the customer party. */
  partyId: Scalars['ID']['output'];
  /** Display name (external_parties.name), null if the party was deleted. */
  partyName?: Maybe<Scalars['String']['output']>;
  /** Orders that hit a failed/loopback QC step (a remake/rework). */
  reworkOrderCount: Scalars['Int']['output'];
  /** reworkOrderCount / orderCount (0 when no orders). */
  reworkRate: Scalars['Float']['output'];
};

/**
 * Input for cloneRole (ENG-841). Copies a source role's EFFECTIVE
 * permission set into a NEW is_system=false role the tenant can then edit.
 * Only permission keys the CALLER themselves hold are copied (RBAC delegation
 * boundary); the source role is never mutated. `name` defaults to
 * `"<source name> (copy)"`, auto-suffixed to the first free slot in the tenant;
 * `description` defaults to the source's.
 */
export type CloneRoleInput = {
  /** Optional description; defaults to the source role's. */
  description?: InputMaybe<Scalars['String']['input']>;
  /**
   * Optional name for the new role. Honored as-is (Conflict if already taken);
   * when omitted, `"<source name> (copy)"` is derived and auto-suffixed.
   */
  name?: InputMaybe<Scalars['String']['input']>;
  /**
   * Role to clone. Must belong to the caller's tenant (typically a seeded
   * is_system=true role); cross-tenant ids resolve as NotFound.
   */
  sourceRoleId: Scalars['ID']['input'];
};

/** Input for closeOrder. */
export type CloseOrderInput = {
  closedAt?: InputMaybe<Scalars['AWSDateTime']['input']>;
  notes?: InputMaybe<Scalars['String']['input']>;
  orderId: Scalars['ID']['input'];
};

export type CloseTicketInput = {
  reason?: InputMaybe<Scalars['String']['input']>;
  ticketId: Scalars['ID']['input'];
};

/**
 * Polymorphic comment (order/order_product/step_run/file).
 * Per docs/clearpath-architecture/03-data-model.md tier 3 (`comments`).
 */
export type Comment = {
  __typename?: 'Comment';
  /**
   * File attachments on this comment, ordered by composer position. Each
   * attachment is a regular File row — clicking surfaces the same signed-URL
   * download path as the Files tab. Empty list when the comment is text-only.
   */
  attachments: Array<File>;
  author?: Maybe<User>;
  authorUserId: Scalars['ID']['output'];
  body: Scalars['String']['output'];
  category?: Maybe<CommentCategory>;
  /**
   * Tenant-defined classification (intake / qc / billing / etc.). Optional;
   * legacy comments and casual replies have null category. Drives the
   * category chip on the thread + Notes-tab filter.
   */
  categoryId?: Maybe<Scalars['ID']['output']>;
  commentableId: Scalars['ID']['output'];
  /** What this comment is attached to. */
  commentableType: CommentableType;
  createdAt: Scalars['AWSDateTime']['output'];
  deletedAt?: Maybe<Scalars['AWSDateTime']['output']>;
  editedAt?: Maybe<Scalars['AWSDateTime']['output']>;
  id: Scalars['ID']['output'];
  /** Internal-only (not visible to portal users / vendors). */
  isInternal: Scalars['Boolean']['output'];
  /** Mentioned user IDs. */
  mentionedUserIds: Array<Scalars['ID']['output']>;
  organizationId: Scalars['ID']['output'];
  /** Structured, server-validated link for an Orovia annotation save. Null for ordinary comments. */
  oroviaReview?: Maybe<OroviaReviewCommentReference>;
  parent?: Maybe<Comment>;
  /** Reply parent (null for root comments). */
  parentCommentId?: Maybe<Scalars['ID']['output']>;
  /** Replies to this comment. */
  replies: CommentConnection;
  tenantId: Scalars['ID']['output'];
};


/**
 * Polymorphic comment (order/order_product/step_run/file).
 * Per docs/clearpath-architecture/03-data-model.md tier 3 (`comments`).
 */
export type CommentRepliesArgs = {
  page?: InputMaybe<PageInput>;
};

/**
 * Tenant-scoped comment classification — admins manage the list at
 * /admin/comment-categories. Used to tint the category chip on the
 * thread and to power the Notes-tab filter.
 */
export type CommentCategory = {
  __typename?: 'CommentCategory';
  /** Hex color used to tint the chip (e.g. "#1d4ed8"). Default slate-500. */
  color: Scalars['String']['output'];
  createdAt: Scalars['AWSDateTime']['output'];
  customFields?: Maybe<Scalars['AWSJSON']['output']>;
  id: Scalars['ID']['output'];
  name: Scalars['String']['output'];
  organizationId: Scalars['ID']['output'];
  /** Sort order within the dropdown / filter; lower = earlier. */
  sortOrder: Scalars['Int']['output'];
  tenantId: Scalars['ID']['output'];
  updatedAt?: Maybe<Scalars['AWSDateTime']['output']>;
};

export type CommentCategoryDeletedPayload = {
  __typename?: 'CommentCategoryDeletedPayload';
  deletedId: Scalars['ID']['output'];
};

export type CommentCategoryPayload = {
  __typename?: 'CommentCategoryPayload';
  category: CommentCategory;
};

export type CommentConnection = {
  __typename?: 'CommentConnection';
  /**
   * ENG-1828 — true when the viewer is NOT permitted to read this thread, so an
   * empty `edges` means "refused", not "nothing here". Null/false everywhere
   * else, including a thread that is genuinely empty and a slot a tenant has
   * hidden (portal view config): hiding is meant to be indistinguishable from
   * absence, a denial is not.
   */
  accessDenied?: Maybe<Scalars['Boolean']['output']>;
  edges: Array<CommentEdge>;
  pageInfo: PageInfo;
  totalCount: Scalars['Int']['output'];
};

export type CommentEdge = {
  __typename?: 'CommentEdge';
  cursor: Scalars['String']['output'];
  node: Comment;
};

/** Comment mutation payload. */
export type CommentPayload = {
  __typename?: 'CommentPayload';
  comment: Comment;
};

/** What a comment can be attached to. */
export enum CommentableType {
  FederationLink = 'FEDERATION_LINK',
  File = 'FILE',
  Order = 'ORDER',
  OrderProduct = 'ORDER_PRODUCT',
  PipelineRun = 'PIPELINE_RUN',
  StepRun = 'STEP_RUN',
  Ticket = 'TICKET'
}

export type CommitTaxImportInput = {
  columnMap: TaxImportColumnMapInput;
  country: Scalars['String']['input'];
  /**
   * The raw file text. The SERVER is the parser of record — the browser parses
   * only the header row, for the mapping step, with the same parser.
   */
  csvText: Scalars['String']['input'];
  /** The digest returned by `taxImportPreview` for this exact file. */
  digest: Scalars['String']['input'];
  /**
   * Required: the file carries no effective date of its own. May be in the
   * future — that is how a quarterly rate change is loaded ahead of time and lies
   * dormant until it takes over.
   */
  effectiveFrom: Scalars['AWSDate']['input'];
  /**
   * Every row in one import shares a region; it is what the fallback rate and
   * the category set are resolved from.
   */
  regionCode: Scalars['String']['input'];
  /**
   * The retained upload, when there was one. Artifact retention, not a scan
   * gate — parsing does not wait on the verdict.
   */
  sourceFileId?: InputMaybe<Scalars['ID']['input']>;
  sourceFilename: Scalars['String']['input'];
};

export type CompleteStepRunInput = {
  customFields?: InputMaybe<Scalars['AWSJSON']['input']>;
  /**
   * ENG-18 — material + lot consumed at this step.
   * @deprecated ENG-1689: superseded by materialLots (per-configured-item capture driven by pipeline_step_consumed_inventory). Ignored: no longer read or written.
   */
  materialConsumption?: InputMaybe<MaterialConsumptionInput>;
  /**
   * ENG-1689 — per-configured-item material lots captured at this step's
   * completion. REQUIRED (all-or-nothing) when the step definition has one or more
   * configured consumed-inventory items (pipeline_step_consumed_inventory): every
   * configured item must carry a non-blank lotNumber, and only configured items may
   * be named. Ignored when the step has no configured items. Captured into
   * step_run_material_lots for lot traceability / recall.
   */
  materialLots?: InputMaybe<Array<MaterialLotInput>>;
  notes?: InputMaybe<Scalars['String']['input']>;
  outcome?: InputMaybe<StepRunOutcome>;
  outputFiles?: InputMaybe<Array<Scalars['ID']['input']>>;
  /**
   * ENG-2416 — what happens to the money when a step that ALREADY has
   * price-modifier charges is completed again after a rework.
   *
   * There is no pre-flight field to consult: the condition depends on what THIS
   * attempt's answers resolve to against the bound form versions, which only the
   * server knows. Instead completeStepRun REFUSES with extensions.code
   * `StepChargeDecisionRequired`, carrying `priorCharges` (each with its field
   * label, option value, amount, attempt number, and whether it is already
   * invoiced); the client asks the completer and RETRIES the same completion with
   * this field set. Omitting it is that refusal rather than a silent default,
   * because every possible default is wrong for somebody: charging work that was
   * scrapped, or dropping work that was done. A first completion needs no
   * decision — it simply charges.
   */
  stepChargeDecision?: InputMaybe<StepChargeDecision>;
  stepRunId: Scalars['ID']['input'];
};

export type ConfigureIteroCredentialsInput = {
  clientId: Scalars['String']['input'];
  clientSecret: Scalars['String']['input'];
  operationId: Scalars['String']['input'];
};

export type ConfigureIteroCredentialsPayload = {
  __typename?: 'ConfigureIteroCredentialsPayload';
  configured: Scalars['Boolean']['output'];
  configuredAt: Scalars['AWSDateTime']['output'];
  replaced: Scalars['Boolean']['output'];
};

export type ConfigureMeditCredentialsInput = {
  clientId: Scalars['String']['input'];
  clientSecret: Scalars['String']['input'];
  operationId: Scalars['String']['input'];
};

export type ConfigureMeditCredentialsPayload = {
  __typename?: 'ConfigureMeditCredentialsPayload';
  configured: Scalars['Boolean']['output'];
  configuredAt: Scalars['AWSDateTime']['output'];
  integrationId: Scalars['ID']['output'];
  replaced: Scalars['Boolean']['output'];
};

/** ENG-3842 — input for Mutation.configureNasStation (the whole config). */
export type ConfigureNasStationInput = {
  assignmentMode: NasStationAssignmentMode;
  /**
   * Null = the organization's format policy; otherwise at least one known,
   * allowed format key.
   */
  formatOverride?: InputMaybe<Array<Scalars['String']['input']>>;
  includeSubUnits: Scalars['Boolean']['input'];
  /** Null = unconfigured. Required for ASSIGNED_USERS. */
  orgUnitId?: InputMaybe<Scalars['ID']['input']>;
  paused: Scalars['Boolean']['input'];
  /** The station (NasAgent.id). */
  stationId: Scalars['ID']['input'];
  /**
   * ASSIGNED_USERS: 1 to 100 internal users of the organization. UNIT_POOL:
   * must be empty.
   */
  userIds: Array<Scalars['ID']['input']>;
};

/** ENG-3842 — payload for Mutation.configureNasStation. */
export type ConfigureNasStationPayload = {
  __typename?: 'ConfigureNasStationPayload';
  agent: NasAgent;
  warnings: Array<NasStationConfigWarning>;
};

export type ConfigureThreeShapeCredentialsInput = {
  clientId: Scalars['String']['input'];
  operationId: Scalars['String']['input'];
};

export type ConfigureThreeShapeCredentialsPayload = {
  __typename?: 'ConfigureThreeShapeCredentialsPayload';
  configured: Scalars['Boolean']['output'];
  configuredAt: Scalars['AWSDateTime']['output'];
  integrationId: Scalars['ID']['output'];
  replaced: Scalars['Boolean']['output'];
};

/**
 * Auth shape for connectIntegration. Exactly one of apiKey, oauthClientCredentials,
 * or oauthAuthorizationCode sub-inputs should be populated.
 */
export type ConnectIntegrationInput = {
  /** API-key auth payload. */
  apiKey?: InputMaybe<Scalars['String']['input']>;
  /** Provider-specific config (apiBaseUrl, clinicId, material codes, ...). */
  config: Scalars['AWSJSON']['input'];
  /** Which hardware kind this connection belongs to. */
  kind: HardwareIntegrationKind;
  /** Human-friendly label shown in the Portal. */
  name: Scalars['String']['input'];
  /** OAuth authorization-code flow. */
  oauthAuthorizationCode?: InputMaybe<Scalars['String']['input']>;
  /** OAuth client-credentials auth. */
  oauthClientId?: InputMaybe<Scalars['String']['input']>;
  oauthClientSecret?: InputMaybe<Scalars['String']['input']>;
  oauthRedirectUri?: InputMaybe<Scalars['String']['input']>;
  /**
   * Provider id — must match a registered Phase 7 adapter:
   * `3shape`, `itero`, `medit`, `formlabs`, `carbon`, `envisiontec`, `roland`, `vhf`.
   */
  provider: Scalars['String']['input'];
};

/** When a pipeline step's consumption rule fires. */
export enum ConsumptionTriggerMode {
  /** When the step completes (and, per consumeOnFailure, when it fails). */
  OnComplete = 'ON_COMPLETE',
  /** When an operator scans the item against the order. */
  OnScanPick = 'ON_SCAN_PICK'
}

/** Create a dashboard owned by the authenticated caller. */
export type CreateAnalyticsDashboardInput = {
  /** Optional dashboard definition; defaults to an empty object. */
  definition?: InputMaybe<Scalars['AWSJSON']['input']>;
  name: Scalars['String']['input'];
};

/**
 * Add a widget to a dashboard the caller can edit (its owner OR a same-org
 * editor-sharee, T6/ENG-2117). `organizationId` is derived server-side from the
 * parent dashboard — never client input.
 */
export type CreateAnalyticsWidgetInput = {
  /** Optional widget config; defaults to an empty object. */
  config?: InputMaybe<Scalars['AWSJSON']['input']>;
  dashboardId: Scalars['ID']['input'];
  /** Optional display position; defaults to 0. */
  position?: InputMaybe<Scalars['Int']['input']>;
};

/** Input for Mutation.createApiKey. */
export type CreateApiKeyInput = {
  /** Optional absolute expiry. Null = non-expiring. */
  expiresAt?: InputMaybe<Scalars['AWSDateTime']['input']>;
  /** Human-readable label — unique per (organisation, serviceAccount). */
  name: Scalars['String']['input'];
  /** Dotted scope strings the key may exercise (e.g. ["clearpath.public.order.submit"]). */
  scopes: Array<Scalars['String']['input']>;
  /** Service account this key belongs to. */
  serviceAccountId: Scalars['ID']['input'];
};

/**
 * Payload for Mutation.createApiKey. `rawKey` is surfaced ONCE — callers MUST
 * save it client-side; it is not retrievable from subsequent queries.
 */
export type CreateApiKeyPayload = {
  __typename?: 'CreateApiKeyPayload';
  /** ISO-8601 timestamp of creation. */
  createdAt: Scalars['AWSDateTime']['output'];
  /** Server-assigned id. Pairs with `ApiKey.id`. */
  id: Scalars['ID']['output'];
  /** First 16 characters of the key — safe to log / display indefinitely. */
  keyPrefix: Scalars['String']['output'];
  /** The full plaintext key `cp_live_…`. Surfaced exactly once. Treat as a secret. */
  rawKey: Scalars['String']['output'];
};

/** Input for createBillingAdjustment. MOL-620. */
export type CreateBillingAdjustmentInput = {
  amount: Scalars['String']['input'];
  approvedByUserId?: InputMaybe<Scalars['ID']['input']>;
  currency: Scalars['String']['input'];
  reason: Scalars['String']['input'];
  relatedBillingEventId?: InputMaybe<Scalars['ID']['input']>;
  type: BillingEventType;
};

export type CreateBillingAdjustmentPayload = {
  __typename?: 'CreateBillingAdjustmentPayload';
  adjustmentId: Scalars['ID']['output'];
};

/**
 * Create a calculated measure owned by the authenticated caller. `formula` is a
 * TYPED TOKEN ARRAY (measure chip | + - * / | number | parens) — NOT free text — the
 * server parses through the ENG-1799 grammar fail-closed. `format` defaults to
 * NUMBER.
 */
export type CreateCalculatedMeasureInput = {
  /**
   * Optional human-authored note (free text, staff-visible, not PHI). Bounded
   * server-side; blank/whitespace is stored as null.
   */
  description?: InputMaybe<Scalars['String']['input']>;
  format?: InputMaybe<CalculatedMeasureFormat>;
  /**
   * The grammar token array (AWSJSON): a list of { kind, ... } tokens. Validated
   * fail-closed against the caller's selectable base catalog.
   */
  formula: Scalars['AWSJSON']['input'];
  key: Scalars['String']['input'];
  label: Scalars['String']['input'];
};

export type CreateCertificationDefinitionInput = {
  issuer: Scalars['String']['input'];
  name: Scalars['String']['input'];
};

/** Input for createComment. */
export type CreateCommentInput = {
  /**
   * File ids to attach in order. Each must already exist (uploaded via
   * the standard requestFileUpload + registerUploadedFile flow). Order is
   * preserved on the rendered thread.
   */
  attachedFileIds?: InputMaybe<Array<Scalars['ID']['input']>>;
  body: Scalars['String']['input'];
  categoryId?: InputMaybe<Scalars['ID']['input']>;
  commentableId: Scalars['ID']['input'];
  commentableType: CommentableType;
  isInternal?: InputMaybe<Scalars['Boolean']['input']>;
  mentionedUserIds?: InputMaybe<Array<Scalars['ID']['input']>>;
  parentCommentId?: InputMaybe<Scalars['ID']['input']>;
};

/** Input for createCustomerDelegation. */
export type CreateCustomerDelegationInput = {
  /** Doctor party the delegate will act on behalf of. */
  doctorPartyId: Scalars['ID']['input'];
  /** Optional expiry (UTC). Omit for non-expiring delegation. */
  expiresAt?: InputMaybe<Scalars['AWSDateTime']['input']>;
  /** Optional Cedar scope overrides — JSONB merged into custom_fields. */
  scope?: InputMaybe<Scalars['AWSJSON']['input']>;
  /** Clinic staff user receiving the grant. */
  staffUserId: Scalars['ID']['input'];
};

/** Input for createEscalationChain. */
export type CreateEscalationChainInput = {
  code: Scalars['String']['input'];
  isActive?: InputMaybe<Scalars['Boolean']['input']>;
  name: Scalars['String']['input'];
  steps: Array<EscalationChainStepInput>;
  targetStepDefinitionId?: InputMaybe<Scalars['ID']['input']>;
};

/** Input for createFeatureFlag. */
export type CreateFeatureFlagInput = {
  /** Default value, serialized as JSON (matches flagType). */
  defaultValue: Scalars['AWSJSON']['input'];
  description?: InputMaybe<Scalars['String']['input']>;
  flagType: FlagType;
  key: Scalars['String']['input'];
  name: Scalars['String']['input'];
  productId?: InputMaybe<Scalars['ID']['input']>;
};

/** Input for createFederationLink. */
export type CreateFederationLinkInput = {
  allowedWorkTypes: Array<Scalars['String']['input']>;
  direction: FederationDirection;
  eventVisibilityConfig?: InputMaybe<Scalars['AWSJSON']['input']>;
  label: Scalars['String']['input'];
  organizationId: Scalars['ID']['input'];
  partnerOrganizationId?: InputMaybe<Scalars['ID']['input']>;
  partnerPublicKey?: InputMaybe<Scalars['String']['input']>;
  partnerTenantId: Scalars['ID']['input'];
};

/**
 * Input for createHoldReasonCode (ENG-687 — hold-reason taxonomy admin).
 *
 * `code` is the stable neutral SCREAMING_SNAKE machine key, unique per
 * organization. `label` is the tenant-authored display string. `excludesFromSla`
 * defaults to true for CUSTOMER/VENDOR reasons and false for INTERNAL ones when
 * omitted (a lab cannot pause its own SLA for its own blocker); `position` appends
 * to the end of the picker order when omitted.
 */
export type CreateHoldReasonCodeInput = {
  code: Scalars['String']['input'];
  /**
   * The customer-safe portal message, distinct from the internal `label`. Only
   * meaningful when `isCustomerVisible` is true; omit or null to fall back to
   * generic copy at the display layer.
   */
  customerFacingText?: InputMaybe<Scalars['String']['input']>;
  description?: InputMaybe<Scalars['String']['input']>;
  excludesFromSla?: InputMaybe<Scalars['Boolean']['input']>;
  /**
   * Whether holds under this reason are shown to the customer on the portal.
   * Defaults false when omitted.
   */
  isCustomerVisible?: InputMaybe<Scalars['Boolean']['input']>;
  label: Scalars['String']['input'];
  position?: InputMaybe<Scalars['Int']['input']>;
  responsibleParty: HoldResponsibleParty;
};

export type CreateInventoryItemInput = {
  /** SKU/material code. Unique per organization. */
  code: Scalars['String']['input'];
  customFields?: InputMaybe<Scalars['AWSJSON']['input']>;
  description?: InputMaybe<Scalars['String']['input']>;
  isActive?: InputMaybe<Scalars['Boolean']['input']>;
  name: Scalars['String']['input'];
  productTypeId?: InputMaybe<Scalars['ID']['input']>;
  /** Reorder threshold. Null means this item never raises a low-stock alert. */
  reorderPoint?: InputMaybe<Scalars['String']['input']>;
  storageLocation?: InputMaybe<Scalars['String']['input']>;
  unitOfMeasure: Scalars['String']['input'];
};

export type CreateMachineInput = {
  name: Scalars['String']['input'];
};

export type CreateNasRolloutInput = {
  arch: Scalars['String']['input'];
  canaryPercent?: InputMaybe<Scalars['Int']['input']>;
  channel?: InputMaybe<Scalars['String']['input']>;
  platform: Scalars['String']['input'];
  targetReleaseId?: InputMaybe<Scalars['ID']['input']>;
  targetVersion?: InputMaybe<Scalars['String']['input']>;
};

/** Input for createNotification — system-only (Lambda + IAM). */
export type CreateNotificationInput = {
  aggregateId?: InputMaybe<Scalars['ID']['input']>;
  aggregateType?: InputMaybe<Scalars['String']['input']>;
  body: Scalars['String']['input'];
  channels?: InputMaybe<Array<NotificationChannel>>;
  notificationType: Scalars['String']['input'];
  organizationId: Scalars['ID']['input'];
  payload?: InputMaybe<Scalars['AWSJSON']['input']>;
  severity?: InputMaybe<NotificationSeverity>;
  subject: Scalars['String']['input'];
  userId: Scalars['ID']['input'];
};

export type CreateOroviaGuestPreviewLinkInput = {
  expiresIn: OroviaGuestPreviewExpiry;
  fileId: Scalars['ID']['input'];
};

/** The raw token is returned once and must not be persisted by clients. */
export type CreateOroviaGuestPreviewLinkPayload = {
  __typename?: 'CreateOroviaGuestPreviewLinkPayload';
  link: OroviaGuestPreviewLink;
  token: Scalars['String']['output'];
};

/**
 * Input for createPriceBook. `organizationId` is NOT accepted — the book is
 * always created in the caller's own Org (`ctx.organizationId`), matching
 * GrantPartyCatalogAccessInput rather than the older per-party pricing inputs.
 * `code` is server-synthesized from `name` and is not settable here.
 */
export type CreatePriceBookInput = {
  currency: Scalars['String']['input'];
  description?: InputMaybe<Scalars['String']['input']>;
  /**
   * Whether this book should become the Org's default catalog. Ignored
   * (forced true) for the very first book in an Org — a non-default book has
   * no effect on order pricing, so a lab's first catalog must always price.
   */
  isDefault?: InputMaybe<Scalars['Boolean']['input']>;
  name: Scalars['String']['input'];
};

/** Input for createRole. */
export type CreateRoleInput = {
  /**
   * Which users.user_type may hold the new role. Defaults to STAFF server-side.
   * Must equal the parent role's audience when parentRoleId is set.
   */
  audience?: InputMaybe<RoleAudience>;
  description?: InputMaybe<Scalars['String']['input']>;
  /**
   * Permission keys to seed the new role with. Each key must exist in
   * the viewer's `permission_registry_cache`; the resolver inserts one
   * role_permissions row per key in the same transaction as the role.
   */
  initialPermissionKeys?: InputMaybe<Array<Scalars['String']['input']>>;
  name: Scalars['String']['input'];
  parentRoleId?: InputMaybe<Scalars['ID']['input']>;
};

/** Input for Phase 6 createShareLink. */
export type CreateShareLinkInput = {
  /** Non-empty subset of ShareLinkAction values. */
  allowedActions: Array<ShareLinkAction>;
  /** Optional expiry; clamped server-side to <= now + 7 days. */
  expiresAt?: InputMaybe<Scalars['AWSDateTime']['input']>;
  /** Optional human label. */
  label?: InputMaybe<Scalars['String']['input']>;
  /** Optional usage cap; null = unlimited until expiry / revoke. */
  maxUses?: InputMaybe<Scalars['Int']['input']>;
  /** Vendor contact the link is issued to — surfaced in audit + UI. */
  recipientEmail: Scalars['AWSEmail']['input'];
  /** UUID of the target Order or StepRun. */
  resourceId: Scalars['ID']['input'];
  /** What the link grants access to. */
  resourceType: ShareLinkResourceType;
};

/** Input for createSharedAccessLink. */
export type CreateSharedAccessLinkInput = {
  expiresAt?: InputMaybe<Scalars['AWSDateTime']['input']>;
  label: Scalars['String']['input'];
  maxUses?: InputMaybe<Scalars['Int']['input']>;
  organizationId: Scalars['ID']['input'];
  permissions: Scalars['AWSJSON']['input'];
  scope: Scalars['AWSJSON']['input'];
};

/** Payload for createSharedAccessLink — JWT returned ONCE at creation. */
export type CreateSharedAccessLinkPayload = {
  __typename?: 'CreateSharedAccessLinkPayload';
  sharedAccessLink: SharedAccessLink;
  /** JWT bearer token for the link — SHOWN ONCE. */
  token: Scalars['String']['output'];
  /** Full URL that wraps the token. */
  url: Scalars['AWSURL']['output'];
};

export type CreateShiftInput = {
  daysOfWeek: Array<Scalars['String']['input']>;
  endTime: Scalars['String']['input'];
  name: Scalars['String']['input'];
  startTime: Scalars['String']['input'];
};

export type CreateShippingAccountInput = {
  /** 9 digits. Write-only. */
  accountNumber: Scalars['String']['input'];
  /**
   * The customers that ship on it (at least one). Each gets EXTERNAL_ACCOUNT
   * terms on it, replacing its own terms; requires clearpath.pricing.write too.
   */
  assignToPartyIds: Array<Scalars['ID']['input']>;
  carrier: ShippingCarrier;
  /** 1-35 characters, printed as the shipper company on the label. */
  labelCompanyName?: InputMaybe<Scalars['String']['input']>;
  labelSenderAddress?: InputMaybe<ShippingLabelSenderAddressInput>;
  nickname: Scalars['String']['input'];
};

export type CreateShippingLabelInput = {
  /**
   * Ship together: the other orders this label covers (at most 24; same
   * customer, terms, account and billable party, none with an active label).
   * orderId is the primary order, whose invoice carries the one shipping charge.
   */
  additionalOrderIds?: InputMaybe<Array<Scalars['ID']['input']>>;
  carrier: ShippingCarrier;
  expectedShippingAccountId?: InputMaybe<Scalars['ID']['input']>;
  /**
   * ENG-3286 — the terms the operator was looking at; the label is refused
   * with Conflict when the order's terms have changed since.
   */
  expectedShippingTerms?: InputMaybe<ShippingTerms>;
  fromAddress: ShippingAddressInput;
  insuredValueUsd?: InputMaybe<Scalars['Float']['input']>;
  /** Null only from a client older than per-label options. */
  options?: InputMaybe<ShipmentOptionsInput>;
  /** Order this label belongs to. Required so tracking + carrier_id land on the order. */
  orderId: Scalars['ID']['input'];
  /** Required for CUSTOMER_PACKAGING; ignored for a carrier package. */
  packageDimensionsIn?: InputMaybe<ShippingPackageDimensionsInput>;
  packageWeightOz: Scalars['Float']['input'];
  /**
   * The chosen rate's quotedAmountUsd. Recorded for quote-vs-charge drift
   * reporting only; the charge is the billing truth.
   */
  quotedAmountUsd?: InputMaybe<Scalars['Float']['input']>;
  /**
   * rateKey of the chosen rate; required with options, and refused when it
   * disagrees with serviceLevel and options.saturdayDelivery.
   */
  rateKey?: InputMaybe<Scalars['String']['input']>;
  /** Optional label reference (e.g. ClearPath order barcode). */
  referenceNumber?: InputMaybe<Scalars['String']['input']>;
  /**
   * Send only after switching the printed sender (orderShippingLabelSender):
   * an id, or null for no sender identity. Omitted = the resolved sender.
   * Switching needs clearpath.orders.shipping_terms.override; FedEx only.
   */
  senderIdentityId?: InputMaybe<Scalars['ID']['input']>;
  /** Carrier-specific service code from a prior requestShippingRates call. */
  serviceLevel: Scalars['String']['input'];
  /** @deprecated Use options.signatureOption. Read only when options is null. */
  signatureRequired?: InputMaybe<Scalars['Boolean']['input']>;
  toAddress: ShippingAddressInput;
};

/**
 * ENG-1043 — input for Mutation.createSite. Writes the backing org_unit
 * (unit_type='site') + the sites extension row in one transaction. `code` is the
 * stable per-org SCREAMING_SNAKE reference (e.g. SITE_TX).
 */
export type CreateSiteInput = {
  acceptsIntake?: InputMaybe<Scalars['Boolean']['input']>;
  code: Scalars['String']['input'];
  isActive?: InputMaybe<Scalars['Boolean']['input']>;
  name: Scalars['String']['input'];
  organizationId: Scalars['ID']['input'];
  performsManufacturing?: InputMaybe<Scalars['Boolean']['input']>;
  shipFromCity?: InputMaybe<Scalars['String']['input']>;
  shipFromCountry?: InputMaybe<Scalars['String']['input']>;
  shipFromLine1?: InputMaybe<Scalars['String']['input']>;
  shipFromLine2?: InputMaybe<Scalars['String']['input']>;
  shipFromName?: InputMaybe<Scalars['String']['input']>;
  shipFromPhone?: InputMaybe<Scalars['String']['input']>;
  shipFromPostalCode?: InputMaybe<Scalars['String']['input']>;
  shipFromRegion?: InputMaybe<Scalars['String']['input']>;
};

export type CreateSkillDefinitionInput = {
  category: Scalars['String']['input'];
  description?: InputMaybe<Scalars['String']['input']>;
  name: Scalars['String']['input'];
};

export type CreateTenantInput = {
  /**
   * Email of the initial tenant admin. Required — provisioner step 09
   * (SendAdminInvite) issues the Cognito invite to this address and flips the
   * tenant to ACTIVE. Matches the proven SFN input contract (adminEmail).
   */
  adminEmail: Scalars['AWSEmail']['input'];
  billingEmail?: InputMaybe<Scalars['AWSEmail']['input']>;
  /** Database placement: 'shared' | 'dedicated'. Staging defaults to shared after the verified routing cutover; production accepts dedicated only. */
  databasePlacement?: InputMaybe<Scalars['String']['input']>;
  name: Scalars['String']['input'];
  primaryIndustry?: InputMaybe<Scalars['String']['input']>;
  slug: Scalars['String']['input'];
  tenantAwsAccountId?: InputMaybe<Scalars['String']['input']>;
  tenantAwsRegion?: InputMaybe<Scalars['String']['input']>;
  /** Service tier: 'starter' (default) | 'growth' | 'enterprise'. Controls dedicated compute capacity, independently of placement. */
  tier?: InputMaybe<Scalars['String']['input']>;
};

export type CreateTenantPayload = {
  __typename?: 'CreateTenantPayload';
  provisioningExecutionArn: Scalars['String']['output'];
  tenant: Tenant;
};

export type CreateTicketInput = {
  assigneeUserId?: InputMaybe<Scalars['ID']['input']>;
  body: Scalars['String']['input'];
  category?: InputMaybe<TicketCategory>;
  customFields?: InputMaybe<Scalars['AWSJSON']['input']>;
  dueAt?: InputMaybe<Scalars['AWSDateTime']['input']>;
  priority?: InputMaybe<TicketPriority>;
  relatedOrderId?: InputMaybe<Scalars['ID']['input']>;
  /** Defaults to the caller's user id when omitted. */
  requesterUserId?: InputMaybe<Scalars['ID']['input']>;
  subject: Scalars['String']['input'];
};

export type CreateTimeOffInput = {
  endDate: Scalars['AWSDate']['input'];
  notes?: InputMaybe<Scalars['String']['input']>;
  portion: Scalars['String']['input'];
  startDate: Scalars['AWSDate']['input'];
};

export type CreateUserCertificationInput = {
  certificationDefinitionId: Scalars['ID']['input'];
  earnedDate?: InputMaybe<Scalars['AWSDate']['input']>;
  expiryDate?: InputMaybe<Scalars['AWSDate']['input']>;
  userId: Scalars['ID']['input'];
};

/** Input for createUser. */
export type CreateUserInput = {
  email?: InputMaybe<Scalars['AWSEmail']['input']>;
  fullName: Scalars['String']['input'];
  /**
   * The ExternalParty this user is anchored to (`users.home_party_id`).
   * Required when userType is CUSTOMER — the resolver rejects a CUSTOMER
   * create with no homePartyId, and rejects an id that doesn't belong to this
   * organization or isn't a party type that can own orders (ENG-user-invite-home-party).
   */
  homePartyId?: InputMaybe<Scalars['ID']['input']>;
  initialRoleAssignments?: InputMaybe<Array<AssignRoleInput>>;
  locale?: InputMaybe<Scalars['String']['input']>;
  organizationId?: InputMaybe<Scalars['ID']['input']>;
  phone?: InputMaybe<Scalars['AWSPhone']['input']>;
  timezone?: InputMaybe<Scalars['String']['input']>;
  userType: UserType;
};

/**
 * ENG-3054 Story 3.1 — a count of customers in one impact partition, with up to
 * 20 named samples ordered by name. `count` is the full total; `samples` is the
 * preview slice (the full list is the Story 3.2 paged impact list).
 */
export type CustomerAccessImpactCount = {
  __typename?: 'CustomerAccessImpactCount';
  count: Scalars['Int']['output'];
  samples: Array<CustomerAccessPartyRef>;
};

/**
 * ENG-3054 Story 3.2 — which partition of a revoke target's reach an impact list
 * reads: the customers who LOSE the ability to order, or those who KEEP it through
 * another grant.
 */
export enum CustomerAccessImpactListKind {
  Keeping = 'KEEPING',
  Losing = 'LOSING'
}

/**
 * ENG-3054 Story 3.1 — the group operation a change set applies to the active
 * independent offices. GRANT FILLS GAPS ONLY: it grants exactly the offices that
 * cannot order now (converging each to one open-ended party-and-descendants
 * grant); an office that can already order is left unchanged, whatever its window
 * or scope. REVOKE removes every active independent office's own direct grant.
 * Omit (null) to leave the independent group untouched.
 */
export enum CustomerAccessIndependentOp {
  Grant = 'GRANT',
  Revoke = 'REVOKE'
}

/**
 * ENG-3054 Story 3.2 — how a KEEPING customer is still covered after the change:
 * by its own grant (OWN_GRANT), or by an ancestor whose group grant reaches it
 * (ANCESTOR_GRANT).
 */
export enum CustomerAccessKeptByKind {
  AncestorGrant = 'ANCESTOR_GRANT',
  OwnGrant = 'OWN_GRANT'
}

/**
 * ENG-3054 Story 2.5 — one parent's direct children matching a results narrowing
 * (see `Query.customerAccessMatchCounts`).
 */
export type CustomerAccessMatchCount = {
  __typename?: 'CustomerAccessMatchCount';
  /**
   * Matching direct children, inactive ones included (the connection's
   * `totalCount` population).
   */
  matchCount: Scalars['Int']['output'];
  parentPartyId: Scalars['ID']['output'];
};

/**
 * ENG-3054 (AD-4, Story 3.1) — a minimal named party reference (id + display name,
 * nothing else, by design). Used by the customer-access reads for a parent
 * organization or a granting ancestor, and by the impact preview and list for a named
 * customer sample or keeper. For an account manager, a reference to a party outside
 * their book is returned as null by the field that would carry it (or left out of a
 * sample), never as this shape.
 */
export type CustomerAccessPartyRef = {
  __typename?: 'CustomerAccessPartyRef';
  id: Scalars['ID']['output'];
  name: Scalars['String']['output'];
};

/** Internal workflow context for one configured customer-approval attempt. */
export type CustomerApprovalRequest = {
  __typename?: 'CustomerApprovalRequest';
  approvalStepDefinitionId: Scalars['ID']['output'];
  approvalStepName: Scalars['String']['output'];
  approvalStepRunId: Scalars['ID']['output'];
  approverName?: Maybe<Scalars['String']['output']>;
  approverUserId: Scalars['ID']['output'];
  attemptNumber: Scalars['Int']['output'];
  decidedAt?: Maybe<Scalars['AWSDateTime']['output']>;
  decisionNote?: Maybe<Scalars['String']['output']>;
  reapprovalRequired?: Maybe<Scalars['Boolean']['output']>;
  requestedAt: Scalars['AWSDateTime']['output'];
  resolutionAction?: Maybe<CustomerApprovalResolution>;
  resolvedAt?: Maybe<Scalars['AWSDateTime']['output']>;
  reworkStepRunId?: Maybe<Scalars['ID']['output']>;
  reworkTargetStepDefinitionId?: Maybe<Scalars['ID']['output']>;
  reworkTargets: Array<CustomerApprovalReworkTarget>;
  status: CustomerApprovalStatus;
};

export enum CustomerApprovalResolution {
  Continue = 'CONTINUE',
  SendBack = 'SEND_BACK'
}

export type CustomerApprovalResolutionPayload = {
  __typename?: 'CustomerApprovalResolutionPayload';
  holdId: Scalars['ID']['output'];
  order: Order;
  orderId: Scalars['ID']['output'];
  resolution: CustomerApprovalResolution;
  reworkStepRun?: Maybe<StepRun>;
};

export type CustomerApprovalReworkTarget = {
  __typename?: 'CustomerApprovalReworkTarget';
  id: Scalars['ID']['output'];
  name: Scalars['String']['output'];
  position: Scalars['Int']['output'];
};

export enum CustomerApprovalStatus {
  Approved = 'APPROVED',
  ChangesRequested = 'CHANGES_REQUESTED',
  Pending = 'PENDING'
}

/**
 * Doctor → clinic_staff delegation link.
 * Per docs/clearpath-architecture/03-data-model.md tier 1 (`customer_delegations`).
 */
export type CustomerDelegation = {
  __typename?: 'CustomerDelegation';
  doctorParty?: Maybe<ExternalParty>;
  /** The doctor party being represented. */
  doctorPartyId: Scalars['ID']['output'];
  expiresAt?: Maybe<Scalars['AWSDateTime']['output']>;
  grantedAt: Scalars['AWSDateTime']['output'];
  grantedByUserId?: Maybe<Scalars['ID']['output']>;
  id: Scalars['ID']['output'];
  isActive: Scalars['Boolean']['output'];
  revokedAt?: Maybe<Scalars['AWSDateTime']['output']>;
  scope?: Maybe<Scalars['AWSJSON']['output']>;
  staffUser?: Maybe<User>;
  /** The clinic staff user acting on behalf of the doctor. */
  staffUserId: Scalars['ID']['output'];
  tenantId: Scalars['ID']['output'];
};

export type CustomerDelegationConnection = {
  __typename?: 'CustomerDelegationConnection';
  edges: Array<CustomerDelegationEdge>;
  pageInfo: PageInfo;
  totalCount: Scalars['Int']['output'];
};

export type CustomerDelegationEdge = {
  __typename?: 'CustomerDelegationEdge';
  cursor: Scalars['String']['output'];
  node: CustomerDelegation;
};

/** Filter for Query.customerDelegations. `activeOnly` defaults to true. */
export type CustomerDelegationFilterInput = {
  /** When true (default) excludes delegations whose expires_at has elapsed. */
  activeOnly?: InputMaybe<Scalars['Boolean']['input']>;
  /** Narrow to delegations held by this clinic_staff user (maps to DB staff_user_id). */
  delegateUserId?: InputMaybe<Scalars['ID']['input']>;
  /** Narrow to delegations granting rights over this doctor party (maps to DB doctor_party_id). */
  delegatorPartyId?: InputMaybe<Scalars['ID']['input']>;
};

/**
 * Mutation payload for createCustomerDelegation / revokeCustomerDelegation /
 * extendCustomerDelegation.
 */
export type CustomerDelegationPayload = {
  __typename?: 'CustomerDelegationPayload';
  delegation: CustomerDelegation;
};

/**
 * One exemption certificate this organization holds on file.
 *
 * ⚠️ IT RELIEVES THE **BILLABLE** PARTY, NOT THE SHIP-TO PARTY. The engine anchors
 * on `invoices.billable_party_id` (`services/pricing/src/tax/invoice_tax.ts`
 * passes it as every line's `customerPartyId`), because a resale or exemption
 * certificate belongs to the PURCHASER OF RECORD. So a practice that ships under a
 * group's billing account is NOT relieved by its own certificate — the billing
 * group's certificate is the one that applies.
 */
export type CustomerTaxExemption = {
  __typename?: 'CustomerTaxExemption';
  /**
   * The certificate or authority reference. NOT NULL in the database and
   * required by the mutation: a certificate row with nothing identifying it is the
   * first thing an auditor disallows. Where there is no number, this holds the
   * letter or ruling reference instead — which is why the form labels it
   * "Certificate / authority reference".
   */
  certificateNumber: Scalars['String']['output'];
  exemptionReason: TaxExemptionReason;
  externalPartyId: Scalars['ID']['output'];
  /**
   * Whether the scanned certificate is attached. An `ACTIVE` certificate with
   * no evidence on file is what an auditor disallows first, so it is surfaced.
   * Slice 5 ships NO upload surface, so this is read-only for now and an active
   * certificate without evidence is warned about rather than refused.
   */
  hasEvidence: Scalars['Boolean']['output'];
  id: Scalars['ID']['output'];
  /**
   * The jurisdiction's code, when the certificate is narrowed to one. Null on a
   * blanket certificate, and also null when the jurisdiction has been retired —
   * the row is still listed rather than silently dropped, because the engine would
   * still honour it.
   */
  jurisdictionCode?: Maybe<Scalars['String']['output']>;
  /** The customer this certificate relieves — the party invoices are BILLED to. */
  partyName: Scalars['String']['output'];
  status: TaxExemptionStatus;
  /** Null = the certificate covers EVERY tax category. Set = only that one. */
  taxCategoryCode?: Maybe<Scalars['String']['output']>;
  /**
   * Null = the certificate covers EVERY jurisdiction (a blanket certificate).
   * Set = it covers only that one. Render null as the word "All", never as an
   * empty cell: the difference between blanket and state-scoped decides whether
   * every other state is relieved too.
   */
  taxJurisdictionId?: Maybe<Scalars['ID']['output']>;
  validFrom: Scalars['AWSDate']['output'];
  /** EXCLUSIVE. Null = no end date. */
  validTo?: Maybe<Scalars['AWSDate']['output']>;
  /** Opaque optimistic-concurrency token. Pass it back as `expectedVersion`. */
  version: Scalars['String']['output'];
};

/** The certificates this organization holds on file. */
export type CustomerTaxExemptionList = {
  __typename?: 'CustomerTaxExemptionList';
  exemptions: Array<CustomerTaxExemption>;
  /** True when more rows exist than were returned. Never silently truncated. */
  truncated: Scalars['Boolean']['output'];
};

export type CustomerTaxExemptionPayload = {
  __typename?: 'CustomerTaxExemptionPayload';
  exemption: CustomerTaxExemption;
};

/**
 * The CUSTOMER-SAFE projection of an open hold, for the portal hold display
 * (ENG-687).
 *
 * This is a strict, minimal subset of `OrderHold`: it carries NOTHING that could
 * leak an internal blocker or personal data. There is deliberately no `note`, no
 * `releaseNote`, no internal reason `label`/`description`, no `responsibleParty`
 * and no `placedBy` — those live on `OrderHold`, which the portal never reads. A
 * row exists here only when the hold's reason has `HoldReasonCode.isCustomerVisible
 * = true`; the projection is enforced in SQL, so the internal columns are never
 * even selected on this path.
 */
export type CustomerVisibleHold = {
  __typename?: 'CustomerVisibleHold';
  /** Managed approval state. Null for an ordinary customer-visible hold. */
  approvalStatus?: Maybe<CustomerApprovalStatus>;
  /** Pipeline step that requested approval. Null for an ordinary hold. */
  approvalStepName?: Maybe<Scalars['String']['output']>;
  /**
   * ENG-19 — server-computed `reason_code === 'AWAITING_CUSTOMER_APPROVAL'`. The
   * portal gates the Approve / Request-changes card on this boolean and derives the
   * actionable `holdId` from `id` where it is true. A single boolean: it exposes NO
   * internal reason taxonomy (the raw code/name are never selected), and is the safe
   * replacement for string-matching `customerFacingText`.
   */
  awaitsCustomerDecision: Scalars['Boolean']['output'];
  /**
   * The tenant-authored customer-safe message (`HoldReasonCode.customerFacingText`),
   * e.g. "On hold: awaiting your approval". NON-NULL: when the tenant left the
   * text blank the server substitutes generic copy — it NEVER falls back to the
   * internal reason label.
   */
  customerFacingText: Scalars['String']['output'];
  /** When the case went on hold (the hold's `startedAt`). */
  heldSince: Scalars['AWSDateTime']['output'];
  /** The hold's id — an opaque random UUID, safe to expose as a stable key. */
  id: Scalars['ID']['output'];
  /** The owning case. */
  orderId: Scalars['ID']['output'];
  /** The held unit, or null when approval applies to the whole case. */
  orderProductId?: Maybe<Scalars['ID']['output']>;
  /** Derived from `orderProductId`. */
  scope: HoldScope;
};

/**
 * Round 5 — scope kind for the generic kanban dashboard surface.
 * Tenant-extensible at the data layer (`scope_kind` is TEXT in queries) but
 * the SDL enum is closed for type-safety; tenants who need additional
 * kinds today will land them via a future amendment.
 *
 * Named `DashboardScopeKind` to avoid collision with the pre-existing
 * `ScopeKind` enum used by `@requiresScope` (line ~57).
 */
export enum DashboardScopeKind {
  ExternalParty = 'EXTERNAL_PARTY',
  OrgUnit = 'ORG_UNIT',
  Role = 'ROLE',
  Team = 'TEAM',
  User = 'USER'
}

/** Date range filter (inclusive bounds). */
export type DateRangeInput = {
  gte?: InputMaybe<Scalars['AWSDateTime']['input']>;
  lte?: InputMaybe<Scalars['AWSDateTime']['input']>;
};

/**
 * Defect count for one failure reason code (Pareto row). A null
 * failureReasonCodeId buckets un-coded failures.
 */
export type DefectReasonCount = {
  __typename?: 'DefectReasonCount';
  code?: Maybe<Scalars['String']['output']>;
  count: Scalars['Int']['output'];
  failureReasonCodeId?: Maybe<Scalars['ID']['output']>;
  label?: Maybe<Scalars['String']['output']>;
  severity?: Maybe<Scalars['String']['output']>;
};

/** One failed step_run in the defect registry list. */
export type DefectRecord = {
  __typename?: 'DefectRecord';
  code?: Maybe<Scalars['String']['output']>;
  failureDetails?: Maybe<Scalars['AWSJSON']['output']>;
  failureReasonCodeId?: Maybe<Scalars['ID']['output']>;
  label?: Maybe<Scalars['String']['output']>;
  occurredAt: Scalars['AWSDateTime']['output'];
  orderId?: Maybe<Scalars['ID']['output']>;
  severity?: Maybe<Scalars['String']['output']>;
  stepDefinitionId: Scalars['ID']['output'];
  stepRunId: Scalars['ID']['output'];
};

/** Paginated defect registry payload returned by Query.defectRegistry. */
export type DefectRegistry = {
  __typename?: 'DefectRegistry';
  records: Array<DefectRecord>;
  totalCount: Scalars['Int']['output'];
};

/** Result of deleting an org's target for a measure. */
export type DeleteAnalyticsTargetPayload = {
  __typename?: 'DeleteAnalyticsTargetPayload';
  deletedMeasureKey: Scalars['String']['output'];
};

/**
 * ENG-656 - entities supported by the deleteBlockers preflight query. Each
 * value maps to the hard-delete mutation whose refusal the preflight predicts
 * (deleteOrgUnit, deletePipeline, deleteProductType, deleteUser,
 * deleteExternalParty, deleteCalculatedMeasure).
 */
export enum DeleteBlockerEntity {
  CalculatedMeasure = 'CALCULATED_MEASURE',
  ExternalParty = 'EXTERNAL_PARTY',
  OrgUnit = 'ORG_UNIT',
  Pipeline = 'PIPELINE',
  ProductType = 'PRODUCT_TYPE',
  User = 'USER'
}

/** Input for deleteComment. */
export type DeleteCommentInput = {
  commentId: Scalars['ID']['input'];
};

/** Input for deleteFieldLabel. */
export type DeleteFieldLabelInput = {
  fieldName: Scalars['String']['input'];
  locale?: InputMaybe<Scalars['String']['input']>;
  organizationId: Scalars['ID']['input'];
};

export type DeleteFlagAssignmentPayload = {
  __typename?: 'DeleteFlagAssignmentPayload';
  id: Scalars['ID']['output'];
};

/** Mutation result for deletePartyPricingAgreement. */
export type DeletePartyPricingAgreementPayload = {
  __typename?: 'DeletePartyPricingAgreementPayload';
  deletedId: Scalars['ID']['output'];
};

export type DeletePipelineConsumptionRulePayload = {
  __typename?: 'DeletePipelineConsumptionRulePayload';
  deletedId: Scalars['ID']['output'];
};

/** Payload returned by deleteRole. */
export type DeleteRolePayload = {
  __typename?: 'DeleteRolePayload';
  deleted: Scalars['Boolean']['output'];
  id: Scalars['ID']['output'];
};

export type DeleteVendorRoutingRulePayload = {
  __typename?: 'DeleteVendorRoutingRulePayload';
  deletedId: Scalars['ID']['output'];
};

/** Dependency type for step DAG edges. */
export enum DependencyType {
  BarrierAllChildren = 'BARRIER_ALL_CHILDREN',
  BarrierCountN = 'BARRIER_COUNT_N',
  Branch = 'BRANCH',
  /**
   * The explicit "otherwise" out of a decision (ENG-1577): taken only when no
   * sibling BRANCH condition on the same source matched, and carrying no
   * branchCondition of its own. Distinct from a BRANCH with a null condition,
   * which the engine reads as always-true.
   */
  BranchDefault = 'BRANCH_DEFAULT',
  Linear = 'LINEAR',
  ParallelJoin = 'PARALLEL_JOIN'
}

export type DiscardFormDefinitionDraftInput = {
  formDefinitionId: Scalars['ID']['input'];
};

/**
 * Payload for discardOrderDraft (ENG-1750). Idempotent: discarding an
 * already-gone / non-owned / cross-tenant id returns `deleted: false` (never an
 * error), so a client can fire-and-forget on abandon.
 */
export type DiscardOrderDraftPayload = {
  __typename?: 'DiscardOrderDraftPayload';
  deleted: Scalars['Boolean']['output'];
  id: Scalars['ID']['output'];
};

/**
 * Input for discardPipelineDraft (self-versioning, requirement #2). Discards
 * the open DRAFT version of the pipeline without publishing.
 */
export type DiscardPipelineDraftInput = {
  pipelineId: Scalars['ID']['input'];
};

/**
 * Sample the DISTINCT top-level JSONB KEY NAMES of a whitelisted tenant custom-field
 * column (ENG-1872 s8). KEYS ONLY — never a field VALUE.
 */
export type DiscoverFreeFormProjectionKeysInput = {
  /** The base relation to sample (orders | order_products). */
  baseRelation: Scalars['String']['input'];
  /** The JSONB column to sample (custom_fields | intake_form_response). */
  jsonbColumn: Scalars['String']['input'];
};

/**
 * Payment dispute.
 * Per docs/clearpath-architecture/03-data-model.md tier 7 (`disputes`).
 */
export type Dispute = {
  __typename?: 'Dispute';
  amount: Scalars['Float']['output'];
  currency: Scalars['String']['output'];
  id: Scalars['ID']['output'];
  invoiceId?: Maybe<Scalars['ID']['output']>;
  openedAt: Scalars['AWSDateTime']['output'];
  organizationId: Scalars['ID']['output'];
  paymentTransactionId?: Maybe<Scalars['ID']['output']>;
  reason?: Maybe<Scalars['String']['output']>;
  resolvedAt?: Maybe<Scalars['AWSDateTime']['output']>;
  status: DisputeStatus;
  tenantId: Scalars['ID']['output'];
};

/** Filter for disputes. FIN-M1-04 (ENG-1449). */
export type DisputeFilterInput = {
  invoiceIds?: InputMaybe<Array<Scalars['ID']['input']>>;
  /**
   * Only disputes that are still contestable (OPEN or UNDER_REVIEW). Combines
   * with `status` as an intersection.
   */
  openOnly?: InputMaybe<Scalars['Boolean']['input']>;
  status?: InputMaybe<Array<DisputeStatus>>;
};

/** Dispute lifecycle status. */
export enum DisputeStatus {
  Lost = 'LOST',
  Open = 'OPEN',
  UnderReview = 'UNDER_REVIEW',
  Withdrawn = 'WITHDRAWN',
  Won = 'WON'
}

/**
 * BA.5 (2026-04-24) — DSAR (Data Subject Access Request) lifecycle.
 * Backend primitives in services/compliance/src/dsar/* cover both export
 * + delete kinds. State machine: RECEIVED → IN_PROGRESS → READY →
 * DELIVERED → ARCHIVED; CANCELLED / FAILED are terminal.
 */
export enum DsarKind {
  Delete = 'DELETE',
  Export = 'EXPORT'
}

export type DsarRequest = {
  __typename?: 'DsarRequest';
  bundleExpiresAt?: Maybe<Scalars['AWSDateTime']['output']>;
  bundleKey?: Maybe<Scalars['String']['output']>;
  createdAt: Scalars['AWSDateTime']['output'];
  error?: Maybe<Scalars['String']['output']>;
  id: Scalars['ID']['output'];
  kind: DsarKind;
  requestedBy: Scalars['String']['output'];
  /**
   * ENG-447 — outcome of the executed DSAR, recorded by executeDsarRequest.
   * export → { bundleKey, rowCounts, outstanding, exceptions } where each
   * exception is { category, count, legal_basis_ref } with no content (ENG-3076);
   * delete → { softDeleted (per
   * table), ordersLinked, objectsDeleted, complete, outstanding, pseudonym };
   * fail-closed abort → { failed, error }. NULL until the request is executed.
   */
  result?: Maybe<Scalars['AWSJSON']['output']>;
  state: DsarState;
  subjectUserId: Scalars['ID']['output'];
  tenantId: Scalars['ID']['output'];
  updatedAt: Scalars['AWSDateTime']['output'];
};

export type DsarRequestFilterInput = {
  kind?: InputMaybe<DsarKind>;
  limit?: InputMaybe<Scalars['Int']['input']>;
  state?: InputMaybe<DsarState>;
  tenantId?: InputMaybe<Scalars['ID']['input']>;
};

export enum DsarState {
  Archived = 'ARCHIVED',
  Cancelled = 'CANCELLED',
  Delivered = 'DELIVERED',
  Failed = 'FAILED',
  InProgress = 'IN_PROGRESS',
  Ready = 'READY',
  Received = 'RECEIVED'
}

/** Input for editComment. */
export type EditCommentInput = {
  body: Scalars['String']['input'];
  commentId: Scalars['ID']['input'];
};

/** A resolved address and where it came from. */
export type EffectiveAddress = {
  __typename?: 'EffectiveAddress';
  address: ExternalPartyAddress;
  /**
   * True when address.country is not stored but read as US from a US state
   * code + ZIP. The stored row is unchanged.
   */
  countryInferred: Scalars['Boolean']['output'];
  /** True when the address belongs to an ancestor, not to this party. */
  inherited: Scalars['Boolean']['output'];
  /**
   * How many accounts resolve to this address row for the same purpose,
   * counting the source party itself: who an edit to it would change. 0 when
   * the address is the party's own.
   */
  sharedByCount: Scalars['Int']['output'];
  sourcePartyId: Scalars['ID']['output'];
  sourcePartyName: Scalars['String']['output'];
};

/**
 * Stored `status` overlaid with derived completeness. INVALID means a required
 * field is missing (see the sibling `reasons`); otherwise it echoes the stored
 * lifecycle status. Never persisted — computed on read from the entity + its
 * related rows (pipeline/form/price).
 */
export enum EffectiveLifecycleStatus {
  Archived = 'ARCHIVED',
  Draft = 'DRAFT',
  Inactive = 'INACTIVE',
  Invalid = 'INVALID',
  Live = 'LIVE'
}

/**
 * Input for enqueueAssistantJob (ENG-2250). Minimal + well under the 8KB WAF
 * request cap: the worker loads prior turns from `conversationId` server-side
 * rather than accepting client-supplied history.
 */
export type EnqueueAssistantInput = {
  /**
   * Client-generated idempotency key. Enqueue no-ops (returns the in-flight
   * job) when a job for this key is already RUNNING, so a double-submit or retry
   * cannot start a second worker.
   */
  clientRequestId: Scalars['String']['input'];
  /** Existing conversation to continue, or null to start a new one. */
  conversationId?: InputMaybe<Scalars['ID']['input']>;
  /** The user's natural-language message for this turn. */
  message: Scalars['String']['input'];
};

/** Input for Mutation.enrollScannerStation. */
export type EnrollScannerStationInput = {
  /** Optional label for the issued key. Defaults to `<name> — scanner key`. */
  keyLabel?: InputMaybe<Scalars['String']['input']>;
  /** Optional free-text location note. */
  location?: InputMaybe<Scalars['String']['input']>;
  /**
   * Station name — used as both the service account name and the
   * scanner_devices workstation id (each unique per organisation).
   */
  name: Scalars['String']['input'];
  /** Optional org unit the station physically lives in. */
  orgUnitId?: InputMaybe<Scalars['ID']['input']>;
};

/**
 * Payload for Mutation.enrollScannerStation. `rawKey` is surfaced ONCE — the
 * caller MUST save it; it is not retrievable afterwards.
 */
export type EnrollScannerStationPayload = {
  __typename?: 'EnrollScannerStationPayload';
  /** The issued API key's id. */
  apiKeyId: Scalars['ID']['output'];
  /** ISO-8601 creation timestamp. */
  createdAt: Scalars['AWSDateTime']['output'];
  /** First 16 characters of the key — safe to display. */
  keyPrefix: Scalars['String']['output'];
  /** The full plaintext key `cp_live_…`/`cp_test_…`. Surfaced exactly once. */
  rawKey: Scalars['String']['output'];
  /** The station's scanner device id (pairs with ScannerStation.id). */
  scannerDeviceId: Scalars['ID']['output'];
  /** The station's service account id. */
  serviceAccountId: Scalars['ID']['output'];
  /** The enrolled station (device + key metadata, no secret). */
  station: ScannerStation;
};

/** Lifecycle of a tenant_product_entitlement row. */
export enum EntitlementStatus {
  Active = 'ACTIVE',
  Cancelled = 'CANCELLED',
  Suspended = 'SUSPENDED',
  Trial = 'TRIAL'
}

/**
 * Custom entity type definition (beyond the core schema).
 * Per docs/clearpath-architecture/03-data-model.md tier 2 (`entity_definitions`).
 */
export type EntityDefinition = {
  __typename?: 'EntityDefinition';
  createdAt: Scalars['AWSDateTime']['output'];
  description?: Maybe<Scalars['String']['output']>;
  fields: Array<EntityField>;
  id: Scalars['ID']['output'];
  key: Scalars['String']['output'];
  name: Scalars['String']['output'];
  organizationId: Scalars['ID']['output'];
  tenantId: Scalars['ID']['output'];
  updatedAt?: Maybe<Scalars['AWSDateTime']['output']>;
};

/**
 * Custom field on a custom entity.
 * Per docs/clearpath-architecture/03-data-model.md tier 2 (`entity_fields`).
 */
export type EntityField = {
  __typename?: 'EntityField';
  entityDefinitionId: Scalars['ID']['output'];
  fieldName: Scalars['String']['output'];
  fieldType: FieldType;
  id: Scalars['ID']['output'];
  isRequired: Scalars['Boolean']['output'];
  label: Scalars['String']['output'];
  options?: Maybe<Scalars['AWSJSON']['output']>;
  position: Scalars['Int']['output'];
};

/**
 * Ordered escalation chain definition. Rewritten in Phase 5 (MOL-562) to
 * match the DB canon in migration 0003_tier3_work.sql — steps are stored as a
 * single JSONB array on `escalation_chains.steps`, not a separate child table.
 * Per docs/clearpath-architecture/03-data-model.md tier 3 and
 * 04-pipeline-engine.md "Escalation on loopback".
 */
export type EscalationChain = {
  __typename?: 'EscalationChain';
  /** Stable per-tenant code; unique within org. */
  code: Scalars['String']['output'];
  createdAt: Scalars['AWSDateTime']['output'];
  id: Scalars['ID']['output'];
  isActive: Scalars['Boolean']['output'];
  /** Human-readable name. */
  name: Scalars['String']['output'];
  organizationId: Scalars['ID']['output'];
  /** Ordered steps. DB canon: JSONB array on `escalation_chains.steps`. */
  steps: Array<EscalationChainStep>;
  /**
   * Optional binding: chain applies only to this pipeline step definition.
   * Null = chain applies generally (selected via loopback `failure_rules`).
   */
  targetStepDefinitionId?: Maybe<Scalars['ID']['output']>;
  updatedAt: Scalars['AWSDateTime']['output'];
};

/**
 * One step in an escalation chain. Exactly one of targetRoleId /
 * targetUserId is set.
 */
export type EscalationChainStep = {
  __typename?: 'EscalationChainStep';
  /** Minutes to wait after firing this level before firing the next level. */
  delayBeforeNextMinutes: Scalars['Int']['output'];
  /** Target role — resolved via user_roles + org_unit_closure at fire time. */
  targetRoleId?: Maybe<Scalars['ID']['output']>;
  /** Target user — used directly, skipping role resolution. */
  targetUserId?: Maybe<Scalars['ID']['output']>;
};

/** Input step shape mirroring EscalationChainStep. */
export type EscalationChainStepInput = {
  delayBeforeNextMinutes: Scalars['Int']['input'];
  targetRoleId?: InputMaybe<Scalars['ID']['input']>;
  targetUserId?: InputMaybe<Scalars['ID']['input']>;
};

/**
 * Triggered escalation record (append-only). One row per level fired.
 * Per docs/clearpath-architecture/03-data-model.md tier 3 (`escalation_events`).
 */
export type EscalationEvent = {
  __typename?: 'EscalationEvent';
  acknowledgedAt?: Maybe<Scalars['AWSDateTime']['output']>;
  acknowledgedByUserId?: Maybe<Scalars['ID']['output']>;
  /** Denormalized; resolved via join to `escalation_chains`. */
  chainId: Scalars['ID']['output'];
  firedAt: Scalars['AWSDateTime']['output'];
  id: Scalars['ID']['output'];
  /** Zero-indexed position in the owning chain's steps array. */
  level: Scalars['Int']['output'];
  notes?: Maybe<Scalars['String']['output']>;
  stepRunId: Scalars['ID']['output'];
  targetUserId?: Maybe<Scalars['ID']['output']>;
};

/**
 * ENG-2211 — paginated escalation events. Replaces the bare
 * `escalationEvents: [EscalationEvent!]` list, which capped at 500 rows with
 * nothing in the response to say so: a busy chain's oldest breaches simply
 * vanished, and "no more escalations" was indistinguishable from "we stopped
 * looking".
 *
 * NULLABLE, like the list it replaces, and for the same reason: a caller missing
 * the permission gets `null` rather than an unrecoverable NonNull violation that
 * takes the whole response down. Consumers coalesce.
 */
export type EscalationEventConnection = {
  __typename?: 'EscalationEventConnection';
  edges: Array<EscalationEventEdge>;
  pageInfo: PageInfo;
  totalCount: Scalars['Int']['output'];
};

export type EscalationEventEdge = {
  __typename?: 'EscalationEventEdge';
  cursor: Scalars['String']['output'];
  node: EscalationEvent;
};

/** Filter for listEscalationEvents / escalationEvents query. */
export type EscalationEventsFilterInput = {
  chainId?: InputMaybe<Scalars['ID']['input']>;
  limit?: InputMaybe<Scalars['Int']['input']>;
  since?: InputMaybe<Scalars['AWSDateTime']['input']>;
  stepRunId?: InputMaybe<Scalars['ID']['input']>;
};

/**
 * Append-only event log; source of truth for SLA, analytics, federation, webhooks.
 * Per docs/clearpath-architecture/03-data-model.md tier 5 (`events`).
 */
export type Event = {
  __typename?: 'Event';
  /** Triggering user (null for system events). */
  actorUserId?: Maybe<Scalars['ID']['output']>;
  /** Aggregate UUID. */
  aggregateId: Scalars['ID']['output'];
  /** Aggregate kind (e.g. order, step_run, federation_link). */
  aggregateType: Scalars['String']['output'];
  /** Causation: previous event ID that triggered this one. */
  causationEventId?: Maybe<Scalars['ID']['output']>;
  /** Correlation ID linking related events (request, saga). */
  correlationId?: Maybe<Scalars['String']['output']>;
  /** Stable event type (e.g. step.completed, order.shipped). */
  eventType: Scalars['String']['output'];
  id: Scalars['ID']['output'];
  occurredAt: Scalars['AWSDateTime']['output'];
  organizationId: Scalars['ID']['output'];
  /** Event payload (JSONB). */
  payload: Scalars['AWSJSON']['output'];
  recordedAt: Scalars['AWSDateTime']['output'];
  tenantId: Scalars['ID']['output'];
};

export type EventConnection = {
  __typename?: 'EventConnection';
  edges: Array<EventEdge>;
  pageInfo: PageInfo;
  totalCount: Scalars['Int']['output'];
};

export type EventEdge = {
  __typename?: 'EventEdge';
  cursor: Scalars['String']['output'];
  node: Event;
};

/** Filter for events. */
export type EventFilterInput = {
  actorUserId?: InputMaybe<Array<Scalars['ID']['input']>>;
  aggregateId?: InputMaybe<Array<Scalars['ID']['input']>>;
  aggregateType?: InputMaybe<Array<Scalars['String']['input']>>;
  eventType?: InputMaybe<Array<Scalars['String']['input']>>;
  occurredAt?: InputMaybe<DateRangeInput>;
};

/**
 * ENG-447 — input for executeDsarRequest. Identifies the RECEIVED request to
 * run; no operator-supplied bundle key (the export path produces the real one).
 */
export type ExecuteDsarRequestInput = {
  id: Scalars['ID']['input'];
  tenantId: Scalars['ID']['input'];
};

export type ExplainConfigResult = {
  __typename?: 'ExplainConfigResult';
  explanation: Scalars['String']['output'];
  tokensUsed: TokenUsage;
};

/**
 * External integration (scanner/mill/printer/shipping/ERP).
 * Per docs/clearpath-architecture/03-data-model.md tier 6 (`external_integrations`).
 */
export type ExternalIntegration = {
  __typename?: 'ExternalIntegration';
  /** Provider config (JSONB; secrets via secrets_references). */
  config: Scalars['AWSJSON']['output'];
  createdAt: Scalars['AWSDateTime']['output'];
  /**
   * Masked pointer to the credentials secret when one is configured (ENG-708).
   * Reads never return the full Secrets Manager ARN — only a redacted display
   * string. Services resolve the real ARN from the database, not this field.
   * To change the secret, pass a new full ARN on UpsertExternalIntegrationInput.
   */
  credentialsSecretArn?: Maybe<Scalars['String']['output']>;
  id: Scalars['ID']['output'];
  /** Integration kind discriminator. */
  integrationType: ExternalIntegrationType;
  isActive: Scalars['Boolean']['output'];
  name: Scalars['String']['output'];
  organizationId: Scalars['ID']['output'];
  /**
   * Provider id — must match a registered adapter (e.g., `3shape`, `itero`,
   * `fedex`, `ups`).
   */
  provider: Scalars['String']['output'];
  tenantId: Scalars['ID']['output'];
  updatedAt?: Maybe<Scalars['AWSDateTime']['output']>;
};

/** External integration kind. */
export enum ExternalIntegrationType {
  Email = 'EMAIL',
  Erp = 'ERP',
  Mill = 'MILL',
  Other = 'OTHER',
  Payment = 'PAYMENT',
  Printer = 'PRINTER',
  Scanner = 'SCANNER',
  ShippingCarrier = 'SHIPPING_CARRIER',
  Sms = 'SMS'
}

/** Filter input for the externalIntegrations query. */
export type ExternalIntegrationsFilterInput = {
  integrationType?: InputMaybe<ExternalIntegrationType>;
  isActive?: InputMaybe<Scalars['Boolean']['input']>;
  /** Filter disabled rows. False retains active and recoverable error rows. */
  isDisabled?: InputMaybe<Scalars['Boolean']['input']>;
  provider?: InputMaybe<Scalars['String']['input']>;
};

/**
 * Customer or vendor organization in the external party graph.
 * Per docs/clearpath-architecture/03-data-model.md tier 1 (`external_parties`).
 */
export type ExternalParty = {
  __typename?: 'ExternalParty';
  /** Default mailing address (JSONB). */
  address?: Maybe<Scalars['AWSJSON']['output']>;
  /**
   * Why nothing resolves for `purpose`, when that is because every address
   * that could serve it (this party's, or for SHIPPING with none of its own the
   * nearest parent's that has rows) is restricted to another purpose. BILLING
   * considers the party's own addresses only. Null when an address resolves or
   * none exists at all.
   */
  addressRestriction?: Maybe<ShipToAddressRestriction>;
  /** Multi-address roster for this party (excludes revoked rows). Round 1a. */
  addresses: Array<ExternalPartyAddress>;
  /**
   * ENG-623 — which product types this customer may order. Access is
   * determined purely by the returned grants: a party with no grants reaching it
   * (directly, or inherited from an ancestor) is entitled to no product types.
   * There is no separate restricted/unrestricted mode. Null if the caller lacks
   * permission to view catalog access for this party.
   */
  catalogAccess?: Maybe<PartyCatalogAccess>;
  /** Direct child parties. */
  children: Array<ExternalParty>;
  /**
   * Primary contact email. Written normalised, but read back as String, NOT
   * AWSEmail — see contactPhone below for why this side of the contract is
   * deliberately lenient while UpsertExternalPartyInput stays strict. ENG-647.
   */
  contactEmail?: Maybe<Scalars['String']['output']>;
  /**
   * Primary contact phone, stored normalised to E.164.
   *
   * READ-PATH SCALAR IS DELIBERATELY `String`, NOT `AWSPhone` (ENG-647). Both
   * this field and contactEmail are tenant-authored values living in
   * `external_parties.custom_fields`, surfaced by
   * services/api/src/handlers/queries/shared.ts's externalPartyRowToGql. A
   * strict scalar HERE means one unserializable row denies the WHOLE collection:
   * AppSync rejects the entire Query.externalParties response, the admin's
   * Customers & Vendors list renders "Something went wrong" with zero parties,
   * and because the list is the only place a party can be edited or deleted the
   * state is unrecoverable without direct SQL. That is what ENG-647 reproduced
   * live on staging from a single bulk-import row whose phone cell read "call
   * the front desk".
   *
   * The write paths are still strict, and that is where validation belongs:
   * UpsertExternalPartyInput.contactPhone/contactEmail keep AWSPhone/AWSEmail so
   * AppSync coerces every mutation input, and the CSV import path (which has no
   * typed input to coerce) normalises to E.164 via @clearpath/phone's
   * normalizeImportedPhone and refuses the row otherwise. This field is defence
   * in depth for the residue those two cannot catch — AWSPhone runs a real NANP
   * *assignment* check (probed: it refuses +15555550142) that no structural
   * validator of ours replicates, so a value can be legitimately stored and
   * still be unserializable by a strict scalar. The worst case must be "one row
   * displays an odd number", never "the collection fails".
   *
   * Clients must therefore treat this as free text: format it with
   * @clearpath/phone's formatPhoneForDisplay, which already returns an
   * unrecognised value unchanged rather than mangling it.
   */
  contactPhone?: Maybe<Scalars['String']['output']>;
  /**
   * Extension for contactPhone (e.g. a lab/office main line's extension).
   * Free text, not part of the number — E.164 (contactPhone's normalised storage
   * form) has no extension concept, so this is always a separate field, never
   * appended to the number. ENG-458.
   */
  contactPhoneExtension?: Maybe<Scalars['String']['output']>;
  createdAt: Scalars['AWSDateTime']['output'];
  customFields?: Maybe<Scalars['AWSJSON']['output']>;
  /**
   * Stable per-tenant customer number (ENG-2522 column, ENG-2524 surface).
   * Null on the many parties that predate the column or were created with no
   * numbering scheme configured — the unique index behind it is partial for that
   * reason, so absence is normal and not a data defect.
   *
   * DISTINCT FROM `customFields.account_numbers`, which is the legacy free-text
   * field and is a `;`-separated LIST because one office legitimately holds
   * several ABS account numbers. This is one value, unique per organization,
   * compared case-insensitively and ignoring surrounding whitespace.
   */
  customerNumber?: Maybe<Scalars['String']['output']>;
  /**
   * Where `customerNumber` came from: `generated` (allocated from the tenant
   * sequence) or `imported` (backfilled from the party's ABS account number by
   * ENG-2527) — both system-managed and IMMUTABLE thereafter, see
   * UpsertExternalPartyInput.customerNumber; `manual` (typed by an operator)
   * stays editable. Null whenever `customerNumber` is null.
   */
  customerNumberSource?: Maybe<Scalars['String']['output']>;
  /** The default bill-to party, when set and visible to the caller. */
  defaultBillingParty?: Maybe<ExternalParty>;
  /**
   * ENG-3738 — the party new orders for this customer bill to when the order
   * names no billing party: this party itself or an ancestor. Null = no default
   * (today's rule applies). Not inherited by child parties.
   */
  defaultBillingPartyId?: Maybe<Scalars['ID']['output']>;
  /**
   * False when the default is no longer this party or an active ancestor (for
   * example after a re-parent); order creation then ignores it. Null when no
   * default is set. Resolves one party at a time: select it on a single-party
   * read, not on lists.
   */
  defaultBillingPartyValid?: Maybe<Scalars['Boolean']['output']>;
  /** All descendant parties via closure table. */
  descendants: Array<ExternalParty>;
  /**
   * The address this party resolves to for `purpose`: its own best eligible
   * row, else (SHIPPING only, and only when it has no rows of its own) the
   * nearest parent's that has rows (inherited = true). BILLING is the party's own
   * address only, as the invoice Bill-To. Null when nothing resolves. The same
   * rule the invoice and tax paths use.
   */
  effectiveAddress?: Maybe<EffectiveAddress>;
  id: Scalars['ID']['output'];
  /** Whether this party is currently active. */
  isActive: Scalars['Boolean']['output'];
  /**
   * Whether this party's `partyType` is configured as a BILLING PRINCIPAL for
   * the organization (`external_party_type_definitions.billing_principal`,
   * migration 0449) — i.e. whether parties of this type are billed directly
   * rather than through a parent practice.
   *
   * False for a party whose type has no definition row at all, which is the
   * normal state until a tenant declares its party types in a config pack.
   *
   * ONLY `Query.externalParty` / `Query.externalParties` /
   * `Query.externalPartiesConnection` answer this for real. Every party reached
   * through a parent — `Order.customerParty`, `billingParty`, `shipToParty`,
   * `orderingPractitionerParty`, `Invoice.billingParty`, and the
   * `parent`/`children`/`descendants` walks — reports `false` unconditionally,
   * because projecting the real flag there would add a correlated subquery per
   * joined party to the order-list hot path. False is the fail-closed direction
   * and no server-side authorization decision reads this field (the grant in
   * services/api/src/handlers/billing/billing_principal.ts reads the column
   * itself), so the hard-coded answer can only SHRINK client capability.
   *
   * ⚠ Apollo normalisation makes the divergence live: both answers write to the
   * same `ExternalParty:<id>` entity, so a query selecting this field under one of
   * the parent relations will overwrite a `true` with `false`. Select it from a
   * party read, not from a nested relation. Pinned by
   * services/api/src/nested-party-payload-shape.test.ts.
   */
  isBillingPrincipal: Scalars['Boolean']['output'];
  /** Display name (e.g. 'Patel Family Dental'). */
  name: Scalars['String']['output'];
  /**
   * ENG-658 — the order-level intake form resolved for THIS party: a direct
   * assignment on this party if one exists, else one inherited from an
   * ancestor (scope=PARTY_AND_DESCENDANTS), else null (meaning the tenant
   * default from TenantSettings.orderIntakeFormDefinition applies). Unlike
   * `catalogAccess`, this is genuine precedence, not a union — see
   * `PartyOrderFormAssignment`.
   */
  orderFormAssignment?: Maybe<PartyOrderFormAssignment>;
  organizationId: Scalars['ID']['output'];
  parent?: Maybe<ExternalParty>;
  /** Parent party (DSO above clinic, vendor parent, etc.). */
  parentPartyId?: Maybe<Scalars['ID']['output']>;
  /**
   * Open-ended party kind. Bible §03:140 — `external_parties.party_type`
   * is TEXT and tenant-extensible (canonical examples: customer, vendor,
   * dso, clinic, doctor; industry-specific values are open-ended). Stored
   * in lowercase by convention; UI maps to display labels.
   */
  partyType: Scalars['String']['output'];
  /**
   * Round 6 (2026-04-30) — per-party negotiated pricing tiers. Sorted
   * by validFrom desc; the live agreement is the first non-revoked row
   * whose validUntil is null or in the future. Each agreement carries
   * embedded line items (productType -> overridePrice / discountPercent /
   * volumeTiers). Empty array when no overrides apply.
   */
  pricingAgreements: Array<PartyPricingAgreement>;
  /**
   * Ad-hoc bidirectional relationships involving this party (excludes
   * revoked rows). Round 1a.
   */
  relationships: Array<ExternalPartyRelationship>;
  /** Short slug. */
  slug?: Maybe<Scalars['String']['output']>;
  /**
   * ENG-2912 — migration provenance: the source system this party was imported
   * from (e.g. `abs_evolution`), the same value recorded in `migration_crosswalk`.
   * Null means the party was created natively in ClearPath, not migrated. Read
   * straight off the `external_parties.source_system` column; surfaced on the
   * External Parties admin page as a Source filter facet.
   */
  sourceSystem?: Maybe<Scalars['String']['output']>;
  /** Tax/VAT identifiers (JSONB). */
  taxIds?: Maybe<Scalars['AWSJSON']['output']>;
  tenantId: Scalars['ID']['output'];
  updatedAt?: Maybe<Scalars['AWSDateTime']['output']>;
  /**
   * Tenant users anchored to this party (users.home_party_id =
   * this.id). Round 1a — staff-side roster surface; invite/remove flows
   * reuse the existing user mutations. Empty when called on internal
   * (non-customer) parties.
   */
  users: Array<User>;
};


/**
 * Customer or vendor organization in the external party graph.
 * Per docs/clearpath-architecture/03-data-model.md tier 1 (`external_parties`).
 */
export type ExternalPartyAddressRestrictionArgs = {
  purpose: AddressPurpose;
};


/**
 * Customer or vendor organization in the external party graph.
 * Per docs/clearpath-architecture/03-data-model.md tier 1 (`external_parties`).
 */
export type ExternalPartyEffectiveAddressArgs = {
  purpose: AddressPurpose;
};

/**
 * Multi-address row attached to an ExternalParty.
 * Per docs/clearpath-architecture/03-data-model.md tier 1
 * (`external_party_addresses`). Round 1a (v2 feature restoration plan).
 * `kind` is a free-form text column — tenants are free to add their own
 * labels (e.g. `billing`, `shipping`, `service`, or custom values). At
 * most one row per party may have `isPrimary = true`.
 */
export type ExternalPartyAddress = {
  __typename?: 'ExternalPartyAddress';
  city?: Maybe<Scalars['String']['output']>;
  /** ISO-3166-1 alpha-2 country code (e.g. US, GB, CA). */
  country?: Maybe<Scalars['String']['output']>;
  createdAt: Scalars['AWSDateTime']['output'];
  customFields?: Maybe<Scalars['AWSJSON']['output']>;
  externalPartyId: Scalars['ID']['output'];
  id: Scalars['ID']['output'];
  /** At most one primary address per party (DB partial unique index). */
  isPrimary: Scalars['Boolean']['output'];
  /** Tenant-extensible label (billing / shipping / service / other / …). */
  kind: Scalars['String']['output'];
  /** Optional human label (e.g. 'HQ', 'Warehouse 2'). */
  label?: Maybe<Scalars['String']['output']>;
  lat?: Maybe<Scalars['Float']['output']>;
  lng?: Maybe<Scalars['Float']['output']>;
  metadata?: Maybe<Scalars['AWSJSON']['output']>;
  organizationId: Scalars['ID']['output'];
  postalCode?: Maybe<Scalars['String']['output']>;
  region?: Maybe<Scalars['String']['output']>;
  revokedAt?: Maybe<Scalars['AWSDateTime']['output']>;
  street1?: Maybe<Scalars['String']['output']>;
  street2?: Maybe<Scalars['String']['output']>;
  /** Purposes this address serves. Empty = every purpose (the default). */
  tags: Array<AddressPurpose>;
  tenantId: Scalars['ID']['output'];
  updatedAt?: Maybe<Scalars['AWSDateTime']['output']>;
};

export type ExternalPartyAddressDeletedPayload = {
  __typename?: 'ExternalPartyAddressDeletedPayload';
  deletedId: Scalars['ID']['output'];
};

export type ExternalPartyAddressPayload = {
  __typename?: 'ExternalPartyAddressPayload';
  externalPartyAddress: ExternalPartyAddress;
};

/**
 * P0a — paginated, server-side-searchable external parties. The bare
 * `externalParties` field caps at 500 with no hasNextPage/totalCount, so DSO-scale
 * offices past the 500th silently vanish from pickers; this connection restores
 * full reachability with keyset pagination + server-side (pg_trgm) name search.
 */
export type ExternalPartyConnection = {
  __typename?: 'ExternalPartyConnection';
  edges: Array<ExternalPartyEdge>;
  pageInfo: PageInfo;
  totalCount: Scalars['Int']['output'];
};

export type ExternalPartyDeletedPayload = {
  __typename?: 'ExternalPartyDeletedPayload';
  deletedId: Scalars['ID']['output'];
};

export type ExternalPartyEdge = {
  __typename?: 'ExternalPartyEdge';
  cursor: Scalars['String']['output'];
  node: ExternalParty;
};

export type ExternalPartyPayload = {
  __typename?: 'ExternalPartyPayload';
  externalParty: ExternalParty;
};

/**
 * Ad-hoc bidirectional relationship between two ExternalParties beyond
 * the strict parent/child tree (acquisition, affiliation, etc.). Round 1a
 * (v2 feature restoration plan).
 */
export type ExternalPartyRelationship = {
  __typename?: 'ExternalPartyRelationship';
  createdAt: Scalars['AWSDateTime']['output'];
  customFields?: Maybe<Scalars['AWSJSON']['output']>;
  id: Scalars['ID']['output'];
  metadata?: Maybe<Scalars['AWSJSON']['output']>;
  organizationId: Scalars['ID']['output'];
  partyA?: Maybe<ExternalParty>;
  partyAId: Scalars['ID']['output'];
  partyB?: Maybe<ExternalParty>;
  partyBId: Scalars['ID']['output'];
  /** Tenant-extensible kind (`parent_of`, `affiliated_with`, `acquired_by`, …). */
  relationshipKind: Scalars['String']['output'];
  revokedAt?: Maybe<Scalars['AWSDateTime']['output']>;
  tenantId: Scalars['ID']['output'];
  updatedAt?: Maybe<Scalars['AWSDateTime']['output']>;
};

export type ExternalPartyRelationshipDeletedPayload = {
  __typename?: 'ExternalPartyRelationshipDeletedPayload';
  deletedId: Scalars['ID']['output'];
};

export type ExternalPartyRelationshipPayload = {
  __typename?: 'ExternalPartyRelationshipPayload';
  externalPartyRelationship: ExternalPartyRelationship;
};

/** Input for failStepRun. */
export type FailStepRunInput = {
  failureDetails?: InputMaybe<Scalars['AWSJSON']['input']>;
  failureReasonCodeId: Scalars['ID']['input'];
  notes?: InputMaybe<Scalars['String']['input']>;
  stepRunId: Scalars['ID']['input'];
  /**
   * How many of the line's identical units this applies to. Omitted, or the
   * line's whole quantity, acts on the whole line. Fewer splits those units off
   * onto a new line with its own pipeline run (it keeps working from here, the
   * rest of the line carries on) — the split is permanent.
   */
  unitCount?: InputMaybe<Scalars['Int']['input']>;
  /**
   * Which of the line's units this applies to, as 0-based positions in
   * `OrderProduct.unitIntakeFormResponses` (each unit's own answers). Takes
   * precedence over `unitCount`; naming every unit acts on the whole line.
   */
  unitIndexes?: InputMaybe<Array<Scalars['Int']['input']>>;
};

/**
 * Failure reason taxonomy per step.
 * Per docs/clearpath-architecture/03-data-model.md tier 3 (`failure_reason_codes`).
 */
export type FailureReasonCode = {
  __typename?: 'FailureReasonCode';
  /** Stable code (e.g. margin_defect). */
  code: Scalars['String']['output'];
  createdAt: Scalars['AWSDateTime']['output'];
  /** Suggested loopback step (template default). */
  defaultLoopbackStepId?: Maybe<Scalars['ID']['output']>;
  description?: Maybe<Scalars['String']['output']>;
  id: Scalars['ID']['output'];
  isActive: Scalars['Boolean']['output'];
  label: Scalars['String']['output'];
  organizationId: Scalars['ID']['output'];
  /** Step definitions this code applies to (empty = all). */
  stepDefinitionIds: Array<Scalars['ID']['output']>;
  tenantId: Scalars['ID']['output'];
  updatedAt?: Maybe<Scalars['AWSDateTime']['output']>;
};

/**
 * Mutation result for deleteFailureReasonCode. The row is gone — mirrors
 * StepDependencyDeletedPayload / OrgUnitDeletedPayload.
 */
export type FailureReasonCodeDeletedPayload = {
  __typename?: 'FailureReasonCodeDeletedPayload';
  deletedId: Scalars['ID']['output'];
};

export type FailureReasonCodePayload = {
  __typename?: 'FailureReasonCodePayload';
  failureReasonCode: FailureReasonCode;
};

export enum FailureReasonCodeSeverity {
  Critical = 'CRITICAL',
  High = 'HIGH',
  Low = 'LOW',
  Medium = 'MEDIUM'
}

/** One row from the control-plane `feature_flags` table. */
export type FeatureFlag = {
  __typename?: 'FeatureFlag';
  /** ISO timestamp if archived, else null. Archived flags return defaults only. */
  archivedAt?: Maybe<Scalars['AWSDateTime']['output']>;
  createdAt: Scalars['AWSDateTime']['output'];
  /** Default value (serialized JSON — shape matches flagType). */
  defaultValue: Scalars['AWSJSON']['output'];
  description?: Maybe<Scalars['String']['output']>;
  flagType: FlagType;
  id: Scalars['ID']['output'];
  /** Dotted key, e.g. 'clearpath.nas_module'. Globally unique. */
  key: Scalars['String']['output'];
  name: Scalars['String']['output'];
  /** Product this flag belongs to. Null = platform-wide. */
  productId?: Maybe<Scalars['ID']['output']>;
  updatedAt: Scalars['AWSDateTime']['output'];
};

/**
 * Principal triple fed into evaluateFeatureFlag. Mirrors the runtime
 * EvaluateContextInput in @clearpath/feature-flags. All fields optional —
 * missing fields skip their precedence tier in the walk.
 */
export type FeatureFlagEvaluationContextInput = {
  productId?: InputMaybe<Scalars['ID']['input']>;
  /**
   * Role keys (e.g. 'manager', 'lead_tech'). Match against scope_type=role
   * assignments.
   */
  roleIds?: InputMaybe<Array<Scalars['String']['input']>>;
  tenantId?: InputMaybe<Scalars['ID']['input']>;
  tenantTier?: InputMaybe<Scalars['String']['input']>;
  userId?: InputMaybe<Scalars['ID']['input']>;
};

export type FeatureFlagEvaluationResult = {
  __typename?: 'FeatureFlagEvaluationResult';
  flagId: Scalars['ID']['output'];
  flagKey: Scalars['String']['output'];
  flagType: FlagType;
  /** Scope that supplied the value, or 'DEFAULT' if the flag fell back. */
  source: Scalars['String']['output'];
  /** Full trace of every candidate assignment ordered by precedence. */
  trace: Array<FeatureFlagEvaluationTrace>;
  /** Evaluated value (defaultValue if nothing matched). */
  value: Scalars['AWSJSON']['output'];
};

/**
 * One candidate row in the evaluation trace — every assignment that
 * matched the context's precedence tiers. Lets the UI display 'tenant scope
 * wins (set by alice@molaris.ai)'.
 */
export type FeatureFlagEvaluationTrace = {
  __typename?: 'FeatureFlagEvaluationTrace';
  assignmentId?: Maybe<Scalars['ID']['output']>;
  scopeId?: Maybe<Scalars['String']['output']>;
  scopeType: FlagScopeType;
  value: Scalars['AWSJSON']['output'];
  /** True for the single row whose value is returned. */
  winner: Scalars['Boolean']['output'];
};

export type FeatureFlagPayload = {
  __typename?: 'FeatureFlagPayload';
  flag: FeatureFlag;
};

/** Federation direction. */
export enum FederationDirection {
  Bidirectional = 'BIDIRECTIONAL',
  Inbound = 'INBOUND',
  Outbound = 'OUTBOUND'
}

/**
 * Vendor-tenant ↔ org-tenant federation link.
 * Per docs/clearpath-architecture/03-data-model.md tier 6 (`federation_links`).
 */
export type FederationLink = {
  __typename?: 'FederationLink';
  acceptedAt?: Maybe<Scalars['AWSDateTime']['output']>;
  /** Allowed work types for federation. */
  allowedWorkTypes: Array<Scalars['String']['output']>;
  createdAt: Scalars['AWSDateTime']['output'];
  /** Direction discriminator. */
  direction: FederationDirection;
  /** When the link transitioned to active. */
  establishedAt?: Maybe<Scalars['AWSDateTime']['output']>;
  /** Per-link event visibility config (JSONB). */
  eventVisibilityConfig?: Maybe<Scalars['AWSJSON']['output']>;
  id: Scalars['ID']['output'];
  /** Display label for this connection. */
  label: Scalars['String']['output'];
  /** Cross-tenant messages on this link. */
  messages: FederationMessageConnection;
  organizationId: Scalars['ID']['output'];
  /** Our KMS signing key reference. */
  ourSigningKeyId?: Maybe<Scalars['String']['output']>;
  /** Partner org identifier (within partner tenant). */
  partnerOrganizationId?: Maybe<Scalars['ID']['output']>;
  /** Partner public key (PEM). */
  partnerPublicKey?: Maybe<Scalars['String']['output']>;
  /** Partner tenant identifier. */
  partnerTenantId: Scalars['ID']['output'];
  /** Display name of the partner tenant. */
  partnerTenantName: Scalars['String']['output'];
  revokedAt?: Maybe<Scalars['AWSDateTime']['output']>;
  /** Lifecycle status. */
  status: FederationStatus;
  tenantId: Scalars['ID']['output'];
  /** When the link was terminated. */
  terminatedAt?: Maybe<Scalars['AWSDateTime']['output']>;
  updatedAt?: Maybe<Scalars['AWSDateTime']['output']>;
};


/**
 * Vendor-tenant ↔ org-tenant federation link.
 * Per docs/clearpath-architecture/03-data-model.md tier 6 (`federation_links`).
 */
export type FederationLinkMessagesArgs = {
  page?: InputMaybe<PageInput>;
};

/** Relay connection wrapper for FederationLink list queries. Phase 1.1 (MOL-603). */
export type FederationLinkConnection = {
  __typename?: 'FederationLinkConnection';
  edges: Array<FederationLinkEdge>;
  pageInfo: PageInfo;
  totalCount: Scalars['Int']['output'];
};

export type FederationLinkEdge = {
  __typename?: 'FederationLinkEdge';
  cursor: Scalars['String']['output'];
  node: FederationLink;
};

/**
 * Filter for Query.federationLinks. All clauses are AND-combined. Empty
 * filter returns every link the viewer's org can see. Phase 1.1 (MOL-603).
 */
export type FederationLinkFilterInput = {
  /** Narrow to a specific direction (OUTBOUND/INBOUND/BIDIRECTIONAL). */
  direction?: InputMaybe<Array<FederationDirection>>;
  /** Narrow to links with this partner tenant. */
  partnerTenantId?: InputMaybe<Scalars['ID']['input']>;
  /** Substring search over label / partner_tenant_name. */
  search?: InputMaybe<Scalars['String']['input']>;
  /** Narrow to a specific lifecycle status (PENDING/ACTIVE/SUSPENDED/REVOKED/EXPIRED). */
  status?: InputMaybe<Array<FederationStatus>>;
};

export type FederationLinkPayload = {
  __typename?: 'FederationLinkPayload';
  federationLink: FederationLink;
};

/**
 * Cross-tenant threaded communication.
 * Per docs/clearpath-architecture/03-data-model.md tier 6 (`federation_messages`).
 */
export type FederationMessage = {
  __typename?: 'FederationMessage';
  author?: Maybe<User>;
  /** Authoring user (local-side). */
  authorUserId?: Maybe<Scalars['ID']['output']>;
  body: Scalars['String']['output'];
  createdAt: Scalars['AWSDateTime']['output'];
  /** Direction relative to local tenant. */
  direction: FederationMessageDirection;
  federationLink?: Maybe<FederationLink>;
  federationLinkId: Scalars['ID']['output'];
  id: Scalars['ID']['output'];
  /** Message kind. */
  messageType: FederationMessageType;
  occurredAt: Scalars['AWSDateTime']['output'];
  payload?: Maybe<Scalars['AWSJSON']['output']>;
  /** When the message was received (inbound) — null for outbound. */
  receivedAt?: Maybe<Scalars['AWSDateTime']['output']>;
  /** Order this message relates to (DB column related_order_id). */
  relatedOrderId?: Maybe<Scalars['ID']['output']>;
  /** Step run this message relates to (DB column related_step_run_id). */
  relatedStepRunId?: Maybe<Scalars['ID']['output']>;
  /** When the message was sent (outbound) — null for inbound. */
  sentAt?: Maybe<Scalars['AWSDateTime']['output']>;
  stepRun?: Maybe<StepRun>;
  /** Step run this message is scoped to. */
  stepRunId?: Maybe<Scalars['ID']['output']>;
  tenantId: Scalars['ID']['output'];
};

export type FederationMessageConnection = {
  __typename?: 'FederationMessageConnection';
  edges: Array<FederationMessageEdge>;
  pageInfo: PageInfo;
  totalCount: Scalars['Int']['output'];
};

/** Federation message direction. */
export enum FederationMessageDirection {
  Inbound = 'INBOUND',
  Outbound = 'OUTBOUND'
}

export type FederationMessageEdge = {
  __typename?: 'FederationMessageEdge';
  cursor: Scalars['String']['output'];
  node: FederationMessage;
};

/** Payload for sendFederationMessage + federation message subscription. */
export type FederationMessagePayload = {
  __typename?: 'FederationMessagePayload';
  federationMessage: FederationMessage;
};

/** Federation message kind. */
export enum FederationMessageType {
  Acceptance = 'ACCEPTANCE',
  Chat = 'CHAT',
  ClarificationRequest = 'CLARIFICATION_REQUEST',
  ClarificationResponse = 'CLARIFICATION_RESPONSE',
  Completion = 'COMPLETION',
  Rejection = 'REJECTION',
  StatusUpdate = 'STATUS_UPDATE'
}

/** Federation link lifecycle status. */
export enum FederationStatus {
  Active = 'ACTIVE',
  Expired = 'EXPIRED',
  Pending = 'PENDING',
  Revoked = 'REVOKED',
  Suspended = 'SUSPENDED'
}

/**
 * Tenant-configurable display label for a DB field.
 * Per docs/clearpath-architecture/03-data-model.md tier 1 (`field_labels`).
 * The composite PK in the DB is (organization_id, field_name, locale);
 * `id` is a client-side synthetic `organizationId:fieldName:locale` key
 * kept for Apollo cache normalization.
 */
export type FieldLabel = {
  __typename?: 'FieldLabel';
  createdAt?: Maybe<Scalars['AWSDateTime']['output']>;
  /** Tenant-facing display label. */
  displayLabel: Scalars['String']['output'];
  /** Internal field name (e.g. 'orders.customer_party_id'). */
  fieldName: Scalars['String']['output'];
  id: Scalars['ID']['output'];
  /** ISO locale this label applies to. */
  locale: Scalars['String']['output'];
  organizationId?: Maybe<Scalars['ID']['output']>;
  tenantId: Scalars['ID']['output'];
  updatedAt?: Maybe<Scalars['AWSDateTime']['output']>;
};

export type FieldLabelPayload = {
  __typename?: 'FieldLabelPayload';
  fieldLabel?: Maybe<FieldLabel>;
};

/** Field type discriminator for custom fields and entity fields. */
export enum FieldType {
  Boolean = 'BOOLEAN',
  Date = 'DATE',
  Datetime = 'DATETIME',
  Enum = 'ENUM',
  File = 'FILE',
  Integer = 'INTEGER',
  Json = 'JSON',
  Multiselect = 'MULTISELECT',
  Number = 'NUMBER',
  Reference = 'REFERENCE',
  String = 'STRING',
  Text = 'TEXT',
  /**
   * Universal 1-32 tooth-position selector — same widget FormBuilder's
   * BuilderFieldType 'tooth_chart' offers form fields (ENG-97), for library
   * attributes.
   */
  ToothChart = 'TOOTH_CHART'
}

/**
 * File registry entry.
 * Per docs/clearpath-architecture/03-data-model.md tier 4 (`files`).
 */
export type File = {
  __typename?: 'File';
  /**
   * ENG-590 — neutral file-kind categorization (files.category). OTHER when
   * unclassified. Resolved from the parent row; no dedicated resolver.
   */
  category: FileCategory;
  /** Comments on this file. */
  comments: CommentConnection;
  /** SHA-256 of file contents. */
  contentHash?: Maybe<Scalars['String']['output']>;
  createdAt: Scalars['AWSDateTime']['output'];
  /** Original filename. */
  filename: Scalars['String']['output'];
  id: Scalars['ID']['output'];
  /** Where this file physically lives — multi-location. */
  locations: Array<FileLocation>;
  /** Tenant-defined metadata (JSONB). */
  metadata?: Maybe<Scalars['AWSJSON']['output']>;
  /** MIME type. */
  mimeType: Scalars['String']['output'];
  organizationId: Scalars['ID']['output'];
  /** How this file entered the system (intake, vendor, scanner, etc.). */
  origin: FileOrigin;
  /**
   * True only when both Portal preview and guest sharing are enabled for the
   * tenant and the exact STL source is currently eligible. Permission is checked
   * separately before the share control is rendered or a link can be created.
   */
  oroviaGuestPreviewAvailable: Scalars['Boolean']['output'];
  /**
   * True only when the evaluated tenant flag and all server-side STL source
   * eligibility rules pass. Never exposes flag assignments or storage state.
   */
  oroviaPreviewAvailable: Scalars['Boolean']['output'];
  /** True only for an eligible exact STL when authenticated review rollout is enabled. */
  oroviaReviewAvailable: Scalars['Boolean']['output'];
  /**
   * ENG-590 — the immediately-prior file in this version lineage (same org),
   * or null for a root (v1) file (files.parent_file_id). Walk this chain to
   * render version history.
   */
  parentFileId?: Maybe<Scalars['ID']['output']>;
  /**
   * Track 4 (2026-04-30) — virus-scan verdict surfaced for the SPA. Sourced
   * from `files.metadata.scan_status` written by services/file/src/virus_scan.ts
   * (S3 ObjectCreated → GuardDuty Malware Protection). Defaults to PENDING
   * when the row was uploaded but no verdict tag has landed yet. Bible §07:128.
   */
  scanStatus: FileScanStatus;
  /** Size in bytes. */
  sizeBytes?: Maybe<Scalars['Float']['output']>;
  tenantId: Scalars['ID']['output'];
  updatedAt?: Maybe<Scalars['AWSDateTime']['output']>;
  uploadedBy?: Maybe<User>;
  /** Uploader user (if applicable). */
  uploadedByUserId?: Maybe<Scalars['ID']['output']>;
  /**
   * ENG-590 — 1-based revision number within this file's version lineage
   * (files.version). First upload is 1; each superseding revision increments.
   */
  version: Scalars['Int']['output'];
  /**
   * ENG-3844 — INTERNAL files are staff-only evidence (e.g. uploaded from a
   * production-step checklist). They are never returned to a portal, vendor or
   * share-link session, so those clients only ever see STANDARD. Resolved from the
   * parent row (files.visibility).
   */
  visibility: FileVisibility;
};


/**
 * File registry entry.
 * Per docs/clearpath-architecture/03-data-model.md tier 4 (`files`).
 */
export type FileCommentsArgs = {
  page?: InputMaybe<PageInput>;
};

/** Where to attach a freshly uploaded file. */
export type FileAttachmentInput = {
  asInput?: InputMaybe<Scalars['Boolean']['input']>;
  asOutput?: InputMaybe<Scalars['Boolean']['input']>;
  commentableId?: InputMaybe<Scalars['ID']['input']>;
  commentableType?: InputMaybe<CommentableType>;
  logicalName?: InputMaybe<Scalars['String']['input']>;
  stepRunId?: InputMaybe<Scalars['ID']['input']>;
};

/**
 * ENG-590 — neutral file-kind categorization. Industry-neutral by policy: the
 * code is generic (a lab labels the real vertical meaning in tenant-authored
 * data, never in these tokens). Maps 1:1 to the `files.category` CHECK whitelist
 * (db/migrations/0257_tier4_files_category_version.sql). Defaults to OTHER when a
 * caller does not classify an upload.
 */
export enum FileCategory {
  /** CAD / design output. */
  Design = 'DESIGN',
  /** A form, order sheet, or other document. */
  Document = 'DOCUMENT',
  /** Uncategorized. */
  Other = 'OTHER',
  /** Machine / production output. */
  Output = 'OUTPUT',
  /** Photographic image. */
  Photo = 'PHOTO',
  /** Volumetric / radiographic image. */
  Radiograph = 'RADIOGRAPH',
  /** Intraoral / model / desktop scan artifacts (e.g. STL/PLY). */
  Scan = 'SCAN'
}

export type FileConnection = {
  __typename?: 'FileConnection';
  edges: Array<FileEdge>;
  pageInfo: PageInfo;
  totalCount: Scalars['Int']['output'];
};

/** Download disposition preference. */
export enum FileDisposition {
  Attachment = 'ATTACHMENT',
  Inline = 'INLINE'
}

export type FileEdge = {
  __typename?: 'FileEdge';
  cursor: Scalars['String']['output'];
  node: File;
};

/**
 * Where a file physically lives. A single file can have multiple locations.
 * Per docs/clearpath-architecture/03-data-model.md tier 4 (`file_locations`).
 */
export type FileLocation = {
  __typename?: 'FileLocation';
  /** Bucket / share / drive name. */
  container?: Maybe<Scalars['String']['output']>;
  createdAt: Scalars['AWSDateTime']['output'];
  /** For federated file refs — federation link granting access. */
  federationLinkId?: Maybe<Scalars['ID']['output']>;
  file?: Maybe<File>;
  fileId: Scalars['ID']['output'];
  id: Scalars['ID']['output'];
  isPrimary: Scalars['Boolean']['output'];
  /** KMS key ID used to encrypt this object. */
  kmsKeyId?: Maybe<Scalars['String']['output']>;
  /** Location kind discriminator. */
  locationType: FileLocationType;
  /** Object key / path within container. */
  objectKey?: Maybe<Scalars['String']['output']>;
  /** Region (for cloud storage). */
  region?: Maybe<Scalars['String']['output']>;
  /** Storage URI (e.g. s3://bucket/key). */
  uri: Scalars['String']['output'];
};

/** File location kind. */
export enum FileLocationType {
  ExternalUrl = 'EXTERNAL_URL',
  Federated = 'FEDERATED',
  LocalDevice = 'LOCAL_DEVICE',
  Nas = 'NAS',
  S3 = 'S3',
  VendorShare = 'VENDOR_SHARE'
}

/** File origin discriminator. */
export enum FileOrigin {
  AdminUpload = 'ADMIN_UPLOAD',
  HardwareDevice = 'HARDWARE_DEVICE',
  IntakeUpload = 'INTAKE_UPLOAD',
  Scanner = 'SCANNER',
  StepOutput = 'STEP_OUTPUT',
  SystemGenerated = 'SYSTEM_GENERATED',
  VendorInbound = 'VENDOR_INBOUND'
}

/** Outcome of an explicit, idempotent malware scan retry request. */
export enum FileScanRetryStatus {
  AlreadyInProgress = 'ALREADY_IN_PROGRESS',
  NotRetryable = 'NOT_RETRYABLE',
  Requested = 'REQUESTED',
  RetryableFailed = 'RETRYABLE_FAILED',
  TooEarly = 'TOO_EARLY'
}

/**
 * File scan verdict. Mirrors the `scan_status` value the virus-scan
 * Lambda writes into `files.metadata`. Download authorization is enforced by
 * requestFileDownload: only CLEAN is normally downloadable; UNSUPPORTED needs an
 * explicit authorized-internal risk acceptance; every other non-clean state is
 * blocked.
 */
export enum FileScanStatus {
  AccessDenied = 'ACCESS_DENIED',
  Clean = 'CLEAN',
  Failed = 'FAILED',
  Infected = 'INFECTED',
  Pending = 'PENDING',
  Unsupported = 'UNSUPPORTED'
}

/**
 * ENG-3844 — why a file is being uploaded, when that changes how it is stored.
 * The server derives the file's visibility from this; a client can never set
 * visibility directly.
 */
export enum FileUploadPurpose {
  /**
   * Evidence for a checklist on a production step. Staff only; must attach to
   * an order (attachTo ORDER). Stored as an INTERNAL file.
   */
  ChecklistEvidence = 'CHECKLIST_EVIDENCE'
}

/**
 * ENG-3844 — who may see a file. Maps 1:1 to the files.visibility CHECK
 * (db/migrations/0537_tier4_files_visibility.sql).
 */
export enum FileVisibility {
  /** Lab staff only: hidden from portal, vendor and share-link sessions. */
  Internal = 'INTERNAL',
  /** Visible to every session the file's order already admits. */
  Standard = 'STANDARD'
}

/** Generic string filter — used for ad-hoc field filtering on list queries. */
export type FilterInput = {
  eq?: InputMaybe<Scalars['String']['input']>;
  gt?: InputMaybe<Scalars['String']['input']>;
  gte?: InputMaybe<Scalars['String']['input']>;
  in?: InputMaybe<Array<Scalars['String']['input']>>;
  like?: InputMaybe<Scalars['String']['input']>;
  lt?: InputMaybe<Scalars['String']['input']>;
  lte?: InputMaybe<Scalars['String']['input']>;
  ne?: InputMaybe<Scalars['String']['input']>;
};

export type FinanceReportColumn = {
  __typename?: 'FinanceReportColumn';
  /** The key of this column in every row object. */
  key: Scalars['String']['output'];
  kind: FinanceReportColumnKind;
  label: Scalars['String']['output'];
};

export enum FinanceReportColumnKind {
  /** Calendar day `YYYY-MM-DD`. */
  Date = 'DATE',
  Int = 'INT',
  /** Decimal string, 4dp, in the row's `currency`. Never a float. */
  Money = 'MONEY',
  Percent = 'PERCENT',
  Text = 'TEXT'
}

/** Totals for one currency. Amounts are never summed across currencies. */
export type FinanceReportCurrencyTotals = {
  __typename?: 'FinanceReportCurrencyTotals';
  currency: Scalars['String']['output'];
  values: Array<FinanceReportTotal>;
};

/** An exported finance report (.xlsx or .csv), delivered as bytes (no URL). */
export type FinanceReportFile = {
  __typename?: 'FinanceReportFile';
  /** Base64 file bytes. Decode to a Blob and download via an object URL. */
  contentBase64: Scalars['String']['output'];
  filename: Scalars['String']['output'];
  generatedAt: Scalars['AWSDateTime']['output'];
  id: FinanceReportId;
  mimeType: Scalars['String']['output'];
  rowCount: Scalars['Int']['output'];
};

/** File type of `exportFinanceReport`. */
export enum FinanceReportFileFormat {
  /**
   * The report's primary table only: UTF-8 with BOM, CRLF. WIP = Cases (Lines with
   * filter.asOf), SALES_REGISTER = Documents; INVOICED_LIST refuses CSV when it spans several currencies.
   */
  Csv = 'CSV',
  Xlsx = 'XLSX'
}

/**
 * Finance reports framework. One id per report; the API holds one definition
 * per id with its own filter, columns and gate. An id with no definition yet is
 * refused with ValidationError ("not available").
 */
export enum FinanceReportId {
  ArLedger = 'AR_LEDGER',
  /** Export only (carries subject names): `financeReport` refuses it. */
  InvoicedList = 'INVOICED_LIST',
  /** ENG-3881: the revenue journal entry per currency (preview; export is refused until the QuickBooks layout lands). */
  RevenueJournal = 'REVENUE_JOURNAL',
  /** ENG-3881: gross, taxable, non-taxable and tax per destination state (optionally per postal code), reconciled to the sales register. */
  SalesByDestinationState = 'SALES_BY_DESTINATION_STATE',
  SalesRegister = 'SALES_REGISTER',
  TaxByMunicipality = 'TAX_BY_MUNICIPALITY',
  Wip = 'WIP'
}

export type FinanceReportPage = {
  __typename?: 'FinanceReportPage';
  /** Now, or for a report of a past day the end of that day in the org calendar zone. */
  asOf: Scalars['AWSDateTime']['output'];
  /** Set for a report of a past day (WIP filter.asOf): that org calendar day. Null for a live report. */
  asOfDay?: Maybe<Scalars['AWSDate']['output']>;
  columns: Array<FinanceReportColumn>;
  id: FinanceReportId;
  /** LIVE until the period close lands. Label the output provisional. */
  periodStatus: RevenuePeriodStatus;
  /** One JSON object per row, keyed by `columns[].key`; values are strings or null. */
  rows: Array<Scalars['AWSJSON']['output']>;
  /** Rows matching the filter. */
  totalRows: Scalars['Int']['output'];
  /** Over the FULL filter, not this page. */
  totalsByCurrency: Array<FinanceReportCurrencyTotals>;
  /** True when rows exist beyond this page. */
  truncated: Scalars['Boolean']['output'];
};

export type FinanceReportPageInput = {
  /** 1 to 200; default 50. */
  limit?: InputMaybe<Scalars['Int']['input']>;
  /** Default 0. */
  offset?: InputMaybe<Scalars['Int']['input']>;
};

export type FinanceReportTotal = {
  __typename?: 'FinanceReportTotal';
  /** Decimal string. */
  amount: Scalars['String']['output'];
  /** A MONEY column key. */
  key: Scalars['String']['output'];
};

export type FirstBillAcknowledgePayload = {
  __typename?: 'FirstBillAcknowledgePayload';
  acknowledgedAt: Scalars['AWSDateTime']['output'];
  /** Released orders auto-invoicing was re-queued for (per-invoice parties). */
  autoInvoiceScheduledOrderIds: Array<Scalars['ID']['output']>;
  lineId: Scalars['ID']['output'];
  /** Orders left with no held charge, now free to bill. */
  releasedOrderIds: Array<Scalars['ID']['output']>;
  /** Orders another unacknowledged line still holds. */
  stillHeldOrderIds: Array<Scalars['ID']['output']>;
};

/**
 * ENG-4024 — a first-bill check: one agreement line carrying the assumed-$0
 * migrated provenance, and the orders it holds.
 */
export type FirstBillCheck = {
  __typename?: 'FirstBillCheck';
  /**
   * True when the line is already acknowledged and its orders are only waiting
   * for the release an acknowledgement runs.
   */
  acknowledged: Scalars['Boolean']['output'];
  agreementId: Scalars['ID']['output'];
  agreementName: Scalars['String']['output'];
  heldOrders: Array<FirstBillHeldOrder>;
  lineId: Scalars['ID']['output'];
  partyId: Scalars['ID']['output'];
  partyName?: Maybe<Scalars['String']['output']>;
  productTypeCode?: Maybe<Scalars['String']['output']>;
  productTypeId: Scalars['ID']['output'];
  productTypeName?: Maybe<Scalars['String']['output']>;
  variantKey?: Maybe<Scalars['String']['output']>;
};

export type FirstBillChecksPayload = {
  __typename?: 'FirstBillChecksPayload';
  checks: Array<FirstBillCheck>;
  /**
   * Held orders no assumed-$0 line explains any more (the line's price was
   * edited, or it was removed). The next acknowledgement releases them.
   */
  unexplainedHeldOrderIds: Array<Scalars['ID']['output']>;
};

/**
 * ENG-4024 — one held order on a first-bill check: an order whose charge an
 * assumed-$0 migrated agreement line priced, held off every invoice and statement
 * until the line is acknowledged.
 */
export type FirstBillHeldOrder = {
  __typename?: 'FirstBillHeldOrder';
  bookCurrency?: Maybe<Scalars['String']['output']>;
  /**
   * What the organization's default price book would have charged: the price
   * the resolver weighed and did not choose. Null when the book had no entry.
   */
  bookUnitPrice?: Maybe<Scalars['Float']['output']>;
  caseNumber: Scalars['String']['output'];
  currency: Scalars['String']['output'];
  customerName?: Maybe<Scalars['String']['output']>;
  heldAt: Scalars['AWSDateTime']['output'];
  orderId: Scalars['ID']['output'];
  orderProductId: Scalars['ID']['output'];
  priceBookId?: Maybe<Scalars['ID']['output']>;
  /** The price the order was held at: always 0 today. */
  unitPrice: Scalars['Float']['output'];
};

/** One row from `feature_flag_assignments`. */
export type FlagAssignment = {
  __typename?: 'FlagAssignment';
  createdAt: Scalars['AWSDateTime']['output'];
  createdByCognitoSub?: Maybe<Scalars['String']['output']>;
  expiresAt?: Maybe<Scalars['AWSDateTime']['output']>;
  flagId: Scalars['ID']['output'];
  /** Flag key denormalized for UI convenience (server-joined). */
  flagKey: Scalars['String']['output'];
  id: Scalars['ID']['output'];
  /** NULL only when scopeType = GLOBAL. */
  scopeId?: Maybe<Scalars['String']['output']>;
  scopeType: FlagScopeType;
  updatedAt: Scalars['AWSDateTime']['output'];
  value: Scalars['AWSJSON']['output'];
};

export type FlagAssignmentPayload = {
  __typename?: 'FlagAssignmentPayload';
  assignment: FlagAssignment;
};

export enum FlagScopeType {
  Global = 'GLOBAL',
  Product = 'PRODUCT',
  Role = 'ROLE',
  Tenant = 'TENANT',
  TenantTier = 'TENANT_TIER',
  User = 'USER'
}

/**
 * Input for flagStepRunVendorDelay (ENG-40) — raise a vendor/outsourcer delay
 * flag on an outsourced step run.
 */
export type FlagStepRunVendorDelayInput = {
  /** Optional revised expected-resolution time the vendor provided. */
  expectedResolutionAt?: InputMaybe<Scalars['AWSDateTime']['input']>;
  /** Why the outsourced step is delayed. Required (non-blank). */
  reason: Scalars['String']['input'];
  stepRunId: Scalars['ID']['input'];
};

export enum FlagType {
  Boolean = 'BOOLEAN',
  Json = 'JSON',
  Number = 'NUMBER',
  String = 'STRING'
}

/** Numeric range filter (inclusive bounds). Used for invoice amountDue. */
export type FloatRangeInput = {
  gte?: InputMaybe<Scalars['Float']['input']>;
  lte?: InputMaybe<Scalars['Float']['input']>;
};

/**
 * Input for forceReleaseOrder — case split (ENG-574/621/627/1151; Bible §05:110-111).
 *
 * Splits a case: `orderProductIds` names EXACTLY which units move OFF the source
 * order (linked via `parent_order_id`), so the rest of the case can proceed — or
 * ship — independently. Works at any point in production, not only at shipping.
 * `orderProductIds` must be non-empty and the source order must retain at least
 * one product; every id must belong to `orderId`.
 *
 * `splitPerItem` chooses the shape of the split (ENG-1151):
 *   - absent / false (grouped) — all selected units move onto ONE new sibling
 *     order (today's behaviour; a binary split into two orders).
 *   - true (per-item) — EACH selected unit becomes its OWN new independent
 *     sibling order (N selected → N new siblings + the source). Capped at 20
 *     selected units per call.
 */
export type ForceReleaseOrderInput = {
  orderId: Scalars['ID']['input'];
  /**
   * order_products ids to move off the source order. Replaces the
   * removed `scanned_at` auto-classifier — the operator states the split
   * explicitly rather than the server inferring it.
   */
  orderProductIds: Array<Scalars['ID']['input']>;
  reason: Scalars['String']['input'];
  /**
   * ENG-1151 — when true, split EACH selected unit into its own new sibling
   * order (N siblings); when absent/false, move all selected units onto ONE new
   * sibling (grouped, the original behaviour). Capped at 20 selected units in
   * per-item mode.
   */
  splitPerItem?: InputMaybe<Scalars['Boolean']['input']>;
};

/** Payload for forceReleaseOrder. */
export type ForceReleaseOrderPayload = {
  __typename?: 'ForceReleaseOrderPayload';
  /**
   * The source order — the selected products were split OUT of it onto the
   * sibling(s); this row reflects what remains.
   */
  order: Order;
  /**
   * DEPRECATED (ENG-1151): the FIRST created sibling order. Kept for
   * back-compat with grouped-mode callers (where it is the only sibling); new
   * callers should read `siblingOrders` instead, which lists every sibling
   * created (one in grouped mode, N in per-item mode).
   * @deprecated ENG-1151: use siblingOrders (every sibling); this is only the first.
   */
  siblingOrder: Order;
  /**
   * ENG-1151 — every sibling order created by this split. Grouped mode yields
   * exactly one element (== `siblingOrder`); per-item mode yields one per
   * selected unit. Never empty — every call splits at least one unit off.
   */
  siblingOrders: Array<Order>;
  /** Count of products split off the source order (across all siblings). */
  splitProductCount: Scalars['Int']['output'];
};

/**
 * Tenant-configurable form definition.
 * Per docs/clearpath-architecture/03-data-model.md tier 2 (`form_definitions`).
 */
export type FormDefinition = {
  __typename?: 'FormDefinition';
  /**
   * ENG-1129 — the product types this form is bound to, resolved
   * from `product_type_forms` (0217). PLURAL because that join is M:N on the
   * form's side: one form may serve many product types, while each product type
   * has at most one bound form (UNIQUE(product_type_id)). It REPLACES the
   * singular `productTypeId`/`productType` pair, which read the legacy 1:1
   * `form_definitions.product_type_id` column: only seeds ever populated it, so
   * a form created in the Forms Library and connected from the product page was
   * bound while both fields read null. The column is dropped in this change, and
   * a 1:1 field could not have expressed the binding anyway. Empty for every
   * non-PRODUCT_INTAKE kind —
   * order intake binds through tenant_settings / party_order_form_assignments,
   * and QC / step / custom-entity forms bind elsewhere again.
   */
  boundProductTypes: Array<ProductType>;
  createdAt: Scalars['AWSDateTime']['output'];
  /**
   * Real draft/live/archived version history (form_definition_versions,
   * db/migrations/0229). Non-null means this form has unpublished edits in
   * flight — a LIVE form with an open draft, or a from-scratch DRAFT — and can
   * be published in place (publishFormDefinition/promoteFormDraftToLive), even
   * while `status` is still LIVE. Set by openFormDefinitionForEdit, cleared by
   * publish or discardFormDefinitionDraft. NULL when there is no open draft (a
   * published form with no draft is immutable until opened for edit again).
   */
  draftVersionId?: Maybe<Scalars['ID']['output']>;
  /** Custom entity this form is bound to (custom-entity forms). */
  entityDefinitionId?: Maybe<Scalars['ID']['output']>;
  /** What this form is for (order intake, product intake, qc, etc.). */
  formKind: FormKind;
  id: Scalars['ID']['output'];
  /**
   * The version new orders resolve to (content lives on
   * form_definition_versions — publishing swings this pointer in place, no
   * binding ever moves). Null for a never-published DRAFT form. Editing a LIVE
   * form forks a draft and leaves this serving.
   */
  liveVersionId?: Maybe<Scalars['ID']['output']>;
  /** Form name. */
  name: Scalars['String']['output'];
  organizationId: Scalars['ID']['output'];
  publishedAt?: Maybe<Scalars['AWSDateTime']['output']>;
  publishedByUserId?: Maybe<Scalars['ID']['output']>;
  /**
   * Bounded iTero patient-field mapping for this effective order-intake form.
   * Populated only by Query.orderIntakeFormDefinition for internal order creation;
   * contains configuration field names only and never patient values.
   */
  scannerIntakeFieldMappings?: Maybe<Scalars['AWSJSON']['output']>;
  /** Form schema (JSON-Schema-like). */
  schema: Scalars['AWSJSON']['output'];
  /** P2.4 (2026-04-25) — versioning lifecycle. See PipelineStatus. */
  status: PipelineStatus;
  tenantId: Scalars['ID']['output'];
  /** UI hints (layout, conditional fields). */
  uiSchema?: Maybe<Scalars['AWSJSON']['output']>;
  updatedAt?: Maybe<Scalars['AWSDateTime']['output']>;
};

export type FormDefinitionEditPayload = {
  __typename?: 'FormDefinitionEditPayload';
  formDefinition: FormDefinition;
  /**
   * Non-fatal notices — e.g. re-opening a form that already has an open
   * draft returns it idempotently with a warning, same as
   * OpenPipelineForEditPayload. Empty for a normal open/discard.
   */
  warnings: Array<Scalars['String']['output']>;
};

export type FormDefinitionPayload = {
  __typename?: 'FormDefinitionPayload';
  formDefinition: FormDefinition;
};

/**
 * ENG-769 / ENG-780 forms lifecycle versioning — a content version of a form.
 * The form's schema/uiSchema now live on a form_definition_versions child row;
 * form_definitions is the stable parent every binding points at. Publish swings
 * FormDefinition.liveVersionId (a pointer, no binding moves). Retained after
 * publish (status ARCHIVED) so an in-flight order re-renders against the exact
 * version it was captured on, and so a form admin can scrub back through the
 * history. Read-only surface — Query.formDefinitionVersions. Mirrors
 * PipelineVersion.
 */
export type FormDefinitionVersion = {
  __typename?: 'FormDefinitionVersion';
  createdAt: Scalars['AWSDateTime']['output'];
  formDefinitionId: Scalars['ID']['output'];
  id: Scalars['ID']['output'];
  organizationId: Scalars['ID']['output'];
  /** When this version was promoted DRAFT→LIVE. Null for DRAFT / never-published. */
  publishedAt?: Maybe<Scalars['AWSDateTime']['output']>;
  publishedByUser?: Maybe<User>;
  publishedByUserId?: Maybe<Scalars['ID']['output']>;
  savedByUser?: Maybe<User>;
  /**
   * User who last saved this version's content. Null when their user row was
   * later deleted (FK ON DELETE SET NULL).
   */
  savedByUserId?: Maybe<Scalars['ID']['output']>;
  /**
   * The form field schema for this version (JSON-Schema-like). Frozen once
   * published; a new edit forks a new version.
   */
  schema: Scalars['AWSJSON']['output'];
  /**
   * DRAFT (the form's currently-open, unpublished editable version), LIVE (the
   * version FormDefinition.liveVersionId points at, serving new orders), or
   * ARCHIVED (a version a later publish replaced; retained for in-flight order
   * pins + history). Callers MUST badge DRAFT rows distinctly from an actual
   * publish. Never INACTIVE — that form-level status has no version-row analog.
   */
  status: PipelineStatus;
  /** Optional UI/layout hints for this version. */
  uiSchema?: Maybe<Scalars['AWSJSON']['output']>;
  updatedAt: Scalars['AWSDateTime']['output'];
  /** Monotonic counter per form_definition_id, starting at 1. */
  versionNumber: Scalars['Int']['output'];
};

export type FormDefinitionVersionConnection = {
  __typename?: 'FormDefinitionVersionConnection';
  edges: Array<FormDefinitionVersionEdge>;
  pageInfo: PageInfo;
  totalCount: Scalars['Int']['output'];
};

export type FormDefinitionVersionEdge = {
  __typename?: 'FormDefinitionVersionEdge';
  cursor: Scalars['String']['output'];
  node: FormDefinitionVersion;
};

/**
 * ENG-658 — a reusable field in the org's form-field library (e.g.
 * "Material"), usable by any number of forms via `form_version_fields`
 * (product intake, order intake, QC, step input/output, ...). The same
 * "Material" definition, with the same option set, is now the SAME row
 * wherever it's used, rather than being retyped per form. Renamed from
 * AttributeDefinition (chunk A's DB rename — form_fields, was
 * attribute_definitions — landed first; this SDL-facing rename completes
 * it).
 */
export type FormField = {
  __typename?: 'FormField';
  /**
   * Stable code — referenced by rules on any form_version_fields row
   * attaching this field.
   */
  code: Scalars['String']['output'];
  createdAt: Scalars['AWSDateTime']['output'];
  fieldType: FieldType;
  helpText?: Maybe<Scalars['String']['output']>;
  id: Scalars['ID']['output'];
  /**
   * ENG-658 Part D — how many of this field's options carry an inventory link.
   * Server-computed so the library LIST page can show "3 of 5 linked" without
   * selecting every option's item.
   */
  inventoryLinkedOptionCount: Scalars['Int']['output'];
  /**
   * False = archived; hidden from the "+ Add field" picker but left
   * attached wherever it already is (matches every other archive-not-delete
   * lifecycle in this codebase).
   */
  isActive: Scalars['Boolean']['output'];
  /**
   * ENG-658 Part D — true when this field's options are expected to name
   * inventory items, which is what turns on the per-option picker in the Fields
   * Library. A DECLARATION, not a derived flag: it is true before every option is
   * linked, so a half-configured field reports as partial rather than as done.
   * Compare `inventoryLinkedOptionCount` against `options` to render that.
   */
  isInventoryTracked: Scalars['Boolean']['output'];
  /** Display name (e.g. "Material"). */
  name: Scalars['String']['output'];
  /**
   * Full option set for enum/multiselect. Empty for other field types.
   * A given form_version_fields attachment may narrow this — see
   * form_version_fields.optionScope (services/api's attribute-library-shared.ts).
   */
  options: Array<FormFieldOption>;
  organizationId: Scalars['ID']['output'];
  /**
   * Shown as placeholder text inside the input on the intake form. Stored
   * in `custom_fields.placeholder`, not a dedicated column (small, freeform,
   * no independent lifecycle).
   */
  placeholder?: Maybe<Scalars['String']['output']>;
  updatedAt?: Maybe<Scalars['AWSDateTime']['output']>;
  /**
   * Product types currently using this field on their bound LIVE form —
   * drives the library list page's "used by N product types" column.
   */
  usedByProductTypeCount: Scalars['Int']['output'];
};

/**
 * One option value for an enum/multiselect FormField. A real row (not a
 * bare string in a JSON array) so it has a stable id — a narrowing rule or the
 * inventory link below can reference it without breaking on a label
 * rename. Renamed from AttributeOption alongside FormField.
 */
export type FormFieldOption = {
  __typename?: 'FormFieldOption';
  formFieldId: Scalars['ID']['output'];
  id: Scalars['ID']['output'];
  /** The linked item, resolved inline. Null when `inventoryItemId` is null. */
  inventoryItem?: Maybe<FormOptionInventoryItem>;
  /**
   * ENG-658 Part D — the stock item this option corresponds to ("Material =
   * Zirconia" -> the zirconia SKU). INFORMATIONAL: nothing consumes stock from
   * this link. Pipeline consumption rules are the mechanism that moves stock.
   */
  inventoryItemId?: Maybe<Scalars['ID']['output']>;
  /** Display label (e.g. "Zirconia"). */
  label: Scalars['String']['output'];
  position: Scalars['Int']['output'];
  /** Stable code (e.g. "zirconia") — what rules and stored answers reference. */
  value: Scalars['String']['output'];
};

/**
 * Form kind discriminator.
 *
 * ORDER_INTAKE and PRODUCT_INTAKE replace the former single `INTAKE` value,
 * which conflated two unrelated things and forced every consumer to re-derive
 * the distinction from a binding instead of the kind. Both still persist to the
 * same `form_definitions.form_type = 'intake'` bucket — the discriminator lives
 * in `custom_fields->>'gql_form_kind'` — so the 0152 one-active-intake-per-SKU
 * index and the ENG-216 customer read gate are unaffected by the split.
 */
export enum FormKind {
  CustomEntity = 'CUSTOM_ENTITY',
  /**
   * Order-level intake. Bound as the tenancy default
   * (`tenant_settings.order_intake_form_definition_id`) or per customer
   * (`party_order_form_assignments`). Never bound to a product type.
   */
  OrderIntake = 'ORDER_INTAKE',
  /**
   * Per-SKU intake. Bound to a product type through `product_type_forms`,
   * rendered once per order line.
   */
  ProductIntake = 'PRODUCT_INTAKE',
  Qc = 'QC',
  StepInput = 'STEP_INPUT',
  StepOutput = 'STEP_OUTPUT',
  VendorBrief = 'VENDOR_BRIEF'
}

/**
 * ENG-658 Part D — the slice of an inventory item a linked form option needs
 * to render.
 *
 * Deliberately NOT `InventoryItem`. That type declares tenantId / code / name /
 * quantityOnHand / unitOfMeasure / isActive / createdAt non-null with no per-field
 * resolvers, so returning a partial stub for it makes AppSync null the WHOLE
 * object ("Cannot return null for non-nullable field") — the ENG-203/ENG-613 class
 * of bug — while returning it in full would mean carrying a live stock balance on
 * every option of every field on the library list page. `InventoryItem.productType`
 * on that same type is the cautionary tale for the other direction: nested SDL
 * surface with no resolver behind it, silently null forever.
 */
export type FormOptionInventoryItem = {
  __typename?: 'FormOptionInventoryItem';
  /** SKU/material code. */
  code: Scalars['String']['output'];
  id: Scalars['ID']['output'];
  /**
   * False = archived. Surfaced rather than filtered: an archived item that is
   * still linked is exactly the state an admin needs to see.
   */
  isActive: Scalars['Boolean']['output'];
  name: Scalars['String']['output'];
  unitOfMeasure: Scalars['String']['output'];
};

/**
 * ENG-658 Part D — one form option that names an inventory item, plus where that
 * option is actually reachable.
 *
 * `liveFormNames` being EMPTY is a meaningful state, not just an absence: it means
 * the option carries a link but sits on no published form. The usual cause is a
 * rename — syncFormVersionFields upserts options by `value`, so changing an option's
 * value mints a NEW (unlinked) row and leaves the old one, and its link, behind.
 * This is the only surface that makes that visible.
 */
export type FormOptionReference = {
  __typename?: 'FormOptionReference';
  formFieldCode: Scalars['String']['output'];
  formFieldId: Scalars['ID']['output'];
  /** The library field this option belongs to (e.g. "Material"). */
  formFieldName: Scalars['String']['output'];
  /**
   * Names of forms whose LIVE version carries this option's field. DRAFT-only
   * usage is excluded — it is not reachable by anyone filling in a form yet.
   */
  liveFormNames: Array<Scalars['String']['output']>;
  optionId: Scalars['ID']['output'];
  /** The option's display label (e.g. "Zirconia"). */
  optionLabel: Scalars['String']['output'];
  /** The option's stable code (e.g. "zirconia"). */
  optionValue: Scalars['String']['output'];
  /** Product types whose bound intake form carries it. */
  productTypeNames: Array<Scalars['String']['output']>;
};

export type FreeFormKeyDiscoveryPayload = {
  __typename?: 'FreeFormKeyDiscoveryPayload';
  discovery: FreeFormKeyDiscoveryResult;
};

/**
 * The DISTINCT top-level JSONB key NAMES found in a bounded sample (ENG-1872 s8).
 * KEY NAMES only — never a field value. Bounded (sampled rows + capped key count).
 */
export type FreeFormKeyDiscoveryResult = {
  __typename?: 'FreeFormKeyDiscoveryResult';
  /** How many distinct keys were returned. */
  keyCount: Scalars['Int']['output'];
  /** The distinct top-level key names found (sorted, capped). */
  keys: Array<Scalars['String']['output']>;
};

/** FX rate between two currencies. MOL-621. */
export type FxRate = {
  __typename?: 'FxRate';
  asOf?: Maybe<Scalars['AWSDateTime']['output']>;
  fromCurrency: Scalars['String']['output'];
  rate: Scalars['String']['output'];
  source?: Maybe<Scalars['String']['output']>;
  toCurrency: Scalars['String']['output'];
};

/**
 * Result of generateInterSiteShipmentLabel (ENG-1265). On success labelStatus
 * is CREATED and the tracking / PDF / ZPL fields are populated; on a carrier or
 * validation failure labelStatus is FAILED and `error` carries the reason (the row
 * is persisted so the action can be retried).
 */
export type GenerateInterSiteShipmentLabelResult = {
  __typename?: 'GenerateInterSiteShipmentLabelResult';
  amountUsd?: Maybe<Scalars['Float']['output']>;
  carrier?: Maybe<ShippingCarrier>;
  /** Populated only when labelStatus is FAILED. */
  error?: Maybe<Scalars['String']['output']>;
  /**
   * Base64-encoded PDF label for immediate print. Also persisted to S3
   * (labelPdfS3Key) for re-view. Null when the carrier returned no PDF.
   */
  labelPdfBase64?: Maybe<Scalars['String']['output']>;
  /**
   * S3 object key of the persisted label PDF. Null when the bucket is unwired
   * or the carrier returned no PDF.
   */
  labelPdfS3Key?: Maybe<Scalars['String']['output']>;
  labelStatus: InterSiteShipmentLabelStatus;
  /**
   * Decoded ZPL for Zebra thermal printers. Null when the carrier API does not
   * provide it.
   */
  labelZpl?: Maybe<Scalars['String']['output']>;
  shipmentId: Scalars['ID']['output'];
  trackingNumber?: Maybe<Scalars['String']['output']>;
};

/** Input for generateInvoiceManual. MOL-615. */
export type GenerateInvoiceManualInput = {
  /**
   * ENG-3881 — required (1-500 characters, trimmed) when `invoiceDate` is a
   * back-date. Stored on the invoice, not in the audit row.
   */
  backdateReason?: InputMaybe<Scalars['String']['input']>;
  billablePartyId: Scalars['ID']['input'];
  billingEventIds?: InputMaybe<Array<Scalars['ID']['input']>>;
  dueInDays?: InputMaybe<Scalars['Int']['input']>;
  /**
   * ENG-3881 — the invoice's calendar day (YYYY-MM-DD, org time zone). Omitted
   * or the org's today = an ordinary invoice. An earlier day in the current org
   * month is a back-date: it needs clearpath.billing.invoice_backdate and
   * `backdateReason`. Future days and earlier months are refused.
   */
  invoiceDate?: InputMaybe<Scalars['AWSDate']['input']>;
  /** ENG-478 — still-active (unshipped) orders to capture + invoice now. */
  orderIds?: InputMaybe<Array<Scalars['ID']['input']>>;
  periodEnd?: InputMaybe<Scalars['AWSDateTime']['input']>;
  periodStart?: InputMaybe<Scalars['AWSDateTime']['input']>;
};

export type GenerateInvoiceManualPayload = {
  __typename?: 'GenerateInvoiceManualPayload';
  billingEventIds: Array<Scalars['ID']['output']>;
  invoiceIds: Array<Scalars['ID']['output']>;
  /**
   * ENG-3881 — the created invoices with their numbers, in `invoiceIds` order.
   * Null from an api that predates the field.
   */
  invoices?: Maybe<Array<GeneratedInvoiceRef>>;
  /**
   * ENG-2497 slice 9 — charges with NO ORDER behind them that were refused.
   *
   * A manual charge carries `order_id IS NULL`, so it cannot appear in
   * `skippedOrders` without `orderId` being a lie. Reported here instead, on the
   * same reason vocabulary, because the alternative is dropping the charge with
   * nothing in the response saying so — a billing event left pending is revenue
   * nobody is looking for.
   */
  skippedCharges: Array<GenerateInvoiceSkippedCharge>;
  /**
   * ENG-478 — orderIds that could not be captured (e.g. no resolvable
   * pricing_snapshot yet). The rest of the request still succeeded.
   */
  skippedOrderIds: Array<Scalars['ID']['output']>;
  /** Per-order skip reasons for generateInvoiceManual warnings. */
  skippedOrders: Array<GenerateInvoiceSkippedOrder>;
};

export enum GenerateInvoiceSkipReason {
  ActiveInvoice = 'ACTIVE_INVOICE',
  /**
   * ENG-2571 §F — the order's own `billingPartyId` is not the party this run
   * is invoicing. Skipped rather than captured, because capturing would stamp the
   * requested party onto the order's billing_events and silently re-point who
   * pays. Change the order's billing party (`setOrderBillingParty`) or invoice it
   * under the party it is billed to.
   */
  BillingPartyMismatch = 'BILLING_PARTY_MISMATCH',
  /**
   * ENG-2571 §F — the order IS billed to the party this run is invoicing, but
   * it already carries pending, uninvoiced billing events stamped to a different
   * one (rows the pre-ENG-2571 re-pointing left behind). Neither party can invoice
   * it until those events are voided or re-stamped; invoicing it under the party
   * they name would contradict the order's own `billingPartyId`.
   */
  CapturedEventsOtherParty = 'CAPTURED_EVENTS_OTHER_PARTY',
  CaptureFailed = 'CAPTURE_FAILED',
  /**
   * ENG-3986 — a charge of this order was priced by an assumed-$0 migrated agreement
   * line that staff have not acknowledged; the order is held off every invoice and
   * statement until they do.
   */
  FirstBillReview = 'FIRST_BILL_REVIEW',
  /**
   * ENG-3286 — charges kept landing on new orders past the run's lock bound;
   * this order was not invoiced. Invoice it again.
   */
  LockClosureCap = 'LOCK_CLOSURE_CAP',
  /**
   * ENG-2916 — capture SUCCEEDED for this order and produced NO charges, so
   * there was nothing to invoice and NO INVOICE WAS CREATED for it.
   *
   * Distinct from CAPTURE_FAILED (capture threw) and from NO_PRICING_SNAPSHOT
   * (there was nothing to project from). This one means the order HAD a frozen
   * pricing snapshot and that snapshot priced no line: the order carries no
   * products, or none of its products is priced by it. `freezePricingSnapshot`
   * writes a snapshot even when it resolved no prices, so "snapshot present" does
   * not imply "priced".
   *
   * Before this value existed such an order came back with no invoice, no charge
   * and no skip row — the operator clicked Generate and got silence. Check the
   * order's products and their pricing (agreement / price book / list price),
   * re-price the order, then invoice it again.
   */
  NothingToCapture = 'NOTHING_TO_CAPTURE',
  NoPricingSnapshot = 'NO_PRICING_SNAPSHOT',
  /**
   * ENG-2497 slice 9 — the ship-to destination is unknown, so NO INVOICE WAS
   * CREATED for this order.
   *
   * A HARD refusal, not a zero-tax line and not a note on an issued document: the
   * ship-to party's address is missing at least one of country, state/region or
   * postal code, so the invoice could not say where the work is going. Complete
   * the address and invoice again.
   *
   * ⚠️ NOT the tax engine's `NO_SHIP_TO_ADDRESS` note, which is a different fact:
   * that one says a LINE was not taxed (it fires on every order-less manual
   * charge) and appears on invoices that were issued. This says nothing was
   * issued.
   */
  NoShipToTaxAddress = 'NO_SHIP_TO_TAX_ADDRESS',
  /** ENG-3286 — customer-invoiced shipping whose label has no known amount. */
  ShippingAmountUnknown = 'SHIPPING_AMOUNT_UNKNOWN',
  /**
   * ENG-3881 S2.6c — the shipping charge's label or manual charge changed
   * while this run linked it; nothing was invoiced for the order. Invoice it again.
   */
  ShippingChargeRetry = 'SHIPPING_CHARGE_RETRY',
  /** ENG-3286 — the label currency differs from the order's charges. */
  ShippingCurrencyMismatch = 'SHIPPING_CURRENCY_MISMATCH',
  /** ENG-3286 — customer-invoiced shipping with no active outbound label. */
  ShippingLabelRequired = 'SHIPPING_LABEL_REQUIRED'
}

/**
 * ENG-2497 slice 9 — one order-less charge that could not be invoiced.
 * See `GenerateInvoiceManualPayload.skippedCharges`.
 */
export type GenerateInvoiceSkippedCharge = {
  __typename?: 'GenerateInvoiceSkippedCharge';
  billingEventId: Scalars['ID']['output'];
  message?: Maybe<Scalars['String']['output']>;
  reason: GenerateInvoiceSkipReason;
};

export type GenerateInvoiceSkippedOrder = {
  __typename?: 'GenerateInvoiceSkippedOrder';
  message?: Maybe<Scalars['String']['output']>;
  orderId: Scalars['ID']['output'];
  reason: GenerateInvoiceSkipReason;
  /**
   * ENG-2497 slice 9 — the party whose address has to be fixed, so the caller
   * can repair the refusal without a second lookup. Set ONLY on
   * `NO_SHIP_TO_TAX_ADDRESS`; the other reasons have nothing to do with a
   * party.
   */
  shipToPartyId?: Maybe<Scalars['ID']['output']>;
};

/** Input for Mutation.generateNasEnrollmentCode. */
export type GenerateNasEnrollmentCodeInput = {
  /**
   * Optional time-to-live in minutes. Omitted => 24h (1440). Clamped to a
   * 7-day maximum.
   */
  expiresInMinutes?: InputMaybe<Scalars['Int']['input']>;
  /**
   * Optional host the code is intended for. Stored on the code and used by the
   * broker as the default hostname at enrollment when the agent omits one.
   */
  hostname?: InputMaybe<Scalars['String']['input']>;
  /**
   * ENG-3842 — optional station configuration applied to the enrolling
   * station (its unit, mode and users). Validated now and re-checked at enroll;
   * a unit deleted or archived before enroll skips the preset, and so does a
   * station already configured to a different unit (move it with
   * configureNasStation).
   */
  stationPreset?: InputMaybe<NasStationPresetInput>;
};

/**
 * Payload for Mutation.generateNasEnrollmentCode. `code` is surfaced ONCE —
 * the caller MUST hand it to the operator; it is not retrievable afterwards.
 */
export type GenerateNasEnrollmentCodePayload = {
  __typename?: 'GenerateNasEnrollmentCodePayload';
  /** The full raw one-time code `nas_…`. Surfaced exactly once. Treat as a secret. */
  code: Scalars['String']['output'];
  /** First 16 characters of the code — safe to log / display indefinitely. */
  codePrefix: Scalars['String']['output'];
  /** Absolute expiry after which the code can no longer enroll. */
  expiresAt: Scalars['AWSDateTime']['output'];
  /** Server-assigned id (nas_enrollment_codes.id). */
  id: Scalars['ID']['output'];
  /**
   * ENG-3842 — non-fatal warnings about the station preset. Empty when there
   * is no preset or nothing to report.
   */
  warnings: Array<NasStationConfigWarning>;
};

/** Input for generateStatementManual. MOL-616. */
export type GenerateStatementManualInput = {
  billablePartyId: Scalars['ID']['input'];
  currency?: InputMaybe<Scalars['String']['input']>;
  periodEnd: Scalars['AWSDateTime']['input'];
  periodStart: Scalars['AWSDateTime']['input'];
};

export type GenerateStatementManualPayload = {
  __typename?: 'GenerateStatementManualPayload';
  charges: Scalars['String']['output'];
  closingBalance: Scalars['String']['output'];
  openingBalance: Scalars['String']['output'];
  payments: Scalars['String']['output'];
  statementId: Scalars['ID']['output'];
};

/** ENG-3881 — an invoice a generate call created. */
export type GeneratedInvoiceRef = {
  __typename?: 'GeneratedInvoiceRef';
  id: Scalars['ID']['output'];
  invoiceNumber: Scalars['String']['output'];
};

/** ENG-623 input for grantPartyCatalogAccess. */
export type GrantPartyCatalogAccessInput = {
  note?: InputMaybe<Scalars['String']['input']>;
  partyId: Scalars['ID']['input'];
  productTypeId: Scalars['ID']['input'];
  /**
   * Defaults to PARTY_AND_DESCENDANTS — granting at a DSO and having practices
   * inherit is the common case.
   */
  scope?: InputMaybe<PartyCatalogGrantScope>;
  /**
   * Applies only when the party holds no active grant for the product and a
   * row is inserted; defaults to now. When an active row exists, the grant
   * converges it (ENG-3054): the surviving row keeps the earliest valid_from of
   * the party's active rows and this field is ignored. A true instant, not a
   * calendar day: the admin UI writes
   * only the default and renders the window read-only, so no operator-typed day
   * is being preserved here. If you are building a date-picker for this, do NOT
   * fill this field from `new Date(dayInput).toISOString()` — that anchors to UTC
   * midnight regardless of the organization's timezone (ENG-2478). Send a
   * calendar day and anchor it server-side instead; see
   * apps/main/src/pages/admin/ExternalPartyDetailPage.ProductsTab.tsx for the
   * full note.
   */
  validFrom?: InputMaybe<Scalars['AWSDateTime']['input']>;
  /**
   * Null / omitted = open-ended. Same instant-vs-calendar-day caveat as
   * validFrom above.
   */
  validUntil?: InputMaybe<Scalars['AWSDateTime']['input']>;
};

export type GrantProductEntitlementInput = {
  expiresAt?: InputMaybe<Scalars['AWSDateTime']['input']>;
  productId: Scalars['ID']['input'];
  seatLimit?: InputMaybe<Scalars['Int']['input']>;
  status?: InputMaybe<EntitlementStatus>;
  tenantId: Scalars['ID']['input'];
  tier?: InputMaybe<Scalars['String']['input']>;
};

/** Kind of hardware integration being connected. */
export enum HardwareIntegrationKind {
  DentalLabPrinter = 'DENTAL_LAB_PRINTER',
  IntraoralScanner = 'INTRAORAL_SCANNER',
  MillCam = 'MILL_CAM'
}

/** Phase 0 hello-world placeholder. Retained for the apps/main demo screen. */
export type HelloWorld = {
  __typename?: 'HelloWorld';
  /** When this placeholder was created (ISO 8601). */
  createdAt: Scalars['AWSDateTime']['output'];
  /** Stable ID for this placeholder record. */
  id: Scalars['ID']['output'];
  /** Human-readable greeting payload. */
  message: Scalars['String']['output'];
};

/**
 * A tenant's hold taxonomy entry.
 *
 * Structural sibling of `FailureReasonCode`. `code` is the stable neutral machine
 * key every consumer matches on; `label` is tenant-authored and safe to rename —
 * a lab admin edits it to its own vocabulary without breaking anything.
 */
export type HoldReasonCode = {
  __typename?: 'HoldReasonCode';
  /**
   * Stable neutral machine key, unique per organization (e.g.
   * `AWAITING_CUSTOMER_APPROVAL`). Never shown to users.
   */
  code: Scalars['String']['output'];
  createdAt: Scalars['AWSDateTime']['output'];
  /**
   * The customer-safe message shown on the portal when `isCustomerVisible` is
   * true, distinct from the internal `label`/`description` (which may name the real
   * blocker or an internal fault). Null = fall back to generic copy at the display
   * layer.
   */
  customerFacingText?: Maybe<Scalars['String']['output']>;
  description?: Maybe<Scalars['String']['output']>;
  /**
   * True = time under this reason is deducted from the turnaround clock.
   * Seeded false for INTERNAL reasons: a lab cannot pause its own SLA for its own
   * material shortage or downed mill.
   */
  excludesFromSla: Scalars['Boolean']['output'];
  id: Scalars['ID']['output'];
  isActive: Scalars['Boolean']['output'];
  /**
   * Whether a hold under this reason is surfaced to the CUSTOMER on the portal
   * at all. Defaults false — a hold is invisible to the customer until a lab admin
   * opts it in. Distinct from `isActive`, which controls the internal Place-hold
   * dropdown.
   */
  isCustomerVisible: Scalars['Boolean']['output'];
  /**
   * Tenant-authored display label. This is what the dropdown and the hold
   * badge show.
   */
  label: Scalars['String']['output'];
  organizationId: Scalars['ID']['output'];
  /** Display order in the reason dropdown (ascending). */
  position: Scalars['Int']['output'];
  responsibleParty: HoldResponsibleParty;
  updatedAt?: Maybe<Scalars['AWSDateTime']['output']>;
};

/** Payload for the hold-reason admin mutations. */
export type HoldReasonCodePayload = {
  __typename?: 'HoldReasonCodePayload';
  holdReasonCode: HoldReasonCode;
};

/**
 * Who the lab is waiting on while a hold is open. Drives which side of the
 * turnaround/SLA clock the wait lands on, and the reporting cut a lab needs to
 * argue turnaround with a customer. `INTERNAL` means the lab is waiting on
 * itself.
 */
export enum HoldResponsibleParty {
  Customer = 'CUSTOMER',
  Internal = 'INTERNAL',
  Vendor = 'VENDOR'
}

/**
 * What a hold covers.
 *
 * `UNIT` (the default in the UI) holds ONE `OrderProduct` — the unit is the unit
 * of independent flow, so the other units on the same case keep moving. `ORDER`
 * holds the whole case.
 *
 * Both can be open at once on the same case: "the whole case is waiting for
 * approval" and "unit 3 is additionally waiting on a remake" are both true in
 * real work, and the two partial unique indexes behind this deliberately permit
 * that (one active hold per unit, one active case-scoped hold).
 */
export enum HoldScope {
  Order = 'ORDER',
  Unit = 'UNIT'
}

/**
 * Supported social / SSO identity providers the account surface can link
 * or unlink. Mirrors the control-plane identity_provider_links.provider CHECK
 * constraint. PASSKEY is exposed here so the unified "linked accounts" UI can
 * list WebAuthn credentials alongside federated SSO — server-side passkeys
 * still live in the MFA tables, not identity_provider_links.
 */
export enum IdentityProvider {
  Apple = 'APPLE',
  Google = 'GOOGLE',
  Microsoft = 'MICROSOFT',
  Passkey = 'PASSKEY'
}

/**
 * Identity-provider-link row from identity_provider_links. One row per
 * (provider, provider_subject) for the calling user's global identity.
 */
export type IdentityProviderLink = {
  __typename?: 'IdentityProviderLink';
  id: Scalars['ID']['output'];
  linkedAt: Scalars['AWSDateTime']['output'];
  provider: IdentityProvider;
  /** Stable subject id from the upstream IdP. UI truncates for display. */
  providerSubject: Scalars['String']['output'];
  unlinkedAt?: Maybe<Scalars['AWSDateTime']['output']>;
};

/**
 * Result of link / unlink identity-provider mutations.
 * - For linkIdentityProvider: `redirectUrl` is populated; `link` is null until
 *   the OAuth callback completes.
 * - For unlinkIdentityProvider: `link` holds the now-unlinked row; `redirectUrl`
 *   is null.
 */
export type IdentityProviderLinkPayload = {
  __typename?: 'IdentityProviderLinkPayload';
  link?: Maybe<IdentityProviderLink>;
  redirectUrl?: Maybe<Scalars['String']['output']>;
};

/**
 * Create a fresh dashboard by copying an org TEMPLATE (ENG-2112 / T3). The new
 * artifact is owned by the caller; the `template` flag is stripped and every widget
 * (its SemanticQuery selection only — never data) is copied, so the instance
 * recomputes under the caller. NotFound if `templateId` is not a template in the
 * caller's org.
 */
export type InstantiateAnalyticsTemplateInput = {
  name: Scalars['String']['input'];
  templateId: Scalars['ID']['input'];
};

/**
 * ENG-463/ENG-658 Part C — resolution of a product type's bound intake form.
 * See ProductType.intakeFormResolutionStatus for why this can't be a plain
 * nullable FormDefinition alone.
 */
export enum IntakeFormResolutionStatus {
  /** Connected and the bound form has a resolvable LIVE version. */
  Live = 'LIVE',
  /** Nothing connected via product_type_forms. */
  None = 'NONE',
  /** Connected, but the bound form has no resolvable LIVE version yet. */
  Unpublished = 'UNPUBLISHED'
}

/** A single per-field intake-validation violation. */
export type IntakeViolation = {
  __typename?: 'IntakeViolation';
  /** Violation kind: required | type | enum | range | rule. */
  code: Scalars['String']['output'];
  /** Field path, e.g. `products[0].shade` or `orderIntake.priority`. */
  field: Scalars['String']['output'];
  message: Scalars['String']['output'];
};

/**
 * Wire shape for an integration connection. Wraps the underlying
 * external_integrations row and surfaces the connect-time health probe.
 */
export type IntegrationConnection = {
  __typename?: 'IntegrationConnection';
  createdAt: Scalars['AWSDateTime']['output'];
  id: Scalars['ID']['output'];
  isActive: Scalars['Boolean']['output'];
  kind: HardwareIntegrationKind;
  lastError?: Maybe<Scalars['String']['output']>;
  /** Last known health check result (null until the first connect / health call). */
  lastHealth?: Maybe<IntegrationHealth>;
  lastSyncAt?: Maybe<Scalars['AWSDateTime']['output']>;
  name: Scalars['String']['output'];
  provider: Scalars['String']['output'];
  updatedAt: Scalars['AWSDateTime']['output'];
};

/** Most recent health snapshot for an integration. */
export type IntegrationHealth = {
  __typename?: 'IntegrationHealth';
  checkedAt: Scalars['AWSDateTime']['output'];
  errorCode?: Maybe<Scalars['String']['output']>;
  errorMessage?: Maybe<Scalars['String']['output']>;
  latencyMs: Scalars['Int']['output'];
  ok: Scalars['Boolean']['output'];
  upstreamVersion?: Maybe<Scalars['String']['output']>;
};

/**
 * Label lifecycle for an inter-site shipment (ENG-1265). Mirrors
 * `inter_site_shipments.label_status` (lowercase in DB).
 */
export enum InterSiteShipmentLabelStatus {
  Created = 'CREATED',
  Failed = 'FAILED',
  Pending = 'PENDING'
}

/**
 * One missing requirement keeping an entity from being valid/publishable.
 * Collected into `LifecycleValidity.reasons` so the UI can show everything at once.
 */
export type InvalidReason = {
  __typename?: 'InvalidReason';
  /**
   * Stable machine code, e.g. 'no_pipeline' | 'pipeline_not_live' |
   * 'price_not_positive' | 'intake_form_not_live'.
   */
  code: Scalars['String']['output'];
  /** The GraphQL field this reason is about, when there is one. */
  field?: Maybe<Scalars['String']['output']>;
  /** Human-readable, admin-facing explanation of what's missing. */
  message: Scalars['String']['output'];
};

/** Loopback invalidation scope. */
export enum InvalidationScope {
  FullRestart = 'FULL_RESTART',
  ReworkAndDownstream = 'REWORK_AND_DOWNSTREAM',
  ReworkOnly = 'REWORK_ONLY'
}

/**
 * Stock, materials, consumables.
 * Per docs/clearpath-architecture/03-data-model.md tier 2 (`inventory_items`).
 */
export type InventoryItem = {
  __typename?: 'InventoryItem';
  /** SKU/material code. */
  code: Scalars['String']['output'];
  createdAt: Scalars['AWSDateTime']['output'];
  customFields?: Maybe<Scalars['AWSJSON']['output']>;
  description?: Maybe<Scalars['String']['output']>;
  id: Scalars['ID']['output'];
  isActive: Scalars['Boolean']['output'];
  name: Scalars['String']['output'];
  organizationId: Scalars['ID']['output'];
  productType?: Maybe<ProductType>;
  /** Linked product type (if this item is itself a finished good). */
  productTypeId?: Maybe<Scalars['ID']['output']>;
  /** Current quantity on hand. */
  quantityOnHand: Scalars['String']['output'];
  /**
   * ENG-658 Part D — every form option that names this item, so an admin can see
   * from the stock side which answers correspond to it. Informational: none of these
   * consume stock. Empty when nothing references it.
   */
  referencedByFormOptions: Array<FormOptionReference>;
  /** Reorder threshold. */
  reorderPoint?: Maybe<Scalars['String']['output']>;
  /** Storage location. */
  storageLocation?: Maybe<Scalars['String']['output']>;
  tenantId: Scalars['ID']['output'];
  unitOfMeasure: Scalars['String']['output'];
  updatedAt?: Maybe<Scalars['AWSDateTime']['output']>;
};

export type InventoryItemPayload = {
  __typename?: 'InventoryItemPayload';
  item: InventoryItem;
};

/**
 * One movement in the append-only inventory ledger.
 * Per docs/clearpath-architecture/03-data-model.md tier 2 (`inventory_transactions`).
 */
export type InventoryTransaction = {
  __typename?: 'InventoryTransaction';
  /**
   * quantity_on_hand immediately after this row was applied, so drift between the
   * materialized balance and SUM(delta) is detectable in one query.
   */
  balanceAfter: Scalars['String']['output'];
  createdAt: Scalars['AWSDateTime']['output'];
  createdById?: Maybe<Scalars['ID']['output']>;
  /** Signed quantity change. Negative consumes, positive adds. */
  delta: Scalars['String']['output'];
  id: Scalars['ID']['output'];
  inventoryItemId: Scalars['ID']['output'];
  notes?: Maybe<Scalars['String']['output']>;
  /** When the movement happened. Authoritative for ordering, unlike createdAt. */
  occurredAt: Scalars['AWSDateTime']['output'];
  /** The order this movement is attributable to. */
  orderId?: Maybe<Scalars['ID']['output']>;
  organizationId: Scalars['ID']['output'];
  /** The step run that caused this movement, when one did. */
  stepRunId?: Maybe<Scalars['ID']['output']>;
  tenantId: Scalars['ID']['output'];
  transactionType: InventoryTransactionType;
};

export type InventoryTransactionConnection = {
  __typename?: 'InventoryTransactionConnection';
  nodes: Array<InventoryTransaction>;
  totalCount: Scalars['Int']['output'];
};

/**
 * Inventory movement kind.
 *
 * ENG-130 reshaped this enum to map 1:1 onto the DB CHECK on
 * `inventory_transactions.transaction_type`. It previously declared
 * RECEIVE/ISSUE/ADJUST/TRANSFER/SCRAP/RETURN — six values against the DB's four,
 * with names that did not correspond — and was referenced by no type, field or
 * mutation anywhere, so reshaping it had no blast radius. The DB vocabulary is
 * authoritative; the single mapping site is
 * services/api/src/handlers/inventory/transactions.ts.
 */
export enum InventoryTransactionType {
  /** Stock received. */
  Addition = 'ADDITION',
  /** A correction. On an append-only ledger this is a new compensating row, never an edit. */
  Adjustment = 'ADJUSTMENT',
  /** Material used by work that completed. */
  Consumption = 'CONSUMPTION',
  /** Material consumed by an attempt that FAILED — lost, not delivered. */
  Scrap = 'SCRAP',
  /** Reserved; ENG-130 ships no multi-location semantics. */
  Transfer = 'TRANSFER'
}

/**
 * Tenant-admin invitation lifecycle. See user_invitations table
 * (migration 0028). Derived `status` is PENDING / ACCEPTED / REVOKED /
 * EXPIRED computed at read time from {accepted_at, revoked_at,
 * expires_at}. (P0.4.3)
 */
export type Invitation = {
  __typename?: 'Invitation';
  acceptedAt?: Maybe<Scalars['AWSDateTime']['output']>;
  createdAt: Scalars['AWSDateTime']['output'];
  expiresAt?: Maybe<Scalars['AWSDateTime']['output']>;
  id: Scalars['ID']['output'];
  invitedBy?: Maybe<User>;
  invitedByUserId?: Maybe<Scalars['ID']['output']>;
  invitedEmail: Scalars['AWSEmail']['output'];
  lastSentAt?: Maybe<Scalars['AWSDateTime']['output']>;
  orgUnit?: Maybe<OrgUnit>;
  orgUnitId?: Maybe<Scalars['ID']['output']>;
  organizationId: Scalars['ID']['output'];
  resendCount: Scalars['Int']['output'];
  revokedAt?: Maybe<Scalars['AWSDateTime']['output']>;
  revokedByUserId?: Maybe<Scalars['ID']['output']>;
  role?: Maybe<Role>;
  /** Role the invitee will be assigned on accept. */
  roleId?: Maybe<Scalars['ID']['output']>;
  status: InvitationStatus;
  tenantId: Scalars['ID']['output'];
  updatedAt: Scalars['AWSDateTime']['output'];
  user?: Maybe<User>;
  userId?: Maybe<Scalars['ID']['output']>;
  userType: UserType;
};

export type InvitationConnection = {
  __typename?: 'InvitationConnection';
  edges: Array<InvitationEdge>;
  pageInfo: PageInfo;
  totalCount: Scalars['Int']['output'];
};

export type InvitationEdge = {
  __typename?: 'InvitationEdge';
  cursor: Scalars['String']['output'];
  node: Invitation;
};

/** Filter for Query.invitations. (P0.4.3) */
export type InvitationFilterInput = {
  invitedByUserId?: InputMaybe<Scalars['ID']['input']>;
  invitedEmail?: InputMaybe<Scalars['String']['input']>;
  status?: InputMaybe<Array<InvitationStatus>>;
};

/**
 * Public-safe view of an invitation, returned by `Query.invitation(token)`
 * on the AcceptInvitationPage. Carries only the fields the invitee needs to
 * decide whether to accept; it deliberately does NOT include
 * revoked_by_user_id, audit metadata, or the inviter's full User row.
 *
 * Round 1b (v2 feature restoration) — drives the public landing page in
 * apps/main + apps/portal. The token IS the invitation row's id; the resolver
 * does NOT require a Cognito match between the caller and the invitee, so
 * the page can render before the invitee signs in. Accepting + declining
 * DO require an authenticated session — see Mutation.acceptInvitation /
 * declineInvitation below.
 */
export type InvitationLookup = {
  __typename?: 'InvitationLookup';
  acceptedAt?: Maybe<Scalars['AWSDateTime']['output']>;
  expiresAt?: Maybe<Scalars['AWSDateTime']['output']>;
  id: Scalars['ID']['output'];
  /** Inviter's email; same nullability rules as inviterName. */
  inviterEmail?: Maybe<Scalars['AWSEmail']['output']>;
  /**
   * Inviter's full name when known. Null when the inviter has been
   * deleted or the invitation pre-dates audit linkage.
   */
  inviterName?: Maybe<Scalars['String']['output']>;
  /**
   * The kind of invite — staff vs. customer — so the page can route the
   * user to the right post-accept destination.
   */
  kind: UserType;
  revokedAt?: Maybe<Scalars['AWSDateTime']['output']>;
  /**
   * Pre-selected role display name (when the inviter chose one). Null when
   * the invitation is tenant-wide and the role is assigned post-accept.
   */
  roleName?: Maybe<Scalars['String']['output']>;
  status: InvitationStatus;
  /** Tenant display name resolved from the `tenants` row in the control plane. */
  tenantName?: Maybe<Scalars['String']['output']>;
};

export enum InvitationStatus {
  Accepted = 'ACCEPTED',
  Expired = 'EXPIRED',
  Pending = 'PENDING',
  Revoked = 'REVOKED'
}

/**
 * Input for inviteStaffUser (MOL-#37) — Molaris-admin staff onboarding.
 * Distinct from inviteUser (which provisions a tenant-internal customer/portal
 * user via the api Lambda). This mutation:
 *
 *   1. Creates a Cognito user in the tenant's staff pool (per-tenant pool
 *      when wired; staging falls back to TENANT_DEV_STAFF_POOL_ID).
 *   2. Inserts staff_tenant_memberships (control-plane).
 *   3. Eager-provisions a `users` row in the tenant's per-tenant Aurora
 *      cluster with user_type='internal'.
 *
 * ENG-1570: the invitee receives a "set your password" email (a reset link,
 * never a password) via the shared notification/outbox pipeline, so the admin
 * no longer relays a credential. Cognito's own email is suppressed and a permanent
 * password is set server-side (leaving the account CONFIRMED so the reset link
 * works); it is never surfaced — the payload's tempPassword is reserved and always
 * null (see InviteStaffUserPayload).
 */
export type InviteStaffUserInput = {
  email: Scalars['AWSEmail']['input'];
  fullName: Scalars['String']['input'];
  /**
   * Optional org_units.id within the target tenant. When set, the resolver now
   * persists a scoped user_roles grant (org_unit_id = this) for each role in
   * roleLocalIds, so it REQUIRES at least one role in roleLocalIds — an org unit
   * picked with no role is rejected rather than silently dropped (ENG-1620).
   * ENG-3923: with roles, either this or `wholeLab: true` is required — leaving
   * both out is refused (reason `role_scope_required`). One unit per invite; add
   * further departments afterwards with assignRole (omitting roleId). The caller
   * must hold clearpath.roles.assign over this unit (their own units and the
   * units below them), else `out_of_scope`.
   */
  orgUnitId?: InputMaybe<Scalars['ID']['input']>;
  /**
   * Optional roles.id values (within the target tenant's per-tenant DB)
   * to seed staff_tenant_memberships.role_local_ids. Empty / null = no
   * initial roles; the admin can assign later via assignRole. A person holds
   * exactly one role (ENG-3498): naming two different roles is refused (reason
   * `multiple_roles`) before anything is provisioned.
   */
  roleLocalIds?: InputMaybe<Array<Scalars['ID']['input']>>;
  tenantId: Scalars['ID']['input'];
  /**
   * ENG-3923 — grant the roles org-wide, on purpose. With roles, a staff invite
   * must place them: an org unit (`orgUnitId`) or the whole lab (this). A blank
   * org unit used to mean org-wide silently, which no later department assignment
   * could narrow. Setting both is refused (reason `role_scope_conflict`). Only a
   * caller whose own role applies to the whole lab may grant it.
   *
   * A whole-lab role places the invitee in no department, so someone whose role
   * takes or finishes steps cannot be given department work until placed in one
   * (Query.staffWithoutDepartment lists them). The decision is that work roles
   * are invited into a department; the server does not yet refuse a whole-lab
   * work role — the choice is the inviting admin's.
   */
  wholeLab?: InputMaybe<Scalars['Boolean']['input']>;
};

/**
 * Payload for inviteStaffUser (ENG-1570). Email-first: the sole hand-off is the
 * emailed "set your password" link — see emailQueued. tempPassword is reserved and
 * currently always null.
 */
export type InviteStaffUserPayload = {
  __typename?: 'InviteStaffUserPayload';
  /** Cognito sub of the newly-created staff user. */
  cognitoSub: Scalars['ID']['output'];
  /** Echo of the invited email. */
  email: Scalars['AWSEmail']['output'];
  /**
   * ENG-1570: true once the "set your password" invite email has been SCHEDULED.
   * The actual outbox write is a deferred post-commit effect, so at response time
   * it has not run yet — this reflects "scheduled", not "delivered". Any failure
   * (the enqueue, or acquiring the client to run it) emits a
   * staff.invite_email_dispatch_failed platform event; on such a failure NO email
   * was sent, so recovery is out-of-band — the admin re-sends the invite, or the
   * invitee self-initiates ForgotPassword on the staff app. On success this is the
   * sole hand-off — the admin never relays a password.
   */
  emailQueued: Scalars['Boolean']['output'];
  /**
   * staff_tenant_memberships.id — the canonical handle the admin can use
   * to revoke the staff membership later.
   */
  invitationId: Scalars['ID']['output'];
  /**
   * RESERVED and currently ALWAYS null. The resolver never surfaces a credential:
   * recovery is the emailed set-password link (Cognito ForgotPassword works because
   * the account is left CONFIRMED). Retained, nullable, only so a future non-email
   * path could populate it without a breaking SDL change.
   */
  tempPassword?: Maybe<Scalars['String']['output']>;
  /** users.id within the target tenant's per-tenant Aurora cluster. */
  userLocalId: Scalars['ID']['output'];
};

/**
 * Input for inviteUser — tenant-admin flow that provisions a Cognito user
 * and enqueues the platform invite email. Shape mirrors CreateUserInput.
 * CUSTOMER invites suppress Cognito's own email and use portal
 * /forgot-password for activation (Phase-4 / ENG-813); a temporary password
 * may still appear on the admin InviteResultPanel for out-of-band handoff
 * only. `userType` is an unconstrained UserType here, but INTERNAL is refused
 * by the resolver — staff go through inviteStaffUser (ENG-2323).
 */
export type InviteUserInput = {
  email: Scalars['AWSEmail']['input'];
  fullName: Scalars['String']['input'];
  /**
   * The ExternalParty this user is anchored to (`users.home_party_id`,
   * migration 0031). Required in practice for CUSTOMER-type invites — the
   * resolver writes this straight to `users.home_party_id`, which is what
   * `ctx.customerPartyId` / order-and-file scoping actually key off, so a
   * CUSTOMER invited without it can never own an order
   * (ENG-user-invite-home-party). Rejected when the id doesn't belong to this
   * organization or isn't a party type that can own orders.
   */
  homePartyId?: InputMaybe<Scalars['ID']['input']>;
  initialRoleAssignments?: InputMaybe<Array<AssignRoleInput>>;
  locale?: InputMaybe<Scalars['String']['input']>;
  organizationId?: InputMaybe<Scalars['ID']['input']>;
  phone?: InputMaybe<Scalars['AWSPhone']['input']>;
  /**
   * Portal role to provision the invited user with (ENG-55). Required for
   * CUSTOMER-type invites — the resolver rejects a CUSTOMER invite with this
   * unset (fix-admin-invite-portal-role) before it writes straight to
   * `users.portal_role`, which is what Cedar + RLS actually key off; a
   * CUSTOMER invited without it would get no RLS scope at all until an admin
   * backfills the role via updateUser. Both apps/portal's TeamPage and
   * apps/main's admin UserInviteSheet send this for CUSTOMER invites.
   */
  portalRole?: InputMaybe<PortalRole>;
  timezone?: InputMaybe<Scalars['String']['input']>;
  userType: UserType;
};

/**
 * Generated invoice.
 * Per docs/clearpath-architecture/03-data-model.md tier 7 (`invoices`).
 */
export type Invoice = {
  __typename?: 'Invoice';
  amountDue: Scalars['Float']['output'];
  amountPaid: Scalars['Float']['output'];
  /**
   * ENG-3881 — the reason given when the invoice was back-dated. STAFF ONLY:
   * set only for an internal caller holding clearpath.billing.invoice, null for
   * everyone else and for an invoice that was not back-dated. Composed by
   * Query.invoice; null on list queries.
   */
  backdateReason?: Maybe<Scalars['String']['output']>;
  /**
   * ENG-3881 — true when `invoiceDate` was chosen earlier than the creation
   * day (back-dated within its month by a holder of
   * clearpath.billing.invoice_backdate). STAFF ONLY: null for every non-internal
   * caller (portal sessions included).
   */
  backdated?: Maybe<Scalars['Boolean']['output']>;
  billingParty?: Maybe<ExternalParty>;
  /** Billing party. */
  billingPartyId: Scalars['ID']['output'];
  createdAt: Scalars['AWSDateTime']['output'];
  /**
   * DISPLAY-ONLY snapshot: the billing party was on credit hold when this
   * invoice was issued. Renders as a badge; does NOT block invoicing (parked).
   */
  creditHold: Scalars['Boolean']['output'];
  currency: Scalars['String']['output'];
  /**
   * The `due_date` calendar day, sent as that day's UTC midnight. Read the day
   * from the UTC date part; formatting the instant in a local zone shows the day
   * before, west of UTC.
   */
  dueAt?: Maybe<Scalars['AWSDateTime']['output']>;
  /**
   * ENG-3881 — every email send of this invoice, newest first: the automatic
   * send on issue (one per recipient) and staff re-sends. STAFF ONLY: rows name
   * customer email addresses, so a portal caller gets null. Composed by
   * Query.invoice like `paymentAttempts`; null on list queries.
   */
  emailDeliveries?: Maybe<Array<InvoiceEmailSend>>;
  /**
   * ENG-3464 — whether the invoice was emailed, from what the send path
   * recorded. Never inferred from `status`: "Approve without email" also leaves an
   * invoice ISSUED. Null where not computed.
   */
  emailDelivery?: Maybe<InvoiceEmailDelivery>;
  /** When the invoice was first emailed. Set only when `emailDelivery` is EMAILED. */
  emailedAt?: Maybe<Scalars['AWSDateTime']['output']>;
  id: Scalars['ID']['output'];
  /**
   * The invoice's calendar day in the org's time zone (`invoices.invoice_date`).
   * Set when the invoice is created and never changed; tax, the due date and the
   * number's year derive from it.
   */
  invoiceDate: Scalars['AWSDate']['output'];
  /** Human-readable invoice number. */
  invoiceNumber: Scalars['String']['output'];
  issuedAt: Scalars['AWSDateTime']['output'];
  /** Line items on this invoice. */
  lines: Array<InvoiceLine>;
  organizationId: Scalars['ID']['output'];
  paidAt?: Maybe<Scalars['AWSDateTime']['output']>;
  /**
   * Every charge attempt against this invoice, newest first — one entry per
   * logical ATTEMPT, not per stored row.
   *
   * `payment_attempts` is append-only, so an attempt is a sequence of rows
   * (pre-provider `pending` -> terminal -> `settled` or `returned`). This field
   * collapses each sequence to its current state and keys it by the root attempt
   * id, which is the id `chargeInvoice` returned. Listing rows raw would show a
   * payer two entries for one payment, one of them stuck PENDING forever.
   *
   * This is what puts payment status on the invoice: in flight ("clearing, 2-5
   * business days"), settled, or returned with its NACHA code. Composed by
   * Query.invoice alongside `lines` and `payments`; it is not populated on
   * Invoice objects returned from list queries.
   *
   * NULLABLE LIST, NON-NULL ELEMENTS. The sentence above is the reason: a field
   * declared `[PaymentAttempt!]!` that resolves to null makes AppSync fail the
   * ENTIRE Invoice object, so the list surfaces this is deliberately absent from
   * could not have returned an Invoice at all. Elements stay non-null — an
   * attempt in the list is always a real attempt.
   */
  paymentAttempts?: Maybe<Array<PaymentAttempt>>;
  /**
   * DISPLAY-ONLY snapshot of the agreed payment terms at issue time
   * (due_on_receipt / net_15 / net_30 / net_45 / net_60 / net_90). NOT wired into
   * dueAt or AR aging — terms-driven due dates are a parked ticket (migration 0318).
   */
  paymentTerms: Scalars['String']['output'];
  /**
   * Every settled transaction that MOVES this invoice's balance — one row per
   * `charge`, `refund`, `chargeback` and ACH `return`, used by the portal
   * timeline. The same set `amountPaid` is computed from, so the two always agree
   * (ENG-1988: `return` and `chargeback` used to be omitted here while still being
   * netted, so a bounced debit rendered as a payment received).
   */
  payments: Array<InvoicePayment>;
  /**
   * Customer purchase-order reference on the invoice, for display + search.
   * Null when the case carries no PO.
   */
  poNumber?: Maybe<Scalars['String']['output']>;
  status: InvoiceStatus;
  subtotal: Scalars['Float']['output'];
  /** Whether tax was configured when this invoice was issued. */
  taxDisclosure: TaxDisclosure;
  taxTotal: Scalars['Float']['output'];
  tenantId: Scalars['ID']['output'];
  total: Scalars['Float']['output'];
  updatedAt?: Maybe<Scalars['AWSDateTime']['output']>;
  voidedAt?: Maybe<Scalars['AWSDateTime']['output']>;
};

export type InvoiceConnection = {
  __typename?: 'InvoiceConnection';
  edges: Array<InvoiceEdge>;
  pageInfo: PageInfo;
  totalCount: Scalars['Int']['output'];
};

export type InvoiceEdge = {
  __typename?: 'InvoiceEdge';
  cursor: Scalars['String']['output'];
  node: Invoice;
};

/** ENG-3464 — how an invoice left draft, as recorded on the invoice. */
export enum InvoiceEmailDelivery {
  /** Emailed to the billing contact (Approve & send, or a later Re-send). */
  Emailed = 'EMAILED',
  /** Approved without email, and not emailed since. */
  NotEmailed = 'NOT_EMAILED',
  /** Left draft before this was recorded. Say nothing about email. */
  Unrecorded = 'UNRECORDED'
}

/** ENG-3881 — one email send of an invoice (`invoice_email_deliveries`). */
export type InvoiceEmailSend = {
  __typename?: 'InvoiceEmailSend';
  createdAt: Scalars['AWSDateTime']['output'];
  /** A short machine code for a FAILED row (e.g. the SES error name). */
  errorCode?: Maybe<Scalars['String']['output']>;
  id: Scalars['ID']['output'];
  kind: InvoiceEmailSendKind;
  /** Null exactly when `status` is NO_RECIPIENT. */
  recipientEmail?: Maybe<Scalars['String']['output']>;
  /** The SES message id of a SENT row, for tracing a bounce. */
  sesMessageId?: Maybe<Scalars['String']['output']>;
  status: InvoiceEmailSendStatus;
  updatedAt: Scalars['AWSDateTime']['output'];
};

export enum InvoiceEmailSendKind {
  /** Sent automatically when the invoice was issued. */
  AutoIssue = 'AUTO_ISSUE',
  /** Sent again by staff. */
  Resend = 'RESEND'
}

export enum InvoiceEmailSendStatus {
  /**
   * Claimed for sending; the send outcome is not recorded yet. A row stuck
   * here may not have been delivered — re-send it.
   */
  Claimed = 'CLAIMED',
  Failed = 'FAILED',
  /** Nobody to send to: no eligible portal user and no usable contact email. */
  NoRecipient = 'NO_RECIPIENT',
  Sent = 'SENT'
}

/** An exported .xlsx, delivered as bytes over the authenticated channel (no URL). */
export type InvoiceExportFile = {
  __typename?: 'InvoiceExportFile';
  /** Base64 file bytes. Decode to a Blob and download via an object URL. */
  contentBase64: Scalars['String']['output'];
  /**
   * Line formats: invoices the filter matched that were left out because they
   * are drafts or voids. Always 0 for INVOICE_SUMMARY.
   */
  excludedInvoiceCount: Scalars['Int']['output'];
  filename: Scalars['String']['output'];
  format: InvoiceExportFormat;
  generatedAt: Scalars['AWSDateTime']['output'];
  invoiceCount: Scalars['Int']['output'];
  mimeType: Scalars['String']['output'];
  rowCount: Scalars['Int']['output'];
};

/** ENG-3738 — Query.exportInvoices layouts. */
export enum InvoiceExportFormat {
  /** One row per invoice: the invoicing list's columns. */
  InvoiceSummary = 'INVOICE_SUMMARY',
  /** One row per invoice line; Invoice Total = the whole invoice's total incl. tax. */
  InvoiceTotal = 'INVOICE_TOTAL',
  /** One row per invoice line; Invoice Total = that line's net amount excl. tax. */
  LineNet = 'LINE_NET'
}

/** Filter for invoices. */
export type InvoiceFilterInput = {
  /** Filter by computed amount due (total minus settled payments). Staff-only list. */
  amountDue?: InputMaybe<FloatRangeInput>;
  /** ENG-3881 — true: back-dated invoices only; false: never back-dated. */
  backdated?: InputMaybe<Scalars['Boolean']['input']>;
  billingPartyIds?: InputMaybe<Array<Scalars['ID']['input']>>;
  dueAt?: InputMaybe<DateRangeInput>;
  /**
   * ENG-2572 — when true, `billingPartyIds` also matches every party BENEATH
   * the named ones in the external-party hierarchy, so naming a DSO returns its
   * practices' invoices too. Defaults to false; staff sessions only (a portal
   * session is already scoped to its own party subtree).
   */
  includeBillingPartyDescendants?: InputMaybe<Scalars['Boolean']['input']>;
  issuedAt?: InputMaybe<DateRangeInput>;
  /** Invoices that include at least one billing_event for these orders. */
  orderIds?: InputMaybe<Array<Scalars['ID']['input']>>;
  /**
   * Case-insensitive match on invoice number, the billing party's name, or the
   * name of ANY party above it in the external-party hierarchy (ENG-2572) — so a
   * DSO's name matches the invoices its practices owe. Ancestor-aware
   * unconditionally; `includeBillingPartyDescendants` does not gate it.
   */
  search?: InputMaybe<Scalars['String']['input']>;
  status?: InputMaybe<Array<InvoiceStatus>>;
};

/**
 * Line item on an invoice.
 * Per docs/clearpath-architecture/03-data-model.md tier 7 (`invoice_lines`).
 */
export type InvoiceLine = {
  __typename?: 'InvoiceLine';
  amount: Scalars['Float']['output'];
  description: Scalars['String']['output'];
  id: Scalars['ID']['output'];
  invoice?: Maybe<Invoice>;
  invoiceId: Scalars['ID']['output'];
  order?: Maybe<Order>;
  orderId?: Maybe<Scalars['ID']['output']>;
  orderProductId?: Maybe<Scalars['ID']['output']>;
  position: Scalars['Int']['output'];
  quantity: Scalars['Float']['output'];
  /**
   * Customer-readable line description composed from the linked order —
   * product name + tooth + shade (e.g. "Zirconia crown · #14 · Shade A2") —
   * instead of the raw internal charge type. Falls back to `description` when
   * the line has no linked order (manual charges). Resolved server-side from
   * the source billing event's order + order_products (ENG-1018 item 5). The
   * product segment is the customer's contracted name when one applies; lab
   * staff read "Contracted (Standard · CODE)" or "Standard (CODE)" instead.
   */
  readableDescription?: Maybe<Scalars['String']['output']>;
  /**
   * ENG-3286 — set on the one line that is the generator's SHIPPING charge
   * (identified by the label link, never by fee code). Invoice surfaces show it
   * as a summary row beside Tax, not as a line item. Populated by Query.invoice;
   * null elsewhere.
   */
  shippingCharge?: Maybe<InvoiceLineShippingCharge>;
  /**
   * Code of the SKU the line bills (product_types.code). Null for lines with
   * no product: fees, manual charges and priced-option add-ons.
   */
  skuCode?: Maybe<Scalars['String']['output']>;
  taxAmount?: Maybe<Scalars['Float']['output']>;
  taxRate?: Maybe<Scalars['Float']['output']>;
  unitPrice: Scalars['Float']['output'];
};

/** ENG-3286 — the label behind an invoiced shipping charge. */
export type InvoiceLineShippingCharge = {
  __typename?: 'InvoiceLineShippingCharge';
  /** Carrier code, lowercase (fedex / ups). */
  carrier: Scalars['String']['output'];
  trackingNumber: Scalars['String']['output'];
};

/**
 * A recorded balance-moving transaction against an invoice — a payment, or one
 * of the ways a payment comes back.
 */
export type InvoicePayment = {
  __typename?: 'InvoicePayment';
  /**
   * ALWAYS POSITIVE, whichever direction the money went. `transactionType` is
   * what says the direction: CHARGE adds to the settled balance; REFUND,
   * CHARGEBACK and RETURN each subtract it. A client that sums `amount` without
   * reading `transactionType` gets a number that is wrong by twice every
   * reversal.
   */
  amount: Scalars['Float']['output'];
  currency: Scalars['String']['output'];
  id: Scalars['ID']['output'];
  occurredAt: Scalars['AWSDateTime']['output'];
  /**
   * One of CHARGE, REFUND, CHARGEBACK, RETURN — the four kinds that move a
   * settled balance. `fee`, `adjustment` and REVERSAL rows are omitted from this
   * list. A RETURN is an ACH debit the receiving bank sent back and must NOT be
   * presented to a payer as a payment or as a refund the lab issued.
   */
  transactionType: PaymentTransactionType;
};

/** Invoice lifecycle status. */
export enum InvoiceStatus {
  Draft = 'DRAFT',
  Issued = 'ISSUED',
  Overdue = 'OVERDUE',
  Paid = 'PAID',
  PartiallyPaid = 'PARTIALLY_PAID',
  Voided = 'VOIDED',
  WrittenOff = 'WRITTEN_OFF'
}

/**
 * Input for issueInvoiceAdjustment (refund, credit memo, write-off).
 * Staff-only — step-up MFA required. MOL-622.
 */
export type IssueInvoiceAdjustmentInput = {
  /** Adjustment amount as a decimal string in the invoice's currency. */
  amount: Scalars['String']['input'];
  invoiceId: Scalars['ID']['input'];
  /**
   * Optional original payment transaction to refund against. Omit for
   * credits / write-offs.
   */
  originalTransactionId?: InputMaybe<Scalars['ID']['input']>;
  reason: Scalars['String']['input'];
  /** Adjustment kind — REFUND, CREDIT, WRITE_OFF, ADJUSTMENT. */
  type: BillingEventType;
};

export type IteroAccountCandidate = {
  __typename?: 'IteroAccountCandidate';
  accountId: Scalars['ID']['output'];
  displayName: Scalars['String']['output'];
};

/** Redacted lifecycle view; never contains tokens, secret ARNs, or credentials. */
export type IteroConnection = {
  __typename?: 'IteroConnection';
  accountId?: Maybe<Scalars['ID']['output']>;
  accountName?: Maybe<Scalars['String']['output']>;
  accounts: Array<IteroAccountCandidate>;
  attentionCode?: Maybe<Scalars['String']['output']>;
  cancelSessionId?: Maybe<Scalars['ID']['output']>;
  configured: Scalars['Boolean']['output'];
  disconnectRecoveryRequired: Scalars['Boolean']['output'];
  expiresAt?: Maybe<Scalars['AWSDateTime']['output']>;
  /**
   * Compatibility indicator for service wiring. Tenant flags no longer gate setup.
   * @deprecated Compatibility service wiring indicator. Use scannerConnections for readiness; tenant entitlements no longer apply.
   */
  featureEnabled: Scalars['Boolean']['output'];
  integrationId: Scalars['ID']['output'];
  integrationName: Scalars['String']['output'];
  recoveryInProgress: Scalars['Boolean']['output'];
  selectionSessionId?: Maybe<Scalars['ID']['output']>;
  status: IteroConnectionStatus;
};

export enum IteroConnectionStatus {
  AttentionRequired = 'ATTENTION_REQUIRED',
  Connected = 'CONNECTED',
  CredentialReplacementRequired = 'CREDENTIAL_REPLACEMENT_REQUIRED',
  Disconnected = 'DISCONNECTED',
  Expired = 'EXPIRED',
  NotConfigured = 'NOT_CONFIGURED',
  Revoked = 'REVOKED'
}

export type IteroOAuthStart = {
  __typename?: 'IteroOAuthStart';
  authorizationUrl: Scalars['AWSURL']['output'];
  expiresAt: Scalars['AWSDateTime']['output'];
  sessionId: Scalars['ID']['output'];
};

/**
 * JIT grant row as surfaced by the admin UI — shape matches the existing
 * apps/admin page expectations (tenantName joined, camelCased columns).
 */
export type JitAccessGrant = {
  __typename?: 'JitAccessGrant';
  accessLevel: Scalars['String']['output'];
  activatedAt?: Maybe<Scalars['AWSDateTime']['output']>;
  approvedAt?: Maybe<Scalars['AWSDateTime']['output']>;
  durationMinutes: Scalars['Int']['output'];
  expiresAt?: Maybe<Scalars['AWSDateTime']['output']>;
  id: Scalars['ID']['output'];
  reason: Scalars['String']['output'];
  requestedAt: Scalars['AWSDateTime']['output'];
  requestedBy: Scalars['String']['output'];
  requestedByEmail: Scalars['String']['output'];
  /** Cedar-style resource pattern (e.g. 'read:orders', 'admin:*'). */
  scope: Scalars['String']['output'];
  /**
   * Cognito sub of the nominated second approver (MOL-657) — null for
   * read_only grants that don't require two-person approval.
   */
  secondApprover?: Maybe<Scalars['String']['output']>;
  /** One of pending_approval / active / expired / revoked / rejected. */
  status: Scalars['String']['output'];
  tenantId: Scalars['ID']['output'];
  tenantName: Scalars['String']['output'];
};

/**
 * Result of activating a JIT grant — the minted short-lived session token the
 * downstream data-plane authorizers accept for the granted, time-boxed scope.
 */
export type JitActivation = {
  __typename?: 'JitActivation';
  /**
   * Signed JWT (aud=molaris-admin-jit). Carries the grant's permission scope,
   * access level and expiry; valid only until expiresAt.
   */
  accessToken: Scalars['String']['output'];
  /**
   * Token expiry (Unix epoch seconds) — equals the grant's expires_at. The
   * elevation cannot outlive this.
   */
  expiresAt: Scalars['AWSTimestamp']['output'];
  /** The activated grant's id. */
  grantId: Scalars['ID']['output'];
  /**
   * Control-plane sessions row id carried in the token; the denylist can
   * revoke at session scope.
   */
  sessionId: Scalars['ID']['output'];
};

/** JIT grant status bucket for the list view filter. */
export enum JitGrantStatusFilter {
  Active = 'active',
  History = 'history',
  Pending = 'pending'
}

/**
 * Round 5 — one card on the kanban. Maps 1:1 to an Order today; the
 * union shape leaves room for vendor-side step_runs in Round 9.
 */
export type KanbanCard = {
  __typename?: 'KanbanCard';
  assigneeName?: Maybe<Scalars['String']['output']>;
  assigneeUserId?: Maybe<Scalars['ID']['output']>;
  currentStepId?: Maybe<Scalars['ID']['output']>;
  currentStepName?: Maybe<Scalars['String']['output']>;
  dueAt?: Maybe<Scalars['AWSDateTime']['output']>;
  id: Scalars['ID']['output'];
  order: Order;
  orderId: Scalars['ID']['output'];
  priority?: Maybe<Scalars['String']['output']>;
  statusKey: Scalars['String']['output'];
  updatedAt: Scalars['AWSDateTime']['output'];
  workflowId?: Maybe<Scalars['ID']['output']>;
  workflowName?: Maybe<Scalars['String']['output']>;
};

/**
 * ENG-2210 — a page of kanban cards.
 *
 * `totalCount` is the size of the whole FILTERED set, not the page: it is what
 * lets a column header read "12 of 340" instead of implying the column holds 12.
 * That number is the point of the ticket — the bare `cards` list it replaces
 * capped at 500 with nothing in the response to say so, and the board reported
 * the truncated length as fact.
 */
export type KanbanCardConnection = {
  __typename?: 'KanbanCardConnection';
  edges: Array<KanbanCardEdge>;
  pageInfo: PageInfo;
  totalCount: Scalars['Int']['output'];
};

export type KanbanCardEdge = {
  __typename?: 'KanbanCardEdge';
  cursor: Scalars['String']['output'];
  node: KanbanCard;
};

/**
 * A printed sender. senderIdentityId null = no sender identity: the account's
 * label settings, or (companyName null) the ship-from site.
 */
export type LabelSenderChoice = {
  __typename?: 'LabelSenderChoice';
  address?: Maybe<ShippingLabelSenderAddress>;
  companyName?: Maybe<Scalars['String']['output']>;
  contactName?: Maybe<Scalars['String']['output']>;
  senderIdentityId?: Maybe<Scalars['ID']['output']>;
};

/**
 * Lead time broken out for one product type (null bucket = orders whose
 * line item has no resolvable product type).
 */
export type LeadTimeByProduct = {
  __typename?: 'LeadTimeByProduct';
  avgHours: Scalars['Float']['output'];
  medianHours: Scalars['Float']['output'];
  orderCount: Scalars['Int']['output'];
  productTypeId?: Maybe<Scalars['ID']['output']>;
  productTypeName?: Maybe<Scalars['String']['output']>;
};

/** Lead-time metrics payload (overall + per product type). */
export type LeadTimeMetrics = {
  __typename?: 'LeadTimeMetrics';
  byProductType: Array<LeadTimeByProduct>;
  overall: LeadTimeStats;
};

/** Lead-time distribution (hours) for delivered/closed orders. */
export type LeadTimeStats = {
  __typename?: 'LeadTimeStats';
  /** Average lead time in hours (created -> delivered/seated/closed). */
  avgHours: Scalars['Float']['output'];
  /** Slowest lead time in hours. */
  maxHours: Scalars['Float']['output'];
  /** Median (p50) lead time in hours. */
  medianHours: Scalars['Float']['output'];
  /** Fastest lead time in hours. */
  minHours: Scalars['Float']['output'];
  /** Orders counted (reached a done state within the window). */
  orderCount: Scalars['Int']['output'];
  /** 90th-percentile lead time in hours. */
  p90Hours: Scalars['Float']['output'];
};

/**
 * ENG-3881 — one account in the organization's chart of accounts. Accounts are
 * never deleted; an inactive one resolves as unmapped.
 */
export type LedgerAccount = {
  __typename?: 'LedgerAccount';
  accountNumber: Scalars['String']['output'];
  accountType: LedgerAccountType;
  createdAt: Scalars['AWSDateTime']['output'];
  id: Scalars['ID']['output'];
  /**
   * True once an invoice allocation references the account: its number and
   * type are then locked (upsertLedgerAccount refuses `LedgerAccountImmutable`).
   */
  inUse: Scalars['Boolean']['output'];
  isActive: Scalars['Boolean']['output'];
  name: Scalars['String']['output'];
  parentId?: Maybe<Scalars['ID']['output']>;
  updatedAt: Scalars['AWSDateTime']['output'];
};

/** ENG-3881 — QuickBooks account types for the chart of accounts. */
export enum LedgerAccountType {
  AccountsPayable = 'ACCOUNTS_PAYABLE',
  AccountsReceivable = 'ACCOUNTS_RECEIVABLE',
  Bank = 'BANK',
  CostOfGoodsSold = 'COST_OF_GOODS_SOLD',
  CreditCard = 'CREDIT_CARD',
  Equity = 'EQUITY',
  Expense = 'EXPENSE',
  FixedAsset = 'FIXED_ASSET',
  Income = 'INCOME',
  LongTermLiability = 'LONG_TERM_LIABILITY',
  OtherAsset = 'OTHER_ASSET',
  OtherCurrentAsset = 'OTHER_CURRENT_ASSET',
  OtherCurrentLiability = 'OTHER_CURRENT_LIABILITY',
  OtherExpense = 'OTHER_EXPENSE',
  OtherIncome = 'OTHER_INCOME'
}

export type LedgerCoverageFee = {
  __typename?: 'LedgerCoverageFee';
  code: Scalars['String']['output'];
  isActive: Scalars['Boolean']['output'];
  label: Scalars['String']['output'];
  reason: LedgerUnmappedReason;
};

/**
 * An option field charged at order level on orders with more than one product
 * type, with no usable override: those charges resolve unmapped. Per-unit charges
 * post to their product's account and are never listed.
 */
export type LedgerCoverageModifierField = {
  __typename?: 'LedgerCoverageModifierField';
  fieldCode: Scalars['String']['output'];
  /** The option values seen that neither `field_code=option_value` nor `field_code` maps. */
  optionValues: Array<Scalars['String']['output']>;
};

export type LedgerCoverageProductType = {
  __typename?: 'LedgerCoverageProductType';
  code: Scalars['String']['output'];
  name: Scalars['String']['output'];
  reason: LedgerUnmappedReason;
  /** DRAFT, LIVE, INACTIVE or ARCHIVED. */
  status: Scalars['String']['output'];
};

export type LedgerCoverageSystemType = {
  __typename?: 'LedgerCoverageSystemType';
  code: Scalars['String']['output'];
  label: Scalars['String']['output'];
  reason: LedgerUnmappedReason;
};

/** ENG-3881 — unmapped stamped allocations in one invoice month. */
export type LedgerCoverageUnmappedMonth = {
  __typename?: 'LedgerCoverageUnmappedMonth';
  /** Σ of the unmapped allocation amounts, NUMERIC(18,4) decimal string. */
  amount: Scalars['String']['output'];
  currency: Scalars['String']['output'];
  /** Invoice lines with at least one unmapped allocation. */
  lineCount: Scalars['Int']['output'];
  /** Invoice month `YYYY-MM` (of invoice_date, the organization's day). */
  month: Scalars['String']['output'];
};

/** ENG-3881 — never-stamped invoice lines in one invoice month. */
export type LedgerCoverageUnstampedMonth = {
  __typename?: 'LedgerCoverageUnstampedMonth';
  currency: Scalars['String']['output'];
  /** Issued invoices with at least one unstamped line. */
  documentCount: Scalars['Int']['output'];
  lineCount: Scalars['Int']['output'];
  /** Invoice month `YYYY-MM` (of invoice_date, the organization's day). */
  month: Scalars['String']['output'];
};

/**
 * ENG-3881 — a customer ledger class. Its account replaces the product account
 * for product and option revenue billed to a party in the class.
 */
export type LedgerCustomerClass = {
  __typename?: 'LedgerCustomerClass';
  accountId: Scalars['ID']['output'];
  accountIsActive: Scalars['Boolean']['output'];
  accountName: Scalars['String']['output'];
  accountNumber: Scalars['String']['output'];
  code: Scalars['String']['output'];
  id: Scalars['ID']['output'];
  name: Scalars['String']['output'];
  updatedAt: Scalars['AWSDateTime']['output'];
};

/** ENG-3881 — a fee definition as the ledger page lists it. */
export type LedgerFeeDefinition = {
  __typename?: 'LedgerFeeDefinition';
  code: Scalars['String']['output'];
  isActive: Scalars['Boolean']['output'];
  label: Scalars['String']['output'];
};

/** Which side of the revenue journal a transaction type posts to. */
export enum LedgerJournalSide {
  Credit = 'CREDIT',
  Debit = 'DEBIT',
  DebitCredit = 'DEBIT_CREDIT',
  /** Counted but not posted. */
  Excluded = 'EXCLUDED',
  /** Cash movement; outside the revenue journal. */
  NotInJournal = 'NOT_IN_JOURNAL',
  /** Reverses the accounts already stamped. */
  Reverses = 'REVERSES'
}

/** ENG-3881 — one subject mapped onto one account. */
export type LedgerMapping = {
  __typename?: 'LedgerMapping';
  accountId: Scalars['ID']['output'];
  /** False = the mapping resolves as unmapped until the account is reactivated. */
  accountIsActive: Scalars['Boolean']['output'];
  accountName: Scalars['String']['output'];
  accountNumber: Scalars['String']['output'];
  id: Scalars['ID']['output'];
  kind: LedgerMappingKind;
  subjectKey: Scalars['String']['output'];
  updatedAt: Scalars['AWSDateTime']['output'];
  updatedByUserId?: Maybe<Scalars['ID']['output']>;
};

/** ENG-3881 — mapping coverage for the organization. */
export type LedgerMappingCoverage = {
  __typename?: 'LedgerMappingCoverage';
  inactiveAccountClasses: Array<LedgerCustomerClass>;
  inactiveAccountMappings: Array<LedgerMapping>;
  /** Category mappings whose node is no longer in the catalog taxonomy. */
  orphanTaxonomyMappings: Array<LedgerMapping>;
  /**
   * Invoice lines whose current stamp posts some amount UNMAPPED, per invoice
   * month and currency. These block the journal export for that month.
   */
  unmappedAllocationMonths: Array<LedgerCoverageUnmappedMonth>;
  unmappedFees: Array<LedgerCoverageFee>;
  unmappedModifierFields: Array<LedgerCoverageModifierField>;
  /** Product types that resolve unmapped (no override, no mapped catalog node). */
  unmappedProductTypes: Array<LedgerCoverageProductType>;
  /** System-mappable transaction types with no active mapping. */
  unmappedSystemTypes: Array<LedgerCoverageSystemType>;
  /**
   * ENG-3881 — issued invoices with lines never stamped, per invoice month and
   * currency. The revenue journal posts them UNMAPPED, so they also block the
   * export for that month. Null from an api that predates the field.
   */
  unstampedMonths?: Maybe<Array<LedgerCoverageUnstampedMonth>>;
};

export type LedgerMappingInput = {
  /** Null removes the mapping. */
  accountId?: InputMaybe<Scalars['ID']['input']>;
  kind: LedgerMappingKind;
  subjectKey: Scalars['String']['input'];
};

/** What a ledger mapping's `subjectKey` names. */
export enum LedgerMappingKind {
  /** A fee definition code. */
  Fee = 'FEE',
  /** An option `field_code`, or `field_code=option_value`. */
  OptionModifier = 'OPTION_MODIFIER',
  /** A product type code. */
  ProductType = 'PRODUCT_TYPE',
  /** A ClearPath transaction type code with `systemMappable: true`. */
  System = 'SYSTEM',
  /** A catalog taxonomy node id; product types under it inherit the account. */
  TaxonomyNode = 'TAXONOMY_NODE'
}

/**
 * ENG-3881 — a ClearPath transaction type and the account it posts to when
 * mapped as a SYSTEM subject. Static list; PRODUCT_LINE, OPTION_MODIFIER, FEE and
 * CUSTOMER_CLASS are mapped through their own kinds and are not system-mappable.
 */
export type LedgerTransactionType = {
  __typename?: 'LedgerTransactionType';
  /** Null when not system-mappable or not mapped. */
  accountId?: Maybe<Scalars['ID']['output']>;
  accountIsActive?: Maybe<Scalars['Boolean']['output']>;
  accountName?: Maybe<Scalars['String']['output']>;
  accountNumber?: Maybe<Scalars['String']['output']>;
  code: Scalars['String']['output'];
  /** The account role a typical chart maps it to (guidance only). */
  defaultRole: Scalars['String']['output'];
  journalSide: LedgerJournalSide;
  label: Scalars['String']['output'];
  /** Where ClearPath records it. */
  source: Scalars['String']['output'];
  systemMappable: Scalars['Boolean']['output'];
};

/** ENG-3881 — why a subject resolves to no account. */
export enum LedgerUnmappedReason {
  CombinedLinesUnusable = 'COMBINED_LINES_UNUSABLE',
  InactiveAccount = 'INACTIVE_ACCOUNT',
  NoMapping = 'NO_MAPPING',
  NoParentProduct = 'NO_PARENT_PRODUCT',
  OrderLevelModifierAmbiguous = 'ORDER_LEVEL_MODIFIER_AMBIGUOUS',
  /** The catalog taxonomy has a cycle, a missing parent or a duplicate id. */
  TaxonomyMalformed = 'TAXONOMY_MALFORMED',
  /** Two catalog nodes at the same depth list the product type. */
  TaxonomyTie = 'TAXONOMY_TIE',
  UnknownProductType = 'UNKNOWN_PRODUCT_TYPE'
}

/**
 * Derived validity for a lifecycle-managed catalog entity (product type,
 * and — via the same shape — pipelines). Computed on read, never stored, so a
 * LIVE entity whose dependency later breaks auto-reads INVALID with no cascade.
 */
export type LifecycleValidity = {
  __typename?: 'LifecycleValidity';
  /** `status` with INVALID overlaid when incomplete. Orderable <=> LIVE. */
  effectiveStatus: EffectiveLifecycleStatus;
  /** False when `reasons` is non-empty. */
  isValid: Scalars['Boolean']['output'];
  /** Every missing requirement; empty when valid. */
  reasons: Array<InvalidReason>;
};

export type LinkExternalPartiesInput = {
  metadata?: InputMaybe<Scalars['AWSJSON']['input']>;
  partyAId: Scalars['ID']['input'];
  partyBId: Scalars['ID']['input'];
  relationshipKind: Scalars['String']['input'];
};

export type LinkIdentityProviderInput = {
  provider: IdentityProvider;
  /**
   * Where to redirect the user after the OAuth callback completes. The
   * account UI sends its own `${origin}/identities` so the page can refresh.
   */
  returnTo?: InputMaybe<Scalars['String']['input']>;
};

/**
 * Input for linkPipelineToProductType — attach an existing pipeline to a SKU
 * via the product_type_pipelines join. One pipeline per SKU: the single link is
 * always the SKU's default (mirrored to the legacy product_types.pipeline_id the
 * intake fan-out reads to route new orders), so `makeDefault` is retained only for
 * backward compatibility and is ignored. Re-linking the pipeline a SKU already
 * has is a no-op; linking a different pipeline while the SKU is already linked is
 * rejected (unlink first).
 */
export type LinkPipelineToProductTypeInput = {
  /** Deprecated / ignored — the single per-SKU link is always the default. */
  makeDefault?: InputMaybe<Scalars['Boolean']['input']>;
  pipelineId: Scalars['ID']['input'];
  productTypeId: Scalars['ID']['input'];
};

/** Input for loopbackStepRun. */
export type LoopbackStepRunInput = {
  failureReasonCodeId?: InputMaybe<Scalars['ID']['input']>;
  invalidationScope: InvalidationScope;
  loopbackToStepId: Scalars['ID']['input'];
  reasonSummary: Scalars['String']['input'];
  stepRunId: Scalars['ID']['input'];
  /**
   * How many of the line's identical units this applies to. Omitted, or the
   * line's whole quantity, acts on the whole line. Fewer splits those units off
   * onto a new line with its own pipeline run (it keeps working from here, the
   * rest of the line carries on) — the split is permanent.
   */
  unitCount?: InputMaybe<Scalars['Int']['input']>;
  /**
   * Which of the line's units this applies to, as 0-based positions in
   * `OrderProduct.unitIntakeFormResponses` (each unit's own answers). Takes
   * precedence over `unitCount`; naming every unit acts on the whole line.
   */
  unitIndexes?: InputMaybe<Array<Scalars['Int']['input']>>;
};

/**
 * Per-user MFA recovery code (hashed).
 * Per docs/clearpath-architecture/03-data-model.md tier 8 (`mfa_recovery_codes`).
 */
export type MfaRecoveryCode = {
  __typename?: 'MFARecoveryCode';
  createdAt: Scalars['AWSDateTime']['output'];
  id: Scalars['ID']['output'];
  /** Last 4 of the recovery code (display only). */
  last4: Scalars['String']['output'];
  usedAt?: Maybe<Scalars['AWSDateTime']['output']>;
  userId: Scalars['ID']['output'];
};

/** Catalog of machines a lab operates (ENG-148). Backing table `machines`. */
export type Machine = {
  __typename?: 'Machine';
  id: Scalars['ID']['output'];
  name: Scalars['String']['output'];
};

export type MachineAuthorization = {
  __typename?: 'MachineAuthorization';
  id: Scalars['ID']['output'];
  /** Catalog reference (ENG-148). machineName is derived from it. */
  machineId: Scalars['ID']['output'];
  machineName: Scalars['String']['output'];
  userId: Scalars['ID']['output'];
};

export type MachineAuthorizationDeletedPayload = {
  __typename?: 'MachineAuthorizationDeletedPayload';
  deletedId: Scalars['ID']['output'];
};

export type MachineAuthorizationPayload = {
  __typename?: 'MachineAuthorizationPayload';
  machineAuthorization: MachineAuthorization;
};

export type MachineDeletedPayload = {
  __typename?: 'MachineDeletedPayload';
  deletedId: Scalars['ID']['output'];
};

export type MachinePayload = {
  __typename?: 'MachinePayload';
  machine: Machine;
};

/**
 * ACH/SEPA pre-authorization records.
 * Per docs/clearpath-architecture/03-data-model.md tier 7 (`mandates`).
 */
export type Mandate = {
  __typename?: 'Mandate';
  createdAt: Scalars['AWSDateTime']['output'];
  expiresAt?: Maybe<Scalars['AWSDateTime']['output']>;
  id: Scalars['ID']['output'];
  mandateReference: Scalars['String']['output'];
  organizationId: Scalars['ID']['output'];
  paymentMethod?: Maybe<PaymentMethod>;
  paymentMethodId: Scalars['ID']['output'];
  signedAt?: Maybe<Scalars['AWSDateTime']['output']>;
  status: MandateStatus;
  tenantId: Scalars['ID']['output'];
};

/** Mandate consent block for ACH / SEPA additions. MOL-622. */
export type MandateConsentInput = {
  /**
   * The `PaymentClientConfig.achAuthorizationVersion` the client actually
   * DISPLAYED. Required for ACH; ignored for SEPA.
   *
   * This is the version pin that makes "displayed equals recorded" provable rather
   * than conventional. `mandateText` is not trusted for ACH — the server records
   * its own canonical wording — so without this field a browser holding a cached
   * build could accept superseded wording and we would record the current wording
   * against it. A mismatch, or an absent value, is REFUSED: the payer reloads and
   * reads the current text. Nothing is reconciled and nothing is guessed.
   */
  authorizationVersion?: InputMaybe<Scalars['String']['input']>;
  mandateText: Scalars['String']['input'];
  /**
   * Payer IP at the time of consent. The server overrides with the
   * resolver-observed source IP for compliance; clients may pass a
   * placeholder.
   */
  signedIp: Scalars['String']['input'];
};

/**
 * Status of the debit authorization behind an ACH / SEPA payment method.
 * Mirrors `mandates.status` and the `payment_methods.mandate_status`
 * denormalization the ACH charge gate reads.
 */
export enum MandateStatus {
  Active = 'ACTIVE',
  Expired = 'EXPIRED',
  Pending = 'PENDING',
  Revoked = 'REVOKED'
}

export type ManualAssignStepInput = {
  /**
   * Formerly required to reassign a step that is already in progress. Accepted and ignored: an in-progress reassignment still supersedes the current attempt and the new assignee starts a fresh attempt, without confirmation.
   * @deprecated ENG-3630: assigning a step starts it; in-progress reassignment no longer requires acknowledgement. Ignored: no longer read.
   */
  acknowledgeInProgressReassign?: InputMaybe<Scalars['Boolean']['input']>;
  acknowledgeUnavailable?: InputMaybe<Scalars['Boolean']['input']>;
  expectedAssignedUserId?: InputMaybe<Scalars['ID']['input']>;
  note?: InputMaybe<Scalars['String']['input']>;
  stepRunId: Scalars['ID']['input'];
  userId: Scalars['ID']['input'];
};

/**
 * ENG-3881 S2.6c — the server re-quotes; the client sends no amount but an
 * explicit adjustment (which needs a reason).
 */
export type ManualShippingChargeInput = {
  /** The amount to bill instead of the proposal (>= 0; 0 waives shipping). */
  adjustedAmountUsd?: InputMaybe<Scalars['Float']['input']>;
  /** Required when the billed amount differs from the proposal, or there is no quote. */
  adjustmentReason?: InputMaybe<Scalars['String']['input']>;
  /**
   * ENG-3881 — the charge the dialog showed (the adjusted amount, else the
   * proposal; 0 for a rider). When set, a different amount to store (to the cent)
   * is a Conflict with reason ShippingChargeMismatch and nothing is saved. Null
   * (older clients) is not checked.
   */
  expectedFinalAmountUsd?: InputMaybe<Scalars['Float']['input']>;
  /** The proposed amount the dialog showed; a different server quote is a Conflict. */
  expectedProposedAmountUsd?: InputMaybe<Scalars['Float']['input']>;
  /** Overrides the package default weight. */
  packageWeightOz?: InputMaybe<Scalars['Float']['input']>;
  /** Carrier service code; default Ground. */
  serviceLevel?: InputMaybe<Scalars['String']['input']>;
};

export type ManualShippingChargeQuote = {
  __typename?: 'ManualShippingChargeQuote';
  /** The most the charge may be. */
  capUsd?: Maybe<Scalars['Float']['output']>;
  /** The package default weight; a different weight needs a reason. */
  defaultPackageWeightOz?: Maybe<Scalars['Float']['output']>;
  existingCharge?: Maybe<ManualShippingChargeSummary>;
  /** The existing charge is invoiced or on a statement: it cannot be replaced. */
  existingChargeLocked: Scalars['Boolean']['output'];
  /** MATCHED_LABEL: the number is another order's label, so a reason is required. */
  labelMatchReasonRequired: Scalars['Boolean']['output'];
  markupKind?: Maybe<Scalars['String']['output']>;
  markupValue?: Maybe<Scalars['Float']['output']>;
  /** MATCHED_LABEL: the order the label was bought for. */
  matchedOrderBarcode?: Maybe<Scalars['String']['output']>;
  packageWeightOz?: Maybe<Scalars['Float']['output']>;
  /** quotedAmountUsd plus the lab markup. */
  proposedAmountUsd?: Maybe<Scalars['Float']['output']>;
  quoteFailure?: Maybe<Scalars['String']['output']>;
  /** The carrier account rate, before markup. */
  quotedAmountUsd?: Maybe<Scalars['Float']['output']>;
  /** RIDES_ON_CHARGE: the order whose charge covers this parcel. */
  ridesOnOrderBarcode?: Maybe<Scalars['String']['output']>;
  serviceLevel?: Maybe<Scalars['String']['output']>;
  serviceName?: Maybe<Scalars['String']['output']>;
  status: ManualShippingChargeStatus;
  /** The quote used a weight other than the package default. */
  weightEntered: Scalars['Boolean']['output'];
};

export enum ManualShippingChargeStatus {
  CarrierQuote = 'CARRIER_QUOTE',
  /** The quote failed: staff enter the amount and a reason. */
  ManualEntry = 'MANUAL_ENTRY',
  /** The tracking number is a ClearPath label: its order carries the charge. */
  MatchedLabel = 'MATCHED_LABEL',
  /** The lab pays shipping: no charge. */
  NotRequired = 'NOT_REQUIRED',
  /** The parcel is already charged on another order (same party): this order rides at $0. */
  RidesOnCharge = 'RIDES_ON_CHARGE'
}

export type ManualShippingChargeSummary = {
  __typename?: 'ManualShippingChargeSummary';
  adjustmentReason?: Maybe<Scalars['String']['output']>;
  finalAmountUsd: Scalars['Float']['output'];
  source: Scalars['String']['output'];
  trackingNumber: Scalars['String']['output'];
};

export type MarkAllNotificationsReadPayload = {
  __typename?: 'MarkAllNotificationsReadPayload';
  updated: Scalars['Int']['output'];
};

export type MarkNotificationReadPayload = {
  __typename?: 'MarkNotificationReadPayload';
  alreadyRead: Scalars['Boolean']['output'];
  id: Scalars['ID']['output'];
  readAt: Scalars['AWSDateTime']['output'];
};

/** Input for markOrderDelivered. */
export type MarkOrderDeliveredInput = {
  deliveredAt?: InputMaybe<Scalars['AWSDateTime']['input']>;
  notes?: InputMaybe<Scalars['String']['input']>;
  orderId: Scalars['ID']['input'];
  proofOfDeliveryFileId?: InputMaybe<Scalars['ID']['input']>;
};

/** Input for markOrderSeated. */
export type MarkOrderSeatedInput = {
  notes?: InputMaybe<Scalars['String']['input']>;
  orderId: Scalars['ID']['input'];
  seatedAt?: InputMaybe<Scalars['AWSDateTime']['input']>;
};

/** Input for markOrderShipped. */
export type MarkOrderShippedInput = {
  carrierId?: InputMaybe<Scalars['ID']['input']>;
  notes?: InputMaybe<Scalars['String']['input']>;
  orderId: Scalars['ID']['input'];
  shippedAt?: InputMaybe<Scalars['AWSDateTime']['input']>;
  trackingNumber: Scalars['String']['input'];
};

export type MarkOrdersShippedInput = {
  carrier: ShippingCarrier;
  /**
   * ENG-3881 S2.6c — required when the number is another order's ClearPath
   * label: why these orders shipped in that parcel (audited).
   */
  labelMatchReason?: InputMaybe<Scalars['String']['input']>;
  notes?: InputMaybe<Scalars['String']['input']>;
  /** 1-25 orders of one customer. */
  orderIds: Array<Scalars['ID']['input']>;
  shippedAt?: InputMaybe<Scalars['AWSDateTime']['input']>;
  /** ENG-3881 S2.6c — one order only: its customer-pays shipping charge. */
  shippingCharge?: InputMaybe<ManualShippingChargeInput>;
  trackingNumber: Scalars['String']['input'];
};

export type MarkOrdersShippedPayload = {
  __typename?: 'MarkOrdersShippedPayload';
  orders: Array<Order>;
};

/**
 * ENG-18 — the material CODE + lot number consumed at a material-consuming
 * step's completion. materialCode is an industry-neutral tenant material/catalog
 * CODE, never a product name.
 */
export type MaterialConsumptionInput = {
  lotNumber: Scalars['String']['input'];
  materialCode: Scalars['String']['input'];
  quantity?: InputMaybe<Scalars['Float']['input']>;
  unitCode?: InputMaybe<Scalars['String']['input']>;
};

/** ENG-1550 — one material lot consumed on one step attempt. */
export type MaterialConsumptionRecord = {
  __typename?: 'MaterialConsumptionRecord';
  consumedAt: Scalars['AWSDateTime']['output'];
  /**
   * Null when that user row has since been deleted — the consumption is still
   * a fact, and reporting it with no name is more honest than dropping it.
   */
  consumedByName?: Maybe<Scalars['String']['output']>;
  consumedByUserId?: Maybe<Scalars['ID']['output']>;
  id: Scalars['ID']['output'];
  lotNumber: Scalars['String']['output'];
  materialCode: Scalars['String']['output'];
  /** Null when the operator recorded a lot without a measured amount. */
  quantity?: Maybe<Scalars['Float']['output']>;
  unitCode?: Maybe<Scalars['String']['output']>;
};

/**
 * ENG-1689 — one captured material lot for a configured consumed-inventory item
 * at a step's completion. inventoryItemId must be a real
 * pipeline_step_consumed_inventory item for the step; lotNumber must be non-blank;
 * quantity is an informational NUMERIC(18,4) decimal string that never affects
 * on-hand stock (inventory_transactions owns balances).
 */
export type MaterialLotInput = {
  inventoryItemId: Scalars['ID']['input'];
  lotNumber: Scalars['String']['input'];
  quantity?: InputMaybe<Scalars['String']['input']>;
};

/**
 * Input for migrationBackfillPartyCrosswalk (ENG-2693). Binds parties that
 * were loaded through the CSV import wizard to their ABS ids, which the wizard
 * never recorded. `accountCrosswalk` is the ABS extract's account-number ->
 * custseqno map as a JSON OBJECT, the same artifact the local rehearsal runner
 * takes; there is no live ABS connection from the API.
 */
export type MigrationBackfillPartyCrosswalkInput = {
  /** AWSJSON object: {"<account number>": "<custseqno>"}. */
  accountCrosswalk: Scalars['AWSJSON']['input'];
  /**
   * Account numbers explicitly waived as known-absent from the extract. An
   * ENUMERATION, never a threshold: a percentage allowance scales with the data
   * and lets a new failure hide inside an old allowance. Every entry is recorded
   * in the audit row, including any that turned out not to be needed.
   */
  allowUnresolved?: InputMaybe<Array<Scalars['String']['input']>>;
  batchId: Scalars['String']['input'];
  mode: MigrationLoadMode;
  /** Source-adapter id, e.g. 'abs_evolution'. Part of the crosswalk key. */
  sourceSystem: Scalars['String']['input'];
};

/**
 * What a party-crosswalk backfill did, or would do. Counts only — never
 * account numbers or party payloads.
 */
export type MigrationBackfillPartyCrosswalkResult = {
  __typename?: 'MigrationBackfillPartyCrosswalkResult';
  /** Account numbers claimed by more than one party. Any is a hard refusal. */
  ambiguous: Scalars['Int']['output'];
  /** Bindings the crosswalk already held — an idempotent re-run is all of them. */
  bindingsAlreadyPresent: Scalars['Int']['output'];
  bindingsPlanned: Scalars['Int']['output'];
  bindingsWritten: Scalars['Int']['output'];
  mode: Scalars['String']['output'];
  /**
   * Parties in the cohort. Excludes practitioner parties: custseqno is an
   * ACCOUNT identity and a practitioner never holds one (ENG-2692).
   */
  partiesConsidered: Scalars['Int']['output'];
  partiesExcludedByType: Scalars['Int']['output'];
  partiesWithAccountNumbers: Scalars['Int']['output'];
  /**
   * Cohort parties carrying no account number. Reported, never an error:
   * group-level parent rows legitimately have none.
   */
  partiesWithoutAccountNumbers: Scalars['Int']['output'];
  /** A few names from the above, so a large count is actionable. */
  partiesWithoutAccountNumbersSample: Array<Scalars['String']['output']>;
  /** Why the plan was refused, when `refused` is true. */
  refusalReason?: Maybe<Scalars['String']['output']>;
  /**
   * True when the plan was refused and NOTHING was written. A refusal is
   * reported here rather than raised as an error: the resolver runs inside the
   * request transaction, which rolls back on a throw, and that would discard the
   * `:refused` audit row — the one most worth keeping. Callers must check this
   * before treating a response as applied.
   */
  refused: Scalars['Boolean']['output'];
  /** Unresolved account numbers NOT waived. Any is a hard refusal. */
  unresolvedRefused: Scalars['Int']['output'];
  /** Unresolved account numbers the caller waived by name. */
  unresolvedWaived: Array<Scalars['String']['output']>;
};

/**
 * Input for migrationResolveCrosswalk (ENG-3954). Resolve a partner's legacy
 * ids to the ClearPath uuids they landed on, for one crosswalk namespace +
 * entity kind. `sourceSystem` must be a registered migration source; `entityKind`
 * must be a crosswalk entity kind (party, product_type, order, ...); `legacyIds`
 * is 1..5000 ids, each 1..256 characters.
 */
export type MigrationCrosswalkLookupInput = {
  entityKind: Scalars['String']['input'];
  legacyIds: Array<Scalars['String']['input']>;
  sourceSystem: Scalars['String']['input'];
};

/**
 * What migrationResolveCrosswalk resolved. `mappings` carries one pair per
 * DISTINCT requested legacy id that has a crosswalk row; `requestedCount -
 * resolvedCount` is the unknown/unlanded remainder. Counts + uuid mappings only —
 * no price, subject or PHI.
 */
export type MigrationCrosswalkLookupResult = {
  __typename?: 'MigrationCrosswalkLookupResult';
  entityKind: Scalars['String']['output'];
  mappings: Array<MigrationCrosswalkMapping>;
  /** Distinct legacy ids requested (after de-duplication). */
  requestedCount: Scalars['Int']['output'];
  /** How many of them resolved to a ClearPath uuid. */
  resolvedCount: Scalars['Int']['output'];
  sourceSystem: Scalars['String']['output'];
};

/**
 * One resolved pair: the partner's legacy id and the ClearPath uuid it landed
 * on. Unknown / unlanded ids are OMITTED from the result rather than returned as a
 * null mapping (ENG-3954).
 */
export type MigrationCrosswalkMapping = {
  __typename?: 'MigrationCrosswalkMapping';
  /** migration_crosswalk.clearpath_id — the ClearPath row it loaded into. */
  clearpathId: Scalars['ID']['output'];
  /** migration_crosswalk.legacy_id — the partner's source id. */
  legacyId: Scalars['String']['output'];
};

/**
 * How far a migrator load is allowed to go (ENG-936 / C1).
 * `DRY_RUN` validates, dedups and resolves cross-wave references but never
 * reaches the per-entity insert, so it cannot see a foreign-key, check-constraint
 * or natural-key problem. `REHEARSE` runs the FULL commit path inside a SAVEPOINT
 * the loader rolls back itself — every constraint exercised, nothing written; this
 * is what a cutover rehearsal runs. `COMMIT` is the real thing, all-or-nothing
 * within the request transaction.
 */
export enum MigrationLoadMode {
  Commit = 'COMMIT',
  DryRun = 'DRY_RUN',
  Rehearse = 'REHEARSE'
}

/**
 * Input for migrationLoadOpenOrders (C7 / ENG-3458, Wave 5). `records` is the
 * canonical OPEN-order stream (open ABS orders, [Order].Status E/S/H/U) as a JSON
 * ARRAY, passed to the loader unaltered. Idempotent on (sourceSystem='abs_evolution',
 * entityKind='order', legacyId) via migration_crosswalk.
 *
 * PHI: subjects are encrypted BEFORE the write transaction through the one
 * dual-wrapped phi-crypto path (no KMS round trip holds a row lock). The key
 * params (`tenantId` control-plane id, `tenantKeyArn`, `retentionKeyArn`,
 * `subjectNamespace`) are supplied by the orchestrator exactly as to the C7
 * history Lambda; they are identifiers, not key material — the KMS authority is
 * the Lambda's encrypt-only IAM grant. INTERNAL-ONLY, Phase-6-gated: nothing loads
 * until the per-tenant retention key and the encrypt-only grant exist.
 */
export type MigrationLoadOpenOrdersInput = {
  batchId: Scalars['String']['input'];
  /** The host Intake step definition for the E/U ready order-scope row. */
  hostIntakeStepDefinitionId: Scalars['ID']['input'];
  /** orders.order_pipeline_version_id pin (host graph, §4) — mandatory. */
  hostPipelineVersionId: Scalars['ID']['input'];
  /**
   * billing_disposition for an ABS-invoiced order: 'no_charge' (default) or
   * 'billable'.
   */
  invoicedBillingDisposition?: InputMaybe<Scalars['String']['input']>;
  /** The loginless migration actor: audit actor and order_holds.placed_by. */
  migrationUserId: Scalars['ID']['input'];
  mode: MigrationLoadMode;
  /** orders.order_pipeline_id — the legacy order_pipelines row (NOT NULL FK). */
  orderPipelineId: Scalars['ID']['input'];
  /** IANA tz for the received-date timestamp (e.g. America/Toronto). */
  orgTimeZone: Scalars['String']['input'];
  /** One of off | initials | full. */
  phiMode: Scalars['String']['input'];
  records: Scalars['AWSJSON']['input'];
  /** The tenant's retention key ARN in the backup account (identifier). */
  retentionKeyArn: Scalars['String']['input'];
  /**
   * Source-adapter id; MUST be 'abs_evolution'. Defaulted when omitted; any
   * other value is refused (it would miss every crosswalk resolve).
   */
  sourceSystem?: InputMaybe<Scalars['String']['input']>;
  /** The tenant's random subject namespace; subject row ids are uuidv5 over it. */
  subjectNamespace: Scalars['String']['input'];
  /** Control-plane tenants.id, pinned into the PHI encryption context. */
  tenantId: Scalars['ID']['input'];
  /** The tenant's PHI generator key ARN (identifier, not key material). */
  tenantKeyArn: Scalars['String']['input'];
};

/**
 * Input for migrationLoadParties (Wave 1). `records` is the canonical
 * `party` NDJSON stream re-serialized as a JSON ARRAY, passed through to the
 * loader unaltered. `sourceSystem` + `batchId` form the crosswalk's idempotency
 * key, so re-running the same batch inserts nothing rather than duplicating.
 */
export type MigrationLoadPartiesInput = {
  /**
   * Parties knowingly left unbound (ENG-2694). An ENUMERATION, never a
   * threshold, for the same reason `allowUnresolved` is one on the backfill: a
   * percentage allowance scales with the data and lets a new failure hide inside
   * an old allowance.
   *
   * Each entry matches either an ACCOUNT NUMBER or a party NAME, compared with
   * surrounding whitespace trimmed and case folded. A party carrying account
   * numbers is excused only when EVERY one of them appears here. A party carrying
   * none can only be matched by name — that is the shape of a group-level parent
   * row, which no account-number-keyed backfill can ever bind.
   *
   * Practitioners are outside the cohort entirely (`custseqno` is an account
   * identity they never hold) and so never need listing here.
   */
  allowUnmapped?: InputMaybe<Array<Scalars['String']['input']>>;
  batchId: Scalars['String']['input'];
  /**
   * Operator attestation that the party crosswalk backfill has already run
   * for this organization and source system (ENG-2691). A party load into an
   * organization whose parties this source does not COVER is refused in every mode,
   * including DRY_RUN, because idempotency resolves through the crosswalk and such a
   * load duplicates the unbound parties rather than skipping them. Absent or
   * false = not attested; only an explicit true bypasses the guard, and doing so is
   * recorded in the audit row alongside the coverage it was asserted against.
   */
  crosswalkBackfilled?: InputMaybe<Scalars['Boolean']['input']>;
  mode: MigrationLoadMode;
  records: Scalars['AWSJSON']['input'];
  /** Source-adapter id, e.g. 'abs_evolution'. */
  sourceSystem: Scalars['String']['input'];
};

/**
 * Input for migrationLoadPipelines (Wave 3). `records` is the canonical
 * `pipeline` NDJSON stream (one linear record per ABS recipe) as a JSON ARRAY.
 * A batch must carry EVERY recipe of each SKU it touches: a slot is composed
 * from all of them. `locations` maps every ABS location the batch uses.
 */
export type MigrationLoadPipelinesInput = {
  batchId: Scalars['String']['input'];
  locations: Array<MigrationLocationInput>;
  mode: MigrationLoadMode;
  records: Scalars['AWSJSON']['input'];
  /** Source-adapter id, e.g. 'abs_evolution'. */
  sourceSystem: Scalars['String']['input'];
};

/**
 * Input for migrationLoadProductTypes (Wave 2). `records` is the canonical
 * `product_type` NDJSON stream re-serialized as a JSON ARRAY, passed through to
 * the loader unaltered. Crosswalk records are keyed within an organization by
 * `sourceSystem`, `entityKind`, and `legacyId`. `batchId` identifies the load run
 * and is stored on new crosswalk rows and audit entries; it is not part of the
 * idempotency key. Every record must carry status DRAFT: publishing needs a
 * default link to a LIVE pipeline, and pipelines do not exist until Wave 3.
 */
export type MigrationLoadProductTypesInput = {
  batchId: Scalars['String']['input'];
  mode: MigrationLoadMode;
  records: Scalars['AWSJSON']['input'];
  /** Source-adapter id, e.g. 'abs_evolution'. */
  sourceSystem: Scalars['String']['input'];
};

/**
 * Input for migrationLoadReferenceData (ENG-934, Wave 0). `records` is the
 * canonical reference-record stream (the adapter's `OrgConfigReference` records)
 * re-serialized as a JSON ARRAY, passed through to the loader unaltered. Reference
 * data is an org-config SEED and is deliberately crosswalk-exempt, so idempotency
 * is the TARGET natural key (organization_id, code), NOT migration_crosswalk; a
 * `batchId` identifies the run on the audit row only. ONLY hold reasons are loaded
 * (into hold_reason_codes). Every other reference kind is reported back as
 * `rejected` or `deferred` with a per-record reason — never silently dropped.
 */
export type MigrationLoadReferenceDataInput = {
  batchId: Scalars['String']['input'];
  mode: MigrationLoadMode;
  records: Scalars['AWSJSON']['input'];
  /** Source-adapter id, e.g. 'abs_evolution'. */
  sourceSystem: Scalars['String']['input'];
};

/**
 * One per-record load failure. `legacyId` is null only for a whole-payload
 * problem that cannot be attributed to a single record.
 */
export type MigrationLoaderError = {
  __typename?: 'MigrationLoaderError';
  legacyId?: Maybe<Scalars['String']['output']>;
  message: Scalars['String']['output'];
};

/** One ABS location mapped to a ClearPath site, by the site's code. */
export type MigrationLocationInput = {
  /** ABS location id, as the recipe stream's workflow_lane. */
  location: Scalars['String']['input'];
  role: MigrationSiteRole;
  siteCode: Scalars['String']['input'];
};

/**
 * What a Wave 3 pipeline load did, or would do. Counts only. A refusal is
 * returned in `errors` with nothing written, not raised, so its audit row
 * survives the request transaction.
 */
export type MigrationPipelineLoadResult = {
  __typename?: 'MigrationPipelineLoadResult';
  errors: Array<MigrationLoaderError>;
  mode: Scalars['String']['output'];
  pipelinesPublished: Scalars['Int']['output'];
  /** Recipes the crosswalk already held: an idempotent re-run is all of them. */
  recipePipelinesAlreadyLoaded: Scalars['Int']['output'];
  recipePipelinesWritten: Scalars['Int']['output'];
  recipesIn: Scalars['Int']['output'];
  /**
   * AWSJSON: the composition report (skipped, dormant, unbound slots, and
   * SKUs with a site slot but no org-wide one).
   */
  report: Scalars['AWSJSON']['output'];
  slotsAlreadyLoaded: Scalars['Int']['output'];
  /** Slots given a composed pipeline with a required variant pick. */
  slotsComposed: Scalars['Int']['output'];
  /** Slots bound straight to their only used recipe. */
  slotsDirect: Scalars['Int']['output'];
  /**
   * Recipe steps written with no department. ABS gives a location per step,
   * not a department, so assignment is set in ClearPath.
   */
  stepsWithoutDepartment: Scalars['Int']['output'];
};

/**
 * What a pipeline-batch undo removed, or would remove. Counts only. A refusal
 * is returned in `errors` with nothing written, not raised.
 */
export type MigrationPipelineUndoResult = {
  __typename?: 'MigrationPipelineUndoResult';
  composedPipelinesRemoved: Scalars['Int']['output'];
  /** The batch's recipe, slot and step rows found in the crosswalk. */
  crosswalkRowsFound: Scalars['Int']['output'];
  crosswalkRowsRemoved: Scalars['Int']['output'];
  errors: Array<MigrationLoaderError>;
  mode: Scalars['String']['output'];
  orgWideSlotsUnlinked: Scalars['Int']['output'];
  recipePipelinesRemoved: Scalars['Int']['output'];
  siteSlotsUnlinked: Scalars['Int']['output'];
  /**
   * Variant forms archived, their codes freed so the batch can be reloaded.
   * Forms are archived, not deleted: tenant sessions cannot delete a form.
   */
  variantFormsArchived: Scalars['Int']['output'];
};

/**
 * Input for migrationPublishWave3 (ENG-939 PR 4): the ABS services (C3
 * product_type legacy ids) whose migrated SKUs to publish, at most 200.
 */
export type MigrationPublishWave3Input = {
  batchId: Scalars['String']['input'];
  mode: MigrationLoadMode;
  services: Array<Scalars['String']['input']>;
  sourceSystem: Scalars['String']['input'];
};

/**
 * Per-entity reconciliation counts the wave gate asserts against the adapter
 * manifest. Invariant: `totalRecords` = `inserted` + `skippedAlreadyMigrated`
 * + `adoptedExisting` + errored records.
 */
export type MigrationReconciliationCounts = {
  __typename?: 'MigrationReconciliationCounts';
  /**
   * Records whose natural key already existed on a row this migration did NOT
   * create, so a legacy id bound itself to a natively authored ClearPath row.
   * That is a merge, not a skip: the wave gate halts on any of these.
   */
  adoptedExisting: Scalars['Int']['output'];
  entityKind: Scalars['String']['output'];
  errors: Array<MigrationLoaderError>;
  inserted: Scalars['Int']['output'];
  /**
   * Records whose legacyId was already in the crosswalk — a genuine
   * idempotent re-run skip, and the expected shape of a resumed batch.
   */
  skippedAlreadyMigrated: Scalars['Int']['output'];
  totalRecords: Scalars['Int']['output'];
};

/**
 * One reference record this loader deliberately did not load, with the reason.
 * The anti-silent-drop seam: every non-loaded record is named and explained.
 */
export type MigrationReferenceDisposition = {
  __typename?: 'MigrationReferenceDisposition';
  legacyId?: Maybe<Scalars['String']['output']>;
  reason: Scalars['String']['output'];
  referenceKind: Scalars['String']['output'];
};

/**
 * Reconciliation result for migrationLoadReferenceData. Counts-only in the
 * audit row; the response additionally returns the `rejected` / `deferred`
 * attributions so a cutover operator can see exactly what was not loaded and why.
 * Invariant: `totalRecords` = `inserted` + `skippedAlreadyMigrated` +
 * `adoptedExisting` + `rejected` + `deferred` + errored records.
 */
export type MigrationReferenceLoadResult = {
  __typename?: 'MigrationReferenceLoadResult';
  /**
   * Conflicts on a row this migration did NOT create (natively authored, or a
   * different source). A merge, not a skip — the row is left untouched and this is
   * flagged for review, never folded into skippedAlreadyMigrated.
   */
  adoptedExisting: Scalars['Int']['output'];
  /** Kinds re-homed to Wave 2/3 SKU form authoring, not loaded here. */
  deferred: Array<MigrationReferenceDisposition>;
  errors: Array<MigrationLoaderError>;
  inserted: Scalars['Int']['output'];
  /** Adapter-internal / B5 mapping kinds that no wave loads as org config. */
  rejected: Array<MigrationReferenceDisposition>;
  /**
   * Conflicts whose stored row carries THIS loader's provenance
   * (source_system + legacy_id) — a genuine idempotent re-run skip.
   */
  skippedAlreadyMigrated: Scalars['Int']['output'];
  /** The table this loader writes — always `hold_reason_codes` (Option A). */
  target: Scalars['String']['output'];
  totalRecords: Scalars['Int']['output'];
};

/**
 * How an ABS location takes part in the Wave 3 pipeline load. ORG_WIDE binds
 * the SKU's org-wide default slot, SITE binds a site row (never the default),
 * HISTORICAL binds nothing and loads a recipe only for its open lines.
 */
export enum MigrationSiteRole {
  Historical = 'HISTORICAL',
  OrgWide = 'ORG_WIDE',
  Site = 'SITE'
}

/**
 * A SKU with an org-wide pipeline binding but no migration crosswalk row
 * (B \\ A). Structurally near-impossible; reported for a human, never a gate
 * failure.
 */
export type MigrationSkuAlignmentAnomaly = {
  __typename?: 'MigrationSkuAlignmentAnomaly';
  code: Scalars['String']['output'];
  productTypeId: Scalars['ID']['output'];
};

/**
 * A DRAFT migrated SKU with no org-wide pipeline that the Wave-3 publish flip
 * will refuse (A \\ B, non-exempt).
 */
export type MigrationSkuAlignmentOrphan = {
  __typename?: 'MigrationSkuAlignmentOrphan';
  /** product_types.code. */
  code: Scalars['String']['output'];
  /** migration_crosswalk.legacy_id — the source service id. */
  legacyId: Scalars['String']['output'];
};

/**
 * What the read-only C3/C4 SKU-set alignment gate measured over whole-tenant
 * committed state. `passed` is false exactly when `orphans` is non-empty — the
 * Wave-3 publish-flip halt. Counts only beyond the orphan/anomaly lists; no
 * price, subject or PHI.
 */
export type MigrationSkuAlignmentResult = {
  __typename?: 'MigrationSkuAlignmentResult';
  anomalies: Array<MigrationSkuAlignmentAnomaly>;
  anomalyCount: Scalars['Int']['output'];
  /** Set A size — DRAFT migrated SKUs the crosswalk produced. */
  draftSkuCount: Scalars['Int']['output'];
  /** DRAFT migrated SKUs flagged pipeline_exempt, excluded from orphans. */
  exemptCount: Scalars['Int']['output'];
  /** Set B size — SKUs with an org-wide pipeline across the tenant. */
  orgWideBoundCount: Scalars['Int']['output'];
  orphanCount: Scalars['Int']['output'];
  orphans: Array<MigrationSkuAlignmentOrphan>;
  passed: Scalars['Boolean']['output'];
  sourceSystem: Scalars['String']['output'];
};

/**
 * One SKU of a Wave 3 publish: whether it went LIVE, and if the completeness
 * gate blocked it, the gate's reasons as `code: message`.
 */
export type MigrationSkuPublishOutcome = {
  __typename?: 'MigrationSkuPublishOutcome';
  productTypeCode: Scalars['String']['output'];
  published: Scalars['Boolean']['output'];
  reasons: Array<Scalars['String']['output']>;
  service: Scalars['String']['output'];
};

/**
 * Input for migrationUndoPipelines (ENG-3512): the batch to reverse, named
 * exactly as its load was.
 */
export type MigrationUndoPipelinesInput = {
  batchId: Scalars['String']['input'];
  mode: MigrationLoadMode;
  sourceSystem: Scalars['String']['input'];
};

/**
 * Input for migrationVerifySkuAlignment (ENG-2986, pilot P-03). `sourceSystem`
 * defaults to the ABS pilot source when omitted; it must name a registered
 * migration source.
 */
export type MigrationVerifySkuAlignmentInput = {
  sourceSystem?: InputMaybe<Scalars['String']['input']>;
};

/**
 * ENG-3659 (D1): what the Wave 3 flip's list-price copy did, or would do in dryRun.
 * Counts DRAFT/INACTIVE SKUs of the batch only; a LIVE SKU's price is never moved.
 */
export type MigrationWave3ListPriceCounts = {
  __typename?: 'MigrationWave3ListPriceCounts';
  /** list_price was set (or would be) from the default book. */
  listPriceSet: Scalars['Int']['output'];
  /** list_price already equals the default-book price. */
  listPriceUnchanged: Scalars['Int']['output'];
  /** Priced above 0 by the default book (list 123 or a D2 base). */
  pricedFromDefaultBook: Scalars['Int']['output'];
  /** No active default-book entry: list_price untouched, and the publish gate blocks the SKU. */
  unpriced: Scalars['Int']['output'];
  /** A $0 default-book entry: the SKDLA-confirmed zero list (D3). */
  zeroConfirmed: Scalars['Int']['output'];
};

/**
 * What a Wave 3 SKU publish did, or would do. A refusal is returned in
 * `errors` with nothing written, not raised.
 */
export type MigrationWave3PublishResult = {
  __typename?: 'MigrationWave3PublishResult';
  errors: Array<MigrationLoaderError>;
  /** ENG-3659: the D1 copy of each SKU's default-book price into list_price, by class. */
  listPrice: MigrationWave3ListPriceCounts;
  mode: Scalars['String']['output'];
  outcomes: Array<MigrationSkuPublishOutcome>;
  servicesIn: Scalars['Int']['output'];
  /** Already LIVE, so counted and left alone: an idempotent re-run is all of them. */
  skusAlreadyLive: Scalars['Int']['output'];
  /** Blocked by the completeness gate; each stays DRAFT, with reasons in outcomes. */
  skusBlocked: Scalars['Int']['output'];
  skusPublished: Scalars['Int']['output'];
};

/** Dashboard headline counters shown on admin.molaris.ai home. */
export type MolarisAdminDashboardSummary = {
  __typename?: 'MolarisAdminDashboardSummary';
  activeTenants: Scalars['Int']['output'];
  /**
   * Billing total (USD) for the current calendar month to-date across all
   * tenants. Sum of billing_aggregations.total_billable_amount where
   * period_start falls in the current month.
   */
  billingMtdUsd: Scalars['Float']['output'];
  pendingJitGrants: Scalars['Int']['output'];
  provisioningTenants: Scalars['Int']['output'];
  suspendedTenants: Scalars['Int']['output'];
};

/**
 * Namespace for Molaris-admin mutations. State transitions on tenants.status
 * are guarded by the handler's ALLOWED_TRANSITIONS matrix.
 */
export type MolarisAdminMutation = {
  __typename?: 'MolarisAdminMutation';
  /**
   * Activate a fully-approved JIT grant: the requester mints a short-lived,
   * scope-bounded JIT session token (ENG-292). Requires a fresh WebAuthn step-up
   * (stepUpRef). The token's permission scope, access level and expiry are drawn
   * from the persisted grant; mint fails closed if the grant is not approved,
   * is expired/revoked, or is not owned by the caller. molaris.jit.request gates
   * it — the requester's own capability — and mint re-checks grant ownership.
   */
  activateJitGrant: JitActivation;
  /** Grant a permission to a tenant role. Writes role_permissions + role_definition_audit. */
  addPermissionToRole: RolePayload;
  /**
   * ENG-231 — admin "reset a locked-out user's MFA" safety valve (Phase 0).
   * Single-approver; step-up MFA is required on the ACTING admin. Covers all
   * three user classes (see AdminMfaResetUserClass) — each lives in a
   * different Cognito pool, resolved server-side from userClass/userId/
   * tenantId. Disables Cognito-native TOTP + SMS MFA, wipes auth-service
   * TOTP/WebAuthn/recovery-code module state, and bumps the target's session
   * watermark so every outstanding token fails on its next check. Two-person
   * approval is a separate, future mutation (ENG-226) — NOT this one.
   */
  adminResetUserMfa: AdminResetUserMfaPayload;
  /**
   * Approve a pending JIT grant. Server re-verifies (a) approver !=
   * requester, (b) caller's mfa_verified_at claim is fresh (< 5 min).
   * On success sets granted_by_cognito_sub + granted_at.
   */
  approveJitGrant: JitAccessGrant;
  /**
   * BA.4 (2026-04-24) — forensic Athena query. SQL allow-list enforced
   * server-side: single SELECT referencing only the audit_log table, with
   * a tenant_id WHERE predicate. Returns an Athena execution id + presigned
   * results URL.
   */
  auditQuery: AuditQueryResponse;
  /**
   * Cancel a tenant — queues deprovisioning at effectiveAt (default: now).
   * Alias surface for queueDeprovisioning; carries a mandatory reason.
   */
  cancelTenant: TenantPayload;
  /** Insert a tenant row (status=provisioning) and kick off the provisioner Step Functions execution. */
  createTenant: CreateTenantPayload;
  /**
   * ENG-447 — EXECUTE a RECEIVED DSAR: actually runs the export
   * (services/compliance runDsarExport) or erasure (softDeleteSubject) against
   * the tenant DB, records the real outcome on the request (bundle key + row
   * counts, or per-table removal counts), and advances RECEIVED → IN_PROGRESS →
   * READY. A fail-closed abort (subject not found in org, undeletable object)
   * lands the request in FAILED with the reason, never a false success. This is
   * the wiring that makes the "erase" action actually erase; before it, the
   * state machine could reach DELIVERED with no erasure code ever running.
   */
  executeDsarRequest: DsarRequest;
  /** INSERT or UPDATE (on tenant_id,product_id) a product entitlement. */
  grantProductEntitlement: TenantProductEntitlement;
  /**
   * Provision a brand-new tenant end-to-end (REQUESTED -> ACTIVE).
   * Invokes the tenant-provisioner Lambda: allocates DB, creates Cognito app
   * client, runs migrations 0001..0015, applies the industry seed template,
   * registers tenant_settings/tenant_security_settings, emits
   * tenant.provisioned, and flips status=active. Typical duration: <5 min.
   */
  provisionTenant: ProvisionTenantPayload;
  /** Queue deprovisioning — moves status=deprovisioning and triggers the deprovisioner SFN. */
  queueDeprovisioning: QueueDeprovisioningPayload;
  /**
   * Reinstate a SUSPENDED tenant — transitions status=suspended → active.
   * Convenience wrapper over updateTenantStatus with the reverse direction.
   */
  reinstateTenant: TenantPayload;
  /** Reject a pending JIT grant. Writes revoke_reason and revoked_at. */
  rejectJitGrant: JitAccessGrant;
  /** Revoke a permission from a tenant role. Writes role_permissions delete + role_definition_audit. */
  removePermissionFromRole: RolePayload;
  /**
   * P1 #53 / Bible §12:119-123 — re-enqueue all messages currently in the
   * webhook DLQ for the named tenant onto the primary dispatcher queue.
   * Wraps services/webhook-dispatcher/src/admin.ts#replayFailedDelivery and
   * caps at 10 messages per call (SQS receive-batch ceiling). Returns the
   * count actually replayed.
   */
  replayWebhookDlq: WebhookDlqReplayResult;
  /**
   * BA.4 (2026-04-24) — request an Athena-backed export of a tenant's
   * audit_log slice. Returns presigned 24h URLs for the data bundle + cover
   * sheet PDF. The tenant S3 export bucket has Object Lock; the presigned
   * URL gives one-shot read access only.
   */
  requestAuditExport: AuditExportResponse;
  /**
   * Create a new pending JIT access grant. Requester drawn from caller's
   * cognito_sub. For access_level='write' a second approver cognito_sub is
   * required (MOL-657). Second approver cannot equal requester.
   */
  requestJitGrant: JitAccessGrant;
  /**
   * Revoke an entitlement by its row id (vs. (tenantId, productId) composite).
   * Mirrors revokeProductEntitlement; uses the same cascade-kick + audit paths.
   */
  revokeEntitlement: TenantProductEntitlementPayload;
  /** Revoke an active or pending JIT grant. Reason is audit-required. */
  revokeJitGrant: JitAccessGrant;
  /** Set entitlement status=cancelled and cascade-kick sessions via Redis. */
  revokeProductEntitlement: TenantProductEntitlement;
  /**
   * ENG-88 Level A white-label — set (or clear) a tenant's transactional-
   * email branding (logo/name/colors/friendly From name). Best-effort syncs the
   * tenant-staff Cognito pool's EmailConfiguration.From when the pool already
   * exists; see SetTenantBrandingPayload.emailConfigSyncWarning.
   */
  setTenantBranding: SetTenantBrandingPayload;
  /** Helper that wraps updateTenantStatus with status=SUSPENDED. */
  suspendTenant: Tenant;
  /**
   * BA.5 (2026-04-24) — admin transition of a DSAR request. Wraps
   * services/compliance/src/dsar/status.ts#transitionDsarState; rejects
   * illegal transitions (state-machine invariants in LEGAL_TRANSITIONS).
   */
  transitionDsarRequest: DsarRequest;
  /** Change entitlement tier by row id. Mirrors updateTenantTier. */
  updateEntitlementTier: TenantProductEntitlementPayload;
  /** State-machine-guarded tenant status change. Only active↔suspended and active/suspended→deprovisioning are allowed. */
  updateTenantStatus: Tenant;
  /** Change entitlement tier without revoking. */
  updateTenantTier: TenantProductEntitlement;
};


/**
 * Namespace for Molaris-admin mutations. State transitions on tenants.status
 * are guarded by the handler's ALLOWED_TRANSITIONS matrix.
 */
export type MolarisAdminMutationActivateJitGrantArgs = {
  input: ActivateJitGrantInput;
};


/**
 * Namespace for Molaris-admin mutations. State transitions on tenants.status
 * are guarded by the handler's ALLOWED_TRANSITIONS matrix.
 */
export type MolarisAdminMutationAddPermissionToRoleArgs = {
  input: AddPermissionToRoleInput;
};


/**
 * Namespace for Molaris-admin mutations. State transitions on tenants.status
 * are guarded by the handler's ALLOWED_TRANSITIONS matrix.
 */
export type MolarisAdminMutationAdminResetUserMfaArgs = {
  input: AdminResetUserMfaInput;
};


/**
 * Namespace for Molaris-admin mutations. State transitions on tenants.status
 * are guarded by the handler's ALLOWED_TRANSITIONS matrix.
 */
export type MolarisAdminMutationApproveJitGrantArgs = {
  input: ApproveJitGrantInput;
};


/**
 * Namespace for Molaris-admin mutations. State transitions on tenants.status
 * are guarded by the handler's ALLOWED_TRANSITIONS matrix.
 */
export type MolarisAdminMutationAuditQueryArgs = {
  input: AuditQueryInput;
};


/**
 * Namespace for Molaris-admin mutations. State transitions on tenants.status
 * are guarded by the handler's ALLOWED_TRANSITIONS matrix.
 */
export type MolarisAdminMutationCancelTenantArgs = {
  input: CancelTenantInput;
};


/**
 * Namespace for Molaris-admin mutations. State transitions on tenants.status
 * are guarded by the handler's ALLOWED_TRANSITIONS matrix.
 */
export type MolarisAdminMutationCreateTenantArgs = {
  input: CreateTenantInput;
};


/**
 * Namespace for Molaris-admin mutations. State transitions on tenants.status
 * are guarded by the handler's ALLOWED_TRANSITIONS matrix.
 */
export type MolarisAdminMutationExecuteDsarRequestArgs = {
  input: ExecuteDsarRequestInput;
};


/**
 * Namespace for Molaris-admin mutations. State transitions on tenants.status
 * are guarded by the handler's ALLOWED_TRANSITIONS matrix.
 */
export type MolarisAdminMutationGrantProductEntitlementArgs = {
  input: GrantProductEntitlementInput;
};


/**
 * Namespace for Molaris-admin mutations. State transitions on tenants.status
 * are guarded by the handler's ALLOWED_TRANSITIONS matrix.
 */
export type MolarisAdminMutationProvisionTenantArgs = {
  input: ProvisionTenantInput;
};


/**
 * Namespace for Molaris-admin mutations. State transitions on tenants.status
 * are guarded by the handler's ALLOWED_TRANSITIONS matrix.
 */
export type MolarisAdminMutationQueueDeprovisioningArgs = {
  input: QueueDeprovisioningInput;
};


/**
 * Namespace for Molaris-admin mutations. State transitions on tenants.status
 * are guarded by the handler's ALLOWED_TRANSITIONS matrix.
 */
export type MolarisAdminMutationReinstateTenantArgs = {
  input: ReinstateTenantInput;
};


/**
 * Namespace for Molaris-admin mutations. State transitions on tenants.status
 * are guarded by the handler's ALLOWED_TRANSITIONS matrix.
 */
export type MolarisAdminMutationRejectJitGrantArgs = {
  input: RejectJitGrantInput;
};


/**
 * Namespace for Molaris-admin mutations. State transitions on tenants.status
 * are guarded by the handler's ALLOWED_TRANSITIONS matrix.
 */
export type MolarisAdminMutationRemovePermissionFromRoleArgs = {
  input: RemovePermissionFromRoleInput;
};


/**
 * Namespace for Molaris-admin mutations. State transitions on tenants.status
 * are guarded by the handler's ALLOWED_TRANSITIONS matrix.
 */
export type MolarisAdminMutationReplayWebhookDlqArgs = {
  tenantId: Scalars['ID']['input'];
};


/**
 * Namespace for Molaris-admin mutations. State transitions on tenants.status
 * are guarded by the handler's ALLOWED_TRANSITIONS matrix.
 */
export type MolarisAdminMutationRequestAuditExportArgs = {
  input: RequestAuditExportInput;
};


/**
 * Namespace for Molaris-admin mutations. State transitions on tenants.status
 * are guarded by the handler's ALLOWED_TRANSITIONS matrix.
 */
export type MolarisAdminMutationRequestJitGrantArgs = {
  input: RequestJitGrantInput;
};


/**
 * Namespace for Molaris-admin mutations. State transitions on tenants.status
 * are guarded by the handler's ALLOWED_TRANSITIONS matrix.
 */
export type MolarisAdminMutationRevokeEntitlementArgs = {
  id: Scalars['ID']['input'];
  reason?: InputMaybe<Scalars['String']['input']>;
};


/**
 * Namespace for Molaris-admin mutations. State transitions on tenants.status
 * are guarded by the handler's ALLOWED_TRANSITIONS matrix.
 */
export type MolarisAdminMutationRevokeJitGrantArgs = {
  id: Scalars['ID']['input'];
  reason: Scalars['String']['input'];
};


/**
 * Namespace for Molaris-admin mutations. State transitions on tenants.status
 * are guarded by the handler's ALLOWED_TRANSITIONS matrix.
 */
export type MolarisAdminMutationRevokeProductEntitlementArgs = {
  input: RevokeProductEntitlementInput;
};


/**
 * Namespace for Molaris-admin mutations. State transitions on tenants.status
 * are guarded by the handler's ALLOWED_TRANSITIONS matrix.
 */
export type MolarisAdminMutationSetTenantBrandingArgs = {
  input: SetTenantBrandingInput;
};


/**
 * Namespace for Molaris-admin mutations. State transitions on tenants.status
 * are guarded by the handler's ALLOWED_TRANSITIONS matrix.
 */
export type MolarisAdminMutationSuspendTenantArgs = {
  input: SuspendTenantInput;
};


/**
 * Namespace for Molaris-admin mutations. State transitions on tenants.status
 * are guarded by the handler's ALLOWED_TRANSITIONS matrix.
 */
export type MolarisAdminMutationTransitionDsarRequestArgs = {
  input: TransitionDsarRequestInput;
};


/**
 * Namespace for Molaris-admin mutations. State transitions on tenants.status
 * are guarded by the handler's ALLOWED_TRANSITIONS matrix.
 */
export type MolarisAdminMutationUpdateEntitlementTierArgs = {
  id: Scalars['ID']['input'];
  newTier: Scalars['String']['input'];
};


/**
 * Namespace for Molaris-admin mutations. State transitions on tenants.status
 * are guarded by the handler's ALLOWED_TRANSITIONS matrix.
 */
export type MolarisAdminMutationUpdateTenantStatusArgs = {
  input: UpdateTenantStatusInput;
};


/**
 * Namespace for Molaris-admin mutations. State transitions on tenants.status
 * are guarded by the handler's ALLOWED_TRANSITIONS matrix.
 */
export type MolarisAdminMutationUpdateTenantTierArgs = {
  input: UpdateTenantTierInput;
};

/**
 * Namespace for Molaris-admin read operations. Every field is Cedar-gated by a
 * `molaris.*.*` permission key and resolved by the control-plane API Lambda
 * (services/control-plane/src/api/handler.ts).
 */
export type MolarisAdminQuery = {
  __typename?: 'MolarisAdminQuery';
  /** Billing aggregations for (tenant, period). */
  billingAggregations: Array<BillingAggregation>;
  /**
   * Cross-tenant billing rollup (aggregated across billing_aggregations)
   * for a YYYY-MM period. Optionally filter to a single tenant.
   */
  billingRollups: Array<BillingRollupRow>;
  /**
   * Dashboard headline counters. One control-plane aggregate query —
   * active / provisioning / suspended tenant counts, pending JIT approvals,
   * and billing month-to-date USD (sum over billing_aggregations for the
   * current calendar month).
   */
  dashboardSummary: MolarisAdminDashboardSummary;
  /**
   * BA.5 (2026-04-24) — DSAR request listing. Filter by tenant / state /
   * kind. Default limit 100, max 500. Sorted newest-first by created_at.
   */
  dsarRequests: Array<DsarRequest>;
  /**
   * Compute the effective feature-flag value for a (tenant, role, user)
   * triple. Mirrors the runtime precedence walk in @clearpath/feature-flags
   * (user > role > tenant > tenant_tier > product > global > default). Used
   * by the admin flag manager's "effective value" explorer.
   */
  evaluateFeatureFlag: FeatureFlagEvaluationResult;
  /** Write-only credential setup targets for one tenant's iTero integrations. */
  iteroIntegrations: Array<PlatformIteroIntegration>;
  /** Single JIT grant detail. Returns null for unknown ids. */
  jitGrant?: Maybe<JitAccessGrant>;
  /**
   * JIT grant list, filterable by status bucket ('pending' = pending
   * approval, 'active' = granted + not expired + not revoked, 'history' =
   * everything else).
   */
  jitGrants: Array<JitAccessGrant>;
  /**
   * Current molaris-admin viewer — cognito_sub + email. Uses authorizer-
   * provided claims; no DB hit. Lets the JIT request form filter the
   * current user out of the approver list.
   */
  me: MolarisStaffViewer;
  /**
   * Open (non-OK) platform alerts. Sourced from the status-page DynamoDB
   * table — rows are written by the SNS → status subscriber when an alarm
   * transitions away from OK, and cleared when it returns.
   *
   * Each row is the *current* state of one CloudWatch alarm; there's no
   * historical replay here. For historical alert flow, query CloudWatch
   * directly (link surfaced from admin.molaris.ai/observability).
   */
  openAlerts: Array<PlatformAlert>;
  /**
   * Pending JIT access grants (status='pending_approval' — i.e., granted_by
   * IS NULL and revoked_at IS NULL). Drives the dashboard review queue.
   */
  pendingJitGrants: Array<JitAccessGrant>;
  /**
   * Full control-plane permission registry. Groups by product and resource
   * type client-side. Filterable for building a scoped role editor (e.g. only
   * show 'clearpath.*' when editing a clearpath role).
   */
  permissionRegistry: Array<AdminPermissionRegistryEntry>;
  /**
   * Platform audit log — append-only rows from control-plane audit_log.
   * Filterable by tenant / actor email / action / time range.
   */
  platformAudit: Array<PlatformAuditEvent>;
  /** Product registry — clearpath, orovia, molaris-admin. */
  products: Array<Product>;
  /**
   * Most-recent incidents as published to status.molaris.ai. Pulled from
   * the same status-page DynamoDB table so operator + customer views stay
   * in sync.
   */
  recentPlatformIncidents: Array<PlatformIncident>;
  /**
   * Recent tenant provisioning lifecycle events. Sourced from audit_log
   * rows where action matches 'tenants.*' or 'tenant.provision.*'.
   */
  recentProvisioningEvents: Array<ProvisioningEvent>;
  /**
   * Runbook markdown for a given alert slug — e.g. `auth-service-down`
   * returns the contents of docs/runbooks/auth-service-down.md. Null when no
   * runbook matches the slug.
   */
  runbook?: Maybe<PlatformRunbook>;
  /**
   * P1 #27 — secrets across every tenant whose `next_rotation_due_at`
   * falls in the next 30 days (or is past-due). Drives the rotation
   * reminder banner on the platform admin shell. Resolver joins the
   * control-plane tenant directory with each tenant DB's
   * `secrets_references`; expensive enough that the admin shell polls on
   * a 10-minute cadence. Per docs/clearpath-architecture/13-audit-secrets-encryption.md §86.
   */
  secretRotationsAcrossTenants: Array<PlatformSecretRotationStatus>;
  /**
   * All Molaris platform staff (staff_tenant_memberships distinct by
   * cognito_sub). Used by the JIT request form's second-approver picker.
   */
  staff: Array<MolarisStaffUser>;
  /** Single tenant by id, including resource ARNs. */
  tenant?: Maybe<Tenant>;
  /** Per-tenant billing detail across a date range. */
  tenantBillingDetail: TenantBillingDetail;
  /**
   * Aggregated tenant-health snapshot — DB ACU, Lambda errors, queue
   * depth, event volume, federation activity, billing balance, user
   * activity. Backed by a mix of control-plane SQL + CloudWatch
   * GetMetricData. Fields with NO data source yet return null and the
   * top-level `partial: true` flag is set; see unwiredMetrics[] for the
   * list.
   */
  tenantHealth: TenantHealthReport;
  /** All product entitlements for a given tenant. */
  tenantProductEntitlements: Array<TenantProductEntitlement>;
  /**
   * Tenant-scoped provisioning and deprovisioning timeline. Returns only
   * structured audit events with sanitized failure detail; raw AWS logs and
   * credentials are never exposed through this field.
   */
  tenantProvisioningEvents: Array<ProvisioningEvent>;
  /**
   * Full role detail (permission keys, parent inheritance, audit trail).
   * See tenantRoles for cross-DB wiring caveats.
   */
  tenantRole?: Maybe<AdminRoleDefinition>;
  /**
   * Roles defined in a tenant's DB. Backed by a cross-DB read into the
   * tenant's per-tenant Aurora cluster (requires active JIT grant for the
   * target tenant). Stubbed with NotImplementedError until MOL-TBD lands
   * the cross-DB connection layer.
   */
  tenantRoles: Array<AdminRoleDefinition>;
  /** Paginated tenant list with filters. */
  tenants: TenantsPage;
  /**
   * P1 #53 / Bible §12:119-123 — peek at messages currently in the
   * webhook dispatcher DLQ for the named tenant. Returns up to 10 messages
   * per call (SQS receive-batch ceiling) without removing them from the
   * DLQ. Drives the admin DLQ table + replay UI.
   */
  webhookDlqMessages: Array<WebhookDlqMessage>;
};


/**
 * Namespace for Molaris-admin read operations. Every field is Cedar-gated by a
 * `molaris.*.*` permission key and resolved by the control-plane API Lambda
 * (services/control-plane/src/api/handler.ts).
 */
export type MolarisAdminQueryBillingAggregationsArgs = {
  period: BillingPeriodInput;
  tenantId: Scalars['ID']['input'];
};


/**
 * Namespace for Molaris-admin read operations. Every field is Cedar-gated by a
 * `molaris.*.*` permission key and resolved by the control-plane API Lambda
 * (services/control-plane/src/api/handler.ts).
 */
export type MolarisAdminQueryBillingRollupsArgs = {
  period: Scalars['String']['input'];
  tenantId?: InputMaybe<Scalars['ID']['input']>;
};


/**
 * Namespace for Molaris-admin read operations. Every field is Cedar-gated by a
 * `molaris.*.*` permission key and resolved by the control-plane API Lambda
 * (services/control-plane/src/api/handler.ts).
 */
export type MolarisAdminQueryDsarRequestsArgs = {
  filter?: InputMaybe<DsarRequestFilterInput>;
};


/**
 * Namespace for Molaris-admin read operations. Every field is Cedar-gated by a
 * `molaris.*.*` permission key and resolved by the control-plane API Lambda
 * (services/control-plane/src/api/handler.ts).
 */
export type MolarisAdminQueryEvaluateFeatureFlagArgs = {
  context: FeatureFlagEvaluationContextInput;
  flagKey: Scalars['String']['input'];
};


/**
 * Namespace for Molaris-admin read operations. Every field is Cedar-gated by a
 * `molaris.*.*` permission key and resolved by the control-plane API Lambda
 * (services/control-plane/src/api/handler.ts).
 */
export type MolarisAdminQueryIteroIntegrationsArgs = {
  tenantId: Scalars['ID']['input'];
};


/**
 * Namespace for Molaris-admin read operations. Every field is Cedar-gated by a
 * `molaris.*.*` permission key and resolved by the control-plane API Lambda
 * (services/control-plane/src/api/handler.ts).
 */
export type MolarisAdminQueryJitGrantArgs = {
  id: Scalars['ID']['input'];
};


/**
 * Namespace for Molaris-admin read operations. Every field is Cedar-gated by a
 * `molaris.*.*` permission key and resolved by the control-plane API Lambda
 * (services/control-plane/src/api/handler.ts).
 */
export type MolarisAdminQueryJitGrantsArgs = {
  status?: InputMaybe<JitGrantStatusFilter>;
};


/**
 * Namespace for Molaris-admin read operations. Every field is Cedar-gated by a
 * `molaris.*.*` permission key and resolved by the control-plane API Lambda
 * (services/control-plane/src/api/handler.ts).
 */
export type MolarisAdminQueryOpenAlertsArgs = {
  limit?: InputMaybe<Scalars['Int']['input']>;
};


/**
 * Namespace for Molaris-admin read operations. Every field is Cedar-gated by a
 * `molaris.*.*` permission key and resolved by the control-plane API Lambda
 * (services/control-plane/src/api/handler.ts).
 */
export type MolarisAdminQueryPendingJitGrantsArgs = {
  limit?: InputMaybe<Scalars['Int']['input']>;
};


/**
 * Namespace for Molaris-admin read operations. Every field is Cedar-gated by a
 * `molaris.*.*` permission key and resolved by the control-plane API Lambda
 * (services/control-plane/src/api/handler.ts).
 */
export type MolarisAdminQueryPermissionRegistryArgs = {
  includeDeprecated?: InputMaybe<Scalars['Boolean']['input']>;
  productCode?: InputMaybe<Scalars['String']['input']>;
};


/**
 * Namespace for Molaris-admin read operations. Every field is Cedar-gated by a
 * `molaris.*.*` permission key and resolved by the control-plane API Lambda
 * (services/control-plane/src/api/handler.ts).
 */
export type MolarisAdminQueryPlatformAuditArgs = {
  action?: InputMaybe<Scalars['String']['input']>;
  actorEmail?: InputMaybe<Scalars['String']['input']>;
  from?: InputMaybe<Scalars['AWSDateTime']['input']>;
  limit?: InputMaybe<Scalars['Int']['input']>;
  tenantId?: InputMaybe<Scalars['ID']['input']>;
  to?: InputMaybe<Scalars['AWSDateTime']['input']>;
};


/**
 * Namespace for Molaris-admin read operations. Every field is Cedar-gated by a
 * `molaris.*.*` permission key and resolved by the control-plane API Lambda
 * (services/control-plane/src/api/handler.ts).
 */
export type MolarisAdminQueryRecentPlatformIncidentsArgs = {
  limit?: InputMaybe<Scalars['Int']['input']>;
};


/**
 * Namespace for Molaris-admin read operations. Every field is Cedar-gated by a
 * `molaris.*.*` permission key and resolved by the control-plane API Lambda
 * (services/control-plane/src/api/handler.ts).
 */
export type MolarisAdminQueryRecentProvisioningEventsArgs = {
  limit?: InputMaybe<Scalars['Int']['input']>;
};


/**
 * Namespace for Molaris-admin read operations. Every field is Cedar-gated by a
 * `molaris.*.*` permission key and resolved by the control-plane API Lambda
 * (services/control-plane/src/api/handler.ts).
 */
export type MolarisAdminQueryRunbookArgs = {
  slug: Scalars['String']['input'];
};


/**
 * Namespace for Molaris-admin read operations. Every field is Cedar-gated by a
 * `molaris.*.*` permission key and resolved by the control-plane API Lambda
 * (services/control-plane/src/api/handler.ts).
 */
export type MolarisAdminQueryTenantArgs = {
  id: Scalars['ID']['input'];
};


/**
 * Namespace for Molaris-admin read operations. Every field is Cedar-gated by a
 * `molaris.*.*` permission key and resolved by the control-plane API Lambda
 * (services/control-plane/src/api/handler.ts).
 */
export type MolarisAdminQueryTenantBillingDetailArgs = {
  from: Scalars['AWSDate']['input'];
  tenantId: Scalars['ID']['input'];
  to: Scalars['AWSDate']['input'];
};


/**
 * Namespace for Molaris-admin read operations. Every field is Cedar-gated by a
 * `molaris.*.*` permission key and resolved by the control-plane API Lambda
 * (services/control-plane/src/api/handler.ts).
 */
export type MolarisAdminQueryTenantHealthArgs = {
  tenantId: Scalars['ID']['input'];
};


/**
 * Namespace for Molaris-admin read operations. Every field is Cedar-gated by a
 * `molaris.*.*` permission key and resolved by the control-plane API Lambda
 * (services/control-plane/src/api/handler.ts).
 */
export type MolarisAdminQueryTenantProductEntitlementsArgs = {
  tenantId: Scalars['ID']['input'];
};


/**
 * Namespace for Molaris-admin read operations. Every field is Cedar-gated by a
 * `molaris.*.*` permission key and resolved by the control-plane API Lambda
 * (services/control-plane/src/api/handler.ts).
 */
export type MolarisAdminQueryTenantProvisioningEventsArgs = {
  limit?: InputMaybe<Scalars['Int']['input']>;
  tenantId: Scalars['ID']['input'];
};


/**
 * Namespace for Molaris-admin read operations. Every field is Cedar-gated by a
 * `molaris.*.*` permission key and resolved by the control-plane API Lambda
 * (services/control-plane/src/api/handler.ts).
 */
export type MolarisAdminQueryTenantRoleArgs = {
  roleId: Scalars['ID']['input'];
  tenantId: Scalars['ID']['input'];
};


/**
 * Namespace for Molaris-admin read operations. Every field is Cedar-gated by a
 * `molaris.*.*` permission key and resolved by the control-plane API Lambda
 * (services/control-plane/src/api/handler.ts).
 */
export type MolarisAdminQueryTenantRolesArgs = {
  tenantId: Scalars['ID']['input'];
};


/**
 * Namespace for Molaris-admin read operations. Every field is Cedar-gated by a
 * `molaris.*.*` permission key and resolved by the control-plane API Lambda
 * (services/control-plane/src/api/handler.ts).
 */
export type MolarisAdminQueryTenantsArgs = {
  filter?: InputMaybe<TenantsFilterInput>;
};


/**
 * Namespace for Molaris-admin read operations. Every field is Cedar-gated by a
 * `molaris.*.*` permission key and resolved by the control-plane API Lambda
 * (services/control-plane/src/api/handler.ts).
 */
export type MolarisAdminQueryWebhookDlqMessagesArgs = {
  tenantId: Scalars['ID']['input'];
};

/**
 * Molaris staff row — distinct by cognito_sub across
 * staff_tenant_memberships. Used by the JIT request form's approver
 * picker.
 */
export type MolarisStaffUser = {
  __typename?: 'MolarisStaffUser';
  cognitoSub: Scalars['String']['output'];
  email: Scalars['String']['output'];
  name: Scalars['String']['output'];
};

/**
 * Current viewer shape used by the admin UI (not to be confused with the
 * top-level Query.me which returns a tenant-plane User).
 */
export type MolarisStaffViewer = {
  __typename?: 'MolarisStaffViewer';
  cognitoSub: Scalars['String']['output'];
  email?: Maybe<Scalars['String']['output']>;
  fullName?: Maybe<Scalars['String']['output']>;
  /** Effective permission keys for the authenticated Molaris staff viewer. */
  permissionKeys: Array<Scalars['String']['output']>;
};

export type Mutation = {
  __typename?: 'Mutation';
  /**
   * Accept a piece of incoming federated work. The workItemId is the
   * step_run id on the recipient tenant; the resolver assigns the step_run
   * to the current vendor user, flips status to IN_PROGRESS, and emits
   * `federation.work.accepted` which the origin tenant's federation-listener
   * translates into an external_work_status_update on their bus.
   */
  acceptFederatedWork: StepRunActionPayload;
  /**
   * Accept an incoming federation link. Phase 1.1 (MOL-603) — takes the
   * link id directly; the resolver looks up the row, validates it's in
   * 'proposed' status, performs the KMS key exchange, and flips status to
   * 'active'.
   */
  acceptFederationLink: FederationLinkPayload;
  /**
   * Invitee-side accept. The `token` is the invitation row's id (UUID).
   * Caller MUST be authenticated — the resolver records `accepted_at` and
   * links the invitee's user_id when it isn't already set. Idempotent for
   * already-accepted rows belonging to the caller. Round 1b — v2 feature
   * restoration. NOT gated by Cedar — possession of the token is the
   * authorization.
   */
  acceptInvitation: Invitation;
  /**
   * Acknowledge an open escalation event. Idempotent (second ack is a
   * no-op). Cancels the next scheduled escalation in EventBridge Scheduler.
   */
  acknowledgeEscalation: EscalationEvent;
  /**
   * ENG-4024 — acknowledge a first-bill check: stamp the agreement line as
   * reviewed, release the charges it held (an order another unacknowledged line
   * still holds stays held) and re-queue auto-invoicing for released orders. Later
   * orders on the line are never held. Staff only (`requireInternalActor` first),
   * then `clearpath.billing.invoice`. Refusals (extensions.code
   * FirstBillCheckRefused, reason): line_not_found, not_assumed_zero,
   * too_many_held, busy.
   */
  acknowledgeFirstBillCheck: FirstBillAcknowledgePayload;
  /**
   * Start collecting under ONE registration: `PENDING` -> `ACTIVE`. ENG-2497.
   *
   * **This starts charging real customers.** `compute.ts#hasNexus` drops every
   * jurisdiction with no active registration, so flipping this row turns tax on
   * for every address that jurisdiction covers.
   *
   * ⚠️ IT IS THE ONLY TRANSITION OFFERED, DELIBERATELY. There is no
   * `ACTIVE -> CLOSED` here: `status` carries no date, so closing it would remove
   * nexus for every PAST date too and a lookup for a day the lab really was
   * registered would begin answering `NO_NEXUS`. Stopping collection is
   * `retireTaxJurisdiction`, which sets `collectsTo` and leaves the status alone.
   *
   * The registration must already carry a permit number — record it with
   * `recordTaxPermit` first. The number is **attested, never verified**.
   *
   * Refuses, by name and never as a silent no-op:
   * `TaxRegistrationNotFound` (another organization's row, or none),
   * `TaxRegistrationJurisdictionNotFound` (the jurisdiction it points at is
   * neither shared platform reference data nor this organization's),
   * `TaxRegistrationAlreadyActive` (a second activate would write an audit row
   * about a change that did not happen), `TaxRegistrationClosed`,
   * `TaxRegistrationNumberRequired`, `TaxRegistrationWindowClosed`,
   * `TaxRegistrationCollectsFromBeforeJurisdiction`,
   * `TaxRegistrationCollectsRangeInvalid`, `TaxRegistrationDateAlreadyUsed`,
   * `TaxWriteBoundsExceeded`, `Conflict` (stale or missing `expectedVersion`).
   */
  activateTaxRegistration: TaxRegistrationPayload;
  /**
   * Register an additional physical location for a file — used by the
   * routing engine and the NAS sync agent (MOL-583).
   */
  addFileLocation: AddFileLocationPayload;
  /**
   * Attach a tokenized payment method to a party. The `providerMethodToken`
   * is produced client-side by the provider's secure element (Stripe Elements /
   * equivalent); raw PAN data NEVER crosses this mutation. For ACH/SEPA the
   * `input.mandate` block is required for NACHA / SEPA compliance. Step-up
   * MFA required (`clearpath.payment_methods.manage.requires_step_up_mfa`).
   * MOL-622. BLOCKED ON payment provider selection (Phase 2 G-G/G-H).
   */
  addPaymentMethod: PaymentMethodPayload;
  /**
   * Grant a permission to a tenant role. Writes role_permissions +
   * audit event. Mirrors MolarisAdminMutation.addPermissionToRole but
   * scoped to the caller's tenant (no tenantId arg).
   */
  addPermissionToRole: TenantRolePayload;
  /**
   * Add Small 6x6x3, Medium 8x6x4 and Large 8x8x8, skipping names an active box
   * already has. setLabDefault: also make Small, 16 oz, your own packaging the lab
   * default. Returns every active box size.
   */
  addStandardShippingBoxPresets: Array<ShippingBoxPreset>;
  /**
   * Post a correction as a signed delta. This appends a compensating ledger row
   * — it never edits history, because inventory_transactions is append-only.
   */
  adjustStock: StockMovementPayload;
  /**
   * Advance a live rollout forward (canary->cohort->fleet->complete) and/or
   * adjust its exposure percent. Rejects backward or illegal transitions.
   */
  advanceNasRollout: NasRolloutPayload;
  /**
   * Book what the provider reports for 1..10 payments, through the SAME
   * `applyEffect` path the daily reconciliation pass and the webhook path use.
   *
   * ONE WRITE PATH, PROVABLY. This mutation has no booking logic of its own: it
   * re-reads the provider and hands the resulting side effect to the shared
   * dispatcher, which dedupes on `provider_transaction_id`, takes the same
   * per-invoice advisory lock and applies the same settled-vs-attempt refusal. Two
   * write paths for one settlement is how a poller and a button come to disagree
   * about what "settled" means.
   *
   * ⚠️ IT DOES NOT TRUST A CHECK RESULT, AND THE CLIENT SUPPLIES NO PROVIDER
   * STATUS. A check is stale by definition — on ACH an entry can be returned days
   * after it settled — so the server performs its own read and re-runs the same
   * eligibility rule. Where the answer has moved, the movement comes back as that
   * item's error.
   *
   * PER-ITEM, NEVER ALL-OR-NOTHING. A mixed selection reports one
   * `{success, error}` per payment; the batch does not fail. Capped at 10
   * SERVER-side: each item is a provider round trip inside a 30-second budget, and
   * a timeout would lose the per-item results for a batch that may already have
   * booked real settlements.
   *
   * ⚠️ THIS IS THE ARM THAT ALARMS. A provider with no record of an id we hold
   * raises the A/R data-integrity alarm here and on the daily pass —
   * deliberately NOT on the freely-available status check. A payment we hold no
   * provider id for at all is refused before the provider is consulted and never
   * reaches that arm: there is no id, so "no record" is not a finding about it.
   */
  applyPaymentAttemptResults: ApplyPaymentAttemptResultsPayload;
  /**
   * ENG-3054 Story 3.3 — save a staged change set for one product in ONE
   * atomic call: every grant, revoke and the independent operation apply
   * together, or nothing does. A repeat of the same call changes nothing for
   * explicit ids and for an independent REVOKE. A repeated independent GRANT
   * is refused with the retryable Conflict `independent_targeted_changed`
   * (the offices it filled are no longer gaps): preview the change again.
   *
   * Refusals (nothing written): ValidationError (shape, overlap, an empty change
   * set, more than 10,000 targeted parties: listed ids plus every active
   * independent office); NotFound (an unknown product, or any id that is not an
   * eligible customer of this organization in the caller's reach); Forbidden
   * `non_internal_actor` or `scoped_caller`; a retryable
   * Conflict when another access change holds the organization, rows changed
   * underneath, the independent offices changed since the preview
   * (`independent_targeted_changed`), or more customers would lose access than
   * `expectedLosing` (`losing_exceeds_expected`); ServiceUnavailable
   * (`scope_unverified` or `apply_timeout`, retryable); and Forbidden
   * `budget_exceeded` (with `cause` and `retryable`) when the organization's
   * rolling 24-hour limit on customers losing access, or on independent REVOKE
   * saves, would be exceeded.
   */
  applyProductTypeCustomerAccess: ApplyProductTypeCustomerAccessResult;
  /** Apply a validated draft document in a single transaction. */
  applyTenantConfig: ApplyTenantConfigResult;
  /**
   * Classify + approve a projection, optionally exposing it. Only a SAFE projection
   * marked exposed becomes catalog-selectable; PHI/BLOCKED classifies but is held.
   */
  approveProjection: ProjectionGovernancePayload;
  /** P2.4 (2026-04-25) — archive an automation_rule (any state). */
  archiveAutomationRule: AutomationRulePayload;
  /**
   * Archive (soft-delete) a calculated measure. ALWAYS allowed, even when the
   * measure is referenced by reports/dashboards — an archived measure is hidden
   * from the authoring picker but KEEPS RESOLVING in the reports that use it.
   * Idempotent (re-archiving preserves the first archive timestamp). NotFound if
   * the id is not a measure in the caller's org.
   */
  archiveCalculatedMeasure: CalculatedMeasurePayload;
  /** Soft-archive a flag (sets archived_at=now()). */
  archiveFeatureFlag: FeatureFlagPayload;
  /**
   * Atomically remove every assignment, then soft-archive the flag.
   * Callers also need assignment-delete permission for every affected scope.
   */
  archiveFeatureFlagWithAssignments: FeatureFlagPayload;
  /**
   * P2.4 (2026-04-25) — archive a form_definition (any state). Since ENG-1073
   * this is the ONE withdrawal mechanism for a form: the `isActive` toggle it used
   * to sit alongside is gone, so pulling a form out of service means archiving it.
   * Reversible — see unarchiveFormDefinition.
   */
  archiveFormDefinition: FormDefinitionPayload;
  /**
   * ENG-658 — archive a library field (is_active = false). Does not detach
   * it from any form version it's already used by. Renamed from
   * archiveAttributeDefinition.
   */
  archiveFormField: FormField;
  /**
   * Archive an org unit: hide it from pickers and new-work assignment
   * while leaving every reference to it intact. Permissions, SLA evaluation
   * and history are unaffected — archiving is visibility-only.
   *
   * Use this when deleteOrgUnit cannot succeed. A unit that has ever carried
   * a scoped role assignment holds permanent rows in the activity trail
   * (role_assignment_audit, ON DELETE RESTRICT), which makes hard deletion
   * impossible forever — revoking a team's last member is itself what
   * creates those rows.
   *
   * Refused while the unit still has LIVE dependents: live child units,
   * steps in LIVE pipelines, unexpired member assignments, active SLA
   * policies, or open orders. The activity trail never blocks, since it can
   * never be cleared. Archiving does not cascade to child units.
   */
  archiveOrgUnit: ArchiveOrgUnitPayload;
  /**
   * P2.4 (2026-04-25) — archive a pipeline. Archived pipelines are unavailable
   * for new orders; in-flight orders continue. Rejected while the pipeline is a
   * SKU's default (repoint that SKU first). NOT terminal — an archived pipeline
   * can be restored via setPipelineStatus (ARCHIVED->LIVE/INACTIVE); reopening a
   * once-published DAG for in-place editing still requires a fork.
   */
  archivePipeline: PipelinePayload;
  /**
   * Archive a price book: stops pricing new orders (clears isDefault) and
   * marks it inactive. Existing orders are unaffected — pricing is frozen at
   * order creation.
   */
  archivePriceBook: PriceBookPayload;
  /** ENG-8 — archive a product bundle. Removes it from selection; existing orders unchanged. */
  archiveProductBundle: ProductBundlePayload;
  /** P2.4 (2026-04-25) — archive a product_type (any state). */
  archiveProductType: ProductTypePayload;
  /** P2.4 (2026-04-25) — archive an sla_policy (any state). */
  archiveSlaPolicy: SlaPolicyPayload;
  /**
   * General-purpose LLM assistant. Answers a natural-language question,
   * optionally running read-only data tools whose per-call authorization is
   * enforced inside the resolver. Returns the answer plus a token-usage
   * breakdown and a redacted trace of any tools invoked. Gate mirrors the
   * sibling config assistant (chatWithConfigAssistant). SDL-01.
   */
  askAssistant: AskAssistantResult;
  /** Create a new assignment row (scope-override for a flag value). */
  assignFeatureFlag: FlagAssignmentPayload;
  /**
   * ENG-658 — assign a specific order-level intake form to a party
   * (optionally reaching its descendants), overriding the tenant default for
   * that party. Replaces any existing active assignment on the same party.
   */
  assignPartyOrderForm: PartyOrderFormAssignmentPayload;
  /**
   * Give a user their one role in a place (a unit, or the whole lab), or add
   * another place for the role they already hold. Never a second role — see
   * AssignRoleInput and UserRole.
   */
  assignRole: UserRolePayload;
  assignShift: ShiftAssignmentResult;
  /**
   * Assign a step run (default assignee = caller). Assigning starts the step
   * (ENG-3630).
   *
   * CLAIMING ("Assign to me": no assigneeUserId, or the caller's own id) needs
   * clearpath.steps.assign on the step AND membership of the step's department
   * (org_unit_memberships; a whole-lab role is not membership) — except that a
   * caller holding clearpath.assignments.write may take a step outside their
   * departments. The decision is that claiming needs permission + membership +
   * skills; the skills test (required skills, machine authorizations, unexpired
   * certifications) is applied where the button is offered
   * (StepRunCapabilities.take, selfAssignableStepRuns) and is NOT re-checked by
   * this mutation.
   *
   * GIVING IT TO SOMEONE ELSE (assigneeUserId set) needs
   * clearpath.assignments.write, and the assignee must be a member of the step's
   * department. The Assign work board uses manualAssignStep instead, which also
   * checks the step's unit is in the caller's scope and the assignee's skills.
   */
  assignStepRun: StepRunActionPayload;
  /**
   * Assign or unassign a ticket. Flips status `new` → `in_progress` on
   * first assign.
   */
  assignTicket: TicketPayload;
  assignUserSkill: UserSkillPayload;
  authorizeMachine: MachineAuthorizationPayload;
  beginDsCoreConnection: BeginMeditConnectionPayload;
  /** Start or restart the authenticated iTero OAuth flow. */
  beginIteroConnection: IteroOAuthStart;
  /** Create a short-lived, one-time launch ticket for Medit OAuth. */
  beginMeditConnection: BeginMeditConnectionPayload;
  /** Create a short-lived, one-time launch ticket for 3Shape OAuth. */
  beginThreeShapeConnection: BeginThreeShapeConnectionPayload;
  /**
   * Round 6 — apply one tier definition across many parties in one
   * transactional call. Returns the agreements that succeeded plus the
   * ids that failed (e.g. unknown party). Per-party failure is reported
   * via failedExternalPartyIds rather than aborting the whole batch.
   */
  bulkApplyPartyPricing: BulkApplyPartyPricingPayload;
  /**
   * Cancel many orders at once. Reuses cancelOrder's guards per order (already
   * cancelled / closed orders fail on their own row). Returns a per-id result.
   */
  bulkCancelOrders: BulkOrderActionPayload;
  /**
   * Bulk-import external parties from a CSV. The 3-step admin wizard calls
   * this twice: once with `dryRun: true` to preview the would-create rows
   * + per-row errors, then once with `dryRun: false` to commit. Commit runs
   * in a single transaction; any row error rolls everything back. Round 1a.
   */
  bulkImportExternalParties: BulkImportExternalPartiesResult;
  /**
   * Mark many invoices paid at once. Reuses markInvoicePaid's guards per
   * invoice (already-paid is a no-op; voided invoices fail on their own row).
   * Returns a per-id result.
   */
  bulkMarkInvoicesPaid: BulkInvoiceActionPayload;
  /**
   * ENG-482 — publish many DRAFT SKUs in one call, PARTIAL-SUCCESS.
   *
   * Applies the identical draft-completeness gate `publishProductType` applies,
   * once per id, inside a per-SKU savepoint: everything that passes goes LIVE and
   * everything that does not is returned with its `reasons`. It never publishes a
   * SKU the single mutation would refuse.
   *
   * Exists for the remainder of the ENG-482 backfill (migration 0166): SKUs
   * stranded at DRAFT that the migration could not safely promote because they are
   * missing a price, a published pipeline, or a LIVE intake form. Clearing those
   * one dialog at a time is the only alternative.
   *
   * Accepts at most 200 ids; duplicates are collapsed.
   */
  bulkPublishProductTypes: BulkPublishProductTypesPayload;
  /**
   * Validate, per invoice, that a payment reminder applies (issued and still
   * owing). Read-only: no invoice is mutated. Reminder DISPATCH is a parked
   * dunning ticket — this returns per-id eligibility only. Returns a per-id
   * result.
   */
  bulkRemindInvoices: BulkInvoiceActionPayload;
  /**
   * ENG-3881 — `setLedgerMapping` for up to 500 subjects in one transaction; any
   * refused item refuses the whole call. A (kind, subjectKey) may appear once.
   */
  bulkSetLedgerMappings: BulkSetLedgerMappingsResult;
  /**
   * Set priority on many orders at once. Reuses updateOrder's priority path
   * (custom_fields.priority) per order. Returns a per-id result.
   */
  bulkSetOrderPriority: BulkOrderActionPayload;
  /**
   * Set the workflow status on many orders at once. Reuses setOrderStatus's
   * guards per order. Returns a per-id result.
   */
  bulkSetOrderStatus: BulkOrderActionPayload;
  /**
   * Void many invoices at once with one shared reason. Reuses voidInvoice's
   * guards per invoice (paid / partially-paid invoices fail on their own row —
   * they must be refunded, not voided). Returns a per-id result.
   */
  bulkVoidInvoices: BulkInvoiceActionPayload;
  /**
   * P1.7 (2026-04-25) — cancel an in-flight account-deletion request.
   * Idempotent: cancelling when no request is in flight is a no-op returning
   * the current (NONE) status.
   */
  cancelAccountDeletion: AccountDeletionStatus;
  /** Cancel a side-effect-free setup session; retain the existing pairing. */
  cancelIteroSetup: IteroConnection;
  /** Cancel an in-flight order. */
  cancelOrder: OrderActionPayload;
  /**
   * Close out a wedged payment, releasing the reservation it holds on its
   * invoice.
   *
   * ⚠️ THIS IS THE ONLY RELEASE VALVE THERE IS. An attempt whose provider call
   * THREW keeps its amount reserved against the invoice — correctly, because
   * releasing a debit that may be live is unrecoverable — and the automatic
   * recovery cannot resolve every one of them. Without this mutation such an
   * invoice is permanently unpayable and the payer is refused with a message
   * naming money nobody can free.
   *
   * It appends a `canceled` row rather than changing one; `payment_attempts` is
   * append-only. A double-click is idempotent: the second call finds the payment
   * already cancelled and writes nothing.
   *
   * WHAT IT REFUSES:
   *   * a payment carrying a provider transaction reference — the provider can
   *     still answer for it, so check its status first. There is no force flag;
   *   * a payment that is not holding a reservation at all;
   *   * a payment still inside its settlement window (6 days on ACH, 15 minutes on
   *     card);
   *   * `acknowledgeUnknownOutcome` on a payment whose outcome is not unknown.
   *
   * `canceled` asserts that NOTHING ARRIVED. The audit row records the exact row
   * state that assertion was made against, the operator, and their reason.
   */
  cancelPaymentAttempt: CancelPaymentAttemptPayload;
  cancelTimeOffRequest: TimeOffPayload;
  /**
   * ENG-3498 — change a user's role. A user holds exactly one role, and this
   * is the only way to change it (the user's settings page): every one of the
   * user's assignments is repointed at the new role in one statement, so the org
   * units it applies in are unchanged. A non-staff user (portal, partner) holding
   * no role gets one tenant-wide assignment. A STAFF user holding no role is
   * refused (reason `role_scope_required`, ENG-3923): their first role is given
   * with assignRole, in a department or team or with an explicit
   * `wholeLab: true`. Requires clearpath.roles.assign AND clearpath.roles.revoke
   * over every org unit the user's assignments cover.
   */
  changeUserRole: ChangeUserRolePayload;
  /**
   * Charge an invoice against a saved payment method. The server resolves
   * the tenant-configured provider via `packages/payments.getProvider(...)`,
   * inserts a payment_attempts row, and returns the provider result plus the
   * invoice post-state. `paymentMethodId` defaults to the payer's default
   * method when omitted. Optional `amount` (decimal string) charges a partial
   * payment up to the amount due; omit to pay the full balance. On
   * REQUIRES_ACTION the client completes the follow-up via `nextAction`.
   * Idempotent per payment attempt. MOL-622.
   *
   * `surchargeDisclosed` (card only, ENG-3881): the surcharge shown from
   * `PaymentClientConfig.cardSurcharge`. With it, the surcharge is charged on top;
   * without it the payer is charged exactly the amount. A disclosure that does not
   * match the server's computation is refused with `ValidationError`.
   */
  chargeInvoice: PaymentAttemptPayload;
  /**
   * Multi-turn LLM chat for YAML config authoring. Returns the
   * assistant reply plus structured YAML diff blocks the UI applies.
   */
  chatWithConfigAssistant: ChatAssistantResult;
  /**
   * Approve a device's pending pairing and cast one of the caller's dashboards
   * to it, returning the projection grant (lease).
   */
  claimProjectionPairing: ClaimProjectionPairingPayload;
  /** Soft-deactivate the reusable mapping for one exact scanner identity. */
  clearScannerIntakeIdentityMapping: ClearScannerIntakeIdentityMappingPayload;
  /**
   * Clone a role (typically a seeded is_system=true one) into a NEW editable
   * is_system=false role. Copies the source's EFFECTIVE permission set, but only
   * the keys the caller themselves hold (RBAC delegation boundary -- a clone can
   * never widen the caller's grant surface); the source is never mutated. Gated
   * on roles.create since it creates a role.
   */
  cloneRole: TenantRolePayload;
  /** Close an order (terminal lifecycle). */
  closeOrder: OrderActionPayload;
  /**
   * Close a ticket (terminal state). Optional reason stored in
   * customFields.
   */
  closeTicket: TicketPayload;
  /**
   * Commit a previewed CSV of tax rates.
   *
   * **Every row goes through the SAME write path as a hand-typed one** — the same
   * claimant refusal, the same category-coverage and nexus invariants, the same
   * rate band and backdating rules — in ONE transaction. It is not a bulk insert:
   * a parallel bulk writer would re-open the two-claimants-for-one-address defect
   * at scale on the SECOND import, which a clean first file never exercises.
   *
   * Re-importing a postal code you already have CORRECTS it. **A postal code
   * absent from a later file is left alone** — a shorter spreadsheet must never
   * silently return addresses to the regional rate.
   *
   * Counts against a separate, much smaller 24-hour budget than single-row edits,
   * because one import is one operation covering many rows.
   *
   * extensions.code: `TaxImportBlocked`, `TaxImportDigestMismatch`, plus the whole
   * `upsertTaxJurisdiction` vocabulary, which every row is subject to.
   */
  commitTaxImport: TaxImportCommitPayload;
  /**
   * Complete a piece of incoming federated work. `outputFileIds` reference
   * files the vendor has already uploaded via requestFileUpload +
   * registerUploadedFile; they are attached to the step_run and included in
   * the `federation.work.completed` event so the origin tenant can pull them.
   * `notes` is free-form text surfaced in the origin tenant's step_run detail.
   * Phase 1.1 (MOL-603).
   */
  completeFederatedWork: StepRunActionPayload;
  /** Complete a step run (success path). */
  completeStepRun: StepRunActionPayload;
  configureDsCoreCredentials: ConfigureMeditCredentialsPayload;
  /** Configure or replace this tenant's write-only iTero credentials. */
  configureIteroCredentials: ConfigureIteroCredentialsPayload;
  /** Configure or replace this tenant's write-only Medit application credentials. */
  configureMeditCredentials: ConfigureMeditCredentialsPayload;
  /**
   * Set a NAS station's configuration (FULL replace): its org unit, assignment
   * mode, assigned users, include-sub-units flag, format override and paused flag.
   * Gated on `clearpath.nas.configure` (feature-flag-gated on
   * `clearpath.nas_module`, step-up MFA declared). Returns the station and any
   * non-fatal warnings.
   */
  configureNasStation: ConfigureNasStationPayload;
  /** Configure or replace this tenant's write-only 3Shape public OAuth client ID. */
  configureThreeShapeCredentials: ConfigureThreeShapeCredentialsPayload;
  /**
   * ENG-881 — connect (or re-point) the intake form bound to a SKU via the
   * product_type_forms join. One intake form per SKU. A DRAFT (built, not yet
   * published) or LIVE intake form may be connected — setup before publish is a
   * supported intermediate state (ProductType.intakeFormResolutionStatus reads
   * UNPUBLISHED until it goes LIVE); ARCHIVED and non-INTAKE forms are rejected.
   * Unlike upsertProductType this is a pure join edit, NOT lifecycle-gated on the
   * SKU, so a LIVE SKU's form can still be changed (mirrors
   * linkPipelineToProductType). The form's OWN fields are edited separately in the
   * Forms Library via upsertFormDefinition.
   */
  connectFormToProductType: ProductTypePayload;
  /**
   * Register a new hardware integration and run a health probe. Writes a
   * row to external_integrations and stashes credentials in Secrets Manager.
   */
  connectIntegration: IntegrationConnection;
  /** Create an analytics dashboard owned by the authenticated caller. */
  createAnalyticsDashboard: AnalyticsDashboardPayload;
  /**
   * Add a widget to one of the caller's own dashboards. NotFound if the parent
   * dashboard is not the caller's own dashboard in their org.
   */
  createAnalyticsWidget: AnalyticsWidgetPayload;
  /**
   * Mint a new API key for a service account in the caller's organisation.
   * Returns the plaintext key ONCE.
   */
  createApiKey: CreateApiKeyPayload;
  /**
   * Create a billing adjustment (credit, refund, write-off, dispute
   * resolution). Amounts above the step-up MFA threshold require a re-authed
   * session. MOL-620.
   */
  createBillingAdjustment: CreateBillingAdjustmentPayload;
  /** Create a calculated (derived) measure for the caller's org. */
  createCalculatedMeasure: CalculatedMeasurePayload;
  createCertificationDefinition: CertificationDefinitionPayload;
  /** Create a comment on a polymorphic target. */
  createComment: CommentPayload;
  /** Grant a clinic_staff user the right to act on behalf of a doctor party. */
  createCustomerDelegation: CustomerDelegationPayload;
  /**
   * Create a new escalation chain. Chain steps are validated server-side:
   * each step must specify exactly one of targetRoleId / targetUserId and a
   * non-negative delayBeforeNextMinutes.
   */
  createEscalationChain: EscalationChain;
  /** Create a new flag definition. Key + flagType are immutable after create. */
  createFeatureFlag: FeatureFlagPayload;
  /** Initiate a federation link with a vendor tenant. */
  createFederationLink: FederationLinkPayload;
  /**
   * Create a hold reason code — an entry in the tenant's hold taxonomy (the
   * Place-hold dialog's reason dropdown). Tenant configuration, gated on
   * clearpath.tenant_config.write (the same key field-label / status-state admin
   * uses), NOT on orders.hold. `code` must be unique per organization
   * (Conflict otherwise).
   */
  createHoldReasonCode: HoldReasonCodePayload;
  /**
   * Define a new inventory item. Opening balance is always zero: stock arrives
   * through receiveStock so every unit on hand is traceable to a ledger row.
   */
  createInventoryItem: InventoryItemPayload;
  createMachine: MachinePayload;
  /**
   * Create a new staged auto-update rollout for a (channel, platform, arch)
   * slice, starting in the canary phase. Validates the target build exists and is
   * not yanked. Fails if a live rollout already exists for the slice. Gated on
   * clearpath.nas.rollout.manage (feature-flag clearpath.nas_module + step-up MFA).
   */
  createNasRollout: NasRolloutPayload;
  /** Create a notification. SYSTEM-ONLY — invoked by Lambda notification dispatcher. */
  createNotification: NotificationPayload;
  /** Create a copied, read-only guest link for one exact STL version. */
  createOroviaGuestPreviewLink: CreateOroviaGuestPreviewLinkPayload;
  /**
   * Create a price book (an Org's default catalog). The very first book in
   * an Org always becomes the default — a non-default book has no effect on
   * order pricing.
   */
  createPriceBook: PriceBookPayload;
  /** Create a new tenant role. */
  createRole: TenantRolePayload;
  /** Create a Phase 6 scoped share link (Mode B). Returns the JWT once. */
  createShareLink: ShareLinkPayload;
  /** Create a vendor mode-B share link. */
  createSharedAccessLink: CreateSharedAccessLinkPayload;
  createShift: ShiftPayload;
  /**
   * ENG-3286 — add an outside FedEx account already linked into the lab's own
   * FedEx API project. Refused unless a live rate quote succeeds on it.
   */
  createShippingAccount: ShippingAccount;
  /** Add a box size. */
  createShippingBoxPreset: ShippingBoxPreset;
  /**
   * Purchase a label with the chosen carrier + service level. Returns the
   * base64-encoded PDF + tracking number. The carrier bills the account derived
   * from the order's shipping terms (ENG-3286): the lab's own account, or the
   * customer's linked outside account. Molaris does not aggregate.
   */
  createShippingLabel: ShippingLabel;
  /**
   * Add a FedEx label sender to an outside account (input.shippingAccountId) or
   * the lab's own account (null). Company, contact, address and phone required.
   */
  createShippingSenderIdentity: ShippingSenderIdentity;
  /**
   * ENG-1043 — create a site (org_unit unit_type='site' + sites extension row,
   * one transaction). Internal-only (requireInternalActor('clearpath.sites.write')),
   * matching the sites read posture (Query.sites carries no permission key).
   */
  createSite: SitePayload;
  createSkillDefinition: SkillDefinitionPayload;
  /** Create a new ticket. Requester defaults to the caller. */
  createTicket: TicketPayload;
  createTimeOffRequest: TimeOffPayload;
  /**
   * Create a new user. Same provisioning path as `inviteUser`, without the
   * invite email (`sendInvite: false`), so `userType: INTERNAL` is REFUSED here
   * identically: PreconditionFailed / staff_provisioning_requires_control_plane,
   * nothing provisioned. Staff onboarding is `inviteStaffUser` only (ENG-2323).
   */
  createUser: UserPayload;
  createUserCertification: UserCertificationPayload;
  /**
   * Retire a hold reason code (set isActive=false). Deactivate, never delete:
   * order_holds reference it by an ON DELETE RESTRICT FK and it is the SLA evidence
   * for every historical hold that used it, so it must not vanish. The picker drops
   * a retired code from NEW holds while past holds keep their label. Reactivate via
   * updateHoldReasonCode(isActive: true). Idempotent.
   */
  deactivateHoldReasonCode: HoldReasonCodePayload;
  /**
   * Deactivate a station: revoke all its keys, mark the device inactive, and
   * archive its service account. Idempotent — returns true once the station can
   * no longer authenticate.
   */
  deactivateScannerStation: Scalars['Boolean']['output'];
  /**
   * Decline a piece of incoming federated work with a mandatory reason.
   * Marks the step_run failed with outcome='rejected', unblocks the origin
   * tenant's pipeline so a fallback vendor can be routed. Phase 1.1 (MOL-603).
   */
  declineFederatedWork: StepRunActionPayload;
  /**
   * Invitee-side decline. Marks the invitation revoked with a
   * `decline_reason` stashed in custom_fields and `revoked_by_user_id` set
   * to the caller. Returns true on first call, true again on a re-decline
   * (idempotent). NOT gated by Cedar. Round 1b — v2 feature restoration.
   */
  declineInvitation: Scalars['Boolean']['output'];
  /**
   * Delete the caller's own analytics dashboard. Its widgets CASCADE-delete
   * (db/migrations/0331 FK). NotFound if the id is not the caller's own dashboard
   * in their org.
   */
  deleteAnalyticsDashboard: AnalyticsDashboardDeletedPayload;
  /**
   * Delete the org's target for a measure (ENG-2092). Silent-delete guarded: a
   * cross-org / absent target raises a Conflict, never a false success. Gated on
   * clearpath.analytics.manage.
   */
  deleteAnalyticsTarget: DeleteAnalyticsTargetPayload;
  /**
   * Delete a widget from one of the caller's own dashboards. NotFound if the
   * widget is not on a dashboard the caller owns in their org.
   */
  deleteAnalyticsWidget: AnalyticsWidgetDeletedPayload;
  /**
   * Hard-delete a calculated measure. ONLY allowed when it is unreferenced —
   * rejected with a structured error naming what blocks it (saved reports,
   * dashboard tiles). For a referenced measure, archive it instead
   * (archiveCalculatedMeasure) — this mutation NEVER removes a referenced measure
   * and cascades into nothing. NotFound if the id is not a measure in the caller's
   * org.
   */
  deleteCalculatedMeasure: CalculatedMeasureDeletedPayload;
  deleteCertificationDefinition: CertificationDefinitionDeletedPayload;
  /** Soft-delete a comment. */
  deleteComment: CommentPayload;
  /**
   * Delete a comment category. Comments referencing it have their
   * category_id reset to NULL via ON DELETE SET NULL.
   */
  deleteCommentCategory: CommentCategoryDeletedPayload;
  /**
   * Delete an external party. Refuses if any order/invoice still references
   * it (FK ON DELETE RESTRICT); admins must reassign first.
   */
  deleteExternalParty: ExternalPartyDeletedPayload;
  /**
   * Soft-delete an external-party address row (sets revoked_at = now()).
   * Historical orders that referenced the address keep their snapshot intact.
   */
  deleteExternalPartyAddress: ExternalPartyAddressDeletedPayload;
  /**
   * Hard-delete a failure reason code. Only allowed when it is unreferenced
   * — refused with a structured error naming what blocks it (step_run QC
   * records via the FK, or a step_dependencies loopback rule keyed to this
   * code's `condition.failure_reason_code`). Retire it instead (toggle
   * isActive off via upsertFailureReasonCode) for a referenced code — this
   * mutation never cascades and never silently retires on your behalf.
   */
  deleteFailureReasonCode: FailureReasonCodeDeletedPayload;
  /**
   * Delete a tenant field_label override (reverts the UI to the internal
   * field_name). Admin-only.
   */
  deleteFieldLabel: FieldLabelPayload;
  /** Delete an assignment row. History preserved in audit_log. */
  deleteFlagAssignment: DeleteFlagAssignmentPayload;
  deleteMachine: MachineDeletedPayload;
  /**
   * Delete an org_unit. Refuses when the unit has descendants OR is
   * referenced by pipeline_steps / user_roles / sla_policies / orders
   * (Postgres FK RESTRICT). Surfaces a structured error so callers can
   * point the user at what to clean up first.
   */
  deleteOrgUnit: OrgUnitDeletedPayload;
  /**
   * Delete a party pricing agreement (and its lines, via cascade). Hard-delete
   * when unreferenced; refused (Conflict) if it has ever priced an order — it is
   * recorded in pricing_resolution_log and removing it would break that order's
   * pricing history. Deactivate it instead in that case. ENG-618.
   */
  deletePartyPricingAgreement: DeletePartyPricingAgreementPayload;
  /**
   * Hard-delete a pipeline. Only allowed when it is unreferenced — rejected
   * with a structured error naming what blocks it (a linked SKU, order/step
   * runs, an SLA policy, an automation rule, or a vendor routing rule targeting
   * one of its steps). Its own versions/steps/graph-save snapshots are removed
   * with it. For a referenced pipeline, archive it instead (see archivePipeline)
   * — this mutation NEVER cascades into orders or runs.
   */
  deletePipeline: PipelineDeletedPayload;
  /**
   * Remove a consumption rule. Stops future consumption; past ledger rows are
   * history and are never touched.
   */
  deletePipelineConsumptionRule: DeletePipelineConsumptionRulePayload;
  /**
   * Delete a price book. Refused if any of its entries has ever priced an
   * order — archive it instead.
   */
  deletePriceBook: PriceBookDeletedPayload;
  /**
   * Delete a price book entry. Refused if it has ever priced an order —
   * expire it instead.
   */
  deletePriceBookEntry: PriceBookDeletedPayload;
  /**
   * Hard-delete a product_type. Only allowed when it is unreferenced —
   * rejected with a structured error naming what blocks it (orders, an intake
   * form, pricing lines/price-book entries, a bundle component, a pipeline still
   * pointed at it, an SLA policy, or a vendor routing rule). Its own
   * product_type_fields and product_type_pipelines join row are removed with it.
   * For a referenced product_type, archive it instead (see archiveProductType) —
   * this mutation NEVER cascades into orders, runs, pricing, or bundles.
   */
  deleteProductType: ProductTypeDeletedPayload;
  /**
   * Delete a tenant role. Rejected when is_system=true or when any
   * user_roles still reference the role (client must revoke first).
   */
  deleteRole: DeleteRolePayload;
  deleteSkillDefinition: SkillDefinitionDeletedPayload;
  /** Delete a step_dependencies row (DAG edge). Returns the deleted id. */
  deleteStepDependency: StepDependencyDeletedPayload;
  /**
   * Delete a step library entry. Refuses to delete if any pipeline_step
   * currently references the entry (safe default — admins unbind first).
   */
  deleteStepLibraryEntry: StepLibraryEntryDeletedPayload;
  /**
   * Hard-delete a user. ENG-679. Succeeds only when the user is
   * unreferenced; refuses with a Conflict naming the blockers (role
   * assignments, submitted orders, uploaded files, audit records) when it is
   * still referenced — deactivate via updateUser(isActive:false) in that case.
   * A user cannot delete their own account, and platform/system users
   * (organization_id IS NULL) cannot be deleted from a tenant.
   */
  deleteUser: UserDeletedPayload;
  deleteUserCertification: UserCertificationDeletedPayload;
  /** Delete a vendor routing rule. Returns the deleted id. */
  deleteVendorRoutingRule: DeleteVendorRoutingRulePayload;
  /**
   * Delete a work location (station). ENG-589. Hard-deletes an unreferenced
   * location; a zero-row delete is surfaced as an error (never a false success).
   */
  deleteWorkLocation: WorkLocationDeletedPayload;
  /** Soft-delete (disable) an external integration. */
  disableExternalIntegration: ExternalIntegration;
  /**
   * ENG-658 Part B — discard the open DRAFT version created by
   * openFormDefinitionForEdit WITHOUT publishing. Deletes the draft
   * form_definition_versions row and clears draftVersionId. The LIVE version
   * is untouched. Rejected when the form has no open draft. Mirrors
   * discardPipelineDraft.
   */
  discardFormDefinitionDraft: FormDefinitionEditPayload;
  /**
   * Discard one of the caller's own order-wizard drafts (ENG-1750). Self + org
   * scoped DELETE (RLS db/rls/0086 is the backstop); idempotent — an already-gone
   * / non-owned / cross-tenant id deletes zero rows and returns
   * `deleted: false`, never an error. Gated on clearpath.orders.submit (the same
   * authority that created the draft).
   */
  discardOrderDraft: DiscardOrderDraftPayload;
  /**
   * Self-versioning (requirement #2) — discard the open DRAFT version created
   * by openPipelineForEdit WITHOUT publishing. Deletes the draft version's steps +
   * dependencies and the DRAFT pipeline_versions row, then clears
   * draftVersionId. The LIVE version and every in-flight order pinned to it are
   * untouched (a DRAFT is never LIVE and no run pins it). Rejected when the
   * pipeline has no open draft.
   */
  discardPipelineDraft: PipelinePayload;
  /**
   * ENG-881 — detach the intake form from a SKU (delete the product_type_forms
   * row). Not lifecycle-gated on the SKU; the SKU keeps its status but reads
   * INVALID if a published form was required for it.
   */
  disconnectFormFromProductType: ProductTypePayload;
  /**
   * Disable an integration. Credentials in Secrets Manager are left intact
   * (the secret is rotated out-of-band, never deleted, to preserve audit).
   */
  disconnectIntegration: Scalars['Boolean']['output'];
  /** Unpair the selected iTero account and schedule secret cleanup. */
  disconnectIteroAccount: IteroConnection;
  disconnectScannerConnection: Array<ScannerConnection>;
  /**
   * Sample the DISTINCT top-level JSONB KEY NAMES of a whitelisted tenant custom-field
   * column so an author can pick a real key. KEYS ONLY — never a field value. Bounded
   * (sampled rows + statement_timeout), read under the org-wide analytics service session.
   * Audited with counts only (never the key names).
   */
  discoverFreeFormProjectionKeys: FreeFormKeyDiscoveryPayload;
  /**
   * Scan the org's LIVE intake forms and (re)derive scalar-field PROJECTION
   * candidates (ENG-1764 s4). Candidates are stamped fail-closed (phi_class=blocked,
   * unreviewed, unclassified, QI-by-default) so a freshly-discovered field is absent
   * from the catalog and k-anon-suppressed until a reviewer classifies it. Idempotent:
   * a re-scan refreshes label/value_type on still-unreviewed rows and NEVER downgrades
   * an approved/opted-down row. Gated on the analytics-module flag AHEAD of
   * clearpath.analytics.projection.manage.
   */
  discoverProjectionCandidates: ProjectionDiscoveryPayload;
  /** Edit an existing comment. */
  editComment: CommentPayload;
  /**
   * Enqueue an async assistant turn (ENG-2250, Phase 1). Fast unit resolver
   * (<2s): validate + internal-actor gate + mint an unguessable jobId + dispatch
   * to the worker (SQS+ESM+DLQ, ENG-2249), then return {jobId, status: RUNNING}.
   * The worker runs the agent loop and publishes the terminal result via
   * publishAssistantResult → onAssistantResult. Idempotent on
   * input.clientRequestId. Gate mirrors askAssistant (same principal).
   */
  enqueueAssistantJob: AssistantJob;
  /**
   * Enrol a scanner station: in one transaction creates a service account, a
   * paired+active `scanner_devices` row, and an API key bound to that account,
   * and returns the `cp_*` bearer ONCE. This is the surface that lets a fresh
   * tenant mint a scanner-station key (createApiKey needs a pre-existing service
   * account; nothing else could create one). Gated on `clearpath.api_keys.admin`
   * because it mints a key.
   */
  enrollScannerStation: EnrollScannerStationPayload;
  /**
   * Stop a price book entry from pricing new orders (sets validUntil to
   * now). Existing orders are unaffected.
   */
  expirePriceBookEntry: PriceBookEntryPayload;
  /** Extend (or clear) the expiry of an existing delegation. */
  extendCustomerDelegation: CustomerDelegationPayload;
  /** Fail a step run (terminal failure; engine handles loopback rules). */
  failStepRun: StepRunActionPayload;
  /**
   * ENG-40 — raise a vendor/outsourcer delay flag on an outsourced step
   * (reason + optional expected-resolution). Refuses a non-outsourced step and a
   * second concurrent active flag. Read it back via StepRun.vendorDelay.
   */
  flagStepRunVendorDelay: StepRunActionPayload;
  /**
   * Force-release (partial shipment) an order whose scanned portion is ready
   * to ship even though sibling products are not. Splits the unscanned products
   * into a new sibling order linked via `parent_order_id`. Bible §05:110-111.
   */
  forceReleaseOrder: ForceReleaseOrderPayload;
  /**
   * Purchase a carrier label for one inter-site shipment (ENG-1265). The
   * shipment's grain is per (order, destination site), so it ships in ONE box
   * and needs ONE label. FROM = origin site ship_from_*, TO = destination site
   * ship_from_* (a site's address serves send + receive in v1); referenceNumber
   * is the order barcode. Staff-triggered so the carrier call stays out of the
   * intake transaction. A carrier / validation failure is returned as
   * labelStatus=FAILED with an error message (and persisted), NOT thrown — so the
   * action can be retried. Always billed to the lab's own carrier account.
   */
  generateInterSiteShipmentLabel: GenerateInterSiteShipmentLabelResult;
  /**
   * Generate an invoice manually from a set of billing events (or a period).
   * Splits by currency into one invoice per currency. MOL-615.
   */
  generateInvoiceManual: GenerateInvoiceManualPayload;
  /**
   * Mint a single-use NAS-agent enrollment code for the caller's organisation
   * and return the raw `nas_…` code ONCE. The on-prem NAS agent presents this
   * code once to enroll (services/auth-service broker), after which it is spent.
   * Gated on `clearpath.nas.configure` — feature-flag-gated on
   * `clearpath.nas_module` AND step-up-MFA-gated (same risk class as
   * `clearpath.api_keys.admin`).
   */
  generateNasEnrollmentCode: GenerateNasEnrollmentCodePayload;
  /** Generate a statement manually for a billable party + period. MOL-616. */
  generateStatementManual: GenerateStatementManualPayload;
  /**
   * ENG-623 — add a product type to a customer's allowlist. Takes effect
   * immediately: there is no separate arming step, the grant list is the whole
   * gate (db/migrations/0187).
   */
  grantPartyCatalogAccess: PartyCatalogAccessPayload;
  /**
   * Instantiate a fresh dashboard from an org TEMPLATE. Copies the template's
   * definition (minus the `template` flag) + every widget's SemanticQuery selection
   * into a NEW dashboard owned by the caller; the copy is data-free and recomputes
   * under the caller. NotFound if `templateId` is not a template in the caller's
   * org. `[SEC]` (ENG-2112 / T3).
   */
  instantiateAnalyticsTemplate: AnalyticsDashboardPayload;
  /**
   * MOL-#37 — invite a STAFF user (distinct from inviteUser, which
   * provisions tenant-internal portal/customer users in the api Lambda).
   * Creates the Cognito user in the tenant's staff pool, the
   * staff_tenant_memberships row in control-plane, and a `users` row in the
   * tenant's Aurora cluster — all in one resolver call. Email send is
   * deferred; the response carries a temporary password the admin hands to
   * the invitee out-of-band. Routed to the control-plane Lambda because
   * staff_tenant_memberships lives in control-plane.
   */
  inviteStaffUser: InviteStaffUserPayload;
  /**
   * Invite a user — provisions a Cognito user (AdminCreateUser) + inserts
   * the tenant users row + applies any initial role assignments. CUSTOMER
   * invites suppress Cognito email and confirm the account so the invitee
   * can set a password via portal /forgot-password (ENG-813); VENDOR invites
   * do the same and carry the branded `user.invited_staff` email (ENG-1575).
   * SERVICE and SYSTEM are the only types for which Cognito's own invite email
   * is still not suppressed here — `createUser`, their normal path, sends
   * nothing at all.
   *
   * `userType: INTERNAL` is REFUSED with PreconditionFailed /
   * staff_provisioning_requires_control_plane and provisions NOTHING — no
   * Cognito account, no users row, no membership (ENG-2323). This Lambda
   * resolves its staff Cognito pool from one per-environment env var and
   * cannot reach a tenant's own staff pool, so staff onboarding is
   * `inviteStaffUser` only. Use that mutation instead.
   *
   * If COGNITO_USER_POOL_ID_INTERNAL /
   * COGNITO_USER_POOL_ID_CUSTOMER isn't wired on the api Lambda (dev mode),
   * the DB row is still created and the caller receives a warning in the
   * payload — the admin can later link the IdP subject via updateUser.
   */
  inviteUser: UserPayload;
  /** Render invoice PDF, upload to S3, and mark issued — no email. */
  issueInvoice: Scalars['Boolean']['output'];
  /**
   * Issue a billing adjustment against an invoice — refund, credit memo,
   * write-off, or generic adjustment. Staff-only; amounts above the tenant
   * step-up threshold require a re-authed session. Writes one
   * billing_adjustments row + recalculates invoice.amountDue. MOL-622.
   * BLOCKED ON payment provider selection for REFUND type (Phase 2 G-G/G-H);
   * CREDIT / WRITE_OFF / ADJUSTMENT types are internal-only and can ship now
   * — the stub returns NotImplemented until the orchestrator lands.
   */
  issueInvoiceAdjustment: BillingAdjustmentPayload;
  /**
   * Phase 2 — set or clear the human-readable label on a snapshot. Pass
   * label: null (or an empty string) to clear.
   */
  labelPipelineVersion: PipelineVersionPayload;
  /**
   * Create an ad-hoc relationship between two external parties (acquisition,
   * affiliation, etc.). The strict parent/child tree still uses
   * `upsertExternalParty(parentPartyId: …)`. Round 1a.
   */
  linkExternalParties: ExternalPartyRelationshipPayload;
  /**
   * Begin the OAuth/OIDC flow to link an identity provider to the caller's
   * global identity. Returns a redirect URL the browser should navigate to;
   * the callback completes the link. Per docs/clearpath-architecture/
   * 09-authentication.md.
   */
  linkIdentityProvider: IdentityProviderLinkPayload;
  /**
   * Attach an existing pipeline to a product_type via the
   * product_type_pipelines join. Idempotent on the (product_type, pipeline) pair.
   * One pipeline per SKU: the single link is always the SKU's default and is
   * mirrored to the legacy product_types.pipeline_id the engine reads
   * (`makeDefault` is ignored). A DRAFT or LIVE pipeline may be linked (setup /
   * fork awaiting publish); INACTIVE/ARCHIVED are rejected. Complements
   * setProductTypeDefaultPipeline (which pins an already-linked pipeline as the
   * default and requires it be LIVE).
   */
  linkPipelineToProductType: ProductTypePayload;
  /** Manually loop back a step run to an earlier step. */
  loopbackStepRun: StepRunActionPayload;
  /**
   * Assign (or reassign) a step to a technician from the Assign work board.
   * Who-may-assign-whom gates, in order: an internal caller holding
   * clearpath.assignments.write on the step (Cedar); the step's
   * unit inside the caller's scope — the units their role applies in and the
   * units below them, every unit for a whole-lab role (reason
   * `scope_not_entitled`); the assignee a member of the step's unit
   * (org_unit_memberships — whole-lab people are never members); the assignee
   * holding the step's required skills, machine authorizations and unexpired
   * certifications; and, unless acknowledged, the assignee working today.
   */
  manualAssignStep: AssignmentWorkItem;
  /** Mark every unread notification for the current viewer as read. */
  markAllNotificationsRead: MarkAllNotificationsReadPayload;
  /** Mark an invoice as paid (idempotent). */
  markInvoicePaid: Scalars['Boolean']['output'];
  /**
   * Mark a single notification as read. Idempotent — returns alreadyRead=true
   * if the row was already seen.
   */
  markNotificationRead: MarkNotificationReadPayload;
  /** Mark an order as delivered. */
  markOrderDelivered: OrderActionPayload;
  /** Mark an order as seated by the doctor. */
  markOrderSeated: OrderActionPayload;
  /** Mark an order as shipped. */
  markOrderShipped: OrderActionPayload;
  /**
   * Mark one order, or several orders of one customer, shipped under one
   * hand-entered tracking number. Each order runs markOrderShipped; one refusal
   * rolls back all.
   */
  markOrdersShipped: MarkOrdersShippedPayload;
  /**
   * Bind CSV-imported parties to their ABS ids (ENG-2693). The party loader's
   * idempotency resolves through migration_crosswalk, and the CSV import wizard
   * writes no crosswalk rows, so without this a Wave 1 party load into such a
   * tenant DUPLICATES every party rather than skipping it — which is what the
   * ENG-2691 precondition guard refuses and points here. Idempotent by
   * (sourceSystem, entityKind, legacyId): re-running writes nothing. One
   * ambiguous or unwaived-unresolved account number refuses the WHOLE plan.
   */
  migrationBackfillPartyCrosswalk: MigrationBackfillPartyCrosswalkResult;
  /**
   * Load one batch of canonical OPEN-order records (C7 / ENG-3458, Wave 5) into
   * orders + the engine state a migrated in-flight case needs (pipeline_runs,
   * step_runs, holds), deriving ClearPath state from the raw ABS status + per-line
   * step plan (eng-942-open-order-state.md §1/§4/§5). Idempotent on
   * (sourceSystem='abs_evolution', entityKind='order', legacyId) via
   * migration_crosswalk. One order's refusal rolls back that order only and the
   * batch carries on; a batch-fatal condition rolls the whole load back. Returns
   * reconciliation counts, never record payloads or PHI. INTERNAL-ONLY, Phase-6
   * gated (PHI write + encrypt-only KMS).
   */
  migrationLoadOpenOrders: MigrationReconciliationCounts;
  /**
   * Load one batch of canonical `party` records from a source adapter into
   * external_parties (LMS->ClearPath migrator Wave 1, ENG-936). Shares the
   * external-party insert core with bulkImportExternalParties, so a migrated
   * party and an imported one are indistinguishable. Idempotent by
   * (sourceSystem, entityKind, legacyId) via migration_crosswalk: re-running a
   * batch inserts nothing. Returns reconciliation counts, never record payloads.
   */
  migrationLoadParties: MigrationReconciliationCounts;
  /**
   * Load one SKU-batched slice of the ABS recipe stream as pipelines (Wave 3):
   * a linear pipeline per used recipe, a composed pipeline with a required
   * variant pick for each (SKU, site) slot with several, every slot bound, then
   * each pipeline published through the publish gate. Adopts the order-scope
   * host and never writes it. Idempotent on the crosswalk. Requires an unscoped
   * internal caller with clearpath.forms.write as well.
   */
  migrationLoadPipelines: MigrationPipelineLoadResult;
  /**
   * Load one batch of canonical `product_type` records into product_types as
   * DRAFT (Wave 2). Idempotent on (sourceSystem, entityKind, legacyId) via
   * migration_crosswalk: re-running a batch inserts nothing. Returns
   * reconciliation counts, never record payloads. SKUs land unpublished and
   * unpriced — list_price is the adapter's named stand-in, because ABS has no
   * list price for a service; real prices arrive in Wave 4.
   */
  migrationLoadProductTypes: MigrationReconciliationCounts;
  /**
   * Load one batch of canonical reference records (ENG-934, Wave 0), of which
   * ONLY hold reasons land — in hold_reason_codes. Reference data is an org-config
   * seed and is crosswalk-exempt, so idempotency is the target natural key
   * (organization_id, code): re-running a batch inserts nothing and reports it as
   * skippedAlreadyMigrated. A conflict with a natively authored reason is left
   * untouched and reported as adoptedExisting (never overwritten). shade, remake
   * reasons and the other non-hold reference kinds are `deferred` to Wave 2/3 form
   * authoring; tooth_crosswalk and carrier records are `rejected` as
   * adapter-internal / B5 mapping — each with a per-record reason, never silently
   * dropped. Returns counts + attributions,
   * never record payloads. Hold reasons land with responsible_party='customer' and
   * excludes_from_sla=true (the common-case defaults the adapter does not emit),
   * which a lab must review.
   */
  migrationLoadReferenceData: MigrationReferenceLoadResult;
  /**
   * Publish one batch of migrated SKUs (Wave 3, ENG-939 PR 4): DRAFT to LIVE
   * through bulkPublishProductTypes, whose publish UPDATE re-asserts that the SKU
   * has a LIVE default pipeline, a price and a LIVE form. Each SKU must be DRAFT
   * and have an org-wide pipeline C4 loaded, unless it is pipeline_exempt.
   * Requires an unscoped internal caller.
   */
  migrationPublishWave3: MigrationWave3PublishResult;
  /**
   * Reverse one committed migrationLoadPipelines batch (ENG-3512), found
   * through its crosswalk rows: unlink its slots, delete its pipelines, archive
   * its variant forms and delete its crosswalk rows, so the batch can be loaded
   * again. Refuses while any run, SLA, automation, routing or inventory rule
   * references its pipelines, or once any SKU it bound is no longer DRAFT.
   * Requires an unscoped internal caller with clearpath.forms.write as well.
   */
  migrationUndoPipelines: MigrationPipelineUndoResult;
  /** Molaris-admin (control plane) mutation namespace. Cedar-gated by molaris.*.* keys. */
  molarisAdmin: MolarisAdminMutation;
  /**
   * ENG-658 Part B — open a LIVE form_definition for editing by creating a
   * new DRAFT *version* of the SAME form (copies the live version's schema/
   * uiSchema into it). No fork, no new form id. In-flight order/product-level
   * answers stay validated against the version they were submitted under;
   * publishFormDefinition later promotes the draft in place. Idempotent —
   * re-opening a form that already has an open draft returns it. Mirrors
   * openPipelineForEdit.
   */
  openFormDefinitionForEdit: FormDefinitionEditPayload;
  /**
   * Self-versioning (requirement #2) — open a LIVE/INACTIVE pipeline for
   * editing by creating a new DRAFT *version* of the SAME pipeline (deep-copies the
   * live version's steps + dependencies into it). No fork, no new pipeline id: SKU /
   * automation / warranty links stay put. In-flight orders keep running on the
   * version they started on; publishPipeline later promotes the draft in place.
   * Idempotent — re-opening a pipeline that already has an open draft returns it.
   * Replaces the removed forkPipeline.
   */
  openPipelineForEdit: PipelinePayload;
  /** Pause a live rollout — freezes pending agents; already-updated agents stay. */
  pauseNasRollout: NasRolloutPayload;
  /**
   * Pause a LIVE product_type (LIVE -> INACTIVE): pull it off new orders
   * without archiving. Reversible via restoreProductType. Rejects non-LIVE.
   */
  pauseProductType: ProductTypePayload;
  /** Phase 0 backward-compat ping. DO NOT REMOVE. */
  ping?: Maybe<HelloWorld>;
  /**
   * Place a hold on a case or one of its units (ENG-16 / ENG-591).
   *
   * A hold records that work is legitimately waiting on somebody outside the lab's
   * control (or on an internal blocker), so the lab can see why a case is not
   * moving and deduct customer/vendor-caused wait time from its turnaround clock.
   *
   * Changes NOTHING about `Order.status`, `Order.lifecycleStatus` or any
   * `StepRun.status` — it writes one `order_holds` row and nothing else. What it
   * DOES change is behaviour: while a hold covers a step's unit (or its whole
   * case), `completeStepRun` / `assignStepRun` / `reassignStepRun` /
   * `loopbackStepRun` / `sendStepRunBack` refuse with `Conflict` naming the
   * reason, and the step disappears from the work queues. `failStepRun` stays
   * allowed — you may need to record the defect that caused the hold.
   *
   * Conflicts (all `Conflict`, never a 500):
   * - the unit already has an open hold, or the case already has an open
   *   case-scoped hold (enforced by partial unique index, so a double-click is
   *   safe);
   * - the order's lifecycle is closed or cancelled.
   */
  placeOrderHold: OrderHoldPayload;
  /** Exchange a purpose-bound guest bearer for a fresh 60-second launch. */
  prepareOroviaGuestPreview: OroviaPreviewPayload;
  /**
   * Authorize the exact file, ensure synchronization, or mint one fresh
   * single-use read-only Orovia launch. The browser supplies only fileId.
   */
  prepareOroviaPreview: OroviaPreviewPayload;
  /** Mint an authenticated doctor/technician edit or exact historical launch. */
  prepareOroviaReview: OroviaPreviewPayload;
  /** Dry-run apply — returns the reconciliation plan without committing. */
  previewTenantConfig: PreviewTenantConfigResult;
  /**
   * Propose an outbound federation link to a partner tenant. Creates the
   * link in 'proposed' status; the partner accepts via acceptFederationLink
   * on their side.
   */
  proposeFederationLink: FederationLinkPayload;
  /**
   * Author + classify + approve an ad-hoc free-text (free_form) custom-field
   * projection in one write. A free-text field classified PHI + exposed becomes
   * exposure=exposed (coherent, phi<>blocked), unlike approveProjection which holds PHI.
   * Idempotent on the field identity; audited with governance flags / closed-vocabulary
   * coordinates only (never json_key/label/values).
   */
  proposeFreeFormProjection: ProjectionGovernancePayload;
  /**
   * Publish an async assistant job's terminal result. SYSTEM-ONLY — invoked by
   * the assistant worker Lambda over AWS_IAM, exactly like createNotification;
   * NEVER reachable by a user token (identity for the turn was fixed at enqueue,
   * not derived from this input). Fans out on the onAssistantResult(jobId)
   * subscription. The caller MUST select `jobId` in its selection set or the
   * fan-out silently drops (see notifications/push.ts). ENG-2250.
   */
  publishAssistantResult: AssistantJob;
  /** P2.4 (2026-04-25) — transition a DRAFT automation_rule to LIVE. */
  publishAutomationRule: AutomationRulePayload;
  /**
   * P2.4 (2026-04-25) — transition a DRAFT form_definition to LIVE. Publishes
   * the form's open DRAFT version, swinging live_version_id to it in place (new
   * orders pick up the new version; in-flight orders keep their pinned version).
   * No binding moves — bindings point at the stable form id. Archives the prior
   * live version (retained for order pins) and clears draftVersionId.
   */
  publishFormDefinition: FormDefinitionPayload;
  /**
   * P2.4 (2026-04-25) — transition a DRAFT pipeline to LIVE. Once LIVE the
   * pipeline + its steps + dependencies become immutable; in-flight orders pin
   * to the version they started on. Rejects calls on LIVE / ARCHIVED rows.
   */
  publishPipeline: PipelinePayload;
  /** ENG-8 — transition a DRAFT product bundle to LIVE (requires >= 1 component). */
  publishProductBundle: ProductBundlePayload;
  /** P2.4 (2026-04-25) — transition a DRAFT product_type to LIVE. */
  publishProductType: ProductTypePayload;
  /** P2.4 (2026-04-25) — transition a DRAFT sla_policy to LIVE. */
  publishSlaPolicy: SlaPolicyPayload;
  publishWorkInvalidation: WorkInvalidationSignal;
  /**
   * ENG-3881 S2.6c — what a hand-shipped order's shipping charge would be:
   * covered by a matching ClearPath label, a live carrier quote plus the lab
   * markup (composed server-side), manual entry when the quote fails, or not
   * required (the lab pays). Writes nothing; markOrdersShipped / setOrderTracking
   * re-quote before recording.
   */
  quoteManualShippingCharge: ManualShippingChargeQuote;
  /**
   * Reassign a step run to a different user. Gated on clearpath.steps.reassign
   * (with the step-run org-unit scope check) only: unlike manualAssignStep it does
   * not test the new assignee's department membership or skills. No app calls it
   * today (apps/main declares the operation but no screen uses it); the boards
   * hand work off with manualAssignStep.
   */
  reassignStepRun: StepRunActionPayload;
  /**
   * Re-enable a saved bank account the payer already has on file, by
   * capturing a FRESH NACHA authorization for it — no re-tokenization, no
   * re-entering account and routing numbers.
   *
   * Allowed ONLY where `PaymentMethod.unusableReason` is MANDATE_MISSING or
   * BILLING_ADDRESS_MISSING. The revoked reasons are refused server-side, not
   * merely hidden in the UI. The server confirms the vault entry still exists at
   * the provider before it writes anything, then records a new mandate and a new
   * append-only authorization row dated today. Same permission and step-up MFA as
   * addPaymentMethod. ENG-1988.
   */
  reauthorizePaymentMethod: PaymentMethodPayload;
  /**
   * Re-run pricing resolution for an existing order. Admin-only — most orders
   * freeze pricing at creation. MOL-611/MOL-613.
   */
  recalculateOrderPricing: Scalars['Boolean']['output'];
  /**
   * ENG-1264 — receive an inter-site shipment at its DESTINATION site. In one
   * transaction: flips every in-flight case (order_product) on the shipment from
   * in_transit -> at_site (current_site_id := the destination), marks the shipment
   * received (received_at / received_by), and emits one case.location.received per
   * case. Rejected (Conflict) if the shipment is not in an open (dispatched /
   * in_transit) state — a re-receive is a no-op the caller must not retry as
   * success. Internal-only (destination lab/handheld staff): the resolver gates
   * with requireInternalActor and intentionally has no fine-grained key.
   */
  receiveInterSiteShipment: ReceiveInterSiteShipmentPayload;
  /**
   * Record stock arriving. Positive quantity only; use adjustStock to correct
   * downward.
   */
  receiveStock: StockMovementPayload;
  /**
   * Record a barcode scan event. Path #3 of the three step-completion paths
   * (button, drag, scan). When scanPurpose is STEP_COMPLETE or STEP_START the
   * resolver also appends a new step_runs row and emits step.completed /
   * step.started; the pipeline engine advances the DAG.
   */
  recordBarcodeScan: BarcodeScanPayload;
  /** Record a billing event. SYSTEM-ONLY — invoked by Lambda billing pipeline. */
  recordBillingEvent: BillingEventPayload;
  /**
   * Record a tax permit number for one state, and optionally START COLLECTING
   * there. ENG-2497.
   *
   * ⚠️ THE NUMBER IS RECORDED AND ATTESTED, NEVER VERIFIED. ClearPath performs NO
   * lookup against CDTFA or any other tax authority — no such check exists in this
   * product. What is stored is what somebody typed from the registration document,
   * and the attestation recorded beside it is the only evidence that exists. The
   * console must never imply the number was checked.
   *
   * Writes the number onto the state's own registration and every postal-code
   * registration under it. With `activate: true` it additionally flips the
   * `PENDING` ones whose collecting window is still open to `ACTIVE`, **which
   * starts charging tax to every customer shipping into that state**. `CLOSED`
   * registrations and ones whose window has ended are never re-opened.
   *
   * Counts as ONE operation against the per-organization 24-hour change budget,
   * whatever its row count.
   *
   * Refuses, by name and never as a silent no-op:
   * `ValidationError` (a malformed country or region),
   * `TaxRegistrationNumberRequired` (a blank or over-long number),
   * `TaxPermitAttestationRequired` (`attested` not true),
   * `TaxPermitAttestationMismatch` (`attestationText` is not the text this server
   * records — reload),
   * `TaxPermitNoMatchingRegistrations` (no registration in this organization
   * covers that state; list the jurisdiction first, which creates one),
   * `TaxWriteBoundsExceeded`, `TaxWriteNotApplied`, `TaxWriteNotPermitted`,
   * `TaxWriteRejectedByDatabase`.
   */
  recordTaxPermit: TaxPermitRecordedPayload;
  /** Re-render and store an invoice PDF without changing status or sending email. */
  refreshInvoicePdf: Scalars['Boolean']['output'];
  /**
   * Send money back to a payer against a settled charge.
   *
   * ⚠️ ITS OWN KEY, DELIBERATELY NARROWER THAN `clearpath.payments.operate`. This
   * is the only action on this console that moves money OUTWARD and the only one
   * that is irreversible, so a tenant can grant "book the settlement the provider
   * already reported" without granting cash-out.
   *
   * ⚠️ AND IT IS SUBJECT TO A SERVER-SIDE PER-ORG DAILY REFUND CEILING. The key is
   * declared step-up-MFA and that requirement is currently WAIVED platform-wide, so
   * the ceiling is what stands in for the second factor: it bounds the total
   * refunded across the organization in a rolling 24 hours, is enforced under a
   * per-org lock ahead of the provider call, and cannot be raised from inside the
   * product.
   *
   * ⚠️ AND IT IS EXACTLY-ONCE PER `input.clientRequestId`. Re-submitting a refund
   * does not send a second one: the token is claimed in a per-tenant ledger before
   * the money path is entered and a repeat is refused with `Conflict`. See the
   * field's own description for why it is required.
   *
   * Every guard on the underlying ledger operation still applies: a per-charge
   * lock, a cumulative already-refunded check, a deterministic provider
   * idempotency key, a hard cap against the ORIGINATING attempt amount, and an
   * outright refusal when the receiving bank already sent the entry back.
   */
  refundPaymentTransaction: RefundPaymentTransactionPayload;
  /**
   * ENG-521: render one work-slip PDF per order_product instead of a single
   * combined PDF, so the lab can print a separate physical ticket per product
   * ("3 products = 3 work tickets" — Huong Le, lab ops). Each PDF uploads to
   * its own S3 key (orders/{orderId}/products/{orderProductId}/slip.pdf) and
   * never overwrites the combined slip (regenerateSlipPdf's
   * orders/{orderId}/slip.pdf). The barcode printed on every returned ticket
   * is still the ORDER's barcode (orders.barcode_value) — order_products has
   * no scannable identity of its own today (no barcode/QR column, and
   * barcode_scans has no order_product FK), so N tickets from an N-product
   * order carry an IDENTICAL barcode; `sequence`/`sku`/`productName` are what
   * a human reads to tell them apart, not what a scanner can. A real
   * per-product SCAN is follow-up work, not implemented here — see
   * services/barcode/src/slip_pdf.ts's module comment.
   */
  regenerateProductSlipPdfs: Array<ProductSlipDocument>;
  /**
   * Regenerate the barcode slip PDF for an order and return its BYTES.
   *
   * ENG-2547: this used to return a 1-hour presigned S3 URL. A presigned URL is
   * bearer authority — for its whole lifetime anyone holding the string fetched
   * the PDF with no ClearPath authentication, no Cedar check and no tenant check,
   * because none of that is re-evaluated at the S3 GET. A copy-pasted link
   * therefore opened the slip for an unauthenticated stranger. The bytes now ride
   * the caller's existing authenticated session and no shareable link is minted;
   * the object is still written to S3 for reprint (see `s3Key`).
   *
   * ENG-2511: `printOptions` overrides, for THIS print only, which form fields
   * appear on the slip. Omit it to use each form's own "Print on order slip"
   * flags, which is what an automated or hardware print gets. A print that
   * matches those flags rewrites the canonical orders/{orderId}/slip.pdf; a
   * customised one is written to a separate ad-hoc key instead, so one tech's
   * one-off reprint never becomes the case's official slip.
   */
  regenerateSlipPdf: SlipDocument;
  /**
   * Register a partner OAuth2 client bound to a service account. Stores the
   * partner's PUBLIC jwks at registration for `private_key_jwt` (no live jwks_uri
   * fetch — SSRF/key-poisoning closed); mints a `cpcs_…` secret ONCE for
   * `client_secret`. A PHI-bearing scope may ONLY be registered on a
   * `private_key_jwt` client — a `client_secret` request for a PHI scope is
   * rejected at registration.
   */
  registerOAuthClient: RegisterOAuthClientPayload;
  /** Confirm an uploaded file (post-PUT). */
  registerUploadedFile: RegisterUploadedFilePayload;
  /**
   * Decline an incoming federation proposal. Bible §06:18. The link must be
   * in `proposed` status; on success status flips to `rejected`, revoked_at is
   * set to now(), and a `federation.link.rejected` event is emitted so the
   * proposer tenant's federation-listener can mirror the outcome on its side.
   * Permission key reuses the revoke family — a tenant that can revoke a link
   * can decline an incoming proposal.
   */
  rejectFederationLink: RejectFederationLinkPayload;
  releaseAssignment: AssignmentWorkItem;
  /**
   * Release an open hold, restarting the turnaround clock (ENG-16 / ENG-591).
   *
   * Writes `releasedAt` / `releasedByUserId` / `releaseNote` on the hold and
   * nothing else. Resume needs no repair work precisely because placing the hold
   * changed no state: the step is still `ready`/`in_progress` with its original
   * assignee, so it simply reappears in the queues and Complete re-enables.
   *
   * `releaseNote` is required (>= 10 characters). Any role that can place a hold
   * can release one, so the note plus the `orders.hold_released` audit row are the
   * only accountability on the action that restarts the clock.
   *
   * Releasing an already-released hold returns `Conflict` rather than succeeding
   * silently — the second caller is acting on a stale view and must refetch. That
   * compare-and-swap also makes a double-clicked release safe.
   */
  releaseOrderHold: OrderHoldPayload;
  /**
   * Mark a file_location stale (the NAS agent removed the physical copy,
   * or a federated share was revoked). Soft-delete only (MOL-583).
   */
  removeFileLocation: RemoveFileLocationPayload;
  /**
   * Detach a saved payment method. The server calls
   * `paymentProvider.detachPaymentMethod(...)` on a best-effort basis and
   * marks the local row inactive regardless. Step-up MFA required. MOL-622.
   * BLOCKED ON payment provider selection (Phase 2 G-G/G-H).
   */
  removePaymentMethod: PaymentMethodPayload;
  /** Revoke a permission from a tenant role. */
  removePermissionFromRole: TenantRolePayload;
  removeShift: ShiftDeletedPayload;
  removeUserSkill: UserSkillDeletedPayload;
  /**
   * 1-click reorder (ENG-1018 item 4). Clone a past order the caller is
   * entitled to (own practice only — the portal order-scope predicate is
   * enforced; a cross-practice orderId resolves to NotFound) into a fresh
   * caller-owned wizard draft with the same products, tooth/material/shade and
   * workflow, then return the prefilled payload for the confirm-recap modal.
   */
  reorderFromOrder: ReorderDraftPayload;
  /**
   * P1.7 (2026-04-25) — start an account-deletion request for the calling
   * customer. The user must echo their own email in `confirmationEmail` to
   * arm the request. Sets a 30-day cooldown window during which the request
   * can be cancelled. After cooldown a scheduled worker performs the soft +
   * hard delete ladder.
   */
  requestAccountDeletion: AccountDeletionStatus;
  /**
   * Bundle multiple files (a whole order, or an explicit set) into one ZIP and
   * mint a single presigned GET for it (ENG-24). Every file is re-authorized
   * through the same owner-scoping as requestFileDownload.
   */
  requestBulkFileExport: RequestBulkFileExportPayload;
  /**
   * Request a chairside ADJUSTMENT to a seated or closed case (ENG-1424).
   *
   * The customer-initiated counterpart to `requestWarrantyReturn`. Creates an
   * `order_type='adjustment'` order that runs the short adjustment pipeline
   * (`product_types.adjustment_pipeline_id`, else the org default adjustment
   * pipeline), and leaves the source case untouched.
   *
   * Gated on `clearpath.portal.adjustment.request` and additionally scoped to
   * the caller's OWN cases: the source is re-read under the caller's full read
   * scope (a case they cannot read is `NotFound`, not `Forbidden`), and for a
   * customer principal the server independently re-checks party ownership of
   * the source before any write.
   */
  requestCaseAdjustment: RequestCaseAdjustmentPayload;
  /**
   * Mint a presigned GET URL for an uploaded file's primary location
   * (MOL-583).
   */
  requestFileDownload: RequestFileDownloadPayload;
  /** Pre-allocate a file row + return a presigned PUT URL. */
  requestFileUpload: RequestFileUploadPayload;
  /**
   * The stored label for one shipment in the caller's org. This field returns
   * bytes, not a URL; clients open or save them through a same-origin blob: URL.
   * OPEN refuses a voided label (Conflict); DOWNLOAD serves it.
   */
  requestShippingLabelDocument: ShippingLabelDocument;
  /**
   * Rate-shop a package with the given carrier. Returns every service
   * level the tenant's account is entitled to; filter client-side by price /
   * transit time. Tenant must have connected the carrier (via
   * connectIntegration) before this succeeds.
   */
  requestShippingRates: Array<ShippingRate>;
  /**
   * Open a warranty return for a delivered/seated/closed order (Bible §05:158-167).
   *
   * Creates a sibling order linked via `warranty_for_order_id`, picks up the
   * warranty pipeline (productType.warranty_pipeline_id, else org default
   * warranty pipeline), and back-updates the source order to
   * lifecycle_status='warranty_returned'.
   */
  requestWarrantyReturn: RequestWarrantyReturnPayload;
  /**
   * Resend an outstanding invite email (increments resend_count +
   * last_sent_at). Rejects when the invitation is already accepted or
   * revoked. (P0.4.3)
   */
  resendInvitation: Invitation;
  /** Continue or send work back after the practitioner requests changes. */
  resolveCustomerApproval: CustomerApprovalResolutionPayload;
  /** Confirm an exact scanner identity while matching cases remain staged. */
  resolveScannerIntakeIdentity: ResolveScannerIntakeIdentityPayload;
  /**
   * ENG-40 — resolve the step's ACTIVE vendor delay flag (optional note).
   * Refuses (NotFound) when there is no active delay.
   */
  resolveStepRunVendorDelay: StepRunActionPayload;
  /**
   * Mark a ticket resolved. Optional resolutionNote is stored in
   * customFields.
   */
  resolveTicket: TicketPayload;
  /**
   * ENG-3881 S3.4 — re-resolve the ledger accounts of one invoice month's issued
   * invoice lines (voided ones included) with the current mappings, appending a
   * new stamp where the account changed; earlier stamps stay readable. Staff only;
   * requires clearpath.finance.ledger_manage. At most 12 calls that change
   * something per organization per rolling 24 hours, 60 calls of any outcome,
   * and 20,000 lines per call. Never moves a mapped line to unmapped. Refuses
   * `LedgerRestampRefused` (extensions.reason: month, reason_required,
   * reason_too_long, reason_invalid, confirm_required, limit, ceiling,
   * too_many_lines, timeout, busy).
   */
  restampInvoiceLedgerAllocations: RestampInvoiceLedgerAllocationsResult;
  /**
   * Restore an archived calculated measure back to live (clears archivedAt), so
   * it is offered in the authoring picker again. Idempotent. NotFound if the id is
   * not a measure in the caller's org.
   */
  restoreCalculatedMeasure: CalculatedMeasurePayload;
  /**
   * Ported from dev's ENG-780b forms lifecycle versioning at the 2026-08-05
   * merge — "Restore this version". Seeds a DRAFT of the form from a chosen
   * prior version's schema/uiSchema (forks one from the live version if none
   * is open) so an admin can review and Publish it. This is the SAFE
   * draft-fork path, NOT a raw revert: it never mutates the archived version,
   * never swings live_version_id, and leaves the seeded version DRAFT for the
   * admin to publish — in-flight orders keep their pinned version. Returns the
   * form with content set to the restored draft (draftVersionId refreshed).
   * Mirrors openFormDefinitionForEdit; dev's own openFormForEdit/
   * discardFormDraft mutations were dropped at merge time as pure duplicates
   * of openFormDefinitionForEdit/discardFormDefinitionDraft below.
   */
  restoreFormVersion: FormDefinitionEditPayload;
  /**
   * Phase 2 (unified-canvas rebuild) — copy a snapshot's YAML + reactflow
   * metadata back onto its parent pipeline. The pipeline must be DRAFT. The
   * restore appears in version history as a new snapshot (labelled
   * "Restored from vN").
   */
  restorePipelineVersion: PipelinePayload;
  /**
   * Restore a paused product_type (INACTIVE -> LIVE). Re-runs the publish
   * completeness gate (Live pipeline + price + Live bound form), since a required
   * field may have lapsed while paused. Rejects non-INACTIVE.
   */
  restoreProductType: ProductTypePayload;
  /** Resume a paused rollout back to the phase it was paused from. */
  resumeNasRollout: NasRolloutPayload;
  /**
   * Stop ONE tax jurisdiction applying, from a date forward — the undo for a
   * postal code listed by mistake. ENG-2497 §9.
   *
   * **THE TRAP THIS EXISTS FOR.** Listing a postal code makes the engine resolve
   * EVERY address in it to your jurisdiction. If that jurisdiction lacks a
   * registration or a rate for the line's category, the sale is taxed at ZERO
   * while every address you did NOT list keeps collecting the regional rate —
   * backwards, and silent. Until this mutation there was no way to unlist it.
   *
   * **IT IS A SOFT CLOSE, NOT A DELETE.** The jurisdiction, its boundaries and its
   * rates are given an end date; its registrations get a `collectsTo`. Nothing is
   * removed. `invoice_line_taxes` references the jurisdiction and is append-only
   * (db/migrations/0307), so an invoice issued while it was live keeps evidencing
   * what it was charged under, and a report re-run over a closed period still
   * resolves it. From `effectiveTo` forward the engine simply stops seeing it —
   * the same date-effectivity every tax read already uses, not a second mechanism.
   *
   * **WHAT THE ADDRESSES COLLECT AFTERWARDS.** They fall back to the next tier: a
   * postal code falls to your regional default, a regional default falls to the
   * published platform row. **If nothing covers them they collect NOTHING**, and
   * this mutation does NOT refuse that — for a postal code listed by mistake it is
   * the intended outcome. Ask `taxRateLookup` with `asOf:` set to the retirement
   * date to see the real answer before you commit to it; it runs the same resolver
   * that will bill it.
   *
   * Refuses, by name and never as a silent no-op:
   * `TaxJurisdictionNotFound` (another organization's row, or none),
   * `TaxPlatformRowImmutable` (a shared platform row),
   * `TaxJurisdictionAlreadyRetired` (already closed on or before this date — a
   * second retire would write an audit row about a change that did not happen;
   * bringing an already-scheduled later close FORWARD is allowed),
   * `TaxWriteBoundsExceeded` (a retirement date on or before the last day an
   * invoice was billed against it, on or before its own `effectiveFrom`, or past
   * the per-organization 24-hour change limit),
   * `Conflict` (stale or missing `expectedVersion`).
   *
   * Counts against the same per-organization 24-hour change budget as the other
   * single-row tax writes.
   */
  retireTaxJurisdiction: TaxJurisdictionRetiredPayload;
  /**
   * Retry GuardDuty for the exact current object after a scan stalls or fails.
   * Duplicate requests are coalesced and downloads remain fail-closed.
   */
  retryFileScan: RetryFileScanPayload;
  reviewTimeOffRequest: TimeOffPayload;
  /**
   * Revoke every session for the caller. If exceptCurrent=true (default) the
   * current session stays active. Bumps the per-user tokens_invalidated_after
   * watermark so all outstanding tokens become invalid. Per
   * docs/clearpath-architecture/11-sessions-and-tokens.md.
   */
  revokeAllMySessions: SessionRevocationPayload;
  /**
   * Immediately revoke an API key. Idempotent — subsequent calls return true
   * without side-effect.
   */
  revokeApiKey: Scalars['Boolean']['output'];
  /**
   * Revoke an existing delegation by setting expires_at=now() and recording
   * the optional reason in custom_fields.revocation_reason.
   */
  revokeCustomerDelegation: CustomerDelegationPayload;
  /**
   * Stop honouring ONE exemption certificate. ENG-2497 slice 5.
   *
   * Sets `status` to `REVOKED`; it does NOT delete. `invoice_line_taxes`
   * references the certificate and is append-only (db/migrations/0307), so an
   * invoice that was issued exempt must keep evidencing WHY. Revoking stops the
   * engine honouring it from the next calculation onward and restores tax on new
   * invoices; invoices already issued keep the figures they were billed at.
   *
   * Refuses, by name and never as a silent no-op: `TaxExemptionNotFound` (another
   * organization's row, or none), `TaxExemptionAlreadyRevoked` (a second revoke
   * would write an audit row about a change that did not happen), `Conflict`.
   */
  revokeCustomerTaxExemption: CustomerTaxExemptionPayload;
  /**
   * Revoke an active federation link. Phase 1.1 (MOL-603). Sets revoked_at
   * + status='revoked'; tears down cross-account EventBridge permissions.
   * Non-reversible — a new link must be created to re-establish the bridge.
   */
  revokeFederationLink: FederationLinkPayload;
  /**
   * Revoke an outstanding invite (terminal; further resend rejects).
   * Optionally cleans up the Cognito shell user if the invitee never
   * signed in. (P0.4.3)
   */
  revokeInvitation: Invitation;
  revokeMachine: MachineAuthorizationDeletedPayload;
  /**
   * Revoke a single session owned by the caller (anything except the current
   * session). Appends a session_revocations row and adds a Redis denylist entry
   * for the session scope. Per docs/clearpath-architecture/11-sessions-and-tokens.md.
   */
  revokeMySession: SessionPayload;
  /**
   * Revoke an enrolled NAS agent device for the caller's organisation so it can
   * no longer mint tokens. Flips the durable revoked state AND denylists the device
   * in the authorizer cache, killing its unexpired tokens immediately. Idempotent:
   * a second revoke, or an unknown/cross-org `deviceId`, returns `revoked:false`
   * without error. Gated on `clearpath.nas.admin` — feature-flag-gated on
   * `clearpath.nas_module` AND step-up-MFA-gated (destructive; higher-privilege
   * than `clearpath.nas.configure`).
   */
  revokeNasDevice: RevokeNasDevicePayload;
  /**
   * Immediately revoke a partner OAuth2 client. Idempotent — subsequent calls
   * return true without side-effect. A revoked client can no longer mint tokens.
   */
  revokeOAuthClient: Scalars['Boolean']['output'];
  /** Revoke one guest-preview link immediately and idempotently. */
  revokeOroviaGuestPreviewLink: OroviaGuestPreviewLink;
  /**
   * ENG-623 — remove a product type from a customer's allowlist.
   *
   * A status flip to revoked, not a delete, so "what could they order in March?"
   * stays answerable. Refuses on an INHERITED grant: it must be revoked at the
   * party that owns it, or the DSO's other practices would lose it silently.
   */
  revokePartyCatalogAccess: PartyCatalogAccessPayload;
  /**
   * ENG-658 — remove a party's order-form assignment, falling back to the
   * tenant default. A status flip, not a delete — same audit-trail reasoning
   * as revokePartyCatalogAccess.
   */
  revokePartyOrderFormAssignment: PartyOrderFormAssignmentPayload;
  /**
   * Revoke one role assignment: one place the user's role applies. Revoking
   * their last row leaves them with no role. Refused when it would remove the
   * organization's last holder of clearpath.roles.assign.
   */
  revokeRole: UserRolePayload;
  /** Soft-delete a Phase 6 share link and propagate via denylist. */
  revokeShareLink: Scalars['Boolean']['output'];
  /** Revoke an active share link. */
  revokeSharedAccessLink: SharedAccessLinkPayload;
  /**
   * Revoke an outstanding STAFF (user_type='internal') invitation (ENG-2225).
   * The staff counterpart to revokeInvitation, routed to the control-plane Lambda
   * because a staff invite spans Cognito + control-plane staff_tenant_memberships +
   * the tenant `users` row. RECOVER-IN-PLACE: DISABLES the Cognito account (never
   * deletes it) and flips the membership + user_invitations row to revoked, so a
   * later re-invite of the same address re-enables the SAME account (same sub).
   * Requires clearpath.users.create AND clearpath.users.deactivate on the target
   * tenant (enforced inline, tenant authority only — no platform-staff fallback).
   * Refuses an already-accepted invitation (deactivate an accepted user via
   * updateUser instead). `invitationId` is the tenant-plane user_invitations.id.
   */
  revokeStaffInvitation: RevokeStaffInvitationPayload;
  /**
   * Revoke a trusted-device record owned by the caller. Idempotent — already-
   * revoked rows return the current state. Per docs/clearpath-architecture/
   * 11-sessions-and-tokens.md.
   */
  revokeTrustedDevice: TrustedDevicePayload;
  /**
   * Roll a rollout back to an earlier version (phase -> rolled_back). The engine
   * serves this version bypassing the no-downgrade guard.
   */
  rollbackNasRollout: NasRolloutPayload;
  /**
   * Re-apply a prior document, creating a new versioned document with
   * status=ROLLED_BACK pointing at the source version.
   */
  rollbackTenantConfig: RollbackTenantConfigResult;
  /**
   * Rotate an API key — mint a replacement, keep both valid for a 7-day
   * grace window, then auto-revoke the old one.
   */
  rotateApiKey: RotateApiKeyPayload;
  /**
   * Rotate a partner OAuth2 client — mint a replacement credential, keep both
   * valid for the rotation grace window, then auto-revoke the old one. For a
   * `private_key_jwt` client supply the new PUBLIC jwks; for `client_secret` a
   * fresh secret is minted and returned ONCE.
   */
  rotateOAuthClient: RotateOAuthClientPayload;
  /**
   * Rotate the active key for an enrolled station — mint a replacement, keep
   * both valid for a 7-day grace window, then auto-revoke the old one. Returns
   * the new `cp_*` bearer ONCE.
   */
  rotateScannerStationKey: RotateScannerStationKeyPayload;
  /** Persist a draft YAML document. Does not validate or apply. */
  saveDraftTenantConfig: TenantConfigDocument;
  /**
   * Persist an in-flight order-wizard draft (Phase 3.4 autosave). LIVE against
   * the `order_drafts` table (db/migrations/0029): a null draftId inserts a new
   * draft, a non-null one UPDATEs the caller's own existing draft (self + org
   * scoped). Companion to the portal's localStorage autosave so drafts survive
   * device swaps + session expiry. The caller resumes drafts via Query.
   * myOrderDrafts / Query.orderDraft, promotes one to a real order by passing
   * SubmitOrderInput.draftId to submitOrder (which deletes it on success), or
   * abandons one via discardOrderDraft.
   */
  saveOrderDraft: SaveOrderDraftPayload;
  /** Explicitly pair one account offered by the caller's live OAuth session. */
  selectIteroAccount: IteroConnection;
  /**
   * Send a threaded federation message scoped to a work item (step_run /
   * order). Phase 9 (MOL-608). Resolver invokes the federation-listener
   * Lambda with operation 'sendFederationMessage'; the Lambda signs + ships
   * the event to the partner's EventBridge bus.
   */
  sendFederationMessage: FederationMessagePayload;
  /** Render an invoice PDF, upload to S3, and email a GetObject presigned link. MOL-615. */
  sendInvoice: Scalars['Boolean']['output'];
  /**
   * Round 2b "Send back" inline action on the OrderDetailPane Steps tab.
   * Supersedes the source step_run and creates a new attempt at the chosen
   * upstream step. Industry-neutral counterpart of `loopbackStepRun` for the
   * operator-facing UX.
   */
  sendStepRunBack: StepRunActionPayload;
  /**
   * Flag/unflag one of the caller's OWN dashboards as a reusable template.
   * Owner-only (only the owner marks their artifact a template); NotFound if the id
   * is not the caller's own dashboard in their org. Sets/clears an additive
   * `template` flag on the dashboard `definition`.
   */
  setAnalyticsDashboardTemplate: AnalyticsDashboardPayload;
  /**
   * Upsert the authenticated caller's own Overview KPI-board personalization
   * (ENG-1961). Self-scoped: organizationId and userId are derived server-side from
   * the caller, so it can only ever write the caller's own row. Gated on
   * clearpath.analytics.view — any viewer may personalize their own board.
   * Presentation-only; changes no measure execution, gating, or k-anon.
   */
  setAnalyticsOverviewPreferences: AnalyticsOverviewPreferencesPayload;
  /**
   * Set (create or replace) the org's target for a measure (ENG-2092). UPSERT on
   * (organization, measureKey); `measureKey` is validated against the semantics
   * registry (unknown -> ValidationError, nothing written). Gated on
   * clearpath.analytics.manage.
   */
  setAnalyticsTarget: AnalyticsTargetPayload;
  /**
   * Set a payment method as the party's default. Un-flags any previous
   * default in the same transaction. MOL-622. BLOCKED ON payment provider
   * selection (Phase 2 G-G/G-H).
   */
  setDefaultPaymentMethod: PaymentMethodPayload;
  /**
   * ENG-3881 — map one subject onto an account, or remove its mapping with a
   * null `accountId` (returns null). Staff only; requires
   * clearpath.finance.ledger_manage. Refuses `LedgerAccountNotFound`,
   * `LedgerAccountInactive`, `LedgerAccountTypeNotAllowed`, `LedgerSubjectNotFound`,
   * `ValidationError`.
   */
  setLedgerMapping?: Maybe<LedgerMapping>;
  /**
   * ENG-2571 §E — set WHO PAYS for an order after submit (DSO-level billing:
   * bill a practice's case to its parent group). Deliberately NOT a field on
   * `UpdateOrderInput`: that mutation is a general customer-visible patch whose
   * `customFields` arm is a deep-merge JSON patch, and an FK party id with its
   * own money semantics does not belong there — it gets its own audit action so
   * a re-point is legible in the Activity tab.
   *
   * Constrained server-side: the new billing party must be in the caller's
   * organization AND be the order's customer party or one of its ancestors.
   *
   * Refused once the order has any billing_events row — the boundary is CAPTURE,
   * not the pricing snapshot.
   *
   * Changing the billing party does NOT re-price the order: pricing resolves from
   * the customer/practitioner anchor, never from who pays.
   *
   * INFRA NOTE: this is a new SDL root Mutation field — it needs a paired
   * API_LAMBDA_FIELDS binding in the infrastructure repo's
   * lib/stacks/api-stack.ts before AppSync will route it to this Lambda.
   */
  setOrderBillingParty: OrderActionPayload;
  /**
   * ENG-3286 — change an order's shipping terms. Refused once the order is on a
   * non-void invoice, and when it would change the account an active label
   * ships on (void the label first). `expectedTerms` / `expectedShippingAccountId`
   * (null = no account) are what the caller saw; a mismatch is a Conflict.
   */
  setOrderShippingTerms: OrderShippingTerms;
  /**
   * Manual override of the order's top-level status. Used by the Pipeline
   * Kanban drag-drop to let managers reposition work when the pipeline
   * state doesn't match ground truth. Emits order.status_overridden event.
   */
  setOrderStatus: OrderActionPayload;
  /**
   * Add or correct the hand-entered tracking number of an order that already
   * shipped (shipped, delivered, seated, closed or pending payment). Refused while an active label
   * covers the order: void the label instead. Changes tracking only: never marks
   * shipped again, bills or notifies.
   */
  setOrderTracking: OrderActionPayload;
  /**
   * ENG-3881 — put a party in a ledger class, or clear it with a null
   * `classId` (returns null). Staff only; requires clearpath.finance.ledger_manage.
   * Refuses `LedgerPartyNotFound`, `LedgerCustomerClassNotFound`.
   */
  setPartyLedgerClass?: Maybe<PartyLedgerClass>;
  /** Set a customer's own label sender; null revokes it so the customer inherits. */
  setPartySenderIdentity: PartySenderIdentity;
  /**
   * ENG-3286 — set a customer's own shipping terms; terms null revokes them so
   * the customer inherits. `expectedShippingAccountId`: change the customer only
   * while its own terms still name that account (Conflict otherwise).
   */
  setPartyShippingTerms: PartyShippingTerms;
  /**
   * ENG-894 - set the pipeline-level default assignment strategy. Version
   * scoped: writes the open DRAFT (or the single version of a DRAFT pipeline),
   * so it takes effect on publish. Rejected on a published pipeline with no open
   * draft - call openPipelineForEdit first.
   */
  setPipelineDefaultAssignmentStrategy: PipelinePayload;
  /**
   * 2026-07-16 — single validated lifecycle transition (drives the settings
   * status picker). Legal moves: DRAFT->LIVE/ARCHIVED; LIVE<->INACTIVE;
   * {LIVE,INACTIVE}->ARCHIVED; ARCHIVED->LIVE/INACTIVE (restore), and
   * ARCHIVED->DRAFT only if never published. Archiving is rejected while the
   * pipeline is a SKU's default. First transition to LIVE stamps publishedAt.
   */
  setPipelineStatus: PipelinePayload;
  /**
   * Round 4 — mark one pipeline as the default for a product_type. The
   * unique partial index on product_type_pipelines (WHERE is_default = true)
   * DB-enforces the at-most-one invariant; this mutation atomically clears any
   * prior default first, then sets the requested pair, inside a single
   * transaction.
   */
  setProductTypeDefaultPipeline: ProductTypePayload;
  /**
   * Rename a projection's display label. The in-product remediation for an
   * exposed-label collision the expose path (or the 0443 partial unique index)
   * refuses: relabel one of two clashing fields, then expose. When the row is
   * exposed (picker-visible) the new label must be non-blank AND unique
   * (case-insensitively) among the org's other exposed projections
   * (PROJECTION_LABEL_REQUIRED_TO_EXPOSE / DUPLICATE_PROJECTION_LABEL); relabeling a
   * non-exposed candidate is unconstrained. Audited with governance flags only —
   * never the label text.
   */
  setProjectionLabel: ProjectionGovernancePayload;
  /**
   * Opt a projection DOWN to non-QI (releasing its group-by from k-anon
   * suppression) or RESTORE it to QI. The @audited action here names the opt-down
   * case; the handler emits analytics.projection.qi_opt_down (false) /
   * analytics.projection.qi_restore (true), with the QI governance flags only.
   */
  setProjectionQuasiIdentifier: ProjectionGovernancePayload;
  /**
   * Save an explicit source import date (null means all history) and IANA calendar.
   * Requires an active tenant-wide built-in Lab Owner/Director role and integrations.write.
   * Moving earlier resets the exact source checkpoint for controlled backfill.
   */
  setScannerImportPolicy: ScannerIntakeSource;
  /** Pause or resume future intake without replacing scanner authorization. */
  setScannerIntake: Array<ScannerConnection>;
  /**
   * Archive or restore an unlinked staged case. Existing orders are never changed.
   * Reference is trimmed to at most 200 characters; omission preserves it, null or blank clears it.
   */
  setScannerIntakeArchived: ScannerIntakeInboxItem;
  /** Explicitly include an old or undated staged case for controlled import. */
  setScannerIntakeImportOverride: ScannerIntakeInboxItem;
  /**
   * Replace an outside account's label company name and printed sender address
   * (the label's ship-from stays the rating and pickup origin).
   */
  setShippingAccountLabelSettings: ShippingAccount;
  /** Replace one level's package defaults; target ids both null = the lab. */
  setShippingPackageDefaults: ShippingPackageDefaults;
  /**
   * ENG-1577 — replace which failure reasons this step OFFERS when work on it
   * fails. An empty list means "offer the library" (every reason attached to no
   * step), which is a real state and not the same as leaving the set alone.
   */
  setStepFailureReasonCodes: StepFailureReasonCodesPayload;
  /**
   * Pin a specific federation link on a step_run before the routing engine
   * runs. Lab user override path. Backed by services/api/src/vendor_routing/
   * manual_override.ts.
   */
  setVendorOverride: SetVendorOverridePayload;
  /**
   * Share one of the caller's own dashboards with a principal. NotFound if the
   * dashboard is not the caller's own in their org. Re-sharing updates the grant.
   */
  shareAnalyticsDashboard: AnalyticsDashboardSharePayload;
  /**
   * ENG-2554 - skip this step: it will not be worked, and the unit moves on to
   * its successors. Appends a terminal attempt (`status: COMPLETED`,
   * `outcome: SKIPPED`) carrying the required `skipReason`, and emits
   * `step.skipped` so the engine activates successors exactly as a completion
   * does - minus the material consumption, which a step nobody performed must not
   * book.
   *
   * Refuses an attempt that is already terminal, and shares the `uq_step_runs_
   * completed_once_*` indexes with `completeStepRun`, so a step cannot be both
   * skipped and completed.
   */
  skipStepRun: StepRunActionPayload;
  smartAssignConfirm: SmartAssignConfirmResult;
  smartAssignPreview: SmartAssignmentPreviewResult;
  /**
   * ENG-1550 — begin work on a step the caller is assigned. `ready` ->
   * `in_progress`, stamping started_at and opening the operator's time entry.
   *
   * Assignment used to do this, so assigning and starting were one act and there
   * was no way to express "assigned, not started". Now assignment answers WHO and
   * this answers WHEN.
   *
   * Gated on clearpath.steps.complete (whoever may finish a step may begin it) AND
   * on OWNERSHIP: you start your own step. Refused on a held step, on any status
   * but `ready`, and on an unassigned one. Already-started is a no-op rather than an
   * error, so a repeated barcode scan cannot open a second time entry.
   */
  startStepRun: StepRunActionPayload;
  /**
   * Act on an `AWAITING_CUSTOMER_APPROVAL` hold as the owning customer (ENG-19).
   *
   * The in-app sign-off that replaces phone/email approval of a design/shade/fit.
   * `APPROVE` reuses the canonical release path (releaseOrderHold's body) so the
   * hold is released and production resumes; `REQUEST_CHANGES` records the reason,
   * notifies the lab, and RETAINS the hold so the case stays blocked until rework.
   *
   * Authorization is owner-only, enforced by three AVP-independent barriers that
   * actually run (Cedar/AVP is off in the deployed store): (1) loadOwnedOrder read
   * scope — a case the caller cannot read is NotFound; (2) the in-process
   * `enforceResourceScope` party check (`clearpath.clarifications.approve` is in
   * cedar_check.ts SCOPED_MUTATION_ACTIONS) DENIES unless the caller's party scope
   * contains the order's `customer_party` — TRUE party ownership, which also blocks
   * a practitioner who merely SUBMITTED a foreign-practice order; (3) the hold must
   * carry the `AWAITING_CUSTOMER_APPROVAL` reason — an internal hold cannot be
   * released down this path.
   *
   * Conflicts (never a 500): the hold is not a customer-approval hold, or it has
   * already been actioned.
   */
  submitApprovalDecision: ApprovalDecisionPayload;
  /** Submit a new order from the portal/intake form. */
  submitOrder: SubmitOrderPayload;
  /**
   * Suspend an active federation link — pauses message exchange and work
   * dispatch. Reversible via acceptFederationLink (back to active).
   */
  suspendFederationLink: FederationLinkPayload;
  /**
   * Terminate a federation link permanently. Sets terminated_at and the
   * link cannot be reactivated; a new link must be proposed.
   */
  terminateFederationLink: FederationLinkPayload;
  /**
   * Dry-run an automation rule against a mock event. No side effects.
   * Returns the trigger / condition outcome + the action list that would fire.
   * Drives the admin rule-editor Dry-run button. Per Phase 2.6 / MOL-566.
   */
  testAutomationRule: AutomationRuleTestResult;
  /**
   * Invoke the adapter's `testConnection` action — used by the Portal's
   * "Test connection" button to confirm a config + secret pair is valid.
   */
  testExternalIntegrationConnection: TestExternalIntegrationConnectionPayload;
  /** ENG-3286 — re-run the live rate-quote check on an account. */
  testShippingAccount: ShippingAccount;
  /**
   * ENG-1073 — bring a retired form back (ARCHIVED -> DRAFT). ARCHIVED was
   * terminal for forms while pipelines (setPipelineStatus) and product types
   * (unarchiveProductType) both had a way back; that asymmetry is why forms still
   * carried a separate `is_active` flag to express reversible withdrawal.
   *
   * Lands in DRAFT rather than straight back to LIVE, matching
   * unarchiveProductType: a form pins versions, so re-serving immediately could
   * hand orders a version referencing fields retired while it was archived. The
   * admin reviews and re-publishes through the normal gate. published_at /
   * publishedByUserId are cleared; the version pointers are left intact so the
   * history and the prior live version survive.
   */
  unarchiveFormDefinition: FormDefinitionPayload;
  /**
   * Restore an archived org unit to live. Clears archivedAt and
   * archivedByUserId together. Refused if the unit is not archived.
   */
  unarchiveOrgUnit: ArchiveOrgUnitPayload;
  /**
   * Unarchive a retired product_type (ARCHIVED -> DRAFT). ARCHIVED is
   * otherwise terminal; this brings a mistakenly-archived (or a discontinued,
   * now-wanted-again) SKU back to DRAFT — the only editable state — so it can be
   * reviewed and re-published through the normal completeness gate rather than
   * re-created from scratch. Clears published_at. Rejects non-ARCHIVED.
   */
  unarchiveProductType: ProductTypePayload;
  unassignShift: ShiftPayload;
  /** Soft-delete an ad-hoc relationship row by id. Round 1a. */
  unlinkExternalParties: ExternalPartyRelationshipDeletedPayload;
  /**
   * Unlink a previously-linked social / SSO provider from the caller's global
   * identity. Idempotent — unlinking an already-unlinked provider is a no-op
   * returning the current (unlinked) state.
   */
  unlinkIdentityProvider: IdentityProviderLinkPayload;
  /**
   * Detach a pipeline from a product_type. One pipeline per SKU, so unlinking
   * leaves the SKU with no pipeline: the legacy product_types.pipeline_id mirror is
   * cleared and the SKU is auto-deactivated (orders for it will not route until a
   * pipeline is linked again).
   */
  unlinkPipelineFromProductType: ProductTypePayload;
  /**
   * ENG-8 — return a LIVE product bundle to DRAFT so it can be edited and
   * republished. While DRAFT it is off new orders; existing orders are unchanged.
   */
  unpublishProductBundle: ProductBundlePayload;
  /**
   * Revoke a principal's access to one of the caller's own dashboards. NotFound
   * if the dashboard is not the caller's own or the share does not exist.
   */
  unshareAnalyticsDashboard: AnalyticsDashboardUnsharedPayload;
  /**
   * Update the caller's own analytics dashboard. NotFound if the id is not the
   * caller's own dashboard in their org.
   */
  updateAnalyticsDashboard: AnalyticsDashboardPayload;
  /**
   * Update a widget on one of the caller's own dashboards. NotFound if the
   * widget is not on a dashboard the caller owns in their org.
   */
  updateAnalyticsWidget: AnalyticsWidgetPayload;
  /**
   * Update the caller's org's calculated measure. NotFound if the id is not a
   * live measure in the caller's org. The output key is immutable.
   */
  updateCalculatedMeasure: CalculatedMeasurePayload;
  /** Patch an escalation chain. Null fields are unchanged. */
  updateEscalationChain: EscalationChain;
  /**
   * Edit an existing party's contact/identity fields and custom fields
   * without resending the whole party. Never writes name, partyType or
   * parentPartyId. See UpdateExternalPartyInput for the field semantics.
   */
  updateExternalParty: ExternalPartyPayload;
  /** Update mutable fields on a flag. Pass archived=true to soft-archive. */
  updateFeatureFlag: FeatureFlagPayload;
  /**
   * Update the event_visibility_config JSONB on a federation_link. Phase 9
   * (MOL-609), extended by Phase 1.1 (MOL-603) to accept an input struct
   * carrying both eventVisibilityConfig and allowedWorkTypes so the admin UI
   * can save both in one round-trip. Either field is optional — null leaves
   * the column unchanged.
   */
  updateFederationVisibilityConfig: FederationLinkPayload;
  /**
   * Update a hold reason code. PATCH semantics — omitted fields are unchanged.
   * Renaming the label is always safe; changing the code changes the stable
   * machine key (still unique per organization).
   */
  updateHoldReasonCode: HoldReasonCodePayload;
  /**
   * Edit an item's catalog fields. Cannot change quantityOnHand — stock only
   * moves through receiveStock / adjustStock, so the balance can never diverge
   * from the ledger.
   */
  updateInventoryItem: UpdateInventoryItemPayload;
  /**
   * Update the caller's own profile. Always targets ctx.userId — no
   * admin permission check; no deactivation. For admin edits on others,
   * use `updateUser`. Phase P1.9.1 / 2026-04-24.
   */
  updateMe: UserPayload;
  /**
   * ENG-3853 — set the organization's NAS file-format policy (FULL replace).
   * Unknown format keys are refused. Org-wide: refused for an org-unit-scoped
   * caller. Gated on `clearpath.nas.configure` (feature-flag-gated on
   * `clearpath.nas_module`, step-up MFA declared). Stations keep their format
   * override; a station receives only the formats both allow.
   */
  updateNasFormatPolicy: NasFormatPolicy;
  /**
   * ENG-3975 — set how long NAS workstations keep an order's files after they
   * stop needing them: 10 to 10080 minutes, or null for the 24-hour default.
   * Applies to holdings released after the change; holdings already waiting to be
   * removed keep their time. Org-wide: refused for an org-unit-scoped caller.
   * Gated on `clearpath.nas.configure` (feature-flag-gated on
   * `clearpath.nas_module`, step-up MFA declared).
   */
  updateNasHoldingGrace: NasHoldingGrace;
  /** Update the current viewer's notification preferences (partial patch). */
  updateNotificationPreferences: NotificationPreferences;
  /**
   * Round 2b inline-edit / inline-picker mutation. Patches the non-status
   * fields on an order (priority, assignee, due date, name, notes, arbitrary
   * customFields). For status changes use `setOrderStatus`; for cancellation
   * use `cancelOrder`.
   */
  updateOrder: OrderActionPayload;
  /**
   * Lab staff correct the ORDER-level intake form answers (order-level twin
   * of updateOrderProductIntake — see UpdateOrderIntakeInput). Writes an
   * audit_log row so the Activity tab shows who changed what + when.
   *
   * INFRA NOTE: this is a new SDL root Mutation field — it needs a paired
   * API_LAMBDA_FIELDS binding in the infrastructure repo's
   * lib/stacks/api-stack.ts before AppSync will route it to this Lambda
   * (see services/api/src/handler-routes.ts's ROUTES entry).
   */
  updateOrderIntake: OrderActionPayload;
  /**
   * Lab staff edit a product line's intake form answers (RX). Writes
   * an audit_log row so the Activity tab shows who changed what + when.
   */
  updateOrderProductIntake: OrderProductPayload;
  /**
   * Update local metadata on a saved payment method (nickname). Does not
   * re-tokenize or call the provider.
   */
  updatePaymentMethod: PaymentMethodPayload;
  /**
   * Set the payment rails this organization accepts for NEW payment methods
   * and NEW charges.
   *
   * DOES NOT AFFECT SETTLEMENT, ACH RETURNS OR RECONCILIATION. Money already sent
   * to the bank lands and reverses regardless of what is switched off here — the
   * gate is deliberately only consulted where something STARTS. An operator who
   * believes this stops an in-flight debit is the most expensive misunderstanding
   * this mutation can create.
   *
   * Writes exactly one column on the existing active provider row and never
   * creates one; provider connection management (credentials, which processor,
   * activation) is not tenant self-service. Every successful save stores an
   * explicit array and returns state = EXPLICIT.
   *
   * extensions.code: PaymentRailsEmpty (empty enabledRails),
   * PaymentRailUnsupported (a rail outside CARD / ACH),
   * PaymentProviderNotConfigured (no active provider row),
   * Conflict (stale expectedVersion, or the UPDATE matched no row). ENG-1532.
   */
  updatePaymentRailSettings: PaymentRailSettingsPayload;
  /** Update a price book's name/description/currency/default flag. */
  updatePriceBook: PriceBookPayload;
  /** Patch an existing tenant role (PATCH semantics — null unchanged). */
  updateRole: TenantRolePayload;
  updateShift: ShiftPayload;
  /**
   * ENG-3286 — rename, archive or restore. The account number cannot change.
   * Archive is refused while a customer default or an active order uses it.
   */
  updateShippingAccount: ShippingAccount;
  /**
   * Replace a box size (input) and/or archive or restore it (status). An
   * archived box reads as no box wherever it was a default.
   */
  updateShippingBoxPreset: ShippingBoxPreset;
  /** ENG-3286 — the tenant default shipping terms and the markup on invoiced shipping. */
  updateShippingChargeSettings: ShippingChargeSettings;
  /** Replace the lab-wide label defaults. */
  updateShippingLabelDefaults: ShippingLabelDefaults;
  /**
   * Replace a sender's details (input) and/or archive or restore it (status).
   * Its account cannot change.
   */
  updateShippingSenderIdentity: ShippingSenderIdentity;
  /**
   * ENG-1043 — update a site's capability flags, ship-from address, code and
   * active state. Internal-only, same posture as createSite.
   */
  updateSite: SitePayload;
  updateSkillDefinition: SkillDefinitionPayload;
  /**
   * Record the QC / form checklist responses for a step run (upserted into the
   * mutable step_run_custom_fields side table; step_runs stays append-only).
   * Unblocks the QC-checklist-at-step UI.
   */
  updateStepRunCustomFields: StepRunActionPayload;
  updateTechCapacity: TechnicianCapacityRow;
  /**
   * Partial update of ticket fields (subject, body, category, priority,
   * status, dueAt, relatedOrderId, customFields). Status transitions also
   * stamp resolved_at / closed_at as appropriate.
   */
  updateTicket: TicketPayload;
  updateTimeOffRedistributionPolicy: TimeOffPayload;
  /** Update an existing user. */
  updateUser: UserPayload;
  updateUserSkill: UserSkillPayload;
  /**
   * Upload a file against a share-link-scoped step_run (Bible §06:116,
   * P0 #22). Caller carries a share-link JWT (`token_use=share_link`,
   * `aud=share-link-<linkId>`) — resolver validates that the link grants
   * `upload_files` for the requested step_run, allocates a file row +
   * S3 location, and returns a presigned PUT URL. Audited as
   * `share_link.file_uploaded`.
   */
  uploadSharedFile: UploadSharedFilePayload;
  /** Create or update an automation rule. Rejects writes to LIVE rows (P2.4). */
  upsertAutomationRule: AutomationRulePayload;
  /**
   * Round 6 — full replacement of the tenant catalog taxonomy. Pass the
   * complete node array; missing nodes are removed. Validated server-side
   * for cycle freedom + parent-id existence.
   */
  upsertCatalogTaxonomy: CatalogTaxonomy;
  /**
   * Create or update a tenant comment category. Powers the
   * /admin/comment-categories admin page + composer dropdown.
   */
  upsertCommentCategory: CommentCategoryPayload;
  /**
   * Record or correct ONE customer exemption certificate. ENG-2497 slice 5.
   *
   * **An ACTIVE certificate stops charging that customer tax on new invoices.** A
   * blanket one (`taxJurisdictionId: null`, `taxCategoryCode: null`) stops it in
   * EVERY jurisdiction and for EVERY category — the per-customer tax-off.
   * `PENDING` is the default and keeps charging.
   *
   * ⚠️ IT RELIEVES THE BILLABLE PARTY. A practice that ships under a group's
   * billing account is not relieved by its own certificate.
   *
   * Refuses, by name and never as a silent no-op:
   * `TaxExemptionCertificateRequired` (a blank certificate / authority
   * reference — the column is NOT NULL),
   * `TaxExemptionPartyNotFound` (a customer belonging to another organization, or
   * none; the foreign key does NOT constrain the party's organization, so this
   * check is the only thing that refuses it),
   * `TaxExemptionJurisdictionNotFound` (a jurisdiction that is neither shared
   * platform reference data nor this organization's),
   * `TaxExemptionCategoryNotFound`,
   * `TaxExemptionInvalidValidityRange` (`validTo` not after `validFrom`),
   * `TaxExemptionAlreadyCovered` (this customer already has an ACTIVE certificate
   * with the SAME scope over an overlapping period — two of them both relieve
   * tax, so revoking one would leave the invoice untaxed with nothing saying
   * why),
   * `TaxExemptionNotFound`, `TaxExemptionWriteNotApplied`,
   * `Conflict` (a concurrent save, or a missing `expectedVersion` on an edit).
   */
  upsertCustomerTaxExemption: CustomerTaxExemptionPayload;
  /** Create or update an external integration row for the caller's tenant. */
  upsertExternalIntegration: ExternalIntegration;
  /** Create or update an external party (customer, vendor, carrier, etc). */
  upsertExternalParty: ExternalPartyPayload;
  /**
   * Create or update an address row attached to an ExternalParty. When
   * `isPrimary = true` the resolver demotes any existing primary on the same
   * party in the same transaction so the partial-unique-index invariant
   * (one primary per party) holds. Round 1a (v2 feature restoration plan).
   */
  upsertExternalPartyAddress: ExternalPartyAddressPayload;
  /**
   * Create or update a failure reason code.
   * When `stepDefinitionId` is null the code is org-wide (applies to every
   * step); otherwise it is scoped to that single step. Per
   * docs/clearpath-architecture/04-pipeline-engine.md failure taxonomy.
   */
  upsertFailureReasonCode: FailureReasonCodePayload;
  /**
   * Create or update a tenant field_label override.
   * Per docs/clearpath-architecture/03-data-model.md tier 1. Admin-only —
   * field-name renames/migrations are NOT done here (those require a DB
   * migration); this endpoint only authors display-label overrides per
   * (field_name, locale). Cedar gates on clearpath.tenant_config.write.
   */
  upsertFieldLabel: FieldLabelPayload;
  /**
   * Create or update a form definition. Editing a LIVE form no longer rejects
   * (ENG-769) — the schema/uiSchema write is routed to the form's DRAFT version
   * (forking one if needed) so the LIVE version keeps serving; parent attributes
   * (name / product binding) update in place. The `isActive` flag was removed in
   * ENG-1073 — withdraw a form by archiving it.
   */
  upsertFormDefinition: FormDefinitionPayload;
  /**
   * ENG-658 — create or update a library field (and its full option
   * set, delete+reinsert). Rejects a code collision with another field in
   * the same org. Renamed from upsertAttributeDefinition; the permission key
   * and audit action are unchanged.
   */
  upsertFormField: FormField;
  /**
   * ENG-3881 — create or edit a ledger account. Staff only; requires
   * clearpath.finance.ledger_manage. Refuses `LedgerAccountNumberTaken`,
   * `LedgerAccountNotFound`, `LedgerAccountParentNotFound`,
   * `LedgerAccountParentCycle`, `LedgerAccountImmutable` (number or type change
   * after an allocation references it), `LedgerAccountTypeNotAllowed` (a type a
   * mapping or class on it does not allow). There is no delete: deactivate
   * instead; a referenced inactive account blocks journal export of its months.
   */
  upsertLedgerAccount: LedgerAccount;
  /**
   * ENG-3881 — create or edit a customer ledger class. Staff only; requires
   * clearpath.finance.ledger_manage. Refuses `LedgerCustomerClassCodeTaken`,
   * `LedgerCustomerClassNotFound`, `LedgerAccountNotFound`, `LedgerAccountInactive`,
   * `LedgerAccountTypeNotAllowed` (INCOME or OTHER_INCOME only). The account is
   * checked on every save, so even a name-only edit is refused while the class's
   * account is inactive: reactivate it or pick another first.
   */
  upsertLedgerCustomerClass: LedgerCustomerClass;
  /**
   * Create or update a branded notification template for the current
   * Organization. ADMIN-SCOPED. Per MOL-595.
   */
  upsertNotificationTemplate: NotificationTemplate;
  /**
   * Create or update an org_unit (department / team / plant / etc.).
   * When `id` is null, inserts a new row; otherwise updates name / parent /
   * position / customFields. Slug is derived from name when not provided.
   * Closure-table rows are maintained by triggers on org_units.
   */
  upsertOrgUnit: OrgUnitPayload;
  /** Create or update a party pricing agreement. */
  upsertPartyPricingAgreement: PartyPricingAgreementPayload;
  /** Create or update a pipeline. Rejects writes to LIVE pipelines (P2.4). */
  upsertPipeline: PipelinePayload;
  /**
   * Bind an item to a pipeline step, or edit an existing binding. Gated on
   * pipelines.write rather than inventory.write: this changes what a pipeline
   * consumes, which is pipeline configuration, not stock movement.
   */
  upsertPipelineConsumptionRule: PipelineConsumptionRulePayload;
  /**
   * Round 4 (v2 feature restoration) — upsert the canonical YAML +
   * reactflow graph metadata for a pipeline. Both the YAML editor and the node
   * graph editor save through this single mutation. Pipeline must be DRAFT.
   */
  upsertPipelineGraph: PipelinePayload;
  /** Create or update a pipeline step. */
  upsertPipelineStep: PipelineStepPayload;
  /**
   * Apply direct visibility overrides to the organization's portal order-detail
   * view. The resolver performs an organization-scope precheck, optimistic
   * concurrency, bounded registry validation, and an atomic audit write.
   */
  upsertPortalViewConfig: PortalViewConfigMutationPayload;
  /** Create or update a price book entry (a SKU's price on that catalog). */
  upsertPriceBookEntry: PriceBookEntryPayload;
  /** ENG-8 — create or update a product bundle. Rejects writes to LIVE rows. */
  upsertProductBundle: ProductBundlePayload;
  /** Create or update a product type (SKU). Rejects writes to LIVE rows (P2.4). */
  upsertProductType: ProductTypePayload;
  /** Create or update an SLA policy. Rejects writes to LIVE rows (P2.4). */
  upsertSlaPolicy: SlaPolicyPayload;
  /**
   * Create or update a step_dependencies row (DAG edge).
   * Phase-5 admin catch-up (MOL-669) — prior editor drew edges client-side only.
   * Writes are scoped by `pipelineId` + both step ids must belong to that
   * pipeline AND the viewer's org.
   */
  upsertStepDependency: StepDependencyPayload;
  /**
   * Create or update a step library entry (reusable step template per
   * tenant). Referenced by pipeline_steps.step_library_id; editing an entry
   * does NOT retroactively mutate pipelines already bound to it — they keep
   * their own snapshotted copy of the defaults.
   */
  upsertStepLibraryEntry: StepLibraryEntryPayload;
  /**
   * Create or correct ONE tax jurisdiction — the postal boundary that routes
   * addresses to it, a rate row for EVERY category the regional default covers,
   * and the registration that lets the engine collect there — in one transaction.
   *
   * **All of it, or none of it.** A partial write is the inversion trap: a
   * boundary pointing at a jurisdiction with no rate, or no registration, bills
   * every address it covers at ZERO while unlisted addresses in the same region
   * keep collecting the regional rate. Exactly backwards, and silent.
   *
   * Refuses, by name and never as a silent no-op:
   * `TaxPlatformRowImmutable` (a shared platform row),
   * `TaxAddressAlreadyClaimed` (another of this organization's jurisdictions
   * already covers this address over an overlapping period — both would resolve,
   * each claiming to be the whole rate, and one would silently never apply; the
   * error names the existing jurisdiction and the two actions that resolve it),
   * `TaxFallbackMissing` (no regional default to sanity-check the rate against),
   * `TaxRateBelowFallback` (a local rate below the regional one it overrides),
   * `TaxWriteBoundsExceeded` (outside the plausibility band, backdating past
   * invoices already billed, or past the per-organization 24-hour change limit),
   * `TaxRateAlreadyBilled` (an in-place correction to a rate an invoice used),
   * `TaxEffectiveDateBeforeJurisdiction` (a rate dated before the jurisdiction
   * itself takes effect, which would resolve to no tax at all in between),
   * `TaxFallbackAmbiguous` / `TaxFallbackReducedRate` (the regional default cannot
   * be mirrored onto one typed combined rate),
   * `TaxJurisdictionAddressImmutable`, `TaxJurisdictionNotFound`,
   * `TaxCategoryCoverageIncomplete`, `TaxNexusMissing`, `TaxRegistrationNotActive`,
   * `TaxWriteNotApplied`, `TaxWriteRejectedByDatabase`, `TaxWriteNotPermitted`,
   * `Conflict`.
   *
   * **Creating a jurisdiction begins collecting tax there on new invoices.** The
   * registration it writes asserts an authority that has not been verified, and is
   * counted as such in tax health until somebody records a permit number.
   */
  upsertTaxJurisdiction: TaxJurisdictionPayload;
  /**
   * Set this organization's own fallback tax rate for a WHOLE STATE — the rate
   * an address in that state is charged when no postal code you have listed covers
   * it.
   *
   * **It is the same write as `upsertTaxJurisdiction`, at a wider tier.** A state
   * default is a region-shaped boundary, which the resolver has always fallen back
   * to; it is not a second mechanism. It therefore carries the same atomic commit,
   * the same plausibility band and 24-hour change limit, the same backdating
   * rules, and the same category-coverage and nexus invariants — and it counts
   * against the same per-organization change budget.
   *
   * **A state default does NOT conflict with a postal code in the same state.**
   * Postal beats region at resolution, so the ZIP rows you have listed keep their
   * own rates and everything else in the state falls to this one. A SECOND state
   * default for the same state IS refused (`TaxAddressAlreadyClaimed`).
   *
   * Takes no `postalCode`; supplying one is refused. Same error vocabulary as
   * `upsertTaxJurisdiction` otherwise.
   */
  upsertTaxStateDefault: TaxJurisdictionPayload;
  /**
   * Upsert the tenant_settings row for an organization. The row is unique
   * per organization_id; repeat calls update in place.
   */
  upsertTenantSettings: TenantSettingsPayload;
  /** Create or update a vendor routing rule. */
  upsertVendorRoutingRule: VendorRoutingRulePayload;
  /**
   * Create or update a work location (station). ENG-589. `id` null inserts,
   * otherwise updates code / name / description / orgUnitId / isActive /
   * customFields. `code` is snake_case and unique per organization.
   */
  upsertWorkLocation: WorkLocationPayload;
  /**
   * Take a set of validation errors and ask the LLM for a plain-English
   * explanation + suggested fix.
   */
  validateAndExplain: ValidateAndExplainResult;
  /** Run 3-layer validation (syntax + JSON Schema + semantic) on raw YAML. */
  validateTenantConfig: ValidationResult;
  /** Void a previously-recorded adjustment. Requires step-up MFA. MOL-620. */
  voidBillingAdjustment: Scalars['Boolean']['output'];
  /** Void an invoice (detaches billing events). Reason required. */
  voidInvoice: Scalars['Boolean']['output'];
  /**
   * Request a refund on an unused label. Carrier-side void window varies
   * (FedEx: ~14d for Ground, UPS: ~90d).
   */
  voidShippingLabel: VoidShippingLabelResult;
};


export type MutationAcceptFederatedWorkArgs = {
  workItemId: Scalars['ID']['input'];
};


export type MutationAcceptFederationLinkArgs = {
  id: Scalars['ID']['input'];
};


export type MutationAcceptInvitationArgs = {
  token: Scalars['ID']['input'];
};


export type MutationAcknowledgeEscalationArgs = {
  escalationEventId: Scalars['ID']['input'];
};


export type MutationAcknowledgeFirstBillCheckArgs = {
  lineId: Scalars['ID']['input'];
};


export type MutationActivateTaxRegistrationArgs = {
  collectsFrom?: InputMaybe<Scalars['AWSDate']['input']>;
  expectedVersion: Scalars['String']['input'];
  id: Scalars['ID']['input'];
};


export type MutationAddFileLocationArgs = {
  input: AddFileLocationInput;
};


export type MutationAddPaymentMethodArgs = {
  input: AddPaymentMethodInput;
};


export type MutationAddPermissionToRoleArgs = {
  input: TenantRolePermissionInput;
};


export type MutationAddStandardShippingBoxPresetsArgs = {
  setLabDefault?: InputMaybe<Scalars['Boolean']['input']>;
};


export type MutationAdjustStockArgs = {
  delta: Scalars['String']['input'];
  itemId: Scalars['ID']['input'];
  notes?: InputMaybe<Scalars['String']['input']>;
};


export type MutationAdvanceNasRolloutArgs = {
  input: AdvanceNasRolloutInput;
};


export type MutationApplyPaymentAttemptResultsArgs = {
  input: ApplyPaymentAttemptResultsInput;
};


export type MutationApplyProductTypeCustomerAccessArgs = {
  input: ApplyProductTypeCustomerAccessInput;
};


export type MutationApplyTenantConfigArgs = {
  documentId: Scalars['ID']['input'];
};


export type MutationApproveProjectionArgs = {
  input: ApproveProjectionInput;
};


export type MutationArchiveAutomationRuleArgs = {
  input: PublishLifecycleInput;
};


export type MutationArchiveCalculatedMeasureArgs = {
  input: CalculatedMeasureIdInput;
};


export type MutationArchiveFeatureFlagArgs = {
  id: Scalars['ID']['input'];
};


export type MutationArchiveFeatureFlagWithAssignmentsArgs = {
  id: Scalars['ID']['input'];
};


export type MutationArchiveFormDefinitionArgs = {
  input: PublishLifecycleInput;
};


export type MutationArchiveFormFieldArgs = {
  input: ArchiveFormFieldInput;
};


export type MutationArchiveOrgUnitArgs = {
  id: Scalars['ID']['input'];
};


export type MutationArchivePipelineArgs = {
  input: ArchivePipelineInput;
};


export type MutationArchivePriceBookArgs = {
  input: PublishLifecycleInput;
};


export type MutationArchiveProductBundleArgs = {
  input: PublishLifecycleInput;
};


export type MutationArchiveProductTypeArgs = {
  input: PublishLifecycleInput;
};


export type MutationArchiveSlaPolicyArgs = {
  input: PublishLifecycleInput;
};


export type MutationAskAssistantArgs = {
  input: AskAssistantInput;
};


export type MutationAssignFeatureFlagArgs = {
  input: AssignFlagInput;
};


export type MutationAssignPartyOrderFormArgs = {
  input: AssignPartyOrderFormInput;
};


export type MutationAssignRoleArgs = {
  input: AssignRoleInput;
};


export type MutationAssignShiftArgs = {
  input: AssignShiftInput;
};


export type MutationAssignStepRunArgs = {
  input: AssignStepRunInput;
};


export type MutationAssignTicketArgs = {
  input: AssignTicketInput;
};


export type MutationAssignUserSkillArgs = {
  input: AssignUserSkillInput;
};


export type MutationAuthorizeMachineArgs = {
  input: AuthorizeMachineInput;
};


export type MutationBeginDsCoreConnectionArgs = {
  integrationId: Scalars['ID']['input'];
};


export type MutationBeginIteroConnectionArgs = {
  integrationId: Scalars['ID']['input'];
};


export type MutationBeginMeditConnectionArgs = {
  integrationId: Scalars['ID']['input'];
};


export type MutationBeginThreeShapeConnectionArgs = {
  integrationId: Scalars['ID']['input'];
};


export type MutationBulkApplyPartyPricingArgs = {
  input: BulkApplyPartyPricingInput;
};


export type MutationBulkCancelOrdersArgs = {
  orderIds: Array<Scalars['ID']['input']>;
  reason?: InputMaybe<Scalars['String']['input']>;
};


export type MutationBulkImportExternalPartiesArgs = {
  input: BulkImportExternalPartiesInput;
};


export type MutationBulkMarkInvoicesPaidArgs = {
  invoiceIds: Array<Scalars['ID']['input']>;
};


export type MutationBulkPublishProductTypesArgs = {
  input: BulkPublishProductTypesInput;
};


export type MutationBulkRemindInvoicesArgs = {
  invoiceIds: Array<Scalars['ID']['input']>;
};


export type MutationBulkSetLedgerMappingsArgs = {
  input: Array<LedgerMappingInput>;
};


export type MutationBulkSetOrderPriorityArgs = {
  orderIds: Array<Scalars['ID']['input']>;
  priority: OrderPriority;
};


export type MutationBulkSetOrderStatusArgs = {
  orderIds: Array<Scalars['ID']['input']>;
  status: Scalars['String']['input'];
};


export type MutationBulkVoidInvoicesArgs = {
  input: BulkVoidInvoicesInput;
};


export type MutationCancelIteroSetupArgs = {
  integrationId: Scalars['ID']['input'];
  sessionId: Scalars['ID']['input'];
};


export type MutationCancelOrderArgs = {
  input: CancelOrderInput;
};


export type MutationCancelPaymentAttemptArgs = {
  input: CancelPaymentAttemptInput;
};


export type MutationCancelTimeOffRequestArgs = {
  id: Scalars['ID']['input'];
};


export type MutationChangeUserRoleArgs = {
  input: ChangeUserRoleInput;
};


export type MutationChargeInvoiceArgs = {
  amount?: InputMaybe<Scalars['String']['input']>;
  invoiceId: Scalars['ID']['input'];
  paymentMethodId?: InputMaybe<Scalars['ID']['input']>;
  surchargeDisclosed?: InputMaybe<SurchargeDisclosureInput>;
};


export type MutationChatWithConfigAssistantArgs = {
  input: ChatWithConfigAssistantInput;
};


export type MutationClaimProjectionPairingArgs = {
  input: ClaimProjectionPairingInput;
};


export type MutationClearScannerIntakeIdentityMappingArgs = {
  input: ClearScannerIntakeIdentityMappingInput;
};


export type MutationCloneRoleArgs = {
  input: CloneRoleInput;
};


export type MutationCloseOrderArgs = {
  input: CloseOrderInput;
};


export type MutationCloseTicketArgs = {
  input: CloseTicketInput;
};


export type MutationCommitTaxImportArgs = {
  input: CommitTaxImportInput;
};


export type MutationCompleteFederatedWorkArgs = {
  notes?: InputMaybe<Scalars['String']['input']>;
  outputFileIds?: InputMaybe<Array<Scalars['ID']['input']>>;
  workItemId: Scalars['ID']['input'];
};


export type MutationCompleteStepRunArgs = {
  input: CompleteStepRunInput;
};


export type MutationConfigureDsCoreCredentialsArgs = {
  input: ConfigureMeditCredentialsInput;
};


export type MutationConfigureIteroCredentialsArgs = {
  input: ConfigureIteroCredentialsInput;
};


export type MutationConfigureMeditCredentialsArgs = {
  input: ConfigureMeditCredentialsInput;
};


export type MutationConfigureNasStationArgs = {
  input: ConfigureNasStationInput;
};


export type MutationConfigureThreeShapeCredentialsArgs = {
  input: ConfigureThreeShapeCredentialsInput;
};


export type MutationConnectFormToProductTypeArgs = {
  formDefinitionId: Scalars['ID']['input'];
  productTypeId: Scalars['ID']['input'];
};


export type MutationConnectIntegrationArgs = {
  input: ConnectIntegrationInput;
};


export type MutationCreateAnalyticsDashboardArgs = {
  input: CreateAnalyticsDashboardInput;
};


export type MutationCreateAnalyticsWidgetArgs = {
  input: CreateAnalyticsWidgetInput;
};


export type MutationCreateApiKeyArgs = {
  input: CreateApiKeyInput;
};


export type MutationCreateBillingAdjustmentArgs = {
  input: CreateBillingAdjustmentInput;
};


export type MutationCreateCalculatedMeasureArgs = {
  input: CreateCalculatedMeasureInput;
};


export type MutationCreateCertificationDefinitionArgs = {
  input: CreateCertificationDefinitionInput;
};


export type MutationCreateCommentArgs = {
  input: CreateCommentInput;
};


export type MutationCreateCustomerDelegationArgs = {
  input: CreateCustomerDelegationInput;
};


export type MutationCreateEscalationChainArgs = {
  input: CreateEscalationChainInput;
};


export type MutationCreateFeatureFlagArgs = {
  input: CreateFeatureFlagInput;
};


export type MutationCreateFederationLinkArgs = {
  input: CreateFederationLinkInput;
};


export type MutationCreateHoldReasonCodeArgs = {
  input: CreateHoldReasonCodeInput;
};


export type MutationCreateInventoryItemArgs = {
  input: CreateInventoryItemInput;
};


export type MutationCreateMachineArgs = {
  input: CreateMachineInput;
};


export type MutationCreateNasRolloutArgs = {
  input: CreateNasRolloutInput;
};


export type MutationCreateNotificationArgs = {
  input: CreateNotificationInput;
};


export type MutationCreateOroviaGuestPreviewLinkArgs = {
  input: CreateOroviaGuestPreviewLinkInput;
};


export type MutationCreatePriceBookArgs = {
  input: CreatePriceBookInput;
};


export type MutationCreateRoleArgs = {
  input: CreateRoleInput;
};


export type MutationCreateShareLinkArgs = {
  input: CreateShareLinkInput;
};


export type MutationCreateSharedAccessLinkArgs = {
  input: CreateSharedAccessLinkInput;
};


export type MutationCreateShiftArgs = {
  input: CreateShiftInput;
};


export type MutationCreateShippingAccountArgs = {
  input: CreateShippingAccountInput;
};


export type MutationCreateShippingBoxPresetArgs = {
  input: ShippingBoxPresetInput;
};


export type MutationCreateShippingLabelArgs = {
  input: CreateShippingLabelInput;
};


export type MutationCreateShippingSenderIdentityArgs = {
  input: ShippingSenderIdentityInput;
};


export type MutationCreateSiteArgs = {
  input: CreateSiteInput;
};


export type MutationCreateSkillDefinitionArgs = {
  input: CreateSkillDefinitionInput;
};


export type MutationCreateTicketArgs = {
  input: CreateTicketInput;
};


export type MutationCreateTimeOffRequestArgs = {
  input: CreateTimeOffInput;
};


export type MutationCreateUserArgs = {
  input: CreateUserInput;
};


export type MutationCreateUserCertificationArgs = {
  input: CreateUserCertificationInput;
};


export type MutationDeactivateHoldReasonCodeArgs = {
  id: Scalars['ID']['input'];
};


export type MutationDeactivateScannerStationArgs = {
  deviceId: Scalars['ID']['input'];
};


export type MutationDeclineFederatedWorkArgs = {
  reason: Scalars['String']['input'];
  workItemId: Scalars['ID']['input'];
};


export type MutationDeclineInvitationArgs = {
  reason?: InputMaybe<Scalars['String']['input']>;
  token: Scalars['ID']['input'];
};


export type MutationDeleteAnalyticsDashboardArgs = {
  id: Scalars['ID']['input'];
};


export type MutationDeleteAnalyticsTargetArgs = {
  measureKey: Scalars['String']['input'];
};


export type MutationDeleteAnalyticsWidgetArgs = {
  id: Scalars['ID']['input'];
};


export type MutationDeleteCalculatedMeasureArgs = {
  input: CalculatedMeasureIdInput;
};


export type MutationDeleteCertificationDefinitionArgs = {
  id: Scalars['ID']['input'];
};


export type MutationDeleteCommentArgs = {
  input: DeleteCommentInput;
};


export type MutationDeleteCommentCategoryArgs = {
  id: Scalars['ID']['input'];
};


export type MutationDeleteExternalPartyArgs = {
  id: Scalars['ID']['input'];
};


export type MutationDeleteExternalPartyAddressArgs = {
  id: Scalars['ID']['input'];
};


export type MutationDeleteFailureReasonCodeArgs = {
  id: Scalars['ID']['input'];
};


export type MutationDeleteFieldLabelArgs = {
  input: DeleteFieldLabelInput;
};


export type MutationDeleteFlagAssignmentArgs = {
  id: Scalars['ID']['input'];
};


export type MutationDeleteMachineArgs = {
  id: Scalars['ID']['input'];
};


export type MutationDeleteOrgUnitArgs = {
  id: Scalars['ID']['input'];
};


export type MutationDeletePartyPricingAgreementArgs = {
  id: Scalars['ID']['input'];
};


export type MutationDeletePipelineArgs = {
  input: ArchivePipelineInput;
};


export type MutationDeletePipelineConsumptionRuleArgs = {
  id: Scalars['ID']['input'];
};


export type MutationDeletePriceBookArgs = {
  input: PublishLifecycleInput;
};


export type MutationDeletePriceBookEntryArgs = {
  input: PublishLifecycleInput;
};


export type MutationDeleteProductTypeArgs = {
  input: PublishLifecycleInput;
};


export type MutationDeleteRoleArgs = {
  id: Scalars['ID']['input'];
};


export type MutationDeleteSkillDefinitionArgs = {
  id: Scalars['ID']['input'];
};


export type MutationDeleteStepDependencyArgs = {
  id: Scalars['ID']['input'];
};


export type MutationDeleteStepLibraryEntryArgs = {
  id: Scalars['ID']['input'];
};


export type MutationDeleteUserArgs = {
  id: Scalars['ID']['input'];
};


export type MutationDeleteUserCertificationArgs = {
  id: Scalars['ID']['input'];
};


export type MutationDeleteVendorRoutingRuleArgs = {
  id: Scalars['ID']['input'];
};


export type MutationDeleteWorkLocationArgs = {
  id: Scalars['ID']['input'];
};


export type MutationDisableExternalIntegrationArgs = {
  id: Scalars['ID']['input'];
};


export type MutationDiscardFormDefinitionDraftArgs = {
  input: DiscardFormDefinitionDraftInput;
};


export type MutationDiscardOrderDraftArgs = {
  id: Scalars['ID']['input'];
};


export type MutationDiscardPipelineDraftArgs = {
  input: DiscardPipelineDraftInput;
};


export type MutationDisconnectFormFromProductTypeArgs = {
  productTypeId: Scalars['ID']['input'];
};


export type MutationDisconnectIntegrationArgs = {
  id: Scalars['ID']['input'];
};


export type MutationDisconnectIteroAccountArgs = {
  integrationId: Scalars['ID']['input'];
};


export type MutationDisconnectScannerConnectionArgs = {
  integrationId: Scalars['ID']['input'];
};


export type MutationDiscoverFreeFormProjectionKeysArgs = {
  input: DiscoverFreeFormProjectionKeysInput;
};


export type MutationEditCommentArgs = {
  input: EditCommentInput;
};


export type MutationEnqueueAssistantJobArgs = {
  input: EnqueueAssistantInput;
};


export type MutationEnrollScannerStationArgs = {
  input: EnrollScannerStationInput;
};


export type MutationExpirePriceBookEntryArgs = {
  input: PublishLifecycleInput;
};


export type MutationExtendCustomerDelegationArgs = {
  id: Scalars['ID']['input'];
  newExpiresAt?: InputMaybe<Scalars['AWSDateTime']['input']>;
};


export type MutationFailStepRunArgs = {
  input: FailStepRunInput;
};


export type MutationFlagStepRunVendorDelayArgs = {
  input: FlagStepRunVendorDelayInput;
};


export type MutationForceReleaseOrderArgs = {
  input: ForceReleaseOrderInput;
};


export type MutationGenerateInterSiteShipmentLabelArgs = {
  shipmentId: Scalars['ID']['input'];
};


export type MutationGenerateInvoiceManualArgs = {
  input: GenerateInvoiceManualInput;
};


export type MutationGenerateNasEnrollmentCodeArgs = {
  input: GenerateNasEnrollmentCodeInput;
};


export type MutationGenerateStatementManualArgs = {
  input: GenerateStatementManualInput;
};


export type MutationGrantPartyCatalogAccessArgs = {
  input: GrantPartyCatalogAccessInput;
};


export type MutationInstantiateAnalyticsTemplateArgs = {
  input: InstantiateAnalyticsTemplateInput;
};


export type MutationInviteStaffUserArgs = {
  input: InviteStaffUserInput;
};


export type MutationInviteUserArgs = {
  input: InviteUserInput;
};


export type MutationIssueInvoiceArgs = {
  id: Scalars['ID']['input'];
};


export type MutationIssueInvoiceAdjustmentArgs = {
  input: IssueInvoiceAdjustmentInput;
};


export type MutationLabelPipelineVersionArgs = {
  label?: InputMaybe<Scalars['String']['input']>;
  versionId: Scalars['ID']['input'];
};


export type MutationLinkExternalPartiesArgs = {
  input: LinkExternalPartiesInput;
};


export type MutationLinkIdentityProviderArgs = {
  input: LinkIdentityProviderInput;
};


export type MutationLinkPipelineToProductTypeArgs = {
  input: LinkPipelineToProductTypeInput;
};


export type MutationLoopbackStepRunArgs = {
  input: LoopbackStepRunInput;
};


export type MutationManualAssignStepArgs = {
  input: ManualAssignStepInput;
};


export type MutationMarkInvoicePaidArgs = {
  id: Scalars['ID']['input'];
};


export type MutationMarkNotificationReadArgs = {
  id: Scalars['ID']['input'];
};


export type MutationMarkOrderDeliveredArgs = {
  input: MarkOrderDeliveredInput;
};


export type MutationMarkOrderSeatedArgs = {
  input: MarkOrderSeatedInput;
};


export type MutationMarkOrderShippedArgs = {
  input: MarkOrderShippedInput;
};


export type MutationMarkOrdersShippedArgs = {
  input: MarkOrdersShippedInput;
};


export type MutationMigrationBackfillPartyCrosswalkArgs = {
  input: MigrationBackfillPartyCrosswalkInput;
};


export type MutationMigrationLoadOpenOrdersArgs = {
  input: MigrationLoadOpenOrdersInput;
};


export type MutationMigrationLoadPartiesArgs = {
  input: MigrationLoadPartiesInput;
};


export type MutationMigrationLoadPipelinesArgs = {
  input: MigrationLoadPipelinesInput;
};


export type MutationMigrationLoadProductTypesArgs = {
  input: MigrationLoadProductTypesInput;
};


export type MutationMigrationLoadReferenceDataArgs = {
  input: MigrationLoadReferenceDataInput;
};


export type MutationMigrationPublishWave3Args = {
  input: MigrationPublishWave3Input;
};


export type MutationMigrationUndoPipelinesArgs = {
  input: MigrationUndoPipelinesInput;
};


export type MutationOpenFormDefinitionForEditArgs = {
  input: OpenFormDefinitionForEditInput;
};


export type MutationOpenPipelineForEditArgs = {
  input: OpenPipelineForEditInput;
};


export type MutationPauseNasRolloutArgs = {
  input: PauseNasRolloutInput;
};


export type MutationPauseProductTypeArgs = {
  input: PublishLifecycleInput;
};


export type MutationPingArgs = {
  message: Scalars['String']['input'];
};


export type MutationPlaceOrderHoldArgs = {
  input: PlaceOrderHoldInput;
};


export type MutationPrepareOroviaPreviewArgs = {
  fileId: Scalars['ID']['input'];
};


export type MutationPrepareOroviaReviewArgs = {
  input: PrepareOroviaReviewInput;
};


export type MutationPreviewTenantConfigArgs = {
  documentId: Scalars['ID']['input'];
};


export type MutationProposeFederationLinkArgs = {
  input: ProposeFederationLinkInput;
};


export type MutationProposeFreeFormProjectionArgs = {
  input: ProposeFreeFormProjectionInput;
};


export type MutationPublishAssistantResultArgs = {
  input: PublishAssistantResultInput;
};


export type MutationPublishAutomationRuleArgs = {
  input: PublishLifecycleInput;
};


export type MutationPublishFormDefinitionArgs = {
  input: PublishLifecycleInput;
};


export type MutationPublishPipelineArgs = {
  input: PublishPipelineInput;
};


export type MutationPublishProductBundleArgs = {
  input: PublishLifecycleInput;
};


export type MutationPublishProductTypeArgs = {
  input: PublishLifecycleInput;
};


export type MutationPublishSlaPolicyArgs = {
  input: PublishLifecycleInput;
};


export type MutationPublishWorkInvalidationArgs = {
  input: PublishWorkInvalidationInput;
};


export type MutationQuoteManualShippingChargeArgs = {
  input: QuoteManualShippingChargeInput;
};


export type MutationReassignStepRunArgs = {
  input: ReassignStepRunInput;
};


export type MutationReauthorizePaymentMethodArgs = {
  input: ReauthorizePaymentMethodInput;
};


export type MutationRecalculateOrderPricingArgs = {
  orderId: Scalars['ID']['input'];
};


export type MutationReceiveInterSiteShipmentArgs = {
  shipmentId: Scalars['ID']['input'];
};


export type MutationReceiveStockArgs = {
  itemId: Scalars['ID']['input'];
  notes?: InputMaybe<Scalars['String']['input']>;
  quantity: Scalars['String']['input'];
};


export type MutationRecordBarcodeScanArgs = {
  input: BarcodeScanInput;
};


export type MutationRecordBillingEventArgs = {
  input: RecordBillingEventInput;
};


export type MutationRecordTaxPermitArgs = {
  input: RecordTaxPermitInput;
};


export type MutationRefreshInvoicePdfArgs = {
  id: Scalars['ID']['input'];
};


export type MutationRefundPaymentTransactionArgs = {
  input: RefundPaymentTransactionInput;
};


export type MutationRegenerateProductSlipPdfsArgs = {
  orderId: Scalars['ID']['input'];
};


export type MutationRegenerateSlipPdfArgs = {
  orderId: Scalars['ID']['input'];
  printOptions?: InputMaybe<SlipPrintOptionsInput>;
};


export type MutationRegisterOAuthClientArgs = {
  input: RegisterOAuthClientInput;
};


export type MutationRegisterUploadedFileArgs = {
  input: RegisterUploadedFileInput;
};


export type MutationRejectFederationLinkArgs = {
  id: Scalars['ID']['input'];
  reason?: InputMaybe<Scalars['String']['input']>;
};


export type MutationReleaseAssignmentArgs = {
  input: ReleaseAssignmentInput;
};


export type MutationReleaseOrderHoldArgs = {
  input: ReleaseOrderHoldInput;
};


export type MutationRemoveFileLocationArgs = {
  input: RemoveFileLocationInput;
};


export type MutationRemovePaymentMethodArgs = {
  id: Scalars['ID']['input'];
};


export type MutationRemovePermissionFromRoleArgs = {
  input: TenantRolePermissionInput;
};


export type MutationRemoveShiftArgs = {
  id: Scalars['ID']['input'];
};


export type MutationRemoveUserSkillArgs = {
  id: Scalars['ID']['input'];
};


export type MutationReorderFromOrderArgs = {
  orderId: Scalars['ID']['input'];
};


export type MutationRequestAccountDeletionArgs = {
  input: RequestAccountDeletionInput;
};


export type MutationRequestBulkFileExportArgs = {
  input: RequestBulkFileExportInput;
};


export type MutationRequestCaseAdjustmentArgs = {
  input: RequestCaseAdjustmentInput;
};


export type MutationRequestFileDownloadArgs = {
  input: RequestFileDownloadInput;
};


export type MutationRequestFileUploadArgs = {
  input: RequestFileUploadInput;
};


export type MutationRequestShippingLabelDocumentArgs = {
  purpose: ShippingLabelDocumentPurpose;
  shipmentId: Scalars['ID']['input'];
};


export type MutationRequestShippingRatesArgs = {
  input: RequestShippingRatesInput;
};


export type MutationRequestWarrantyReturnArgs = {
  input: RequestWarrantyReturnInput;
};


export type MutationResendInvitationArgs = {
  id: Scalars['ID']['input'];
};


export type MutationResolveCustomerApprovalArgs = {
  input: ResolveCustomerApprovalInput;
};


export type MutationResolveScannerIntakeIdentityArgs = {
  input: ResolveScannerIntakeIdentityInput;
};


export type MutationResolveStepRunVendorDelayArgs = {
  input: ResolveStepRunVendorDelayInput;
};


export type MutationResolveTicketArgs = {
  input: ResolveTicketInput;
};


export type MutationRestampInvoiceLedgerAllocationsArgs = {
  input: RestampInvoiceLedgerAllocationsInput;
};


export type MutationRestoreCalculatedMeasureArgs = {
  input: CalculatedMeasureIdInput;
};


export type MutationRestoreFormVersionArgs = {
  input: RestoreFormVersionInput;
};


export type MutationRestorePipelineVersionArgs = {
  versionId: Scalars['ID']['input'];
};


export type MutationRestoreProductTypeArgs = {
  input: PublishLifecycleInput;
};


export type MutationResumeNasRolloutArgs = {
  input: ResumeNasRolloutInput;
};


export type MutationRetireTaxJurisdictionArgs = {
  input: RetireTaxJurisdictionInput;
};


export type MutationRetryFileScanArgs = {
  input: RetryFileScanInput;
};


export type MutationReviewTimeOffRequestArgs = {
  input: ReviewTimeOffInput;
};


export type MutationRevokeAllMySessionsArgs = {
  exceptCurrent?: InputMaybe<Scalars['Boolean']['input']>;
};


export type MutationRevokeApiKeyArgs = {
  id: Scalars['ID']['input'];
};


export type MutationRevokeCustomerDelegationArgs = {
  id: Scalars['ID']['input'];
  reason?: InputMaybe<Scalars['String']['input']>;
};


export type MutationRevokeCustomerTaxExemptionArgs = {
  expectedVersion: Scalars['String']['input'];
  id: Scalars['ID']['input'];
};


export type MutationRevokeFederationLinkArgs = {
  id: Scalars['ID']['input'];
  reason?: InputMaybe<Scalars['String']['input']>;
};


export type MutationRevokeInvitationArgs = {
  id: Scalars['ID']['input'];
  reason?: InputMaybe<Scalars['String']['input']>;
};


export type MutationRevokeMachineArgs = {
  input: RevokeMachineInput;
};


export type MutationRevokeMySessionArgs = {
  sessionId: Scalars['ID']['input'];
};


export type MutationRevokeNasDeviceArgs = {
  input: RevokeNasDeviceInput;
};


export type MutationRevokeOAuthClientArgs = {
  id: Scalars['ID']['input'];
};


export type MutationRevokeOroviaGuestPreviewLinkArgs = {
  id: Scalars['ID']['input'];
};


export type MutationRevokePartyCatalogAccessArgs = {
  input: RevokePartyCatalogAccessInput;
};


export type MutationRevokePartyOrderFormAssignmentArgs = {
  input: RevokePartyOrderFormAssignmentInput;
};


export type MutationRevokeRoleArgs = {
  input: RevokeRoleInput;
};


export type MutationRevokeShareLinkArgs = {
  id: Scalars['ID']['input'];
};


export type MutationRevokeSharedAccessLinkArgs = {
  input: RevokeSharedAccessLinkInput;
};


export type MutationRevokeStaffInvitationArgs = {
  input: RevokeStaffInvitationInput;
};


export type MutationRevokeTrustedDeviceArgs = {
  id: Scalars['ID']['input'];
};


export type MutationRollbackNasRolloutArgs = {
  input: RollbackNasRolloutInput;
};


export type MutationRollbackTenantConfigArgs = {
  toDocumentId: Scalars['ID']['input'];
};


export type MutationRotateApiKeyArgs = {
  id: Scalars['ID']['input'];
};


export type MutationRotateOAuthClientArgs = {
  input: RotateOAuthClientInput;
};


export type MutationRotateScannerStationKeyArgs = {
  deviceId: Scalars['ID']['input'];
};


export type MutationSaveDraftTenantConfigArgs = {
  yaml: Scalars['String']['input'];
};


export type MutationSaveOrderDraftArgs = {
  input: SaveOrderDraftInput;
};


export type MutationSelectIteroAccountArgs = {
  input: SelectIteroAccountInput;
};


export type MutationSendFederationMessageArgs = {
  input: SendFederationMessageInput;
};


export type MutationSendInvoiceArgs = {
  id: Scalars['ID']['input'];
};


export type MutationSendStepRunBackArgs = {
  input: SendStepRunBackInput;
};


export type MutationSetAnalyticsDashboardTemplateArgs = {
  input: SetAnalyticsDashboardTemplateInput;
};


export type MutationSetAnalyticsOverviewPreferencesArgs = {
  input: SetAnalyticsOverviewPreferencesInput;
};


export type MutationSetAnalyticsTargetArgs = {
  input: SetAnalyticsTargetInput;
};


export type MutationSetDefaultPaymentMethodArgs = {
  id: Scalars['ID']['input'];
};


export type MutationSetLedgerMappingArgs = {
  accountId?: InputMaybe<Scalars['ID']['input']>;
  kind: LedgerMappingKind;
  subjectKey: Scalars['String']['input'];
};


export type MutationSetOrderBillingPartyArgs = {
  input: SetOrderBillingPartyInput;
};


export type MutationSetOrderShippingTermsArgs = {
  expectedShippingAccountId?: InputMaybe<Scalars['ID']['input']>;
  expectedTerms?: InputMaybe<ShippingTerms>;
  orderId: Scalars['ID']['input'];
  shippingAccountId?: InputMaybe<Scalars['ID']['input']>;
  terms: ShippingTerms;
};


export type MutationSetOrderStatusArgs = {
  input: SetOrderStatusInput;
};


export type MutationSetOrderTrackingArgs = {
  input: SetOrderTrackingInput;
};


export type MutationSetPartyLedgerClassArgs = {
  classId?: InputMaybe<Scalars['ID']['input']>;
  partyId: Scalars['ID']['input'];
};


export type MutationSetPartySenderIdentityArgs = {
  partyId: Scalars['ID']['input'];
  senderIdentityId?: InputMaybe<Scalars['ID']['input']>;
};


export type MutationSetPartyShippingTermsArgs = {
  expectedShippingAccountId?: InputMaybe<Scalars['ID']['input']>;
  partyId: Scalars['ID']['input'];
  shippingAccountId?: InputMaybe<Scalars['ID']['input']>;
  terms?: InputMaybe<ShippingTerms>;
};


export type MutationSetPipelineDefaultAssignmentStrategyArgs = {
  input: SetPipelineDefaultAssignmentStrategyInput;
};


export type MutationSetPipelineStatusArgs = {
  input: SetPipelineStatusInput;
};


export type MutationSetProductTypeDefaultPipelineArgs = {
  pipelineId: Scalars['ID']['input'];
  productTypeId: Scalars['ID']['input'];
};


export type MutationSetProjectionLabelArgs = {
  input: SetProjectionLabelInput;
};


export type MutationSetProjectionQuasiIdentifierArgs = {
  input: SetProjectionQuasiIdentifierInput;
};


export type MutationSetScannerImportPolicyArgs = {
  fromDate?: InputMaybe<Scalars['AWSDate']['input']>;
  integrationId: Scalars['ID']['input'];
  timeZone: Scalars['String']['input'];
};


export type MutationSetScannerIntakeArgs = {
  integrationId: Scalars['ID']['input'];
  paused: Scalars['Boolean']['input'];
};


export type MutationSetScannerIntakeArchivedArgs = {
  archived: Scalars['Boolean']['input'];
  ingestId: Scalars['ID']['input'];
  reference?: InputMaybe<Scalars['String']['input']>;
};


export type MutationSetScannerIntakeImportOverrideArgs = {
  allow: Scalars['Boolean']['input'];
  ingestId: Scalars['ID']['input'];
};


export type MutationSetShippingAccountLabelSettingsArgs = {
  id: Scalars['ID']['input'];
  input: ShippingAccountLabelSettingsInput;
};


export type MutationSetShippingPackageDefaultsArgs = {
  input: ShippingPackageDefaultsInput;
  target?: InputMaybe<ShippingPackageDefaultsTarget>;
};


export type MutationSetStepFailureReasonCodesArgs = {
  failureReasonCodeIds: Array<Scalars['ID']['input']>;
  pipelineStepId: Scalars['ID']['input'];
};


export type MutationSetVendorOverrideArgs = {
  reason?: InputMaybe<Scalars['String']['input']>;
  stepRunId: Scalars['ID']['input'];
  vendorFederationLinkId: Scalars['ID']['input'];
};


export type MutationShareAnalyticsDashboardArgs = {
  input: ShareAnalyticsDashboardInput;
};


export type MutationSkipStepRunArgs = {
  input: SkipStepRunInput;
};


export type MutationSmartAssignConfirmArgs = {
  input: SmartAssignConfirmInput;
};


export type MutationSmartAssignPreviewArgs = {
  input: SmartAssignPreviewInput;
};


export type MutationStartStepRunArgs = {
  input: StartStepRunInput;
};


export type MutationSubmitApprovalDecisionArgs = {
  input: SubmitApprovalDecisionInput;
};


export type MutationSubmitOrderArgs = {
  input: SubmitOrderInput;
};


export type MutationSuspendFederationLinkArgs = {
  id: Scalars['ID']['input'];
  reason?: InputMaybe<Scalars['String']['input']>;
};


export type MutationTerminateFederationLinkArgs = {
  id: Scalars['ID']['input'];
  reason?: InputMaybe<Scalars['String']['input']>;
};


export type MutationTestAutomationRuleArgs = {
  input: TestAutomationRuleInput;
};


export type MutationTestExternalIntegrationConnectionArgs = {
  input: TestExternalIntegrationConnectionInput;
};


export type MutationTestShippingAccountArgs = {
  id: Scalars['ID']['input'];
};


export type MutationUnarchiveFormDefinitionArgs = {
  input: PublishLifecycleInput;
};


export type MutationUnarchiveOrgUnitArgs = {
  id: Scalars['ID']['input'];
};


export type MutationUnarchiveProductTypeArgs = {
  input: PublishLifecycleInput;
};


export type MutationUnassignShiftArgs = {
  input: UnassignShiftInput;
};


export type MutationUnlinkExternalPartiesArgs = {
  relationshipId: Scalars['ID']['input'];
};


export type MutationUnlinkIdentityProviderArgs = {
  provider: IdentityProvider;
};


export type MutationUnlinkPipelineFromProductTypeArgs = {
  pipelineId: Scalars['ID']['input'];
  productTypeId: Scalars['ID']['input'];
};


export type MutationUnpublishProductBundleArgs = {
  input: PublishLifecycleInput;
};


export type MutationUnshareAnalyticsDashboardArgs = {
  input: UnshareAnalyticsDashboardInput;
};


export type MutationUpdateAnalyticsDashboardArgs = {
  input: UpdateAnalyticsDashboardInput;
};


export type MutationUpdateAnalyticsWidgetArgs = {
  input: UpdateAnalyticsWidgetInput;
};


export type MutationUpdateCalculatedMeasureArgs = {
  input: UpdateCalculatedMeasureInput;
};


export type MutationUpdateEscalationChainArgs = {
  id: Scalars['ID']['input'];
  input: UpdateEscalationChainInput;
};


export type MutationUpdateExternalPartyArgs = {
  input: UpdateExternalPartyInput;
};


export type MutationUpdateFeatureFlagArgs = {
  input: UpdateFeatureFlagInput;
};


export type MutationUpdateFederationVisibilityConfigArgs = {
  input: UpdateFederationVisibilityConfigInput;
};


export type MutationUpdateHoldReasonCodeArgs = {
  input: UpdateHoldReasonCodeInput;
};


export type MutationUpdateInventoryItemArgs = {
  id: Scalars['ID']['input'];
  input: UpdateInventoryItemInput;
};


export type MutationUpdateMeArgs = {
  input: UpdateMeInput;
};


export type MutationUpdateNasFormatPolicyArgs = {
  input: UpdateNasFormatPolicyInput;
};


export type MutationUpdateNasHoldingGraceArgs = {
  input: UpdateNasHoldingGraceInput;
};


export type MutationUpdateNotificationPreferencesArgs = {
  input: UpdateNotificationPreferencesInput;
};


export type MutationUpdateOrderArgs = {
  input: UpdateOrderInput;
};


export type MutationUpdateOrderIntakeArgs = {
  input: UpdateOrderIntakeInput;
};


export type MutationUpdateOrderProductIntakeArgs = {
  input: UpdateOrderProductIntakeInput;
};


export type MutationUpdatePaymentMethodArgs = {
  input: UpdatePaymentMethodInput;
};


export type MutationUpdatePaymentRailSettingsArgs = {
  input: UpdatePaymentRailSettingsInput;
};


export type MutationUpdatePriceBookArgs = {
  input: UpdatePriceBookInput;
};


export type MutationUpdateRoleArgs = {
  input: UpdateRoleInput;
};


export type MutationUpdateShiftArgs = {
  input: UpdateShiftInput;
};


export type MutationUpdateShippingAccountArgs = {
  id: Scalars['ID']['input'];
  nickname?: InputMaybe<Scalars['String']['input']>;
  status?: InputMaybe<ShippingAccountStatus>;
};


export type MutationUpdateShippingBoxPresetArgs = {
  id: Scalars['ID']['input'];
  input?: InputMaybe<ShippingBoxPresetInput>;
  status?: InputMaybe<ShippingAccountStatus>;
};


export type MutationUpdateShippingChargeSettingsArgs = {
  input: ShippingChargeSettingsInput;
};


export type MutationUpdateShippingLabelDefaultsArgs = {
  input: ShippingLabelDefaultsInput;
};


export type MutationUpdateShippingSenderIdentityArgs = {
  id: Scalars['ID']['input'];
  input?: InputMaybe<ShippingSenderIdentityInput>;
  status?: InputMaybe<ShippingAccountStatus>;
};


export type MutationUpdateSiteArgs = {
  input: UpdateSiteInput;
};


export type MutationUpdateSkillDefinitionArgs = {
  input: UpdateSkillDefinitionInput;
};


export type MutationUpdateStepRunCustomFieldsArgs = {
  input: UpdateStepRunCustomFieldsInput;
};


export type MutationUpdateTechCapacityArgs = {
  input: UpdateTechCapacityInput;
};


export type MutationUpdateTicketArgs = {
  input: UpdateTicketInput;
};


export type MutationUpdateTimeOffRedistributionPolicyArgs = {
  input: UpdateRedistributionPolicyInput;
};


export type MutationUpdateUserArgs = {
  input: UpdateUserInput;
};


export type MutationUpdateUserSkillArgs = {
  input: UpdateUserSkillInput;
};


export type MutationUploadSharedFileArgs = {
  input: UploadSharedFileInput;
};


export type MutationUpsertAutomationRuleArgs = {
  input: UpsertAutomationRuleInput;
};


export type MutationUpsertCatalogTaxonomyArgs = {
  nodes: Array<CatalogTaxonomyNodeInput>;
};


export type MutationUpsertCommentCategoryArgs = {
  input: UpsertCommentCategoryInput;
};


export type MutationUpsertCustomerTaxExemptionArgs = {
  input: UpsertCustomerTaxExemptionInput;
};


export type MutationUpsertExternalIntegrationArgs = {
  input: UpsertExternalIntegrationInput;
};


export type MutationUpsertExternalPartyArgs = {
  input: UpsertExternalPartyInput;
};


export type MutationUpsertExternalPartyAddressArgs = {
  input: UpsertExternalPartyAddressInput;
};


export type MutationUpsertFailureReasonCodeArgs = {
  input: UpsertFailureReasonCodeInput;
};


export type MutationUpsertFieldLabelArgs = {
  input: UpsertFieldLabelInput;
};


export type MutationUpsertFormDefinitionArgs = {
  input: UpsertFormDefinitionInput;
};


export type MutationUpsertFormFieldArgs = {
  input: UpsertFormFieldInput;
};


export type MutationUpsertLedgerAccountArgs = {
  input: UpsertLedgerAccountInput;
};


export type MutationUpsertLedgerCustomerClassArgs = {
  input: UpsertLedgerCustomerClassInput;
};


export type MutationUpsertNotificationTemplateArgs = {
  input: UpsertNotificationTemplateInput;
};


export type MutationUpsertOrgUnitArgs = {
  input: UpsertOrgUnitInput;
};


export type MutationUpsertPartyPricingAgreementArgs = {
  input: UpsertPartyPricingAgreementInput;
};


export type MutationUpsertPipelineArgs = {
  input: UpsertPipelineInput;
};


export type MutationUpsertPipelineConsumptionRuleArgs = {
  input: UpsertPipelineConsumptionRuleInput;
};


export type MutationUpsertPipelineGraphArgs = {
  input: UpsertPipelineGraphInput;
};


export type MutationUpsertPipelineStepArgs = {
  input: UpsertPipelineStepInput;
};


export type MutationUpsertPortalViewConfigArgs = {
  input: UpsertPortalViewConfigInput;
};


export type MutationUpsertPriceBookEntryArgs = {
  input: UpsertPriceBookEntryInput;
};


export type MutationUpsertProductBundleArgs = {
  input: UpsertProductBundleInput;
};


export type MutationUpsertProductTypeArgs = {
  input: UpsertProductTypeInput;
};


export type MutationUpsertSlaPolicyArgs = {
  input: UpsertSlaPolicyInput;
};


export type MutationUpsertStepDependencyArgs = {
  input: UpsertStepDependencyInput;
};


export type MutationUpsertStepLibraryEntryArgs = {
  input: UpsertStepLibraryEntryInput;
};


export type MutationUpsertTaxJurisdictionArgs = {
  input: UpsertTaxJurisdictionInput;
};


export type MutationUpsertTaxStateDefaultArgs = {
  input: UpsertTaxJurisdictionInput;
};


export type MutationUpsertTenantSettingsArgs = {
  input: UpsertTenantSettingsInput;
};


export type MutationUpsertVendorRoutingRuleArgs = {
  input: UpsertVendorRoutingRuleInput;
};


export type MutationUpsertWorkLocationArgs = {
  input: UpsertWorkLocationInput;
};


export type MutationValidateAndExplainArgs = {
  input: ValidateAndExplainInput;
};


export type MutationValidateTenantConfigArgs = {
  yaml: Scalars['String']['input'];
};


export type MutationVoidBillingAdjustmentArgs = {
  input: VoidBillingAdjustmentInput;
};


export type MutationVoidInvoiceArgs = {
  input: VoidInvoiceInput;
};


export type MutationVoidShippingLabelArgs = {
  carrier: ShippingCarrier;
  trackingNumber: Scalars['String']['input'];
};

/** A shift as seen by the assigned technician (read-only, no staff list). */
export type MyShift = {
  __typename?: 'MyShift';
  days: Array<Scalars['String']['output']>;
  endMinutes: Scalars['Int']['output'];
  endTime: Scalars['String']['output'];
  endsNextDay: Scalars['Boolean']['output'];
  id: Scalars['ID']['output'];
  name: Scalars['String']['output'];
  startMinutes: Scalars['Int']['output'];
  startTime: Scalars['String']['output'];
};

/**
 * One on-prem NAS sync agent (one row per org+host), with its last-known
 * heartbeat + telemetry. Read-only admin surface (Query.nasAgents). Org-scoped;
 * customers/external partners read zero rows under RLS.
 */
export type NasAgent = {
  __typename?: 'NasAgent';
  /** Agent build the last heartbeat reported; null until the first report. */
  agentVersion?: Maybe<Scalars['String']['output']>;
  /** Local removals the agent is holding back. Null until emitted (JSONB). */
  blockedRemovals?: Maybe<Scalars['Int']['output']>;
  /** Free capacity on the share, bytes — Float (see NAS SDL). Null until emitted. */
  capacityBytesFree?: Maybe<Scalars['Float']['output']>;
  createdAt: Scalars['AWSDateTime']['output'];
  /**
   * ENG-3822 — credential state of this host, from its linked
   * nas_agent_devices rows (org-scoped). Distinct from liveness: whether the host
   * is online is derived client-side from lastHeartbeatAt. Null = unavailable
   * (treat as unknown; fall back to heartbeat liveness).
   */
  credentialState?: Maybe<NasAgentCredentialState>;
  /**
   * ENG-3832 — every device credential linked to this host (live and revoked),
   * org-scoped, newest enrolled first, capped at 20. Empty when none. Null =
   * unavailable (treat as unknown).
   */
  devices?: Maybe<Array<NasAgentDevice>>;
  /** Rolling sync-error count the agent self-reports. */
  errorCount: Scalars['Int']['output'];
  /** Files the agent is failing to sync. Null until the agent emits it (JSONB). */
  failingFiles?: Maybe<Scalars['Int']['output']>;
  /** Backlog depth — files the agent still has queued to sync. */
  filesPending: Scalars['Int']['output'];
  /** Orders the agent holds locally. Null until the agent emits it (JSONB). */
  heldOrders?: Maybe<Scalars['Int']['output']>;
  /** Host the agent runs on; unique within the org. */
  hostname: Scalars['String']['output'];
  id: Scalars['ID']['output'];
  /**
   * ENG-3832 — last REAL check-in (heartbeat). Null = this host has never
   * checked in: lastHeartbeatAt is then only its enroll-time default. Clients that
   * do not select this field keep the old behaviour.
   */
  lastCheckInAt?: Maybe<Scalars['AWSDateTime']['output']>;
  /** Last agent check-in. Every heartbeat advances it; drives staleness. */
  lastHeartbeatAt: Scalars['AWSDateTime']['output'];
  /**
   * Last SUCCESSFUL sync (distinct from lastHeartbeatAt liveness). Null until
   * the agent emits it (JSONB).
   */
  lastSyncAt?: Maybe<Scalars['AWSDateTime']['output']>;
  /**
   * ENG-3092 — ids (nas_agent_devices.id) of this agent's enrolled, NOT-revoked
   * device credentials linked via nas_agent_devices.nas_agent_id, org-scoped,
   * oldest first. Empty when none. Null = unavailable (clients must treat as
   * unknown). Read-only; revoke via Mutation.revokeNasDevice.
   */
  liveDeviceIds?: Maybe<Array<Scalars['ID']['output']>>;
  /** OS/arch string, e.g. 'linux/amd64'. Null until the agent emits it (JSONB). */
  platform?: Maybe<Scalars['String']['output']>;
  /** ENG-2583 — auto-update slice + live update state (0423 typed columns). */
  rolloutState: NasAgentRolloutState;
  /**
   * ENG-3842 — this station's configuration (NAS workstation sync). Nothing
   * reads it yet; an unconfigured station has a null orgUnit.
   */
  station: NasStationConfig;
  /** False while the share root or Orders folder is missing. Null until emitted (JSONB). */
  storageAvailable?: Maybe<Scalars['Boolean']['output']>;
  syncStatus: NasAgentSyncStatus;
  /** Files on the share not filed under an order. Null until emitted (JSONB). */
  unfiledFiles?: Maybe<Scalars['Int']['output']>;
  updatedAt: Scalars['AWSDateTime']['output'];
  /** Process/host uptime in seconds. Null until the agent emits it (JSONB). */
  uptimeSeconds?: Maybe<Scalars['Int']['output']>;
};

/** ENG-3097 — server-provided NAS agent connection settings for the admin setup wizard (ENG-3100). */
export type NasAgentConnectionSettings = {
  __typename?: 'NasAgentConnectionSettings';
  /** NAS data API GraphQL URL for config api_base_url; null when this environment has no NAS API. */
  apiBaseUrl?: Maybe<Scalars['String']['output']>;
  /**
   * ENG-3601 — the release channel for the agent config.yaml `release.channel`, and
   * the channel every `downloads[].release` in this payload was selected from:
   * `prerelease` on staging, `stable` everywhere else. Always present; exactly
   * `stable` or `prerelease`.
   */
  channel: Scalars['String']['output'];
  /** NAS release CDN host for config release_host; null when not configured. */
  releaseHost?: Maybe<Scalars['String']['output']>;
};

/**
 * ENG-3822 — credential state of a NAS agent host (see NasAgent.credentialState).
 * ACTIVE: a live (not revoked) device credential exists AND a heartbeat arrived
 * after the newest live credential was enrolled.
 * PENDING_FIRST_CHECK_IN: a live device credential exists but no heartbeat has
 * arrived since the newest live credential was enrolled (lastHeartbeatAt is then
 * the enroll time, not a real check-in).
 * REVOKED: no live device credential; at least one revoked one.
 * UNLINKED: no device credential is linked to this host (the row was created by
 * heartbeats; lastHeartbeatAt is real).
 */
export enum NasAgentCredentialState {
  Active = 'ACTIVE',
  PendingFirstCheckIn = 'PENDING_FIRST_CHECK_IN',
  Revoked = 'REVOKED',
  Unlinked = 'UNLINKED'
}

/**
 * ENG-3832 — one device credential (nas_agent_devices row) linked to a NAS
 * agent host. Identifiers and timestamps only: never key material, fingerprint or
 * hostname. Read-only; revoke via Mutation.revokeNasDevice.
 */
export type NasAgentDevice = {
  __typename?: 'NasAgentDevice';
  enrolledAt: Scalars['AWSDateTime']['output'];
  /** nas_agent_devices.id (not bearer material). */
  id: Scalars['ID']['output'];
  /**
   * Best-effort time of the last successful token exchange by this device.
   * Not a heartbeat. Null = not recorded.
   */
  lastTokenIssuedAt?: Maybe<Scalars['AWSDateTime']['output']>;
  /** Null = the credential is still live. */
  revokedAt?: Maybe<Scalars['AWSDateTime']['output']>;
};

/**
 * ENG-3097 — one NAS agent release with signed, short-lived (about 10 minutes)
 * install links. The URLs are bearer credentials: never log or persist them, and
 * rely on `expiresAt` rather than a fixed lifetime.
 */
export type NasAgentDownloadRelease = {
  __typename?: 'NasAgentDownloadRelease';
  artifactUrl: Scalars['String']['output'];
  /** Instant at and after which the three URLs stop working. */
  expiresAt: Scalars['AWSDateTime']['output'];
  /** The package file name (e.g. nas-agent-v1.2.3-linux-amd64.tar.gz). */
  filename: Scalars['String']['output'];
  manifestUrl: Scalars['String']['output'];
  /** Lowercase hex sha256 of the package. */
  sha256: Scalars['String']['output'];
  signatureUrl: Scalars['String']['output'];
  sizeBytes: Scalars['Int']['output'];
  version: Scalars['String']['output'];
};

/** Input for Query.nasAgentDownloads. */
export type NasAgentDownloadsInput = {
  /** Restrict to one target. Omitted => all supported targets. */
  target?: InputMaybe<NasAgentTarget>;
};

/** Payload for Query.nasAgentDownloads. */
export type NasAgentDownloadsPayload = {
  __typename?: 'NasAgentDownloadsPayload';
  /** Connection settings to pre-fill the NAS agent config.yaml. Always present; apiBaseUrl and releaseHost are null when not configured in this environment; channel is always set. */
  agentSettings: NasAgentConnectionSettings;
  /** One entry per requested target, ordered WINDOWS_AMD64, LINUX_AMD64, LINUX_ARM64. */
  downloads: Array<NasAgentTargetDownload>;
};

/**
 * ENG-2583 — per-agent auto-update slice + live state, from the nas_agents
 * rollout columns (0423). Distinct from the top-level `platform` JSONB projection.
 */
export type NasAgentRolloutState = {
  __typename?: 'NasAgentRolloutState';
  arch?: Maybe<Scalars['String']['output']>;
  channel: Scalars['String']['output'];
  lastUpdateError?: Maybe<Scalars['String']['output']>;
  platform?: Maybe<Scalars['String']['output']>;
  targetVersion?: Maybe<Scalars['String']['output']>;
  updateStatus: NasAgentUpdateStatus;
  updatingVersion?: Maybe<Scalars['String']['output']>;
};

/**
 * Agent-reported coarse sync health (mirrors the nas_agents.sync_status DB
 * CHECK). Drives the dashboard status badge; staleness (offline) is additionally
 * derived client-side from lastHeartbeatAt age.
 */
export enum NasAgentSyncStatus {
  Degraded = 'DEGRADED',
  Error = 'ERROR',
  Healthy = 'HEALTHY',
  Offline = 'OFFLINE',
  Syncing = 'SYNCING',
  Unknown = 'UNKNOWN'
}

/** ENG-3097 — a NAS agent install target (platform + arch). */
export enum NasAgentTarget {
  LinuxAmd64 = 'LINUX_AMD64',
  LinuxArm64 = 'LINUX_ARM64',
  WindowsAmd64 = 'WINDOWS_AMD64'
}

/** ENG-3097 — the latest install build for one NAS agent target. */
export type NasAgentTargetDownload = {
  __typename?: 'NasAgentTargetDownload';
  /** Either `amd64` or `arm64`. */
  arch: Scalars['String']['output'];
  /** Either `windows` or `linux`. */
  platform: Scalars['String']['output'];
  /** Latest published, non-yanked release for this target on `agentSettings.channel`; null when none exists. */
  release?: Maybe<NasAgentDownloadRelease>;
  target: NasAgentTarget;
};

/** ENG-2583 — agent-reported auto-update state (mirrors nas_agents.update_status CHECK). */
export enum NasAgentUpdateStatus {
  Applying = 'APPLYING',
  Assigned = 'ASSIGNED',
  Downloading = 'DOWNLOADING',
  Failed = 'FAILED',
  Idle = 'IDLE',
  RolledBack = 'ROLLED_BACK',
  Updated = 'UPDATED',
  Verifying = 'VERIFYING'
}

/** ENG-3853 — the organization's NAS file-format policy. */
export type NasFormatPolicy = {
  __typename?: 'NasFormatPolicy';
  /**
   * The format keys sent to the organization's stations, in vocabulary order.
   * Empty = none.
   */
  allowedFormats: Array<Scalars['String']['output']>;
  /**
   * True when the organization has never set a policy (the built-in default
   * applies).
   */
  isDefault: Scalars['Boolean']['output'];
};

/**
 * ENG-3975 — how long a NAS station keeps an order's files after it stops
 * needing them.
 */
export type NasHoldingGrace = {
  __typename?: 'NasHoldingGrace';
  /** True when the organization has not set one (the 24-hour default applies). */
  isDefault: Scalars['Boolean']['output'];
  /** Effective minutes (the default when unset). */
  minutes: Scalars['Int']['output'];
};

/**
 * ENG-2583 — a published NAS agent build (global nas_releases catalog).
 * manifestUrl/sha256 are intentionally not exposed (agent-only).
 */
export type NasRelease = {
  __typename?: 'NasRelease';
  arch: Scalars['String']['output'];
  channel: Scalars['String']['output'];
  createdAt: Scalars['AWSDateTime']['output'];
  id: Scalars['ID']['output'];
  isYanked: Scalars['Boolean']['output'];
  minUpgradeFrom?: Maybe<Scalars['String']['output']>;
  platform: Scalars['String']['output'];
  publishedAt?: Maybe<Scalars['AWSDateTime']['output']>;
  version: Scalars['String']['output'];
};

/** ENG-2583 — an org-scoped NAS auto-update rollout (nas_rollouts). */
export type NasRollout = {
  __typename?: 'NasRollout';
  arch: Scalars['String']['output'];
  canaryPercent?: Maybe<Scalars['Int']['output']>;
  channel: Scalars['String']['output'];
  cohortPercent?: Maybe<Scalars['Int']['output']>;
  createdAt: Scalars['AWSDateTime']['output'];
  createdByUserId?: Maybe<Scalars['ID']['output']>;
  id: Scalars['ID']['output'];
  pausedAt?: Maybe<Scalars['AWSDateTime']['output']>;
  phase: NasRolloutPhase;
  platform: Scalars['String']['output'];
  rollbackTargetVersion?: Maybe<Scalars['String']['output']>;
  targetReleaseId?: Maybe<Scalars['ID']['output']>;
  targetVersion: Scalars['String']['output'];
  updatedAt: Scalars['AWSDateTime']['output'];
};

export type NasRolloutPayload = {
  __typename?: 'NasRolloutPayload';
  rollout: NasRollout;
};

/** ENG-2583 — rollout lifecycle phase (mirrors nas_rollouts.phase CHECK). */
export enum NasRolloutPhase {
  Canary = 'CANARY',
  Cohort = 'COHORT',
  Complete = 'COMPLETE',
  Fleet = 'FLEET',
  Paused = 'PAUSED',
  RolledBack = 'ROLLED_BACK'
}

/**
 * ENG-3842 — how a station picks the orders it holds. UNIT_POOL: orders on the
 * station's unit (and its sub-units when includeSubUnits). ASSIGNED_USERS: orders
 * assigned to the station's users.
 */
export enum NasStationAssignmentMode {
  AssignedUsers = 'ASSIGNED_USERS',
  UnitPool = 'UNIT_POOL'
}

/** ENG-3842 — a NAS station's configuration. */
export type NasStationConfig = {
  __typename?: 'NasStationConfig';
  assignmentMode: NasStationAssignmentMode;
  /**
   * File-format keys (e.g. stl, 3mf) this station receives. Null = the
   * organization's format policy.
   */
  formatOverride?: Maybe<Array<Scalars['String']['output']>>;
  /** UNIT_POOL only: also serve the unit's sub-units. */
  includeSubUnits: Scalars['Boolean']['output'];
  /** Null = unconfigured: the station receives nothing. */
  orgUnit?: Maybe<NasStationOrgUnit>;
  /** Paused stations receive no new work. */
  paused: Scalars['Boolean']['output'];
  /** Empty unless ASSIGNED_USERS. Ordered by name. */
  users: Array<NasStationUser>;
};

/** ENG-3842 — a non-fatal station configuration warning. */
export type NasStationConfigWarning = {
  __typename?: 'NasStationConfigWarning';
  code: NasStationConfigWarningCode;
  userId: Scalars['ID']['output'];
};

/**
 * ENG-3842 — non-fatal station configuration warning codes.
 * USER_HAS_NO_ROLE_IN_UNIT: an assigned user holds no live role overlapping the
 * station's unit (tenant-wide, on the unit or a descendant, or on an ancestor).
 */
export enum NasStationConfigWarningCode {
  UserHasNoRoleInUnit = 'USER_HAS_NO_ROLE_IN_UNIT'
}

/** ENG-3842 — the org unit a station serves (identifiers only). */
export type NasStationOrgUnit = {
  __typename?: 'NasStationOrgUnit';
  /** Set when the unit is archived; the station keeps it until reconfigured. */
  archivedAt?: Maybe<Scalars['AWSDateTime']['output']>;
  id: Scalars['ID']['output'];
  name: Scalars['String']['output'];
};

/** ENG-3842 — a station configuration preset carried by an enrollment code. */
export type NasStationPresetInput = {
  assignmentMode: NasStationAssignmentMode;
  orgUnitId: Scalars['ID']['input'];
  /** ASSIGNED_USERS only: 1 to 100 internal users of the organization. */
  userIds?: InputMaybe<Array<Scalars['ID']['input']>>;
};

/** ENG-3842 — a user an ASSIGNED_USERS station serves. */
export type NasStationUser = {
  __typename?: 'NasStationUser';
  fullName?: Maybe<Scalars['String']['output']>;
  /**
   * True when the user holds a live role tenant-wide, on the station's unit or
   * a descendant, or on an ancestor of it.
   */
  hasRoleInUnit: Scalars['Boolean']['output'];
  id: Scalars['ID']['output'];
};

/**
 * User-facing notification.
 * Per docs/clearpath-architecture/03-data-model.md tier 5 (`notifications`).
 */
export type Notification = {
  __typename?: 'Notification';
  aggregateId?: Maybe<Scalars['ID']['output']>;
  /** Linked aggregate (e.g. orderId). */
  aggregateType?: Maybe<Scalars['String']['output']>;
  /** Body text. */
  body: Scalars['String']['output'];
  createdAt: Scalars['AWSDateTime']['output'];
  /** Channels this notification was dispatched to. */
  deliveredChannels: Array<NotificationChannel>;
  id: Scalars['ID']['output'];
  /** Notification type discriminator. */
  notificationType: Scalars['String']['output'];
  organizationId: Scalars['ID']['output'];
  /** In-app payload (JSONB). */
  payload?: Maybe<Scalars['AWSJSON']['output']>;
  /** Read state. */
  readAt?: Maybe<Scalars['AWSDateTime']['output']>;
  /** Severity for UI styling. */
  severity: NotificationSeverity;
  /** Subject line. */
  subject: Scalars['String']['output'];
  tenantId: Scalars['ID']['output'];
  user?: Maybe<User>;
  /** Recipient user. */
  userId: Scalars['ID']['output'];
};

/** Delivery channel. */
export enum NotificationChannel {
  Email = 'EMAIL',
  InApp = 'IN_APP',
  Push = 'PUSH',
  Sms = 'SMS',
  Webhook = 'WEBHOOK'
}

export type NotificationChannelToggles = {
  __typename?: 'NotificationChannelToggles';
  email: Scalars['Boolean']['output'];
  in_app: Scalars['Boolean']['output'];
  push: Scalars['Boolean']['output'];
  sms: Scalars['Boolean']['output'];
};

export type NotificationChannelTogglesInput = {
  email?: InputMaybe<Scalars['Boolean']['input']>;
  in_app?: InputMaybe<Scalars['Boolean']['input']>;
  push?: InputMaybe<Scalars['Boolean']['input']>;
  sms?: InputMaybe<Scalars['Boolean']['input']>;
};

export type NotificationConnection = {
  __typename?: 'NotificationConnection';
  edges: Array<NotificationEdge>;
  pageInfo: PageInfo;
  totalCount: Scalars['Int']['output'];
};

export type NotificationDndInput = {
  enabled?: InputMaybe<Scalars['Boolean']['input']>;
  endTime?: InputMaybe<Scalars['String']['input']>;
  startTime?: InputMaybe<Scalars['String']['input']>;
  timezone?: InputMaybe<Scalars['String']['input']>;
};

export type NotificationDndPreferences = {
  __typename?: 'NotificationDndPreferences';
  enabled: Scalars['Boolean']['output'];
  /** HH:MM (24h). */
  endTime: Scalars['String']['output'];
  /** HH:MM (24h). */
  startTime: Scalars['String']['output'];
  /** IANA timezone. */
  timezone: Scalars['String']['output'];
};

export type NotificationEdge = {
  __typename?: 'NotificationEdge';
  cursor: Scalars['String']['output'];
  node: Notification;
};

/** Filter for notifications. */
export type NotificationFilterInput = {
  notificationType?: InputMaybe<Array<Scalars['String']['input']>>;
  occurredAt?: InputMaybe<DateRangeInput>;
  severity?: InputMaybe<Array<NotificationSeverity>>;
  unreadOnly?: InputMaybe<Scalars['Boolean']['input']>;
};

export type NotificationPayload = {
  __typename?: 'NotificationPayload';
  notification: Notification;
};

/** Per-user notification preferences (MOL-596). */
export type NotificationPreferences = {
  __typename?: 'NotificationPreferences';
  /** Master switch per channel. */
  channels: NotificationChannelToggles;
  /** Do-Not-Disturb quiet hours. */
  dnd: NotificationDndPreferences;
  /**
   * Per-type channel overrides (AWSJSON — shape:
   * { "step.assigned": {"in_app":true,"email":true,"sms":false,"push":false}, ... }).
   */
  types: Scalars['AWSJSON']['output'];
};

/** Notification severity. */
export enum NotificationSeverity {
  Critical = 'CRITICAL',
  Error = 'ERROR',
  Info = 'INFO',
  Success = 'SUCCESS',
  Warning = 'WARNING'
}

/**
 * Per-tenant branded notification template.
 * Per docs/clearpath-architecture/03-data-model.md tier 5 (`notification_templates`).
 */
export type NotificationTemplate = {
  __typename?: 'NotificationTemplate';
  bodyTemplate: Scalars['String']['output'];
  channel: NotificationChannel;
  createdAt: Scalars['AWSDateTime']['output'];
  id: Scalars['ID']['output'];
  isActive: Scalars['Boolean']['output'];
  locale: Scalars['String']['output'];
  notificationType: Scalars['String']['output'];
  organizationId: Scalars['ID']['output'];
  subjectTemplate: Scalars['String']['output'];
  tenantId: Scalars['ID']['output'];
  updatedAt?: Maybe<Scalars['AWSDateTime']['output']>;
};

/**
 * Input for openFormDefinitionForEdit / discardFormDefinitionDraft
 * (ENG-658 Part B, mirroring OpenPipelineForEditInput / DiscardPipelineDraftInput).
 */
export type OpenFormDefinitionForEditInput = {
  formDefinitionId: Scalars['ID']['input'];
};

/**
 * Input for openPipelineForEdit (self-versioning, requirement #2). Opens a
 * LIVE/INACTIVE pipeline for editing by creating a new DRAFT *version* of the SAME
 * pipeline (no fork, no new pipeline id). Idempotent: re-opening a pipeline that
 * already has an open draft returns it unchanged. Replaces the removed forkPipeline.
 */
export type OpenPipelineForEditInput = {
  pipelineId: Scalars['ID']['input'];
};

/**
 * Primary work unit. Hierarchical via parentOrderId; warranty/remake links via relatedOrderId.
 * Per docs/clearpath-architecture/03-data-model.md tier 3 (`orders`).
 */
export type Order = {
  __typename?: 'Order';
  /**
   * Open holds covering this case — every `order_holds` row for this order
   * whose `releasedAt` is null, case-scoped and unit-scoped alike.
   *
   * A LIST, and empty rather than null when nothing is held. A 3-unit case can
   * legitimately carry three concurrent unit holds (each unit runs its own
   * pipeline) plus one case-scoped hold, so a nullable singleton would silently
   * hide holds. Order is oldest-first by `startedAt`.
   *
   * Note what this field does NOT change: `status`, `lifecycleStatus` and every
   * `StepRun.status` on the case are completely unaffected by a hold. Read this
   * field to decide whether to show a hold badge and disable Complete/Rework —
   * never infer it from a lifecycle value.
   */
  activeHolds: Array<OrderHold>;
  /**
   * Users eligible to be set as the order-level default assignee right
   * now. Returns the union of `assignableUsers` across every step that
   * has a step_run currently in `ready` state on this order — so a
   * multi-product order shows members of all currently-actionable teams.
   * When no step is ready (e.g. the order is in `intake` or `completed`),
   * returns an empty list. Used by the order header's assignee chip.
   */
  assignableUsersForReadySteps: Array<User>;
  /** Audit trail for this order. */
  auditLog: AuditLogEntryConnection;
  /** Inherited from tenant settings (Code128 / Code39 / QR / DataMatrix). */
  barcodeFormat: BarcodeFormat;
  /**
   * Rendered barcode as a `data:image/png;base64,...` URI, encoding
   * barcodeValue in barcodeFormat (ENG-191). Computed inline from the same
   * bwip-js generator the lab-slip PDF uses (services/barcode) — no extra
   * network round trip, and only resolved when a query selects it, so list
   * views that don't ask for it pay no cost. Null when barcodeValue is empty
   * (should not happen for a persisted order, but the resolver degrades
   * instead of throwing).
   */
  barcodeImageUrl?: Maybe<Scalars['String']['output']>;
  /** Auto-generated barcode value (unique per tenant). */
  barcodeValue: Scalars['String']['output'];
  /** Billing events emitted by this order. */
  billingEvents: BillingEventConnection;
  billingParty?: Maybe<ExternalParty>;
  /** Who pays. */
  billingPartyId?: Maybe<Scalars['ID']['output']>;
  /**
   * This order's billing facts, computed ONLY by the `orders()` list path. See
   * OrderBillingState — null everywhere else, and null for a caller without
   * `clearpath.billing.read` + `clearpath.invoices.view`.
   */
  billingState?: Maybe<OrderBillingState>;
  /**
   * ENG-1542 — the expanded-panel payload. LAZY: a real field resolver, so it
   * costs nothing until a row is expanded.
   *
   * For expanding MANY rows at once — `Expand all`, pipeline-view mode — use
   * `Query.orderBreakouts` instead. Selecting this per row across a page is one
   * Lambda invocation per row, each independently deriving per-Unit progress;
   * the bulk query answers the whole page in one. Both resolve through the same
   * derivation, so they cannot disagree.
   */
  breakout?: Maybe<OrderBreakout>;
  /** ENG-3881 — when the order was cancelled (orders.cancelled_at); null otherwise. */
  cancelledAt?: Maybe<Scalars['AWSDateTime']['output']>;
  /** Carrier integration (FK → external_integrations). */
  carrierId?: Maybe<Scalars['ID']['output']>;
  /**
   * ENG-3118 — the case subject of a migrated order: who the case is for, as
   * recorded (protected data; names only). Readable only with
   * clearpath.case_subjects.view, from an internal or customer session that can
   * see the order. A session without it gets a `Forbidden` error on this field,
   * never a silent null; an order that links a subject the session's data lens
   * hides gets `CaseSubjectUnavailable`. Null when the order has no subject (a
   * native order) or when the viewer's portal view config hides the order-intake
   * card (the same redaction as the recorded subject-name field). Every read is
   * audited (`case_subject.phi_read`). Batched on the `Query.orders` page; other
   * parents resolve it per order.
   */
  caseSubject?: Maybe<CaseSubject>;
  closedAt?: Maybe<Scalars['AWSDateTime']['output']>;
  /** JSONB list of items blocking closure. */
  closureBlockers?: Maybe<Scalars['AWSJSON']['output']>;
  /** Comments on this order. */
  comments: CommentConnection;
  createdAt: Scalars['AWSDateTime']['output'];
  /** Tenant-defined custom fields (JSONB). */
  customFields?: Maybe<Scalars['AWSJSON']['output']>;
  customerParty?: Maybe<ExternalParty>;
  /** The doctor/customer who owns the case. */
  customerPartyId: Scalars['ID']['output'];
  /**
   * The CUSTOMER-SAFE view of this case's open holds, for the portal display
   * (ENG-687). Unlike `activeHolds` — which carries internal fields (the
   * operator `note`, the internal reason label/description, `responsibleParty`,
   * the staff who placed it) — this field returns ONLY holds whose reason a lab
   * admin opted into the portal (`HoldReasonCode.isCustomerVisible`), projected to
   * the customer-safe `CustomerVisibleHold` (order id + `customerFacingText` +
   * `heldSince`). A LIST, empty (never null) when the case has no customer-visible
   * open hold. Server-scoped to the caller's own orders; a portal customer never
   * sees a hold on a case they do not own, nor a hold whose reason is not
   * customer-visible. This is the field the portal renders — never `activeHolds`.
   */
  customerVisibleHolds: Array<CustomerVisibleHold>;
  deliveredAt?: Maybe<Scalars['AWSDateTime']['output']>;
  /** Materialized — recomputed on every pipeline event. */
  estimatedCompletionAt?: Maybe<Scalars['AWSDateTime']['output']>;
  /** Federation links currently dispatched against this order. */
  federationLinks: Array<FederationLink>;
  /** Files attached to this order. */
  files: FileConnection;
  /**
   * ENG-2293 — this order has at least one unit sitting on a step it was sent
   * BACK to: a non-terminal step_run created by a rework. Drives the rework
   * marker on the order cards so a case that has gone backwards is
   * distinguishable at a glance from one progressing normally.
   *
   * "Currently", not "ever". An order that was reworked and has since moved on
   * is progressing normally again, and a permanent mark would end up on most
   * cases and mean nothing.
   *
   * Derived from `step_runs.loopback_from_step_run_id`, not from
   * `attempt_number > 1`: a reassignment and a scan retry also raise the attempt
   * number without the unit having gone backwards.
   *
   * Populated inline+batched on the list path for the same reason as
   * `pipelinePosition` above, and NULLABLE for the same reason — it has no field
   * resolver, so off the list path it is `null`, meaning "not computed here"
   * rather than "this order has no rework".
   */
  hasActiveRework?: Maybe<Scalars['Boolean']['output']>;
  id: Scalars['ID']['output'];
  /**
   * Track 2 (2026-04-30) — order-level intake form answers, structured
   * per the schema in `tenantSettings.orderIntakeFormDefinition.schema`.
   * Empty object when the tenant has not configured an order-level form.
   * Distinct from `customFields` (free-form) and `OrderProduct.intakeFormResponse`
   * (per-line).
   */
  intakeFormResponse?: Maybe<Scalars['AWSJSON']['output']>;
  /** Invoices generated against this order. */
  invoices: InvoiceConnection;
  /** Long-term lifecycle state. */
  lifecycleStatus: OrderLifecycleStatus;
  /** Notifications scoped to this order (current viewer). */
  notifications: NotificationConnection;
  /**
   * ENG-775 — THE PIN (order-level intake). The `form_definition_versions` id
   * that was LIVE for the tenant's bound order-intake form when this order was
   * submitted. Stamped once at submit and immutable thereafter, so a reader can
   * re-render `intakeFormResponse` against the exact version's schema it was
   * captured on — never a later republished one. Null for historical/unpinned
   * orders and for tenants with no order-level form bound.
   */
  orderIntakeFormVersionId?: Maybe<Scalars['ID']['output']>;
  orderPipeline?: Maybe<OrderPipeline>;
  /** Order-level pipeline this order follows. */
  orderPipelineId?: Maybe<Scalars['ID']['output']>;
  /** Line items on this order — each runs its own pipeline. */
  orderProducts: Array<OrderProduct>;
  /** Order kind discriminator. */
  orderType: OrderType;
  orderingPractitionerParty?: Maybe<ExternalParty>;
  /**
   * The individual practitioner this case is attributed to
   * (orders.ordering_practitioner_party_id, migration 0263). DISTINCT from
   * customerPartyId, which is the commercial practice that may be shared by
   * many practitioners — that distinction is what db/rls/0058's per-practitioner
   * visibility lens is built on. Null for orders submitted before the column was
   * populated, or where no practitioner was named.
   */
  orderingPractitionerPartyId?: Maybe<Scalars['ID']['output']>;
  organizationId: Scalars['ID']['output'];
  parent?: Maybe<Order>;
  /** Parent order (sub-orders within one job). */
  parentOrderId?: Maybe<Scalars['ID']['output']>;
  /**
   * ENG-1542 — collapsed-row pipeline-position summary, for the orders LIST.
   * Backs the segment bar and its one-line summary.
   *
   * Populated inline+batched on the list path, exactly like `splitSubOrders` and
   * for the same reason: a per-row field resolver would be an N+1 across a full
   * page. NULLABLE on purpose — it has no field resolver, so off the list path it
   * is `null` (honestly "not computed here"), never an empty summary that would
   * read as "this order has no pipeline". Also null for an org that declares no
   * order-scope pipeline, which is a real state and not an error.
   */
  pipelinePosition?: Maybe<OrderPipelinePosition>;
  /**
   * Charges attributed to this order's ordering practitioner. See
   * PractitionerRevenue for the basis and for why this does NOT reconcile with
   * Query.revenueReport. Null without clearpath.finance.read.
   */
  practitionerRevenue?: Maybe<PractitionerRevenue>;
  /** Pricing snapshot frozen at order creation (JSONB). */
  pricingSnapshot?: Maybe<Scalars['AWSJSON']['output']>;
  relatedOrder?: Maybe<Order>;
  /** Cross-job link (warranty/remake referencing original). */
  relatedOrderId?: Maybe<Scalars['ID']['output']>;
  /**
   * Cross-job related orders — rows whose `relatedOrderId` points at
   * this order (warranty returns, remakes, credit links). Distinct from
   * the sub-order hierarchy. Bible §05:22.
   */
  relatedOrders: Array<Order>;
  /**
   * ENG-1422 — what a PROSPECTIVE adjustment or remake of this case would cost
   * the customer, under the tenant's current charge policy and this case's
   * warranty window. Read this instead of hardcoding "remakes are free" copy.
   *
   * Resolver-computed per call (there is no column behind it) and scoped exactly
   * like `Query.order`: a portal practitioner reaches it only on a case they own.
   * NULLABLE on purpose — it is null off the resolver path (honestly "not
   * computed here") rather than a fabricated free/paid answer; see the CP-P0-02
   * resolver-binding drift gate. It does NOT create the rework; it only quotes it.
   */
  reworkChargeEstimate?: Maybe<ReworkChargeEstimate>;
  /**
   * Scanner (iTero/3Shape) intake surface for an order that originated from a
   * connected intake scanner. Null for manually-created orders. Reads the
   * scanner_rx_ingests + scanner_order_artifacts tables (migration 0075) scoped
   * to the viewer's organization. PHI (raw RX payload): the resolver is
   * Cedar-gated on `clearpath.orders.view` and writes a data-read audit row.
   * Bible §05 (orders) / §07 (files-and-nas).
   */
  scannerIntake?: Maybe<ScannerIntake>;
  seatedAt?: Maybe<Scalars['AWSDateTime']['output']>;
  /** Active share links scoped to this order. */
  sharedAccessLinks: Array<SharedAccessLink>;
  shipToParty?: Maybe<ExternalParty>;
  /** Physical delivery address. */
  shipToPartyId?: Maybe<Scalars['ID']['output']>;
  shippedAt?: Maybe<Scalars['AWSDateTime']['output']>;
  /**
   * ENG-3286 — who pays for shipping and which account it ships on, frozen at
   * creation. Lab staff only: null for portal and vendor sessions.
   */
  shippingTerms?: Maybe<OrderShippingTerms>;
  /**
   * ENG-3455 — the imported order's source-system references (ABS case number
   * + ABS Order.id), read from the migration crosswalk. LAB STAFF ONLY: null for
   * portal (customer), vendor and every non-internal session, and null for a
   * natively-created order that was never imported.
   */
  sourceReference?: Maybe<OrderSourceReference>;
  /**
   * ENG-661 — force-release split siblings nested under this order, for the
   * portal orders LIST only. A lab-side split is hidden from the customer's
   * top-level list (it must not read as two unrelated cases); this field
   * re-surfaces those siblings AS children of the source so the practitioner
   * still sees the split-off units, nested under the parent. Unlike `subOrders`
   * (a per-order resolver returning ALL children — combo-case/warranty included),
   * this is populated inline+batched on the list path for portal customers.
   * Identified by the `force_release_split_from` custom-fields marker, NOT
   * `parent_order_id`. NULLABLE on purpose: it has no field resolver, so off the
   * list path it is `null` (honestly "not computed here"), never a silent empty
   * list — see the CP-P0-02 resolver-binding drift gate.
   */
  splitSubOrders?: Maybe<Array<Order>>;
  /** Workflow state — where the order is in its pipeline right now. */
  status: Scalars['String']['output'];
  /** All step runs across this order (order-level + product-level). */
  stepRuns: StepRunConnection;
  /**
   * Sub-order count — number of children pointing at this order via
   * `parent_order_id`. Cheaper than selecting `subOrders { id }` when the
   * list view only needs a `3 sub-orders` indicator. Bible §05:9.
   */
  subOrderCount: Scalars['Int']['output'];
  /**
   * Sub-orders (children) — the bible-canonical order-group hierarchy
   * (Bible §05:9). Returns rows whose `parent_order_id` equals this.id.
   */
  subOrders: Array<Order>;
  /**
   * Case-subject display name, derived server-side from the order-level
   * intake response (`first_name` + `last_name`, else the `subject_ref`
   * answer). Null when the order predates those fields or was created outside
   * the intake wizard — clients render a blank, never a fallback from other
   * sources. Redacted (null) for viewers whose portal view config hides the
   * order-intake card, same as `intakeFormResponse`.
   */
  subjectName?: Maybe<Scalars['String']['output']>;
  submittedByUser?: Maybe<User>;
  /** User who clicked submit. */
  submittedByUserId?: Maybe<Scalars['ID']['output']>;
  tenantId: Scalars['ID']['output'];
  /** Carrier tracking number. */
  trackingNumber?: Maybe<Scalars['String']['output']>;
  updatedAt?: Maybe<Scalars['AWSDateTime']['output']>;
  warrantyForOrder?: Maybe<Order>;
  /**
   * Typed warranty linkage (Bible §05:158-167). Set by requestWarrantyReturn
   * on the new sibling order; the referenced source order transitions to
   * lifecycle_status='warranty_returned'. Distinct from relatedOrderId.
   */
  warrantyForOrderId?: Maybe<Scalars['ID']['output']>;
  /** Materialized from product_types.warranty_period_days. */
  warrantyUntil?: Maybe<Scalars['AWSDateTime']['output']>;
};


/**
 * Primary work unit. Hierarchical via parentOrderId; warranty/remake links via relatedOrderId.
 * Per docs/clearpath-architecture/03-data-model.md tier 3 (`orders`).
 */
export type OrderAssignableUsersForReadyStepsArgs = {
  search?: InputMaybe<Scalars['String']['input']>;
};


/**
 * Primary work unit. Hierarchical via parentOrderId; warranty/remake links via relatedOrderId.
 * Per docs/clearpath-architecture/03-data-model.md tier 3 (`orders`).
 */
export type OrderAuditLogArgs = {
  page?: InputMaybe<PageInput>;
};


/**
 * Primary work unit. Hierarchical via parentOrderId; warranty/remake links via relatedOrderId.
 * Per docs/clearpath-architecture/03-data-model.md tier 3 (`orders`).
 */
export type OrderBillingEventsArgs = {
  page?: InputMaybe<PageInput>;
};


/**
 * Primary work unit. Hierarchical via parentOrderId; warranty/remake links via relatedOrderId.
 * Per docs/clearpath-architecture/03-data-model.md tier 3 (`orders`).
 */
export type OrderCommentsArgs = {
  page?: InputMaybe<PageInput>;
};


/**
 * Primary work unit. Hierarchical via parentOrderId; warranty/remake links via relatedOrderId.
 * Per docs/clearpath-architecture/03-data-model.md tier 3 (`orders`).
 */
export type OrderFilesArgs = {
  page?: InputMaybe<PageInput>;
};


/**
 * Primary work unit. Hierarchical via parentOrderId; warranty/remake links via relatedOrderId.
 * Per docs/clearpath-architecture/03-data-model.md tier 3 (`orders`).
 */
export type OrderInvoicesArgs = {
  page?: InputMaybe<PageInput>;
};


/**
 * Primary work unit. Hierarchical via parentOrderId; warranty/remake links via relatedOrderId.
 * Per docs/clearpath-architecture/03-data-model.md tier 3 (`orders`).
 */
export type OrderNotificationsArgs = {
  page?: InputMaybe<PageInput>;
};


/**
 * Primary work unit. Hierarchical via parentOrderId; warranty/remake links via relatedOrderId.
 * Per docs/clearpath-architecture/03-data-model.md tier 3 (`orders`).
 */
export type OrderReworkChargeEstimateArgs = {
  mode: ReworkMode;
};


/**
 * Primary work unit. Hierarchical via parentOrderId; warranty/remake links via relatedOrderId.
 * Per docs/clearpath-architecture/03-data-model.md tier 3 (`orders`).
 */
export type OrderStepRunsArgs = {
  filter?: InputMaybe<StepRunFilterInput>;
  page?: InputMaybe<PageInput>;
};

/** Generic order action payload. */
export type OrderActionPayload = {
  __typename?: 'OrderActionPayload';
  order: Order;
};

/** Bundle of all order analytics for a window (single round-trip). */
export type OrderAnalytics = {
  __typename?: 'OrderAnalytics';
  leadTime: LeadTimeMetrics;
  /** Per-step cycle times, slowest average first (bottlenecks at the top). */
  stepCycleTimes: Array<StepCycleTime>;
  throughput: ThroughputMetrics;
};

/**
 * Billing-activity modes for `OrderFilterInput.billingActivity`. One member: an
 * order is either in the invoicing working set or it is finished history, and the
 * honest model has no third answer. Omitting the filter is how you ask for every
 * order regardless.
 */
export enum OrderBillingActivityFilter {
  /**
   * The working set: every order that is NOT FULLY INVOICED. An order is
   * excluded only when it carries at least one charge, none of them is still in
   * play, and at least one of them is on an invoice. Everything else is listed.
   *
   * "Not fully invoiced" rather than "has a billable charge", deliberately, and
   * rather than "looks billable" even more so:
   *
   * * An order whose charges are ALL parked on a statement or ALL written off has
   *   nothing billable and belongs here, carrying the ON_STATEMENT or
   *   NO_BILLABLE_CHARGES blocker that says why.
   * * An order with NO charges belongs here whatever its lifecycle and whatever
   *   its pricing snapshot. This arm used to require `active` plus a non-empty
   *   snapshot, which hid two populations the server bills anyway — an open
   *   unpriced case (capture freezes a price itself) and a closed never-captured
   *   case (nothing on the invoice path reads `lifecycle_status`).
   * * Partly-invoiced orders carry a billable charge, so they appear here,
   *   labelled and blocked — which is the point.
   *
   * There is NO `cancelled` exception, deliberately, and it is not an oversight
   * that a cancelled case appears here. The automated `billing.invoice.due` path
   * reaches the generator with billing-event ids and no order ids and never reads
   * `orders`, so a cancelled order with pending charges is invoiced by the server
   * today; hiding it would leave the one operator who could stop that unable to
   * see it. It is listed and REFUSED — `Order.billingState.blockers` carries
   * ORDER_CANCELLED — rather than filtered away.
   */
  Unbilled = 'UNBILLED'
}

/**
 * Why an order cannot be billed RIGHT NOW to the party that already owns it
 * (`Order.billingPartyId`). Selectable ⇔ this list is empty.
 *
 * THE GOVERNING INVARIANT: a blocker may only be emitted for a refusal PROVABLE
 * FROM THE ROW. Everything else is listed, attempted, and refused BY NAME through
 * `GenerateInvoicePayload.skippedOrders`. A blocker the server does not enforce
 * renders a billable case permanently inert on the only screen that bills it,
 * which is how NOT_CAPTURABLE below came to be deprecated.
 *
 * Deliberately NOT a superset of `GenerateInvoiceSkipReason`. ONE of that model's
 * reasons is absent by construction, not by omission:
 *
 *   * BILLING_PARTY_MISMATCH / `ORDER_BILLED_ELSEWHERE` — every blocker here is
 *     computed against the order's OWN payer, because the generate flow issues one
 *     call per payer and passes that party. There is no second party to disagree
 *     with, so the blocker is unreachable rather than unreported.
 *
 * `NO_SHIP_TO_TAX_ADDRESS` used to be the second, and an earlier version of this
 * description called the location of the single `checkShipToTaxAddress` copy an
 * open decision. IT IS DECIDED. The rule — the deterministic address pick and the
 * postal normaliser that judges it — lives in
 * `@clearpath/shared-types/ship-to-tax-address`;
 * `services/pricing/src/invoicing/ship_to_tax_address.ts` imports and RE-EXPORTS
 * it, so the generator's surface is unchanged, and the orders-list projection
 * imports the same module. Each service keeps its own read and interpolates the
 * same exported `TAX_ADDRESS_PICK_ORDER_BY` constant. That is the move
 * `@clearpath/shared-types/tax-postal` already made, for the same reason: services
 * /api cannot depend on services/pricing, and a divergent second copy of this rule
 * is worse than no gate.
 */
export enum OrderBillingBlocker {
  /**
   * A non-void invoice already touches this order. Note that ANY such invoice
   * blocks the WHOLE order, even a partly-invoiced one; the remedy is to void it
   * (`bulkVoidInvoices`), which detaches its charges and restores the prior state.
   */
  ActiveInvoice = 'ACTIVE_INVOICE',
  /**
   * The order has pending, uninvoiced charges stamped to a party OTHER than the
   * order's own payer — the pre-ENG-2571 shape, where `input.billablePartyId`
   * overrode the order at capture. Same predicate as `findOrdersNotBillableTo`'s
   * `events_billed_elsewhere` arm.
   */
  ChargesBilledElsewhere = 'CHARGES_BILLED_ELSEWHERE',
  /**
   * NO LONGER EMITTED. Retained because removing a published enum value is
   * breaking; a client may still receive it from a stale cache, and should treat
   * it as no blocker at all.
   *
   * It tested `orders.lifecycle_status`, and no refusal on the invoice path is
   * provable from that column — nothing in services/pricing gates on it
   * (`ship_to_tax_address_report.ts` is a report, not the generator), and
   * `captureBillingEvent` calls `ensurePricingSnapshot` and freezes a price on the
   * spot. So a closed order with no charges IS captured and billed by the server,
   * while this value rendered it permanently inert on the only screen that bills
   * it. Whether capture produces charges is decided at capture and reported
   * through `GenerateInvoiceSkipReason`.
   * @deprecated No longer emitted. Whether capture produces charges is decided at capture and reported through GenerateInvoiceSkipReason.
   */
  NotCapturable = 'NOT_CAPTURABLE',
  /**
   * Charges exist, none are billable, and none are on an invoice or a statement
   * — every one was written off, credited or refunded. The only dead end this
   * model still declares; an order with NO charges is not one, because capture
   * would create them.
   */
  NoBillableCharges = 'NO_BILLABLE_CHARGES',
  /**
   * The ship-to party's engine-picked address cannot resolve an invoice
   * destination — at least one of country, state/region or postal code is missing
   * or unusable. Acceptance criterion 8's "destination unknown".
   *
   * THE ONE BLOCKER THAT IS NOT ABOUT MONEY, which is why it is declared here,
   * between the order-level refusals above and the charge-level ones below. An
   * order can be perfectly billable in every other respect and still be refused by
   * this, and that is its main population.
   *
   * IT MIRRORS A REFUSAL THE SERVER ALREADY MAKES. `generateInvoice` hard-refuses
   * such an order on BOTH arms, pre-capture and already-captured, and reports
   * `GenerateInvoiceSkipReason.NO_SHIP_TO_TAX_ADDRESS` with the offending
   * `shipToPartyId`. Before this value, that refusal reached the operator only
   * AFTER they clicked Generate — the row looked ready and then was not.
   *
   * Emitted even alongside ACTIVE_INVOICE, unlike the charge-level blockers below,
   * which suppress each other when they would be redundant. This one is not
   * redundant with anything: it is a separate repair, and hiding it behind an
   * invoice the operator is about to void would just defer the discovery.
   *
   * NOT a "no tax will be charged" note. A line the engine legitimately does not
   * tax still needs a destination on the document; this says the document cannot
   * be written at all. The repair is to complete the ship-to party's shipping
   * address, which the generate flow also offers inline.
   */
  NoShipToTaxAddress = 'NO_SHIP_TO_TAX_ADDRESS',
  /**
   * Every remaining charge sits on a statement. `billing_events.statement_id`
   * is set, the status stays `pending`, and `loadUnbilledEvents` excludes it — so
   * without this the order reads as ready, generate returns zero invoices, and
   * nothing explains why.
   */
  OnStatement = 'ON_STATEMENT',
  /**
   * The order is cancelled (`lifecycle_status = 'cancelled'`). A cancelled case
   * is never an option on this screen.
   *
   * DECLARED FIRST DELIBERATELY. A client that shows one blocker reads this enum's
   * order as precedence, and this is the refusal no other remedy clears — offering
   * "void the invoice to bill this case again" in front of a cancelled case sends
   * the operator somewhere that does not help.
   *
   * THE ROW IS STILL LISTED, which is the point. `billingActivity: UNBILLED` has
   * no lifecycle arm, because the automated `billing.invoice.due` path reaches the
   * generator with billing-event ids and no order ids and never reads `orders` —
   * so a cancelled order's pending charges ARE invoiced by the server today.
   * Hiding the row would leave the one operator who could stop that unable to see
   * it. Listed, labelled, and inert.
   *
   * It is emitted whether or not the order has charges: it is a fact about the
   * case, not about its money. Where money IS still attached, the remedy is on the
   * charges rather than the case — void or re-stamp them, or re-open the case if
   * it should be billed after all.
   */
  OrderCancelled = 'ORDER_CANCELLED',
  /**
   * ENG-3286 — the order's shipping is invoiced to the customer and its active
   * label has no known charge amount (the carrier quoted none, or zero).
   */
  ShippingAmountUnknown = 'SHIPPING_AMOUNT_UNKNOWN',
  /**
   * ENG-3286 — the shipping charge is still to be created and the label's
   * currency differs from the order's charges, so the generator will refuse it.
   */
  ShippingCurrencyMismatch = 'SHIPPING_CURRENCY_MISMATCH',
  /**
   * ENG-3286 — the order's shipping is invoiced to the customer and it has no
   * active outbound label yet. Buy the label first; the invoice then carries the
   * shipping charge.
   */
  ShippingLabelRequired = 'SHIPPING_LABEL_REQUIRED'
}

export type OrderBillingPartyChange = {
  __typename?: 'OrderBillingPartyChange';
  /** Null when changeable. */
  blocker?: Maybe<OrderBillingPartyChangeBlocker>;
  changeable: Scalars['Boolean']['output'];
  /** Captured charges the change re-stamps to the new account; 0 when not changeable. */
  chargesToMove: Scalars['Int']['output'];
  /** Non-void invoices the caller can see; only for LIVE_INVOICE, else empty. */
  liveInvoices: Array<OrderBillingPartyChangeInvoice>;
};

/** Why who pays cannot change (Query.orderBillingPartyChange). */
export enum OrderBillingPartyChangeBlocker {
  /** Charges are no longer pending. */
  ChargeNotPending = 'CHARGE_NOT_PENDING',
  /** Charges are on an invoice that is not void. Voiding it frees them. */
  LiveInvoice = 'LIVE_INVOICE',
  /** A payment against these charges was taken or is in flight. */
  Payment = 'PAYMENT',
  /** The order shares an active shipping label. Voiding the label frees it. */
  SharedLabel = 'SHARED_LABEL',
  /** Charges are already billed to a party other than the order's own. */
  StampedElsewhere = 'STAMPED_ELSEWHERE',
  /** Charges are on a statement to the current payer. */
  Statement = 'STATEMENT'
}

export type OrderBillingPartyChangeInvoice = {
  __typename?: 'OrderBillingPartyChangeInvoice';
  id: Scalars['ID']['output'];
  invoiceNumber: Scalars['String']['output'];
};

/**
 * The billing facts for one order, as of this page read.
 *
 * NULLABLE AND UNBOUND BY DESIGN — exactly the `Order.pipelinePosition` /
 * `Order.splitSubOrders` contract (services/api/src/orders/order-row.ts). There is
 * no field resolver: AppSync unit-resolves this from `$context.source`, and only
 * the `orders()` list path computes it, in one grouped read for the whole page.
 * Selecting it anywhere else returns null, which means "not computed on this
 * path", never "this order has no billing".
 *
 * Null ALSO means: the caller lacks `clearpath.billing.read` or
 * `clearpath.invoices.view`; or the caller is a portal customer; or this order's
 * charges span more than one currency, which makes every total below arithmetic
 * with no defined meaning (the same refusal `PractitionerRevenue` and
 * `settled_amount_sql.ts` make).
 *
 * ⚠ NO CHARGE-DATE BOUNDS ARE APPLIED. `generateInvoice` additionally filters the
 * charges it bills by `periodStart`/`periodEnd` on `billing_events.event_at`, so a
 * caller that sends a narrower period than this projection saw will invoice LESS
 * than `billableAmount` says. Either do not send a period, or wait for the bounds
 * to be threaded through this projection.
 */
export type OrderBillingState = {
  __typename?: 'OrderBillingState';
  /**
   * The non-void invoices touching this order. Non-empty ⇔ the ACTIVE_INVOICE
   * blocker; the same 3-way read `findOrderIdsWithActiveInvoice` uses, so the
   * preflight and the generator's own guard cannot disagree.
   */
  activeInvoiceIds: Array<Scalars['ID']['output']>;
  /**
   * Exact sum of the charges `generateInvoice` would bill for this order to its
   * own payer: pending, on no invoice, on no statement, stamped to that payer.
   * For NONE_CAPTURED it covers only charges already recorded (0, or a pre-ship
   * fee); the products are the client's estimate to derive from
   * `pricingSnapshot`, because only capture can turn them into money.
   */
  billableAmount: Scalars['Float']['output'];
  /**
   * The ids behind `billableAmount`, for `generateInvoiceManual.billingEventIds`.
   * Either form bills the whole order (ENG-3464): an `orderIds` call also takes the
   * order's pending charges to the same payer (pre-ship fees included), and naming
   * only a fee's id still captures the order's products. Every generator gate
   * applies either way.
   */
  billableBillingEventIds: Array<Scalars['ID']['output']>;
  /** Why it cannot be billed right now. Empty ⇔ selectable. */
  blockers: Array<OrderBillingBlocker>;
  /** What the money is. See OrderChargeState. */
  chargeState: OrderChargeState;
  /** The single currency of this order's charges. Null when it has none. */
  currency?: Maybe<Scalars['String']['output']>;
  /** Sum of the charges already on a non-void invoice. */
  invoicedAmount: Scalars['Float']['output'];
  /**
   * ENG-3286 — DISPLAY ONLY. The shipping charge the next invoice would add
   * (label quote plus the tenant markup) when it has not been created yet. The
   * generator computes and freezes the real amount; this is an estimate.
   */
  pendingShippingCharge?: Maybe<Scalars['Float']['output']>;
  /**
   * Set with NO_SHIP_TO_TAX_ADDRESS when every address that could serve the
   * order (the ship-to party's, or with none of its own an ancestor's) is
   * marked for Billing only. Null otherwise.
   */
  shipToRestriction?: Maybe<ShipToAddressRestriction>;
  /** Things to say without refusing the row. */
  warnings: Array<OrderBillingWarning>;
};

/**
 * Something a client must SAY about an order without refusing to select it.
 * Warnings never make a row unselectable; `blockers` does that.
 */
export enum OrderBillingWarning {
  /**
   * NONE_CAPTURED with an empty pricing snapshot. NOT a blocker: capture calls
   * `ensurePricingSnapshot` first and only fails if that also fails, so refusing
   * here would lose orders the server would happily bill.
   */
  PricingNotFrozen = 'PRICING_NOT_FROZEN'
}

/**
 * ENG-1542 — the expanded orders-table panel for one order.
 *
 * `order steps -> [one branch per Unit] -> order steps resume`. There is no split
 * step and no merge step: the `sku_breakout` node is the REGION the branches
 * occupy — it names that region through `breakoutLabel` — and it owns the join.
 */
export type OrderBreakout = {
  __typename?: 'OrderBreakout';
  branches: Array<OrderUnitBranch>;
  /**
   * The region's name (e.g. "Product Pipelines"). Never a step on the rail:
   * no operator can claim it and no step_run is ever created for it. Null when the
   * order pipeline declares no fan-out, in which case `branches` is empty and
   * `railBefore` is the whole rail.
   */
  breakoutLabel?: Maybe<Scalars['String']['output']>;
  merge: OrderMergeState;
  orderId: Scalars['ID']['output'];
  /**
   * The order-scope pipeline this rail comes from, for a link to its graph
   * editor. The `pipelines` row declaring `pipeline_scope = 'order'` — NOT
   * `orders.order_pipeline_id`, which still points at the legacy table.
   */
  pipelineId: Scalars['ID']['output'];
  /** Order steps that resume after the merge. */
  railAfter: Array<OrderRailStep>;
  /** Order steps before the fan-out region. */
  railBefore: Array<OrderRailStep>;
};

/** ENG-3922 — per-order capability flags (Query.orderCapabilities). */
export type OrderCapabilities = {
  __typename?: 'OrderCapabilities';
  cancel: Scalars['Boolean']['output'];
  close: Scalars['Boolean']['output'];
  /**
   * Comment: the order is readable (the staff baseline makes every order in
   * the organization readable for staff) and clearpath.comments.create.
   */
  comment: Scalars['Boolean']['output'];
  deliver: Scalars['Boolean']['output'];
  forceRelease: Scalars['Boolean']['output'];
  hold: Scalars['Boolean']['output'];
  orderId: Scalars['ID']['output'];
  printSlip: Scalars['Boolean']['output'];
  releaseHold: Scalars['Boolean']['output'];
  seat: Scalars['Boolean']['output'];
  ship: Scalars['Boolean']['output'];
  update: Scalars['Boolean']['output'];
};

/**
 * What an order's money IS — a fact, never a verdict on whether it can be
 * billed. `blockers` answers that separately, and a row can legitimately be both
 * ready and blocked.
 */
export enum OrderChargeState {
  /**
   * Captured, and on no non-void invoice. `billableAmount` is exact.
   *
   * Also covers the residual case where every captured charge has been parked on a
   * statement, written off, credited or refunded: charges exist, none are billable,
   * and none sit on an invoice. `billableAmount` is then 0 and `blockers` carries
   * ON_STATEMENT or NO_BILLABLE_CHARGES to say which.
   */
  CapturedUnbilled = 'CAPTURED_UNBILLED',
  /** On a non-void invoice with nothing left to bill. */
  FullyBilled = 'FULLY_BILLED',
  /**
   * No `billing_events` rows at all. The amount a client shows is the pre-ship
   * ESTIMATE from the pricing snapshot, and must be marked as one.
   *
   * Carries an EMPTY `blockers` list for every lifecycle but `cancelled`, because
   * no refusal on the invoice path is provable from a zero-charge row: capture
   * freezes a price on the spot and nothing in services/pricing gates on
   * `lifecycle_status`. Whether capture produces charges is decided at capture and
   * reported through `GenerateInvoiceSkipReason`. A cancelled case carries
   * ORDER_CANCELLED, which is a rule this screen owns rather than a prediction
   * about the server. `blockers` is still the only authority on whether the row
   * can be selected.
   *
   * ENG-3464 — also a case whose products have no capture yet but which already
   * holds an additive charge (a pre-ship fee): Generate captures the products and
   * bills the fee with them.
   */
  NoneCaptured = 'NONE_CAPTURED',
  /**
   * On a non-void invoice AND still carrying a billable charge. The remaining
   * amount is `billableAmount`; the issued documents are `activeInvoiceIds`.
   */
  PartlyBilled = 'PARTLY_BILLED'
}

export type OrderConnection = {
  __typename?: 'OrderConnection';
  /**
   * ENG-2149 — the LABEL of the intake field this tenant flagged "use as case
   * reference" (`uiSchema['ui:caseReferenceField']` on the default order-intake
   * form), e.g. `Patient name`. Titles the list's case-reference column, whose
   * values are `Order.subjectName`.
   *
   * Shipped inside the connection for the same reason `statusStates` is: it comes
   * from tenant settings, which gates on `clearpath.tenant_settings.read` — a key
   * neither a technician nor a portal customer holds, so a header read from there
   * would be blank for exactly the people reading the list. Null when the tenant
   * has flagged no field, in which case the column keeps its generic heading and
   * `subjectName` falls back to the canonical `first_name`/`last_name` fields.
   */
  caseRefLabel?: Maybe<Scalars['String']['output']>;
  edges: Array<OrderEdge>;
  pageInfo: PageInfo;
  /**
   * ENG-1542 — the tenant's order-status vocabulary, so the list's Status
   * filter offers what this tenant actually authored instead of a hardcoded five.
   *
   * Shipped INSIDE the connection rather than read from `TenantSettings`, which
   * gates on `clearpath.tenant_settings.read` — a key held by admin roles only,
   * so a technician's filter would have come back empty. Whoever may list the
   * orders may read the names of the statuses those orders are in; the labels are
   * already rendered in every row's Status cell. Same pattern as
   * `ScopedKanbanResult.statusStates`, and the same source
   * (`tenant_settings.status_states_json`), so the board, the pipeline editor and
   * this filter cannot disagree.
   */
  statusStates: Array<StatusState>;
  totalCount: Scalars['Int']['output'];
  /**
   * Currently-billable charges in this query's billing-party scope that belong
   * to no order, one bucket per (payer, currency). See UnattachedChargeBucket.
   *
   * Shipped inside the connection for the same reason `statusStates` is: it has no
   * home of its own. It is PARTY-grain, not order-grain, so it cannot be an edge;
   * and it is deliberately not served from `Query.billingEvents`, which clamps
   * `first` to 100, has no search, and pages on `event_at` alone with a strict `<`
   * — so charges tied on a timestamp at a page boundary are unreachable there.
   * Capture writes one row per line in one transaction, which makes those ties
   * routine.
   *
   * Null — "not computed" — when the field is not selected, when the filter names
   * no `billingPartyIds` (there is no payer to bucket by), or when the caller
   * lacks `clearpath.billing.read` + `clearpath.invoices.view`. Empty means the
   * scope genuinely has none.
   */
  unattachedCharges?: Maybe<Array<UnattachedChargeBucket>>;
};

/**
 * A server-stored order-wizard draft, owned by the caller (ENG-1750).
 *
 * The read side of saveOrderDraft: the portal lists the caller's own in-flight
 * drafts and resumes one by id. `payload` is the OPAQUE serialized WizardDraft
 * state the client saved — the server never parses it (validation happens on
 * submitOrder). Every field maps 1:1 to an `order_drafts` row; the read handlers
 * re-stringify `payload` for the AWSJSON scalar and emit the timestamps as ISO
 * 8601. Self-scoped: the queries only ever return rows WHERE user_id = viewer AND
 * organization_id = viewer's org, so there is no separate permission model.
 */
export type OrderDraft = {
  __typename?: 'OrderDraft';
  createdAt: Scalars['AWSDateTime']['output'];
  /** Wizard step the doctor was on — used to resume in the right place. */
  currentStepKey?: Maybe<Scalars['String']['output']>;
  /** Optional scoping hint: the customer party this draft is being submitted for. */
  customerPartyId?: Maybe<Scalars['ID']['output']>;
  id: Scalars['ID']['output'];
  /** Opaque serialized WizardDraft state (same shape saveOrderDraft persisted). */
  payload: Scalars['AWSJSON']['output'];
  updatedAt: Scalars['AWSDateTime']['output'];
};

export type OrderEdge = {
  __typename?: 'OrderEdge';
  cursor: Scalars['String']['output'];
  node: Order;
};

/** Filter for orders. Any combination of fields ANDs together. */
export type OrderFilterInput = {
  /**
   * Orders with billing ACTIVITY. Omitted means no billing filter at all.
   *
   * REPLACED `invoiceEligible`, which is gone. That one answered a yes/no the
   * server does not enforce — it required `lifecycle_status = 'active'` and a
   * frozen pricing snapshot, neither of which any refusal on the invoice path
   * reads — and its "no non-void invoice" arm dropped exactly the invoiced rows
   * the honest model wants listed. The state of an order is now REPORTED, in
   * `Order.billingState`, rather than decided by a filter.
   *
   * Composes with `billingPartyIds` + `includeBillingPartyDescendants`, which is
   * what makes "every order billed anywhere beneath this group" expressible. The
   * measured cost is in
   * docs/qa/generate-invoice-billing-state-performance-2026-09-21.md.
   */
  billingActivity?: InputMaybe<OrderBillingActivityFilter>;
  /** Filter by billing party. */
  billingPartyIds?: InputMaybe<Array<Scalars['ID']['input']>>;
  /** Filter by created date range. */
  createdAt?: InputMaybe<DateRangeInput>;
  /** Filter by tags / custom field equality. */
  customFieldsContain?: InputMaybe<Scalars['AWSJSON']['input']>;
  /** Filter by customer party. */
  customerPartyIds?: InputMaybe<Array<Scalars['ID']['input']>>;
  /** Filter by ETA date range. */
  estimatedCompletionAt?: InputMaybe<DateRangeInput>;
  /**
   * Bible §05:9 — order-group hierarchy filter. ROOTS_ONLY returns parent
   * orders only (parent_order_id IS NULL), powering the OrdersList "Show
   * only ${plural}" toggle. CHILDREN_ONLY returns sub-orders only. ALL is
   * the default (returns every row regardless of position in the tree).
   */
  hierarchy?: InputMaybe<OrderHierarchyFilter>;
  /**
   * ENG-2572 — when true, `billingPartyIds` also matches every party BENEATH
   * the named ones in the external-party hierarchy, so naming a DSO returns its
   * practices' orders too. Defaults to false; staff sessions only (a portal
   * session is already scoped to its own party subtree).
   */
  includeBillingPartyDescendants?: InputMaybe<Scalars['Boolean']['input']>;
  /**
   * Include authorized force-release split orders as independently filtered,
   * sorted and paginated rows. Default false preserves parent-grouped lists.
   */
  includeSplitOrders?: InputMaybe<Scalars['Boolean']['input']>;
  /** Filter by lifecycle status. */
  lifecycleStatus?: InputMaybe<Array<OrderLifecycleStatus>>;
  /** Filter by order type. */
  orderType?: InputMaybe<Array<OrderType>>;
  /** Filter by ordering practitioner (for example, an individual doctor). */
  orderingPractitionerPartyIds?: InputMaybe<Array<Scalars['ID']['input']>>;
  /** Filter by org unit (closure includes descendants). */
  orgUnitIds?: InputMaybe<Array<Scalars['ID']['input']>>;
  /** Substring search across barcode/customer/etc. */
  search?: InputMaybe<Scalars['String']['input']>;
  /** Filter by status. */
  status?: InputMaybe<Array<Scalars['String']['input']>>;
  /** Filter by submitter. */
  submittedByUserIds?: InputMaybe<Array<Scalars['ID']['input']>>;
};

/** Hierarchy filter enum for Query.orders. Default ALL. */
export enum OrderHierarchyFilter {
  All = 'ALL',
  ChildrenOnly = 'CHILDREN_ONLY',
  RootsOnly = 'ROOTS_ONLY'
}

/**
 * A hold on a case or one of its units.
 *
 * Orthogonal to lifecycle: a hold changes NOTHING about `Order.status`,
 * `Order.lifecycleStatus`, or any `StepRun.status`. That is what makes release
 * free of side effects — the step keeps its own status and assignee for the whole
 * duration, so releasing restores flow with no writes to `step_runs` at all.
 */
export type OrderHold = {
  __typename?: 'OrderHold';
  /**
   * Which step the case was sitting at when it was held. Provenance for
   * display ("held at QC") and reporting only — never an input to a state
   * transition.
   */
  blockedStepRunId?: Maybe<Scalars['ID']['output']>;
  createdAt: Scalars['AWSDateTime']['output'];
  /** Configured practitioner approval state; null for ordinary/manual holds. */
  customerApproval?: Maybe<CustomerApprovalRequest>;
  /**
   * Operator's expectation of when the blocker clears. ADVISORY ONLY — it is
   * not an SLA input and never moves `Order.estimatedCompletionAt`. Extending a
   * commitment date is a human decision made through `updateOrder`.
   */
  expectedResolutionAt?: Maybe<Scalars['AWSDateTime']['output']>;
  holdReasonCodeId: Scalars['ID']['output'];
  id: Scalars['ID']['output'];
  note?: Maybe<Scalars['String']['output']>;
  /** Owning case. Always present, including for unit-scoped holds. */
  orderId: Scalars['ID']['output'];
  /** The held unit, or null for a case-scoped hold. */
  orderProductId?: Maybe<Scalars['ID']['output']>;
  organizationId: Scalars['ID']['output'];
  placedBy?: Maybe<User>;
  placedByUserId: Scalars['ID']['output'];
  reasonCode?: Maybe<HoldReasonCode>;
  releaseNote?: Maybe<Scalars['String']['output']>;
  /** Null while the hold is open. */
  releasedAt?: Maybe<Scalars['AWSDateTime']['output']>;
  releasedBy?: Maybe<User>;
  releasedByUserId?: Maybe<Scalars['ID']['output']>;
  /** Derived from `orderProductId`: UNIT when set, ORDER when null. */
  scope: HoldScope;
  startedAt: Scalars['AWSDateTime']['output'];
  updatedAt?: Maybe<Scalars['AWSDateTime']['output']>;
};

/** Payload for placeOrderHold / releaseOrderHold. */
export type OrderHoldPayload = {
  __typename?: 'OrderHoldPayload';
  hold: OrderHold;
  /**
   * The affected case. Re-read so the client can refresh `activeHolds` and the
   * hold badge without a second round-trip.
   */
  order: Order;
};

export type OrderLabelSender = {
  __typename?: 'OrderLabelSender';
  /** Empty unless canOverride. */
  alternatives: Array<LabelSenderChoice>;
  canOverride: Scalars['Boolean']['output'];
  inheritedFromPartyName?: Maybe<Scalars['String']['output']>;
  /** Why an assigned sender is not the one printing. */
  note?: Maybe<Scalars['String']['output']>;
  resolved: LabelSenderChoice;
  source: OrderLabelSenderSource;
};

export enum OrderLabelSenderSource {
  AccountDefault = 'ACCOUNT_DEFAULT',
  PartyDirect = 'PARTY_DIRECT',
  PartyInherited = 'PARTY_INHERITED',
  Site = 'SITE'
}

/** Long-term lifecycle status. */
export enum OrderLifecycleStatus {
  Active = 'ACTIVE',
  Cancelled = 'CANCELLED',
  Closed = 'CLOSED',
  Delivered = 'DELIVERED',
  PendingPayment = 'PENDING_PAYMENT',
  Seated = 'SEATED',
  Shipped = 'SHIPPED',
  WarrantyReturned = 'WARRANTY_RETURNED'
}

/** ENG-1542 — how many Units the order waits for before it continues. */
export enum OrderMergeJoinMode {
  /** Every Unit that resolves to a LIVE pipeline. */
  AllUnits = 'ALL_UNITS',
  /** A quorum: the order continues once N Units finish. */
  CountN = 'COUNT_N'
}

/**
 * ENG-1542 — the convergence at the end of the fan-out region.
 *
 * NOT a node. The `sku_breakout` node owns the join (migration 0328 fused them),
 * so this describes the convergence rather than standing for a step.
 */
export type OrderMergeState = {
  __typename?: 'OrderMergeState';
  /**
   * True when a COUNT_N join declares no usable quorum. NOT collapsed to
   * ALL_UNITS: the engine fails that case closed, so showing a total here would
   * claim the opposite of what happens. Render it as unknown, not as a number.
   */
  indeterminate: Scalars['Boolean']['output'];
  mode: OrderMergeJoinMode;
  /** Units that have finished their pipeline. */
  ready: Scalars['Int']['output'];
  /**
   * Units that must finish. ALL_UNITS takes the barrier's expected-unit count;
   * COUNT_N takes the authored quorum. Null only when `indeterminate`.
   */
  required?: Maybe<Scalars['Int']['output']>;
  /** The order step the branches converge INTO. Null when none follows. */
  resumesIntoStepName?: Maybe<Scalars['String']['output']>;
};

/** ENG-3962 — NAS activity for one order (Query.orderNasActivity). */
export type OrderNasActivity = {
  __typename?: 'OrderNasActivity';
  /** Files on this order that a station registered. */
  files: Array<OrderNasFile>;
  /** Current holdings first, then recently released ones; at most 50. */
  holdings: Array<OrderNasHolding>;
  orderId: Scalars['ID']['output'];
};

/** ENG-3962 — a file version a station received, and when it removed it. */
export type OrderNasDeliveredFile = {
  __typename?: 'OrderNasDeliveredFile';
  deliveredAt: Scalars['AWSDateTime']['output'];
  fileId: Scalars['ID']['output'];
  /** Null while the file is still on the station. */
  removedAt?: Maybe<Scalars['AWSDateTime']['output']>;
  version: Scalars['Int']['output'];
};

/** ENG-3962 — a file on the order that a station registered. */
export type OrderNasFile = {
  __typename?: 'OrderNasFile';
  conflictBaseFileId?: Maybe<Scalars['ID']['output']>;
  conflictBaseVersion?: Maybe<Scalars['Int']['output']>;
  /**
   * True when the station saved this file from an older version, so it was
   * kept as a separate copy.
   */
  conflictCopy: Scalars['Boolean']['output'];
  fileId: Scalars['ID']['output'];
  station: OrderNasStation;
};

/** ENG-3962 — one station's holding of the order. */
export type OrderNasHolding = {
  __typename?: 'OrderNasHolding';
  failingFileCount: Scalars['Int']['output'];
  /** Per-file delivery proof, newest delivery first. */
  files: Array<OrderNasDeliveredFile>;
  heldSince: Scalars['AWSDateTime']['output'];
  /** The station's last acknowledged sync of this order. */
  lastAckedAt?: Maybe<Scalars['AWSDateTime']['output']>;
  /** Earliest time the station may remove the files (REMOVING only). */
  removeAfter?: Maybe<Scalars['AWSDateTime']['output']>;
  /** When the station confirmed removal (REMOVED only). */
  removedAt?: Maybe<Scalars['AWSDateTime']['output']>;
  station: OrderNasStation;
  status: OrderNasHoldingStatus;
};

/** ENG-3962 — lifecycle of a station's holding of an order. */
export enum OrderNasHoldingStatus {
  Active = 'ACTIVE',
  Removed = 'REMOVED',
  Removing = 'REMOVING'
}

/** ENG-3962 — a NAS station (nas_agents row), identified by hostname. */
export type OrderNasStation = {
  __typename?: 'OrderNasStation';
  hostname: Scalars['String']['output'];
  id: Scalars['ID']['output'];
};

/**
 * Order-level pipeline template (Intake → [SKU pipelines] → Assembly → Ship).
 * Per docs/clearpath-architecture/03-data-model.md tier 3 (`order_pipelines`).
 * INTERNAL-ONLY by contract: reachable ONLY via Query.orderPipelines (gated by
 * requireInternalActor); Order.orderPipeline is intentionally unwired. Must NOT be
 * hydrated into any portal/vendor-visible source.
 */
export type OrderPipeline = {
  __typename?: 'OrderPipeline';
  /** Stable template code for this order pipeline. */
  code?: Maybe<Scalars['String']['output']>;
  createdAt: Scalars['AWSDateTime']['output'];
  description?: Maybe<Scalars['String']['output']>;
  id: Scalars['ID']['output'];
  name: Scalars['String']['output'];
  organizationId: Scalars['ID']['output'];
  /** Order-level steps in this pipeline. */
  steps: Array<PipelineStep>;
  tenantId: Scalars['ID']['output'];
  updatedAt?: Maybe<Scalars['AWSDateTime']['output']>;
  /** Template version for this order pipeline. */
  version?: Maybe<Scalars['Int']['output']>;
};

/**
 * ENG-1542 — the collapsed orders-table row's pipeline position.
 *
 * The rail is `order steps -> [one branch per Unit] -> order steps resume`, so
 * `segments` carries at most ONE `UNITS` entry and no entry for the breakout node
 * itself.
 */
export type OrderPipelinePosition = {
  __typename?: 'OrderPipelinePosition';
  /** Distinct step names the Units are actively on, in a stable order. */
  activeStepNames: Array<Scalars['String']['output']>;
  /**
   * The merge denominator, from the barrier's own expected-unit count. NOT
   * `unitCount`: a Unit resolving to no LIVE pipeline is excluded from the quorum
   * (ENG-1503), so a 4-line order can legitimately read `of 3`. Reading
   * `unitCount` here would promise a merge that can never complete.
   */
  expectedUnitCount: Scalars['Int']['output'];
  /** Units that have finished their pipeline and are waiting at the merge. */
  readyToMergeCount: Scalars['Int']['output'];
  /** Ordered left to right, exactly as the bar renders. */
  segments: Array<OrderPipelinePositionSegment>;
  /** Order-pipeline template name, for the `Restorative · v4` chip. */
  templateName?: Maybe<Scalars['String']['output']>;
  /**
   * The PINNED version the order runs (orders.order_pipeline_version_id,
   * migration 0325), not the current live one — an in-flight order must render
   * the rail it is actually executing.
   */
  templateVersion?: Maybe<Scalars['Int']['output']>;
  /** Every Unit on the order, including ones that run no pipeline. */
  unitCount: Scalars['Int']['output'];
};

export type OrderPipelinePositionSegment = {
  __typename?: 'OrderPipelinePositionSegment';
  kind: OrderPositionSegmentKind;
  /** The step's name, or the fan-out region's name (e.g. "Product Pipelines"). */
  label: Scalars['String']['output'];
  state: OrderPositionSegmentState;
};

/** ENG-1542 — what one segment of the pipeline-position bar represents. */
export enum OrderPositionSegmentKind {
  /** A single order-level step on the rail. */
  OrderStep = 'ORDER_STEP',
  /**
   * The per-Unit fan-out region. ONE segment stands in for EVERY Unit, and
   * the `sku_breakout` node that declares the region is never a segment of its
   * own — it is not a step, no operator can claim it, and no step_run is ever
   * created for it (services/api/src/orders/order-rail.ts).
   */
  Units = 'UNITS'
}

/**
 * ENG-1542 — a segment's state. Mirrors the per-Unit step states so one order
 * never shows two vocabularies for the same idea.
 */
export enum OrderPositionSegmentState {
  Active = 'ACTIVE',
  /**
   * An open hold applies. Wins over ACTIVE: a bar reading "in production"
   * while a Unit sits on a hold is the specific thing this surface fixes.
   */
  Blocked = 'BLOCKED',
  Done = 'DONE',
  NotStarted = 'NOT_STARTED'
}

/**
 * Industry-neutral priority levels for orders. Display in OrderDetailPane
 * inline picker. Stored on `orders.custom_fields.priority` until a first-class
 * column is added — keeps existing tenants from needing a migration.
 */
export enum OrderPriority {
  High = 'HIGH',
  Low = 'LOW',
  Normal = 'NORMAL',
  Rush = 'RUSH'
}

/**
 * Line item on an order — each runs its own SKU pipeline.
 * Per docs/clearpath-architecture/03-data-model.md tier 3 (`order_products`).
 */
export type OrderProduct = {
  __typename?: 'OrderProduct';
  /**
   * Per-unit identity barcode. Nullable — the column is nullable and
   * backfills lazily, so historical/unbackfilled lines return null.
   */
  barcodeValue?: Maybe<Scalars['String']['output']>;
  /**
   * The name the customer's pricing agreement gives this line's SKU, or null
   * for the standard productType.name: from the agreement that priced the line,
   * else (not yet priced) the customer's agreements. Vendors and lab staff
   * without clearpath.pricing.read get null. On the line, not on ProductType,
   * because it depends on the order while ProductType is cached by id.
   * Resolved by packages/shared-types/src/contracted-sku-name.ts.
   */
  contractedName?: Maybe<Scalars['String']['output']>;
  createdAt: Scalars['AWSDateTime']['output'];
  currency?: Maybe<Scalars['String']['output']>;
  /**
   * Per-line custom fields (JSONB). Free-form tenant extensions —
   * distinct from `intakeFormResponse` which carries the structured
   * per-product intake answers.
   */
  customFields?: Maybe<Scalars['AWSJSON']['output']>;
  id: Scalars['ID']['output'];
  /**
   * Round 6 follow-up — structured per-line intake form answers keyed
   * by field code. Schema lives on `productType.intakeFormDefinition.schema`
   * (Round 6c). Empty object when the line has no intake form bound. Read
   * by `IntakeFormResponseView` on portal + lab order detail pages.
   */
  intakeFormResponse?: Maybe<Scalars['AWSJSON']['output']>;
  /**
   * ENG-775 — THE PIN (per-line intake). The `form_definition_versions` id
   * that was LIVE for this line's product-type default intake form when the order
   * was submitted. Stamped once at submit and immutable, so `intakeFormResponse`
   * re-renders against the version's schema it was captured on. Null for
   * historical/unpinned lines and SKUs with no default form bound.
   */
  intakeFormVersionId?: Maybe<Scalars['ID']['output']>;
  order?: Maybe<Order>;
  orderId: Scalars['ID']['output'];
  organizationId: Scalars['ID']['output'];
  /** Pipeline run for this line item. */
  pipelineRun?: Maybe<PipelineRun>;
  /** Position in the order line list. */
  position: Scalars['Int']['output'];
  productType?: Maybe<ProductType>;
  productTypeId: Scalars['ID']['output'];
  /** Quantity for this line. */
  quantity: Scalars['Int']['output'];
  /**
   * ENG-1559 — coarse, customer-safe per-Unit lifecycle stage rail (SKU +
   * coarse stage + which is current + reached dates). A pure fold of the single
   * per-Unit derivation (F2); never exposes internal step names, assignees,
   * org-units, routing, raw step counts or hold reasons. Nullable: null when the
   * caller did not derive it (e.g. the single-line Query.orderProduct lookup, or a
   * viewer whose lab hid the Products tab). Returned inline in the orderProducts
   * hydration — no field-resolver binding.
   */
  stageSummary?: Maybe<UnitStageSummary>;
  /** Step runs for this line item. */
  stepRuns: StepRunConnection;
  tenantId: Scalars['ID']['output'];
  /**
   * Each unit's own intake answers when this line groups several units whose
   * answers differ outside the fields their pipeline routes on (e.g. different
   * positions). One element per unit, in unit order. Null when every unit
   * answered the same — `intakeFormResponse` then is every unit's answers.
   */
  unitIntakeFormResponses?: Maybe<Scalars['AWSJSON']['output']>;
  /** Resolved unit price at order creation. */
  unitPrice?: Maybe<Scalars['Float']['output']>;
  updatedAt?: Maybe<Scalars['AWSDateTime']['output']>;
};


/**
 * Line item on an order — each runs its own SKU pipeline.
 * Per docs/clearpath-architecture/03-data-model.md tier 3 (`order_products`).
 */
export type OrderProductStepRunsArgs = {
  filter?: InputMaybe<StepRunFilterInput>;
  page?: InputMaybe<PageInput>;
};

export type OrderProductEdge = {
  __typename?: 'OrderProductEdge';
  cursor: Scalars['String']['output'];
  node: OrderProduct;
};

/** Order product mutation payload. */
export type OrderProductPayload = {
  __typename?: 'OrderProductPayload';
  orderProduct: OrderProduct;
};

/** ENG-1542 — one step on a rail, order-level or per-Unit. */
export type OrderRailStep = {
  __typename?: 'OrderRailStep';
  /**
   * Whoever holds or held this step. Null when never assigned, and null
   * without `clearpath.assignments.read` — the step and its date still render.
   */
  assigneeId?: Maybe<Scalars['ID']['output']>;
  assigneeName?: Maybe<Scalars['String']['output']>;
  /**
   * When the step happened: its completion time, else when it opened. Null
   * when no attempt exists yet.
   *
   * NOT a due date. This ticket cut per-step due dates because none exists
   * anywhere in the schema — `step_runs` has no due/SLA column. Only ORDER-rail
   * steps carry this; a per-Unit branch chip is deliberately undated.
   */
  at?: Maybe<Scalars['AWSDateTime']['output']>;
  /**
   * Display name of that group. A group inside another reads outermost first
   * ("Finishing › Coat").
   */
  groupName?: Maybe<Scalars['String']['output']>;
  /**
   * The group this step runs inside, when its pipeline authored it as a step
   * within another (`pipeline_steps.parent_step_id`, the YAML's `parentCode`).
   * Publishing dissolves a group into its steps, so the group itself is never a
   * rail step; this names it so a surface can draw its steps in one container.
   * Adjacent steps sharing this id belong to one container. Null outside any
   * group.
   */
  groupStepId?: Maybe<Scalars['ID']['output']>;
  name: Scalars['String']['output'];
  /**
   * The department or team this step is ROUTED to
   * (`pipeline_steps.org_unit_id`), not wherever its assignee happens to sit.
   */
  orgUnitId?: Maybe<Scalars['ID']['output']>;
  /**
   * Display label for that crew. A team renders with its department in front
   * ("CAD Department · CAD Team") because a bare team name repeats across
   * departments. NOT gated on `clearpath.assignments.read`: routing says nothing
   * about which person holds the step.
   */
  orgUnitName?: Maybe<Scalars['String']['output']>;
  position: Scalars['Int']['output'];
  /**
   * ENG-2554 - this step was SKIPPED rather than worked.
   *
   * A FLAG BESIDE `state`, not a sixth OrderRailStepState value, and for the same
   * reason `skipped` is an outcome rather than a step_runs status: to every
   * consumer that asks "is this step behind the unit" - the progress count, the
   * frontier, the live-step set, the customer-facing milestone fold - a skipped
   * step IS done, and a new state would have made each of them wrong until it was
   * taught otherwise. `state` therefore stays DONE and only the LABEL differs,
   * which is the same split the rail already makes for READY (assigned reads "not
   * yet started", unassigned reads "unassigned").
   *
   * False for every step that was actually performed, and for every step with no
   * attempt yet.
   */
  skipped: Scalars['Boolean']['output'];
  state: OrderRailStepState;
  /** Stable code in the pinned product pipeline YAML. Null for legacy/order rail steps. */
  stepCode?: Maybe<Scalars['String']['output']>;
  stepDefinitionId: Scalars['ID']['output'];
  /**
   * The LATEST attempt's step_run id, or null when no attempt exists yet.
   *
   * ENG-1550 — what a surface needs to ACT on the step it is showing. Without it
   * the order detail pane fell back to its own legacy "active step" derivation
   * over the whole order, which on a multi-Unit order resolves to a DIFFERENT run
   * than the one the viewer holds: Start and Complete silently vanished (ownership
   * failed against the wrong row) while Report defect, gated on permission alone,
   * stayed.
   */
  stepRunId?: Maybe<Scalars['ID']['output']>;
};

/**
 * ENG-1542 — a step's state on a rail. Same four values the per-Unit
 * derivation uses, so the order rail and the branch rails read alike.
 */
export enum OrderRailStepState {
  /**
   * Cannot proceed: an open hold applies to this step, or its latest attempt
   * FAILED and is waiting on a rework/defect decision. Both are "stopped, needs a
   * human", which is why they share one state rather than reading as in-progress.
   */
  Blocked = 'BLOCKED',
  Done = 'DONE',
  InProgress = 'IN_PROGRESS',
  NotStarted = 'NOT_STARTED',
  /**
   * Assigned and materialized, but nobody has pressed Start work yet.
   *
   * ENG-1550 split this out of IN_PROGRESS. Before, every live non-completed
   * attempt read as in-progress, so a step a lead had merely handed out was
   * indistinguishable from one being worked — "Ready to start" and "In progress"
   * named the same state on every board.
   */
  Ready = 'READY'
}

export type OrderShippingTerms = {
  __typename?: 'OrderShippingTerms';
  /** The outside account's label company name; null on the lab's own account. */
  accountLabelCompanyName?: Maybe<Scalars['String']['output']>;
  /** The outside account's printed sender address; null when it prints the site. */
  accountLabelSenderAddress?: Maybe<ShippingLabelSenderAddress>;
  /**
   * Sites holding this order's lines (custody; in-transit lines excluded), for
   * the label's ship-from suggestion. Set on Order.shippingTerms only.
   */
  custodySites?: Maybe<Array<ShippingCustodySite>>;
  displayName: Scalars['String']['output'];
  shippingAccount?: Maybe<ShippingAccountRef>;
  source: ShippingTermsSource;
  terms: ShippingTerms;
};

export type OrderShippingTermsInput = {
  /** Required exactly when terms is EXTERNAL_ACCOUNT. */
  shippingAccountId?: InputMaybe<Scalars['ID']['input']>;
  terms: ShippingTerms;
};

export enum OrderSortField {
  BarcodeValue = 'BARCODE_VALUE',
  CreatedAt = 'CREATED_AT',
  CustomerName = 'CUSTOMER_NAME',
  EstimatedCompletionAt = 'ESTIMATED_COMPLETION_AT',
  LifecycleStatus = 'LIFECYCLE_STATUS',
  Status = 'STATUS',
  UpdatedAt = 'UPDATED_AT'
}

/** Sort spec for orders. */
export type OrderSortInput = {
  direction: SortDirection;
  field: OrderSortField;
};

/**
 * ENG-3455 — an imported order's references in the source system it was
 * migrated from (e.g. ABS/Evolution). LAB STAFF ONLY: null for portal (customer),
 * vendor and every non-internal session, and null for a natively-created
 * (non-imported) order. `sourceOrderId` is read from the migration crosswalk —
 * the source of truth for the legacy-id <-> ClearPath-id mapping, never a
 * denormalised copy. Distinct from ClearPath's own `Order.id` (a UUID) and its
 * `CYYYY-NNNNNN` case number, so the two reference spaces can never be confused.
 */
export type OrderSourceReference = {
  __typename?: 'OrderSourceReference';
  /**
   * The source system's human-facing case number, e.g. `2024-01847`. Carried
   * on the imported order's migrated custom fields (`legacy_case_number`); null
   * when the import had none. ENG-3185 confirmed the case number is NOT PHI and is
   * safe to expose to staff.
   */
  caseNumber?: Maybe<Scalars['String']['output']>;
  /**
   * The source system's own order identifier — the ABS `Order.id`. Read from
   * migration_crosswalk.legacy_id (entity_kind='order'), which is the source of
   * truth for this mapping.
   */
  sourceOrderId: Scalars['String']['output'];
  /**
   * The source adapter id the order was imported under, e.g.
   * `abs_evolution_history` (migration_crosswalk.source_system).
   */
  sourceSystem: Scalars['String']['output'];
};

/** Order summary projection used by the vendor inbox query. */
export type OrderSummary = {
  __typename?: 'OrderSummary';
  barcodeValue?: Maybe<Scalars['String']['output']>;
  createdAt: Scalars['AWSDateTime']['output'];
  estimatedCompletionAt?: Maybe<Scalars['AWSDateTime']['output']>;
  id: Scalars['ID']['output'];
  lifecycleStatus?: Maybe<Scalars['String']['output']>;
  orderType: Scalars['String']['output'];
  /**
   * How the viewer can see this order: via a share link they issued
   * ("share_link") OR via an incoming Mode A federation link ("federation").
   */
  source: Scalars['String']['output'];
  status: Scalars['String']['output'];
};

/** Order kind. */
export enum OrderType {
  /**
   * A chairside fix to an already-seated case (ENG-1415/ENG-1424). Runs the
   * short adjustment pipeline rather than a full rebuild, and escalates to
   * `REMAKE` when the fix is not enough. Mirrors the `adjustment` value migration
   * 0299 added to the `orders_order_type_check` constraint — without this member
   * every resolver that returns an adjustment order fails to serialize
   * `Order.orderType`, because orderRowToGraphQL upper-cases the column straight
   * into this enum.
   */
  Adjustment = 'ADJUSTMENT',
  CreditOnly = 'CREDIT_ONLY',
  EvaluationOnly = 'EVALUATION_ONLY',
  FederatedIncoming = 'FEDERATED_INCOMING',
  Remake = 'REMAKE',
  Standard = 'STANDARD',
  Warranty = 'WARRANTY'
}

/**
 * ENG-1542 — one Unit's branch through the fan-out region.
 *
 * Carries IDENTITY, not a composed display title: naming the tenant field keys a
 * case reference can live under would put vertical-specific vocabulary in core
 * code (docs/qa/INDUSTRY-NEUTRAL-POLICY.md). Clients compose the title from these
 * fields with their own helpers, degrading to SKU + barcode suffix when no case
 * reference exists — which is common, and is what keeps two Units of the same SKU
 * distinguishable.
 */
export type OrderUnitBranch = {
  __typename?: 'OrderUnitBranch';
  /**
   * Null without `clearpath.assignments.read` — the affordance is removed,
   * the branch is not.
   */
  assigneeId?: Maybe<Scalars['ID']['output']>;
  assigneeName?: Maybe<Scalars['String']['output']>;
  /**
   * `<order barcode>-U01`. The ONLY value that distinguishes same-SKU
   * siblings, so a client must never omit it from a Unit's label.
   */
  barcodeValue?: Maybe<Scalars['String']['output']>;
  /** Hold reason when this Unit is blocked, for a `blocked · furnace` chip. */
  blockedReason?: Maybe<Scalars['String']['output']>;
  /**
   * Catalog-taxonomy name for the SKU's category. Null is COMMON:
   * product_types.product_category_id is an opaque tenant grouping with no FK
   * target, so a tenant that authored no taxonomy has no name to show. Needs a
   * client-side fallback.
   */
  category?: Maybe<Scalars['String']['output']>;
  currentStepName?: Maybe<Scalars['String']['output']>;
  /** The Unit's own custom fields, for composing a title client-side. */
  customFields?: Maybe<Scalars['AWSJSON']['output']>;
  /**
   * False for a Unit running no pipeline — a service line. Such a Unit is
   * neither dropped nor shown as "not started": fan-out skips it and the barrier
   * excludes it from the quorum (ENG-1503), so anything counting Units must be
   * able to tell the difference.
   */
  hasPipeline: Scalars['Boolean']['output'];
  orderProductId: Scalars['ID']['output'];
  /**
   * This Unit's pipeline_run id. Null for a Unit running no pipeline.
   *
   * ENG-1550 — what scopes a step's attempt history to THIS Unit. Same-SKU
   * siblings on one order share a pipeline VERSION and therefore share step
   * definition ids, so a history filtered by definition alone blends two Units.
   */
  pipelineRunId?: Maybe<Scalars['ID']['output']>;
  productName?: Maybe<Scalars['String']['output']>;
  /**
   * How many identical units this line carries. They share ONE pipeline run,
   * so each step on the branch is assigned and completed once for all of them.
   * 1 for a single unit.
   */
  quantity: Scalars['Int']['output'];
  /**
   * ENG-2153 — the fewest work steps this Unit can still be routed through.
   * Follows the pipeline graph, not the roadmap's positions: a decision ahead
   * contributes its cheapest arm, not all of them. Null when it cannot be told —
   * no pipeline, a legacy unpinned run, or the beat between one step completing
   * and the engine activating the next.
   */
  remainingSteps?: Maybe<OrderUnitRemainingSteps>;
  skuCode?: Maybe<Scalars['String']['output']>;
  /**
   * Barcode of the line these units were split off from when some of its
   * identical units failed a check and were reworked through their own pipeline.
   * Null for a line created at submit.
   */
  splitFromBarcodeValue?: Maybe<Scalars['String']['output']>;
  steps: Array<OrderRailStep>;
};

/** ENG-2153 — a floor on the work left on one Unit's route. */
export type OrderUnitRemainingSteps = {
  __typename?: 'OrderUnitRemainingSteps';
  /**
   * No decision lies ahead, so `min` is the route itself. False means a
   * branch is still to be chosen — possibly on a step form not yet filled in — and
   * `min` is a floor ("4+ more steps").
   */
  exact: Scalars['Boolean']['output'];
  /** Work steps not yet started on the cheapest route the graph allows. */
  min: Scalars['Int']['output'];
};

/**
 * One party the viewer can switch to in the portal org-scope switcher.
 * Enum `kind` discriminates DSO / clinic / doctor / delegated-doctor so
 * the UI can render the right affordances (e.g. delegation end-date,
 * DSO children tree). (P0.4.7)
 */
export type OrgScope = {
  __typename?: 'OrgScope';
  /**
   * ENG-1435 — the customer ORGANISATION this scope belongs to: the
   * nearest customer-eligible party at or above `partyId` (itself when the
   * scope already IS the organisation; the parent org when the scope is a
   * practitioner-grain sub-party). Null when no such party is resolvable.
   *
   * Exists because a practitioner-grain scope's `name` is the PERSON, so a
   * shell that labels the workspace with `name` echoes the signed-in user's
   * own name back at them. `customerOrgName` is the label that names the
   * organisation the viewer is working inside.
   */
  customerOrgId?: Maybe<Scalars['ID']['output']>;
  /** Display name of `customerOrgId`. Null whenever `customerOrgId` is. */
  customerOrgName?: Maybe<Scalars['String']['output']>;
  /** For delegated-doctor scopes, the user-id of the acting staff. */
  delegateUserId?: Maybe<Scalars['ID']['output']>;
  /** Expiry, if any — only set for delegated-doctor scopes. */
  expiresAt?: Maybe<Scalars['AWSDateTime']['output']>;
  /**
   * When this scope was granted (delegation granted_at OR party
   * created_at for non-delegated scopes).
   */
  grantedAt?: Maybe<Scalars['AWSDateTime']['output']>;
  kind: OrgScopeKind;
  name: Scalars['String']['output'];
  /** Parent party for tree rendering. Null for DSO root / standalone clinics. */
  parentPartyId?: Maybe<Scalars['ID']['output']>;
  partyId: Scalars['ID']['output'];
};

export enum OrgScopeKind {
  Clinic = 'CLINIC',
  DelegatedDoctor = 'DELEGATED_DOCTOR',
  Doctor = 'DOCTOR',
  Dso = 'DSO'
}

/**
 * Hierarchical org structure (department/team/plant); self-referential.
 * Per docs/clearpath-architecture/03-data-model.md tier 1.
 */
export type OrgUnit = {
  __typename?: 'OrgUnit';
  /**
   * Soft-archive timestamp; null for a live unit. An archived unit is
   * hidden from pickers and from new-work assignment, but KEEPS its
   * permissions scope, its SLA evaluation, and every historical reference —
   * archiving is visibility-only. Units whose history makes hard deletion
   * impossible (role_assignment_audit holds an ON DELETE RESTRICT FK) are
   * archived instead of deleted.
   */
  archivedAt?: Maybe<Scalars['AWSDateTime']['output']>;
  /**
   * Actor who archived this unit; null whenever archivedAt is null.
   * Exposed as an id rather than a User object deliberately — a nested
   * `archivedBy: User` would need its own field resolver, and an unbound
   * nested field resolves to null on AppSync while throwing locally.
   */
  archivedByUserId?: Maybe<Scalars['ID']['output']>;
  /** Direct child org units. */
  children: Array<OrgUnit>;
  createdAt: Scalars['AWSDateTime']['output'];
  /** Custom-fields blob (tenant-defined). */
  customFields?: Maybe<Scalars['AWSJSON']['output']>;
  /** All descendants via the closure table. */
  descendants: Array<OrgUnit>;
  id: Scalars['ID']['output'];
  /**
   * The people who WORK in this unit: active internal users who are members
   * of it by department membership (org_unit_memberships(), db/migrations/0574,
   * ENG-3920) — an unexpired role assignment bound to this unit, to a unit above
   * it (a department's manager is a member of its teams) or to a unit below it
   * (a team's crew are members of their department). A whole-lab assignment
   * (orgUnitId null) makes nobody a member of anything (see UserRole). The same
   * membership decides who is an assignment candidate for a step pinned to this
   * unit. Powers the /admin/departments member list and the step assignee
   * pickers (capped at 200). `search` is case-insensitive substring match
   * against fullName / email.
   *
   * Unaffected by archivedAt on either end: archiving is visibility-only and
   * members keep their permissions, so an archived unit still reports its
   * members (the unit detail page needs them to explain why it cannot be
   * deleted) and an archived unit in the closure still confers membership.
   */
  members: Array<User>;
  /** Human-readable name. */
  name: Scalars['String']['output'];
  /** Owning Organization. */
  organizationId: Scalars['ID']['output'];
  parent?: Maybe<OrgUnit>;
  /** Parent org unit (null for roots). */
  parentOrgUnitId?: Maybe<Scalars['ID']['output']>;
  /** Short slug used in URLs/UI. */
  slug: Scalars['String']['output'];
  /** Type discriminator (department, team, plant). */
  unitType: OrgUnitType;
  updatedAt?: Maybe<Scalars['AWSDateTime']['output']>;
  /**
   * Active user_role rows directly scoped to this org_unit (not the
   * closure — only direct bindings): the people placed in this unit, each with
   * their one role. A whole-lab assignment is never listed here. Used by
   * /admin/departments to render the per-member Remove button (revokeRole takes a
   * user_role id).
   */
  userRoles: Array<UserRole>;
};


/**
 * Hierarchical org structure (department/team/plant); self-referential.
 * Per docs/clearpath-architecture/03-data-model.md tier 1.
 */
export type OrgUnitMembersArgs = {
  search?: InputMaybe<Scalars['String']['input']>;
};

/** Mutation result for deleteOrgUnit. */
export type OrgUnitDeletedPayload = {
  __typename?: 'OrgUnitDeletedPayload';
  deletedId: Scalars['ID']['output'];
};

/** Which disposal action the Teams UI should offer for an org unit. */
export enum OrgUnitDeletionOutcome {
  /**
   * No LIVE dependents, but permanent history (role_assignment_audit's ON
   * DELETE RESTRICT FK) makes hard deletion impossible forever. Offer
   * archiveOrgUnit instead.
   */
  Archive = 'ARCHIVE',
  /**
   * LIVE dependents remain. Neither action is available until the admin
   * clears them — see archiveBlockers.
   */
  Blocked = 'BLOCKED',
  /** No references at all — deleteOrgUnit will succeed. */
  Delete = 'DELETE'
}

/**
 * Preflight for the Teams delete/archive confirm dialog. Computed from the
 * same two censuses the mutations enforce, so the dialog can never promise an
 * outcome the mutation will refuse.
 *
 * The two blocker lists answer different questions and are NOT subsets of one
 * another. deleteBlockers is FK-driven: any referencing row blocks, history
 * included, because Postgres says so. archiveBlockers is semantics-driven:
 * archiving is an UPDATE of a non-key column, so no FK is consulted and only
 * LIVE work blocks. The activity trail appears in deleteBlockers and never in
 * archiveBlockers — it can never be cleared.
 */
export type OrgUnitDeletionPreflight = {
  __typename?: 'OrgUnitDeletionPreflight';
  /**
   * Human-readable LIVE dependents preventing BOTH actions. Empty unless
   * outcome is BLOCKED.
   */
  archiveBlockers: Array<Scalars['String']['output']>;
  /**
   * Human-readable references preventing hard deletion. Populated when
   * outcome is ARCHIVE (and when BLOCKED) so the dialog can explain why
   * deletion is off the table.
   */
  deleteBlockers: Array<Scalars['String']['output']>;
  outcome: OrgUnitDeletionOutcome;
};

/** Mutation result for upsertOrgUnit. */
export type OrgUnitPayload = {
  __typename?: 'OrgUnitPayload';
  orgUnit: OrgUnit;
};

/** Discriminator for OrgUnit kind. */
export enum OrgUnitType {
  Department = 'DEPARTMENT',
  Division = 'DIVISION',
  Other = 'OTHER',
  Plant = 'PLANT',
  /**
   * A physical site — an org_unit extended 1:1 by the `sites` table
   * (unit_type='site', migration 0271). A site is the routing origin ClearPath
   * Light stamps on intake; me.roleAssignments[].orgUnit.unitType surfaces this so
   * a scoped intake operator's origin site can be resolved.
   */
  Site = 'SITE',
  Team = 'TEAM'
}

/**
 * Tenant-level container for one logical business entity (e.g. SKDLA Lab).
 * Per docs/clearpath-architecture/03-data-model.md tier 1.
 */
export type Organization = {
  __typename?: 'Organization';
  createdAt: Scalars['AWSDateTime']['output'];
  /** ISO 4217 currency for billing on this Org. */
  defaultCurrency?: Maybe<Scalars['String']['output']>;
  /** Tenant-defined locale for default labels (e.g. en-US). */
  defaultLocale?: Maybe<Scalars['String']['output']>;
  /** Org UUID. */
  id: Scalars['ID']['output'];
  /** Human-readable Org name. */
  name: Scalars['String']['output'];
  /** Top-level org units belonging to this Organization. */
  orgUnits: Array<OrgUnit>;
  /** Per-Org settings blob (branding, business calendar, defaults). */
  settings?: Maybe<Scalars['AWSJSON']['output']>;
  /** Short slug used in URLs/UI. */
  slug: Scalars['String']['output'];
  /** Tenant this Org belongs to. */
  tenantId: Scalars['ID']['output'];
  updatedAt?: Maybe<Scalars['AWSDateTime']['output']>;
};

/** Fixed V1 lifetimes for a copied Orovia guest-preview bearer link. */
export enum OroviaGuestPreviewExpiry {
  FifteenMinutes = 'FIFTEEN_MINUTES',
  OneHour = 'ONE_HOUR',
  ThirtyMinutes = 'THIRTY_MINUTES',
  TwoHours = 'TWO_HOURS'
}

/** Metadata only. The bearer token is never returned by a read query. */
export type OroviaGuestPreviewLink = {
  __typename?: 'OroviaGuestPreviewLink';
  createdAt: Scalars['AWSDateTime']['output'];
  expiresAt: Scalars['AWSDateTime']['output'];
  fileId: Scalars['ID']['output'];
  firstOpenedAt?: Maybe<Scalars['AWSDateTime']['output']>;
  id: Scalars['ID']['output'];
  lastOpenedAt?: Maybe<Scalars['AWSDateTime']['output']>;
  openCount: Scalars['Int']['output'];
  revokedAt?: Maybe<Scalars['AWSDateTime']['output']>;
};

/** Bounded, non-sensitive Orovia preview failure reason. */
export enum OroviaPreviewErrorCode {
  FeatureDisabled = 'FEATURE_DISABLED',
  HashMismatch = 'HASH_MISMATCH',
  IdempotencyConflict = 'IDEMPOTENCY_CONFLICT',
  IncompatibleProvider = 'INCOMPATIBLE_PROVIDER',
  LaunchExpired = 'LAUNCH_EXPIRED',
  LaunchInvalid = 'LAUNCH_INVALID',
  MissingContentHash = 'MISSING_CONTENT_HASH',
  NotOrderFile = 'NOT_ORDER_FILE',
  PayloadTooLarge = 'PAYLOAD_TOO_LARGE',
  ProviderUnavailable = 'PROVIDER_UNAVAILABLE',
  RateLimited = 'RATE_LIMITED',
  ScanNotClean = 'SCAN_NOT_CLEAN',
  SourceChanged = 'SOURCE_CHANGED',
  SourceUnavailable = 'SOURCE_UNAVAILABLE',
  UnsupportedMedia = 'UNSUPPORTED_MEDIA',
  UploadFailed = 'UPLOAD_FAILED',
  UploadIncomplete = 'UPLOAD_INCOMPLETE'
}

/** Safe Portal projection. Launch fields exist only for a fresh one-time READY response. */
export type OroviaPreviewPayload = {
  __typename?: 'OroviaPreviewPayload';
  errorCode?: Maybe<OroviaPreviewErrorCode>;
  launchExpiresAt?: Maybe<Scalars['AWSDateTime']['output']>;
  launchUrl?: Maybe<Scalars['AWSURL']['output']>;
  retryAfterSeconds?: Maybe<Scalars['Int']['output']>;
  status: OroviaPreviewStatus;
};

/** Customer-safe Orovia preview preparation state. */
export enum OroviaPreviewStatus {
  PermanentFailed = 'PERMANENT_FAILED',
  Preparing = 'PREPARING',
  Ready = 'READY',
  RetryableFailed = 'RETRYABLE_FAILED'
}

export type OroviaReviewCommentReference = {
  __typename?: 'OroviaReviewCommentReference';
  annotationIds: Array<Scalars['ID']['output']>;
  createdCount: Scalars['Int']['output'];
  deletedCount: Scalars['Int']['output'];
  fileId: Scalars['ID']['output'];
  orderId: Scalars['ID']['output'];
  path: Scalars['String']['output'];
  revisionId: Scalars['ID']['output'];
  updatedCount: Scalars['Int']['output'];
};

export enum OroviaReviewMode {
  Edit = 'EDIT',
  HistoricalReadOnly = 'HISTORICAL_READ_ONLY'
}

/** Relay-style page info for cursor pagination. */
export type PageInfo = {
  __typename?: 'PageInfo';
  /** Opaque cursor pointing to the last edge in this page. */
  endCursor?: Maybe<Scalars['String']['output']>;
  /** True if more pages exist after this one. */
  hasNextPage: Scalars['Boolean']['output'];
  /** True if pages exist before this one. */
  hasPreviousPage: Scalars['Boolean']['output'];
  /** Opaque cursor pointing to the first edge in this page. */
  startCursor?: Maybe<Scalars['String']['output']>;
};

/**
 * Relay-style cursor pagination input. `offset` is the offset-mode escape
 * hatch for admin/forensic surfaces that page numerically (e.g. SLA evaluations
 * drill-down, MOL-640). Connections that use cursor pagination ignore it.
 */
export type PageInput = {
  after?: InputMaybe<Scalars['String']['input']>;
  before?: InputMaybe<Scalars['String']['input']>;
  first?: InputMaybe<Scalars['Int']['input']>;
  last?: InputMaybe<Scalars['Int']['input']>;
  offset?: InputMaybe<Scalars['Int']['input']>;
};

/** Party pricing agreement kind. */
export enum PartyAgreementType {
  FixedFee = 'FIXED_FEE',
  Free = 'FREE',
  PercentDiscount = 'PERCENT_DISCOUNT',
  PriceOverride = 'PRICE_OVERRIDE',
  VolumeTier = 'VOLUME_TIER'
}

/**
 * ENG-623 — a customer's resolved catalog access.
 *
 * There is no separate restricted/unrestricted switch: a party with no grant
 * reaching it (directly, or from an ancestor with PARTY_AND_DESCENDANTS scope) is
 * entitled to nothing. See db/migrations/0187.
 */
export type PartyCatalogAccess = {
  __typename?: 'PartyCatalogAccess';
  /** Distinct product types this party may order, own + inherited. */
  entitledProductTypeIds: Array<Scalars['ID']['output']>;
  grants: Array<PartyCatalogGrant>;
  partyId: Scalars['ID']['output'];
};

/**
 * ENG-623 — the party's access after the change, so the client can re-render
 * without a refetch.
 */
export type PartyCatalogAccessPayload = {
  __typename?: 'PartyCatalogAccessPayload';
  catalogAccess: PartyCatalogAccess;
};

/**
 * ENG-623 — one entry on a customer's product allowlist.
 *
 * Stored once against the party an admin chose. When `scope` is
 * PARTY_AND_DESCENDANTS it reaches every descendant party, resolved at read time
 * — so a practice added AFTER the grant inherits it with no new row.
 */
export type PartyCatalogGrant = {
  __typename?: 'PartyCatalogGrant';
  id: Scalars['ID']['output'];
  /**
   * True when this grant lives on an ancestor rather than on the party being
   * asked about. Inherited grants are read-only where they surface — they must be
   * removed at the party that owns them.
   */
  inherited: Scalars['Boolean']['output'];
  note?: Maybe<Scalars['String']['output']>;
  /** The party the grant was made AGAINST — the DSO, for an inherited grant. */
  partyId: Scalars['ID']['output'];
  /**
   * Null when the caller cannot see that party (a portal customer resolving an
   * ancestor's grant). Presentational only.
   */
  partyName?: Maybe<Scalars['String']['output']>;
  productType?: Maybe<ProductType>;
  productTypeId: Scalars['ID']['output'];
  /**
   * Denormalized for display — an admin table must show a product name, not a
   * UUID. Null only if the SKU was hard-deleted, which the FK prevents.
   */
  productTypeName?: Maybe<Scalars['String']['output']>;
  scope: PartyCatalogGrantScope;
  /**
   * Grant start, rendered through the same server instant clamp as
   * ProductTypeAccessDirectGrant: `0001-01-01T00:00:00.000Z` (stored -infinity / at or
   * before 0001-01-01T00:00:00.000Z) and `9999-12-31T23:59:59.999Z` (stored +infinity /
   * at or after 9999-12-31T23:59:59.999Z) are clamp sentinels for an unbounded or
   * out-of-range start, not literal dates.
   */
  validFrom: Scalars['AWSDateTime']['output'];
  /**
   * Null = open-ended (including a stored +infinity). An expiring grant only ever
   * NARROWS access. `9999-12-31T23:59:59.999Z` (out-of-range future) and
   * `0001-01-01T00:00:00.000Z` (stored -infinity) are clamp sentinels, not literal dates.
   */
  validUntil?: Maybe<Scalars['AWSDateTime']['output']>;
};

export enum PartyCatalogGrantScope {
  /** Applies to this party and every descendant, including ones created later. */
  PartyAndDescendants = 'PARTY_AND_DESCENDANTS',
  /** Applies to this party alone. */
  PartyOnly = 'PARTY_ONLY'
}

/** A party's ledger class assignment. */
export type PartyLedgerClass = {
  __typename?: 'PartyLedgerClass';
  classCode: Scalars['String']['output'];
  classId: Scalars['ID']['output'];
  className: Scalars['String']['output'];
  partyId: Scalars['ID']['output'];
};

/**
 * ENG-658 — an order-level intake form assigned to a party, optionally
 * reaching its descendants. Precedence, not union: when resolving a party's
 * order-level form, a direct assignment beats one inherited from an ancestor,
 * and either beats falling back to TenantSettings.orderIntakeFormDefinition.
 * Deliberately NOT modeled on PartyCatalogGrant's resolution (that one unions
 * direct + inherited with no ranking) — see the migration's own header for why.
 */
export type PartyOrderFormAssignment = {
  __typename?: 'PartyOrderFormAssignment';
  formDefinition?: Maybe<FormDefinition>;
  formDefinitionId: Scalars['ID']['output'];
  id: Scalars['ID']['output'];
  /**
   * True when this assignment lives on an ancestor rather than on the
   * party being asked about. Read-only where it surfaces as inherited — it
   * must be changed at the party that owns it.
   */
  inherited: Scalars['Boolean']['output'];
  /**
   * The party the assignment was made AGAINST — the DSO, for an inherited
   * assignment reaching a practice.
   */
  partyId: Scalars['ID']['output'];
  /**
   * Null when the caller cannot see that party (a portal customer
   * resolving an ancestor's assignment). Presentational only.
   */
  partyName?: Maybe<Scalars['String']['output']>;
  scope: PartyCatalogGrantScope;
};

/** ENG-658 — the party's order-form assignment after the change. */
export type PartyOrderFormAssignmentPayload = {
  __typename?: 'PartyOrderFormAssignmentPayload';
  orderFormAssignment?: Maybe<PartyOrderFormAssignment>;
  party: ExternalParty;
};

/**
 * Negotiated pricing arrangement with a party.
 * Per docs/clearpath-architecture/03-data-model.md tier 7 (`party_pricing_agreements`).
 */
export type PartyPricingAgreement = {
  __typename?: 'PartyPricingAgreement';
  /** Discriminator (override, discount, volume_tier). */
  agreementType: PartyAgreementType;
  createdAt: Scalars['AWSDateTime']['output'];
  id: Scalars['ID']['output'];
  isActive: Scalars['Boolean']['output'];
  lines: Array<PartyPricingLine>;
  organizationId: Scalars['ID']['output'];
  party?: Maybe<ExternalParty>;
  partyId: Scalars['ID']['output'];
  /** How conflicts resolve (per_line, party_first, sku_first). */
  resolutionMode: PricingResolutionMode;
  /** Scope JSONB (which SKUs / categories). */
  scope?: Maybe<Scalars['AWSJSON']['output']>;
  tenantId: Scalars['ID']['output'];
  updatedAt?: Maybe<Scalars['AWSDateTime']['output']>;
  validFrom: Scalars['AWSDateTime']['output'];
  /**
   * ENG-2478. The calendar day valid_from was anchored from, interpreted in
   * the organization's CURRENT timezone. No anchoring zone is stored alongside
   * it, so the day is always read against the organization's timezone as it
   * stands now. Null on a legacy row
   * (written before this field existed, or where only an instant was supplied)
   * — validFrom's own UTC calendar day is then the correct read, unchanged from
   * behaviour before this field.
   */
  validFromDay?: Maybe<Scalars['AWSDate']['output']>;
  validUntil?: Maybe<Scalars['AWSDateTime']['output']>;
  /**
   * ENG-2478. The calendar day valid_until was anchored from, interpreted in
   * the organization's CURRENT timezone like validFromDay. The FALLBACK differs,
   * because the two bounds are encoded differently: validUntil is the EXCLUSIVE
   * start of the day after this one, so on a legacy row the day to read is the
   * UTC calendar day of (validUntil - 1ms), not validUntil's own — rendering the
   * instant's own day reads one day late. Null on a legacy row (written before
   * this field existed, or where only an instant was supplied).
   */
  validUntilDay?: Maybe<Scalars['AWSDate']['output']>;
};

export type PartyPricingAgreementPayload = {
  __typename?: 'PartyPricingAgreementPayload';
  agreement: PartyPricingAgreement;
};

/**
 * Pricing line under an agreement.
 * Per docs/clearpath-architecture/03-data-model.md tier 7 (`party_pricing_lines`).
 */
export type PartyPricingLine = {
  __typename?: 'PartyPricingLine';
  agreement?: Maybe<PartyPricingAgreement>;
  agreementId: Scalars['ID']['output'];
  /**
   * The name this agreement gives the SKU on the customer's invoices and
   * portal. Null = the standard product name. Never set on an all-product line.
   */
  contractedName?: Maybe<Scalars['String']['output']>;
  /** Discount percentage (0-100). */
  discountPercent?: Maybe<Scalars['Float']['output']>;
  id: Scalars['ID']['output'];
  /** Override price. */
  overridePrice?: Maybe<Scalars['Float']['output']>;
  position: Scalars['Int']['output'];
  productCategoryId?: Maybe<Scalars['ID']['output']>;
  productType?: Maybe<ProductType>;
  productTypeId?: Maybe<Scalars['ID']['output']>;
  /**
   * ENG-2564 — the canonical SKU-variant token this line prices and names
   * (@clearpath/form-rules computeVariantKey). Null = the SKU-level line, which
   * applies to every variant as the fallback. Parse it back into per-field
   * selections for the editor; it is never free-form.
   */
  variantKey?: Maybe<Scalars['String']['output']>;
  /** Volume tier definitions (JSONB). */
  volumeTiers?: Maybe<Scalars['AWSJSON']['output']>;
};

/**
 * ENG-3054 — the two kinds of root customer, computed from the party tree when
 * read (never stored). A root is a customer-eligible party with no customer-eligible
 * parent in the same organization: a party with no parent, a party whose parent is
 * not customer-eligible (for example a vendor), or a party whose parent is in
 * another organization. PARENT: a root with at least one direct child that is
 * customer-eligible and in the same organization, active or not. INDEPENDENT: a
 * root with no such child. Children of a non-customer type never make a root a
 * PARENT.
 */
export enum PartyRootKind {
  Independent = 'INDEPENDENT',
  Parent = 'PARENT'
}

export type PartySenderIdentity = {
  __typename?: 'PartySenderIdentity';
  /** The customer's own sender, or null when it inherits. */
  direct?: Maybe<ShippingSenderIdentity>;
  /** The sender its labels print, or null when the account default or the site prints. */
  effective?: Maybe<ShippingSenderIdentity>;
  /** Why the nearest assigned sender does not print (archived, another account). */
  effectiveNote?: Maybe<Scalars['String']['output']>;
  /** The account the customer effectively ships on; null = the lab's own. */
  effectiveShippingAccountId?: Maybe<Scalars['ID']['output']>;
  inheritedFromPartyName?: Maybe<Scalars['String']['output']>;
  /** Active senders on effectiveShippingAccountId. */
  options: Array<ShippingSenderIdentity>;
  partyId: Scalars['ID']['output'];
};

export type PartyShippingTerms = {
  __typename?: 'PartyShippingTerms';
  /** The party's own active terms, or null when it inherits. */
  direct?: Maybe<ShippingTermsChoice>;
  effective: ResolvedShippingTerms;
  partyId: Scalars['ID']['output'];
};

/**
 * Materialized volume tracking per party/product/window.
 * Per docs/clearpath-architecture/03-data-model.md tier 7 (`party_volume_counters`).
 */
export type PartyVolumeCounter = {
  __typename?: 'PartyVolumeCounter';
  amountCounted: Scalars['Float']['output'];
  id: Scalars['ID']['output'];
  organizationId: Scalars['ID']['output'];
  partyId: Scalars['ID']['output'];
  productTypeId?: Maybe<Scalars['ID']['output']>;
  tenantId: Scalars['ID']['output'];
  unitsCounted: Scalars['Int']['output'];
  updatedAt?: Maybe<Scalars['AWSDateTime']['output']>;
  windowEnd: Scalars['AWSDateTime']['output'];
  windowStart: Scalars['AWSDateTime']['output'];
};

export type PauseNasRolloutInput = {
  id: Scalars['ID']['input'];
};

export type PaymentActivityConnection = {
  __typename?: 'PaymentActivityConnection';
  edges: Array<PaymentActivityEdge>;
  pageInfo: PageInfo;
  /**
   * Total lifecycles matching the filter, BEFORE the cursor and the page limit.
   * Computed in the same statement as the page, so it cannot disagree with the
   * edges the way `Query.invoices` once did.
   */
  totalCount: Scalars['Int']['output'];
};

export type PaymentActivityEdge = {
  __typename?: 'PaymentActivityEdge';
  cursor: Scalars['String']['output'];
  node: PaymentActivityEntry;
};

/**
 * One payment lifecycle, collapsed to its newest row, with the context an
 * operator needs to decide whether to act on it.
 *
 * ⚠️ ONE ENTRY IS ONE PAYMENT, NOT ONE ROW. `payment_attempts` is append-only, so
 * a single ACH payment writes a sequence: `pending` before the provider call,
 * `succeeded` when Basys accepts the entry, then `settled` days later — or
 * `returned`, with a NACHA code, days or weeks later. `id` is the lifecycle ROOT
 * (`COALESCE(root_attempt_id, id)`), computed by the one shared current-state
 * query (`@clearpath/shared-types/payment-attempt-state`), which is also what the
 * double-debit guard, reconciliation and dunning read. Listing the rows raw would
 * show one payment as two entries with one stuck `pending` forever; that was the
 * ENG-1988 defect.
 */
export type PaymentActivityEntry = {
  __typename?: 'PaymentActivityEntry';
  /**
   * The NEWEST row of the lifecycle. Its `id` is the ROOT id, not the newest
   * row's own id, and `rootAttemptId` is therefore null — the same identity
   * `Invoice.paymentAttempts` presents, so the two surfaces agree about what an
   * attempt is called. The newest row's own id is on the timeline in
   * `Query.paymentLifecycle`.
   */
  attempt: PaymentAttempt;
  billingPartyId?: Maybe<Scalars['ID']['output']>;
  billingPartyName?: Maybe<Scalars['String']['output']>;
  /** The lifecycle root id. Pass it to `Query.paymentLifecycle`. */
  id: Scalars['ID']['output'];
  invoiceNumber?: Maybe<Scalars['String']['output']>;
  /**
   * Null when the payment is not attached to an invoice (a statement-level or
   * manual charge), which is also why the joins behind this are all LEFT: a
   * payment whose invoice was later voided is still a payment to show.
   */
  invoiceStatus?: Maybe<InvoiceStatus>;
  /**
   * True when the SYSTEM originated this lifecycle to re-present a returned
   * entry, rather than a payer clicking Pay again
   * (`payment_attempts.represents_attempt_id`, db/migrations/0376).
   */
  isRepresentment: Scalars['Boolean']['output'];
  /**
   * Whether we actually KNOW what happened to this payment.
   *
   * ⚠️ READ THIS BEFORE TREATING A `FAILED` LIFECYCLE AS A DECLINE. `UNKNOWN`
   * means the provider call THREW rather than answered: a socket timeout AFTER
   * the bank accepted the debit and a request that never left arrive at the
   * writer as the same rejection, and it books the one that means "definitely
   * nothing outstanding". The money may have left the payer, the reservation is
   * still held, and the invoice is unpayable until a human closes it out. It is
   * the single most consequential row class on this surface.
   *
   * SERVER-COMPUTED, and that is deliberate rather than convenient. The
   * discriminator is three columns — status, an EMPTY `provider_attempt_id`, and
   * `error_code = 'provider_exception'` — and a client CANNOT re-derive it from
   * this payload: `providerReference` is projected as `null` when the column is
   * the empty string (handlers/billing/shared.ts), and the status arrives as the
   * SDL enum rather than the column value. A client that called the shared
   * `isUnknownProviderOutcome` with a wire row would get `false` for every row,
   * every time, and this class would render as an ordinary decline. Same reason
   * `returnDisposition` is computed here: one definition, on the server.
   */
  outcomeCertainty: PaymentOutcomeCertainty;
  /**
   * The disposition of the return, when the lifecycle is `RETURNED`.
   * `NOT_RETURNED` otherwise — this is deliberately not nullable, because "no
   * disposition" and "we could not classify the code" are different answers and
   * the second one is `UNKNOWN`.
   */
  returnDisposition: PaymentReturnDisposition;
};

/**
 * Filter for `Query.paymentActivity`.
 *
 * Every field is ANDed. An empty list is treated as "not filtered on this field"
 * rather than "match nothing", matching `Query.invoices`.
 */
export type PaymentActivityFilterInput = {
  amount?: InputMaybe<FloatRangeInput>;
  /** Bounds on the newest row's `attempted_at`. */
  attemptedAt?: InputMaybe<DateRangeInput>;
  /**
   * Provider/adapter error codes on a failed lifecycle. `provider_exception`
   * is the one that means the outcome is UNKNOWN rather than failed.
   */
  errorCodes?: InputMaybe<Array<Scalars['String']['input']>>;
  /**
   * Payments whose INVOICE carries at least one `billing_events` row for these
   * orders. A payment reaches an order only THROUGH its invoice — there is no
   * direct link — so this is the same join `Query.invoices.orderIds` uses.
   */
  orderIds?: InputMaybe<Array<Scalars['ID']['input']>>;
  /**
   * The rail the debit actually went out on
   * (`payment_attempts.provider_method_type`, stamped at charge time). Null on
   * rows written before db/migrations/0370, which means "the rail was not
   * recorded" and NOT "card".
   */
  rails?: InputMaybe<Array<PaymentMethodType>>;
  /**
   * NACHA return-reason codes (`R01`, `R02`, …), or the literal `unknown` for a
   * return the provider reported with no readable code.
   */
  returnCodes?: InputMaybe<Array<Scalars['String']['input']>>;
  /**
   * Invoice number or billing-party name, case-insensitive substring. `%` and
   * `_` in the needle are matched literally.
   */
  search?: InputMaybe<Scalars['String']['input']>;
  /**
   * Bounds on `settled_at` — when an ACH debit actually CLEARED. Null on every
   * unsettled lifecycle and on every card payment, so any bound here excludes
   * those entirely.
   */
  settledAt?: InputMaybe<DateRangeInput>;
  /**
   * Statuses of the COLLAPSED lifecycle, not of any individual row. Filtering
   * on `PENDING` therefore means "this payment is currently pending", not "this
   * payment was ever pending" — every payment was.
   */
  statuses?: InputMaybe<Array<PaymentAttemptStatus>>;
};

/**
 * Every charge attempt.
 * Per docs/clearpath-architecture/03-data-model.md tier 7 (`payment_attempts`).
 */
export type PaymentAttempt = {
  __typename?: 'PaymentAttempt';
  amount: Scalars['Float']['output'];
  attemptedAt: Scalars['AWSDateTime']['output'];
  currency: Scalars['String']['output'];
  failureCode?: Maybe<Scalars['String']['output']>;
  failureMessage?: Maybe<Scalars['String']['output']>;
  /**
   * Total taken from the payer (`amount` + `surchargeAmount`). Null when the
   * provider reported no breakdown (ACH, pre-0561 rows, other providers) or no
   * charge has been recorded. Populated by `Invoice.paymentAttempts` only.
   */
  grossAmount?: Maybe<Scalars['Float']['output']>;
  id: Scalars['ID']['output'];
  invoiceId?: Maybe<Scalars['ID']['output']>;
  organizationId: Scalars['ID']['output'];
  paymentMethodId?: Maybe<Scalars['ID']['output']>;
  /**
   * Which rail this debit actually went out on, stamped at charge time from
   * the payment method used (`payment_attempts.provider_method_type`,
   * db/migrations/0370).
   *
   * Read it before choosing what SUCCEEDED means to a payer. On CARD, SUCCEEDED is
   * money received. On ACH it is only "the provider accepted the entry" — the
   * funds clear 2-5 business days later and can still be returned. A client that
   * assumes one rail tells the other rail's payers the wrong thing about their own
   * money.
   *
   * Null on rows written before 0370. That means "the rail was not recorded", NOT
   * "card" — though in practice no historical row can be an ACH debit, because ACH
   * charging did not work at all before this ticket (the charge body omitted
   * `payment_method_type` and BASYS rejected it).
   */
  providerMethodType?: Maybe<PaymentMethodType>;
  providerReference?: Maybe<Scalars['String']['output']>;
  /**
   * ENG-3881 — base amount given back by refunds of this attempt's charges
   * (each refund row's amount; excess-surcharge corrections of amount 0 excluded).
   * 0 when none; null when no charge has been recorded. Populated by
   * `Invoice.paymentAttempts` and the payments console activity list
   * (`PaymentActivityEntry.attempt`).
   */
  refundedAmount?: Maybe<Scalars['Float']['output']>;
  /**
   * NACHA return-reason code when `status` is RETURNED (`R01`, `R02`, …), or
   * `unknown` when the provider reported a return with no readable code. Null on
   * every other status.
   */
  returnCode?: Maybe<Scalars['String']['output']>;
  /**
   * The first attempt row of this lifecycle.
   *
   * `payment_attempts` is append-only, so one logical attempt is a SEQUENCE of
   * rows and `rootAttemptId` is what links them (db/migrations/0370). Null means
   * THIS is the root — which is the case for every attempt returned by
   * `Invoice.paymentAttempts`, since that field already collapses each lifecycle
   * to one entry keyed by its root. Null on all rows written before 0370; the
   * pairing was never recorded and cannot be reconstructed, so historical attempts
   * read as independent roots.
   */
  rootAttemptId?: Maybe<Scalars['ID']['output']>;
  /**
   * When an ACH debit actually CLEARED. Distinct from `attemptedAt`, which is
   * when the provider accepted the entry — for ACH the two are 2-5 business days
   * apart. Null until settlement, and null for card.
   */
  settledAt?: Maybe<Scalars['AWSDateTime']['output']>;
  status: PaymentAttemptStatus;
  /**
   * Card surcharge the provider took ON TOP of `amount` (ENG-3881), summed
   * over this attempt's charge transactions, less any excess-surcharge correction
   * (a refund row of amount 0). `amount` stays the base applied to
   * the invoice. 0 when none was taken; null when no charge has been recorded.
   * Populated by `Invoice.paymentAttempts` only.
   */
  surchargeAmount?: Maybe<Scalars['Float']['output']>;
  /**
   * Card surcharge given back by refunds of this attempt's charges (ENG-3881
   * S1.4): each refund returns the base plus its proportional share of the
   * surcharge. Not subtracted from `surchargeAmount`, which stays what was
   * charged. 0 when none; null when no charge has been recorded. Populated by
   * `Invoice.paymentAttempts` only.
   */
  surchargeRefundedAmount?: Maybe<Scalars['Float']['output']>;
  tenantId: Scalars['ID']['output'];
};

/**
 * What asking the provider about one payment produced.
 *
 * ⚠️ `NOT_ADDRESSABLE` AND `MISSING` ARE NOT THE SAME ANSWER AND MUST NOT BE
 * RENDERED THE SAME WAY. `MISSING` means we hold a transaction id the provider
 * denies having — a genuine data-integrity finding. `NOT_ADDRESSABLE` means we
 * never recorded an id at all, so no question was asked and NOTHING was learned
 * about whether the debit reached the bank. Presenting the second as the first
 * tells an operator the provider confirmed nothing was sent, which is false and
 * walks them straight to Cancel on the one class where the money may be live.
 */
export enum PaymentAttemptCheckOutcome {
  /**
   * The provider reports an outcome we have not booked. This is the one that
   * offers Apply.
   */
  Divergent = 'DIVERGENT',
  /** Still moving at the provider. Not an error, and nothing to book yet. */
  InFlight = 'IN_FLIGHT',
  /** The provider answered and agrees with what we already recorded. */
  MatchesOurRecord = 'MATCHES_OUR_RECORD',
  /**
   * The provider has NO RECORD of an id WE HOLD. Reported here and deliberately
   * NOT alarmed: with checking available to every finance viewer, alarming on a
   * read would be an open path to A/R alarm noise. Apply and the daily
   * reconciliation pass are what alarm.
   */
  Missing = 'MISSING',
  /**
   * There is no provider transaction id to ask about, so no question was asked.
   * Read the type docs above before rendering this.
   */
  NotAddressable = 'NOT_ADDRESSABLE',
  /** No payment with that id in this organization. */
  NotFound = 'NOT_FOUND',
  /**
   * We could not reach the provider. Deliberately distinct from MISSING — one
   * is a network failure and the other is evidence.
   */
  ProviderUnreachable = 'PROVIDER_UNREACHABLE',
  /** The provider answered and the answer could not be mapped. */
  Unresolved = 'UNRESOLVED'
}

/**
 * Payload envelope for chargeInvoice. Returns the underlying
 * payment_attempts row plus the invoice post-state so clients can refresh
 * their view atomically. MOL-622.
 */
export type PaymentAttemptPayload = {
  __typename?: 'PaymentAttemptPayload';
  /** Normalized error code when status = FAILED (card_declined, ...). */
  errorCode?: Maybe<Scalars['String']['output']>;
  /** Safe-to-display error message when status = FAILED. */
  errorMessage?: Maybe<Scalars['String']['output']>;
  /** The invoice after status recalc (amountDue / status may have moved). */
  invoice: Invoice;
  /** Required follow-up for REQUIRES_ACTION results. */
  nextAction?: Maybe<PaymentNextAction>;
  paymentAttempt: PaymentAttempt;
  /** Underlying provider transaction id, present on success. */
  transactionId?: Maybe<Scalars['ID']['output']>;
};

/** Payment attempt status. */
export enum PaymentAttemptStatus {
  Cancelled = 'CANCELLED',
  Failed = 'FAILED',
  Pending = 'PENDING',
  RequiresAction = 'REQUIRES_ACTION',
  /**
   * The receiving bank sent the ACH debit back (NSF, account closed,
   * unauthorized). Carries `returnCode`. Backed by
   * `payment_attempts.status = 'returned'` (db/migrations/0370). Must NOT be
   * presented to a payer as a payment still processing.
   */
  Returned = 'RETURNED',
  /**
   * The funds cleared. Backed by `payment_attempts.status = 'settled'`
   * (db/migrations/0370); carries `settledAt`.
   */
  Settled = 'SETTLED',
  /**
   * The provider ACCEPTED the entry. For ACH this is NOT money received —
   * a BASYS ACH sale answers `pending_settlement` and clears 2-5 business days
   * later. Use SETTLED for "the funds arrived".
   */
  Succeeded = 'SUCCEEDED'
}

/**
 * One on-demand check of one payment against the provider.
 *
 * ⚠️ THIS FIELD WRITES NOTHING — no payment row, no ledger row, and deliberately
 * NO AUDIT ROW. It may be called as often as an operator likes. `GET
 * /api/transaction/{id}` is a pure read at the provider, so the whole risk lives in
 * what is done with the answer, and that is `Mutation.applyPaymentAttemptResults`
 * behind a different key. "Who looked at this" is answered by a structured log
 * line rather than a row (services/pricing attempt_status_check.ts).
 *
 * ⚠️ CLIENT CACHE NOTE, AND IT IS LOAD-BEARING. This type has an `attemptId` and
 * NO `id`. A normalizing cache MUST key it on `attemptId`
 * (apps/main/src/apollo-client.ts). Keying it on `[]` — the singleton shape the
 * reconciliation report uses — gives every attempt in the org ONE shared cache
 * slot, so opening the drawer for payment B renders payment A's answer. And the
 * query must be fetched network-only: a check whose whole purpose is "what does the
 * provider say RIGHT NOW" served from cache is a stale answer presented as a fresh
 * one, and "check again" would not re-ask.
 */
export type PaymentAttemptStatusCheck = {
  __typename?: 'PaymentAttemptStatusCheck';
  /**
   * True only when applying would actually change something. The server
   * re-derives this from a FRESH provider read when Apply is called and does not
   * trust this value, which is stale the moment it is returned — an ACH entry can
   * be returned between the check and the click.
   */
  applyEligible: Scalars['Boolean']['output'];
  /** The lifecycle ROOT id that was checked. The cache key for this type. */
  attemptId: Scalars['ID']['output'];
  /** When we asked. NOT a settlement time. */
  checkedAt: Scalars['AWSDateTime']['output'];
  /**
   * Our own collapsed status, so the console can show both sides of a
   * divergence. Null when the lifecycle was not found.
   */
  ourStatus?: Maybe<PaymentAttemptStatus>;
  outcome: PaymentAttemptCheckOutcome;
  /** What the provider's answer maps to, when it maps to anything. */
  providerEffect?: Maybe<PaymentProviderEffect>;
  /**
   * One operator-facing sentence, always populated. "The provider agrees" is as
   * much an answer as a refusal, and a blank reason beside a refused action is what
   * sends somebody to a runbook.
   */
  reason: Scalars['String']['output'];
};

/**
 * The ACH authorization on file for the debited payment method, resolved AS
 * AT `PaymentLifecycle.authorizationAsOf` — the LATEST authorization for that
 * method with `consentedAt <= authorizationAsOf`, never simply the latest row,
 * so a reauthorization captured after this payment can never be shown against
 * it.
 *
 * THIS IS A FACT ABOUT THE METHOD, NOT A CLAIM ABOUT THIS DEBIT.
 * `ach_authorizations` (`db/migrations/0310_tier7_ach_authorizations.sql`) is
 * captured exactly twice in this codebase — at method-add
 * (`services/pricing/src/payments/method_management.ts`) and at `reauthorize`
 * (`reauthorize_method.ts`) — and NEVER on the charge path itself: no code
 * writes or reads an authorization keyed to one payment. Reading this field
 * never means "this specific debit was authorized"; it means "here is when the
 * debited method's authorization on file was captured, by whom, and under which
 * type" — the same way a bank statement shows an account's standing mandate
 * rather than re-proving consent on every line.
 *
 * NULL MEANS NO RECORD ON FILE, NEVER A FAILED READ. Null is the honest answer
 * for every card payment (no ACH evidence row could ever exist for one) and for
 * any ACH method saved before migration 0310. A failing read on the underlying
 * statement is never represented as a null here — it propagates as a
 * query-level error on this whole query, exactly like every other lifecycle
 * read this same query performs.
 *
 * ⚠️ SOFT-GATED ON `clearpath.audit.read`, THE SAME KEY `auditHistory` USES —
 * this is consent evidence, the identical sensitivity class as the audit trail.
 * Read `PaymentLifecycle.authorizationVisible` before treating a null value here
 * as "none on file"; a caller lacking the key also sees null, for a DIFFERENT
 * reason this field alone cannot distinguish.
 */
export type PaymentAuthorizationRecord = {
  __typename?: 'PaymentAuthorizationRecord';
  /**
   * Rendered exactly as stored, never assumed. Null for the type every row
   * in this database carries today (`single` — see `AchAuthorizationType`),
   * because presenting a `single` row as a standing authorization would be the
   * per-debit claim this field must never make. Only a `recurring` row
   * surfaces, as `RECURRING`: a standing authorization is on file.
   */
  authorizationType?: Maybe<AchAuthorizationType>;
  /**
   * Hydrated from `consentActorUserId` when present. Null exactly when
   * `consentActorUserId` is null — the explicit UNKNOWN-ACTOR state a renderer
   * must show, rather than a blank actor beside a populated authorization.
   */
  consentActor?: Maybe<PaymentConsentActor>;
  /**
   * Raw `ach_authorizations.consent_user_id`. Null means the consenting
   * actor is unknown — there is no machine-driven capture path today, so a null
   * here is a legacy-row / integrity gap rather than an expected case.
   */
  consentActorUserId?: Maybe<Scalars['ID']['output']>;
  /**
   * When the payer consented, read back verbatim off the append-only row.
   * NOT the same moment as `PaymentLifecycle.authorizationAsOf`, which is when
   * this row was SELECTED, not when it was captured.
   */
  consentedAt: Scalars['AWSDateTime']['output'];
  /**
   * The payment method this authorization was captured against — always the
   * lifecycle's own debited method, never any other method the payer holds.
   */
  paymentMethodId: Scalars['ID']['output'];
};

/**
 * Client-side payment form bootstrap for the active tenant provider.
 * Secrets never appear here — only publishable / tokenizer public fields.
 */
export type PaymentClientConfig = {
  __typename?: 'PaymentClientConfig';
  /**
   * The NACHA ACH authorization wording the client MUST render verbatim.
   *
   * Served from the same server-side constant the evidence writer records
   * (`achWebAuthorizationCopy()`), so the text a payer is shown and the text
   * stored in the append-only `ach_authorizations` row are one string rather than
   * two that happen to agree. Clients MUST NOT hold their own copy: the portal
   * previously did, and the two differed in phrasing and in scope, which meant the
   * proof of authorization we would produce on a dispute stated wording the payer
   * never saw.
   *
   * Non-null and provider-independent — the wording is ours, not the gateway's, so
   * it is served even when no provider is configured.
   */
  achAuthorizationText: Scalars['String']['output'];
  /**
   * Version identifier of `achAuthorizationText`.
   *
   * Echo it back as `MandateConsentInput.authorizationVersion`. The server REFUSES
   * a mismatch rather than reconciling it, so a cached SPA build showing
   * superseded wording gets a clean error instead of silently recording text its
   * payer never read.
   */
  achAuthorizationVersion: Scalars['String']['output'];
  /**
   * NACHA Standard Entry Class code for the authorization above (`web` for
   * internet-initiated debits). Stored per authorization because NACHA's
   * requirements differ by SEC code.
   */
  achSecCode: Scalars['String']['output'];
  /** Basys IQ Pro tokenizer fields (public pub_ key + script URLs). */
  basysAuthKey?: Maybe<Scalars['String']['output']>;
  basysTokenizerOrigin?: Maybe<Scalars['String']['output']>;
  basysTokenizerScriptUrl?: Maybe<Scalars['String']['output']>;
  /**
   * The card surcharge to disclose before a CARD payment (ENG-3881).
   *
   * When `enabled`, a card payment adds `rateBps` (rounded UP to the cent) on top
   * of the amount, and the client MUST show it and echo it as
   * `chargeInvoice(surchargeDisclosed:)`. ACH is never surcharged. Null (older
   * server) means no surcharge.
   */
  cardSurcharge?: Maybe<CardSurchargeConfig>;
  configured: Scalars['Boolean']['output'];
  /**
   * Rails this organization accepts for a NEW payment method right now
   * (ENG-1532).
   *
   * The server REFUSES anything absent from this list
   * (`assertMethodTypeEnabled`, services/pricing), so a client MUST NOT offer a
   * rail that is not here. Produced by the SAME shared predicate that enforces the
   * gate (`@clearpath/shared-types/payment-rails`), so the tab strip and the
   * refusal cannot disagree.
   *
   * Only CARD and ACH are ever returned. Empty when no active provider is
   * configured — that case is already carried by `configured: false`.
   *
   * Computed in services/api, NOT the pricing Lambda: the field is non-null and
   * the two deploy separately, so producing it in pricing would raise an AppSync
   * non-null violation on the portal's bootstrap query for the whole window
   * between the two deploys.
   */
  enabledMethodTypes: Array<PaymentMethodType>;
  /** Active provider key: stripe | basys | none | … */
  provider: Scalars['String']['output'];
  /** Stripe publishable key (pk_…). */
  publishableKey?: Maybe<Scalars['String']['output']>;
  unavailableReason?: Maybe<Scalars['String']['output']>;
};

/**
 * Who consented to `PaymentAuthorizationRecord.consentActorUserId`, when that
 * id is present.
 *
 * Reuses the same db `user_type_enum` → `UserType` mapping and payer/staff
 * labelling `AuditLogEntry.actor` already carries
 * (`mapUserTypeDbToGqlOrNull`, `services/api/src/utils/user_type.ts`;
 * `auditActorIdentity` / `ACTOR_PARTY_LABELS`,
 * `apps/main/src/admin/pages/auditActorLabel.ts`), so this authorization's actor
 * renders with the identical staff-versus-payer treatment a dispute reader
 * already knows how to read.
 *
 * `fullName` IS NULLABLE, WHICH IS DELIBERATE HERE. `users.full_name` is itself
 * nullable (`db/migrations/0001_tier1_identity_org.sql:231`); a non-null
 * `String!` fed from it would null the WHOLE PARENT `PaymentAuthorizationRecord`
 * the moment one legacy user row carries no name, surfacing as a query-level
 * GraphQL error on a dispute surface rather than one field going empty.
 */
export type PaymentConsentActor = {
  __typename?: 'PaymentConsentActor';
  fullName?: Maybe<Scalars['String']['output']>;
  id: Scalars['ID']['output'];
  userType: UserType;
};

/**
 * The instrument a payment lifecycle actually debited — the ONE payment
 * method the lifecycle's current attempt names, not a history of every method
 * the payer has ever saved.
 *
 * EXACTLY ONE INSTRUMENT, NEVER A WALLET. This is deliberately not `PaymentMethod`
 * and does not carry `partyId`, `isDefault`, `chargeable`, `brand`, `type`,
 * `status`, mandate state, or `billingAddress` — a reader here is identifying
 * the account behind one payment, not browsing the payer's saved methods.
 *
 * NOTHING ELSE ON `PaymentLifecycle` CARRIES THEM EITHER. A payment's drawer
 * answers what happened to this debit; the instrument's state today is a
 * different question asked on a different surface, and putting it here invites
 * reading a method revoked since as a claim that this debit was unauthorized.
 *
 * `maskedAccount` is NOT a `last4` and the two are never interchangeable or
 * derived from one another. `maskedAccount` is the provider's own mask for a
 * bank account (BASYS: `11*****11` — first two and last two digits, not the
 * last four); folding it into a `···· {last4}` render produces the nonsense
 * `···· 11*****11`. `last4` is card only and is always null for ACH.
 */
export type PaymentDebitedAccount = {
  __typename?: 'PaymentDebitedAccount';
  /** Card last 4. Always null for ACH — read `maskedAccount` instead. */
  last4?: Maybe<Scalars['String']['output']>;
  /**
   * Provider mask for a bank account, VERBATIM. Null for card, and null for an
   * ACH method saved with no mask on file.
   */
  maskedAccount?: Maybe<Scalars['String']['output']>;
  /** The payment method this lifecycle's current attempt charged. */
  paymentMethodId: Scalars['ID']['output'];
};

/**
 * One provider webhook delivery (`payment_webhooks_log`).
 *
 * THE FIRST READER THIS TABLE HAS EVER HAD. It has been written since ACH landed
 * and nothing in the repo read it, so a delivery that failed to process left no
 * operator-visible trace at all.
 *
 * ⚠️ THE RAW `payload` IS DELIBERATELY NOT EXPOSED, on this type or anywhere. It
 * is the provider's verbatim body and carries payer identity and bank-account
 * fragments; an operator needs to know THAT a delivery arrived and whether it
 * processed, not what was in it. Reading a payload is a database-access
 * operation with its own trail.
 */
export type PaymentIngestion = {
  __typename?: 'PaymentIngestion';
  eventType: Scalars['String']['output'];
  /**
   * WHETHER a delivery failed, never WHY. The provider writes
   * `payment_webhooks_log.error_message` as free text and routinely quotes the
   * payload back inside it — payer name, bank-account fragments, the same content
   * this type withholds by not exposing `payload` at all. Both surfaces that read
   * this field already render a fixed withheld-notice rather than the string, so
   * shipping the string only put it in the response body, the Apollo cache and
   * anything logging either. It is now collapsed to a boolean in SQL and the text
   * never leaves Postgres; reading one is a database operation with its own
   * trail.
   */
  hasError: Scalars['Boolean']['output'];
  id: Scalars['ID']['output'];
  /**
   * Null while `PENDING`. A delivery that has been pending for a long time is
   * the interesting case: nothing retries it.
   */
  processedAt?: Maybe<Scalars['AWSDateTime']['output']>;
  processingStatus: PaymentIngestionStatus;
  /**
   * The provider's event id. `UNIQUE (payment_provider_id, provider_event_id)`
   * is what makes a duplicate delivery idempotent.
   */
  providerEventId: Scalars['String']['output'];
  receivedAt: Scalars['AWSDateTime']['output'];
};

export type PaymentIngestionConnection = {
  __typename?: 'PaymentIngestionConnection';
  edges: Array<PaymentIngestionEdge>;
  pageInfo: PageInfo;
  totalCount: Scalars['Int']['output'];
};

export type PaymentIngestionEdge = {
  __typename?: 'PaymentIngestionEdge';
  cursor: Scalars['String']['output'];
  node: PaymentIngestion;
};

/** Filter for `Query.paymentIngestions`. Every field is ANDed. */
export type PaymentIngestionFilterInput = {
  /** Provider event types, matched exactly as the provider spelled them. */
  eventTypes?: InputMaybe<Array<Scalars['String']['input']>>;
  processingStatuses?: InputMaybe<Array<PaymentIngestionStatus>>;
  receivedAt?: InputMaybe<DateRangeInput>;
};

/**
 * How a provider webhook delivery was handled.
 * Mirrors `payment_webhooks_log.processing_status` (db/migrations/0007).
 */
export enum PaymentIngestionStatus {
  /**
   * Processing threw. This is the one an operator has to look at: the provider
   * told us something about money and we did not book it.
   */
  Failed = 'FAILED',
  /**
   * Recognised and deliberately not acted on — an event type this system does
   * not consume. Not an error.
   */
  Ignored = 'IGNORED',
  /** Received and not yet processed. Nothing retries it. */
  Pending = 'PENDING',
  Processed = 'PROCESSED'
}

/**
 * One row of the money ledger (`payment_transactions`) under a payment
 * lifecycle.
 *
 * ⚠️ THIS IS NOT THE ATTEMPT. An attempt is what we TRIED and everything we
 * learned about it; a ledger row is money that actually MOVED. A returned ACH
 * debit writes BOTH — a `RETURN` ledger row and a `returned` attempt row — so a
 * client that reads one and not the other gets a different answer about the same
 * event.
 */
export type PaymentLedgerEntry = {
  __typename?: 'PaymentLedgerEntry';
  /**
   * ALWAYS POSITIVE, whichever direction the money went. `transactionType` says
   * the direction: CHARGE adds to the settled balance; REFUND, CHARGEBACK and
   * RETURN each subtract it. A client that sums `amount` without reading
   * `transactionType` gets a number wrong by twice every reversal.
   */
  amount: Scalars['Float']['output'];
  currency: Scalars['String']['output'];
  feeAmount?: Maybe<Scalars['Float']['output']>;
  /**
   * `amount` + `surchargeAmount` as the provider moved it. Null when the row has
   * no breakdown.
   */
  grossAmount?: Maybe<Scalars['Float']['output']>;
  id: Scalars['ID']['output'];
  netAmount?: Maybe<Scalars['Float']['output']>;
  occurredAt: Scalars['AWSDateTime']['output'];
  /**
   * The provider's own id for this movement. This is the value the settlement
   * dedupe keys on, so it is what proves the daily pass and an operator action
   * booked through ONE write path rather than two.
   */
  providerTransactionId?: Maybe<Scalars['String']['output']>;
  /**
   * The CHARGE row this row reverses, when it is a refund
   * (`payment_transactions.refunded_transaction_id`, db/migrations/0324). Null on
   * a charge, and null on a refund row written before that column existed — those
   * are invisible to the over-refund guard, so do not read null as "no parent".
   */
  refundedTransactionId?: Maybe<Scalars['ID']['output']>;
  /**
   * ENG-3881 — card surcharge on this row, on top of `amount` (a charge's
   * surcharge taken, a refund's share given back). Null when none was recorded.
   */
  surchargeAmount?: Maybe<Scalars['Float']['output']>;
  transactionType: PaymentTransactionType;
};

/**
 * Everything recorded under one payment lifecycle: what we tried, what money
 * moved, who acted, and what the provider told us.
 *
 * THREE AXES, NOT ONE LIST. The attempt is the INTENT, the ledger row is the
 * MONEY, and the audit row is the ACT. They are separate because they answer
 * separate questions and because a single event can write two of them.
 */
export type PaymentLifecycle = {
  __typename?: 'PaymentLifecycle';
  /**
   * Actor and before/after state for every `payment.*` action recorded
   * against this lifecycle's own attempts and ledger rows — scoped by RESOURCE
   * TYPE (`payment_attempt`, `payment_transaction`), never by an action
   * allow-list. `sourceIp` and `userAgent` are never populated on these rows.
   *
   * DELIBERATELY NOT payment-method or mandate rows (ENG-2424 reversal of the
   * ENG-1988 choice to include them): a filter keyed on resource ids alone, with
   * no resource-type bound, matched every audit row about a payment method this
   * payment ever used, forever, in both time directions — including
   * `payment.method_added` and `payment.method_default_changed`, neither of
   * which is an action on THIS payment. (Those are the ACTION strings the
   * writers actually emit, from `PAYMENT_AUDIT_ACTIONS` in
   * services/pricing/src/payments/payment_audit.ts; the `payment_method.*`
   * namespace appears in `docs/qa/PROD-READINESS-GAPS.md` as "Not started" and
   * is emitted by nothing.)
   *
   * ⚠️ SOFT-GATED, AND EMPTY IS NOT THE SAME AS NONE. This pane requires
   * `clearpath.audit.read` IN ADDITION to the page's own key, and a caller without
   * it gets an EMPTY connection rather than an error — the same posture
   * `Order.auditLog` takes. Read `auditHistoryVisible` to tell "no history" from
   * "not allowed to see the history"; rendering the empty pane as "nothing
   * happened" is wrong and is the reason that flag exists.
   */
  auditHistory: AuditLogEntryConnection;
  /**
   * False when the caller lacks `clearpath.audit.read`, in which case
   * `auditHistory` is empty because it was WITHHELD, not because it is empty.
   */
  auditHistoryVisible: Scalars['Boolean']['output'];
  /**
   * The authorization on file for the debited method, selected as at
   * `authorizationAsOf`. See `PaymentAuthorizationRecord` for what this is and,
   * more importantly, what it is not — it is never a claim that this specific
   * debit was authorized.
   *
   * ⚠️ SOFT-GATED ON `clearpath.audit.read`, THE SAME KEY `auditHistory` USES.
   * Read `authorizationVisible` before treating a null value here as "no record
   * on file"; a caller lacking the key sees null for a DIFFERENT reason
   * (withheld) that this field alone cannot distinguish from "none on file".
   */
  authorization?: Maybe<PaymentAuthorizationRecord>;
  /**
   * The moment `authorization` was SELECTED AS AT — the lifecycle ROOT
   * attempt's own `attemptedAt`, i.e. the DEBIT instant, NOT
   * `authorization.consentedAt` (when the authorization was captured) and NOT
   * the current (collapsed) row's `attemptedAt` once the lifecycle has settled
   * or been returned, which is the provider's settlement or return instant, not
   * the debit's. A NACHA dispute is filed against the debit, so this is the
   * instant "what consent was on file" is asked about. The row returned for
   * `authorization` is the latest authorization on file for the debited method
   * with `consentedAt <= authorizationAsOf`, so a reauthorization captured
   * after the debit is never shown against it.
   */
  authorizationAsOf: Scalars['AWSDateTime']['output'];
  /**
   * True when the caller holds `clearpath.audit.read`, in which case a null
   * `authorization` means no authorization record is on file for the debited
   * method. False means `authorization` is WITHHELD, not absent — distinguishable
   * from both "none on file" and from a populated authorization whose consent
   * actor is unknown. The drawer's other panes are unaffected by this key.
   */
  authorizationVisible: Scalars['Boolean']['output'];
  /**
   * The collapsed current state, identical to the `Query.paymentActivity` entry
   * for this lifecycle.
   */
  current: PaymentActivityEntry;
  /**
   * The account this payment debited — provider mask and last four only, for
   * identifying the instrument in a dispute. Null when the current attempt
   * carries no payment method, when the method row is gone, or when it belongs
   * to another organization; never an error.
   *
   * Read on the same `clearpath.finance.read` gate as the rest of this query —
   * there is no separate, narrower key for this field.
   */
  debitedAccount?: Maybe<PaymentDebitedAccount>;
  /**
   * The lifecycle root id — the same value as `rootAttemptId`, present so the
   * type normalizes in a client cache.
   */
  id: Scalars['ID']['output'];
  /**
   * Provider webhook deliveries matched to this lifecycle's provider attempt
   * ids, newest first.
   */
  ingestions: Array<PaymentIngestion>;
  /**
   * The money that moved: the charge, and any return/refund/chargeback
   * reversing it, oldest first.
   */
  ledger: Array<PaymentLedgerEntry>;
  representment: PaymentRepresentmentLineage;
  rootAttemptId: Scalars['ID']['output'];
  /**
   * EVERY row under the root, OLDEST FIRST — the lifecycle timeline, not the
   * collapsed state. `payment_attempts` is append-only, so the sequence IS the
   * history. Each element's `id` is that ROW's own id here (unlike `current`),
   * because the timeline is about the rows.
   */
  timeline: Array<PaymentAttempt>;
};

/**
 * Saved payment method per party.
 * Per docs/clearpath-architecture/03-data-model.md tier 7 (`payment_methods`).
 */
export type PaymentMethod = {
  __typename?: 'PaymentMethod';
  /** Brand (visa, mc, amex). */
  brand?: Maybe<Scalars['String']['output']>;
  /**
   * Whether this method may be used to originate a payment RIGHT NOW.
   *
   * ONE server-side predicate, evaluated identically here and in the pricing
   * service before a debit leaves (`@clearpath/shared-types/payment-method-chargeable`),
   * so a picker cannot offer a method the server will then refuse. For ACH it
   * requires an active method AND an active mandate AND a confirmed billing
   * address; card is unchanged and requires only an active method.
   *
   * A method that is not chargeable is NOT deleted and NOT hidden from the
   * payment-methods page — a payer who saved a bank account and finds it gone
   * assumes we lost it. It is simply not offered in the pay flow.
   */
  chargeable: Scalars['Boolean']['output'];
  createdAt: Scalars['AWSDateTime']['output'];
  /** Expiry month (1-12). */
  expiryMonth?: Maybe<Scalars['Int']['output']>;
  /** Expiry year (4-digit). */
  expiryYear?: Maybe<Scalars['Int']['output']>;
  id: Scalars['ID']['output'];
  isActive: Scalars['Boolean']['output'];
  isDefault: Scalars['Boolean']['output'];
  /** Customer-chosen nickname (e.g. \"Clinic Amex\"). Stored in custom_fields. */
  label?: Maybe<Scalars['String']['output']>;
  /**
   * Last 4 digits / IBAN suffix. Always null for ACH — a BASYS bank-account
   * mask is `11*****11` (first two and last two digits) and a last-4 cannot be
   * recovered from it. Read `maskedAccount` instead for a bank account.
   */
  last4?: Maybe<Scalars['String']['output']>;
  /**
   * Latest mandate status for an ACH / SEPA method; null for card, which needs
   * no mandate. Denormalized from `mandates.status` onto
   * `payment_methods.mandate_status`.
   */
  mandateStatus?: Maybe<MandateStatus>;
  /**
   * Provider mask for a bank account, VERBATIM (BASYS: `11*****11`). Null for
   * card. Deliberately NOT folded into `last4`: clients render that as
   * `···· {last4}`, which turns a mask into the nonsense `···· 11*****11`.
   * Backed by `payment_methods.masked_account_number` (db/migrations/0370);
   * methods saved before that migration fall back to the value phase 2 parked in
   * `custom_fields`.
   */
  maskedAccount?: Maybe<Scalars['String']['output']>;
  /** Method kind (card, ach, sepa, wire). */
  methodType: PaymentMethodType;
  organizationId: Scalars['ID']['output'];
  party?: Maybe<ExternalParty>;
  partyId: Scalars['ID']['output'];
  provider?: Maybe<PaymentProvider>;
  providerId: Scalars['ID']['output'];
  tenantId: Scalars['ID']['output'];
  /**
   * Why `chargeable` is false; null when it is true.
   *
   * MANDATE_MISSING and BILLING_ADDRESS_MISSING are RE-AUTHORIZABLE: our record is
   * incomplete, the bank account itself is fine and still vaulted at the provider,
   * so the payer re-consents in place rather than re-entering account details.
   *
   * MANDATE_REVOKED and METHOD_REVOKED are NOT. A mandate is revoked because the
   * payer withdrew authorization or because a revoke-class NACHA return came back
   * (R02 account closed, R03 no account, R04 invalid account number). Re-enabling
   * originates another debit against a dead account and earns another return, so
   * no re-enable action may be offered — the payer adds a new account.
   */
  unusableReason?: Maybe<PaymentMethodUnusableReason>;
  updatedAt?: Maybe<Scalars['AWSDateTime']['output']>;
};

/** Payload envelope for payment-method mutations. MOL-622. */
export type PaymentMethodPayload = {
  __typename?: 'PaymentMethodPayload';
  paymentMethod: PaymentMethod;
};

/** Payment method kind. */
export enum PaymentMethodType {
  Ach = 'ACH',
  Card = 'CARD',
  Cash = 'CASH',
  Check = 'CHECK',
  Other = 'OTHER',
  Sepa = 'SEPA',
  Wire = 'WIRE'
}

/**
 * Why a saved payment method cannot currently be charged. Exactly one is
 * reported — the first that applies, in the order declared here — so a revoked
 * method is never described as merely missing an address.
 */
export enum PaymentMethodUnusableReason {
  /**
   * No confirmed payer billing address on the method. Re-authorizable.
   * BASYS itself would accept an accountholder name with no street; the full
   * address is OUR requirement, held as NACHA authorization evidence.
   */
  BillingAddressMissing = 'BILLING_ADDRESS_MISSING',
  /**
   * No active mandate on file (never had one, still pending, or expired).
   * Re-authorizable.
   */
  MandateMissing = 'MANDATE_MISSING',
  /** The debit authorization is gone. NOT re-authorizable. */
  MandateRevoked = 'MANDATE_REVOKED',
  /** The payment method row itself is not active. */
  MethodRevoked = 'METHOD_REVOKED'
}

/**
 * Optional follow-up action returned by chargeInvoice when the provider
 * needs a second interaction with the payer (3-D Secure, micro-deposit
 * verification, bank redirect). The portal consumes `type` to choose the
 * client-side confirmation helper. MOL-622.
 */
export type PaymentNextAction = {
  __typename?: 'PaymentNextAction';
  /**
   * Kind of follow-up: CLIENT_SECRET (Stripe Elements confirmCardPayment)
   * or REDIRECT_URL (bank hosted flow).
   */
  type: PaymentNextActionType;
  /** Opaque value the client hands to the provider SDK. */
  value: Scalars['String']['output'];
};

export enum PaymentNextActionType {
  ClientSecret = 'CLIENT_SECRET',
  RedirectUrl = 'REDIRECT_URL'
}

/** Do we know what happened to this payment? */
export enum PaymentOutcomeCertainty {
  /**
   * The provider answered. A `FAILED` lifecycle here really is a decline, a
   * `SETTLED` one really cleared.
   */
  Known = 'KNOWN',
  /**
   * The provider call THREW and we do not know whether the debit was accepted.
   * Backed by the same three-column discriminator the reservation and the
   * reconciliation pass read (`@clearpath/shared-types/payment-attempt-state`
   * `isUnknownProviderOutcome`), so this surface and the money path cannot
   * disagree about which rows are in this class.
   */
  Unknown = 'UNKNOWN'
}

/**
 * Per-tenant payment provider config.
 * Per docs/clearpath-architecture/03-data-model.md tier 7 (`payment_providers`).
 */
export type PaymentProvider = {
  __typename?: 'PaymentProvider';
  config?: Maybe<Scalars['AWSJSON']['output']>;
  createdAt: Scalars['AWSDateTime']['output'];
  displayName: Scalars['String']['output'];
  id: Scalars['ID']['output'];
  isActive: Scalars['Boolean']['output'];
  organizationId: Scalars['ID']['output'];
  providerKey: Scalars['String']['output'];
  tenantId: Scalars['ID']['output'];
  updatedAt?: Maybe<Scalars['AWSDateTime']['output']>;
};

/**
 * The outcome the provider's answer maps to, in OUR vocabulary rather than the
 * provider's.
 *
 * Null when there is nothing to book — no answer, an in-flight answer, or an
 * effect kind this action does not apply. Absence is a legitimate answer here, so
 * it is nullable rather than carrying a NONE member.
 */
export enum PaymentProviderEffect {
  /** The provider reports the attempt as failed. */
  Failure = 'FAILURE',
  /**
   * The receiving bank sent the entry back. Applying it reverses the money and
   * re-opens the invoice.
   */
  Return = 'RETURN',
  /** The debit cleared. Applying it books the settlement and the ledger row. */
  Settlement = 'SETTLEMENT'
}

/** One payment rail and whether this organization accepts it right now. */
export type PaymentRail = {
  __typename?: 'PaymentRail';
  /**
   * Whether a NEW payment method / NEW charge on this rail is accepted now.
   *
   * Says nothing about money already moving: settlement, ACH returns and
   * reconciliation never consult this gate, so an in-flight debit still lands and
   * still reverses on a rail that is switched off.
   */
  enabled: Scalars['Boolean']['output'];
  /** CARD or ACH. No other PaymentMethodType is ever returned here. */
  rail: PaymentMethodType;
};

/**
 * How this organization's payment-rail configuration currently stands.
 *
 * Read honestly, never repaired on read: a read path that mutates a money gate
 * is worse than the bug it would be covering up.
 */
export enum PaymentRailConfigState {
  /** A usable allow-list is stored and is being enforced. */
  Explicit = 'EXPLICIT',
  /**
   * A value is stored that the shared predicate cannot read (a bare string, an
   * object, an array of non-strings). The gate FAILS OPEN, so every rail is
   * currently accepted — the stored value is not doing what it looks like. One
   * save repairs it.
   */
  Malformed = 'MALFORMED',
  /**
   * No active payment_providers row. Online payments are not set up for this
   * organization at all. Read-only — connecting a provider is a Molaris ops
   * action, not tenant self-service.
   */
  NotConfigured = 'NOT_CONFIGURED',
  /**
   * A provider row exists but the allow-list was never set (null or `[]`, the
   * column default), so the gate allows EVERY rail. The first save converts this
   * to EXPLICIT.
   */
  UnrestrictedLegacy = 'UNRESTRICTED_LEGACY'
}

/**
 * This organization's payment-rail configuration.
 *
 * ONE server-side predicate (`@clearpath/shared-types/payment-rails`) produces
 * this and enforces the gate in the pricing service, so the page cannot show a
 * rail as enabled that the server will then refuse.
 */
export type PaymentRailSettings = {
  __typename?: 'PaymentRailSettings';
  /** Provider display name (e.g. "Basys"). Null when NOT_CONFIGURED. */
  providerDisplayName?: Maybe<Scalars['String']['output']>;
  /**
   * Exactly CARD and ACH, in that order. Always both entries — a rail that is
   * switched off is returned with `enabled: false`, never omitted.
   */
  rails: Array<PaymentRail>;
  state: PaymentRailConfigState;
  /**
   * Opaque optimistic-concurrency token (the provider row's updated_at).
   * Empty string when NOT_CONFIGURED. Pass it back as `expectedVersion`.
   */
  version: Scalars['String']['output'];
};

export type PaymentRailSettingsPayload = {
  __typename?: 'PaymentRailSettingsPayload';
  settings: PaymentRailSettings;
};

/**
 * The reconciliation panel's whole answer: recent passes plus the two facts
 * that make a pass that DID NOT RUN visible as such.
 *
 * ⚠️ SINGLETON-SHAPED — no `id`. A client cache must give this type
 * `keyFields: []` or successive reads cannot be normalized into one entry
 * (apps/main/src/apollo-client.ts).
 */
export type PaymentReconciliationReport = {
  __typename?: 'PaymentReconciliationReport';
  /**
   * When a pass last ran at all, failed or not, over all history. A
   * `lastRunAt` far in the past is the signal the tick has stopped; a `lastRunAt`
   * newer than `lastSuccessfulRunAt` means the recent passes are failing.
   */
  lastRunAt?: Maybe<Scalars['AWSDateTime']['output']>;
  /**
   * When a pass last COMPLETED WITHOUT FAILING for this organization, over all
   * history rather than only the returned window. Null means one never has, and
   * that is the honest answer for a tenant whose pass has never run — do not
   * render it as "today".
   */
  lastSuccessfulRunAt?: Maybe<Scalars['AWSDateTime']['output']>;
  /** Recent passes for this organization, newest first. */
  runs: Array<PaymentReconciliationRun>;
};

/**
 * One organization's record of one daily reconciliation pass
 * (`reconciliation_runs`, db/migrations/0387).
 *
 * Counters are `ReconciliationSummary`'s fields (services/pricing reconcile.ts)
 * minus `organizations`, which is the pass's ROW COUNT rather than a column.
 */
export type PaymentReconciliationRun = {
  __typename?: 'PaymentReconciliationRun';
  /**
   * Settlements and returns it booked, through the same `applyEffect` path the
   * webhook uses.
   */
  applied: Scalars['Int']['output'];
  errorMessage?: Maybe<Scalars['String']['output']>;
  /** Candidates the pass actually asked the provider about. */
  examined: Scalars['Int']['output'];
  /**
   * The pass threw part-way through this organization. The counters are
   * whatever had accumulated — partial, and labelled partial.
   */
  failed: Scalars['Boolean']['output'];
  /**
   * Null means the writer recorded no end. It does NOT mean "still running" —
   * the table is append-only and the row is written once, at the end.
   */
  finishedAt?: Maybe<Scalars['AWSDateTime']['output']>;
  id: Scalars['ID']['output'];
  lateReturnsFound: Scalars['Int']['output'];
  /**
   * When the pass started working this organization.
   *
   * ⚠️ NOT WHEN MONEY MOVED. ACH batches close overnight (T+1), so a settlement
   * this pass booked may have cleared a full cycle earlier. Presenting this as a
   * settlement time reads as a delay the lab caused.
   */
  ranAt: Scalars['AWSDateTime']['output'];
  /** Attempts whose lost provider transaction id it adopted. */
  recovered: Scalars['Int']['output'];
  retriesDue: Scalars['Int']['output'];
  /**
   * Zero every day on a healthy system: the primary path already created every
   * schedule. Non-zero means the primary path lost one.
   */
  retriesRescheduled: Scalars['Int']['output'];
  /**
   * Candidates it deliberately did not ask about — too young for their rail, or
   * their provider row could not be built. Not an error. A high value beside a low
   * `examined` means the tick is running before the batch closes.
   */
  skipped: Scalars['Int']['output'];
  stillPending: Scalars['Int']['output'];
  sweptForReturns: Scalars['Int']['output'];
  /**
   * ⚠️ THE COUNTER THAT MATTERS. Attempts the pass could not explain and
   * therefore wrote NOTHING about. Each one keeps its reservation, is invisible on
   * every other surface, and has to be closed out by a human.
   */
  unresolved: Scalars['Int']['output'];
};

/**
 * How far a refund got at the provider. There is no FAILED member: a failed
 * refund is an error, not a payload — nothing is written and the caller is told
 * why.
 */
export enum PaymentRefundStatus {
  /** Accepted by the provider and not yet final. The ledger row exists. */
  Pending = 'PENDING',
  Succeeded = 'SUCCEEDED'
}

/**
 * How many times a returned entry has been re-presented, against the NACHA
 * cap.
 *
 * NACHA permits an Originator to re-initiate a returned entry at most TWICE, and
 * the original entry is not one of the two. `cap` comes from
 * `@clearpath/shared-types/payment-attempt-state#MAX_REPRESENTMENTS` rather than
 * being written here, so the surface and the writer that enforces it cannot
 * drift.
 */
export type PaymentRepresentmentLineage = {
  __typename?: 'PaymentRepresentmentLineage';
  cap: Scalars['Int']['output'];
  /**
   * True when the SYSTEM originated this lifecycle to re-present a returned
   * entry. `represents_attempt_id` is FLATTENED — it points at the root of the
   * ORIGINAL entry, never at a prior re-presentment — so this is the whole test
   * for "not the payer clicking Pay again".
   */
  isRepresentment: Scalars['Boolean']['output'];
  /** The ORIGINAL entry's root id, when this lifecycle re-presents one. */
  representsAttemptId?: Maybe<Scalars['ID']['output']>;
  /**
   * Re-presentments that exist against the original entry, including this one
   * when `isRepresentment` is true.
   */
  used: Scalars['Int']['output'];
};

/**
 * What a NACHA return code says about the payer's authorization, plus the two
 * non-return answers a payments-console row needs.
 *
 * Extends the classification `AchReturnDisposition` carries with the cases a
 * lifecycle row has that a return row cannot: not a return at all, and a return
 * whose code the provider never gave us. The classification itself is
 * `@clearpath/shared-types/ach-return-disposition` — the SAME table that decided
 * whether the write side revoked the payer's mandate — so this label can never
 * disagree with what the system actually did.
 */
export enum PaymentReturnDisposition {
  /** This lifecycle is not in a returned state. The common case. */
  NotReturned = 'NOT_RETURNED',
  /**
   * A funds/timing return (R01 insufficient, R09 uncollected). The
   * authorization survives; the payer can pay again with the same account.
   */
  Retriable = 'RETRIABLE',
  /**
   * The account is closed or invalid, or the payer/their bank says the debit
   * was not authorized. The mandate and the payment method are revoked and nothing
   * may debit that account again.
   */
  Revokes = 'REVOKES',
  /**
   * A code the classifier does not cover — which is MOST of them: 13 of roughly
   * 80 NACHA codes are classified. Nothing automatic was done (no re-present, no
   * revoke) and a human decides.
   */
  Unknown = 'UNKNOWN'
}

/** Payment transaction kind. */
export enum PaymentTransactionType {
  Charge = 'CHARGE',
  Chargeback = 'CHARGEBACK',
  Refund = 'REFUND',
  /**
   * An ACH debit the receiving bank sent back (NSF, administrative,
   * unauthorized). Carries a Nacha return-reason code and is deliberately NOT
   * CHARGEBACK, which is a card-network dispute with an issuer case. Backed by
   * `payment_transactions.transaction_type = 'return'` (db/migrations/0311);
   * stored positive and SUBTRACTED when netting a settled balance.
   */
  Return = 'RETURN',
  Reversal = 'REVERSAL'
}

/**
 * One entry in the caller's "Needs your review" queue (ENG-19): a case with an
 * open `AWAITING_CUSTOMER_APPROVAL` hold. `hold.heldSince` drives the elapsed-time
 * display (there is no approval-SLA policy in v1).
 */
export type PendingApproval = {
  __typename?: 'PendingApproval';
  /**
   * The customer-safe view of the open approval hold. `hold.id` is the
   * `holdId` to pass to `submitApprovalDecision`; `awaitsCustomerDecision` is always
   * true here.
   */
  hold: CustomerVisibleHold;
  order: Order;
};

/** The caller's pending design approvals (ENG-19). */
export type PendingApprovalConnection = {
  __typename?: 'PendingApprovalConnection';
  items: Array<PendingApproval>;
  /**
   * Number of cases awaiting the caller's approval — drives the nav count
   * badge.
   */
  totalCount: Scalars['Int']['output'];
};

/**
 * Synced mirror of control-plane permissions for Cedar eval.
 * Per docs/clearpath-architecture/03-data-model.md tier 8 (`permission_registry_cache`).
 */
export type PermissionRegistryEntry = {
  __typename?: 'PermissionRegistryEntry';
  action?: Maybe<Scalars['String']['output']>;
  deprecatedAt?: Maybe<Scalars['AWSDateTime']['output']>;
  description?: Maybe<Scalars['String']['output']>;
  mfaMaxAgeMinutes?: Maybe<Scalars['Int']['output']>;
  permissionKey: Scalars['String']['output'];
  productId?: Maybe<Scalars['String']['output']>;
  replacedBy?: Maybe<Scalars['String']['output']>;
  requiresStepUpMfa: Scalars['Boolean']['output'];
  resourceType?: Maybe<Scalars['String']['output']>;
  syncedAt: Scalars['AWSDateTime']['output'];
};

/**
 * Product-level pipeline template, linked to product_type_id.
 * Per docs/clearpath-architecture/03-data-model.md tier 3 (`pipelines`).
 */
export type Pipeline = {
  __typename?: 'Pipeline';
  createdAt: Scalars['AWSDateTime']['output'];
  /**
   * ENG-894 - strategy a Process step on this pipeline uses when it has not
   * chosen one itself (no `assignmentStrategy` key in its custom_fields). Read
   * from the version this query resolved: the open DRAFT when `asDraft: true`,
   * the LIVE version otherwise. NULLABLE: `pipelineRowToGql` shapes the Pipeline
   * payload of most pipeline mutations from the `pipelines` row alone and cannot
   * populate it, so those payloads return null here. Selecting this field on a
   * mutation payload other than setPipelineDefaultAssignmentStrategy (which does
   * return the value it wrote) will clobber the cached query value with null.
   */
  defaultAssignmentStrategy?: Maybe<PipelineAssignmentStrategy>;
  /**
   * ENG-414 — the byte-perfect YAML persisted for the version this read
   * resolved (pipeline_versions.definition_yaml, the SAME version steps/
   * dependencies above hydrate from — LIVE by default, the open DRAFT when
   * the caller passes asDraft:true). NULL when the pipeline has no version yet
   * (a from-scratch pipeline that has never been graph-saved). The editor
   * hydrates from THIS field, not from a client-side reconstruction of
   * steps+dependencies into YAML — that reconstruction cannot represent
   * custom_fields kind-specific fields, branchCondition/JSONLogic, barrierTarget,
   * or automation/form steps (which have no pipeline_steps row at all).
   */
  definitionYaml?: Maybe<Scalars['String']['output']>;
  /** Step dependencies (DAG edges) within this pipeline. */
  dependencies: Array<StepDependency>;
  description?: Maybe<Scalars['String']['output']>;
  /**
   * Self-versioning (requirement #2) — the open DRAFT version's id, set by
   * openPipelineForEdit when a published (LIVE/INACTIVE) pipeline is opened for
   * edit. NON-NULL means the pipeline has unpublished edits in flight and can be
   * published in place (promoteDraftToLive), even while `status` is still LIVE.
   * NULL when there is no open draft (a from-scratch DRAFT edits its single
   * version in place; a published pipeline with no draft is immutable).
   */
  draftVersionId?: Maybe<Scalars['ID']['output']>;
  /** Round 4 — when this fork was created. NULL for originals. */
  forkedAt?: Maybe<Scalars['AWSDateTime']['output']>;
  /**
   * Round 4 (v2 feature restoration) — reactflow node positions + viewport
   * state. Empty object when only the YAML editor is used. The canonical DAG
   * lives in the YAML definition; this column is a UI hint.
   */
  graphMetadata?: Maybe<Scalars['AWSJSON']['output']>;
  /**
   * ENG-414 — reactflow node positions + viewport captured on the SAME
   * version `definitionYaml` reflects (pipeline_versions.graph_metadata_json).
   * Distinct from the legacy `graphMetadata` field above, which reads the
   * PARENT `pipelines` row's column rather than the version-scoped one. NULL
   * whenever `definitionYaml` is NULL.
   */
  graphMetadataJson?: Maybe<Scalars['AWSJSON']['output']>;
  id: Scalars['ID']['output'];
  /**
   * True when this pipeline exists only to carry the ORDER-level step DAG
   * (order-scoped steps, no SKU) — the order-step "host" pipeline.
   *
   * ENG-1579 — now READ FROM the declared `pipelines.pipeline_scope` column
   * (db/migrations/0326) rather than inferred from whether any step happened to
   * carry scope='order'. The inferred version could not answer for a payload that
   * did not load steps (hence the nullability), and it made a pipeline's identity a
   * side effect of its step contents — editing steps could silently flip it.
   *
   * Prefer `pipelineScope` below for new code; this stays because several admin
   * surfaces already read it, and it is now exactly `pipelineScope == ORDER`.
   */
  isOrderScoped?: Maybe<Scalars['Boolean']['output']>;
  name: Scalars['String']['output'];
  organizationId: Scalars['ID']['output'];
  parentPipeline?: Maybe<Pipeline>;
  /** Round 4 — pipeline this pipeline was forked from. NULL for originals. */
  parentPipelineId?: Maybe<Scalars['ID']['output']>;
  /**
   * ENG-1579 — the pipeline's DECLARED scope
   * (`pipelines.pipeline_scope`). ORDER is the organization's single order-step
   * host, carrying the order-level DAG; PRODUCT is an ordinary per-SKU pipeline.
   * Authoritative: the editor keys the palette off this, so an order-only node kind
   * is offered only where it can actually run.
   */
  pipelineScope: PipelineScope;
  /**
   * A SKU this pipeline serves (singular; the first when it serves several).
   * Reads the authoritative product_type_pipelines join.
   */
  productType?: Maybe<ProductType>;
  productTypeId?: Maybe<Scalars['ID']['output']>;
  /**
   * All SKUs this pipeline serves. A pipeline can be reused across many SKUs
   * (each SKU still has exactly one pipeline). Backed by the join.
   */
  productTypes: Array<ProductType>;
  /** When this pipeline transitioned DRAFT to LIVE. NULL while DRAFT. */
  publishedAt?: Maybe<Scalars['AWSDateTime']['output']>;
  /** User who published this pipeline (NULL for backfilled-LIVE rows). */
  publishedByUserId?: Maybe<Scalars['ID']['output']>;
  /**
   * P2.4 (2026-04-25) — versioning lifecycle, and the SOLE selectability
   * signal: a pipeline is selectable for new orders iff `status == LIVE` (the
   * former `isActive` field was removed — it had no backing column and was
   * synthesized differently by different resolvers). DRAFT pipelines are
   * editable but not selectable. LIVE pipelines are immutable; in-flight orders
   * pin to the version they started on. INACTIVE is paused (reversible).
   * ARCHIVED pipelines are neither editable nor selectable.
   */
  status: PipelineStatus;
  /** Step definitions in this pipeline. */
  steps: Array<PipelineStep>;
  tenantId: Scalars['ID']['output'];
  updatedAt?: Maybe<Scalars['AWSDateTime']['output']>;
};

/**
 * ENG-894 - the strategy vocabulary the DAG editor and the pipeline-level
 * default speak. Deliberately distinct from `AssignmentPolicy`, which describes
 * `pipeline_steps.assignment_policy` (the DB column the engine reads and that
 * nothing currently writes). `MANUAL` from the step-level picker is absent here:
 * it means "a named user", which is carried by assigneePolicy and is incoherent
 * as a pipeline-wide default.
 */
export enum PipelineAssignmentStrategy {
  LoadBalanced = 'LOAD_BALANCED',
  RoleBased = 'ROLE_BASED',
  RoundRobin = 'ROUND_ROBIN',
  Unassigned = 'UNASSIGNED'
}

/** A page of pipelines. See `Query.pipelinesPage`. */
export type PipelineConnection = {
  __typename?: 'PipelineConnection';
  edges: Array<PipelineEdge>;
  pageInfo: PageInfo;
  /**
   * Total matching the filter, ignoring the page window — the count the list
   * header shows, and what tells a paginator how many pages exist.
   */
  totalCount: Scalars['Int']['output'];
};

/**
 * What a pipeline step consumes, how much per produced unit, and when.
 * CONFIGURATION, not a ledger — the record of an actual movement is an
 * InventoryTransaction. Per docs/clearpath-architecture/03-data-model.md tier 3
 * (`pipeline_step_consumed_inventory`).
 */
export type PipelineConsumptionRule = {
  __typename?: 'PipelineConsumptionRule';
  /**
   * Whether a FAILED attempt still consumes, recorded as SCRAP. Only meaningful
   * for ON_COMPLETE.
   */
  consumeOnFailure: Scalars['Boolean']['output'];
  id: Scalars['ID']['output'];
  inventoryItemId: Scalars['ID']['output'];
  itemCode?: Maybe<Scalars['String']['output']>;
  /**
   * False means the step will silently consume nothing — the engine skips
   * archived items rather than blocking the step.
   */
  itemIsActive?: Maybe<Scalars['Boolean']['output']>;
  itemName?: Maybe<Scalars['String']['output']>;
  itemUnitOfMeasure?: Maybe<Scalars['String']['output']>;
  organizationId: Scalars['ID']['output'];
  pipelineStepId: Scalars['ID']['output'];
  /**
   * Canonical NUMERIC(18,4) decimal string, multiplied by
   * order_products.quantity at consumption time.
   */
  quantityPerUnit: Scalars['String']['output'];
  stepName?: Maybe<Scalars['String']['output']>;
  tenantId: Scalars['ID']['output'];
  triggerMode: ConsumptionTriggerMode;
};

export type PipelineConsumptionRulePayload = {
  __typename?: 'PipelineConsumptionRulePayload';
  rule?: Maybe<PipelineConsumptionRule>;
};

/**
 * Mutation result for deletePipeline. The row is gone — unlike PipelinePayload
 * there is no Pipeline left to return, so this mirrors OrgUnitDeletedPayload.
 */
export type PipelineDeletedPayload = {
  __typename?: 'PipelineDeletedPayload';
  deletedId: Scalars['ID']['output'];
};

export type PipelineEdge = {
  __typename?: 'PipelineEdge';
  cursor: Scalars['String']['output'];
  node: Pipeline;
};

/**
 * Version-proliferation consolidation (2026-07-21) — an append-only per-SAVE
 * ledger row (pipeline_graph_saves). This is the granular autosave history that
 * used to be stamped into pipeline_versions on every keystroke-autosave. It now
 * lives in its own table so pipeline_versions holds ONE row per PUBLISH. Inherits
 * the parent pipeline's RBAC (read requires `clearpath.pipelines.read`).
 */
export type PipelineGraphSave = {
  __typename?: 'PipelineGraphSave';
  /** Canonical YAML at the moment of save. */
  definitionYaml: Scalars['String']['output'];
  /** Reactflow node positions + viewport at the moment of save. */
  graphMetadata?: Maybe<Scalars['AWSJSON']['output']>;
  id: Scalars['ID']['output'];
  /**
   * Optional free-form label. Carries the source version_number (e.g. "v42")
   * for rows migrated out of pipeline_versions.
   */
  label?: Maybe<Scalars['String']['output']>;
  organizationId: Scalars['ID']['output'];
  pipelineId: Scalars['ID']['output'];
  /**
   * The DRAFT/LIVE pipeline version this save targeted, when known. NULL for
   * rows migrated out of pipeline_versions and after that version is removed.
   */
  pipelineVersionId?: Maybe<Scalars['ID']['output']>;
  savedAt: Scalars['AWSDateTime']['output'];
  savedByUser?: Maybe<User>;
  /** User who triggered the save. NULL when their user row was later deleted. */
  savedByUserId?: Maybe<Scalars['ID']['output']>;
};

export type PipelineGraphSaveConnection = {
  __typename?: 'PipelineGraphSaveConnection';
  edges: Array<PipelineGraphSaveEdge>;
  pageInfo: PageInfo;
  totalCount: Scalars['Int']['output'];
};

export type PipelineGraphSaveEdge = {
  __typename?: 'PipelineGraphSaveEdge';
  cursor: Scalars['String']['output'];
  node: PipelineGraphSave;
};

export type PipelinePayload = {
  __typename?: 'PipelinePayload';
  pipeline: Pipeline;
  /**
   * Non-fatal validation notices. One producer today: publishPipeline (e.g. a
   * multi-exit substep group's conditional edge coerced to an unconditional
   * parallel_join — T5/ENG-269 flatten-at-compile, PR #221 review cheap-fix #4).
   * Empty for every other mutation.
   *
   * ENG-27 PR 2/5 (authoring path) will add upsertPipelineGraph and
   * restorePipelineVersion as producers — reporting a checklist binding that did
   * not attach (unbound form_link, form left unselected, duplicate collapsed).
   * This docstring is updated when that slice lands rather than now, since PR 1
   * ships no authoring path.
   */
  warnings: Array<Scalars['String']['output']>;
};

/**
 * Pipeline run — instance per order_product.
 * Per docs/clearpath-architecture/03-data-model.md tier 3 (`pipeline_runs`).
 */
export type PipelineRun = {
  __typename?: 'PipelineRun';
  createdAt: Scalars['AWSDateTime']['output'];
  /** Current active step IDs (parallel — multiple). */
  currentStepIds: Array<Scalars['ID']['output']>;
  endedAt?: Maybe<Scalars['AWSDateTime']['output']>;
  id: Scalars['ID']['output'];
  orderProduct?: Maybe<OrderProduct>;
  /** Order product this run belongs to. */
  orderProductId: Scalars['ID']['output'];
  organizationId: Scalars['ID']['output'];
  pipeline?: Maybe<Pipeline>;
  pipelineId: Scalars['ID']['output'];
  startedAt?: Maybe<Scalars['AWSDateTime']['output']>;
  /** Current run status. */
  status: PipelineRunStatus;
  /** All step runs under this pipeline run. */
  stepRuns: StepRunConnection;
  tenantId: Scalars['ID']['output'];
  updatedAt?: Maybe<Scalars['AWSDateTime']['output']>;
};


/**
 * Pipeline run — instance per order_product.
 * Per docs/clearpath-architecture/03-data-model.md tier 3 (`pipeline_runs`).
 */
export type PipelineRunStepRunsArgs = {
  page?: InputMaybe<PageInput>;
};

/** Pipeline run lifecycle states. */
export enum PipelineRunStatus {
  Cancelled = 'CANCELLED',
  Completed = 'COMPLETED',
  Failed = 'FAILED',
  InProgress = 'IN_PROGRESS',
  Pending = 'PENDING',
  Superseded = 'SUPERSEDED'
}

/**
 * ENG-1579 — which pipelines a query means. Mirrors `pipelines.pipeline_scope`
 * (db/migrations/0326): ORDER is the single order-step host pipeline carrying the
 * order-level DAG, PRODUCT is an ordinary per-SKU pipeline. Distinct from
 * `StepScope`, which describes a STEP.
 */
export enum PipelineScope {
  Order = 'ORDER',
  Product = 'PRODUCT'
}

/** Pipeline lifecycle state. P2.4 (2026-04-25). */
export enum PipelineStatus {
  Archived = 'ARCHIVED',
  Draft = 'DRAFT',
  /**
   * Was live, paused — reversible back to LIVE. Does not route new orders.
   * Used by both pipelines and product_types (0116 added it to pipelines; 0152
   * added it to product_types, replacing the removed is_active toggle).
   */
  Inactive = 'INACTIVE',
  /** Published and in use (product-facing label: "Active"). Immutable DAG. */
  Live = 'LIVE'
}

/**
 * Step definition within a pipeline. Self-referential for substeps.
 * Per docs/clearpath-architecture/03-data-model.md tier 3 (`pipeline_steps`).
 */
export type PipelineStep = {
  __typename?: 'PipelineStep';
  /**
   * Users who work in this step's org unit: its members by department
   * membership (org_unit_memberships, ENG-3920) — a role bound to the unit, a
   * unit above it or a unit below it; never a whole-lab role. Same pool as
   * OrgUnit.members; skills are not applied. Empty when the step is pinned to no
   * unit. Powers the per-step Reassign picker. `search` is case-insensitive
   * substring match against fullName / email.
   */
  assignableUsers: Array<User>;
  /**
   * Auto-assignment policy used by the pipeline engine when a step_run
   * on this step transitions to ready. `MANUAL` leaves the step unassigned
   * (operator picks); `ROUND_ROBIN` cycles through the step's team members
   * using the engine's last_round_robin_user_id cursor; `LOWEST_CAPACITY`
   * picks the team member with the fewest in-flight step_runs.
   */
  assignmentPolicy: AssignmentPolicy;
  createdAt: Scalars['AWSDateTime']['output'];
  customFields?: Maybe<Scalars['AWSJSON']['output']>;
  description?: Maybe<Scalars['String']['output']>;
  /** Estimated duration for ETA calculation. */
  estimatedDurationMinutes?: Maybe<Scalars['Int']['output']>;
  /**
   * ENG-27 — checklists bound to this step, optionally scoped by product type
   * and/or customer party. `isRequired` on each binding row records the intent
   * that the step cannot be completed until required items are answered.
   *
   * Enforcement IS wired. `assertChecklistSatisfied` (`@clearpath/step-gating`,
   * re-thrown as `ChecklistIncomplete` by services/api/src/checklist_gate.ts) runs
   * in `completeStepRun` — see services/api/src/step_runs/complete.ts — and only for
   * PASSED completions, since failing a step is not an attestation. This paragraph
   * previously said the gate was "present but invoked by no completion path yet
   * (PR 1/5)", which stopped being true when PR 3/5 landed and left the SDL
   * describing a live gate as inert — the more dangerous direction of stale, since
   * it invites re-implementing a check that already refuses.
   *
   * Note this is the ENFORCED half. `PipelineStep.formIsRequired` below mirrors the
   * same authoring flag onto the step row but is schema-only; the refusal comes
   * from the `pipeline_step_form_bindings` row that
   * services/api/src/handlers/pipelines/form_bindings.resolve.ts derives from the
   * same canvas node.
   *
   * Before ENG-27 the SDL exposed no per-step form link at all, so the technician
   * UI had to guess which form applied (product-type match, then org-wide, then
   * first, then a hardcoded default) and the only gate was a client-side check
   * keyed off the step's NAME. This field is the authoritative answer once PR 3
   * lands; today it is the durable configuration record.
   *
   * Version-scoped implicitly: a PipelineStep row belongs to one
   * pipeline_version, so a step's bindings are the bindings of that version.
   */
  formBindings: Array<PipelineStepFormBinding>;
  formDefinition?: Maybe<FormDefinition>;
  /**
   * ENG-658 Part E — the form connected to this step via a canvas
   * `form_link` node, resolved from edge adjacency at graph-save time
   * (services/api's graph_sync.ts). form_link itself is never a step in its
   * own right — this is the durable, queryable counterpart to what used to
   * be only a live edge in the canvas's own graph state. Null when no
   * form_link node is connected. QC / STEP_INPUT / STEP_OUTPUT are the
   * FormKind values expected here; ORDER_INTAKE / PRODUCT_INTAKE /
   * CUSTOM_ENTITY forms attach elsewhere (tenant_settings or
   * party_order_form_assignments, product_type_forms, and
   * entity_definition_id respectively).
   */
  formDefinitionId?: Maybe<Scalars['ID']['output']>;
  /**
   * Mirrors the connected form_link node's formIsRequired. Schema-only:
   * not enforced as a step-completion precondition (plan decision #4).
   */
  formIsRequired: Scalars['Boolean']['output'];
  id: Scalars['ID']['output'];
  /** Whether step is mandatory. */
  isMandatory: Scalars['Boolean']['output'];
  name: Scalars['String']['output'];
  orderPipeline?: Maybe<OrderPipeline>;
  /** Owning order-level pipeline (null if product-level). */
  orderPipelineId?: Maybe<Scalars['ID']['output']>;
  orgUnit?: Maybe<OrgUnit>;
  /** Owning org unit (department). */
  orgUnitId?: Maybe<Scalars['ID']['output']>;
  organizationId: Scalars['ID']['output'];
  parent?: Maybe<PipelineStep>;
  /** Parent step (for substeps). */
  parentStepId?: Maybe<Scalars['ID']['output']>;
  pipeline?: Maybe<Pipeline>;
  /** Owning product-level pipeline (null if order-level). */
  pipelineId?: Maybe<Scalars['ID']['output']>;
  /** Position within pipeline. */
  position: Scalars['Int']['output'];
  /** order-scope vs product-scope. */
  scope: StepScope;
  /**
   * Stable per-pipeline step identity (pipeline_steps.step_key). The graph
   * editor upserts rows by (pipelineId, stepKey), so this is the canonical
   * code the canvas must reuse on reload instead of re-deriving one from
   * name+position — re-derivation drifts the key every reload and mints
   * duplicate rows. Null only for legacy rows persisted before step_key
   * existed; callers fall back to deriving a code for those.
   *
   * ENG-408: if a client sees `Validation error ... FieldUndefined: Field
   * 'stepKey' in type 'PipelineStep' is undefined` this field is NOT missing
   * from the SDL/resolver (Query.pipeline / Query.pipelines / Query.pipelineStep
   * in services/api/src/handlers/queries/pipeline-resolvers.ts already project
   * it via `SELECT ps.*` + toCamelRow's snake->camel mapping of pipeline_steps
   * .step_key, confirmed present since ENG-351 / PR #241, 2026-07-20). The
   * error means the AppSync API the client is hitting is running a schema
   * older than this file — this repo's schema.graphql is not what's live until
   * a separate infra-repo `cdk deploy` of the AppSync API stack runs (that
   * deploy is gated on `main`, which lags `dev` — see ENG-383). Fix is an
   * out-of-band schema deploy, not a code change here.
   */
  stepKey?: Maybe<Scalars['String']['output']>;
  stepLibrary?: Maybe<StepLibraryEntry>;
  /** Reference to a reusable step in the step library. */
  stepLibraryId?: Maybe<Scalars['ID']['output']>;
  /** Step type (internal, external_vendor, external_hardware, automatic). */
  stepType: StepType;
  /** Substeps — children of this step. */
  substeps: Array<PipelineStep>;
  tenantId: Scalars['ID']['output'];
  updatedAt?: Maybe<Scalars['AWSDateTime']['output']>;
  /** Vendor selection / SLA override config (JSONB). */
  vendorConfig?: Maybe<Scalars['AWSJSON']['output']>;
};


/**
 * Step definition within a pipeline. Self-referential for substeps.
 * Per docs/clearpath-architecture/03-data-model.md tier 3 (`pipeline_steps`).
 */
export type PipelineStepAssignableUsersArgs = {
  search?: InputMaybe<Scalars['String']['input']>;
};

/**
 * A checklist bound to a pipeline step (ENG-27, `pipeline_step_form_bindings`).
 *
 * Scope semantics: both scope fields null = applies to every order on this step;
 * `productTypeId` set = only orders for that product type; `externalPartyId` set =
 * only orders for that customer. When more than one binding applies to an order,
 * the enforced required-item set is the UNION of their required fields.
 */
export type PipelineStepFormBinding = {
  __typename?: 'PipelineStepFormBinding';
  createdAt: Scalars['AWSDateTime']['output'];
  /** Scope: applies only to orders whose customer party matches. Null = any. */
  externalPartyId?: Maybe<Scalars['ID']['output']>;
  /** The bound `form_definitions` row. */
  formDefinitionId: Scalars['ID']['output'];
  id: Scalars['ID']['output'];
  /**
   * When true, the step cannot be completed until every required item in the
   * bound form is answered. Enforcement lands in PR 3/5 (the gate on all four
   * human completion paths) — this slice ships the intent as durable
   * configuration but invokes no gate yet.
   */
  isRequired: Scalars['Boolean']['output'];
  organizationId: Scalars['ID']['output'];
  pipelineStepId: Scalars['ID']['output'];
  /** Scope: applies only to orders for this product type. Null = any. */
  productTypeId?: Maybe<Scalars['ID']['output']>;
  updatedAt?: Maybe<Scalars['AWSDateTime']['output']>;
};

export type PipelineStepPayload = {
  __typename?: 'PipelineStepPayload';
  pipelineStep: PipelineStep;
};

/**
 * Phase 2 of the unified-canvas rebuild — append-only snapshot of a pipeline
 * graph save. Every successful upsertPipelineGraph stamps one of these so the
 * admin UI can scrub back through history. Snapshots inherit the parent
 * pipeline's RBAC; users with `clearpath.pipelines.read` see them, users with
 * `clearpath.pipelines.write` can restore or label them.
 */
export type PipelineVersion = {
  __typename?: 'PipelineVersion';
  /**
   * ENG-894 - the pipeline-level default assignment strategy AS AT THIS
   * VERSION. Version-scoped like the graph itself, so a history preview can
   * label an inheriting step with the strategy that version actually had rather
   * than the draft's current one. Nullable: rows predating the column, and any
   * value a future migration adds that this build does not know, serialize as
   * null.
   */
  defaultAssignmentStrategy?: Maybe<PipelineAssignmentStrategy>;
  /**
   * Canonical YAML at the moment of save. Restoring writes this back through
   * upsertPipelineGraph (which itself appends a new snapshot row).
   */
  definitionYaml: Scalars['String']['output'];
  /** Reactflow node positions + viewport at the moment of save. */
  graphMetadata?: Maybe<Scalars['AWSJSON']['output']>;
  id: Scalars['ID']['output'];
  /** Optional human-readable label set via labelPipelineVersion. */
  label?: Maybe<Scalars['String']['output']>;
  organizationId: Scalars['ID']['output'];
  pipelineId: Scalars['ID']['output'];
  /**
   * Wall-clock time this version was published (promoted to LIVE). Nullable:
   * DRAFT rows are not published, and seeded/legacy LIVE rows predate the
   * publish stamp, so both legitimately carry null.
   */
  publishedAt?: Maybe<Scalars['AWSDateTime']['output']>;
  savedAt: Scalars['AWSDateTime']['output'];
  savedByUser?: Maybe<User>;
  /** User who triggered the save. NULL when their user row was later deleted. */
  savedByUserId?: Maybe<Scalars['ID']['output']>;
  /**
   * ENG-416 — DRAFT (the pipeline's currently-open, unpublished editable
   * version), LIVE (the version promoted by the most recent publish), or
   * ARCHIVED (a version a prior publish replaced; retained for in-flight
   * pins). The History timeline previously showed every row the same way,
   * making an opened-for-edit draft indistinguishable from an actual publish
   * ("versions appeared without clicking Publish") — callers MUST badge DRAFT
   * rows distinctly rather than treating them as a published release. Never
   * INACTIVE — that pipeline-level status has no version-row analog.
   */
  status: PipelineStatus;
  /** Monotonic counter per pipeline_id, starting at 1. */
  versionNumber: Scalars['Int']['output'];
};

export type PipelineVersionConnection = {
  __typename?: 'PipelineVersionConnection';
  edges: Array<PipelineVersionEdge>;
  pageInfo: PageInfo;
  totalCount: Scalars['Int']['output'];
};

export type PipelineVersionEdge = {
  __typename?: 'PipelineVersionEdge';
  cursor: Scalars['String']['output'];
  node: PipelineVersion;
};

export type PipelineVersionPayload = {
  __typename?: 'PipelineVersionPayload';
  pipelineVersion: PipelineVersion;
};

/**
 * Input for placeOrderHold.
 *
 * Scope is implied by `orderProductId`: supply it to hold ONE unit (the UI
 * default), omit it to hold the whole case. `orderId` is required either way.
 */
export type PlaceOrderHoldInput = {
  /** Optional provenance: the step_run the case was sitting at. Display only. */
  blockedStepRunId?: InputMaybe<Scalars['ID']['input']>;
  /** Optional operator estimate of when the blocker clears. Advisory. */
  expectedResolutionAt?: InputMaybe<Scalars['AWSDateTime']['input']>;
  holdReasonCodeId: Scalars['ID']['input'];
  /**
   * Optional free-text detail ("waiting on shade confirmation from Cedar
   * Ridge").
   */
  note?: InputMaybe<Scalars['String']['input']>;
  orderId: Scalars['ID']['input'];
  /** Omit for a whole-case hold. When supplied, must belong to `orderId`. */
  orderProductId?: InputMaybe<Scalars['ID']['input']>;
};

/** Reconciliation plan mutation entry. */
export type PlanMutation = {
  __typename?: 'PlanMutation';
  action: Scalars['String']['output'];
  details?: Maybe<Scalars['AWSJSON']['output']>;
  entityId?: Maybe<Scalars['ID']['output']>;
  entityName?: Maybe<Scalars['String']['output']>;
  entityType: Scalars['String']['output'];
};

/** One row of the 'open alerts' feed on admin.molaris.ai/observability. */
export type PlatformAlert = {
  __typename?: 'PlatformAlert';
  /** Alarm name as reported by CloudWatch. */
  alarmName: Scalars['String']['output'];
  /** Alarm description pulled from the alarm configuration. */
  description?: Maybe<Scalars['String']['output']>;
  /** CloudWatch metric name that fired. */
  metricName?: Maybe<Scalars['String']['output']>;
  /** CloudWatch namespace (`AWS/Lambda`, `Molarisai/Auth`, ...). */
  metricNamespace?: Maybe<Scalars['String']['output']>;
  /** Short human-readable reason for the current state. */
  reason?: Maybe<Scalars['String']['output']>;
  /** CloudWatch region. */
  region?: Maybe<Scalars['String']['output']>;
  /** Severity bucket (CRITICAL / WARN / INFO). */
  severity: PlatformAlertSeverity;
  /** Slug suitable for looking up the matching runbook markdown. */
  slug: Scalars['String']['output'];
  /** Current alarm state. */
  state: PlatformAlertState;
  /** ISO timestamp of the most recent state change. */
  stateChangedAt?: Maybe<Scalars['AWSDateTime']['output']>;
};

/** Severity tag assigned by the SNS subscriber based on the alarm name. */
export enum PlatformAlertSeverity {
  Critical = 'CRITICAL',
  Info = 'INFO',
  Warn = 'WARN'
}

/** State of a CloudWatch alarm as seen by the status-page subscriber. */
export enum PlatformAlertState {
  Alarm = 'ALARM',
  InsufficientData = 'INSUFFICIENT_DATA',
  Ok = 'OK'
}

/**
 * One row of the platform audit log projection. Control-plane audit_log
 * surfaced to the admin UI with actor_email resolved via a join against
 * staff_tenant_memberships (best-effort; null when the actor isn't in the
 * staff table).
 */
export type PlatformAuditEvent = {
  __typename?: 'PlatformAuditEvent';
  action: Scalars['String']['output'];
  actorEmail?: Maybe<Scalars['String']['output']>;
  actorGlobalId?: Maybe<Scalars['String']['output']>;
  at: Scalars['AWSDateTime']['output'];
  detail?: Maybe<Scalars['AWSJSON']['output']>;
  id: Scalars['ID']['output'];
  targetId?: Maybe<Scalars['String']['output']>;
  targetType: Scalars['String']['output'];
  tenantId?: Maybe<Scalars['ID']['output']>;
};

/** One incident as surfaced on the public status page. */
export type PlatformIncident = {
  __typename?: 'PlatformIncident';
  affectedComponentIds: Array<Scalars['String']['output']>;
  id: Scalars['ID']['output'];
  resolvedAt?: Maybe<Scalars['AWSDateTime']['output']>;
  severity: Scalars['String']['output'];
  startedAt: Scalars['AWSDateTime']['output'];
  status: Scalars['String']['output'];
  title: Scalars['String']['output'];
};

export type PlatformIteroIntegration = {
  __typename?: 'PlatformIteroIntegration';
  configured: Scalars['Boolean']['output'];
  id: Scalars['ID']['output'];
  name: Scalars['String']['output'];
  organizationId: Scalars['ID']['output'];
};

/** Runbook markdown + metadata, served from docs/runbooks/<slug>.md. */
export type PlatformRunbook = {
  __typename?: 'PlatformRunbook';
  markdown: Scalars['String']['output'];
  slug: Scalars['String']['output'];
  title: Scalars['String']['output'];
};

/**
 * P1 #27 — same as SecretRotationStatus, but with the owning tenant's
 * identity attached. Used by the platform admin shell's banner so a
 * Molaris staff member can pivot to the affected tenant in one click.
 */
export type PlatformSecretRotationStatus = {
  __typename?: 'PlatformSecretRotationStatus';
  daysUntilDue?: Maybe<Scalars['Int']['output']>;
  id: Scalars['ID']['output'];
  lastRotatedAt?: Maybe<Scalars['AWSDateTime']['output']>;
  nextRotationDueAt?: Maybe<Scalars['AWSDateTime']['output']>;
  rotationStrategy: Scalars['String']['output'];
  secretPurpose: Scalars['String']['output'];
  tenantId: Scalars['ID']['output'];
  tenantName: Scalars['String']['output'];
  tenantSlug: Scalars['String']['output'];
};

/**
 * Practice-portal pricing visibility for the authenticated tenant (ENG-3064).
 * `hidePrice` is true only for a tenant assigned the control-plane
 * clearpath.portal_hide_pricing rollout; it fails closed to false (price shown)
 * for every other tenant and on any unresolved state. UI-only: list_price still
 * flows to the client.
 */
export type PortalPricingDisplay = {
  __typename?: 'PortalPricingDisplay';
  hidePrice: Scalars['Boolean']['output'];
};

/**
 * Customer-portal role — mirrors db `portal_role_enum` exactly
 * (db/migrations/0001_tier1_identity_org.sql) so the wire enum and the RLS/
 * Cedar-authoritative DB column never drift (ENG-55 root cause: the portal
 * invite form's role options didn't map to any of these four values, so the
 * selected role was silently discarded).
 */
export enum PortalRole {
  ClinicAdmin = 'CLINIC_ADMIN',
  ClinicStaff = 'CLINIC_STAFF',
  Doctor = 'DOCTOR',
  DsoAdmin = 'DSO_ADMIN'
}

/** A customer-safe page of processed scanner cases mapped to the active scope. */
export type PortalScannerIntakeConnection = {
  __typename?: 'PortalScannerIntakeConnection';
  items: Array<PortalScannerIntakeItem>;
  totalCount: Scalars['Int']['output'];
};

/**
 * Customer-safe proof that a scanner case was received and mapped to the
 * active doctor or clinic. Provider identity keys, mapping ids, and raw payloads
 * remain lab-only.
 */
export type PortalScannerIntakeItem = {
  __typename?: 'PortalScannerIntakeItem';
  /** Mapped ClearPath customer/clinic display name within the active scope. */
  customerName?: Maybe<Scalars['String']['output']>;
  id: Scalars['ID']['output'];
  integrationCaseId: Scalars['String']['output'];
  mappingState: PortalScannerMappingState;
  orderId?: Maybe<Scalars['ID']['output']>;
  /** Human-readable ClearPath case/order number when an order exists. */
  orderReference?: Maybe<Scalars['String']['output']>;
  /** Mapped ClearPath practitioner display name; null for clinic-only mappings. */
  practitionerName?: Maybe<Scalars['String']['output']>;
  procedure?: Maybe<Scalars['String']['output']>;
  provider: Scalars['String']['output'];
  receivedAt: Scalars['AWSDateTime']['output'];
  scanDate?: Maybe<Scalars['String']['output']>;
  state: PortalScannerIntakeState;
  subjectRef?: Maybe<Scalars['String']['output']>;
  unitCount: Scalars['Int']['output'];
  units: Array<ScannerRxUnit>;
};

export enum PortalScannerIntakeState {
  /** The lab received the scan but has not submitted a ClearPath order yet. */
  AwaitingLab = 'AWAITING_LAB',
  /** The lab completed intake and created the linked ClearPath order. */
  OrderCreated = 'ORDER_CREATED'
}

export enum PortalScannerMappingState {
  /** The case has a confirmed clinic and doctor mapping. */
  DoctorConfirmed = 'DOCTOR_CONFIRMED',
  /** The case is mapped to the clinic, but no doctor has been confirmed. */
  DoctorNeeded = 'DOCTOR_NEEDED'
}

/**
 * ENG-832 (epic ENG-830) — the effective portal view configuration for one
 * surface, as delivered to the portal client. Presentation only; see
 * Query.portalViewConfig.
 */
export type PortalViewConfig = {
  __typename?: 'PortalViewConfig';
  /**
   * Slot ids hidden by this organization's configuration, after applying the
   * subtractive resolution rules (override + structural cascade). The client does
   * not render these. Never carries WHY a slot is hidden. Capability-based hiding
   * is applied separately on the client with its own capability set, so this list
   * reflects the tenant's configuration only.
   */
  hiddenSlotIds: Array<Scalars['String']['output']>;
  /** The surface this configuration describes (v1: `order_detail`). */
  surface: Scalars['String']['output'];
};

export type PortalViewConfigMutationPayload = {
  __typename?: 'PortalViewConfigMutationPayload';
  portalViewRegistry: PortalViewRegistry;
};

/**
 * ENG-832 (epic ENG-830) — the admin-facing slot vocabulary for one surface:
 * the code-defined registry plus the requesting organization's direct sparse
 * overrides and optimistic-concurrency version. Never exposed to portal
 * customers. See Query.portalViewRegistry.
 */
export type PortalViewRegistry = {
  __typename?: 'PortalViewRegistry';
  slots: Array<PortalViewRegistrySlot>;
  surface: Scalars['String']['output'];
  /**
   * Optimistic-concurrency version. Zero means the organization has no row
   * yet; the first save inserts version one.
   */
  version: Scalars['Int']['output'];
};

/**
 * ENG-832 (epic ENG-830) — one slot in the admin-facing view-config
 * registry.
 */
export type PortalViewRegistrySlot = {
  __typename?: 'PortalViewRegistrySlot';
  /**
   * The Order.* field names this slot's data rides on (empty for cosmetic and
   * container slots). Admin/diagnostic only.
   */
  backingFields: Array<Scalars['String']['output']>;
  /**
   * The organization's direct sparse override, before parent/dependency
   * cascading. This is admin-only state and is never added to PortalViewConfig.
   */
  configuredHidden: Scalars['Boolean']['output'];
  /**
   * Whether hiding this slot is cosmetic only — the control is removed from
   * view but the data is still sent / the underlying action remains permitted.
   * `false` means the slot is backed by a real read path (or is a container whose
   * backed children carry its visibility). The accurate per-slot copy the admin
   * UI must render — a security control — lives in `description` and, for cosmetic
   * slots, `whyNotEnforceable`; this codebase does not encode the finer
   * enforced-vs-partial distinction as a machine tier (see the resolver note).
   */
  cosmeticOnly: Scalars['Boolean']['output'];
  /** A slot whose effective hiding also hides this slot. */
  dependsOnSlotId?: Maybe<Scalars['String']['output']>;
  description: Scalars['String']['output'];
  /** Visible explanation when editable is false. */
  disabledReason?: Maybe<Scalars['String']['output']>;
  /** Whether ENG-835 may submit this slot in an authoring mutation. */
  editable: Scalars['Boolean']['output'];
  /**
   * Presentation grouping (`tab` | `panel` | `container` | `banner` |
   * `summary` | `action`).
   */
  group: Scalars['String']['output'];
  id: Scalars['String']['output'];
  label: Scalars['String']['output'];
  /** Explicit structural parent. Never inferred from the slot-id prefix. */
  parentSlotId?: Maybe<Scalars['String']['output']>;
  /** Reserved ordering value. ENG-835 round-trips it but provides no reorder UI. */
  position?: Maybe<Scalars['Int']['output']>;
  /**
   * Present only for cosmetic slots — rendered VERBATIM by the admin UI so an
   * admin cannot mistake a cosmetic toggle for a confidentiality control.
   */
  whyNotEnforceable?: Maybe<Scalars['String']['output']>;
};

/**
 * One direct sparse portal-view override. `hidden: false` clears the hidden
 * property; an omitted position preserves the stored position.
 */
export type PortalViewSlotOverrideInput = {
  hidden: Scalars['Boolean']['input'];
  position?: InputMaybe<Scalars['Int']['input']>;
  slotId: Scalars['String']['input'];
};

/**
 * Charges attributed to an order's ordering practitioner.
 *
 * BASIS: CHARGES. The sum of `billing_events.billable_amount` for this order,
 * counting status pending / invoiced / paid / disputed, and excluding voided /
 * credited / refunded. `disputed` is deliberately counted: it is still claimed
 * money, and excluding it would make the figure move the moment a dispute opens.
 *
 * Attribution runs through `orders.ordering_practitioner_party_id`, NOT through
 * `billing_events.billable_party_id`. `billing_events` is one row per
 * order-product, so order -> practitioner is many-to-one and no fan-out is
 * possible. It also means a billing event mis-stamped to the wrong party (the
 * ENG-2571 shape) does not move this number.
 *
 * THIS FIGURE WILL NOT RECONCILE WITH `Query.revenueReport`, BY DESIGN. Three
 * deltas, all deliberate:
 *   1. PRE-TAX here; revenueReport is post-tax.
 *   2. Includes PENDING, uninvoiced charges here; revenueReport is invoiced-only.
 *      Surfacing work already performed but not yet billed is the main thing this
 *      field is for.
 *   3. EXCLUDES manual invoice lines that carry no billing event; revenueReport
 *      includes them.
 * Compare the two numbers and they will differ. That is expected — do not file a
 * defect against it.
 *
 * Null unless the caller holds `clearpath.finance.read` (or a role that inherits
 * it). Never visible to a bare `clearpath.orders.view` caller.
 */
export type PractitionerRevenue = {
  __typename?: 'PractitionerRevenue';
  /** Pre-tax charge total. See the type description for the exact basis. */
  amount: Scalars['Float']['output'];
  currency: Scalars['String']['output'];
  /**
   * The attributed practitioner's external party. Null when the order names no
   * ordering practitioner — the amount is then the order's unattributed charges.
   */
  practitionerPartyId?: Maybe<Scalars['ID']['output']>;
};

export type PrepareOroviaReviewInput = {
  fileId: Scalars['ID']['input'];
  mode: OroviaReviewMode;
  orderId: Scalars['ID']['input'];
  revisionId?: InputMaybe<Scalars['ID']['input']>;
};

export type PreviewTenantConfigResult = {
  __typename?: 'PreviewTenantConfigResult';
  estimatedDuration?: Maybe<Scalars['Int']['output']>;
  plan: Array<PlanMutation>;
};

/**
 * Per-Org default catalog.
 * Per docs/clearpath-architecture/03-data-model.md tier 7 (`price_books`).
 */
export type PriceBook = {
  __typename?: 'PriceBook';
  /**
   * Server-synthesized short identifier, unique per Org. Not settable via
   * the API — derived from `name` on create so a non-technical admin never has
   * to invent one or resolve a collision.
   */
  code: Scalars['String']['output'];
  createdAt: Scalars['AWSDateTime']['output'];
  currency: Scalars['String']['output'];
  description?: Maybe<Scalars['String']['output']>;
  entries: Array<PriceBookEntry>;
  id: Scalars['ID']['output'];
  isActive: Scalars['Boolean']['output'];
  isDefault: Scalars['Boolean']['output'];
  name: Scalars['String']['output'];
  organizationId: Scalars['ID']['output'];
  tenantId: Scalars['ID']['output'];
  updatedAt?: Maybe<Scalars['AWSDateTime']['output']>;
};

/** Mutation result for deletePriceBook / deletePriceBookEntry. */
export type PriceBookDeletedPayload = {
  __typename?: 'PriceBookDeletedPayload';
  deletedId: Scalars['ID']['output'];
};

/**
 * Line item in a price book.
 * Per docs/clearpath-architecture/03-data-model.md tier 7 (`price_book_entries`).
 */
export type PriceBookEntry = {
  __typename?: 'PriceBookEntry';
  currency: Scalars['String']['output'];
  id: Scalars['ID']['output'];
  priceBook?: Maybe<PriceBook>;
  priceBookId: Scalars['ID']['output'];
  productType?: Maybe<ProductType>;
  productTypeId: Scalars['ID']['output'];
  unitPrice: Scalars['Float']['output'];
  validFrom?: Maybe<Scalars['AWSDateTime']['output']>;
  /**
   * ENG-2478. The calendar day valid_from was anchored from, interpreted in
   * the organization's CURRENT timezone. No anchoring zone is stored alongside
   * it, so the day is always read against the organization's timezone as it
   * stands now. Null on a legacy row
   * (written before this field existed, or where only an instant was supplied)
   * — validFrom's own UTC calendar day is then the correct read, unchanged from
   * behaviour before this field.
   */
  validFromDay?: Maybe<Scalars['AWSDate']['output']>;
  validUntil?: Maybe<Scalars['AWSDateTime']['output']>;
  /**
   * ENG-2478. The calendar day valid_until was anchored from, interpreted in
   * the organization's CURRENT timezone like validFromDay. The FALLBACK differs,
   * because the two bounds are encoded differently: validUntil is the EXCLUSIVE
   * start of the day after this one, so on a legacy row the day to read is the
   * UTC calendar day of (validUntil - 1ms), not validUntil's own — rendering the
   * instant's own day reads one day late. Null on a legacy row (written before
   * this field existed, or where only an instant was supplied).
   */
  validUntilDay?: Maybe<Scalars['AWSDate']['output']>;
};

export type PriceBookEntryPayload = {
  __typename?: 'PriceBookEntryPayload';
  entry: PriceBookEntry;
};

export type PriceBookPayload = {
  __typename?: 'PriceBookPayload';
  priceBook: PriceBook;
};

/** How pricing conflicts resolve. */
export enum PricingResolutionMode {
  HighestDiscount = 'HIGHEST_DISCOUNT',
  LowestPrice = 'LOWEST_PRICE',
  PartyFirst = 'PARTY_FIRST',
  SkuFirst = 'SKU_FIRST'
}

/** Product registry row. */
export type Product = {
  __typename?: 'Product';
  code: Scalars['String']['output'];
  createdAt: Scalars['AWSDateTime']['output'];
  description?: Maybe<Scalars['String']['output']>;
  id: Scalars['ID']['output'];
  isActive: Scalars['Boolean']['output'];
  name: Scalars['String']['output'];
  permissionNamespace: Scalars['String']['output'];
  updatedAt: Scalars['AWSDateTime']['output'];
};

/**
 * ENG-8 — a tenant-defined product bundle: a named, lifecycle-managed set of
 * component product types. Selecting a LIVE bundle in the new-order wizard expands
 * into its component Units (one order line each, each on its own pipeline); price is
 * the sum of the components. The server-authoritative, shareable catalog version of
 * the client-only OrderTemplate.
 * Per docs/clearpath-architecture/03-data-model.md tier 2 (`product_bundles`).
 */
export type ProductBundle = {
  __typename?: 'ProductBundle';
  /** Stable tenant-unique code. */
  code: Scalars['String']['output'];
  /**
   * Component product types that make up this bundle. Non-null list; the
   * components field resolver hydrates the rows lazily.
   */
  components: Array<ProductBundleComponent>;
  createdAt: Scalars['AWSDateTime']['output'];
  customFields?: Maybe<Scalars['AWSJSON']['output']>;
  description?: Maybe<Scalars['String']['output']>;
  id: Scalars['ID']['output'];
  /** Display name. */
  name: Scalars['String']['output'];
  organizationId: Scalars['ID']['output'];
  publishedAt?: Maybe<Scalars['AWSDateTime']['output']>;
  publishedByUserId?: Maybe<Scalars['ID']['output']>;
  /** DRAFT/LIVE/ARCHIVED lifecycle — see PipelineStatus. Selection filters to LIVE. */
  status: PipelineStatus;
  tenantId: Scalars['ID']['output'];
  updatedAt?: Maybe<Scalars['AWSDateTime']['output']>;
};

/**
 * ENG-8 — one component of a ProductBundle: N Units of a product type, with
 * optional per-component intake defaults.
 */
export type ProductBundleComponent = {
  __typename?: 'ProductBundleComponent';
  createdAt: Scalars['AWSDateTime']['output'];
  /** Per-component intake defaults pre-filled into each expanded Unit; editable. */
  defaultIntakeFormResponse?: Maybe<Scalars['AWSJSON']['output']>;
  id: Scalars['ID']['output'];
  /** Display + expansion order within the bundle. */
  position: Scalars['Int']['output'];
  productBundleId: Scalars['ID']['output'];
  /** The component SKU. */
  productTypeId: Scalars['ID']['output'];
  /** Units of this component the bundle contributes (expands to N order lines). */
  quantity: Scalars['Int']['output'];
  /** V1 always true (reserved for optional components). */
  required: Scalars['Boolean']['output'];
  updatedAt?: Maybe<Scalars['AWSDateTime']['output']>;
};

export type ProductBundlePayload = {
  __typename?: 'ProductBundlePayload';
  productBundle: ProductBundle;
};

/** Product category — hierarchical grouping for SKUs. */
export type ProductCategory = {
  __typename?: 'ProductCategory';
  children: Array<ProductCategory>;
  id: Scalars['ID']['output'];
  name: Scalars['String']['output'];
  organizationId: Scalars['ID']['output'];
  parent?: Maybe<ProductCategory>;
  parentCategoryId?: Maybe<Scalars['ID']['output']>;
  tenantId: Scalars['ID']['output'];
};

/**
 * One order_product's individual work-slip PDF (ENG-521: "3 products = 3
 * work tickets" — Huong Le, lab ops, 2026-07-29). See
 * regenerateProductSlipPdfs's doc comment for why the barcode printed on this
 * ticket is still the ORDER's barcode, not a per-product one.
 */
export type ProductSlipDocument = {
  __typename?: 'ProductSlipDocument';
  orderProductId: Scalars['ID']['output'];
  /**
   * ENG-2547 — base64-encoded PDF bytes for this ticket, replacing the
   * presigned URL this field used to carry. See SlipDocument.pdfBase64.
   */
  pdfBase64: Scalars['String']['output'];
  productName: Scalars['String']['output'];
  /** Deterministic S3 key: orders/{orderId}/products/{orderProductId}/slip.pdf. */
  s3Key: Scalars['String']['output'];
  /**
   * 1-based position of this product among the order's products, in the
   * same order the combined slip prints them (order_products.position, then
   * created_at).
   */
  sequence: Scalars['Int']['output'];
  sku: Scalars['String']['output'];
  /** Total product count on the order at generation time. */
  totalProducts: Scalars['Int']['output'];
};

/**
 * SKU definition — what a tenant manufactures.
 * Per docs/clearpath-architecture/03-data-model.md tier 2 (`product_types`).
 */
export type ProductType = {
  __typename?: 'ProductType';
  category?: Maybe<ProductCategory>;
  /** Stable code (e.g. ZR-CR-3UNIT). */
  code: Scalars['String']['output'];
  /**
   * The name the pinned customer's pricing agreements give this SKU, or null
   * for the standard `name`. Only set by `productTypes(customerPartyId:)`; null on
   * every other read, so null there means "not resolved", not "no contract".
   * Order lines carry their own OrderProduct.contractedName.
   * Resolved by packages/shared-types/src/contracted-sku-name.ts.
   */
  contractedName?: Maybe<Scalars['String']['output']>;
  createdAt: Scalars['AWSDateTime']['output'];
  /** ISO 4217 currency. */
  currency?: Maybe<Scalars['String']['output']>;
  customFields?: Maybe<Scalars['AWSJSON']['output']>;
  id: Scalars['ID']['output'];
  intakeFormDefinition?: Maybe<FormDefinition>;
  /**
   * ENG-658 Part C — the form bound to this SKU via `product_type_forms`
   * (one form per product type; a form may be shared by many product types).
   * Connected through `UpsertProductTypeInput.formDefinitionId`; that form's
   * OWN fields are edited separately via upsertFormDefinition — connect and
   * edit are deliberately two different actions, same split every other
   * form-bound entity in this schema uses. Null when nothing is connected.
   */
  intakeFormDefinitionId?: Maybe<Scalars['ID']['output']>;
  /**
   * Non-null companion to `intakeFormDefinition` — distinguishes "nothing
   * connected" (NONE) from "connected but not published yet" (UNPUBLISHED,
   * fails closed at order-submit time) from "connected and live" (LIVE),
   * which a plain null can't carry without losing information.
   */
  intakeFormResolutionStatus: IntakeFormResolutionStatus;
  /**
   * Out of the tax base everywhere — see `UpsertProductTypeInput.isTaxExempt`.
   * Lines for this SKU carry no tax and exactly one note,
   * `PRODUCT_NOT_TAXABLE`. ENG-2497 slice 4.
   */
  isTaxExempt: Scalars['Boolean']['output'];
  /** List price in default currency. */
  listPrice?: Maybe<Scalars['Float']['output']>;
  /** Display name (e.g. 'Zirconia Crown'). */
  name: Scalars['String']['output'];
  organizationId: Scalars['ID']['output'];
  /**
   * ENG-623 — which customers may order this SKU. Returns the DIRECT grants
   * only (rows whose party_id is that party); inheritance is expanded by the
   * admin UI's party tree, not flattened here, so a DSO grant appears once
   * against the DSO rather than once per descendant. Optional `search` filters
   * by party name. Null if the caller lacks permission to view catalog access
   * for this product type.
   */
  partyAccess?: Maybe<Array<PartyCatalogGrant>>;
  pipeline?: Maybe<Pipeline>;
  /** Default pipeline template attached to this SKU. */
  pipelineId?: Maybe<Scalars['ID']['output']>;
  /**
   * Round 4 (v2 feature restoration) — pipelines linked to this product type
   * via the M:N join. Replaces the legacy 1:1 product_type_id direct FK on
   * pipelines (still kept for back-compat but the join is authoritative going
   * forward). Empty when no pipelines bound.
   */
  pipelines: Array<ProductTypePipelineLink>;
  /** Optional category for grouping. */
  productCategoryId?: Maybe<Scalars['ID']['output']>;
  publishedAt?: Maybe<Scalars['AWSDateTime']['output']>;
  publishedByUserId?: Maybe<Scalars['ID']['output']>;
  /**
   * Versioning lifecycle (stored INTENT). DRAFT = editable, not orderable (the
   * only editable state). LIVE = orderable, immutable. INACTIVE = was live,
   * paused, reversible to LIVE, not orderable. ARCHIVED = retired. Orderability is
   * `status == LIVE` alone (the former `isActive` toggle was removed). Whether a
   * required field is missing is NOT encoded here — see `validity.effectiveStatus`
   * (INVALID) and `validity.reasons`.
   */
  status: PipelineStatus;
  /**
   * Tax category — see `UpsertProductTypeInput.taxCategoryCode`. Null means
   * unclassified, and an unclassified SKU is taxed at the `GENERAL` rate with
   * `CATEGORY_FALLBACK` on the invoice. ENG-2497 slice 4.
   */
  taxCategoryCode?: Maybe<Scalars['String']['output']>;
  /** Why it is exempt. Never blank when `isTaxExempt` is true. */
  taxExemptReason?: Maybe<Scalars['String']['output']>;
  tenantId: Scalars['ID']['output'];
  updatedAt?: Maybe<Scalars['AWSDateTime']['output']>;
  /**
   * Derived completeness (computed on read, never stored). `isValid` is false
   * when a required field is missing (no Live pipeline, no price > 0, or a bound
   * intake form that isn't Live); `effectiveStatus` overlays INVALID on top of
   * `status`; `reasons` lists everything missing. Orderable <=> effectiveStatus
   * == LIVE.
   */
  validity: LifecycleValidity;
  /** Warranty period applied to orders for this SKU. */
  warrantyPeriodDays?: Maybe<Scalars['Int']['output']>;
};


/**
 * SKU definition — what a tenant manufactures.
 * Per docs/clearpath-architecture/03-data-model.md tier 2 (`product_types`).
 */
export type ProductTypePartyAccessArgs = {
  search?: InputMaybe<Scalars['String']['input']>;
};

/**
 * ENG-3054 Story 3.1 — a staged change set for one product: customers to grant,
 * customers to revoke, and an optional independent-group operation. Null or omitted
 * lists mean empty. At most 200 grants and 200 revokes; an id may not appear in
 * both. Ids compare case-insensitively: they are lower-cased before de-duplication
 * and before the both-lists check. Ids that are non-UUID strings, unknown, foreign,
 * ineligible or out-of-book are ignored (see `ignoredIds`), never an error.
 */
export type ProductTypeAccessChangeSetInput = {
  /**
   * Customers to grant the ability to order (converges each to one open-ended
   * group grant).
   */
  grants?: InputMaybe<Array<Scalars['ID']['input']>>;
  /** Grant or revoke every active independent office as one operation. */
  independent?: InputMaybe<CustomerAccessIndependentOp>;
  /** Customers to revoke the ability to order (removes their own direct grant). */
  revokes?: InputMaybe<Array<Scalars['ID']['input']>>;
};

/**
 * ENG-3054 (AD-4) — the tenant-wide customer-access coverage summary for a product,
 * counted in CUSTOMERS at the transaction's now() (the live rule). A parent
 * organization counts as one customer plus its active offices. For an account manager
 * every total is restricted to their book.
 */
export type ProductTypeAccessCoverage = {
  __typename?: 'ProductTypeAccessCoverage';
  /** How many of those can order the product now. */
  canOrder: Scalars['Int']['output'];
  /** Active, eligible customers in the tenant (the denominator). */
  eligibleActive: Scalars['Int']['output'];
  /** Active independent customers (roots with no eligible child). */
  independentActive: Scalars['Int']['output'];
  /** How many of those independent customers can order the product now. */
  independentCanOrder: Scalars['Int']['output'];
  /**
   * Parent organizations with at least one active direct grant for the product
   * (any window) — the "X parent orgs granted" figure.
   */
  parentsGranted: Scalars['Int']['output'];
};

/**
 * ENG-3054 (AD-4) — the single surviving direct grant of a customer for a product:
 * the row convergeGrant would keep among the customer's active direct rows (any
 * window). This is server truth about what is granted ON the customer itself, not
 * what it inherits.
 *
 * CLAMP SENTINELS: `validFrom`/`validUntil` are rendered through the one access
 * instant renderer, which clamps a stored ±infinity or an out-of-range instant (at
 * or after 9999-12-31T23:59:59.999Z, or at or before 0001-01-01T00:00:00.000Z) into
 * the representable AWSDateTime window. Either sentinel can appear on either field:
 * `0001-01-01T00:00:00.000Z` stands for a stored -infinity or at-or-before-0001
 * bound, and `9999-12-31T23:59:59.999Z` for a stored +infinity (start only) or
 * at-or-after-9999 bound. Neither is a literal date; the field docs say what each
 * means on that field. The one exception: a stored +infinity END is never a
 * sentinel, it is a null `validUntil` (open-ended).
 *
 * A `validFrom == validUntil` ZERO-LENGTH WINDOW is possible (a grant that never
 * overlaps `now()`): such a grant's customer cannot order, so pair this with
 * `ProductTypeAccessState.canOrder` rather than inferring order-ability from the
 * window alone.
 */
export type ProductTypeAccessDirectGrant = {
  __typename?: 'ProductTypeAccessDirectGrant';
  grantId: Scalars['ID']['output'];
  scope: PartyCatalogGrantScope;
  /**
   * Grant start, rendered through the server instant clamp. EITHER sentinel is
   * possible: `0001-01-01T00:00:00.000Z` for a stored -infinity / at-or-before-0001
   * start ("no lower bound"), or `9999-12-31T23:59:59.999Z` for a stored +infinity /
   * at-or-after-9999 start ("no reachable start") — neither is a literal date.
   */
  validFrom: Scalars['AWSDateTime']['output'];
  /**
   * Grant end, rendered through the server instant clamp. Null = open-ended (a
   * stored +infinity). `9999-12-31T23:59:59.999Z` is the sentinel for an out-of-range
   * future (at or after 9999-12-31T23:59:59.999Z) end ("no upper bound");
   * `0001-01-01T00:00:00.000Z` for a stored -infinity end — neither is a literal date.
   * May equal `validFrom` (a zero-length window that never covers now).
   */
  validUntil?: Maybe<Scalars['AWSDateTime']['output']>;
};

/**
 * ENG-3054 Story 3.2 — one page of a revoke target's LOSING or KEEPING customers,
 * ordered by name then id. For an account manager the rows and totalCount intersect
 * the book. Pages are not a snapshot: every page re-plans the change set, so a write
 * between pages can shift rows.
 */
export type ProductTypeAccessImpactConnection = {
  __typename?: 'ProductTypeAccessImpactConnection';
  edges: Array<ProductTypeAccessImpactEdge>;
  pageInfo: PageInfo;
  /**
   * The full size of this partition: equal to the preview's losing.count or
   * keeping.count for this target, or independentLosing.count for the independent
   * target, for the same arguments.
   */
  totalCount: Scalars['Int']['output'];
};

/** ENG-3054 Story 3.2 — one impact-list edge. */
export type ProductTypeAccessImpactEdge = {
  __typename?: 'ProductTypeAccessImpactEdge';
  /** Opaque forward cursor of this row's (name, id) position. */
  cursor: Scalars['String']['output'];
  node: ProductTypeAccessImpactRow;
};

/**
 * ENG-3054 Story 3.2 — the arguments of one impact-list page. The change set is
 * the same one sent to productTypeAccessImpactPreview, and the list re-plans it on
 * every page. Exactly one target: `targetPartyId` (one of `changeSet.revokes`) or
 * `targetIndependent: true` (requires `changeSet.independent: REVOKE`, LOSING only).
 * A target in `revokes` that the preview ignores (foreign, unknown, ineligible or
 * out-of-book) returns an empty list.
 */
export type ProductTypeAccessImpactListInput = {
  /** The staged change set (same caps and rules as the preview). */
  changeSet: ProductTypeAccessChangeSetInput;
  /** LOSING or KEEPING. */
  kind: CustomerAccessImpactListKind;
  /** The product (required). */
  productTypeId: Scalars['ID']['input'];
  /**
   * List the combined independent revoke target instead of one party. False or
   * null means absent.
   */
  targetIndependent?: InputMaybe<Scalars['Boolean']['input']>;
  /** The revoke target whose partition to list; must be in changeSet.revokes. */
  targetPartyId?: InputMaybe<Scalars['ID']['input']>;
};

/**
 * ENG-3054 Story 3.1 — the server preview of exactly what saving a change set
 * would do, before any write. All counts are in customers (a parent organization is
 * one customer plus its active offices; practitioners are never counted). Before and
 * after are both measured at now(). For an account manager the DISPLAYED counts and
 * samples (canOrderBefore, canOrderAfter, revokeTargets, independentLosing) intersect
 * the book; losingTotal, losingScheduled and independentTargeted stay org-wide.
 * ignoredIds is not book-intersected, but for an account manager out-of-book ids
 * are counted in it.
 */
export type ProductTypeAccessImpactPreview = {
  __typename?: 'ProductTypeAccessImpactPreview';
  /**
   * Customers that can order at now() once the change set is saved. Book-scoped
   * for an account manager.
   */
  canOrderAfter: Scalars['Int']['output'];
  /**
   * Customers that can order now, before the change (0 when productTypeId is
   * null — the wizard's empty before-state). Book-scoped for an account manager.
   */
  canOrderBefore: Scalars['Int']['output'];
  /**
   * Distinct requested ids that are inert (non-UUID strings, unknown, foreign,
   * ineligible, or out-of-book for an account manager), each counted once; none
   * appear in any set. Not book-intersected; for an account manager, out-of-book
   * ids are counted here.
   */
  ignoredIds: Scalars['Int']['output'];
  /**
   * Customers losing access through the independent revoke (empty otherwise).
   * Book-scoped for an account manager.
   */
  independentLosing: CustomerAccessImpactCount;
  /**
   * The independent offices the independent operation targets: for GRANT, the
   * gaps it will grant (active independent offices that cannot order now, minus any
   * also sent as an explicit revoke); for REVOKE, every active independent office
   * (before exceptions); 0 when no independent operation is staged. Org-wide.
   */
  independentTargeted: Scalars['Int']['output'];
  /**
   * Of losingTotal, the customers whose loss is scheduled for a future instant
   * (a coverage window that ends earlier but still later than now()); they can still
   * order now, so they are not in the canOrderBefore − canOrderAfter drop. Org-wide.
   */
  losingScheduled: Scalars['Int']['output'];
  /**
   * Distinct customers across the whole change set that lose the ability to
   * order. Org-wide (never book-scoped); 0 when productTypeId is null.
   * losingTotal = losingNow + losingScheduled (both org-wide). For an ORG-WIDE
   * caller, canOrderBefore − canOrderAfter = losingNow − gained (grants that gain
   * customers offset the now-losers, so the before → after drop is NOT losingTotal);
   * a book-scoped caller's before/after are book-narrowed, so that identity does not
   * hold against the org-wide total.
   */
  losingTotal: Scalars['Int']['output'];
  /**
   * Per revoke target, who loses and who keeps access. Book-scoped for an account
   * manager.
   */
  revokeTargets: Array<ProductTypeAccessRevokeImpact>;
};

/**
 * ENG-3054 Story 3.2 — one customer in an impact list. Minimal by design: the
 * customer, its direct parent, and when it loses or what keeps it.
 */
export type ProductTypeAccessImpactRow = {
  __typename?: 'ProductTypeAccessImpactRow';
  /**
   * KEEPING rows only (null on LOSING): what keeps it covered. Null in the rare
   * case that no single in-window grant can be named at this instant.
   */
  keptBy?: Maybe<ProductTypeAccessKeptBy>;
  /**
   * LOSING rows only (null on KEEPING): when the customer loses the ability to
   * order — now, or the new, earlier end of its coverage. An instant outside the
   * AWSDateTime range renders as a clamp sentinel (0001-01-01T00:00:00.000Z or
   * 9999-12-31T23:59:59.999Z), not as a real instant.
   */
  losesAt?: Maybe<Scalars['AWSDateTime']['output']>;
  /**
   * The customer's direct parent organization. Null when it has none, when it is
   * not a customer, or for an account manager when it is outside the book.
   */
  parent?: Maybe<CustomerAccessPartyRef>;
  /** The customer. */
  party: CustomerAccessPartyRef;
};

/**
 * ENG-3054 Story 3.2 — what keeps a KEEPING customer able to order after the
 * change: of the grants on its path that reach it now (active or staged), the one
 * that ends latest (open-ended first), the nearest on a tie. A grant that starts
 * later is not considered, even when it would extend the coverage.
 */
export type ProductTypeAccessKeptBy = {
  __typename?: 'ProductTypeAccessKeptBy';
  /**
   * OWN_GRANT when the keeping grant is the customer's own; otherwise
   * ANCESTOR_GRANT.
   */
  kind: CustomerAccessKeptByKind;
  /**
   * The keeping party. Null when it is not a customer, or for an account manager
   * when it is outside the book.
   */
  party?: Maybe<CustomerAccessPartyRef>;
  /** True when the keeping grant is staged in this change set and not yet saved. */
  staged: Scalars['Boolean']['output'];
  /**
   * The keeping grant's OWN end (exclusive). Null when it is open-ended. A lower
   * bound on this customer's coverage, not its end: a later grant can chain on,
   * so the customer may keep access past it. Given even when `party` is null; it
   * reveals no more than `losesAt` already does. A stored end outside the
   * AWSDateTime range renders as a clamp sentinel (0001-01-01T00:00:00.000Z or
   * 9999-12-31T23:59:59.999Z), not as a real instant.
   */
  validUntil?: Maybe<Scalars['AWSDateTime']['output']>;
};

/**
 * ENG-3054 Story 3.1 — for one revoke target, who loses the ability to order and
 * who keeps it (still covered by another grant). Per-target universes may overlap;
 * the change set's distinct total is `losingTotal`.
 */
export type ProductTypeAccessRevokeImpact = {
  __typename?: 'ProductTypeAccessRevokeImpact';
  /** Customers in this target's reach who keep it through another grant. */
  keeping: CustomerAccessImpactCount;
  /** Customers in this target's reach who lose the ability to order. */
  losing: CustomerAccessImpactCount;
  /** The targeted customer party. */
  partyId: Scalars['ID']['output'];
};

/**
 * ENG-3054 (AD-4) — one customer's server-computed access state for a product, at
 * the transaction's now() (the live rule), from the one coverage rule set that the
 * order path uses. Counts are customers, active only. For an account manager every
 * field is restricted to their book; a parent/ancestor outside the book is unnamed
 * (null).
 */
export type ProductTypeAccessState = {
  __typename?: 'ProductTypeAccessState';
  /** Count of this customer's active, eligible, same-org direct child offices. */
  activeChildCount: Scalars['Int']['output'];
  /**
   * Whether this customer can order the product now, by the one order-path rule
   * (own grant or an ancestor's party-and-descendants grant).
   */
  canOrder: Scalars['Boolean']['output'];
  /** How many of those child offices can order the product now. */
  canOrderChildCount: Scalars['Int']['output'];
  /**
   * The surviving direct grant, or null when the customer has no active direct
   * row for the product.
   */
  directGrant?: Maybe<ProductTypeAccessDirectGrant>;
  /**
   * The nearest ELIGIBLE (customer) ancestor organization whose grant covers this
   * customer now (inherited access); null when none, when it is outside an account
   * manager's book, or when coverage comes only through a non-customer ancestor (a
   * vendor's party-and-descendants grant still makes `canOrder` true but is never
   * named here, exactly as `parentName` never names a non-customer).
   */
  inheritedFrom?: Maybe<CustomerAccessPartyRef>;
  /**
   * The direct parent organization's name, only when that parent is an eligible
   * customer in the same org (i.e. this customer is not a root) and, for an account
   * manager, in their book; null otherwise (a root, or a vendor/cross-org/out-of-book
   * parent).
   */
  parentName?: Maybe<Scalars['String']['output']>;
  partyId: Scalars['ID']['output'];
};

/**
 * Mutation result for deleteProductType. The row is gone — mirrors
 * OrgUnitDeletedPayload / PipelineDeletedPayload.
 */
export type ProductTypeDeletedPayload = {
  __typename?: 'ProductTypeDeletedPayload';
  deletedId: Scalars['ID']['output'];
};

export type ProductTypePayload = {
  __typename?: 'ProductTypePayload';
  productType: ProductType;
};

/**
 * Round 4 (v2 feature restoration) — join row between product_type and
 * pipeline. Carries display position + default flag (at most one default per
 * product_type, DB-enforced via the partial unique index).
 */
export type ProductTypePipelineLink = {
  __typename?: 'ProductTypePipelineLink';
  createdAt: Scalars['AWSDateTime']['output'];
  id: Scalars['ID']['output'];
  isDefault: Scalars['Boolean']['output'];
  pipeline: Pipeline;
  pipelineId: Scalars['ID']['output'];
  position: Scalars['Int']['output'];
  productTypeId: Scalars['ID']['output'];
};

export type ProjectionDiscoveryPayload = {
  __typename?: 'ProjectionDiscoveryPayload';
  discovery: ProjectionDiscoveryResult;
};

/**
 * Tally of a projection-discovery run (ENG-1764 s4). COUNTS ONLY — never field
 * identities/labels (those never leave the discovery surface).
 */
export type ProjectionDiscoveryResult = {
  __typename?: 'ProjectionDiscoveryResult';
  /** Total candidates upserted (inserted OR label/type-refreshed) across scanned forms. */
  candidateCount: Scalars['Int']['output'];
  /** How many live intake forms contributed at least one candidate. */
  formsScanned: Scalars['Int']['output'];
  /** Of those, how many were brand-new unreviewed rows (first sighting of the field). */
  insertedCount: Scalars['Int']['output'];
};

/**
 * Governance-only view of a custom-field projection (ENG-1764 s3): its row id +
 * the governance flags a `clearpath.analytics.projection.manage` holder controls.
 * DELIBERATELY excludes json_key / label / sample values — those never leave the
 * governance surface (and never enter the audit trail).
 */
export type ProjectionGovernance = {
  __typename?: 'ProjectionGovernance';
  /**
   * Exposure (lower-cased: exposed/pending_review/unclassified). Only `exposed`
   * becomes catalog-selectable.
   */
  exposure: Scalars['String']['output'];
  id: Scalars['ID']['output'];
  /** PHI class (lower-cased: safe/phi/blocked). */
  phiClass: Scalars['String']['output'];
  /** When it was opted down to non-QI (null unless quasiIdentifier=false). */
  qiOptedDownAt?: Maybe<Scalars['AWSDateTime']['output']>;
  /** Actor who opted the projection down to non-QI (null unless quasiIdentifier=false). */
  qiOptedDownBy?: Maybe<Scalars['ID']['output']>;
  /**
   * k-anon QI floor. true (default) ⇒ a group-by is small-cell suppressed; false ⇒
   * explicitly opted down to non-QI (released from suppression).
   */
  quasiIdentifier: Scalars['Boolean']['output'];
  /** Review status (lower-cased: unreviewed/classified/approved). */
  reviewStatus: Scalars['String']['output'];
};

export type ProjectionGovernancePayload = {
  __typename?: 'ProjectionGovernancePayload';
  projection: ProjectionGovernance;
};

/**
 * A lease on projecting one analytics dashboard to one paired shop-floor
 * device. organizationId is derived server-side from the parent dashboard; the
 * capability allowlist + hard expiry are frozen at cast time.
 */
export type ProjectionGrant = {
  __typename?: 'ProjectionGrant';
  /**
   * The lease's hard stop; the grant and any token minted from it are invalid
   * after this instant regardless of revocation.
   */
  absoluteExpiresAt: Scalars['AWSDateTime']['output'];
  /**
   * Frozen capability ceiling captured at cast time (an allowlist, never the
   * author's resolved capabilities).
   */
  capsAllowlist: Array<Scalars['String']['output']>;
  createdAt: Scalars['AWSDateTime']['output'];
  dashboardId: Scalars['ID']['output'];
  /** RFC-7638 JWK thumbprint of the device's public key, bound at claim time. */
  deviceFingerprint: Scalars['String']['output'];
  /** The paired device this grant leases projection to. */
  devicePairingId: Scalars['ID']['output'];
  id: Scalars['ID']['output'];
  organizationId: Scalars['ID']['output'];
};

/**
 * PHI classification a projection may carry (mirrors the DB enum
 * analytics_projection_phi_class). `blocked` can never be exposed.
 */
export enum ProjectionPhiClass {
  Blocked = 'BLOCKED',
  Phi = 'PHI',
  Safe = 'SAFE'
}

/**
 * Input for proposeFederationLink — initiate an outbound link proposal to
 * a partner tenant. The partner accepts via acceptFederationLink on their
 * side; until then status is 'proposed'.
 */
export type ProposeFederationLinkInput = {
  allowedWorkTypes: Array<Scalars['String']['input']>;
  eventVisibilityConfig?: InputMaybe<Scalars['AWSJSON']['input']>;
  /** Defaults to the caller's organization if omitted. Phase 1.1 (MOL-603). */
  organizationId?: InputMaybe<Scalars['ID']['input']>;
  partnerPublicKey?: InputMaybe<Scalars['String']['input']>;
  partnerTenantId: Scalars['ID']['input'];
  partnerTenantName: Scalars['String']['input'];
};

/**
 * Author + classify + approve an AD-HOC free-text (free_form) custom-field
 * projection in one write (ENG-1872 s8). Unlike approveProjection (which holds a PHI
 * projection at pending_review), a free-text field classified PHI and marked exposed
 * becomes exposure=exposed — coherent under the 0339 CHECK (phi <> blocked). A free-text
 * field has no form field code, so its stable identity IS the custom_fields key:
 * sourceRef is set to jsonKey server-side (source_kind=free_form). baseRelation /
 * jsonbColumn / valueType are validated against the closed whitelist server-side (a value
 * outside the vocabulary is a structured ValidationError). Selectability is still
 * re-guarded fail-closed downstream — a PHI projection compiles ONLY under a caller who
 * holds clearpath.analytics.pii — so authoring one does not itself widen egress.
 */
export type ProposeFreeFormProjectionInput = {
  /** The base relation the field lives on (orders | order_products). */
  baseRelation: Scalars['String']['input'];
  /**
   * Whether to expose the field (make it catalog-eligible). Only a non-blocked field
   * the caller marks exposed becomes exposure=exposed.
   */
  exposed: Scalars['Boolean']['input'];
  /**
   * The custom_fields key extracted. Becomes both json_key and source_ref (the
   * free-form field's stable identity). The only value that reaches SQL, as a $N bind.
   */
  jsonKey: Scalars['String']['input'];
  /** The JSONB column the field lives in (custom_fields | intake_form_response). */
  jsonbColumn: Scalars['String']['input'];
  /** Optional display label (never reaches SQL, never audited). */
  label?: InputMaybe<Scalars['String']['input']>;
  /** PHI classification to assign. Free-text is typically PHI. */
  phiClass: ProjectionPhiClass;
  /** Scalar extraction type (int | bool | string | date | enum). */
  valueType: Scalars['String']['input'];
};

/** Input for provisionTenant. Idempotent by slug. */
export type ProvisionTenantInput = {
  /** Billing email for invoices; falls back to initialAdminEmail. */
  billingEmail?: InputMaybe<Scalars['String']['input']>;
  /** Industry seed template key: 'dental-lab' | 'generic-manufacturer' | 'aligners'. */
  industry: Scalars['String']['input'];
  /** Email of the initial tenant admin (will receive an invite). */
  initialAdminEmail: Scalars['String']['input'];
  /** Human-readable tenant name (e.g., 'Aspen Dental Lab'). */
  name: Scalars['String']['input'];
  /** DNS-safe slug: lowercase alphanumeric + hyphen. Matches /^[a-z0-9][a-z0-9-]{1,62}$/. */
  slug: Scalars['String']['input'];
  /** Subdomain under molaris.ai; defaults to slug. */
  subdomain?: InputMaybe<Scalars['String']['input']>;
  /** Entitlement tier: 'starter' (default) | 'pro' | 'enterprise'. */
  tier?: InputMaybe<Scalars['String']['input']>;
};

/** Payload returned by provisionTenant. */
export type ProvisionTenantPayload = {
  __typename?: 'ProvisionTenantPayload';
  /** Correlation id threaded through every audit entry for this provisioning run. */
  correlationId: Scalars['String']['output'];
  /** End-to-end provisioning duration in milliseconds. */
  durationMs: Scalars['Int']['output'];
  /** Current lifecycle state (ACTIVE on success, earlier on partial progress). */
  lifecycle: Scalars['String']['output'];
  /** Organization row id inside the tenant DB. */
  organizationId?: Maybe<Scalars['ID']['output']>;
  tenant: Tenant;
};

/**
 * Synthetic provisioning event projection over the control-plane audit_log
 * (actions tenants.create / tenants.update_status / tenant.provision.* /
 * tenant.deprovision.*). Historically emitted from the provisioner pipeline.
 */
export type ProvisioningEvent = {
  __typename?: 'ProvisioningEvent';
  at: Scalars['AWSDateTime']['output'];
  detail?: Maybe<Scalars['String']['output']>;
  id: Scalars['ID']['output'];
  /** Event kind: stable enum-ish string the dashboard UI groups on. */
  kind: Scalars['String']['output'];
  /**
   * Whether the audited step succeeded. False for rows whose action ends
   * in .failed; true otherwise.
   */
  ok: Scalars['Boolean']['output'];
  tenantId: Scalars['ID']['output'];
};

/**
 * Input for publishAssistantResult — SYSTEM-ONLY (Lambda + IAM), mirroring
 * CreateNotificationInput. The async worker calls this over a SigV4 (AWS_IAM)
 * request to fan out the terminal result on onAssistantResult. NEVER reachable by
 * a user token: identity + authorization for the turn were fixed at enqueue and
 * are NOT re-derived from this input. ENG-2250.
 */
export type PublishAssistantResultInput = {
  /** Conversation the turn belongs to. */
  conversationId?: InputMaybe<Scalars['ID']['input']>;
  /**
   * Structured error/refusal envelope on FAILED (an encodeErrorEnvelope
   * string). Null on SUCCEEDED.
   */
  errorEnvelope?: InputMaybe<Scalars['String']['input']>;
  /**
   * On-frame size hint surfaced as AssistantJob.hasProposal — true when
   * `result` carries an analytics proposal.
   */
  hasProposal: Scalars['Boolean']['input'];
  /**
   * The job being completed — the subscription filter key. The mutation call
   * MUST select `jobId` in its selection set or the @aws_subscribe fan-out
   * silently drops the frame (see AssistantJob doc + notifications/push.ts).
   */
  jobId: Scalars['ID']['input'];
  /**
   * The full terminal result, serialized as AWSJSON:
   * `{ answer, toolTrace, tokensUsed, proposal }`. Carried as JSON (not duplicate
   * input types) because this is an internal system→system contract, exactly like
   * CreateNotificationInput.payload. The resolver persists it to the job row; the
   * assistantJob(jobId) poll re-projects it into the structured AssistantJob
   * fields. Null / partial on FAILED. Poll-delivered — never selected onto the
   * subscription frame (WS + 8KB WAF limits).
   */
  result?: InputMaybe<Scalars['AWSJSON']['input']>;
  /** Terminal status: SUCCEEDED or FAILED. RUNNING is never published. */
  status: AssistantJobStatus;
};

/**
 * Shared input for the P2.4 publish*\/archive* mutations on the four
 * non-pipeline lifecycle entities (product_types, form_definitions,
 * automation_rules, sla_policies). Pipelines kept their own input shape for
 * backwards-compat but use the same `{ id }` payload.
 */
export type PublishLifecycleInput = {
  id: Scalars['ID']['input'];
};

/** Input for publishPipeline (P2.4). */
export type PublishPipelineInput = {
  /**
   * ENG-416 concurrency guard. Pass the Pipeline.draftVersionId the editor
   * had loaded (null if none) when the user clicked Publish. If the pipeline's
   * CURRENT draft_version_id no longer matches — e.g. a concurrent discard +
   * re-open minted a different draft since this editor session started —
   * the mutation is rejected with a Conflict instead of silently promoting a
   * draft the caller never saw. Omit to skip the check (back-compat).
   */
  expectedDraftVersionId?: InputMaybe<Scalars['ID']['input']>;
  id: Scalars['ID']['input'];
};

export type PublishWorkInvalidationInput = {
  lane: WorkInvalidationLane;
  routes: Array<Scalars['String']['input']>;
  signalId: Scalars['ID']['input'];
};

/** Aggregate QC quality metrics for the caller's organization. */
export type QcMetrics = {
  __typename?: 'QcMetrics';
  /** Defect counts grouped by reason code, descending by count. */
  defectsByReason: Array<DefectReasonCount>;
  failedCount: Scalars['Int']['output'];
  /**
   * First-pass yield over first attempts: attempt-1 passed / attempt-1
   * terminal. Range 0..1; 0 when no first-attempt terminal runs.
   */
  firstPassYield: Scalars['Float']['output'];
  passedCount: Scalars['Int']['output'];
  /** Rework rate: failed / totalEvaluated. Range 0..1; 0 when none. */
  reworkRate: Scalars['Float']['output'];
  /** Terminal QC step_runs considered (outcome passed + failed). */
  totalEvaluated: Scalars['Int']['output'];
};

/**
 * Shared filter for qcMetrics + defectRegistry. All optional (AND
 * semantics). `occurredAt` bounds step_runs.created_at.
 */
export type QcMetricsFilterInput = {
  occurredAt?: InputMaybe<DateRangeInput>;
  stepDefinitionId?: InputMaybe<Scalars['ID']['input']>;
};

export type Query = {
  __typename?: 'Query';
  /**
   * P1.7 (2026-04-25) — current account-deletion request status for the
   * calling customer. Returns NONE when no request is in flight.
   */
  accountDeletionStatus: AccountDeletionStatus;
  /**
   * The per-tenant, PHI-filtered candidate catalog the builder MFE selects
   * measures/dimensions from. Fail-closed: an entry is served ONLY when exposed AND
   * affirmatively PHI-safe; PHI-classed base fields and tenant projections are
   * ABSENT for every caller (the dimension-level PHI entitlement gate is RA-P3-4,
   * not yet built). The base surface is the curated seed catalog re-filtered through
   * the SAME selectability guard the compiler uses; the tenant's opted-in scalar
   * custom-field projections are read live and classified through the SAME merged
   * projection seam. Display-companion columns (e.g. a step's human label) are NOT
   * independently selectable — the served catalog exposes one Step dimension keyed
   * on the step code. Reads are ORG-WIDE within the caller's tenant, served under an
   * org-wide `service` DB session whose org binding is the AUTHENTICATED caller's —
   * never client input. Gated by clearpath.analytics.view (staff-only) and the
   * analytics-module feature flag. See handlers/analytics/catalog.ts.
   */
  analyticsCatalog: AnalyticsCatalog;
  /**
   * A single analytics dashboard by id, or null when it is not visible to the
   * caller (a different org's dashboard is invisible under RLS). Visible when the
   * caller owns it or it is shared with them.
   */
  analyticsDashboard?: Maybe<AnalyticsDashboard>;
  /**
   * The shares on one of the caller's OWN dashboards. Owner-scoped: a non-owner
   * gets an empty list. Per ENG-1656 / T15.
   */
  analyticsDashboardShares: Array<AnalyticsDashboardShare>;
  /**
   * The authenticated caller's own analytics dashboards ("my dashboards"),
   * newest first. Owner-scoped: returns only dashboards where ownerUserId is the
   * caller. Org isolation is the RLS backstop.
   */
  analyticsDashboards: Array<AnalyticsDashboard>;
  /**
   * Dashboards shared TO the authenticated caller ("shared with me"), newest
   * first. Read-only for the sharee; excludes the caller's own dashboards.
   */
  analyticsDashboardsSharedWithMe: Array<AnalyticsDashboard>;
  /**
   * Drill ONE released analytics aggregate cell to the caller's OWN underlying order
   * rows (ENG-2022 M1 / B1 slice) `[SEC]`. `cell` is an AWSJSON `{ query, coordinates }`:
   * the SemanticQuery that produced the aggregate (measures + grouping dimensions + grain,
   * NO client filters) and the coordinate VALUES of the ONE cell to open (exactly the
   * grouping dimensions — no extra coordinate). The cell is honored ONLY if RELEASED by
   * k-anon small-cell suppression (count(distinct order_id) >= the floor); a suppressed /
   * dropped / never-released cell is refused, not returned as rows. Drill is defined only
   * for cells whose measure source relation carries an order_id (order_fact /
   * order_unit_fact); an sla_evaluation_fact cell is refused wholesale (relation gate).
   * Supports the three B1 dimensions (date, lifecycle_state, customer_org) via faithful
   * operational filters AND the four B2 dimensions (completed_date, product_type,
   * product_family, region_code) via an id-set derived from the cell's single fact relation
   * under the caller's own security_invoker session; a composite cell with any B2 dimension
   * takes the B2 path (B1 dimensions become extra conjuncts), and an unsupported dimension
   * refuses the whole cell (refusal dominates B2 dominates B1). Detail rows are read ONLY
   * under the caller's own session (RLS re-applies; a service/system session is structurally
   * refused), so the result is the caller's OWN slice — NO remainder, NO total, only a keyset
   * cursor over the caller's OWN authorized rows. Gated by clearpath.analytics.drill (a SURFACE gate
   * that AND-composes on top of operational order-read, never replacing it) and the
   * analytics-module feature flag. One counts-only analytics.drill_read audit row is
   * written fail-closed before return.
   */
  analyticsDrillDetail: AnalyticsDrillDetailResult;
  /**
   * Serve a BEHAVIORAL (event) FUNNEL for the caller's tenant (ENG-2032 / E1-5)
   * `[SEC]`. `funnel` is a serialized EventFunnelQuery (AWSJSON): a pinned
   * `subjectType` (never `user`), an ordered `steps` spine (each an event_type + an
   * optional stage-identity predicate), an optional `conversionWindowSeconds`, and an
   * optional `breakdownBy` (customer_org / region_code). Reads are ORG-WIDE within the
   * caller's tenant over the append-only `events` table, enforced by an ORG-SCOPED
   * `service` DB session whose org binding is the AUTHENTICATED caller's — never client
   * input — with a system/BYPASSRLS session refused (the events FORCE-RLS policy is the
   * tenant boundary). Every breakdown / stage-identity dimension key is allowlisted
   * against the event registry BEFORE compile; client filters and path/flow query
   * shapes are rejected (v1 serves the funnel_stage shape only). k-anon disclosure
   * control (event-kanon) is applied at the executor tail: a below-k stage cell is
   * suppressed (counts nulled, `suppressed:true`), the order-grain population is the
   * floor, and precise dwell times are coarsened. A customer_org breakdown additionally
   * requires clearpath.analytics.customers.read. RESULT BOUND (ENG-2202): a breakdown
   * makes the cardinality steps x distinct-breakdown-values, so the aggregate is subject
   * to the same 50,000-row ceiling as analyticsQuery and is REFUSED — never truncated —
   * with `extensions.reason: RESULT_TOO_LARGE`. The refusal is decided on the RAW row
   * count BEFORE k-anon suppression, so it never varies with which cells were suppressed.
   * Gated by clearpath.analytics.view (and the analytics-module feature flag).
   */
  analyticsEventFunnel: AnalyticsEventFunnelResult;
  /**
   * Serve the case-lifecycle FUNNEL for the caller's tenant (ENG-1793). `stages`
   * is the ordered set of canonical spine stages to report — each one of
   * received|design|produce|qc|ship|seat; output is always in canonical spine order
   * regardless of request order, so the reached-or-beyond counting is well-defined.
   * Reads are ORG-WIDE within the caller's tenant (the funnel counts every unit in
   * the org, not just the caller's own), enforced by an org-wide `service` DB session
   * whose org binding is the AUTHENTICATED caller's — never client input — over the
   * unit_stage_fact view (security_invoker; RLS is the cross-tenant backstop). A
   * step-binding stage (design/produce/qc) the tenant never bound is returned
   * `configured:false` with null measures (fail-closed — never a guessed zero). The
   * optional `splitBy` is DEFERRED in v1: the argument is reserved but a non-null
   * value is rejected with a ValidationError (the v1 result is the flat, no-split
   * funnel). Gated by clearpath.analytics.view (and the analytics-module feature
   * flag). RESULT BOUND (ENG-2202): the aggregate is subject to the same 50,000-row
   * ceiling as analyticsQuery and is REFUSED — never truncated, which would read as a
   * real drop-off — with `extensions.reason: RESULT_TOO_LARGE`. `[SEC]`.
   */
  analyticsFunnel: AnalyticsFunnelResult;
  /**
   * The authenticated caller's own Overview KPI-board personalization, or null
   * when they have never personalized (the client then renders the code default:
   * all KPIs shown, in catalog order, with default targets). Self-scoped to the
   * caller; org isolation is the RLS backstop.
   */
  analyticsOverviewPreferences?: Maybe<AnalyticsOverviewPreferences>;
  /**
   * EVERY custom-field projection in the caller's org WITH its row id + governance
   * metadata — the review-queue read behind the analytics-admin "Fields" surface.
   * Unlike Query.analyticsCatalog (which serves only the selectable, safe subset and
   * never the row id), this returns unreviewed candidates, held/hidden rows,
   * phi/blocked rows and released rows alike, each with the id the s3/s4 governance
   * mutations key off. Gated on clearpath.analytics.projection.manage (the SAME
   * Director-own key as the governance writes, behind the analytics-module flag), read
   * org-scoped under the org-wide `service` session; db/rls/0082 is the cross-tenant
   * backstop. Returns governance METADATA ONLY — never json_key VALUES / the JSONB
   * blob / any tenant answer. See handlers/analytics/projection-governance-list.ts.
   */
  analyticsProjectionGovernance: Array<AnalyticsProjectionGovernanceRow>;
  /**
   * Run a SemanticQuery (validate -> compile -> execute) and return its tabular
   * result. `query` is the serialized SemanticQuery contract from
   * @clearpath/analytics-semantics (measures, dimensions, grain, optional filters +
   * viz). Reads are ORG-WIDE within the caller's tenant (an aggregate counts every
   * order in the org, not only the caller's own), enforced by an org-wide `service`
   * DB session whose org binding is the AUTHENTICATED caller's — never client input.
   * Financial (MONEY) measures are dropped server-side unless the caller also holds
   * clearpath.finance.read; `excludedMeasures` reports any dropped. Gated by
   * clearpath.analytics.view (and the analytics-module feature flag, composed via
   * ENG-1649 / T8's Cedar policy). RESULT BOUND (ENG-2202): a result that would
   * exceed 50,000 rows is REFUSED with a structured ValidationError
   * (`extensions.reason: RESULT_TOO_LARGE`, `extensions.cap: 50000`) — never
   * silently truncated, and never disclosing the actual row count. Narrow the
   * query (coarser grain, fewer break-downs, shorter date range) and retry.
   */
  analyticsQuery: AnalyticsQueryResult;
  /**
   * Execute up to 24 SemanticQueries (one per dashboard tile) in ONE round-trip,
   * for a multi-tile exec board (Overview) that would otherwise fire N separate
   * analyticsQuery calls. Each tile runs through the SAME server-side gated path as
   * Query.analyticsQuery (server-side measure gating -> validate -> compile ->
   * execute under an org-wide `service` session whose org binding is the
   * AUTHENTICATED caller's — never client input), with the caller's capabilities
   * resolved ONCE and applied to every tile, so a batched tile is gated IDENTICALLY
   * to a standalone query: financial (MONEY) measures are dropped unless the caller
   * also holds clearpath.finance.read, and the batch can never expose a measure the
   * caller could not read standalone. All tiles run in ONE org-wide `service`
   * transaction; RLS on the fact views is the hard cross-tenant backstop. Per-tile
   * error isolation: one malformed / all-gated-out tile returns THAT tile's error
   * (`ok:false`) while the others still return data. The whole call is rejected only
   * for a structural problem: unauthenticated / unpermitted, over the 24-tile limit,
   * a non-array `items`, an item missing its `key`, or duplicate keys. Gated by
   * clearpath.analytics.view (and the analytics-module feature flag). `[SEC]`
   * (ENG-1779).
   */
  analyticsQueryBatch: AnalyticsBatchQueryPayload;
  /** The org's target for one measure, or null when none is set. Org-visible. */
  analyticsTarget?: Maybe<AnalyticsTarget>;
  /**
   * Every analytics target in the caller's org (org-VISIBLE — a target is a
   * shared org goal). Ordered by measureKey. Org isolation is the RLS backstop.
   */
  analyticsTargets: Array<AnalyticsTarget>;
  /**
   * The org's reusable analytics TEMPLATES — dashboards flagged
   * `definition.template = true`, newest first. ORG-VISIBLE (not owner-scoped): any
   * clearpath.analytics.view holder in the org may list AND instantiate them — that
   * is the point of a template. This is a DELIBERATE departure from the ENG-1951
   * owner-scoping and is safe because the read is AND-scoped on the template flag,
   * so it returns ONLY template rows and NEVER an arbitrary/private dashboard.
   * Optional `kind` narrows to "report" or "board". Org isolation is the db/rls/0080
   * backstop. See handlers/analytics/templates.ts. `[SEC]` (ENG-2112 / T3).
   */
  analyticsTemplates: Array<AnalyticsDashboard>;
  /**
   * Rank the top `limit` customers by case (distinct-order) volume for the
   * caller's tenant (ENG-1807) — the TopPractices leaderboard. Reads are ORG-WIDE
   * within the tenant (the aggregate counts every order in the org), enforced by an
   * org-wide `service` DB session whose org binding is the AUTHENTICATED caller's —
   * never client input. It is behind k-anon small-cell suppression: a customer with
   * fewer than the suppression floor of orders is DROPPED, so the leaderboard only
   * shows customers at or above it. Each customer's NAME is resolved ONLY under the
   * caller's own visibility lens: for a customer outside that lens, `name` is null,
   * `inScope` is false, and `practiceId` is an OPAQUE per-response alias — the real
   * out-of-scope party id never egresses. Gated by a dedicated read capability,
   * clearpath.analytics.customers.read (distinct from clearpath.analytics.view), and
   * the analytics-module feature flag. `[SEC]`.
   */
  analyticsTopPractices: AnalyticsTopPracticesResult;
  /**
   * The widgets on a dashboard, ordered by position. Empty when the dashboard
   * is not visible to the caller.
   */
  analyticsWidgets: Array<AnalyticsWidget>;
  /**
   * Outstanding AR aged into current / 1-30 / 31-60 / 61-90 / 90+ buckets,
   * grouped by currency AND by billing party. Computed server-side: this is the
   * one place the aging arithmetic lives, so the AR cockpit, the nav badge and
   * the aging screen cannot drift from each other (and cannot re-introduce the
   * mixed-currency client-side sum it replaces). FIN-M1-01 (ENG-1446).
   */
  arAging: ArAging;
  assignWorkInvalidationPlan: WorkInvalidationPlan;
  /**
   * ENG-3921 — the org units the viewer may ASSIGN work in: the "All
   * Departments" half of the Department board's and Assign work page's filters.
   * Requires clearpath.assignments.write (not the RBAC-admin org-structure key
   * `orgUnits` needs, which a Department Head or Manager does not hold). Bounded by
   * the caller's work scope — the units Query.unassignedWork accepts as a filter:
   * the caller's units and the units below and above them — so an unscoped
   * (whole-lab) assigner gets every unit. Archived units are not offered.
   * Identity only (id / name / type / parent). Empty for a caller without
   * clearpath.assignments.write. A unit ABOVE the caller's is offered as a filter
   * but its steps fail manualAssignStep's scope check (see unassignedWork).
   */
  assignableOrgUnits: Array<OrgUnit>;
  /**
   * Candidate technicians for a step run (capacity + availability + shifts).
   *
   * Candidates are the MEMBERS of the step's unit (OrgUnit.members:
   * org_unit_memberships, both closure directions). A whole-lab role is
   * authority, not membership, so whole-lab people are never candidates; a
   * tenant whose staff are all whole-lab gets an empty list for a pinned step.
   * This manual picker does not filter on skills, machines or certifications
   * (smartAssignPreview does), but manualAssignStep refuses an assignee who
   * lacks them. The caller needs clearpath.assignments.read and the step's unit
   * inside their scope (their units and the units below; whole lab: any), else
   * `scope_not_entitled`. Empty for a step pinned to no unit.
   */
  assignmentCandidates: Array<AssignmentCandidate>;
  /**
   * Poll an async assistant job by id (ENG-2250, Phase 1). Poll fallback for
   * the onAssistantResult subscription AND the delivery path for a large
   * proposal/answer that must not ride the WS frame (8KB WAF cap). Returns null
   * for an unknown job. Same principal gate as askAssistant — a caller polls their
   * own turn; RLS on the job row is the real cross-user-read boundary.
   */
  assistantJob?: Maybe<AssistantJob>;
  /** Audit log — requires permission. */
  auditLog: AuditLogEntryConnection;
  /** Lookup a single AutomationRule by ID. */
  automationRule?: Maybe<AutomationRule>;
  /**
   * All automation rules. T12/ENG-370: optional pipelineId filters to rules
   * attached to that pipeline (drives the T13 filtered index).
   */
  automationRules: Array<AutomationRule>;
  /** Barcode scan history (paginated). */
  barcodeScans: BarcodeScanConnection;
  /** Barcode scans for one order since a cutoff. MOL-563. */
  barcodeScansForOrder: Array<BarcodeScan>;
  /** Paginated list of billing adjustments. MOL-620. */
  billingAdjustments: Array<BillingAdjustment>;
  /** Paginated, filterable list of billing events. */
  billingEvents: BillingEventConnection;
  /**
   * The caller's org's live CALCULATED (derived) measures — the read side of
   * the ENG-1803 create/update mutations, so the Builder UI can list persisted
   * measures WITH their formula (returned as a raw JSON object) and description.
   * ORG-WIDE, not owner-scoped: a calculated measure is org catalog data every
   * analytics viewer in the org sees (ownerUserId is provenance only). Gated on
   * clearpath.analytics.view (formula/description are staff-visible, not PHI);
   * org isolation is the db/rls/0085 backstop, so no cross-org measure is ever
   * returned. Ordered by key. See handlers/analytics/calculated_measures.ts.
   *
   * `includeArchived` defaults to false (live measures only, the authoring list).
   * Pass true to also return archived (soft-deleted) measures — the future
   * "Archived" management section — each carrying its `archivedAt` timestamp.
   */
  calculatedMeasures: Array<CalculatedMeasure>;
  /**
   * The caller's own case-health aggregate for the portal dashboard: counts of
   * their in-flight cases by lifecycle bucket, plus the 30-day seated outcome.
   * Server-computed and org- + party-scoped IDENTICALLY to `orders` (bound
   * organization_id + `customerOrderScopePredicate`, RLS backstop db/rls/0058).
   * Portal customers carry no dotted RBAC key here (their scope is the SQL
   * predicate; an empty scope counts nothing); non-customer callers are gated on
   * `clearpath.orders.view`. Returns only aggregate counts, never a case row.
   */
  caseHealth: CaseHealthSummary;
  /**
   * ENG-2101 — bounded catalog-browser sibling of `productTypes` for internal
   * staff. Search (name or code), taxonomy code narrowing, orderability filtering,
   * and pagination happen on the server so `/catalog` never downloads or renders
   * the tenant's complete SKU list in one request.
   */
  catalogProductTypes: CatalogProductTypeConnection;
  /**
   * Round 6 — direct read of the tenant catalog taxonomy. Same payload
   * as TenantSettings.catalogTaxonomy; surfaced as a top-level query so
   * the catalog browser does not have to fetch the entire TenantSettings
   * shape.
   */
  catalogTaxonomy: CatalogTaxonomy;
  /** Certification catalog for the org (ENG-148) — used to set per-step required certifications. */
  certificationDefinitions: Array<CertificationDefinition>;
  /**
   * One customer's operational analytics — order volume, on-time %, rework
   * rate, and lead time — computed from that customer's real orders. `range`
   * bounds the windowed metrics on order created_at (activeOrderCount is
   * point-in-time). Returns an all-zero payload for a customer with no orders in
   * the window.
   */
  clientAnalytics: ClientAnalytics;
  /** Tenant-scoped comment categories (admin-managed). */
  commentCategories: Array<CommentCategory>;
  /**
   * Comments scoped to a polymorphic target.
   * @deprecated Caps at 500 rows sorted oldest-first, so a long thread silently loses its NEWEST comments (ENG-2211). Use commentsConnection.
   */
  comments: Array<Comment>;
  /**
   * ENG-2211 — paginated comments on a polymorphic target. Ordering is unchanged
   * (oldest-first, as a thread reads), so paging forward walks toward the most
   * recent message.
   */
  commentsConnection: CommentConnection;
  /** Current viewer's primary Organization. */
  currentOrganization?: Maybe<Organization>;
  /** Currently-applied tenant config for the viewer's tenant. */
  currentTenantConfig?: Maybe<TenantConfigDocument>;
  /**
   * ENG-3054 Story 2.5 — for each listed parent, how many of its DIRECT children
   * match the Customer access results view's narrowing, in one grouped read per
   * results page. For the same `search` / `accessProductTypeId` / `canOrder`, each
   * `matchCount` equals `externalPartiesConnection(customerEligibleOnly: true,
   * parentPartyId: <id>, ...)`'s `totalCount`: the same tenant, actor and
   * account-manager book scope, the same trimmed, escaped name search, the same
   * eligibility and canOrder rules and the same 2 s read cap. Internal staff only,
   * behind clearpath.catalog_entitlements.read; read-only and
   * lock-free. `parentPartyIds`: at most 50 entries, every one a UUID (else a
   * validation error, never a clamp), lower-cased and deduped; `search` at most 200
   * characters. Every distinct id (lower-cased, deduped) gets exactly one entry, in
   * first-seen order. The parent itself must be in the org and the caller's scope
   * and book (stricter than the connection's `parentPartyId`, which narrows only the
   * children); any other id, or one with no matching child, reads 0 (no existence
   * signal). The canOrder pair goes together; a non-UUID or foreign product reads 0
   * for every id.
   */
  customerAccessMatchCounts: Array<CustomerAccessMatchCount>;
  /**
   * Paginated list of customer_delegations rows the caller may see — the
   * practitioner x staff delegation matrix. Requires
   * `clearpath.portal_users.read` (ENG-1881: this read carried no capability check
   * at all, so every internal staff principal could enumerate the tenant's whole
   * delegation graph). All four portal roles hold that key and
   * `clearpath.portal_users.manage` remains the gate on create/revoke/extend.
   * HOW MANY rows a permitted caller sees is still decided by the RESTRICTIVE RLS
   * lens in db/rls/0020, which narrows portal callers by portal role, so this
   * permission grants the right to ask, not a wider row set. For a caller's OWN
   * delegations use `myDelegations`, which is self-scoped and needs no capability.
   * `activeOnly` defaults to true.
   */
  customerDelegations: CustomerDelegationConnection;
  /**
   * ENG-3286 — the same answer for an order not yet submitted: the customer's
   * default and, for an override holder, exactly the terms submitOrder accepts.
   */
  customerShippingTermsOptions: ShippingTermsOptions;
  /**
   * The exemption certificates this organization holds on file — the customers
   * it does NOT charge tax, and why. ENG-2497 slice 5.
   *
   * Gates like its two siblings above: `requireInternalActor` FIRST, then
   * `clearpath.finance.read`. The WRITE affordances gate on the narrower
   * `clearpath.tax.manage` inside the page.
   *
   * ⚠️ Every certificate relieves the BILLABLE party, never the ship-to — see
   * `CustomerTaxExemption`.
   */
  customerTaxExemptions: CustomerTaxExemptionList;
  /**
   * Paginated defect registry — failed step_runs with their reason code +
   * owning order/step. Ordered by occurredAt DESC.
   */
  defectRegistry: DefectRegistry;
  /**
   * ENG-656 - delete-confirmation preflight: the references that will block
   * the entity's hard-delete, as the same operator-facing phrases the delete
   * mutation's refusal message uses (e.g. "5 pipeline steps"). Empty = the
   * delete would currently succeed. Advisory only - the delete mutation keeps
   * its own authoritative check. Enforces the SAME per-entity permission gate
   * as the corresponding delete mutation (resolver-side, since the key varies
   * by entity); NotFound for ids outside the caller's org.
   */
  deleteBlockers: Array<Scalars['String']['output']>;
  departmentWorkInvalidationPlan: WorkInvalidationPlan;
  /**
   * Payment disputes / chargebacks for the caller's organization, newest
   * first. An org with no disputes returns `[]`, never null. FIN-M1-04
   * (ENG-1449).
   */
  disputes: Array<Dispute>;
  /** All entity definitions (custom entity types). */
  entityDefinitions: Array<EntityDefinition>;
  /** Single escalation chain by id. */
  escalationChain?: Maybe<EscalationChain>;
  /** All escalation chains for the viewer's organization. Phase 5 (MOL-562). */
  escalationChains: Array<EscalationChain>;
  /**
   * Escalation events audit stream. Filterable by step run, chain, and time.
   * 2026-04-25 — declared nullable so the resolver's Cedar denial (caller lacks
   * clearpath.sla.read) surfaces as `null` rather than an unrecoverable NonNull
   * violation. Every UI consumer already coalesces with `?? []`.
   * @deprecated Caps at 500 rows with no way to detect truncation (ENG-2211). Use escalationEventsConnection.
   */
  escalationEvents?: Maybe<Array<EscalationEvent>>;
  /**
   * ENG-2211 — paginated escalation events. Same filter, same permission, same
   * nullability as the list above; adds `page` and reports `totalCount` so a
   * caller can tell a quiet chain from a truncated one.
   */
  escalationEventsConnection?: Maybe<EscalationEventConnection>;
  /** Event log — requires permission. */
  events: EventConnection;
  /** LLM-generated plain-English explanation of a config document. */
  explainTenantConfig: ExplainConfigResult;
  /**
   * Export a dashboard's data as CSV (and a print-ready document). Re-runs each
   * widget's saved SemanticQuery through the SAME server-side gated path as
   * Query.analyticsQuery (validate -> compile -> execute under an org-wide `service`
   * session whose org binding is the AUTHENTICATED caller's), so financial (MONEY)
   * measures are dropped unless the caller also holds clearpath.finance.read and an
   * export can never expose data the viewer could not see in-app. Aggregate data
   * only. `filters` (optional) is a JSON array of SemanticFilters ANDed onto every
   * widget query (they may only narrow results on declared dimension columns).
   * NotFound when the dashboard id is not visible to the caller. Gated by
   * clearpath.analytics.view then clearpath.analytics.export (and the
   * analytics-module feature flag); writes exactly one analytics.export audit row
   * (PHI-egress). `[SEC]` (ENG-1657 / T16).
   */
  exportAnalyticsDashboard: AnalyticsExportPayload;
  /**
   * A finance report as .xlsx (default) or .csv, inline as base64. Same gate and filter as
   * `financeReport`; INVOICED_LIST takes `format` plus the InvoiceFilterInput fields
   * and the gate of `exportInvoices`. Over the row or byte cap it refuses with
   * ValidationError reason EXPORT_TOO_LARGE; a read that runs too long refuses with
   * reason EXPORT_TIMEOUT. Writes one keys-only audit row.
   */
  exportFinanceReport: FinanceReportFile;
  /**
   * ENG-3738 — every invoice matching `filter` (the SAME scope and filter as
   * `Query.invoices`, not one page) as an .xlsx file, returned inline as base64.
   * Staff only; also requires clearpath.billing.read, and clearpath.orders.view
   * for the line formats. Line formats exclude drafts and voided invoices and put
   * each currency on its own worksheet (an all-USD export is one sheet). Over the
   * row or byte cap it refuses with ValidationError reason EXPORT_TOO_LARGE; a
   * read that runs too long refuses with reason EXPORT_TIMEOUT. Writes one
   * `invoices.export` audit row.
   */
  exportInvoices: InvoiceExportFile;
  /** Lookup a single external integration by id. */
  externalIntegration?: Maybe<ExternalIntegration>;
  /** List external integrations for the caller's tenant. */
  externalIntegrations: Array<ExternalIntegration>;
  /**
   * All external parties (customers/vendors).
   *
   * DEPRECATED (P0a): this bare list hard-caps at 500 rows with no
   * hasNextPage/totalCount, so at DSO scale offices past the 500th silently vanish
   * from pickers, and it has no server-side name search. Use
   * `externalPartiesConnection`, which pages (keyset) and searches on the server.
   * @deprecated P0a: caps at 500 with no paging/search signal; use externalPartiesConnection
   */
  externalParties: Array<ExternalParty>;
  /**
   * P0a — paginated, server-side-searchable external parties for DSO-scale
   * pickers. Keyset pagination (order by name), server-side `search` (name,
   * trigram/ILIKE), `type` (partyType) and `parentPartyId` (direct children)
   * filters. Same tenant + per-actor scope as `externalParties` (ENG-194): a
   * customer sees only their own party subtree, internal is unrestricted (with
   * account-manager narrowing), other actor types are denied. Every filter is
   * narrowing-only; none widens access relative to the bare list.
   */
  externalPartiesConnection: ExternalPartyConnection;
  /** Lookup a single ExternalParty by ID. */
  externalParty?: Maybe<ExternalParty>;
  /** Lookup a single FailureReasonCode by ID. */
  failureReasonCode?: Maybe<FailureReasonCode>;
  /**
   * All failure reason codes. Optionally scoped by `stepDefinitionId`
   * (codes bound to that step OR org-wide) or `pipelineId` (codes bound to
   * any step in the pipeline OR org-wide). Phase-5 admin (MOL-669).
   */
  failureReasonCodes: Array<FailureReasonCode>;
  /** Lookup one flag by id. */
  featureFlag?: Maybe<FeatureFlag>;
  /** All feature-flag definitions. Molaris staff only. */
  featureFlags: Array<FeatureFlag>;
  /** Lookup a single FederationLink by ID. */
  federationLink?: Maybe<FederationLink>;
  /**
   * Paginated, filterable list of federation links for the current viewer's
   * Organization. Phase 1.1 (MOL-603).
   */
  federationLinks: FederationLinkConnection;
  /**
   * Threaded federation messages for a specific work item (step_run / order).
   * Scoped by federation_link via the underlying work item. Phase 9 (MOL-608).
   * `page` is accepted for forward compat; the initial implementation returns
   * the full thread oldest-first. Phase 1.1 (MOL-603).
   */
  federationMessages: Array<FederationMessage>;
  /**
   * All tenant-configurable field_labels rows for the viewer's org,
   * optionally filtered by an internal field_name prefix (e.g. 'orders.')
   * and locale. Per docs/clearpath-architecture/03-data-model.md tier 1.
   * Backs the admin `/admin/custom-fields` list + form-builder field palette.
   */
  fieldLabels: Array<FieldLabel>;
  /** Lookup a single File by ID. */
  file?: Maybe<File>;
  /** All physical locations for a single file (MOL-583). */
  fileLocations: Array<FileLocation>;
  /** Files for the current viewer (paginated). */
  files: FileConnection;
  /**
   * The CFO's org-wide AR and cash position in one call: open receivable and
   * the past-due part of it per currency, month-to-date days sales outstanding per
   * currency, the full aging grid, and the worst-paying accounts.
   *
   * ONE POPULATION — every figure is derived from the same row set `arAging`
   * returns for the same `asOf` (embedded whole as `aging`), so the tiles, the grid
   * and the accounts table cannot disagree. Nothing is summed across currencies.
   *
   * STAFF-ONLY and ORG-WIDE, gated identically to `revenueReport`: the resolver
   * refuses any non-internal session outright BEFORE it reads anything, then
   * requires `clearpath.finance.read` (granted only to the Finance Director role;
   * NOT the `clearpath.billing.read` key portal roles hold). The registry has no
   * audience column, so the whole lab's receivable in a customer's or vendor's
   * hands is a data breach, not a filtered read — there is no party-scoped variant
   * to degrade to.
   *
   * Collection Effectiveness Index and the historical, point-in-time collection
   * metric are NOT here: both need the M5 period-close AR snapshot (ENG-1453) and
   * arrive in FIN-M6 slice 5. `periodStatus` is LIVE until then; treat the figures
   * as provisional. FIN-M6 slice 4 (ENG-1480).
   */
  financeArDashboard: ArDashboard;
  /**
   * One page of a finance report, plus per-currency totals over the full
   * filter. `filter` is the report's own JSON object; unknown keys are a
   * ValidationError. Staff only; each report then requires its own permission
   * (clearpath.finance.read unless the report says otherwise), so no single key is
   * advertised here. Export-only reports (INVOICED_LIST) are refused. A read that
   * runs too long refuses with ValidationError reason REPORT_TIMEOUT.
   */
  financeReport: FinanceReportPage;
  /**
   * ENG-4024 — the first-bill tripwire's open checks: each assumed-$0 migrated
   * agreement line holding a charge, with the orders it holds and the default-book
   * price each would otherwise have taken. Staff only (`requireInternalActor`
   * first), then `clearpath.billing.read`.
   */
  firstBillChecks: FirstBillChecksPayload;
  /**
   * Flag assignments, optionally filtered by flag key or scope. Molaris staff only.
   * Pagination uses the opaque `after` cursor returned with the previous page.
   */
  flagAssignments: Array<FlagAssignment>;
  /** Lookup a single FormDefinition by ID. */
  formDefinition?: Maybe<FormDefinition>;
  /**
   * ENG-780 forms lifecycle versioning — the paginated PER-VERSION timeline for
   * a form: the current DRAFT plus every LIVE/ARCHIVED version, newest first
   * (sorted DESC on versionNumber). Read-only; powers the form-editor version
   * history / scrub-back panel. Requires `clearpath.forms.read`.
   */
  formDefinitionVersions: FormDefinitionVersionConnection;
  /**
   * Form definitions. 2026-04-27 — declared nullable so a Cedar denial
   * (caller lacks clearpath.forms.read) surfaces as `null` rather than an
   * unrecoverable NonNull violation. Every UI consumer already coalesces
   * with `?? []`.
   *
   * ENG-1073 removed the `isActive` arg along with the column. Availability is
   * the lifecycle alone now: pass `status: LIVE` for "currently serving".
   *
   * `status` (2026-08-05) narrows to a single lifecycle status — e.g. a
   * product/order intake-form PICKER should pass `LIVE` so it only offers
   * forms that are actually published, unlike the Forms Library management
   * list, which wants every status and omits this arg.
   */
  formDefinitions?: Maybe<Array<FormDefinition>>;
  /**
   * ENG-658 — lookup a single library field by ID. Renamed from
   * attributeDefinition; the permission key (clearpath.attribute_library.read)
   * is unchanged — it's a persisted RBAC grant, not part of this rename.
   */
  formField?: Maybe<FormField>;
  /**
   * ENG-658 — the org's reusable form-field library. Declared nullable
   * (Cedar-denial-as-null, same convention as `formDefinitions`). Renamed
   * from attributeDefinitions.
   */
  formFields?: Maybe<Array<FormField>>;
  /** Current FX rates for the requested currencies. MOL-621. */
  fxRates: Array<FxRate>;
  /** Hello-world placeholder. Phase 0 backward-compat — DO NOT REMOVE. */
  helloWorld?: Maybe<HelloWorld>;
  /**
   * The tenant's hold reason taxonomy, ordered by `position` then `label`.
   * Source for the Hold dialog's reason dropdown (ENG-16).
   *
   * `includeInactive` defaults false, so the dropdown only ever offers reasons a
   * lab admin has not retired. Pass true for admin screens that must still render
   * the label of a retired reason on a historical hold.
   *
   * Gated on `clearpath.orders.hold`: if you cannot place a hold there is no
   * reason to enumerate the taxonomy. An empty list is a real, expected state (a
   * tenant that has not been seeded/backfilled) and the UI must render "no hold
   * reasons configured" rather than an empty select.
   */
  holdReasonCodes: Array<HoldReasonCode>;
  /** All inventory items. */
  inventoryItems: Array<InventoryItem>;
  /** Movement ledger, newest first. Omit itemId for the whole organization. */
  inventoryTransactions: InventoryTransactionConnection;
  /**
   * Public-token lookup for the invitee's AcceptInvitationPage. The token
   * IS the invitation id (UUID, ~122 bits of entropy — sufficient as a
   * bearer secret for this use). NOT gated by Cedar — anyone in possession
   * of the token can read the invite's public-safe attributes. The caller
   * MUST still be authenticated against the tenant's Cognito pool, which
   * means in practice they've already signed in (or signed up) before this
   * resolver runs. Returns null when the token doesn't match a row in this
   * tenant. Round 1b — v2 feature restoration.
   */
  invitation?: Maybe<InvitationLookup>;
  /**
   * Outstanding invitations for the tenant — drives admin-invitations
   * page. Filter by status to narrow to pending / accepted / revoked /
   * expired. The expired derivation is done at read time (no sweep job).
   * (P0.4.3)
   */
  invitations: InvitationConnection;
  /**
   * Lookup a single Invoice by ID.
   *
   * ONE REFUSAL SHAPE, and it is chosen by WHAT was refused, not by which field
   * you called. The same rule governs `Query.invoices`:
   *
   *   * The CALLER may not read invoices at all — no `clearpath.invoices.view`,
   *     by role or by the billing-principal grant — is a `Forbidden` error, here
   *     and on the list. A caller-level refusal tells the client something true
   *     about the session and nothing about any row.
   *   * The ROW is not theirs, is a draft they may not see, or does not exist:
   *     `null` here, absent from the page on the list. Never `Forbidden`. These
   *     three are deliberately INDISTINGUISHABLE — answering differently for an
   *     invoice that exists but belongs to another account turns an unguessable
   *     id into an oracle for whose invoice it is.
   *
   * The two are not interchangeable and neither can be collapsed into the other:
   * a client that treats `null` as "permission denied" will tell a practitioner
   * she lacks access to an invoice that simply is not hers, and a list that
   * answered an empty page instead of `Forbidden` would hide a broken session
   * behind "you have no invoices".
   */
  invoice?: Maybe<Invoice>;
  /** Short-lived presigned URL to download the invoice PDF (ENG-1256). */
  invoiceDownloadUrl: Scalars['AWSURL']['output'];
  /**
   * Paginated, filterable list of invoices.
   *
   * Refuses on the same rule as `Query.invoice` — read it there. In this field's
   * terms: no `clearpath.invoices.view` is `Forbidden`; rows the caller may not
   * see are simply not in the page, including when `filter.billingPartyIds` names
   * another account (the filter is AND-ed with the caller's party scope, so it can
   * only ever subtract).
   */
  invoices: InvoiceConnection;
  /** Redacted iTero connection state for one integration owned by this tenant. */
  iteroConnection: IteroConnection;
  /**
   * Order lead time (created -> delivered/closed) — avg/median/p90/min/max in
   * hours, overall + by product type.
   */
  leadTimeMetrics: LeadTimeMetrics;
  /**
   * ENG-3881 — the chart of accounts, ordered by account number. Staff only;
   * requires clearpath.finance.read. Inactive accounts only when `includeInactive`.
   */
  ledgerAccounts: Array<LedgerAccount>;
  /** ENG-3881 — customer ledger classes. Staff only; requires clearpath.finance.read. */
  ledgerCustomerClasses: Array<LedgerCustomerClass>;
  /**
   * ENG-3881 — the organization's fee definitions (code and label), so the
   * ledger page can name mapped fees. Staff only; requires clearpath.finance.read.
   */
  ledgerFeeDefinitions: Array<LedgerFeeDefinition>;
  /**
   * ENG-3881 — what would post UNMAPPED today, and mappings that no longer
   * resolve. Staff only; requires clearpath.finance.read.
   */
  ledgerMappingCoverage: LedgerMappingCoverage;
  /**
   * ENG-3881 — ledger mappings, optionally one kind. Staff only; requires
   * clearpath.finance.read.
   */
  ledgerMappings: Array<LedgerMapping>;
  /**
   * ENG-3881 — every ClearPath transaction type with its SYSTEM-mapped account.
   * Staff only; requires clearpath.finance.read.
   */
  ledgerTransactionTypes: Array<LedgerTransactionType>;
  /** Machine catalog for the org (ENG-148) — used to set per-step required machines. */
  machines: Array<Machine>;
  /** Currently authenticated user. */
  me?: Maybe<User>;
  /**
   * ENG-3954 — per-record landed-UUID acknowledgement. Resolve a partner's
   * legacy ids back to the ClearPath uuids they landed on, for one
   * (sourceSystem, entityKind), in a single indexed crosswalk lookup. Unknown ids
   * are omitted, never an error. Writes nothing but a counts-only audit row;
   * staff-only, gated on the dedicated `clearpath.migration.read` key.
   */
  migrationResolveCrosswalk: MigrationCrosswalkLookupResult;
  /**
   * ENG-2986 (pilot P-03) — the read-only C3/C4 SKU-set alignment gate. Reports
   * the DRAFT migrated SKUs with no org-wide pipeline that the Wave-3 publish flip
   * will refuse (A \\ B, excluding pipeline_exempt), and the defensive B \\ A
   * anomalies. `passed` is false exactly when orphans is non-empty. Writes nothing
   * but an audit row; gated on `clearpath.pipelines.read`.
   */
  migrationVerifySkuAlignment: MigrationSkuAlignmentResult;
  /** Molaris-admin (control plane) read namespace. Cedar-gated by molaris.*.* keys. */
  molarisAdmin: MolarisAdminQuery;
  /**
   * Active sessions for the calling user (never-revoked sessions across
   * every product the user has touched). Per
   * docs/clearpath-architecture/11-sessions-and-tokens.md.
   */
  myActiveSessions: Array<UserSession>;
  /** API keys visible to the caller — scoped to the caller's organisation. */
  myApiKeys: Array<ApiKey>;
  /**
   * Aggregated billing state for the portal Billing home view. Auto-scoped
   * to the viewer's billable party closure (doctor self, DSO parent → child
   * clinics, clinic_staff → delegated doctors). MOL-622.
   */
  myBillingSummary: BillingSummary;
  /**
   * Active delegations for the signed-in clinic_staff user — the doctor
   * parties they can currently act on behalf of. Returns [] for other roles.
   */
  myDelegations: Array<CustomerDelegation>;
  /**
   * Social / SSO provider links for the calling customer identity. Per
   * docs/clearpath-architecture/09-authentication.md.
   */
  myIdentityProviderLinks: Array<IdentityProviderLink>;
  /** All hardware integrations for the caller's tenant (active + disabled). */
  myIntegrations: Array<IntegrationConnection>;
  /**
   * The caller's own server-stored order-wizard drafts (ENG-1750), newest
   * first. Self-scoped: the SQL binds WHERE user_id = viewer AND organization_id =
   * viewer's org (RLS db/rls/0086 is the backstop), so the scope IS the
   * authorization — no @requiresPermission, and deliberately NOT @audited (a draft
   * is in-flight UI state, not a business fact).
   */
  myOrderDrafts: Array<OrderDraft>;
  /**
   * The viewer's available portal org scopes — the set of parties
   * (DSO / clinic / doctor / delegated-doctor) they can switch to. Union
   * of: their home party (users.home_party_id) + delegated doctor
   * parties + descendants of the home party (via
   * external_parties.parent_party_id) for DSO admins. Drives the
   * portal's OrgScopeSwitcher. (P0.4.7)
   */
  myOrganizationScopes: Array<OrgScope>;
  /**
   * The caller's "Needs your review" queue (ENG-19): cases with an open
   * `AWAITING_CUSTOMER_APPROVAL` hold, oldest first, plus a count for the nav badge.
   *
   * Owner-scoped IDENTICALLY to `orders`: a portal practitioner sees ONLY their own
   * pending approvals (own party + delegated + own submissions), an office/
   * practice-admin role sees the practice's, and no caller sees another practice's.
   * Portal
   * customers carry no dotted RBAC key here (their scope is the SQL predicate);
   * non-customer callers are gated on `clearpath.orders.view`.
   */
  myPendingApprovals: PendingApprovalConnection;
  /**
   * Processed scanner cases mapped to the signed-in doctor's active party or
   * clinic admin's exact active clinic. Completely unmapped cases remain lab-only.
   */
  myScannerIntake: PortalScannerIntakeConnection;
  /**
   * Round 5 — scopes the calling user has visibility for. Used by the
   * topbar scope picker. Includes scopes derived from `user_roles`,
   * `user_org_units`, team membership, and any explicit scopes minted via
   * JIT grants.
   */
  myScopes: Array<UserScope>;
  /**
   * Vendor inbox — orders visible to the viewer via either (a) a Mode B
   * share link they have issued OR (b) a Mode A federated-in order.
   */
  mySharedOrders: Array<OrderSummary>;
  /** The calling technician's own shift assignments. */
  myShifts: Array<MyShift>;
  /**
   * Tenant memberships for the calling customer (global_customer_id ↔ tenant
   * rows). Per docs/clearpath-architecture/01-tenancy-model.md.
   */
  myTenantMemberships: Array<TenantMembership>;
  /** The caller's own time-off requests. */
  myTimeOffRequests: Array<TimeOffRequest>;
  /**
   * Trusted devices for the calling user (device-trust "remember me" records).
   * User-scoped: returns rows owned by the caller. Per
   * docs/clearpath-architecture/11-sessions-and-tokens.md.
   */
  myTrustedDevices: Array<TrustedDevice>;
  /**
   * ENG-1959 — the org units the viewer WORKS IN: the units their
   * department-bound role assignments are placed in, the units those belong to,
   * and everything below them. Since ENG-3920 this is the viewer's department
   * membership, org_unit_memberships() (db/migrations/0574), not the access
   * scope Query.stepRuns narrows work by: a whole-lab row adds no unit.
   *
   * Exists because `orgUnits` is gated on the RBAC-admin org-structure read key
   * (ENG-47), which a Lead Technician does not hold — so the board they CAN open
   * had no way to name the departments they work in. This field needs no admin
   * key: it returns only units the caller can already see work in, and only their
   * identity (id / name / type / parent), never membership or settings.
   *
   * Unlike a list derived from returned work rows, it includes units with NO work
   * in them, which is what a FILTER needs — a department with nothing in it is a
   * meaningful thing to select and see empty.
   *
   * An unscoped caller (Lab Owner, Director — tenant-wide grants with a null
   * org_unit_id) belongs to NO unit and gets an EMPTY list. That is deliberate and
   * is where this field parts company with the work queries: they read a null scope
   * as "no narrowing, show everything", which is right for WORK and wrong for
   * MEMBERSHIP. An owner sees every department's work and is a member of none.
   */
  myWorkOrgUnits: Array<OrgUnit>;
  /**
   * ENG-3097 — short-lived signed install links for the NAS agent, one entry per
   * supported target (ordered WINDOWS_AMD64, LINUX_AMD64, LINUX_ARM64; `input.target`
   * restricts to one), for the internal admin setup wizard. Internal org users only
   * (portal and vendor sessions are refused); requires `clearpath.nas.configure`
   * (feature-flag-gated on `clearpath.nas_module`). Step-up MFA is DECLARED for this
   * permission (permission registry) but is currently WAIVED (warn mode) under the
   * step-up rollout ledger (services/api/src/auth/step_up_rollout.ts) — the same
   * level as generateNasEnrollmentCode. Each link is minted by the nas-url-minter
   * per request (never cached by the API) and lives about 10 minutes — always use
   * `expiresAt`. The URLs are bearer credentials: never log or persist them. A
   * target with no published, non-yanked release on this environment's release
   * channel (`agentSettings.channel`) has `release: null`. Enforced
   * server-side in services/api/src/handlers/nas_agent_downloads.ts; every call
   * that creates links writes an audit row (without the URL).
   */
  nasAgentDownloads: NasAgentDownloadsPayload;
  /**
   * ENG-1353 / NAS-14 — the org's enrolled NAS sync agents and their last-known
   * heartbeat + telemetry, for the internal NAS admin dashboard. A plain list
   * (agents-per-org is small); sorted server-side by hostname. Gated on
   * `clearpath.nas.read` (feature-flag-gated on `clearpath.nas_module`, no step-up
   * MFA — it is a read). Enforced server-side in
   * services/api/src/handlers/nas_agents.ts (requireInternalActor +
   * requirePermission); RLS org-fences the result to the caller's org.
   */
  nasAgents: Array<NasAgent>;
  /**
   * ENG-3853 — the organization's NAS file-format policy: the file formats sent
   * to its NAS workstations. Before the organization sets one, the built-in
   * default applies (isDefault). Gated on `clearpath.nas.configure`
   * (feature-flag-gated on `clearpath.nas_module`). Labels and categories for
   * each key come from @clearpath/shared-types/nas-file-formats.
   */
  nasFormatPolicy: NasFormatPolicy;
  /**
   * ENG-3975 — how long the organization's NAS workstations keep an order's
   * files after they stop needing them. Before the organization sets one, the
   * 24-hour default applies (isDefault). Gated on `clearpath.nas.configure`
   * (feature-flag-gated on `clearpath.nas_module`).
   */
  nasHoldingGrace: NasHoldingGrace;
  /**
   * ENG-2583 — the GLOBAL published NAS agent build catalog (non-tenant,
   * read-only) used to pick a rollout target. Gated on clearpath.nas.read.
   */
  nasReleases: Array<NasRelease>;
  /**
   * ENG-2583 — the caller's org NAS auto-update rollouts (one active per
   * channel/platform/arch slice), for the internal rollout admin surface. Gated on
   * clearpath.nas.read; internal-only + org-fenced under db/rls/0109.
   */
  nasRollouts: Array<NasRollout>;
  /**
   * Current viewer's notification preferences (channels + per-type + DND).
   * Per MOL-596. Returns the default-shape when the user has none stored.
   */
  notificationPreferences: NotificationPreferences;
  /**
   * All notification templates for the current viewer's Organization.
   * ADMIN-SCOPED — requires tenant admin role. Per MOL-595.
   */
  notificationTemplates: Array<NotificationTemplate>;
  /** Notifications for the current viewer. */
  notifications: NotificationConnection;
  /**
   * Lookup a single Order by ID. Staff read under the staff baseline — see
   * Query.orders.
   */
  order?: Maybe<Order>;
  /**
   * All order analytics for the window in one round-trip — lead time,
   * throughput + WIP, and per-step cycle times. `range` bounds each metric on
   * its natural timestamp (see the analytics types). `granularity` controls
   * the throughput bucketing (default DAY).
   */
  orderAnalytics: OrderAnalytics;
  /**
   * Whether setOrderBillingParty would refuse this order now, and how many
   * captured charges it would move. Read-only; the mutation re-checks under its
   * lock and still decides whether the caller may bill the chosen account.
   */
  orderBillingPartyChange: OrderBillingPartyChange;
  /**
   * ENG-1542 — expanded-panel payloads for MANY orders in ONE call.
   *
   * `Order.breakout` is the right shape for expanding a single row. It is the
   * wrong shape for `Expand all` and for pipeline-view mode, where every visible
   * row is expanded: selecting it per row across a page is one Lambda invocation
   * per row, each independently deriving per-Unit progress. This answers the whole
   * page in one, through the SAME derivation, so the two cannot disagree.
   *
   * Bounded at 100 order ids — the same ceiling `PageInput.first` is clamped to,
   * so a caller can always ask for exactly the page it is showing and never more.
   * Duplicates are collapsed; over the cap is REFUSED rather than truncated, so a
   * client never silently renders a partial page as a complete one. Orders the
   * caller cannot see, or whose org declares no order-scope pipeline, are simply
   * absent from the result — it is not index-aligned with the input.
   */
  orderBreakouts: Array<OrderBreakout>;
  /**
   * ENG-3922 — what the CALLER may do to this order: the order mutations'
   * gates (the order is one they may act on, plus each action's permission),
   * asked the way the mutations ask them. State (lifecycle, holds) is not part
   * of a flag. All false for non-internal callers or an order they cannot act on;
   * `comment` needs only that they can read it (under the staff baseline, any
   * order in the organization) plus clearpath.comments.create. Same client rule
   * as stepRunCapabilities: order-pane buttons render from these flags.
   */
  orderCapabilities: OrderCapabilities;
  /**
   * Resume one of the caller's own order-wizard drafts by id (ENG-1750).
   * Self-scoped exactly like myOrderDrafts: a draft id owned by another user or in
   * another tenant matches zero rows and resolves to null (never another user's
   * payload). No @requiresPermission, not @audited.
   */
  orderDraft?: Maybe<OrderDraft>;
  /**
   * The live order-level intake form available without the broader
   * `clearpath.tenant_settings.read` permission. For a selected active customer,
   * resolution checks its direct assignment, then the nearest inherited
   * assignment, then the tenant default. Omitting customerPartyId resolves only
   * the tenant default; an invalid or inactive selected customer returns null.
   * This narrow field exposes only the effective FormDefinition and no raw form
   * custom_fields or unrelated tenant settings.
   */
  orderIntakeFormDefinition?: Maybe<FormDefinition>;
  /**
   * ENG-3962 — NAS activity for one order: the files a station registered (with
   * conflict-copy marks) and the stations holding the order, current and recently
   * released, with per-file delivered and removed times. Internal staff only;
   * gated on `clearpath.nas.read` (feature-flag-gated on `clearpath.nas_module`).
   * Null when the module is off or the caller cannot open the order. Carries ids,
   * versions, timestamps and station hostnames only, never filenames.
   */
  orderNasActivity?: Maybe<OrderNasActivity>;
  /** All order-level pipelines. */
  orderPipelines: Array<OrderPipeline>;
  /** Lookup a single OrderProduct by ID. */
  orderProduct?: Maybe<OrderProduct>;
  /**
   * The sender a new FedEx label on this order prints, and what it may switch
   * to (alternatives empty unless canOverride).
   */
  orderShippingLabelSender: OrderLabelSender;
  /**
   * ENG-3286 — the customer default for this order and, for a holder of
   * clearpath.orders.shipping_terms.override, the terms it may be changed to.
   */
  orderShippingTermsOptions: ShippingTermsOptions;
  /**
   * Read an order's latest slip PDF without re-rendering it. MOL-563. Pass
   * orderProductId (ENG-521) for a single product's PER-PRODUCT ticket instead of
   * the combined slip.
   *
   * ENG-2547 — REPLACES `orderSlipUrl`, which re-signed a presigned S3 URL. The
   * rename is deliberate: the field returns bytes now, and a name ending in `Url`
   * would describe the thing this ticket removed. A presigned URL is bearer
   * authority for its whole TTL, so a copy-pasted link opened the slip for an
   * unauthenticated stranger; bytes ride the caller's authenticated session
   * instead.
   */
  orderSlipDocument: SlipDocument;
  /**
   * ENG-2511 — every field the slip COULD print for this order, for the
   * "Print slip" dialog to build its checklist from.
   *
   * Computed SERVER-SIDE, from the same queries and the same resolved form
   * versions the renderer prints from, so the dialog and the PDF cannot disagree
   * about what exists or what is default. The client must not assemble this
   * itself: only COMPLETED steps print, and "completed" means the step's LATEST
   * attempt completed — a property of append-only step_runs and supersession that
   * a client would re-derive, and re-derive differently the first time rework
   * semantics changed.
   *
   * Gated on the same permission as printing, because it reads intake and
   * step-form answers.
   */
  orderSlipPrintCatalog: SlipPrintCatalog;
  /**
   * ENG-2153 — the pipeline version one Unit (order line) runs, for the order
   * panel's read-only "View full pipeline" graph. The PINNED version the Unit's
   * run was created against, so an in-flight Unit is shown the graph it is
   * actually walking, not the pipeline's current one. Null without
   * `clearpath.pipelines.read` (same gate as `Order.breakout`), when the caller
   * cannot see the Unit's order, or when the Unit runs no pipeline.
   */
  orderUnitPipelineVersion?: Maybe<PipelineVersion>;
  /**
   * Paginated, filterable list of Orders.
   *
   * ENG-3922 STAFF BASELINE: a staff (internal) session reads every order in its
   * organization whatever its role assignments — the read is not narrowed by
   * org-unit scope (expandOrderReadOrgUnits; db/rls/0176 adds the same branch to
   * the orders / step_runs / inter_site_shipments visibility policies). Off for
   * account managers, who keep to their book of customers, and for ClearPath
   * Light sessions, which keep their site fence. The baseline gives reading, and
   * commenting (with clearpath.comments.create) without the org-unit scope;
   * every other action on an order stays scoped (orderCapabilities).
   */
  orders: OrderConnection;
  /** Lookup a single OrgUnit by ID. */
  orgUnit?: Maybe<OrgUnit>;
  /**
   * Preflight for the Teams delete/archive dialog: which action is
   * available for this org unit, and why. Prefer this over deleteBlockers for
   * org units — deleteBlockers reports only what prevents hard deletion, so a
   * unit that can be archived looks simply undeletable.
   */
  orgUnitDeletionPreflight: OrgUnitDeletionPreflight;
  /** All org units for the current viewer's Organization. */
  orgUnits: Array<OrgUnit>;
  /** Recent guest-preview grants for one exact file. Never returns tokens. */
  oroviaGuestPreviewLinks: Array<OroviaGuestPreviewLink>;
  /**
   * ENG-623 — resolved catalog access for one customer party, at an instant.
   * `at` defaults to now; pass it to preview a future or past window. Used by the
   * customer's Products tab and by the staff new-order wizard's step-2 check.
   */
  partyCatalogAccess: PartyCatalogAccess;
  /**
   * ENG-3881 — one party's ledger class, or null when it has none (or the
   * party is not in this organization). Staff only; requires clearpath.finance.read.
   */
  partyLedgerClass?: Maybe<PartyLedgerClass>;
  /** All party pricing agreements. */
  partyPricingAgreements: Array<PartyPricingAgreement>;
  /**
   * A customer's own label sender, the one it effectively gets on the account
   * it ships on, and the active senders on that account.
   */
  partySenderIdentity: PartySenderIdentity;
  /**
   * ENG-3286 — a customer's own shipping terms and what it effectively gets
   * (its own, an ancestor's, or the tenant default).
   */
  partyShippingTerms: PartyShippingTerms;
  /**
   * Every payment in this organization — card and ACH, all statuses —
   * collapsed one entry per PAYMENT rather than one per row, newest first.
   *
   * ONE POPULATION, ONE DEFINITION OF "CURRENT". Every entry comes from the same
   * shared current-state query (`@clearpath/shared-types/payment-attempt-state`)
   * that the double-debit guard, `Invoice.paymentAttempts`, reconciliation and
   * dunning read, bound org-wide. There is deliberately no second definition: a
   * console that computed "current" its own way would show an attempt as finished
   * while the server still refused to re-charge it.
   *
   * ⚠️ THE FILTER APPLIES TO THE COLLAPSED LIFECYCLE. `statuses: [PENDING]` means
   * "these payments are pending right now", not "these payments were ever
   * pending" — every payment was, because the pre-provider row is written
   * `pending` before the provider is called.
   *
   * CARD LOOKS DIFFERENT HERE AND THAT IS CORRECT. Card has no `settled` step, so
   * `SUCCEEDED` is effectively final on that rail and card payments never appear
   * in a pending-settlement view. They still appear in all activity and in the
   * failure views, because declines and chargebacks matter on both rails.
   *
   * Returns only the payment lifecycle plus the invoice number, invoice status and
   * billing-party display name needed to recognise it. No payment-method
   * instrument data, no provider credentials, and never the raw provider
   * payload.
   */
  paymentActivity: PaymentActivityConnection;
  /**
   * Ask the provider what it currently says about ONE payment, on demand.
   *
   * ⚠️ IT WRITES NOTHING. No payment row, no ledger row, no audit row. It may be
   * called as often as an operator likes on any payment in the organization, which
   * is why it gates on `clearpath.finance.read` — the same key that opens the
   * console — and not on the narrower write keys. `GET /api/transaction/{id}` is a
   * pure read at the provider; nothing moves there either. All of the risk is in
   * what is DONE with the answer, and that is
   * `Mutation.applyPaymentAttemptResults` behind `clearpath.payments.operate`.
   *
   * It deliberately IGNORES the 24-hour polling age gate the daily reconciliation
   * pass observes: that gate exists to save the pass a round trip being told
   * `pending_settlement`, not because reads are risky, and an operator pressing a
   * button has already decided the round trip is worth it.
   *
   * ⚠️ READ `PaymentAttemptCheckOutcome` BEFORE RENDERING THE RESULT.
   * `NOT_ADDRESSABLE` and `MISSING` are different answers and conflating them
   * tells an operator the provider confirmed nothing was sent when it confirmed
   * nothing at all.
   *
   * Takes the lifecycle ROOT id, which is what `PaymentActivityEntry.id` and
   * `PaymentAttempt.id` both carry.
   */
  paymentAttemptStatusCheck: PaymentAttemptStatusCheck;
  /**
   * Portal-safe payment client bootstrap (tokenizer / publishable key).
   * Never returns secret API credentials. ENG-36 / FR-34.
   */
  paymentClientConfig: PaymentClientConfig;
  /**
   * Provider webhook deliveries for this organization, newest first — the
   * first reader `payment_webhooks_log` has ever had.
   *
   * It answers "did the provider tell us, and did we book it": a `FAILED`
   * delivery is money news we received and did not act on, and until this field
   * existed there was no operator-visible trace of one.
   *
   * ⚠️ NEVER RETURNS THE RAW `payload`. See `PaymentIngestion`.
   */
  paymentIngestions: PaymentIngestionConnection;
  /**
   * Everything recorded under ONE payment lifecycle: every attempt row under
   * its root, the ledger rows, the provider deliveries, the debited account, the
   * authorization on file for the debited method,
   * the re-presentment lineage and — soft-gated on `clearpath.audit.read` — the
   * audit history scoped to this lifecycle's own attempts and ledger rows.
   *
   * Takes the lifecycle ROOT id, which is what `PaymentActivityEntry.id` and
   * `PaymentAttempt.id` both carry. Returns null for an id that names no
   * lifecycle in this organization; it does NOT distinguish "no such lifecycle"
   * from "another tenant's lifecycle", because that distinction is itself a
   * disclosure.
   *
   * `debitedAccount` is exactly one instrument — provider mask and last four —
   * never a wallet, and reads on this same `clearpath.finance.read` gate.
   *
   * `authorization` is soft-gated on the SAME `clearpath.audit.read` key as
   * `auditHistory` — see `authorizationVisible` — because it is consent
   * evidence, the same sensitivity class as the audit trail. It is a fact about
   * the METHOD, captured at method-add or reauthorize and selected as at
   * `authorizationAsOf` (the debit's own attempt time), never a claim that this
   * specific debit was authorized.
   *
   * THE INSTRUMENT'S STATE TODAY IS DELIBERATELY NOT HERE. A drawer about one
   * past payment is the wrong place to answer "can we still debit this account",
   * and the two readings are easy to confuse: a method revoked since reads as a
   * claim that this debit was unauthorized, which is exactly wrong. That
   * question belongs to the payment-method surface.
   *
   * `auditHistory` is EMPTY rather than an error for a caller without
   * `clearpath.audit.read` — read `auditHistoryVisible` before rendering the empty
   * pane as "nothing happened".
   */
  paymentLifecycle?: Maybe<PaymentLifecycle>;
  /** Payment methods for a given party. */
  paymentMethods: Array<PaymentMethod>;
  /** All payment providers. */
  paymentProviders: Array<PaymentProvider>;
  /**
   * This organization's payment-rail configuration (which of CARD / ACH it
   * accepts for NEW payment methods and NEW charges), plus the state that
   * configuration is in and an optimistic-concurrency token for updating it.
   *
   * Same key as the rest of the finance cockpit — the WRITE is a separate,
   * narrower key (clearpath.payment_rails.manage) so a viewer can see the policy
   * without being able to change it. Staff-only; a portal session is refused
   * before the permission check. ENG-1532.
   */
  paymentRailSettings: PaymentRailSettings;
  /**
   * This organization's recent daily reconciliation passes, plus when a pass
   * last ran and last SUCCEEDED.
   *
   * WHY THE TWO TIMESTAMPS ARE THE POINT. ACH has no completion signal, so the
   * daily pass is the only thing that learns whether a debit settled — and until
   * db/migrations/0387 it returned its summary to a Lambda log and nothing
   * inspected it, which made "the pass did not run last night" indistinguishable
   * from "the pass ran and found nothing to do". `lastRunAt` and
   * `lastSuccessfulRunAt` are computed over ALL history, not just the returned
   * window, so a tick that stopped weeks ago is still visible.
   *
   * Counters are `ReconciliationSummary`'s, unrenamed.
   */
  paymentReconciliationRuns: PaymentReconciliationReport;
  /** Pending time-off requests awaiting manager review. */
  pendingTimeOffRequests: Array<TimeOffReviewRow>;
  /**
   * Synced mirror of the control-plane permission registry. Feeds the
   * tenant role editor (RoleCreatePage / RoleDetailPage / PermissionsBrowser)
   * with the permission catalogue it can choose from. Served out of the
   * per-tenant `permission_registry_cache` table (synced by the
   * permission-sync Lambda, MOL-536). Optional `productCode` filter narrows
   * to one product ('clearpath', 'molaris'); omit for all. Deprecated rows
   * hidden by default.
   */
  permissionRegistry: Array<PermissionRegistryEntry>;
  /**
   * Lookup a single Pipeline by ID. `asDraft: true` hydrates the open DRAFT
   * version's graph (the pipeline editor wants the version it is editing);
   * the default (false) hydrates the LIVE version. Self-versioning scopes
   * steps/dependencies by pipeline_version_id so an open draft never merges
   * into the live read.
   */
  pipeline?: Maybe<Pipeline>;
  /** Every consumption rule on a pipeline's steps, in step order. */
  pipelineConsumptionRules: Array<PipelineConsumptionRule>;
  /**
   * Version-proliferation consolidation (2026-07-21) — the paginated PER-SAVE
   * ledger for a pipeline (pipeline_graph_saves): the granular autosave history
   * that used to live in pipelineVersions. Sorted DESC on savedAt so page 1 is the
   * most recent save. Requires `clearpath.pipelines.read`.
   */
  pipelineGraphSaves: PipelineGraphSaveConnection;
  /**
   * ENG-1261 — the "Check routing" button. Proves that every decision in the
   * pipeline has a branch for every answer the linked SKUs' intake forms allow,
   * and that every step an order can reach can still reach an end of the pipeline.
   * Reports warnings as well as errors and blocks nothing; the publish gate
   * decides consequence from the same check, so the two cannot disagree.
   * `asDraft` defaults to true — the open draft is what an author is editing.
   * Requires `clearpath.pipelines.read`.
   */
  pipelineRoutingCheck: RoutingCheckResult;
  /** Lookup a single PipelineRun by ID. */
  pipelineRun?: Maybe<PipelineRun>;
  /** Lookup a single PipelineStep by ID. */
  pipelineStep?: Maybe<PipelineStep>;
  /**
   * Version-proliferation consolidation (2026-07-21) — the paginated
   * PER-PUBLISH timeline for a pipeline: the current DRAFT plus every LIVE/ARCHIVED
   * publish (one row per publish, not per autosave). Sorted DESC on versionNumber
   * so page 1 is the most recent open-for-edit / publish. Requires
   * `clearpath.pipelines.read`.
   */
  pipelineVersions: PipelineVersionConnection;
  /**
   * All pipelines visible to the current viewer.
   *
   * `includeInactive` controls status filtering:
   *   - `false` — only LIVE pipelines (hides DRAFT / INACTIVE / ARCHIVED). Used by
   *     the /admin/pipelines list so paused, in-authoring and superseded pipelines
   *     don't clutter the default view.
   *   - `true` or omitted (null) — every pipeline regardless of status. Null behaves
   *     like `true` for back-compat with callers that need the full set (e.g. the
   *     department Kanban, pipeline pickers).
   */
  pipelines: Array<Pipeline>;
  /**
   * ENG-1579 — the PAGINATED pipeline list, for the /admin/pipelines screen.
   *
   * Deliberately a SEPARATE field rather than a change to `pipelines` above:
   * that one is documented as serving the department Kanban and the pipeline
   * pickers, which need the full set, and paginating it to suit an admin table
   * would break them.
   *
   * Filtering is server-side and applied BEFORE the page window, which is the
   * whole point — the admin screen used to fetch every pipeline and filter by
   * status/search in the browser, so paginating that client-side list would have
   * produced pages of a pre-filtered set and a total that disagreed with what the
   * table showed.
   *
   * `scope` selects the tab: PRODUCT for the per-SKU pipelines, ORDER for the
   * order-step host. Omit for both. `status` narrows to one lifecycle stage; omit
   * for all. `search` is a case-insensitive substring on the name. Requires
   * `clearpath.pipelines.read`.
   */
  pipelinesPage: PipelineConnection;
  /**
   * Whether the practice portal should HIDE every monetary figure in the
   * new-order flow (product-picker price + review-step line/order estimate) for
   * this tenant. Non-sensitive; fails closed to hidePrice=false (price shown).
   * ENG-3064.
   */
  portalPricingDisplay: PortalPricingDisplay;
  /**
   * ENG-832 (epic ENG-830) — the effective portal view configuration for a
   * surface: the slot ids the viewer's organization has hidden on the
   * customer portal page. Presentation only and strictly SUBTRACTIVE — it can
   * only remove panels the viewer was already authorized to see, never widen
   * access; the server enforcement that makes hiding real lives in ENG-837 and
   * runs AFTER the existing permission gate, never instead of it. Returns the
   * hidden-id list only — never WHY a slot is hidden (that would disclose a
   * deliberate lab decision to a practitioner). Readable by the tenant's own
   * practitioners and staff; deliberately NOT public-tenant — share-link, vendor
   * and service principals receive an empty configuration rather than the hidden
   * list. `surface` is a slot-registry surface key (v1: `order_detail`); an
   * unknown surface resolves to null. Infra: add `portalViewConfig` to
   * API_LAMBDA_FIELDS in the infrastructure repo's api-stack.ts.
   */
  portalViewConfig?: Maybe<PortalViewConfig>;
  /**
   * ENG-832 (epic ENG-830) — the admin-facing slot vocabulary for a surface:
   * labels, descriptions, enforcement classification, direct sparse override
   * state, and version for the authoring page (ENG-835) and its live preview
   * (ENG-838). Gated on
   * `clearpath.tenant_config.read`, the tenant-config authoring key. Deliberately
   * NEVER shipped to portal customers: it would hand a practitioner a
   * machine-readable map of exactly which hidden controls are merely cosmetic and
   * therefore still reachable. Infra: add `portalViewRegistry` to
   * API_LAMBDA_FIELDS in the infrastructure repo's api-stack.ts.
   */
  portalViewRegistry?: Maybe<PortalViewRegistry>;
  /** Lookup a single PriceBook by ID. */
  priceBook?: Maybe<PriceBook>;
  /** All price books. */
  priceBooks: Array<PriceBook>;
  /** Frozen pricing snapshot + resolution log for an order. MOL-613. */
  pricingForOrder?: Maybe<Scalars['AWSJSON']['output']>;
  /**
   * ENG-8 — lookup a single ProductBundle by ID. Portal customers must supply
   * an exact authorized customer party entitled to every component.
   */
  productBundle?: Maybe<ProductBundle>;
  /**
   * ENG-8 — product bundles for the current viewer's Organization. Portal
   * customers must supply an exact authorized customer party and receive only
   * selectable LIVE bundles whose components are all entitled for that party.
   */
  productBundles: Array<ProductBundle>;
  /** All product categories. */
  productCategories: Array<ProductCategory>;
  /**
   * Lookup a single ProductType by ID. Portal customers must supply an exact
   * authorized customer party whose catalog contains the product.
   */
  productType?: Maybe<ProductType>;
  /**
   * ENG-3054 (AD-4) — the tenant-wide customer-access coverage summary for a
   * product, counted in customers at now() (the live rule): eligible-active,
   * can-order, independent-active, independent-can-order and parent-orgs-granted
   * totals. Internal staff only, behind clearpath.catalog_entitlements.read; read-only
   * and lock-free; book-scoped for an account manager. A gone or foreign productTypeId
   * is NotFound. An explicit null productTypeId is the wizard's not-yet-created product
   * (Story 4.2): it binds a NULL uuid so every total that depends on a grant is 0
   * (canOrder 0, independentCanOrder 0, parentsGranted 0); an OMITTED argument is a
   * ValidationError, never read as null.
   */
  productTypeAccessCoverage: ProductTypeAccessCoverage;
  /**
   * ENG-3054 Story 3.2 — the full, paged list behind one revoke target's losing or
   * keeping count in productTypeAccessImpactPreview (forward pages only: page.first
   * from 1 to 100, default 50, and page.after). Internal staff only; read-only and
   * lock-free. totalCount equals the preview's figure for the same arguments; rows
   * are book-scoped for an account manager.
   */
  productTypeAccessImpactList: ProductTypeAccessImpactConnection;
  /**
   * ENG-3054 Story 3.1 — preview exactly what saving a change set would do,
   * before any write: before/after can-order totals, the distinct org-wide losing
   * total, the independent target size and its losing, and per revoke target who
   * loses and who keeps access. Internal staff only; read-only and lock-free. Pass
   * productTypeId null for the wizard's empty before-state (no revokes allowed).
   */
  productTypeAccessImpactPreview: ProductTypeAccessImpactPreview;
  /**
   * ENG-3054 (AD-4) — the server-computed access state of each listed customer for
   * a product: its surviving direct grant, whether it can order now, the nearest
   * ancestor it inherits from, its parent name and its active / can-order child
   * counts. Computed from the one order-path rule set, never from the rows a client
   * loaded. Internal staff only, behind clearpath.catalog_entitlements.read; read-only
   * and lock-free. `partyIds` is deduped and capped at 200 distinct (over it is a
   * validation error, never a clamp), and a raw array longer than 1,000 entries is a
   * validation error (a cheap upper bound on per-id work and the raw list, within
   * AppSync's request-size limit), checked before per-id validation; an id that is not
   * an eligible customer in this org — or, for an account manager, not in their book —
   * is inert (no row, no error). A gone or foreign productTypeId is NotFound. An
   * explicit null productTypeId is the wizard's not-yet-created product (Story 4.2):
   * it skips the existence check and binds a NULL uuid so no grant matches (canOrder
   * false, no direct grant, no inheritedFrom, canOrderChildCount 0); an OMITTED argument
   * is a ValidationError, never read as null.
   */
  productTypeAccessStates: Array<ProductTypeAccessState>;
  /**
   * All product types for the current viewer's Organization. Portal customers
   * may pass a customer party they are allowed to act for to receive that exact
   * party's entitled catalog instead of the union across their party scope.
   */
  productTypes: Array<ProductType>;
  /**
   * Aggregate QC metrics — first-pass yield, rework rate, and defect
   * counts by reason — over the caller's org for the selected window.
   */
  qcMetrics: QcMetrics;
  /**
   * Revenue aggregated out of the money ledger on either an accrual (invoice
   * issue date) or a cash (settled transaction) basis, grouped by period or by
   * customer org, ALWAYS per currency, with both gross and net reported.
   *
   * STAFF-ONLY and ORG-WIDE. `clearpath.finance.read` is granted only to the
   * Finance Director role (Director / Lab Owner inherit it) and is NOT the
   * `clearpath.billing.read` key portal roles hold. The resolver additionally
   * refuses any non-internal session outright before it reads anything, because
   * the permission registry has no audience column: an org-wide view of the whole
   * book in a customer's or vendor's hands is a data breach, not a filtered read.
   *
   * `periodStatus` is LIVE until the period-close snapshot lands (FIN-M6 slice 5);
   * treat the figures as provisional and show `asOf`. FIN-M6 slice 1 (ENG-1477).
   */
  revenueReport: RevenueReport;
  /** Lookup a single Role by ID. */
  role?: Maybe<Role>;
  /** All roles for the current tenant. */
  roles: Array<Role>;
  /** Server-owned readiness and lifecycle for all scanner providers. */
  scannerConnections: Array<ScannerConnection>;
  /** All scanner devices. */
  scannerDevices: Array<ScannerDevice>;
  scannerIntakeCase?: Maybe<ScannerIntakeCase>;
  /**
   * Whether the shared scanner-intake experience is enabled for this tenant.
   * This non-sensitive status is used to fail-close both Main and Portal routes.
   */
  scannerIntakeFeature: ScannerIntakeFeature;
  /** Whole-lab identity groups, independently searched and paginated from case pages. */
  scannerIntakeIdentities: ScannerIntakeIdentityConnection;
  /**
   * Scanner intake inbox — every staged scanner submission (scanner_rx_ingests,
   * migration 0075) for the caller's tenant, newest first. This is the review
   * surface for scans that have been received from a connected scanner (iTero,
   * Medit, 3Shape) but not yet turned into an order: with auto-order-creation off,
   * a staged scan is visible here (subject, unit summary, provider, received time,
   * status) instead of silently landing nowhere. `orderId` is null until a scan is
   * mapped to an order. Backed by scanner_rx_ingests scoped to the viewer's
   * organization; the RX summary is derived from the stored payload (PHI). Bible
   * §05 (orders) / §07 (files-and-nas).
   */
  scannerIntakeInbox: ScannerIntakeInboxConnection;
  /** Redacted source policy, sync status and progress. Never exposes credentials or raw errors. */
  scannerIntakeSources: Array<ScannerIntakeSource>;
  /**
   * All enrolled scanner stations (ENG-219 companion): each device paired to a
   * service-account key, with the key prefix / last-used / status. Never returns
   * the key secret. Internal-only (mirrors `scannerDevices`).
   */
  scannerStations: Array<ScannerStation>;
  /**
   * Round 5 — scoped kanban dashboard. `scopeKind` selects the actor type
   * (org unit / team / role / external party / user); `scopeId` is the row id
   * within that table. Returns the kanban shape + the tenant's status states.
   * @deprecated Caps at 500 cards with no way to detect truncation, and the cap lands BEFORE the JS groups cards into columns — so a busy scope can lose a whole column while another looks full (ENG-2210). Use scopedKanbanMeta + scopedKanbanCards.
   */
  scopedKanban: ScopedKanbanResult;
  /**
   * ENG-2210 — a page of cards for a scope, newest-updated first.
   *
   * ONE field serves both views. The BOARD calls it once per column, passing that
   * column's `columnKey`; the LIST view calls it once with no columnKey and a
   * larger page. Keyset-paged on (updated_at, id) — not OFFSET, which drops rows
   * when an order is touched between page fetches, and not updated_at alone,
   * which is not unique.
   */
  scopedKanbanCards: KanbanCardConnection;
  /**
   * ENG-2210 — the scope's chrome: its label and the tenant's configured status
   * states. Fetch this FIRST: it tells the board which columns exist and in what
   * order, and each column then pages scopedKanbanCards independently with its
   * own columnKey. Carries no cards, so it stays cheap however large the scope.
   */
  scopedKanbanMeta: ScopedKanbanMeta;
  /**
   * P1 #27 — rows from `secrets_references` whose next_rotation_due_at
   * falls within the next 30 days (or is already past-due). Drives the
   * admin-shell secret-rotation reminder banner. Per
   * docs/clearpath-architecture/13-audit-secrets-encryption.md §86.
   */
  secretRotations: Array<SecretRotationStatus>;
  /**
   * ENG-3795 — ids of the order's step runs the CALLER may assign to themselves:
   * live READY, unassigned, not automatic and not on hold; in a department the
   * caller belongs to (a role on the step's org unit, an ancestor or a
   * descendant of it; a whole-lab role does not count, ENG-3920 membership);
   * the caller holds the step's required skills, machine
   * authorizations and unexpired certifications; and Cedar permits
   * `clearpath.steps.assign`. Empty for non-internal callers and for an order the
   * caller cannot see. Drives the order pane's "Assign to me" button only; the
   * write is `assignStepRun`.
   */
  selfAssignableStepRuns: Array<Scalars['ID']['output']>;
  /** Lookup a single SharedAccessLink by ID. */
  sharedAccessLink?: Maybe<SharedAccessLink>;
  /** All currently active shared access links. */
  sharedAccessLinks: Array<SharedAccessLink>;
  /**
   * Session introspection for the inbound Mode-B share-link JWT. Narrows
   * the response to the link's permissions + scope (orders / step runs the
   * JWT can see). TODO: needs resolver impl (MOL-574).
   */
  sharedAccessScope?: Maybe<SharedAccessScope>;
  /** Shift management board (lazy-seeds the 3 default shifts on first read). */
  shiftManagement: ShiftManagementPayload;
  /**
   * Orders that can share a label with this one: the same customer's active
   * orders, each with its destination and why it cannot join, if it cannot.
   */
  shipTogetherCandidates: ShipTogetherCandidates;
  /**
   * Org-scoped shipment labels (FedEx/UPS) for the lab shipping directory.
   * Read-only — buy/void stay on the order Shipping panel.
   */
  shipments: ShipmentConnection;
  /**
   * ENG-3286 — how many offices would inherit an outside account assigned to
   * these customers (at most 100). Internal sessions only.
   */
  shippingAccountAssignmentPreview: ShippingAccountAssignmentPreview;
  /**
   * ENG-3286 — outside carrier accounts the lab ships on. Account numbers are
   * never returned in full.
   */
  shippingAccounts: Array<ShippingAccount>;
  /** Box sizes (inside dimensions) a label can pick for your own packaging. */
  shippingBoxPresets: Array<ShippingBoxPreset>;
  /** ENG-3286 — the tenant default shipping terms and invoiced-shipping markup. */
  shippingChargeSettings: ShippingChargeSettings;
  /** The lab-wide label defaults. Everyone who composes a label reads them. */
  shippingLabelDefaults: ShippingLabelDefaults;
  /**
   * Package type, box and weight that prefill a label, per level: the lab (both
   * target ids null), each outside account and each sender identity. A null value
   * inherits from the next level; nothing is built in.
   */
  shippingPackageDefaults: Array<ShippingPackageDefaults>;
  /**
   * Senders printed on FedEx labels for one outside account (shippingAccountId)
   * or the lab's own account (labAccount); neither = every sender. Print-only:
   * the ship-from site stays the rating and pickup origin.
   */
  shippingSenderIdentities: Array<ShippingSenderIdentity>;
  /**
   * ENG-1036/ENG-1038 — the org's sites (org_units of unit_type='site',
   * extended by the `sites` table). Powers the internal-site target picker in the
   * routing-rule editor. Internal-only, org-scoped. includePlants adds every
   * live plant (and any plant with a ship-from record) for ship-from choices;
   * routing pickers must leave it false.
   */
  sites: Array<Site>;
  /** Skill catalog for the org. */
  skillDefinitions: Array<SkillDefinition>;
  /**
   * Per-(step × product_type × org_unit) drill-down rollups for the
   * selected window. `groupBy` selects the primary axis for sorting.
   */
  slaDrillDown: SlaDrillDown;
  /**
   * Raw append-only SLAEvaluation rows for a given policy. Relay-style
   * pagination; ordered by evaluated_at DESC.
   */
  slaEvaluationsForPolicy: SlaEvaluationOffsetConnection;
  /**
   * Top-level SLA dashboard counts + daily breach rate + top breaching
   * steps/departments for the selected window. Per bible doc 04.
   */
  slaOverview: SlaOverview;
  /** All SLA policies for the current viewer's Organization. */
  slaPolicies: Array<SlaPolicy>;
  /** Lookup a single SLAPolicy by ID. */
  slaPolicy?: Maybe<SlaPolicy>;
  /**
   * ENG-3926 — active staff nobody can give work to: their roles let them take
   * or finish steps (clearpath.steps.assign / clearpath.steps.complete), they
   * cannot work across departments (no clearpath.assignments.write), and they are
   * in no department — a whole-lab role places nobody. Work is only offered
   * inside a step's department, so each needs placing in one. Gated like `users`
   * (internal + the RBAC-admin staff-directory read key).
   */
  staffWithoutDepartment: Array<User>;
  /** Lookup a single Statement by id. MOL-616. */
  statement?: Maybe<Statement>;
  /** Paginated, filterable list of statements. MOL-616. */
  statements: Array<Statement>;
  /**
   * Per-step wall-clock cycle times from step_runs timings — slowest average
   * first (surfaces bottlenecks).
   */
  stepCycleTimes: Array<StepCycleTime>;
  /**
   * ENG-1577 — the failure reasons this step OWNS, empty when it has none.
   * Distinct from `failureReasonCodes(stepDefinitionId:)`, which answers "what
   * does this step offer" and falls back to the library — right for a picker,
   * useless for authoring the set, because the two look identical on screen.
   */
  stepFailureReasonCodes: Array<FailureReasonCode>;
  /** All step library entries. */
  stepLibrary: Array<StepLibraryEntry>;
  /** Lookup a single StepRun by ID. */
  stepRun?: Maybe<StepRun>;
  /**
   * ENG-3922 — what the CALLER may do to each of these step runs (at most 250),
   * one entry per id in the order given. Each flag is the permission half of the
   * action's own gate, asked the way the action asks it, so a button shows only
   * where the server would allow it. State (on hold, not ready yet, requirements
   * missing) is not part of a flag. All false for non-internal callers and for
   * ids the caller cannot see.
   *
   * Clients render a step's action buttons from these flags ("hide, don't
   * disable"), never from User.effectivePermissionKeys or the role's name: the
   * answer also depends on where the step is, the caller's scope and their
   * membership, which only the server computes.
   */
  stepRunCapabilities: Array<StepRunCapabilities>;
  /** Paginated, filterable list of StepRuns. */
  stepRuns: StepRunConnection;
  /**
   * API-01 — completed step_runs aggregated BY ASSIGNEE over `range`
   * (attributed on each run's completion timestamp), most productive first.
   * `orgUnitIds` narrows to the step's owning org unit (closure-expanded); the
   * caller's own org-unit scope is always applied on top. Paginated + bounded.
   */
  stepThroughputByAssignee: StepThroughputByAssigneeConnection;
  /**
   * LLM-generated optimization suggestions for a config document.
   * `focus` narrows the analysis (e.g. 'sla', 'pricing', 'pipelines').
   */
  suggestImprovement: SuggestImprovementResult;
  /**
   * Tax billed per customer over a window — who the tax came from.
   *
   * Answers *how many transactions carried tax, the sum of it, and from whom*:
   * `invoiceCount` counts every invoice, `taxedInvoiceCount` only the ones that
   * carried a non-zero figure, and the gap between them is the interesting number.
   *
   * Every figure is invoice-basis `taxBilled`, NOT reconciled to cash received,
   * and nothing is summed across currencies. Voided invoices are excluded, not
   * zeroed. Read `TaxByCustomerReport` before rendering any of it.
   */
  taxByCustomer: TaxByCustomerReport;
  /**
   * One row per invoice — the money beside the tax engine's own notes.
   *
   * This is the field that joins the money view to the health view: `taxBilled`
   * is summed from `invoice_line_taxes`, and `noteCodes` is replayed from the
   * snapshot frozen onto the invoice at issue time. An invoice with zero tax and a
   * `NO_RATE_FOUND` note is what silent under-collection looks like.
   */
  taxByInvoice: TaxByInvoiceReport;
  /**
   * Tax BILLED per jurisdiction over a filing window — taxable base, tax, the
   * rate(s) applied, the taxability breakdown and the dataset version the rates came
   * from, per period and per currency.
   *
   * **INVOICE BASIS, NOT CASH.** The money field is `taxBilled` and means what it
   * says: tax on invoices ISSUED in the window, whether or not anyone paid. It is
   * not reconciled to receipts. Which basis a jurisdiction actually wants is a
   * tax-advisor decision that has not been made, so this query reports the basis it
   * can compute correctly and names it precisely instead of guessing.
   *
   * Aggregated over `invoice_line_taxes`, the append-only per-line per-jurisdiction
   * record the tax engine writes. Voided invoices are excluded. Nothing is summed
   * across currencies, and a currency that contradicts its jurisdiction is reported
   * in `currencyExceptions` rather than folded into any total.
   *
   * STAFF-ONLY and ORG-WIDE, gated identically to `revenueReport`: the resolver
   * refuses any non-internal session outright BEFORE it reads anything, then requires
   * `clearpath.finance.read` (granted only to the Finance Director role; NOT the
   * `clearpath.billing.read` key portal roles hold). The registry has no audience
   * column, so the lab's whole tax position in a customer's or vendor's hands is a
   * data breach, not a filtered read — there is no party-scoped variant to degrade
   * to.
   *
   * `periodStatus` is LIVE until FIN-M6 slice 5 gates filing exports on a closed
   * period; treat the figures as provisional. FIN-M6 slice 3 (ENG-1479).
   */
  taxCollectedByJurisdiction: TaxRemittanceReport;
  /**
   * Open orders the invoice destination gate will REFUSE, and what each needs.
   * ENG-2497 slice 9.
   *
   * Slice 9 makes a usable ship-to tax address a hard requirement for creating an
   * invoice: an order whose ship-to party is missing a country, state/region or
   * postal code is not invoiced, on the manual path AND the automated one. The
   * automated run has no human to prompt, so without this read the only symptom is
   * revenue that stays pending.
   *
   * ⚠️ NOT GATED ON WHETHER THE ORGANIZATION COLLECTS TAX ANYWHERE. A lab with no
   * registrations at all still has its orders refused — the product decision is
   * that an invoice with an unknown destination is inaccurate, not merely
   * untaxed — so this list is worth reading even when the tax engine is switched
   * off.
   *
   * Gates like its siblings above: `requireInternalActor` FIRST, then
   * `clearpath.finance.read`. Runs the gate's own validator rather than a second
   * copy of the rule, so nothing listed here can disagree with the refusal.
   */
  taxDestinationGaps: TaxDestinationGapsPayload;
  /**
   * Is this organization's tax collection healthy, and what is it costing?
   *
   * The panel the rest of ENG-2497 feeds. Several engine note codes have been
   * computed and read by NOTHING since the engine shipped; this is what makes them
   * visible. Two halves — what the engine said while billing (`outcomes`, which
   * can only report a problem after it has cost something) and what the reference
   * data says today (`configuration`, which catches it before it bills).
   *
   * `range` scopes the OUTCOME half only. The configuration half and the coverage
   * roll-up are always AS OF NOW: a rate gap that exists today exists whether or
   * not it has billed anything yet, and dating it to a past window would hide the
   * one class of finding this panel is for.
   */
  taxHealth: TaxHealthReport;
  /**
   * What a CSV of tax rates WOULD do, without writing anything.
   *
   * Parses and validates the file SERVER-SIDE — the browser parses only the header
   * row, for the mapping step, with the same parser — prices every row against
   * your real regional default, and returns the coverage consequence in plain
   * English plus every problem found.
   *
   * Gated on the console's READ key, deliberately: a finance viewer can see what
   * an import would do without being able to commit it.
   */
  taxImportPreview: TaxImportPreview;
  /**
   * This organization's own tax jurisdictions — the editable half of
   * Finance -> Tax. Shared platform reference rows are excluded; see
   * `TaxJurisdictionList`.
   */
  taxJurisdictions: TaxJurisdictionList;
  /**
   * What would this address be taxed at, and WHY.
   *
   * Runs the REAL resolver against this organization's reference data — the same
   * `resolveJurisdictions` / `computeTax` an invoice is billed through — so a rate
   * confirmed here is the rate that will bill. Supply a region, a postal code, or
   * both; an address with neither matches no boundary and the empty answer would
   * read as "not taxed here".
   */
  taxRateLookup: TaxRateLookup;
  /**
   * Where this organization says it is registered to collect tax. ENG-2497.
   *
   * Gates like its siblings above: `requireInternalActor` FIRST, then
   * `clearpath.finance.read`. The WRITE affordances gate on the narrower
   * `clearpath.tax.manage`.
   *
   * ⚠️ THIS IS THE ONLY READ THAT SHOWS A REGISTRATION ON A SHARED PLATFORM
   * JURISDICTION, which is the normal case — `taxJurisdictions` lists only the
   * jurisdictions this organization owns, so a registration on the published
   * `US-CA` row is invisible there.
   *
   * ⚠️ EVERY PERMIT NUMBER HERE IS RECORDED AND ATTESTED, NEVER VERIFIED. No
   * lookup against CDTFA or any other tax authority happens, or exists, anywhere
   * in this product.
   */
  taxRegistrations: TaxRegistrationList;
  /** A team member's skills, certifications, and machine authorizations. */
  teamMemberSkills: TeamMemberSkills;
  /** Per-technician capacity + load for a date (default today, org tz). */
  technicianCapacity: Array<TechnicianCapacityRow>;
  /** Single config document by id (any status). */
  tenantConfigDocument?: Maybe<TenantConfigDocument>;
  /** Paginated config history with optional status/author/date filters. */
  tenantConfigHistory: TenantConfigHistoryResult;
  /** Tenant settings for the current viewer's Organization. */
  tenantSettings?: Maybe<TenantSettings>;
  /** Orders completed vs created per DAY/WEEK bucket + current WIP count. */
  throughputMetrics: ThroughputMetrics;
  /** Fetch one ticket by id. Scoped to the caller's organization. */
  ticket?: Maybe<Ticket>;
  /** List tickets for the caller's organization with optional filters. */
  tickets: TicketConnection;
  /**
   * Per-customer analytics across the tenant, busiest customer first. `limit`
   * caps the leaderboard (default 20, max 200). Same metrics as clientAnalytics.
   */
  topClientsAnalytics: Array<ClientAnalytics>;
  /**
   * Fetch the current tracking snapshot for a shipment. Read-only — the
   * pipeline engine separately polls + caches via the integrations Lambda's
   * EventBridge scheduler.
   */
  trackShipment: ShippingTrackingSnapshot;
  /**
   * Unassigned ready/in-progress work for the manager's departments: for a
   * caller whose role applies to departments, work pinned to those units and the
   * units below AND above them; for a whole-lab caller, all pinned work.
   * Note the board can list a step pinned ABOVE the caller's units that
   * manualAssignStep then refuses (it accepts only the caller's units and those
   * below); its `assign` capability flag is false there.
   *
   * `orgUnitIds` narrows to a SET of org units and takes precedence over the
   * single `orgUnitId` (kept for existing callers). The Assign board sends a set
   * because its department filter has to mean "the department AND its teams" — a
   * step pins to exactly one unit, so a department alone would match nothing on a
   * team-pinned step — and because this query paginates in Postgres, so the
   * narrowing cannot happen on the client without hiding rows from later pages.
   */
  unassignedWork: AssignmentWorkList;
  /** Lookup a single User by ID. */
  user?: Maybe<User>;
  /** Paginated, filterable list of users. */
  users: UserConnection;
  /**
   * Dry-run the submit-time intake validation for a candidate order, returning
   * per-field violations (empty = valid). No writes; same permission as submitOrder.
   */
  validateIntake: Array<IntakeViolation>;
  /** Single vendor routing rule by id. */
  vendorRoutingRule?: Maybe<VendorRoutingRule>;
  /** All vendor routing rules. */
  vendorRoutingRules: Array<VendorRoutingRule>;
  /** Materialized volume counters per party (and optionally SKU/period). MOL-612. */
  volumeCounters: Array<PartyVolumeCounter>;
  /**
   * All work locations (stations) for the current viewer's Organization
   * (ENG-589).
   */
  workLocations: Array<WorkLocation>;
};


export type QueryAnalyticsDashboardArgs = {
  id: Scalars['ID']['input'];
};


export type QueryAnalyticsDashboardSharesArgs = {
  dashboardId: Scalars['ID']['input'];
};


export type QueryAnalyticsDrillDetailArgs = {
  cell: Scalars['AWSJSON']['input'];
  page?: InputMaybe<Scalars['AWSJSON']['input']>;
};


export type QueryAnalyticsEventFunnelArgs = {
  funnel: Scalars['AWSJSON']['input'];
};


export type QueryAnalyticsFunnelArgs = {
  splitBy?: InputMaybe<Scalars['String']['input']>;
  stages: Array<Scalars['String']['input']>;
};


export type QueryAnalyticsQueryArgs = {
  query: Scalars['AWSJSON']['input'];
};


export type QueryAnalyticsQueryBatchArgs = {
  items: Array<AnalyticsBatchQueryItem>;
};


export type QueryAnalyticsTargetArgs = {
  measureKey: Scalars['String']['input'];
};


export type QueryAnalyticsTemplatesArgs = {
  kind?: InputMaybe<Scalars['String']['input']>;
};


export type QueryAnalyticsTopPracticesArgs = {
  limit?: InputMaybe<Scalars['Int']['input']>;
};


export type QueryAnalyticsWidgetsArgs = {
  dashboardId: Scalars['ID']['input'];
};


export type QueryArAgingArgs = {
  filter?: InputMaybe<ArAgingFilterInput>;
};


export type QueryAssignmentCandidatesArgs = {
  limit?: InputMaybe<Scalars['Int']['input']>;
  search?: InputMaybe<Scalars['String']['input']>;
  stepRunId: Scalars['ID']['input'];
};


export type QueryAssistantJobArgs = {
  jobId: Scalars['ID']['input'];
};


export type QueryAuditLogArgs = {
  filter?: InputMaybe<AuditLogFilterInput>;
  page?: InputMaybe<PageInput>;
};


export type QueryAutomationRuleArgs = {
  id: Scalars['ID']['input'];
};


export type QueryAutomationRulesArgs = {
  isActive?: InputMaybe<Scalars['Boolean']['input']>;
  pipelineId?: InputMaybe<Scalars['ID']['input']>;
};


export type QueryBarcodeScansArgs = {
  orderId?: InputMaybe<Scalars['ID']['input']>;
  page?: InputMaybe<PageInput>;
};


export type QueryBarcodeScansForOrderArgs = {
  orderId: Scalars['ID']['input'];
  since?: InputMaybe<Scalars['AWSDateTime']['input']>;
};


export type QueryBillingAdjustmentsArgs = {
  filter?: InputMaybe<BillingAdjustmentFilterInput>;
  page?: InputMaybe<PageInput>;
};


export type QueryBillingEventsArgs = {
  filter?: InputMaybe<BillingEventFilterInput>;
  page?: InputMaybe<PageInput>;
};


export type QueryCalculatedMeasuresArgs = {
  includeArchived?: InputMaybe<Scalars['Boolean']['input']>;
};


export type QueryCatalogProductTypesArgs = {
  page?: InputMaybe<PageInput>;
  productTypeCodes?: InputMaybe<Array<Scalars['String']['input']>>;
  search?: InputMaybe<Scalars['String']['input']>;
};


export type QueryClientAnalyticsArgs = {
  partyId: Scalars['ID']['input'];
  range?: InputMaybe<DateRangeInput>;
};


export type QueryCommentsArgs = {
  commentableId: Scalars['ID']['input'];
  commentableType: CommentableType;
};


export type QueryCommentsConnectionArgs = {
  commentableId: Scalars['ID']['input'];
  commentableType: CommentableType;
  page?: InputMaybe<PageInput>;
};


export type QueryCustomerAccessMatchCountsArgs = {
  accessProductTypeId?: InputMaybe<Scalars['ID']['input']>;
  canOrder?: InputMaybe<Scalars['Boolean']['input']>;
  parentPartyIds: Array<Scalars['ID']['input']>;
  search?: InputMaybe<Scalars['String']['input']>;
};


export type QueryCustomerDelegationsArgs = {
  filter?: InputMaybe<CustomerDelegationFilterInput>;
  page?: InputMaybe<PageInput>;
};


export type QueryCustomerShippingTermsOptionsArgs = {
  customerPartyId: Scalars['ID']['input'];
};


export type QueryCustomerTaxExemptionsArgs = {
  externalPartyId?: InputMaybe<Scalars['ID']['input']>;
};


export type QueryDefectRegistryArgs = {
  filter?: InputMaybe<QcMetricsFilterInput>;
  page?: InputMaybe<PageInput>;
};


export type QueryDeleteBlockersArgs = {
  entity: DeleteBlockerEntity;
  id: Scalars['ID']['input'];
};


export type QueryDisputesArgs = {
  filter?: InputMaybe<DisputeFilterInput>;
  page?: InputMaybe<PageInput>;
};


export type QueryEscalationChainArgs = {
  id: Scalars['ID']['input'];
};


export type QueryEscalationEventsArgs = {
  filter?: InputMaybe<EscalationEventsFilterInput>;
};


export type QueryEscalationEventsConnectionArgs = {
  filter?: InputMaybe<EscalationEventsFilterInput>;
  page?: InputMaybe<PageInput>;
};


export type QueryEventsArgs = {
  filter?: InputMaybe<EventFilterInput>;
  page?: InputMaybe<PageInput>;
};


export type QueryExplainTenantConfigArgs = {
  documentId: Scalars['ID']['input'];
};


export type QueryExportAnalyticsDashboardArgs = {
  dashboardId: Scalars['ID']['input'];
  filters?: InputMaybe<Scalars['AWSJSON']['input']>;
};


export type QueryExportFinanceReportArgs = {
  filter?: InputMaybe<Scalars['AWSJSON']['input']>;
  format?: InputMaybe<FinanceReportFileFormat>;
  id: FinanceReportId;
};


export type QueryExportInvoicesArgs = {
  filter?: InputMaybe<InvoiceFilterInput>;
  format: InvoiceExportFormat;
};


export type QueryExternalIntegrationArgs = {
  id: Scalars['ID']['input'];
};


export type QueryExternalIntegrationsArgs = {
  filter?: InputMaybe<ExternalIntegrationsFilterInput>;
};


export type QueryExternalPartiesArgs = {
  filter?: InputMaybe<FilterInput>;
  page?: InputMaybe<PageInput>;
  parentPartyId?: InputMaybe<Scalars['ID']['input']>;
};


export type QueryExternalPartiesConnectionArgs = {
  accessProductTypeId?: InputMaybe<Scalars['ID']['input']>;
  canOrder?: InputMaybe<Scalars['Boolean']['input']>;
  customerEligibleOnly?: InputMaybe<Scalars['Boolean']['input']>;
  page?: InputMaybe<PageInput>;
  parentPartyId?: InputMaybe<Scalars['ID']['input']>;
  rootKind?: InputMaybe<PartyRootKind>;
  rootsOnly?: InputMaybe<Scalars['Boolean']['input']>;
  search?: InputMaybe<Scalars['String']['input']>;
  type?: InputMaybe<Scalars['String']['input']>;
};


export type QueryExternalPartyArgs = {
  id: Scalars['ID']['input'];
};


export type QueryFailureReasonCodeArgs = {
  id: Scalars['ID']['input'];
};


export type QueryFailureReasonCodesArgs = {
  pipelineId?: InputMaybe<Scalars['ID']['input']>;
  stepDefinitionId?: InputMaybe<Scalars['ID']['input']>;
};


export type QueryFeatureFlagArgs = {
  id: Scalars['ID']['input'];
};


export type QueryFederationLinkArgs = {
  id: Scalars['ID']['input'];
};


export type QueryFederationLinksArgs = {
  filter?: InputMaybe<FederationLinkFilterInput>;
  page?: InputMaybe<PageInput>;
};


export type QueryFederationMessagesArgs = {
  page?: InputMaybe<PageInput>;
  workItemId: Scalars['ID']['input'];
};


export type QueryFieldLabelsArgs = {
  fieldNamePrefix?: InputMaybe<Scalars['String']['input']>;
  locale?: InputMaybe<Scalars['String']['input']>;
};


export type QueryFileArgs = {
  id: Scalars['ID']['input'];
};


export type QueryFileLocationsArgs = {
  fileId: Scalars['ID']['input'];
};


export type QueryFilesArgs = {
  filter?: InputMaybe<FilterInput>;
  page?: InputMaybe<PageInput>;
};


export type QueryFinanceArDashboardArgs = {
  asOf?: InputMaybe<Scalars['AWSDate']['input']>;
};


export type QueryFinanceReportArgs = {
  filter?: InputMaybe<Scalars['AWSJSON']['input']>;
  id: FinanceReportId;
  page?: InputMaybe<FinanceReportPageInput>;
};


export type QueryFlagAssignmentsArgs = {
  after?: InputMaybe<Scalars['String']['input']>;
  first?: InputMaybe<Scalars['Int']['input']>;
  flagId?: InputMaybe<Scalars['ID']['input']>;
  flagKey?: InputMaybe<Scalars['String']['input']>;
  scopeId?: InputMaybe<Scalars['String']['input']>;
  scopeType?: InputMaybe<FlagScopeType>;
};


export type QueryFormDefinitionArgs = {
  id: Scalars['ID']['input'];
};


export type QueryFormDefinitionVersionsArgs = {
  formDefinitionId: Scalars['ID']['input'];
  page?: InputMaybe<PageInput>;
};


export type QueryFormDefinitionsArgs = {
  formKind?: InputMaybe<FormKind>;
  status?: InputMaybe<PipelineStatus>;
};


export type QueryFormFieldArgs = {
  id: Scalars['ID']['input'];
};


export type QueryFormFieldsArgs = {
  isActive?: InputMaybe<Scalars['Boolean']['input']>;
};


export type QueryFxRatesArgs = {
  currencies: Array<Scalars['String']['input']>;
};


export type QueryHoldReasonCodesArgs = {
  includeInactive?: InputMaybe<Scalars['Boolean']['input']>;
};


export type QueryInventoryItemsArgs = {
  id?: InputMaybe<Scalars['ID']['input']>;
  isActive?: InputMaybe<Scalars['Boolean']['input']>;
};


export type QueryInventoryTransactionsArgs = {
  itemId?: InputMaybe<Scalars['ID']['input']>;
  page?: InputMaybe<PageInput>;
};


export type QueryInvitationArgs = {
  token: Scalars['ID']['input'];
};


export type QueryInvitationsArgs = {
  filter?: InputMaybe<InvitationFilterInput>;
  page?: InputMaybe<PageInput>;
};


export type QueryInvoiceArgs = {
  id: Scalars['ID']['input'];
};


export type QueryInvoiceDownloadUrlArgs = {
  id: Scalars['ID']['input'];
};


export type QueryInvoicesArgs = {
  filter?: InputMaybe<InvoiceFilterInput>;
  page?: InputMaybe<PageInput>;
};


export type QueryIteroConnectionArgs = {
  integrationId: Scalars['ID']['input'];
};


export type QueryLeadTimeMetricsArgs = {
  range?: InputMaybe<DateRangeInput>;
};


export type QueryLedgerAccountsArgs = {
  includeInactive?: InputMaybe<Scalars['Boolean']['input']>;
};


export type QueryLedgerMappingsArgs = {
  kind?: InputMaybe<LedgerMappingKind>;
};


export type QueryMigrationResolveCrosswalkArgs = {
  input: MigrationCrosswalkLookupInput;
};


export type QueryMigrationVerifySkuAlignmentArgs = {
  input?: InputMaybe<MigrationVerifySkuAlignmentInput>;
};


export type QueryMyScannerIntakeArgs = {
  page?: InputMaybe<PageInput>;
  scopePartyId: Scalars['ID']['input'];
};


export type QueryNasAgentDownloadsArgs = {
  input?: InputMaybe<NasAgentDownloadsInput>;
};


export type QueryNasReleasesArgs = {
  arch?: InputMaybe<Scalars['String']['input']>;
  channel?: InputMaybe<Scalars['String']['input']>;
  includeYanked?: InputMaybe<Scalars['Boolean']['input']>;
  platform?: InputMaybe<Scalars['String']['input']>;
};


export type QueryNasRolloutsArgs = {
  activeOnly?: InputMaybe<Scalars['Boolean']['input']>;
  arch?: InputMaybe<Scalars['String']['input']>;
  channel?: InputMaybe<Scalars['String']['input']>;
  platform?: InputMaybe<Scalars['String']['input']>;
};


export type QueryNotificationTemplatesArgs = {
  channel?: InputMaybe<NotificationChannel>;
  locale?: InputMaybe<Scalars['String']['input']>;
};


export type QueryNotificationsArgs = {
  filter?: InputMaybe<NotificationFilterInput>;
  page?: InputMaybe<PageInput>;
};


export type QueryOrderArgs = {
  id: Scalars['ID']['input'];
};


export type QueryOrderAnalyticsArgs = {
  granularity?: InputMaybe<AnalyticsGranularity>;
  range?: InputMaybe<DateRangeInput>;
};


export type QueryOrderBillingPartyChangeArgs = {
  orderId: Scalars['ID']['input'];
};


export type QueryOrderBreakoutsArgs = {
  orderIds: Array<Scalars['ID']['input']>;
};


export type QueryOrderCapabilitiesArgs = {
  orderId: Scalars['ID']['input'];
};


export type QueryOrderDraftArgs = {
  id: Scalars['ID']['input'];
};


export type QueryOrderIntakeFormDefinitionArgs = {
  customerPartyId?: InputMaybe<Scalars['ID']['input']>;
  includeScannerMapping?: InputMaybe<Scalars['Boolean']['input']>;
  orderId?: InputMaybe<Scalars['ID']['input']>;
};


export type QueryOrderNasActivityArgs = {
  orderId: Scalars['ID']['input'];
};


export type QueryOrderProductArgs = {
  id: Scalars['ID']['input'];
};


export type QueryOrderShippingLabelSenderArgs = {
  orderId: Scalars['ID']['input'];
};


export type QueryOrderShippingTermsOptionsArgs = {
  orderId: Scalars['ID']['input'];
};


export type QueryOrderSlipDocumentArgs = {
  orderId: Scalars['ID']['input'];
  orderProductId?: InputMaybe<Scalars['ID']['input']>;
};


export type QueryOrderSlipPrintCatalogArgs = {
  orderId: Scalars['ID']['input'];
};


export type QueryOrderUnitPipelineVersionArgs = {
  orderProductId: Scalars['ID']['input'];
};


export type QueryOrdersArgs = {
  filter?: InputMaybe<OrderFilterInput>;
  page?: InputMaybe<PageInput>;
  sort?: InputMaybe<OrderSortInput>;
};


export type QueryOrgUnitArgs = {
  id: Scalars['ID']['input'];
};


export type QueryOrgUnitDeletionPreflightArgs = {
  id: Scalars['ID']['input'];
};


export type QueryOrgUnitsArgs = {
  includeArchived?: InputMaybe<Scalars['Boolean']['input']>;
};


export type QueryOroviaGuestPreviewLinksArgs = {
  fileId: Scalars['ID']['input'];
};


export type QueryPartyCatalogAccessArgs = {
  at?: InputMaybe<Scalars['AWSDateTime']['input']>;
  partyId: Scalars['ID']['input'];
};


export type QueryPartyLedgerClassArgs = {
  partyId: Scalars['ID']['input'];
};


export type QueryPartyPricingAgreementsArgs = {
  partyId?: InputMaybe<Scalars['ID']['input']>;
};


export type QueryPartySenderIdentityArgs = {
  partyId: Scalars['ID']['input'];
};


export type QueryPartyShippingTermsArgs = {
  partyId: Scalars['ID']['input'];
};


export type QueryPaymentActivityArgs = {
  filter?: InputMaybe<PaymentActivityFilterInput>;
  page?: InputMaybe<PageInput>;
};


export type QueryPaymentAttemptStatusCheckArgs = {
  rootAttemptId: Scalars['ID']['input'];
};


export type QueryPaymentIngestionsArgs = {
  filter?: InputMaybe<PaymentIngestionFilterInput>;
  page?: InputMaybe<PageInput>;
};


export type QueryPaymentLifecycleArgs = {
  rootAttemptId: Scalars['ID']['input'];
};


export type QueryPaymentMethodsArgs = {
  partyId: Scalars['ID']['input'];
};


export type QueryPaymentReconciliationRunsArgs = {
  limit?: InputMaybe<Scalars['Int']['input']>;
};


export type QueryPermissionRegistryArgs = {
  includeDeprecated?: InputMaybe<Scalars['Boolean']['input']>;
  productCode?: InputMaybe<Scalars['String']['input']>;
};


export type QueryPipelineArgs = {
  asDraft?: InputMaybe<Scalars['Boolean']['input']>;
  id: Scalars['ID']['input'];
};


export type QueryPipelineConsumptionRulesArgs = {
  pipelineId: Scalars['ID']['input'];
  versionId?: InputMaybe<Scalars['ID']['input']>;
};


export type QueryPipelineGraphSavesArgs = {
  page?: InputMaybe<PageInput>;
  pipelineId: Scalars['ID']['input'];
};


export type QueryPipelineRoutingCheckArgs = {
  asDraft?: InputMaybe<Scalars['Boolean']['input']>;
  pipelineId: Scalars['ID']['input'];
};


export type QueryPipelineRunArgs = {
  id: Scalars['ID']['input'];
};


export type QueryPipelineStepArgs = {
  id: Scalars['ID']['input'];
};


export type QueryPipelineVersionsArgs = {
  page?: InputMaybe<PageInput>;
  pipelineId: Scalars['ID']['input'];
};


export type QueryPipelinesArgs = {
  includeInactive?: InputMaybe<Scalars['Boolean']['input']>;
};


export type QueryPipelinesPageArgs = {
  page?: InputMaybe<PageInput>;
  scope?: InputMaybe<PipelineScope>;
  search?: InputMaybe<Scalars['String']['input']>;
  status?: InputMaybe<PipelineStatus>;
};


export type QueryPortalViewConfigArgs = {
  surface: Scalars['String']['input'];
};


export type QueryPortalViewRegistryArgs = {
  surface: Scalars['String']['input'];
};


export type QueryPriceBookArgs = {
  id: Scalars['ID']['input'];
};


export type QueryPricingForOrderArgs = {
  orderId: Scalars['ID']['input'];
};


export type QueryProductBundleArgs = {
  customerPartyId?: InputMaybe<Scalars['ID']['input']>;
  id: Scalars['ID']['input'];
};


export type QueryProductBundlesArgs = {
  customerPartyId?: InputMaybe<Scalars['ID']['input']>;
};


export type QueryProductTypeArgs = {
  customerPartyId?: InputMaybe<Scalars['ID']['input']>;
  id: Scalars['ID']['input'];
};


export type QueryProductTypeAccessCoverageArgs = {
  productTypeId?: InputMaybe<Scalars['ID']['input']>;
};


export type QueryProductTypeAccessImpactListArgs = {
  input: ProductTypeAccessImpactListInput;
  page?: InputMaybe<PageInput>;
};


export type QueryProductTypeAccessImpactPreviewArgs = {
  changeSet: ProductTypeAccessChangeSetInput;
  productTypeId?: InputMaybe<Scalars['ID']['input']>;
};


export type QueryProductTypeAccessStatesArgs = {
  partyIds: Array<Scalars['ID']['input']>;
  productTypeId?: InputMaybe<Scalars['ID']['input']>;
};


export type QueryProductTypesArgs = {
  customerPartyId?: InputMaybe<Scalars['ID']['input']>;
};


export type QueryQcMetricsArgs = {
  filter?: InputMaybe<QcMetricsFilterInput>;
};


export type QueryRevenueReportArgs = {
  filter?: InputMaybe<RevenueReportFilterInput>;
};


export type QueryRoleArgs = {
  id: Scalars['ID']['input'];
};


export type QueryScannerIntakeCaseArgs = {
  ingestId: Scalars['ID']['input'];
};


export type QueryScannerIntakeIdentitiesArgs = {
  after?: InputMaybe<Scalars['String']['input']>;
  first?: InputMaybe<Scalars['Int']['input']>;
  integrationId?: InputMaybe<Scalars['ID']['input']>;
  query?: InputMaybe<Scalars['String']['input']>;
  state?: InputMaybe<ScannerIntakeIdentityState>;
};


export type QueryScannerIntakeInboxArgs = {
  filter?: InputMaybe<ScannerIntakeFilter>;
  ingestId?: InputMaybe<Scalars['ID']['input']>;
  page?: InputMaybe<PageInput>;
  provider?: InputMaybe<Scalars['String']['input']>;
  status?: InputMaybe<Scalars['String']['input']>;
};


export type QueryScopedKanbanArgs = {
  filter?: InputMaybe<ScopedKanbanFilter>;
  scopeId: Scalars['ID']['input'];
  scopeKind: DashboardScopeKind;
};


export type QueryScopedKanbanCardsArgs = {
  columnKey?: InputMaybe<Scalars['String']['input']>;
  filter?: InputMaybe<ScopedKanbanFilter>;
  page?: InputMaybe<PageInput>;
  scopeId: Scalars['ID']['input'];
  scopeKind: DashboardScopeKind;
};


export type QueryScopedKanbanMetaArgs = {
  scopeId: Scalars['ID']['input'];
  scopeKind: DashboardScopeKind;
};


export type QuerySelfAssignableStepRunsArgs = {
  orderId: Scalars['ID']['input'];
};


export type QuerySharedAccessLinkArgs = {
  id: Scalars['ID']['input'];
};


export type QueryShipTogetherCandidatesArgs = {
  orderId: Scalars['ID']['input'];
};


export type QueryShipmentsArgs = {
  filter?: InputMaybe<ShipmentFilterInput>;
  page?: InputMaybe<PageInput>;
};


export type QueryShippingAccountAssignmentPreviewArgs = {
  partyIds: Array<Scalars['ID']['input']>;
};


export type QueryShippingAccountsArgs = {
  carrier?: InputMaybe<ShippingCarrier>;
  includeArchived?: InputMaybe<Scalars['Boolean']['input']>;
};


export type QueryShippingBoxPresetsArgs = {
  includeArchived?: InputMaybe<Scalars['Boolean']['input']>;
};


export type QueryShippingSenderIdentitiesArgs = {
  includeArchived?: InputMaybe<Scalars['Boolean']['input']>;
  labAccount?: InputMaybe<Scalars['Boolean']['input']>;
  shippingAccountId?: InputMaybe<Scalars['ID']['input']>;
};


export type QuerySitesArgs = {
  includePlants?: InputMaybe<Scalars['Boolean']['input']>;
};


export type QuerySlaDrillDownArgs = {
  filter?: InputMaybe<SlaDashboardFilterInput>;
  groupBy?: InputMaybe<SlaDrillDownDimension>;
};


export type QuerySlaEvaluationsForPolicyArgs = {
  page?: InputMaybe<PageInput>;
  policyId: Scalars['ID']['input'];
};


export type QuerySlaOverviewArgs = {
  filter?: InputMaybe<SlaDashboardFilterInput>;
};


export type QuerySlaPolicyArgs = {
  id: Scalars['ID']['input'];
};


export type QueryStatementArgs = {
  id: Scalars['ID']['input'];
};


export type QueryStatementsArgs = {
  filter?: InputMaybe<StatementFilterInput>;
  page?: InputMaybe<PageInput>;
};


export type QueryStepCycleTimesArgs = {
  range?: InputMaybe<DateRangeInput>;
};


export type QueryStepFailureReasonCodesArgs = {
  pipelineStepId: Scalars['ID']['input'];
};


export type QueryStepRunArgs = {
  id: Scalars['ID']['input'];
};


export type QueryStepRunCapabilitiesArgs = {
  stepRunIds: Array<Scalars['ID']['input']>;
};


export type QueryStepRunsArgs = {
  filter?: InputMaybe<StepRunFilterInput>;
  page?: InputMaybe<PageInput>;
};


export type QueryStepThroughputByAssigneeArgs = {
  orgUnitIds?: InputMaybe<Array<Scalars['ID']['input']>>;
  page?: InputMaybe<PageInput>;
  range: DateRangeInput;
};


export type QuerySuggestImprovementArgs = {
  documentId: Scalars['ID']['input'];
  focus?: InputMaybe<Scalars['String']['input']>;
};


export type QueryTaxByCustomerArgs = {
  range: DateRangeInput;
};


export type QueryTaxByInvoiceArgs = {
  range: DateRangeInput;
};


export type QueryTaxCollectedByJurisdictionArgs = {
  grain: RevenuePeriodGrain;
  range: DateRangeInput;
};


export type QueryTaxDestinationGapsArgs = {
  orderIds?: InputMaybe<Array<Scalars['ID']['input']>>;
};


export type QueryTaxHealthArgs = {
  range: DateRangeInput;
};


export type QueryTaxImportPreviewArgs = {
  columnMap: TaxImportColumnMapInput;
  country: Scalars['String']['input'];
  csvText: Scalars['String']['input'];
  effectiveFrom: Scalars['AWSDate']['input'];
  regionCode: Scalars['String']['input'];
};


export type QueryTaxRateLookupArgs = {
  asOf?: InputMaybe<Scalars['AWSDate']['input']>;
  categoryCode?: InputMaybe<Scalars['String']['input']>;
  country: Scalars['String']['input'];
  postalCode?: InputMaybe<Scalars['String']['input']>;
  region?: InputMaybe<Scalars['String']['input']>;
};


export type QueryTeamMemberSkillsArgs = {
  userId: Scalars['ID']['input'];
};


export type QueryTechnicianCapacityArgs = {
  date?: InputMaybe<Scalars['AWSDate']['input']>;
};


export type QueryTenantConfigDocumentArgs = {
  id: Scalars['ID']['input'];
};


export type QueryTenantConfigHistoryArgs = {
  filter?: InputMaybe<TenantConfigHistoryFilter>;
  page?: InputMaybe<Scalars['Int']['input']>;
};


export type QueryThroughputMetricsArgs = {
  granularity?: InputMaybe<AnalyticsGranularity>;
  range?: InputMaybe<DateRangeInput>;
};


export type QueryTicketArgs = {
  id: Scalars['ID']['input'];
};


export type QueryTicketsArgs = {
  filter?: InputMaybe<TicketFilterInput>;
  page?: InputMaybe<PageInput>;
};


export type QueryTopClientsAnalyticsArgs = {
  limit?: InputMaybe<Scalars['Int']['input']>;
  range?: InputMaybe<DateRangeInput>;
};


export type QueryTrackShipmentArgs = {
  carrier: ShippingCarrier;
  trackingNumber: Scalars['String']['input'];
};


export type QueryUnassignedWorkArgs = {
  orgUnitId?: InputMaybe<Scalars['ID']['input']>;
  orgUnitIds?: InputMaybe<Array<Scalars['ID']['input']>>;
  page?: InputMaybe<Scalars['Int']['input']>;
  search?: InputMaybe<Scalars['String']['input']>;
};


export type QueryUserArgs = {
  id: Scalars['ID']['input'];
};


export type QueryUsersArgs = {
  filter?: InputMaybe<UserFilterInput>;
  page?: InputMaybe<PageInput>;
};


export type QueryValidateIntakeArgs = {
  input: ValidateIntakeInput;
};


export type QueryVendorRoutingRuleArgs = {
  id: Scalars['ID']['input'];
};


export type QueryVolumeCountersArgs = {
  partyId: Scalars['ID']['input'];
  period?: InputMaybe<Scalars['String']['input']>;
  productTypeId?: InputMaybe<Scalars['ID']['input']>;
};

export type QueueDeprovisioningInput = {
  id: Scalars['ID']['input'];
  /** Required for zero-retention cleanup: `DELETE <tenant id>`. */
  immediateDeleteConfirmation?: InputMaybe<Scalars['String']['input']>;
  reason?: InputMaybe<Scalars['String']['input']>;
  /**
   * Recover tenant-owned resources left by a legacy failed teardown.
   * Allowed only for a DEPROVISIONING tenant with no retry metadata, a latest
   * terminal cleanup failure, zero retention, and the exact DELETE confirmation.
   */
  recoverLegacyResources?: InputMaybe<Scalars['Boolean']['input']>;
  retentionDays?: InputMaybe<Scalars['Int']['input']>;
};

export type QueueDeprovisioningPayload = {
  __typename?: 'QueueDeprovisioningPayload';
  deprovisioningExecutionArn: Scalars['String']['output'];
  retentionDays: Scalars['Int']['output'];
  tenant: Tenant;
};

export type QuoteManualShippingChargeInput = {
  carrier: ShippingCarrier;
  orderId: Scalars['ID']['input'];
  packageWeightOz?: InputMaybe<Scalars['Float']['input']>;
  serviceLevel?: InputMaybe<Scalars['String']['input']>;
  trackingNumber: Scalars['String']['input'];
};

/** Input for reassignStepRun. */
export type ReassignStepRunInput = {
  /**
   * Formerly required to reassign a step that is already in progress. Accepted and ignored: an in-progress reassignment still supersedes the current attempt and the new assignee starts a fresh attempt, without confirmation.
   * @deprecated ENG-3630: assigning a step starts it; in-progress reassignment no longer requires acknowledgement. Ignored: no longer read.
   */
  acknowledgeInProgressReassign?: InputMaybe<Scalars['Boolean']['input']>;
  newAssigneeUserId: Scalars['ID']['input'];
  reason?: InputMaybe<Scalars['String']['input']>;
  stepRunId: Scalars['ID']['input'];
};

/**
 * Input for reauthorizePaymentMethod — re-enable a saved BANK ACCOUNT in
 * place, without re-entering the account and routing numbers.
 *
 * Only for methods whose `unusableReason` is MANDATE_MISSING or
 * BILLING_ADDRESS_MISSING: our record is incomplete while the account itself is
 * still vaulted at the provider. MANDATE_REVOKED and METHOD_REVOKED are REFUSED
 * server-side — a mandate is revoked because the payer withdrew authorization or
 * because a revoke-class NACHA return came back (R02 account closed, R03 no
 * account, R04 invalid account number), so re-enabling originates another debit
 * against a probably-dead account and earns another return.
 *
 * The server verifies the vault entry still exists at the provider, then writes a
 * NEW mandate and a NEW append-only `ach_authorizations` row stamped with TODAY's
 * date, the server-observed IP and User-Agent, and the confirmed billing address.
 * Nothing is backdated and nothing implies the payer consented earlier.
 */
export type ReauthorizePaymentMethodInput = {
  /**
   * The billing address the payer confirmed on this screen. REQUIRED and
   * refused if incomplete, exactly as on addPaymentMethod: it is snapshotted into
   * the immutable authorization row beside the consent text and the timestamp.
   */
  billingAddress: BillingAddressInput;
  /** The saved method to re-enable. */
  id: Scalars['ID']['input'];
  /**
   * REQUIRED. Carries the `authorizationVersion` the client displayed; the
   * server refuses a mismatch. `mandateText` is ignored for ACH — the recorded
   * wording is always the server's own.
   */
  mandate: MandateConsentInput;
};

/**
 * ENG-1264 — result of receiving an inter-site shipment at its destination.
 * `receivedCaseCount` is the number of cases (order_products) whose custody was
 * flipped in_transit -> at_site by this receive; it is 0 only for a shipment that
 * carried no in-flight case (already-received legs are rejected, not counted).
 */
export type ReceiveInterSiteShipmentPayload = {
  __typename?: 'ReceiveInterSiteShipmentPayload';
  receivedAt: Scalars['AWSDateTime']['output'];
  receivedCaseCount: Scalars['Int']['output'];
  shipmentId: Scalars['ID']['output'];
  /** Terminal status after receipt — always 'received'. */
  status: Scalars['String']['output'];
};

/** Input for recordBillingEvent — system-only mutation (Lambda + IAM). */
export type RecordBillingEventInput = {
  amount: Scalars['Float']['input'];
  currency: Scalars['String']['input'];
  eventType: BillingEventType;
  invoiceId?: InputMaybe<Scalars['ID']['input']>;
  occurredAt?: InputMaybe<Scalars['AWSDateTime']['input']>;
  orderId?: InputMaybe<Scalars['ID']['input']>;
  organizationId: Scalars['ID']['input'];
  payload?: InputMaybe<Scalars['AWSJSON']['input']>;
};

/**
 * Record a permit number against a state's registrations, and optionally start
 * collecting.
 *
 * ⚠️ THERE IS NO `taxJurisdictionId` AND THERE MUST NEVER BE ONE — see the section
 * comment above. The region selects the rows; the server does the selection.
 *
 * ⚠️ THERE IS NO `collectsFrom` / `collectsTo` HERE AND THERE MUST NEVER BE. The
 * earliest `collectsFrom` in an organization is the ABSOLUTE FLOOR for how far any
 * tax rate write may be backdated, so a freely settable date on a bulk sweep would
 * lower that floor for every later rate write in the organization. The start date
 * is settable once, on `activateTaxRegistration`, where it is bounded by the
 * jurisdiction's own effective date.
 *
 * ⚠️ AND THERE IS NO `expectedVersion`, DELIBERATELY. A sweep covers many rows and
 * cannot carry one token. Concurrency is a per-organization lock held to commit,
 * and the audit row records the PRIOR permit numbers so a re-run with a different
 * number is visible rather than silent.
 */
export type RecordTaxPermitInput = {
  /**
   * Whether to START COLLECTING. An explicit decision, never implied by a
   * number arriving: `true` flips every selected `PENDING` registration whose
   * collecting window is still open to `ACTIVE`, which starts charging tax to
   * every customer shipping into that state. `CLOSED` registrations and ones whose
   * window has ended are left alone and reported in `skipped`.
   */
  activate: Scalars['Boolean']['input'];
  /**
   * The attestation text the client displayed, verbatim. Checked for equality
   * against the server's own copy and refused on mismatch
   * (`TaxPermitAttestationMismatch`) — recording an agreement different from the
   * one shown would make the audit trail worthless.
   */
  attestationText: Scalars['String']['input'];
  /**
   * Must be `true`. The attestation replaces the verification this product
   * cannot perform, so `false` is refused by name
   * (`TaxPermitAttestationRequired`).
   */
  attested: Scalars['Boolean']['input'];
  /** ISO-3166-1 alpha-2, e.g. `US`. */
  country: Scalars['String']['input'];
  /**
   * The state / province the permit was issued for, e.g. `CA`. Selects the
   * region's own registration and every postal-code registration under it.
   */
  regionCode: Scalars['String']['input'];
  /**
   * The permit / licence number from the registration document.
   *
   * ⚠️ RECORDED AND ATTESTED, NEVER VERIFIED. No lookup against CDTFA or any other
   * authority happens, or exists, anywhere in this product.
   */
  registrationNumber: Scalars['String']['input'];
};

/** Send money back to a payer against a settled charge. */
export type RefundPaymentTransactionInput = {
  /**
   * Decimal string, at most two decimal places — the same shape and the same
   * reason as `Mutation.chargeInvoice`'s amount.
   *
   * ⚠️ REQUIRED, unlike the orchestrator's optional argument. The per-org daily
   * refund ceiling has to compare a KNOWN figure against the day's total before the
   * provider is called, and "refund whatever is left" is a button whose amount the
   * operator never saw. The console has the charge in hand and can offer the full
   * refundable balance as a default.
   */
  amount: Scalars['String']['input'];
  /**
   * ⚠️ REQUIRED. Mint ONE value per refund you intend to issue — when the
   * confirmation opens, not when you press the button — and send the SAME value on
   * every retry of that same refund.
   *
   * WHAT IT IS FOR. Without it a re-submitted refund is a SECOND refund. The
   * provider idempotency key underneath this mutation was derived from the total
   * already refunded against the charge, which reproduces on a retry only while the
   * first attempt's write has NOT committed; once it has, the derivation moves and
   * the provider accepts the request as new money going out. This token is a fact
   * about the REQUEST, so it does not move, and the server additionally claims it
   * in a per-tenant ledger before the money path is entered: a repeat is refused
   * with `Conflict` naming the refund that already exists, and no second refund is
   * sent.
   *
   * ⚠️ REQUIRED RATHER THAN OPTIONAL, DELIBERATELY. An opt-in control on an
   * irreversible cash-out is not a control — a client that omitted it would keep
   * the full exposure while the schema advertised the fix. Nothing is broken by
   * requiring it: this mutation is new in the same change, so there is no caller in
   * the field to migrate. Making it required LATER would be the breaking change.
   *
   * A UUID is the expected shape. Do NOT reuse one across two genuinely different
   * refunds of the same charge — the second is refused. Do NOT mint a fresh one to
   * get past a refusal; the refusal means the money already went.
   */
  clientRequestId: Scalars['ID']['input'];
  /** Required. Recorded on the paired billing adjustment and the audit row. */
  reason: Scalars['String']['input'];
  /** The `payment_transactions` CHARGE row being refunded. */
  transactionId: Scalars['ID']['input'];
};

export type RefundPaymentTransactionPayload = {
  __typename?: 'RefundPaymentTransactionPayload';
  /** The provider's own id for the refund. */
  providerRefundId: Scalars['String']['output'];
  /** The new `payment_transactions` refund row. */
  refundTransactionId: Scalars['ID']['output'];
  status: PaymentRefundStatus;
};

/** Input for Mutation.registerOAuthClient. */
export type RegisterOAuthClientInput = {
  /** Optional absolute expiry. Null = non-expiring. */
  expiresAt?: InputMaybe<Scalars['AWSDateTime']['input']>;
  /** Mark the client as sandbox (non-production). Defaults false. */
  isSandbox?: InputMaybe<Scalars['Boolean']['input']>;
  /**
   * Partner PUBLIC keys as a JWKS object. REQUIRED for private_key_jwt, must be
   * ABSENT for client_secret. Stored verbatim at registration; never fetched from
   * a live jwks_uri. Must contain PUBLIC keys only (private component rejected).
   */
  jwks?: InputMaybe<Scalars['AWSJSON']['input']>;
  /**
   * Dotted scope strings the client may exercise (same vocabulary as
   * api_keys.scopes). A PHI-bearing scope requires private_key_jwt.
   */
  scopes: Array<Scalars['String']['input']>;
  /** Service account this client authenticates as (oauth_clients.service_account_id). */
  serviceAccountId: Scalars['ID']['input'];
  /**
   * Client-authentication method: "client_secret" (shared secret, argon2id-
   * hashed) or "private_key_jwt" (RFC 7523; we store the partner's PUBLIC jwks).
   */
  tokenEndpointAuthMethod: Scalars['String']['input'];
};

/**
 * Payload for Mutation.registerOAuthClient. `clientSecret` is surfaced ONCE for
 * client_secret clients (null for private_key_jwt) — callers MUST save it; it is
 * not retrievable afterward.
 */
export type RegisterOAuthClientPayload = {
  __typename?: 'RegisterOAuthClientPayload';
  /** Stable public client identifier `cpc_…` presented at /token. Safe to display. */
  clientId: Scalars['String']['output'];
  /**
   * The full plaintext secret `cpcs_…` (client_secret method only). Surfaced
   * exactly once; null for private_key_jwt. Treat as a secret.
   */
  clientSecret?: Maybe<Scalars['String']['output']>;
  /** ISO-8601 creation timestamp. */
  createdAt: Scalars['AWSDateTime']['output'];
  /** Server-assigned oauth_clients.id. */
  id: Scalars['ID']['output'];
};

/** Input for registerUploadedFile. */
export type RegisterUploadedFileInput = {
  contentHash?: InputMaybe<Scalars['String']['input']>;
  fileId: Scalars['ID']['input'];
  metadata?: InputMaybe<Scalars['AWSJSON']['input']>;
};

/** Payload for registerUploadedFile. */
export type RegisterUploadedFilePayload = {
  __typename?: 'RegisterUploadedFilePayload';
  file: File;
};

export type ReinstateTenantInput = {
  id: Scalars['ID']['input'];
  reason?: InputMaybe<Scalars['String']['input']>;
};

/**
 * Payload for rejectFederationLink — Bible §06:18. Carries the rejected
 * link (status now `rejected`, revokedAt populated) plus the reason text the
 * proposer will see in their notification.
 */
export type RejectFederationLinkPayload = {
  __typename?: 'RejectFederationLinkPayload';
  federationLink: FederationLink;
  reason?: Maybe<Scalars['String']['output']>;
};

export type RejectJitGrantInput = {
  id: Scalars['ID']['input'];
  reason: Scalars['String']['input'];
};

export type ReleaseAssignmentInput = {
  reason: Scalars['String']['input'];
  stepRunId: Scalars['ID']['input'];
};

/**
 * Input for releaseOrderHold.
 *
 * `releaseNote` is REQUIRED and must be at least 10 characters. Release is the
 * action that restarts the turnaround clock, and any role that can place a hold
 * can release one — so the note plus the audit row are the only accountability on
 * it.
 */
export type ReleaseOrderHoldInput = {
  holdId: Scalars['ID']['input'];
  releaseNote: Scalars['String']['input'];
};

/**
 * Input for removeFileLocation (MOL-583) — mark a location stale. Retains
 * the row for audit; does not DELETE.
 */
export type RemoveFileLocationInput = {
  locationId: Scalars['ID']['input'];
  reason: Scalars['String']['input'];
};

/** Payload for removeFileLocation. */
export type RemoveFileLocationPayload = {
  __typename?: 'RemoveFileLocationPayload';
  location?: Maybe<FileLocation>;
};

export type RemovePermissionFromRoleInput = {
  permissionKey: Scalars['String']['input'];
  reason?: InputMaybe<Scalars['String']['input']>;
  roleId: Scalars['ID']['input'];
  tenantId: Scalars['ID']['input'];
};

/**
 * Payload for reorderFromOrder (ENG-1018 item 4 — 1-click reorder).
 *
 * Clones a past order into a fresh, caller-owned wizard draft: same products,
 * tooth/material/shade and workflow answers, for the same customer_party. The
 * draft is persisted (order_drafts) so it resumes like any autosaved wizard,
 * and the prefilled `payload` (same shape as SaveOrderDraftInput.payload /
 * the portal WizardDraft) is returned so the client can open the confirm-recap
 * modal immediately without a second round-trip. No order is created here —
 * the doctor reviews + submits from the wizard.
 */
export type ReorderDraftPayload = {
  __typename?: 'ReorderDraftPayload';
  /** New draft id, owned by the caller. Stable across subsequent autosaves. */
  draftId: Scalars['ID']['output'];
  /**
   * Human-readable material-for-restoration guardrail note(s) for the
   * confirm-recap modal. Empty when there is nothing to flag.
   */
  guardrailNotes: Array<Scalars['String']['output']>;
  /**
   * Prefilled WizardDraft state (products, quantities, per-line intake incl.
   * tooth/shade/material/workflow, order-level intake + case reference).
   */
  payload: Scalars['AWSJSON']['output'];
  /** The order this reorder was cloned from. */
  sourceOrderId: Scalars['ID']['output'];
};

export type RequestAccountDeletionInput = {
  /**
   * The user's own email — must match the row in customer_global_identities.
   * Acts as a typing-confirmation gate (prevents accidental clicks).
   */
  confirmationEmail: Scalars['AWSEmail']['input'];
};

/** Input for BA.4 requestAuditExport. */
export type RequestAuditExportInput = {
  actionFilter?: InputMaybe<Scalars['String']['input']>;
  dateRangeEnd: Scalars['AWSDateTime']['input'];
  dateRangeStart: Scalars['AWSDateTime']['input'];
  format: AuditExportFormat;
  tenantId: Scalars['ID']['input'];
  tenantName: Scalars['String']['input'];
  tenantSlug: Scalars['String']['input'];
};

/**
 * Input for requestBulkFileExport (ENG-24). Provide EXACTLY ONE selection:
 * either `orderId` (bundle every file on that order the caller may download) or
 * a non-empty `fileIds` set (bundle those specific files). Bounded server-side
 * to a maximum file count and total size.
 */
export type RequestBulkFileExportInput = {
  /**
   * Bundle this explicit set of files. A file the caller is not authorized
   * for is rejected (fail-closed) rather than silently omitted.
   */
  fileIds?: InputMaybe<Array<Scalars['ID']['input']>>;
  /** Bundle all downloadable files attached to this order. */
  orderId?: InputMaybe<Scalars['ID']['input']>;
  /** Presigned-URL lifetime; clamped to the download TTL ceiling. */
  ttlSeconds?: InputMaybe<Scalars['Int']['input']>;
};

/**
 * Payload for requestBulkFileExport — a single presigned GET against the
 * assembled ZIP archive. `skippedCount` reports files omitted because they were
 * scan-blocked (or, in orderId mode, outside the caller's download scope).
 */
export type RequestBulkFileExportPayload = {
  __typename?: 'RequestBulkFileExportPayload';
  downloadUrl: Scalars['AWSURL']['output'];
  expiresAt: Scalars['AWSDateTime']['output'];
  fileCount: Scalars['Int']['output'];
  skippedCount: Scalars['Int']['output'];
  totalBytes: Scalars['Float']['output'];
};

/**
 * Input for requestCaseAdjustment (ENG-1424).
 *
 * Creates a NEW `order_type='adjustment'` order linked to the source via
 * `parentOrderId`/`relatedOrderId`, which runs the SHORT adjustment pipeline
 * (`product_types.adjustment_pipeline_id`, else the org default adjustment
 * pipeline) rather than a full rebuild.
 *
 * The source case is NOT modified: an adjustment is a fix to a restoration
 * still in place, so the source keeps its `seated`/`closed` lifecycle and its
 * own history. That is the difference from `requestWarrantyReturn`, which
 * retires the source.
 *
 * Deliberately carries NO `customerPartyId`: every party on the new order is
 * inherited from the source case. Whether the customer is charged is decided
 * SERVER-SIDE from the tenant's adjustment charge policy crossed with the
 * source case's warranty window, and frozen onto the new order — it is not an
 * input and cannot be influenced by the caller.
 */
export type RequestCaseAdjustmentInput = {
  /** Subset of source-case lines needing adjustment; non-empty. */
  productLines: Array<AdjustmentProductLineInput>;
  /** Free-form reason captured on the audit row + emitted event payload. */
  reason: Scalars['String']['input'];
  /** Source case being adjusted. Must be seated or closed. */
  sourceOrderId: Scalars['ID']['input'];
};

/** Payload for requestCaseAdjustment. */
export type RequestCaseAdjustmentPayload = {
  __typename?: 'RequestCaseAdjustmentPayload';
  /**
   * The newly-created adjustment order (`orderType` = `adjustment`,
   * `lifecycleStatus` = `active`). parentOrderId/relatedOrderId echo the source.
   */
  order: Order;
  /**
   * The source case, UNCHANGED. Returned so a caller can see that raising an
   * adjustment did not move the original case's lifecycle.
   */
  sourceOrder: Order;
  /** Warnings. Empty today — every quantity/lifecycle problem is a hard refusal. */
  warnings: Array<Scalars['String']['output']>;
};

/** Input for requestFileDownload (MOL-583). */
export type RequestFileDownloadInput = {
  /**
   * Internal users only. Explicitly accepts the risk of downloading a file
   * GuardDuty could not fully inspect. It never overrides INFECTED, FAILED,
   * ACCESS_DENIED, PENDING, or unknown states.
   */
  acceptUnverifiedRisk?: InputMaybe<Scalars['Boolean']['input']>;
  disposition?: InputMaybe<FileDisposition>;
  fileId: Scalars['ID']['input'];
  ttlSeconds?: InputMaybe<Scalars['Int']['input']>;
};

/**
 * Payload for requestFileDownload — presigned GET against the primary
 * location of the file.
 */
export type RequestFileDownloadPayload = {
  __typename?: 'RequestFileDownloadPayload';
  downloadUrl: Scalars['AWSURL']['output'];
  expiresAt: Scalars['AWSDateTime']['output'];
  location: FileLocation;
};

/** Input for requestFileUpload. */
export type RequestFileUploadInput = {
  attachTo?: InputMaybe<FileAttachmentInput>;
  /**
   * ENG-590 — optional neutral file-kind classification for the new files row.
   * Defaults to OTHER when omitted.
   */
  category?: InputMaybe<FileCategory>;
  /** Browser-computed SHA-256 hex digest. Required for STL files attached to an order. */
  contentHash?: InputMaybe<Scalars['String']['input']>;
  filename: Scalars['String']['input'];
  mimeType: Scalars['String']['input'];
  origin: FileOrigin;
  /**
   * ENG-3844 — optional upload purpose. CHECKLIST_EVIDENCE is refused for any
   * non-staff caller and requires attachTo ORDER; the file is stored INTERNAL.
   * Omit for every other upload.
   */
  purpose?: InputMaybe<FileUploadPurpose>;
  sizeBytes: Scalars['Float']['input'];
  /**
   * ENG-590 — optional lineage parent: the file this upload supersedes. When
   * set, the new row's version is the parent's version + 1 and parent_file_id is
   * bound to it (same-org enforced server-side). Omit for a brand-new file.
   */
  supersedesFileId?: InputMaybe<Scalars['ID']['input']>;
};

/** Payload for requestFileUpload — caller PUTs to the presigned URL. */
export type RequestFileUploadPayload = {
  __typename?: 'RequestFileUploadPayload';
  /** When the URL expires. */
  expiresAt: Scalars['AWSDateTime']['output'];
  /** Pre-allocated file row. */
  file: File;
  /** Required headers for the PUT. */
  uploadHeaders: Scalars['AWSJSON']['output'];
  /** Presigned PUT URL (single-use). */
  uploadUrl: Scalars['AWSURL']['output'];
};

export type RequestJitGrantInput = {
  accessLevel: Scalars['String']['input'];
  durationMinutes: Scalars['Int']['input'];
  /** Required when accessLevel == 'write' (MOL-657). */
  nominatedSecondApproverCognitoSub?: InputMaybe<Scalars['String']['input']>;
  reason: Scalars['String']['input'];
  scope: Scalars['String']['input'];
  tenantId: Scalars['ID']['input'];
};

export type RequestShippingRatesInput = {
  /** Which carrier to rate-shop. Must be connected via connectIntegration first. */
  carrier: ShippingCarrier;
  fromAddress: ShippingAddressInput;
  insuredValueUsd?: InputMaybe<Scalars['Float']['input']>;
  /**
   * Null only from a client older than per-label options: the pre-options
   * behaviour then applies.
   */
  options?: InputMaybe<ShipmentOptionsInput>;
  /** ENG-3286 — quote on the account this order ships on. Omit for the lab's own account. */
  orderId?: InputMaybe<Scalars['ID']['input']>;
  /** Required for CUSTOMER_PACKAGING; ignored for a carrier package. */
  packageDimensionsIn?: InputMaybe<ShippingPackageDimensionsInput>;
  packageWeightOz: Scalars['Float']['input'];
  /** Optional service-level filter; omit to return all available. */
  serviceLevel?: InputMaybe<ShippingServiceLevel>;
  toAddress: ShippingAddressInput;
};

/**
 * Input for requestWarrantyReturn (Bible §05:158-167).
 *
 * Creates a NEW order linked to the source via `warranty_for_order_id` and
 * flips the source order's `lifecycle_status` to `warranty_returned`. The new
 * order picks up the warranty pipeline (per `productType.warranty_pipeline_id`,
 * falling back to the org default warranty pipeline).
 */
export type RequestWarrantyReturnInput = {
  /** Optional override; resolver uses original order's customer_party_id if null. */
  customerPartyId?: InputMaybe<Scalars['ID']['input']>;
  /** Source order being warranty-claimed. */
  originalOrderId: Scalars['ID']['input'];
  /** Subset of source-order lines included in the return; non-empty. */
  productLines: Array<WarrantyProductLineInput>;
  /** Free-form reason captured on the audit row + emitted event payload. */
  reason: Scalars['String']['input'];
};

/** Payload for requestWarrantyReturn. */
export type RequestWarrantyReturnPayload = {
  __typename?: 'RequestWarrantyReturnPayload';
  /**
   * The newly-created warranty order. parentOrderId/relatedOrderId echo
   * the source order; warrantyForOrderId is the typed pointer set by the
   * resolver.
   */
  order: Order;
  /** Source order after the lifecycle_status='warranty_returned' back-update. */
  originalOrder: Order;
  /** Warnings (e.g. product type missing warranty pipeline → fell back to default). */
  warnings: Array<Scalars['String']['output']>;
};

/** How lab staff resolve a practitioner's REQUEST_CHANGES decision. */
export type ResolveCustomerApprovalInput = {
  holdId: Scalars['ID']['input'];
  /** Required for SEND_BACK. False skips exactly the next approval at this step. */
  requireReapproval?: InputMaybe<Scalars['Boolean']['input']>;
  resolution: CustomerApprovalResolution;
  /**
   * Required for SEND_BACK; must be a rework step offered by the server. The
   * approval-producing step itself is included so work can restart there.
   */
  targetStepId?: InputMaybe<Scalars['ID']['input']>;
};

/**
 * Human-selected destination for an exact scanner identity tuple. Omitting
 * the practitioner saves a clinic-only mapping that remains lab-only.
 */
export type ResolveScannerIntakeIdentityInput = {
  customerPartyId: Scalars['ID']['input'];
  ingestId: Scalars['ID']['input'];
  practitionerPartyId?: InputMaybe<Scalars['ID']['input']>;
};

/** Result of confirming one exact scanner identity tuple. */
export type ResolveScannerIntakeIdentityPayload = {
  __typename?: 'ResolveScannerIntakeIdentityPayload';
  customerPartyId: Scalars['ID']['output'];
  mappingId: Scalars['ID']['output'];
  matchedCaseCount: Scalars['Int']['output'];
  practitionerPartyId?: Maybe<Scalars['ID']['output']>;
};

/**
 * Input for resolveStepRunVendorDelay (ENG-40) — mark the step's ACTIVE vendor
 * delay resolved.
 */
export type ResolveStepRunVendorDelayInput = {
  /** Optional note captured at resolution (e.g. "vendor shipped, ETA tomorrow"). */
  resolutionNote?: InputMaybe<Scalars['String']['input']>;
  stepRunId: Scalars['ID']['input'];
};

export type ResolveTicketInput = {
  resolutionNote?: InputMaybe<Scalars['String']['input']>;
  ticketId: Scalars['ID']['input'];
};

export type ResolvedShippingTerms = {
  __typename?: 'ResolvedShippingTerms';
  displayName: Scalars['String']['output'];
  /** Set when the terms come from an ancestor party. */
  inheritedFromPartyId?: Maybe<Scalars['ID']['output']>;
  inheritedFromPartyName?: Maybe<Scalars['String']['output']>;
  shippingAccount?: Maybe<ShippingAccountRef>;
  source: ShippingTermsSource;
  terms: ShippingTerms;
};

/** ENG-3881 S3.4 — input for `restampInvoiceLedgerAllocations`. */
export type RestampInvoiceLedgerAllocationsInput = {
  /**
   * Required true when `onlyUnmapped` is false, and when a changed stamp touches
   * a month already exported to QuickBooks: the caller confirms the difference
   * will be posted in QuickBooks by hand.
   */
  acknowledgeReclassification?: InputMaybe<Scalars['Boolean']['input']>;
  /**
   * Omitted or true: lines issued before ledger stamping existed get their
   * first stamp, with the reason prefixed `backfill: `.
   */
  includeUnstamped?: InputMaybe<Scalars['Boolean']['input']>;
  /** One invoice month `YYYY-MM` (invoice_date, the organization's day). */
  month: Scalars['String']['input'];
  /**
   * Omitted or true: only lines whose current stamp posts some amount UNMAPPED.
   * False: every stamped line of the month is resolved again.
   */
  onlyUnmapped?: InputMaybe<Scalars['Boolean']['input']>;
  /**
   * Why; 1 to 490 characters after trimming, with a letter or number and no
   * control or invisible characters. Stored on every new stamp.
   */
  reason: Scalars['String']['input'];
};

/**
 * ENG-3881 S3.4 — what a restamp did. A line whose resolution did not change
 * gets no new stamp, so a second identical call restamps nothing.
 */
export type RestampInvoiceLedgerAllocationsResult = {
  __typename?: 'RestampInvoiceLedgerAllocationsResult';
  /** Of `touchedMonths`, the ones already exported to QuickBooks. */
  exportedMonthsTouched: Array<Scalars['String']['output']>;
  /** Of `linesRestamped`, lines that had no stamp before. */
  linesBackfilled: Scalars['Int']['output'];
  /** Lines of the month that matched the filters. */
  linesConsidered: Scalars['Int']['output'];
  /**
   * Lines left on their stamp because their source kind would change (for
   * example a shipping charge whose shipment was relinked).
   */
  linesKeptClassification: Scalars['Int']['output'];
  /** Lines given a new stamp (including backfilled ones). */
  linesRestamped: Scalars['Int']['output'];
  /**
   * Mapped lines left alone because they would now resolve UNMAPPED (an
   * inactive or removed mapping).
   */
  linesWouldUnmap: Scalars['Int']['output'];
  month: Scalars['String']['output'];
  /** Considered lines whose current stamp still posts some amount UNMAPPED. */
  stillUnmapped: Scalars['Int']['output'];
  /**
   * Journal months (`YYYY-MM`) the new stamps change: the invoice month plus the
   * months of those invoices' voids, adjustments and refunds.
   */
  touchedMonths: Array<Scalars['String']['output']>;
};

/**
 * Input for restoreFormVersion (ported from dev's ENG-780b / epic ENG-769
 * at the 2026-08-05 merge). "Restore this version": seeds a DRAFT of the form
 * from a chosen prior version's content, so an admin can review it and Publish
 * — the SAFE draft-fork path (open-for-edit -> rebuild the draft from the old
 * version -> publish), NOT a raw revert. versionId must be a version of
 * formDefinitionId. Returns FormDefinitionEditPayload, same as
 * openFormDefinitionForEdit/discardFormDefinitionDraft above.
 */
export type RestoreFormVersionInput = {
  formDefinitionId: Scalars['ID']['input'];
  versionId: Scalars['ID']['input'];
};

export type ResumeNasRolloutInput = {
  id: Scalars['ID']['input'];
};

/**
 * Stop a tax jurisdiction applying, from a date forward. ENG-2497 §9.
 *
 * **A SOFT CLOSE, NOT A DELETE.** It sets the jurisdiction, its boundaries and its
 * rates to end on `effectiveTo`, and closes its registrations' collecting window
 * on the same date. Nothing is removed: `invoice_line_taxes` references the
 * jurisdiction and is append-only, so an invoice issued while it was live must
 * keep evidencing what it was charged under.
 */
export type RetireTaxJurisdictionInput = {
  /**
   * The date it stops applying.
   *
   * **EXCLUSIVE**, like every effective window in the tax engine: the last day it
   * is in force is the day BEFORE this date. May be in the future, which is how a
   * planned stop-collecting date is scheduled. May not fall on or before the last
   * day an invoice was billed against it, and may not fall on or before the
   * jurisdiction's own `effectiveFrom`.
   */
  effectiveTo: Scalars['AWSDate']['input'];
  /**
   * The `version` the operator was shown. REQUIRED — retiring changes what
   * every address it covers is charged, so it must not silently overwrite a
   * concurrent edit. A mismatch returns `Conflict` and writes nothing.
   */
  expectedVersion: Scalars['String']['input'];
  /**
   * The jurisdiction to retire. Must belong to this organization — a shared
   * platform row is refused by name (`TaxPlatformRowImmutable`).
   */
  id: Scalars['ID']['input'];
};

export type RetryFileScanInput = {
  fileId: Scalars['ID']['input'];
};

export type RetryFileScanPayload = {
  __typename?: 'RetryFileScanPayload';
  retryAfterSeconds?: Maybe<Scalars['Int']['output']>;
  scanStatus: FileScanStatus;
  status: FileScanRetryStatus;
};

/**
 * Which side of the ledger a revenue figure is anchored to. FIN-M6 (ENG-1477).
 *
 * `ACCRUAL_BILLED` anchors on the invoice ISSUE date —
 * `COALESCE(invoices.sent_at, invoices.created_at)`, the same convention
 * `Query.invoices`' `issuedAt` filter and `Invoice.issuedAt` use — so a bucket
 * holds exactly the invoices an operator would find by filtering the invoice list
 * to the same window.
 *
 * `CASH` anchors on `payment_transactions.occurred_at` and reports money that
 * actually moved: charges less refunds less chargebacks. Provider `fee` and
 * `adjustment` transactions are excluded (a fee is a cost, not negative revenue,
 * and `adjustment` carries no agreed sign convention).
 *
 * The two bases will NOT agree for the same window, and are not meant to.
 */
export enum RevenueBasis {
  AccrualBilled = 'ACCRUAL_BILLED',
  Cash = 'CASH'
}

/**
 * One (currency, group) revenue row. Decimal amounts are STRINGS, matching the
 * money-as-string convention the billing SDL already uses (`FxRate.rate`), so a
 * figure a CFO reads cannot pick up a binary-float artefact in transit.
 */
export type RevenueBucket = {
  __typename?: 'RevenueBucket';
  /**
   * Credit notes and write-offs (`billing_adjustments` of type `credit` /
   * `write_off`) attributed to this bucket's invoices via
   * `billing_events.invoice_id` — the only path from an adjustment back to an
   * invoice. Always "0.00" on the CASH basis: a credit never moves cash.
   */
  creditsAndWriteOffs: Scalars['String']['output'];
  /** ISO-4217 code. Part of the grouping key on every bucket. */
  currency: Scalars['String']['output'];
  /**
   * Billing party (`invoices.billable_party_id`); null unless `dimension` is
   * CUSTOMER_ORG. On the CASH basis it is ALSO null for cash that is not linked
   * to an invoice (a statement-level settlement) — that row is real cash with no
   * attributable customer, and is reported rather than dropped.
   */
  customerOrgId?: Maybe<Scalars['ID']['output']>;
  /**
   * Display name at query time. Flat scalar rather than a nested party so this
   * projection needs no extra field resolver.
   */
  customerOrgName?: Maybe<Scalars['String']['output']>;
  /**
   * ACCRUAL_BILLED: sum of invoice totals issued in this bucket.
   * CASH: sum of charges that settled in this bucket.
   */
  grossBilled: Scalars['String']['output'];
  /**
   * `grossBilled - creditsAndWriteOffs - refundsAndChargebacks`, derived from
   * those same rounded components so a consumer re-deriving it gets the identical
   * cent. Can be NEGATIVE (a period whose only activity was a refund).
   *
   * On ACCRUAL_BILLED, credits and refunds are netted into the bucket of the
   * INVOICE they belong to, not the bucket they occurred in — "net revenue billed
   * in period P" is what was invoiced in P less what was ultimately given back on
   * it. A past period's net figure can therefore still move, which is exactly why
   * `asOf` and `periodStatus` are on the response.
   */
  netBilled: Scalars['String']['output'];
  /** Last day of the period, inclusive (UTC); null unless PERIOD. */
  periodEnd?: Maybe<Scalars['AWSDate']['output']>;
  /** First day of the period (UTC); null unless `dimension` is PERIOD. */
  periodStart?: Maybe<Scalars['AWSDate']['output']>;
  /**
   * Settled `payment_transactions` of type `refund`, `chargeback` OR `return`.
   * The field name predates ACH and is kept because renaming it is a breaking
   * change: a `return` is an ACH debit the receiving bank sent back, and on a CASH
   * basis it is cash that left the bank account again, so leaving it out
   * overstated collections by the full amount of every bounce (ENG-1988).
   *
   * Sourced from the payment ledger and NOT from `billing_adjustments` of type
   * `refund`, because the in-app refund path writes BOTH rows for one refund and
   * counting both would double-subtract it. Consequence: an approved but
   * not-yet-settled refund has not reduced `netBilled` yet.
   */
  refundsAndChargebacks: Scalars['String']['output'];
  /**
   * Rows behind this bucket: invoices (ACCRUAL_BILLED) or payment transactions
   * (CASH). A bucket can have a non-zero count and zero money.
   */
  sourceCount: Scalars['Int']['output'];
};

/**
 * How revenue is grouped. FIN-M6 (ENG-1477).
 *
 * `PRODUCT_TYPE` is deliberately absent: attributing revenue to a SKU depends on
 * billing-event granularity (per-order vs per-SKU) and needs an UNATTRIBUTED
 * bucket for manual invoice lines that carry no billing event. That is FIN-M6
 * slice 2. Asking for a dimension this enum does not declare is a validation
 * error, never a silently mislabelled PERIOD report.
 */
export enum RevenueDimension {
  CustomerOrg = 'CUSTOMER_ORG',
  Period = 'PERIOD'
}

/**
 * Calendar period length for the PERIOD dimension. Periods are UTC calendar
 * periods, so a result is reproducible regardless of session time zone.
 */
export enum RevenuePeriodGrain {
  Month = 'MONTH',
  Quarter = 'QUARTER',
  Year = 'YEAR'
}

/**
 * Whether the periods in a report are still open to change. FIN-M6 (ENG-1477).
 *
 * Always `LIVE` today: the M5 period-close snapshot (ENG-1453) is FIN-M6 slice 5,
 * and until it lands there is no CLOSED state to read — reporting one would be a
 * lie. A consumer MUST label a `LIVE` report provisional; the figures can move
 * after the period ends (see `RevenueBucket.netBilled`).
 */
export enum RevenuePeriodStatus {
  Closed = 'CLOSED',
  Live = 'LIVE'
}

/**
 * Revenue aggregated out of the money ledger, per currency. FIN-M6 (ENG-1477).
 *
 * Three invariants a consumer may rely on:
 *
 *   1. **Amounts are NEVER summed across currencies.** Currency is part of the
 *      grouping key on every bucket and there is deliberately no grand-total
 *      field anywhere in this payload — the same refusal `Query.arAging` and
 *      `Query.myBillingSummary` make. A caller rendering one number must pick one
 *      currency.
 *   2. **Voided invoices are excluded on every basis** (including cash received
 *      against an invoice later voided).
 *   3. **Both gross and net are reported.** Whether the lab's headline revenue is
 *      gross or net of credits is an unmade CFO decision, so this query does not
 *      pick one.
 *
 * Computed on read from `invoices` / `billing_adjustments` /
 * `payment_transactions`. No rollup table, no materialized view: the figures
 * cannot drift from the rows an auditor would count.
 */
export type RevenueReport = {
  __typename?: 'RevenueReport';
  /**
   * Instant the aggregate was computed. Two reports with different `asOf`
   * values may legitimately disagree about the same closed period — a credit
   * raised in between reduces the earlier period's `netBilled`.
   */
  asOf: Scalars['AWSDateTime']['output'];
  basis: RevenueBasis;
  /** One row per (currency, group key). Empty is a real, expected state. */
  buckets: Array<RevenueBucket>;
  dimension: RevenueDimension;
  /** The period length the buckets use; null unless `dimension` is PERIOD. */
  grain?: Maybe<RevenuePeriodGrain>;
  /** Always LIVE until FIN-M6 slice 5. Label the output provisional. */
  periodStatus: RevenuePeriodStatus;
  rangeEnd?: Maybe<Scalars['AWSDateTime']['output']>;
  /** Echo of the requested window on the basis anchor; null when unbounded. */
  rangeStart?: Maybe<Scalars['AWSDateTime']['output']>;
};

/** Filter for revenueReport. FIN-M6 (ENG-1477). */
export type RevenueReportFilterInput = {
  /** Defaults to ACCRUAL_BILLED. */
  basis?: InputMaybe<RevenueBasis>;
  /**
   * Narrow to these ISO-4217 currency codes. Narrowing never merges: each
   * currency stays its own bucket.
   */
  currencies?: InputMaybe<Array<Scalars['String']['input']>>;
  /**
   * Narrow to these billing parties. On the CASH basis this implicitly drops
   * cash with no invoice link, since such a row has no attributable party.
   */
  customerOrgIds?: InputMaybe<Array<Scalars['ID']['input']>>;
  /**
   * Window on the basis anchor — the invoice issue date on ACCRUAL_BILLED, the
   * transaction instant on CASH. Both bounds are inclusive. Unbounded when
   * omitted, which on a large tenant means the whole book.
   */
  dateRange?: InputMaybe<DateRangeInput>;
  /** Defaults to PERIOD. */
  dimension?: InputMaybe<RevenueDimension>;
  /** Defaults to MONTH. Ignored unless `dimension` is PERIOD. */
  grain?: InputMaybe<RevenuePeriodGrain>;
};

export type ReviewTimeOffInput = {
  /** approve | deny | approve_redistribute */
  decision: Scalars['String']['input'];
  decisionNote?: InputMaybe<Scalars['String']['input']>;
  id: Scalars['ID']['input'];
};

export type RevokeMachineInput = {
  machineId: Scalars['ID']['input'];
  userId: Scalars['ID']['input'];
};

/** Input for Mutation.revokeNasDevice. */
export type RevokeNasDeviceInput = {
  /** The device to revoke (nas_agent_devices.id). Must be a UUID. */
  deviceId: Scalars['ID']['input'];
  /** Optional free-form reason, recorded on the device and in the audit log. */
  reason?: InputMaybe<Scalars['String']['input']>;
};

/** Payload for Mutation.revokeNasDevice. */
export type RevokeNasDevicePayload = {
  __typename?: 'RevokeNasDevicePayload';
  /** The device the revoke targeted (echoes the input). */
  deviceId: Scalars['ID']['output'];
  /**
   * True if THIS call flipped the device from live to revoked. False if it was
   * already revoked, or the deviceId is unknown / belongs to another org — both
   * idempotent no-ops, never errors.
   */
  revoked: Scalars['Boolean']['output'];
  /**
   * Revocation timestamp when THIS call flipped it; null on an idempotent
   * no-op (the no-op path does not re-read an existing revoked_at).
   */
  revokedAt?: Maybe<Scalars['AWSDateTime']['output']>;
};

/** ENG-623 input for revokePartyCatalogAccess. */
export type RevokePartyCatalogAccessInput = {
  /**
   * The grant row to revoke. Must belong to the party you are managing —
   * revoking an inherited grant from a descendant is refused.
   */
  grantId: Scalars['ID']['input'];
};

/** ENG-658 input for revokePartyOrderFormAssignment. */
export type RevokePartyOrderFormAssignmentInput = {
  /**
   * The assignment row to revoke. Must belong to the party you are
   * managing — revoking an inherited assignment from a descendant is
   * refused, same convention as RevokePartyCatalogAccessInput.grantId.
   */
  assignmentId: Scalars['ID']['input'];
};

export type RevokeProductEntitlementInput = {
  productId: Scalars['ID']['input'];
  reason: Scalars['String']['input'];
  tenantId: Scalars['ID']['input'];
};

/** Input for revokeRole. */
export type RevokeRoleInput = {
  reason?: InputMaybe<Scalars['String']['input']>;
  userRoleId: Scalars['ID']['input'];
};

/** Input for revokeSharedAccessLink. */
export type RevokeSharedAccessLinkInput = {
  reason?: InputMaybe<Scalars['String']['input']>;
  sharedAccessLinkId: Scalars['ID']['input'];
};

/**
 * Input for revokeStaffInvitation (ENG-2225) — the STAFF (user_type='internal')
 * counterpart to revokeInvitation, routed to the control-plane Lambda because a
 * staff invite spans Cognito, the control-plane staff_tenant_memberships row, AND
 * the tenant `users` row, and only the control plane can reconcile all three.
 * `invitationId` is the tenant-plane user_invitations.id the /admin list holds
 * (NOT staff_tenant_memberships.id). Recover-in-place: revoke DISABLES the Cognito
 * account (never deletes it) and flips the membership/invitation to revoked, so a
 * later re-invite can re-enable the SAME account keeping the same sub.
 */
export type RevokeStaffInvitationInput = {
  invitationId: Scalars['ID']['input'];
  reason?: InputMaybe<Scalars['String']['input']>;
  tenantId: Scalars['ID']['input'];
};

/**
 * Payload for revokeStaffInvitation (ENG-2225). A dedicated payload rather than
 * Invitation! so the control-plane resolver does not need the api's tenant-plane
 * row-mapper. Ids only — no reason echoed.
 */
export type RevokeStaffInvitationPayload = {
  __typename?: 'RevokeStaffInvitationPayload';
  /** Cognito sub of the DISABLED (never deleted) staff account. */
  cognitoSub: Scalars['ID']['output'];
  /**
   * Post-commit READ-PATH echo of user_invitations.invited_email (a TEXT
   * column), serialized AFTER the revoke has committed and the Cognito account is
   * disabled. Deliberately `String`, NOT `AWSEmail` — same reasoning as
   * ExternalParty.contactEmail (ENG-647): a strict scalar HERE would let a legacy
   * whitespace/malformed stored value fail AppSync serialization and surface a
   * FAILURE to the client AFTER the revoke already SUCCEEDED, the exact
   * false-failure-across-effects this ticket removes. The address was validated on
   * the WRITE path (the invite), which is where strictness belongs.
   */
  email: Scalars['String']['output'];
  /** The tenant-plane user_invitations.id that was revoked. */
  invitationId: Scalars['ID']['output'];
  /** staff_tenant_memberships.id flipped to status='revoked'. */
  membershipId: Scalars['ID']['output'];
  /** When the revoke committed. */
  revokedAt: Scalars['AWSDateTime']['output'];
};

/**
 * ENG-1422 — what a PROSPECTIVE rework of this case would cost, so the portal
 * can tell a practitioner "this remake is free" or "this remake is about $618"
 * before they commit to requesting it.
 *
 * An ESTIMATE, and deliberately labelled as one. `billingDisposition` is exact —
 * it is the value that will be frozen onto the new order (migration 0299) if the
 * rework is created right now under the tenant's current policy. `chargeEstimate`
 * is derived from the SOURCE case's frozen pricing snapshot, which is the same
 * number billing capture would bill; it can still move if the case is re-priced,
 * if the rework covers a different set of units, or if the lab applies a fee.
 *
 * Nothing here is computed from client input: the disposition comes from the
 * tenant's charge policy plus this case's warranty window, both read server-side.
 *
 * Scoped to the caller's own cases — a portal practitioner can only read this on
 * a case they own, on exactly the boundary `Query.order` enforces.
 */
export type ReworkChargeEstimate = {
  __typename?: 'ReworkChargeEstimate';
  /** What the new order's `billing_disposition` would be. Exact, not an estimate. */
  billingDisposition: BillingDisposition;
  /**
   * Decimal string in `currency`. '0' whenever `billingDisposition` is
   * NO_CHARGE. NULL — never 0, never a guess — when the amount cannot be derived:
   * the source case has no frozen pricing snapshot, a line is missing/unparseable
   * in it, or its lines span more than one currency. A null here means "we cannot
   * quote this; ask the lab", and the portal must render it that way.
   */
  chargeEstimate?: Maybe<Scalars['String']['output']>;
  /**
   * ISO-4217 code for `chargeEstimate`. Null exactly when no currency could be
   * established from the source case's snapshot.
   */
  currency?: Maybe<Scalars['String']['output']>;
  /**
   * Whether the SOURCE case was still inside its warranty window when this was
   * computed. False when the case carries no warranty date.
   */
  inWarranty: Scalars['Boolean']['output'];
  /** The mode this estimate was computed for. */
  mode: ReworkMode;
  /** Why this disposition — for the customer-facing copy. */
  reason: ChargeDispositionReason;
};

/**
 * ENG-1422 — the two rework modes a customer can ask for when they report a
 * problem with a delivered case. Maps 1:1 to `orders.order_type`
 * 'adjustment' / 'remake' (migration 0299).
 */
export enum ReworkMode {
  /** A chairside fix to the existing case — the shorter adjustment pipeline. */
  Adjustment = 'ADJUSTMENT',
  /** A full rebuild of the case. */
  Remake = 'REMAKE'
}

/** ENG-2293 — the rework a defect report on a step would trigger. */
export type ReworkPreview = {
  __typename?: 'ReworkPreview';
  /** The guard as stored, when `conditional`. Null otherwise. */
  conditionSummary?: Maybe<Scalars['String']['output']>;
  /**
   * The matching rework is guarded, so whether it fires depends on order data
   * evaluated at failure time. Render it as a possibility, not a certainty.
   */
  conditional: Scalars['Boolean']['output'];
  destinationStepId?: Maybe<Scalars['ID']['output']>;
  /** The destination step's display name, for the dialog. */
  destinationStepName?: Maybe<Scalars['String']['output']>;
  /** `rework_only` | `rework_and_downstream` | `full_restart`. */
  invalidationScope?: Maybe<Scalars['String']['output']>;
  /**
   * False when no rework rule applies: the step is simply marked failed and
   * nothing is reworked.
   */
  matches: Scalars['Boolean']['output'];
  /** `same_assignee` | `different_assignee` | `escalate_to_manager` | `custom`. */
  reassignmentPolicy?: Maybe<Scalars['String']['output']>;
};

/**
 * Role definitions per tenant; supports inheritance via parentRoleId.
 * Per docs/clearpath-architecture/03-data-model.md tier 1.
 */
export type Role = {
  __typename?: 'Role';
  /**
   * Count of active (non-expired) user_roles rows holding this role,
   * tenant-scoped. Resolved inline by the roles/role query resolvers and the
   * role-mutation payload builder (services/api/src/handlers/queries/
   * role-resolvers.ts, handlers/roles.ts loadRoleForPayload) via a correlated
   * subquery -- no dedicated field resolver / AppSync resolver wiring needed.
   */
  assignedUserCount: Scalars['Int']['output'];
  /**
   * Which users.user_type may hold this role. A role's parent must carry the
   * same audience — permissions are unioned down the parent chain.
   */
  audience: RoleAudience;
  /**
   * Neutral, stable, per-organization role key (SCREAMING_SNAKE) from
   * roles.code (db/migrations/0298). Unlike `name` it is not tenant-renameable,
   * and unlike `key` (derived from the current name) it never changes once set.
   * Authored role references (loopback/automation role scopes) store THIS value;
   * read sites match on it. Use instead of `name`/`key` for any durable
   * role reference (ENG-2789).
   */
  code: Scalars['String']['output'];
  createdAt: Scalars['AWSDateTime']['output'];
  /** Optional human description. */
  description?: Maybe<Scalars['String']['output']>;
  id: Scalars['ID']['output'];
  /** Customer portal role (dso_admin, clinic_admin, doctor, clinic_staff). */
  isPortalRole: Scalars['Boolean']['output'];
  /** System role (cannot be deleted). */
  isSystem: Scalars['Boolean']['output'];
  /** Stable key (e.g. manager, lead_tech) for code references. */
  key: Scalars['String']['output'];
  /** Display name (tenant-renameable). */
  name: Scalars['String']['output'];
  /** Owning Organization (null for tenant-wide roles). */
  organizationId?: Maybe<Scalars['ID']['output']>;
  parent?: Maybe<Role>;
  /** Parent role for inheritance. */
  parentRoleId?: Maybe<Scalars['ID']['output']>;
  /** Permission keys directly granted to this role. */
  permissionKeys: Array<Scalars['String']['output']>;
  /** JSONB add/remove overrides for diamond inheritance. */
  permissionOverrides?: Maybe<Scalars['AWSJSON']['output']>;
  tenantId: Scalars['ID']['output'];
  updatedAt?: Maybe<Scalars['AWSDateTime']['output']>;
};

/** Role assignment change kind. */
export enum RoleAssignmentChangeType {
  AutoRevoke = 'AUTO_REVOKE',
  Expire = 'EXPIRE',
  Grant = 'GRANT',
  Revoke = 'REVOKE'
}

/**
 * Which class of users.user_type may hold a role. This is a PARTITION, not a
 * cap on how many roles a user may hold — that cap is separate: a user holds
 * exactly one role (ENG-3498, trg_user_roles_single_role in
 * db/migrations/0498). STAFF<-internal, PORTAL<-customer, PARTNER<-external_partner;
 * service/system identities may hold no role at all and authorize via
 * service_accounts + api_keys. Enforced at the database by
 * trg_user_roles_audience_guard (db/migrations/0137_tier1_role_audience_gate.sql),
 * because the API is not the only writer to user_roles.
 */
export enum RoleAudience {
  Partner = 'PARTNER',
  Portal = 'PORTAL',
  Staff = 'STAFF'
}

/** Role definition change kind. */
export enum RoleDefinitionChangeType {
  Create = 'CREATE',
  Delete = 'DELETE',
  PermissionAdd = 'PERMISSION_ADD',
  PermissionRemove = 'PERMISSION_REMOVE',
  Rename = 'RENAME',
  Update = 'UPDATE'
}

export type RolePayload = {
  __typename?: 'RolePayload';
  role: AdminRoleDefinition;
};

export type RollbackNasRolloutInput = {
  id: Scalars['ID']['input'];
  toVersion: Scalars['String']['input'];
};

export type RollbackTenantConfigResult = {
  __typename?: 'RollbackTenantConfigResult';
  newDocumentId: Scalars['ID']['output'];
  reconciliationId: Scalars['ID']['output'];
  status: Scalars['String']['output'];
};

/**
 * Payload for Mutation.rotateApiKey. Rotation mints a new key, sets the old
 * key's expires_at to now() + gracePeriodDays (default 7d), keeps both valid
 * during the grace window, and emits an audit row. The old key is auto-revoked
 * when it passes expires_at.
 */
export type RotateApiKeyPayload = {
  __typename?: 'RotateApiKeyPayload';
  /** ID of the newly-minted key. */
  id: Scalars['ID']['output'];
  /** First 16 characters of the new key. */
  keyPrefix: Scalars['String']['output'];
  /** Timestamp after which the old key stops authenticating. */
  oldKeyExpiresAt: Scalars['AWSDateTime']['output'];
  /** The full plaintext of the new key. Surfaced ONCE. */
  rawKey: Scalars['String']['output'];
  /** ID of the now-rotated predecessor key. */
  replacedKeyId: Scalars['ID']['output'];
};

/** Input for Mutation.rotateOAuthClient. */
export type RotateOAuthClientInput = {
  /** The oauth_clients.id to rotate. */
  id: Scalars['ID']['input'];
  /**
   * New PUBLIC JWKS for a private_key_jwt client (REQUIRED to rotate one);
   * ignored for client_secret, which mints a fresh secret instead.
   */
  jwks?: InputMaybe<Scalars['AWSJSON']['input']>;
};

/**
 * Payload for Mutation.rotateOAuthClient. Rotation mints a new credential, sets
 * the old one's expiry to now() + grace, keeps both valid during the window, and
 * emits an audit row.
 */
export type RotateOAuthClientPayload = {
  __typename?: 'RotateOAuthClientPayload';
  /** The new public client identifier `cpc_…`. */
  clientId: Scalars['String']['output'];
  /**
   * The new plaintext secret (client_secret method) or null (private_key_jwt).
   * Surfaced ONCE.
   */
  clientSecret?: Maybe<Scalars['String']['output']>;
  /** ID of the newly-minted client credential. */
  id: Scalars['ID']['output'];
  /** Timestamp after which the old client stops authenticating. */
  oldClientExpiresAt: Scalars['AWSDateTime']['output'];
  /** ID of the now-rotated predecessor client. */
  replacedClientId: Scalars['ID']['output'];
};

/**
 * Payload for Mutation.rotateScannerStationKey. Same reveal-once contract as
 * RotateApiKeyPayload.
 */
export type RotateScannerStationKeyPayload = {
  __typename?: 'RotateScannerStationKeyPayload';
  /** ID of the newly-minted key. */
  id: Scalars['ID']['output'];
  /** First 16 characters of the new key. */
  keyPrefix: Scalars['String']['output'];
  /** Timestamp after which the old key stops authenticating. */
  oldKeyExpiresAt: Scalars['AWSDateTime']['output'];
  /** The full plaintext of the new key. Surfaced ONCE. */
  rawKey: Scalars['String']['output'];
  /** ID of the now-rotated predecessor key. */
  replacedKeyId: Scalars['ID']['output'];
};

/**
 * One routing-check finding. `productTypeId` is set when the finding is about
 * a specific SKU's intake form rather than the pipeline itself.
 */
export type RoutingCheckIssue = {
  __typename?: 'RoutingCheckIssue';
  code: Scalars['String']['output'];
  field?: Maybe<Scalars['String']['output']>;
  message: Scalars['String']['output'];
  productTypeId?: Maybe<Scalars['ID']['output']>;
  productTypeName?: Maybe<Scalars['String']['output']>;
  /**
   * Whether this finding is true of the pipeline itself or only of one
   * product’s pairing with it. It decides the CONSEQUENCE, so the editor must
   * have it: a PIPELINE error refuses publish, while a PAIRING error makes that
   * one product invalid and leaves the pipeline publishable for the others.
   * Reporting both as "publishing will be refused" promised something the gate
   * does not do.
   */
  scope: RoutingIssueScope;
  severity: RoutingIssueSeverity;
};

/**
 * Result of Query.pipelineRoutingCheck. `ok` is false when any finding is an
 * ERROR. `notVerifiedReason` explains why the check could not be conclusive —
 * most often that no product is linked yet, so there is no intake form to check
 * the branches against.
 */
export type RoutingCheckResult = {
  __typename?: 'RoutingCheckResult';
  combinationsEvaluated: Scalars['Int']['output'];
  decisionsChecked: Scalars['Int']['output'];
  issues: Array<RoutingCheckIssue>;
  notVerifiedReason?: Maybe<Scalars['String']['output']>;
  ok: Scalars['Boolean']['output'];
  productTypesChecked: Array<Scalars['ID']['output']>;
};

/**
 * ENG-1037/ENG-1038 — the two target classes a routing rule can dispatch to.
 * SITE routes are authored here (ENG-1038); their EXECUTION (advancing work to
 * the site) is decision-gated under ENG-1039 and not yet wired.
 */
export enum RoutingDestinationKind {
  Site = 'SITE',
  Vendor = 'VENDOR'
}

/** Whether a routing finding is about the pipeline or about one pairing. */
export enum RoutingIssueScope {
  Pairing = 'PAIRING',
  Pipeline = 'PIPELINE'
}

/**
 * Severity of a routing-check finding. ERROR means an order can be misrouted
 * or stranded; WARNING means the branch could not be proved either way.
 */
export enum RoutingIssueSeverity {
  Error = 'ERROR',
  Warning = 'WARNING'
}

/** SLA timing mode. */
export enum SlaCalculationMode {
  ActiveProcessing = 'ACTIVE_PROCESSING',
  BusinessHours = 'BUSINESS_HOURS',
  WallClock = 'WALL_CLOCK'
}

/**
 * Append-only SLA evaluation result.
 * Per docs/clearpath-architecture/03-data-model.md tier 5 (`sla_evaluations`).
 */
export type SlaEvaluation = {
  __typename?: 'SLAEvaluation';
  /** Elapsed minutes at evaluation time. */
  elapsedMinutes: Scalars['Int']['output'];
  evaluatedAt: Scalars['AWSDateTime']['output'];
  id: Scalars['ID']['output'];
  orderId?: Maybe<Scalars['ID']['output']>;
  organizationId: Scalars['ID']['output'];
  /**
   * Percentage of the target consumed at evaluation time
   * (0.0–100.0+; >100 means breached). Matches sla_evaluations.percentage_consumed.
   * Phase 2.5 (MOL-640).
   */
  percentageConsumed: Scalars['Float']['output'];
  /** Remaining minutes (negative if breached). */
  remainingMinutes: Scalars['Int']['output'];
  slaPolicy?: Maybe<SlaPolicy>;
  slaPolicyId: Scalars['ID']['output'];
  /** Outcome of this evaluation. */
  status: SlaEvaluationStatus;
  stepRun?: Maybe<StepRun>;
  stepRunId?: Maybe<Scalars['ID']['output']>;
  /**
   * Target duration (minutes) snapshotted at evaluation time. Matches
   * sla_evaluations.target_minutes. Phase 2.5 (MOL-640).
   */
  targetMinutes: Scalars['Int']['output'];
  tenantId: Scalars['ID']['output'];
};

/**
 * Simple offset-paginated connection for SLA evaluations — distinct from
 * the Relay cursor connections used elsewhere because the UI uses a
 * first/offset page model for forensic paging (not infinite scroll).
 */
export type SlaEvaluationOffsetConnection = {
  __typename?: 'SLAEvaluationOffsetConnection';
  items: Array<SlaEvaluation>;
  totalCount: Scalars['Int']['output'];
};

/** SLA evaluation outcome. */
export enum SlaEvaluationStatus {
  Breached = 'BREACHED',
  Cancelled = 'CANCELLED',
  Met = 'MET',
  OnTrack = 'ON_TRACK',
  Warning = 'WARNING'
}

/**
 * Target durations per step/SKU/department.
 * Per docs/clearpath-architecture/03-data-model.md tier 5 (`sla_policies`).
 */
export type SlaPolicy = {
  __typename?: 'SLAPolicy';
  /** Wall clock vs active processing only. */
  calculationMode: SlaCalculationMode;
  createdAt: Scalars['AWSDateTime']['output'];
  description?: Maybe<Scalars['String']['output']>;
  id: Scalars['ID']['output'];
  isActive: Scalars['Boolean']['output'];
  name: Scalars['String']['output'];
  /** Org unit this policy applies to (null = all). */
  orgUnitId?: Maybe<Scalars['ID']['output']>;
  organizationId: Scalars['ID']['output'];
  /** Step definition this policy applies to (null = whole order). */
  pipelineStepId?: Maybe<Scalars['ID']['output']>;
  /** SKU this policy applies to (null = all). */
  productTypeId?: Maybe<Scalars['ID']['output']>;
  publishedAt?: Maybe<Scalars['AWSDateTime']['output']>;
  publishedByUserId?: Maybe<Scalars['ID']['output']>;
  /** P2.4 (2026-04-25) — versioning lifecycle. See PipelineStatus. */
  status: PipelineStatus;
  /** Target duration (minutes). */
  targetDurationMinutes: Scalars['Int']['output'];
  tenantId: Scalars['ID']['output'];
  updatedAt?: Maybe<Scalars['AWSDateTime']['output']>;
  /** Warning threshold as fraction (e.g. 0.8 means warn at 80% used). */
  warningThresholdRatio?: Maybe<Scalars['Float']['output']>;
};

export type SlaPolicyPayload = {
  __typename?: 'SLAPolicyPayload';
  slaPolicy: SlaPolicy;
};

/**
 * Input for saveOrderDraft (Phase 3.4 — wizard autosave).
 *
 * The portal's 5-step order wizard autosaves progress to the server so doctors
 * don't lose work if their session expires or the browser crashes. Payload is
 * the full wizard state as AWSJSON — the resolver does not validate intake
 * forms; that happens on submitOrder. `draftId` lets the client resume an
 * existing draft; omit for a new one.
 */
export type SaveOrderDraftInput = {
  /** Wizard step the doctor was on — used to resume in the right place. */
  currentStepKey?: InputMaybe<Scalars['String']['input']>;
  /** Existing draft to overwrite. Omit to create a new draft. */
  draftId?: InputMaybe<Scalars['ID']['input']>;
  /** Serialized WizardDraft state (patient, products, intake, files, notes). */
  payload: Scalars['AWSJSON']['input'];
};

/** Payload for saveOrderDraft. */
export type SaveOrderDraftPayload = {
  __typename?: 'SaveOrderDraftPayload';
  /** Draft id. Stable across saves so the client can PUT back into it. */
  draftId: Scalars['ID']['output'];
  /** Server-set updatedAt for conflict detection. */
  updatedAt: Scalars['AWSDateTime']['output'];
};

/** Barcode scan purpose. */
export enum ScanPurpose {
  Handoff = 'HANDOFF',
  InventoryPick = 'INVENTORY_PICK',
  Lookup = 'LOOKUP',
  Packaging = 'PACKAGING',
  ShipOut = 'SHIP_OUT',
  StepComplete = 'STEP_COMPLETE',
  StepStart = 'STEP_START'
}

/**
 * An imported scanner order file. Bytes live in the canonical files /
 * file_locations registry (fileId); this row is the per-artifact import record.
 * Port of clearpath-v2 ScannerFilesCard's artifact shape.
 */
export type ScannerArtifact = {
  __typename?: 'ScannerArtifact';
  /** scan_model | rx_html | thumbnail | metadata_xml | screen_capture. */
  artifactType: Scalars['String']['output'];
  createdAt: Scalars['AWSDateTime']['output'];
  /**
   * Canonical files registry id — pass to requestFileDownload to fetch the
   * presigned URL. Null when the artifact byte-import has not completed.
   */
  fileId?: Maybe<Scalars['ID']['output']>;
  filename: Scalars['String']['output'];
  id: Scalars['ID']['output'];
  mimeType: Scalars['String']['output'];
  sizeBytes: Scalars['Float']['output'];
  /** Vendor-side label describing where the file came from (IDE zip, etc.). */
  sourceLabel: Scalars['String']['output'];
  status: Scalars['String']['output'];
};

export type ScannerConnection = {
  __typename?: 'ScannerConnection';
  canConnect: Scalars['Boolean']['output'];
  canDisconnect: Scalars['Boolean']['output'];
  canPause: Scalars['Boolean']['output'];
  configured: Scalars['Boolean']['output'];
  connected: Scalars['Boolean']['output'];
  environmentReady: Scalars['Boolean']['output'];
  intakePaused: Scalars['Boolean']['output'];
  integrationId?: Maybe<Scalars['ID']['output']>;
  message: Scalars['String']['output'];
  provider: Scalars['String']['output'];
  publicBaseUrl?: Maybe<Scalars['String']['output']>;
  reasonCode?: Maybe<Scalars['String']['output']>;
  state: ScannerConnectionState;
};

/** Server-owned readiness and authorization; availability never initiates intake. */
export enum ScannerConnectionState {
  AttentionRequired = 'ATTENTION_REQUIRED',
  Connected = 'CONNECTED',
  CredentialsMissing = 'CREDENTIALS_MISSING',
  Disconnected = 'DISCONNECTED',
  EnvironmentUnavailable = 'ENVIRONMENT_UNAVAILABLE',
  IntakePaused = 'INTAKE_PAUSED'
}

/**
 * Barcode scanner registry.
 * Per docs/clearpath-architecture/03-data-model.md tier 6 (`scanner_devices`).
 */
export type ScannerDevice = {
  __typename?: 'ScannerDevice';
  createdAt: Scalars['AWSDateTime']['output'];
  id: Scalars['ID']['output'];
  isActive: Scalars['Boolean']['output'];
  lastSeenAt?: Maybe<Scalars['AWSDateTime']['output']>;
  /** Physical location. */
  location?: Maybe<Scalars['String']['output']>;
  /** Device-reported model/firmware. */
  model?: Maybe<Scalars['String']['output']>;
  organizationId: Scalars['ID']['output'];
  pairedUser?: Maybe<User>;
  /** Currently paired user. */
  pairedUserId?: Maybe<Scalars['ID']['output']>;
  tenantId: Scalars['ID']['output'];
  /** Workstation identifier. */
  workstationId: Scalars['String']['output'];
};

/**
 * Per-order scanner intake surface. Bundles the raw RX payload, a parsed RX
 * summary, and the imported scan artifacts for an order created from a
 * connected scanner (iTero, 3Shape). Backed by scanner_rx_ingests +
 * scanner_order_artifacts (migration 0075). Port of the clearpath-v2 per-order
 * scanner surfaces (orders.scannerData / scannerRxDetails / scannerArtifacts)
 * into the rebuild/v2 read model.
 */
export type ScannerIntake = {
  __typename?: 'ScannerIntake';
  /**
   * Imported scan files (models, screen captures, thumbnails, RX docs).
   * Only successfully-stored artifacts are returned. Download bytes via the
   * existing requestFileDownload(fileId) mutation using each artifact's fileId.
   */
  artifacts: Array<ScannerArtifact>;
  /** Scanner-side account/company id, when the payload carried one. */
  externalAccountId?: Maybe<Scalars['String']['output']>;
  /** Scanner-side doctor id, when the payload carried one. */
  externalDoctorId?: Maybe<Scalars['String']['output']>;
  /** Ingest row id. */
  id: Scalars['ID']['output'];
  /** Vendor-side case identifier (the idempotency key). */
  integrationCaseId: Scalars['String']['output'];
  /** Registry provider id — 'itero', '3shape', …. */
  provider: Scalars['String']['output'];
  /**
   * Provider's display reference. iTero's numeric Scan ID (Order.ID), when
   * available; distinct from its Rx GUID in integrationCaseId.
   */
  providerOrderRef?: Maybe<Scalars['String']['output']>;
  /** Raw GetFullRX payload as received from the vendor (PHI). AWSJSON object. */
  rawPayload: Scalars['AWSJSON']['output'];
  /** When the scanner submission was received. */
  receivedAt: Scalars['AWSDateTime']['output'];
  /**
   * Human-meaningful RX summary parsed from the payload. Null when the
   * payload is not a recognizable RX (e.g. a bare notification).
   */
  rxDetails?: Maybe<ScannerRxDetails>;
  /** Ingest status: received | processed | failed | duplicate. */
  status: Scalars['String']['output'];
};

export type ScannerIntakeCase = {
  __typename?: 'ScannerIntakeCase';
  files: Array<ScannerIntakeFile>;
  /** Whole-lab count for this exact reusable iTero/Medit identity, across every case state. */
  identityCaseCount?: Maybe<Scalars['Int']['output']>;
  item: ScannerIntakeInboxItem;
};

export type ScannerIntakeCounts = {
  __typename?: 'ScannerIntakeCounts';
  archived: Scalars['Int']['output'];
  failed: Scalars['Int']['output'];
  historical: Scalars['Int']['output'];
  linked: Scalars['Int']['output'];
  needsMapping: Scalars['Int']['output'];
  needsOrder: Scalars['Int']['output'];
};

/** Resolved tenant rollout shared by the lab and customer scanner surfaces. */
export type ScannerIntakeFeature = {
  __typename?: 'ScannerIntakeFeature';
  enabled: Scalars['Boolean']['output'];
};

export type ScannerIntakeFile = {
  __typename?: 'ScannerIntakeFile';
  artifactType: Scalars['String']['output'];
  fileId?: Maybe<Scalars['ID']['output']>;
  filename: Scalars['String']['output'];
  id: Scalars['ID']['output'];
  mimeType?: Maybe<Scalars['String']['output']>;
  /** Known provider-listed or stored byte count. Null means unknown; zero is a real count. */
  sizeBytes?: Maybe<Scalars['Float']['output']>;
  source: Scalars['String']['output'];
  status: Scalars['String']['output'];
  /** Durably stored bytes only. Never synthesized from the provider listing. */
  storedSizeBytes?: Maybe<Scalars['Float']['output']>;
};

export type ScannerIntakeFilter = {
  fromDate?: InputMaybe<Scalars['AWSDate']['input']>;
  integrationId?: InputMaybe<Scalars['ID']['input']>;
  query?: InputMaybe<Scalars['String']['input']>;
  toDate?: InputMaybe<Scalars['AWSDate']['input']>;
  workflow?: InputMaybe<ScannerIntakeWorkflow>;
};

export type ScannerIntakeIdentity = {
  __typename?: 'ScannerIntakeIdentity';
  caseCount: Scalars['Int']['output'];
  id: Scalars['ID']['output'];
  representative: ScannerIntakeInboxItem;
  state: ScannerIntakeIdentityState;
};

export type ScannerIntakeIdentityConnection = {
  __typename?: 'ScannerIntakeIdentityConnection';
  items: Array<ScannerIntakeIdentity>;
  pageInfo: PageInfo;
  totalCount: Scalars['Int']['output'];
};

export enum ScannerIntakeIdentityState {
  All = 'ALL',
  ClinicOnly = 'CLINIC_ONLY',
  Complete = 'COMPLETE',
  Unmapped = 'UNMAPPED'
}

/** A page of staged scanner submissions for the scanner intake inbox. */
export type ScannerIntakeInboxConnection = {
  __typename?: 'ScannerIntakeInboxConnection';
  counts: ScannerIntakeCounts;
  items: Array<ScannerIntakeInboxItem>;
  pageInfo: PageInfo;
  /** Total staged submissions matching the filter, across all pages. */
  totalCount: Scalars['Int']['output'];
};

/**
 * One staged scanner submission in the intake inbox — the lightweight,
 * list-oriented view of a scanner_rx_ingests row (migration 0075). Carries just
 * enough of the derived RX summary (subject reference, restoration units) for a
 * lab technician to triage a scan before it becomes an order. The full raw
 * payload + imported artifacts remain on the order-scoped ScannerIntake surface
 * (Order.scannerIntake) once a scan is mapped to an order.
 */
export type ScannerIntakeInboxItem = {
  __typename?: 'ScannerIntakeInboxItem';
  /** Optional local archive reference; never a provider status or order link. */
  archiveReference?: Maybe<Scalars['String']['output']>;
  archivedAt?: Maybe<Scalars['AWSDateTime']['output']>;
  /** Requested due date received from the provider as YYYY-MM-DD. */
  dueDate?: Maybe<Scalars['String']['output']>;
  /** Scanner-side account/company id. For Medit this is seller.uuid (the lab). */
  externalAccountId?: Maybe<Scalars['String']['output']>;
  /** Scanner-side account/company display name (Medit seller/lab), when present. */
  externalAccountName?: Maybe<Scalars['String']['output']>;
  /** Scanner-side customer address, used only as technician context. */
  externalLocationAddress?: Maybe<Scalars['String']['output']>;
  /** Scanner-side customer phone values, used only as technician context. */
  externalLocationContactPhone?: Maybe<Scalars['String']['output']>;
  /** Scanner-side customer location id (iTero ShipToId or Medit buyer.uuid). */
  externalLocationId?: Maybe<Scalars['String']['output']>;
  /** Scanner-side customer location label, used only as a matching hint. */
  externalLocationName?: Maybe<Scalars['String']['output']>;
  /** Scanner-side practitioner id, when the payload carried one. */
  externalPractitionerId?: Maybe<Scalars['String']['output']>;
  /** Ingest row id. */
  id: Scalars['ID']['output'];
  importClassification: Scalars['String']['output'];
  importOverride: Scalars['Boolean']['output'];
  /** Vendor-side case identifier (the idempotency key). */
  integrationCaseId: Scalars['String']['output'];
  /** Exact ClearPath integration row that fetched this case. */
  integrationId?: Maybe<Scalars['ID']['output']>;
  mappedCustomerName?: Maybe<Scalars['String']['output']>;
  mappedCustomerPartyId?: Maybe<Scalars['ID']['output']>;
  mappedPractitionerName?: Maybe<Scalars['String']['output']>;
  mappedPractitionerPartyId?: Maybe<Scalars['ID']['output']>;
  /** Active exact identity mapping, including a clinic-only intermediate mapping. */
  mappingId?: Maybe<Scalars['ID']['output']>;
  /** Provider-supplied case instructions/notes, when present. */
  notes?: Maybe<Scalars['String']['output']>;
  /**
   * Order this scan has been mapped to, or null while it is still just staged
   * in the inbox (the normal case while auto-order-creation is off).
   */
  orderId?: Maybe<Scalars['ID']['output']>;
  /** Original provider case date in the saved source calendar; never local ingestion time. */
  originalCaseDate?: Maybe<Scalars['AWSDate']['output']>;
  originalCaseDateMissing: Scalars['Boolean']['output'];
  /**
   * Referring practitioner/contact hint parsed from the payload. Medit does
   * not document buyer.contact.userName as a stable practitioner identity.
   */
  practitionerRef?: Maybe<Scalars['String']['output']>;
  /** Case priority parsed from the payload (e.g. standard / rush), when present. */
  priority?: Maybe<Scalars['String']['output']>;
  /** Provider-supplied procedure label/code, when present. */
  procedure?: Maybe<Scalars['String']['output']>;
  /** Registry provider id — 'itero', 'medit', '3shape'. */
  provider: Scalars['String']['output'];
  /** Human-readable case code supplied inside the provider RX, when present. */
  providerCaseCode?: Maybe<Scalars['String']['output']>;
  /** Provider-side order number, distinct from the provider case id. */
  providerOrderRef?: Maybe<Scalars['String']['output']>;
  /** Provider-side order status, when supplied. */
  providerOrderStatus?: Maybe<Scalars['String']['output']>;
  /** Provider-supplied case tags, when present. */
  providerTags: Array<Scalars['String']['output']>;
  /** When the scanner submission was received. */
  receivedAt: Scalars['AWSDateTime']['output'];
  /** ISO date (YYYY-MM-DD) the case was scanned/submitted, when present. */
  scanDate?: Maybe<Scalars['String']['output']>;
  /** Ingest status: received | processed | failed | duplicate. */
  status: Scalars['String']['output'];
  /**
   * ISO date of birth received as an explicit provider field (PHI), when
   * present. Never log or expose this field on a portal surface.
   */
  subjectBirthDate?: Maybe<Scalars['String']['output']>;
  /**
   * Subject first name received as an explicit provider field (PHI). Never
   * derived by splitting subjectRef.
   */
  subjectFirstName?: Maybe<Scalars['String']['output']>;
  /**
   * Subject last name received as an explicit provider field (PHI). Never
   * derived by splitting subjectRef.
   */
  subjectLastName?: Maybe<Scalars['String']['output']>;
  /**
   * Subject reference parsed from the payload — a case subject name or code
   * (PHI). Null when the payload carried none.
   */
  subjectRef?: Maybe<Scalars['String']['output']>;
  /** Count of restoration units parsed from the payload. */
  unitCount: Scalars['Int']['output'];
  /**
   * One entry per restoration unit parsed from the payload (FDI/Universal
   * position, vendor material code, shade).
   */
  units: Array<ScannerRxUnit>;
  workflow: ScannerIntakeWorkflow;
};

export type ScannerIntakeSource = {
  __typename?: 'ScannerIntakeSource';
  fromDate?: Maybe<Scalars['AWSDate']['output']>;
  integrationId: Scalars['ID']['output'];
  lastSyncedAt?: Maybe<Scalars['AWSDateTime']['output']>;
  name: Scalars['String']['output'];
  /** False preserves an existing source's all-history behavior. */
  policyConfigured: Scalars['Boolean']['output'];
  progress: ScannerIntakeSourceProgress;
  provider: Scalars['String']['output'];
  status: Scalars['String']['output'];
  timeZone: Scalars['String']['output'];
};

export type ScannerIntakeSourceProgress = {
  __typename?: 'ScannerIntakeSourceProgress';
  archived: Scalars['Int']['output'];
  linked: Scalars['Int']['output'];
  needsOrder: Scalars['Int']['output'];
  received: Scalars['Int']['output'];
};

/**
 * Workflow is separate from ingestion status: processed cases can still need an order.
 * NEEDS_MAPPING and FAILED overlap NEEDS_ORDER; historical cases require explicit inclusion.
 */
export enum ScannerIntakeWorkflow {
  All = 'ALL',
  Archived = 'ARCHIVED',
  Failed = 'FAILED',
  Historical = 'HISTORICAL',
  Linked = 'LINKED',
  NeedsMapping = 'NEEDS_MAPPING',
  NeedsOrder = 'NEEDS_ORDER'
}

/**
 * Parsed RX summary for a scanner order — the human-meaningful fields pulled
 * from the GetFullRX payload for display. Adapted from clearpath-v2's
 * iteroExportParser / ScannerRxDetailsCard (which parsed the export XML); the
 * rebuild derives the same shape from the stored JSON payload.
 */
export type ScannerRxDetails = {
  __typename?: 'ScannerRxDetails';
  doctor?: Maybe<Scalars['String']['output']>;
  doctorEmail?: Maybe<Scalars['String']['output']>;
  doctorId?: Maybe<Scalars['String']['output']>;
  notes?: Maybe<Scalars['String']['output']>;
  patient?: Maybe<Scalars['String']['output']>;
  priority?: Maybe<Scalars['String']['output']>;
  procedure?: Maybe<Scalars['String']['output']>;
  /** ISO date (YYYY-MM-DD) the case was scanned/submitted, when present. */
  scanDate?: Maybe<Scalars['String']['output']>;
  /** One entry per restoration unit parsed from the payload. */
  units: Array<ScannerRxUnit>;
};

/** A single restoration unit parsed from a scanner RX payload. */
export type ScannerRxUnit = {
  __typename?: 'ScannerRxUnit';
  /** Scanner-side material/product code — NOT a ClearPath product_type id. */
  materialCode?: Maybe<Scalars['String']['output']>;
  /**
   * Human material description resolved from the code via the iTero RX-definition
   * dictionary (iTero only). Null when unmapped or for providers without a
   * dictionary; clients fall back to materialCode.
   */
  materialDescription?: Maybe<Scalars['String']['output']>;
  shade?: Maybe<Scalars['String']['output']>;
  toothNumber: Scalars['String']['output'];
};

/**
 * An enrolled scanner station: a scanner_devices row paired to a service
 * account, plus the current key's prefix/status. NEVER carries the key secret.
 */
export type ScannerStation = {
  __typename?: 'ScannerStation';
  createdAt: Scalars['AWSDateTime']['output'];
  /** scanner_devices.id. */
  id: Scalars['ID']['output'];
  /** True when status = 'active'. */
  isActive: Scalars['Boolean']['output'];
  /** When the current key expires (set during rotation grace), if any. */
  keyExpiresAt?: Maybe<Scalars['AWSDateTime']['output']>;
  /** When the current key was last used to authenticate. */
  keyLastUsedAt?: Maybe<Scalars['AWSDateTime']['output']>;
  /** First 16 chars of the current key — safe to display. Null if no key. */
  keyPrefix?: Maybe<Scalars['String']['output']>;
  /** When the current key was revoked, if it has been. */
  keyRevokedAt?: Maybe<Scalars['AWSDateTime']['output']>;
  /** Last time the device itself checked in. */
  lastSeenAt?: Maybe<Scalars['AWSDateTime']['output']>;
  /** Physical location (org unit display name, or the free-text note). */
  location?: Maybe<Scalars['String']['output']>;
  /** Station name (== scanner_devices.workstation_id). */
  name: Scalars['String']['output'];
  organizationId: Scalars['ID']['output'];
  /** The paired service account (service_accounts.id). */
  serviceAccountId: Scalars['ID']['output'];
  /** Raw device status: active | inactive | disabled. */
  status: Scalars['String']['output'];
  tenantId: Scalars['ID']['output'];
};

/** Permission scope kind — used by @requiresScope to declare which authorization tier the field operates at. */
export enum ScopeKind {
  /** Field operates at the platform/control-plane level (Molaris staff only). */
  Global = 'GLOBAL',
  /** Field operates against a single Org within the tenant. */
  Org = 'ORG',
  /** Field operates across the whole tenant (multi-org admin). */
  Tenant = 'TENANT'
}

/** Round 5 — filter input for `Query.scopedKanban`. */
export type ScopedKanbanFilter = {
  /** Free-text search across order barcode + customer name. */
  search?: InputMaybe<Scalars['String']['input']>;
  /**
   * Restrict to a subset of status keys. When omitted, all states
   * surfaced from tenant_settings.
   */
  statusKeys?: InputMaybe<Array<Scalars['String']['input']>>;
  /**
   * Restrict to one workflow (pipeline). When omitted, returns the
   * aggregated `All workflows` view.
   */
  workflowId?: InputMaybe<Scalars['ID']['input']>;
};

/**
 * ENG-2210 — the scope's chrome, without any cards.
 *
 * Split from ScopedKanbanResult so the board can learn its COLUMNS before it
 * pages any of them: a connection cannot carry statusStates, and the board needs
 * the tenant's configured state set to know how many columns exist and in what
 * order. Cheap by construction — a scope label plus tenant_settings, no order
 * join, no card rows.
 */
export type ScopedKanbanMeta = {
  __typename?: 'ScopedKanbanMeta';
  scopeId: Scalars['ID']['output'];
  scopeKind: DashboardScopeKind;
  scopeLabel: Scalars['String']['output'];
  statusStates: Array<StatusState>;
};

/** Round 5 — full payload for the scoped kanban view. */
export type ScopedKanbanResult = {
  __typename?: 'ScopedKanbanResult';
  cards: Array<KanbanCard>;
  scopeId: Scalars['ID']['output'];
  scopeKind: DashboardScopeKind;
  scopeLabel: Scalars['String']['output'];
  statusStates: Array<StatusState>;
};

/**
 * P1 #27 — projection of one row from `secrets_references` for the
 * admin-shell rotation-reminder banner. `daysUntilDue` is server-computed
 * (may be negative when past due). Per
 * docs/clearpath-architecture/13-audit-secrets-encryption.md §86.
 */
export type SecretRotationStatus = {
  __typename?: 'SecretRotationStatus';
  /** Days until rotation is due (negative when past-due). */
  daysUntilDue?: Maybe<Scalars['Int']['output']>;
  id: Scalars['ID']['output'];
  lastRotatedAt?: Maybe<Scalars['AWSDateTime']['output']>;
  nextRotationDueAt?: Maybe<Scalars['AWSDateTime']['output']>;
  rotationStrategy: Scalars['String']['output'];
  secretArnOrParamPath: Scalars['String']['output'];
  secretPurpose: Scalars['String']['output'];
};

export type SelectIteroAccountInput = {
  accountId: Scalars['ID']['input'];
  integrationId: Scalars['ID']['input'];
  sessionId: Scalars['ID']['input'];
};

/**
 * Input for sendFederationMessage. Exactly one of relatedStepRunId /
 * relatedOrderId must be set; workItemId is the UI-level handle that the
 * resolver maps to whichever scope applies. Phase 9 (MOL-608).
 */
export type SendFederationMessageInput = {
  body: Scalars['String']['input'];
  /**
   * Optional — when omitted the resolver derives the federation_link_id from
   * workItemId (the step_run/order's vendor_federation_link_id). Phase 1.1 (MOL-603).
   */
  federationLinkId?: InputMaybe<Scalars['ID']['input']>;
  messageType?: InputMaybe<FederationMessageType>;
  relatedOrderId?: InputMaybe<Scalars['ID']['input']>;
  relatedStepRunId?: InputMaybe<Scalars['ID']['input']>;
  workItemId: Scalars['ID']['input'];
};

/**
 * Input for sendStepRunBack — Round 2b "send back to previous step" dialog.
 *
 * Functionally a thinner sibling of `loopbackStepRun` for the OrderDetailPane
 * UI: the operator picks an upstream step + types a reason, with no need to
 * choose an InvalidationScope or a FailureReasonCode. The resolver supersedes
 * the source step_run (status → `superseded`) and inserts a new attempt on
 * the target step (status → `ready`). Industry-neutral — works on any
 * pipeline. Audit: `step_run.sent_back` + `step_run.created_via_loopback`.
 */
export type SendStepRunBackInput = {
  /** Whether to notify the previous step's assignee. Defaults to true. */
  notifyAssignee?: InputMaybe<Scalars['Boolean']['input']>;
  /** Free-form reason; resolver enforces min length 10. */
  reason: Scalars['String']['input'];
  /** The currently in_progress / completed step_run to send back. */
  stepRunId: Scalars['ID']['input'];
  /** The upstream pipeline step the work should re-enter. */
  targetStepId: Scalars['ID']['input'];
  /**
   * How many of the line's identical units this applies to. Omitted, or the
   * line's whole quantity, acts on the whole line. Fewer splits those units off
   * onto a new line with its own pipeline run (it keeps working from here, the
   * rest of the line carries on) — the split is permanent.
   */
  unitCount?: InputMaybe<Scalars['Int']['input']>;
  /**
   * Which of the line's units this applies to, as 0-based positions in
   * `OrderProduct.unitIntakeFormResponses` (each unit's own answers). Takes
   * precedence over `unitCount`; naming every unit acts on the whole line.
   */
  unitIndexes?: InputMaybe<Array<Scalars['Int']['input']>>;
};

/**
 * Machine identity for API integrations.
 * Per docs/clearpath-architecture/03-data-model.md tier 1 (`service_accounts`).
 */
export type ServiceAccount = {
  __typename?: 'ServiceAccount';
  createdAt: Scalars['AWSDateTime']['output'];
  createdByUserId?: Maybe<Scalars['ID']['output']>;
  description?: Maybe<Scalars['String']['output']>;
  id: Scalars['ID']['output'];
  isActive: Scalars['Boolean']['output'];
  lastUsedAt?: Maybe<Scalars['AWSDateTime']['output']>;
  name: Scalars['String']['output'];
  organizationId?: Maybe<Scalars['ID']['output']>;
  tenantId: Scalars['ID']['output'];
};

export type SessionPayload = {
  __typename?: 'SessionPayload';
  session: UserSession;
};

/**
 * Result of revokeAllMySessions — reports how many rows were appended to
 * session_revocations so the UI can show "Signed out of N other sessions".
 */
export type SessionRevocationPayload = {
  __typename?: 'SessionRevocationPayload';
  /** Session id of the still-active session when exceptCurrent=true. */
  keptSessionId?: Maybe<Scalars['ID']['output']>;
  revokedCount: Scalars['Int']['output'];
  /** Epoch-seconds of the Redis user-watermark bump. */
  tokensInvalidatedAfter: Scalars['Int']['output'];
};

/**
 * Flag (or unflag) one of the caller's OWN dashboards as a reusable TEMPLATE
 * (ENG-2112 / T3). Owner-only: only the artifact's owner may mark it a template.
 * Sets/clears an additive `template` flag on the dashboard `definition` — no schema
 * or migration change. NotFound if the id is not the caller's own dashboard in
 * their org.
 */
export type SetAnalyticsDashboardTemplateInput = {
  id: Scalars['ID']['input'];
  isTemplate: Scalars['Boolean']['input'];
};

/**
 * Upsert the caller's own Overview personalization. `prefs` fully replaces the
 * stored blob (the client sends the complete desired state).
 */
export type SetAnalyticsOverviewPreferencesInput = {
  prefs: Scalars['AWSJSON']['input'];
};

/**
 * Set (create or replace) the org's target for a measure. UPSERT on
 * (organizationId, measureKey) — one target per measure per org (v1). `measureKey`
 * is validated against the semantics registry; an unknown key is a ValidationError
 * and nothing is written.
 */
export type SetAnalyticsTargetInput = {
  comparisonLabel?: InputMaybe<Scalars['String']['input']>;
  measureKey: Scalars['String']['input'];
  targetValue: Scalars['Float']['input'];
};

/**
 * Input for setOrderBillingParty (ENG-2571 §E) — re-point who pays for an
 * order after submit. `billingPartyId` must be in the caller's organization and
 * must be the order's customer party or one of its ancestors.
 */
export type SetOrderBillingPartyInput = {
  billingPartyId: Scalars['ID']['input'];
  orderId: Scalars['ID']['input'];
};

/** Input for setOrderStatus (manual override). */
export type SetOrderStatusInput = {
  orderId: Scalars['ID']['input'];
  reason?: InputMaybe<Scalars['String']['input']>;
  status: Scalars['String']['input'];
};

export type SetOrderTrackingInput = {
  carrier: ShippingCarrier;
  /**
   * ENG-3881 S2.6c — required when the number is another order's ClearPath
   * label: why this order shipped in that parcel (audited).
   */
  labelMatchReason?: InputMaybe<Scalars['String']['input']>;
  orderId: Scalars['ID']['input'];
  /**
   * ENG-3881 S2.6c — add or replace the customer-pays shipping charge.
   * Required when the order has a replaceable charge and the number changes.
   */
  shippingCharge?: InputMaybe<ManualShippingChargeInput>;
  trackingNumber: Scalars['String']['input'];
};

/**
 * Input for setPipelineDefaultAssignmentStrategy (ENG-894). Writes to the
 * pipeline's open DRAFT version, so the change takes effect on publish.
 */
export type SetPipelineDefaultAssignmentStrategyInput = {
  defaultAssignmentStrategy: PipelineAssignmentStrategy;
  id: Scalars['ID']['input'];
};

/**
 * Input for setPipelineStatus (2026-07-16). `status` is the target lifecycle
 * state; the server validates the transition is legal from the current state.
 */
export type SetPipelineStatusInput = {
  id: Scalars['ID']['input'];
  status: PipelineStatus;
};

/**
 * Rename a projection's tenant-facing display label. When the target row is
 * exposed (picker-visible), the new label must be non-blank AND unique
 * (case-insensitively) among the org's other exposed projections; relabeling a
 * non-exposed candidate is unconstrained. NotFound if the id is not a projection in
 * the caller's org.
 */
export type SetProjectionLabelInput = {
  label: Scalars['String']['input'];
  projectionId: Scalars['ID']['input'];
};

/**
 * Opt a projection DOWN to non-QI (`quasiIdentifier: false`, releasing its
 * group-by from k-anon small-cell suppression) or RESTORE it to QI (`true`).
 * Symmetric + idempotent. NotFound if the id is not a projection in the caller's org.
 */
export type SetProjectionQuasiIdentifierInput = {
  projectionId: Scalars['ID']['input'];
  quasiIdentifier: Scalars['Boolean']['input'];
};

/**
 * ENG-88 Level A white-label — set (or clear, by passing null) a tenant's
 * transactional-email branding. Any field omitted from the input is left
 * unchanged; any field explicitly set to null clears it back to the
 * Molaris/ClearPath default.
 */
export type SetTenantBrandingInput = {
  displayName?: InputMaybe<Scalars['String']['input']>;
  fromName?: InputMaybe<Scalars['String']['input']>;
  logoUrl?: InputMaybe<Scalars['String']['input']>;
  primaryColor?: InputMaybe<Scalars['String']['input']>;
  productName?: InputMaybe<Scalars['String']['input']>;
  tenantId: Scalars['ID']['input'];
};

export type SetTenantBrandingPayload = {
  __typename?: 'SetTenantBrandingPayload';
  /**
   * Non-null only when branding saved successfully but the best-effort sync
   * of the tenant-staff Cognito pool's EmailConfiguration.From failed.
   */
  emailConfigSyncWarning?: Maybe<Scalars['String']['output']>;
  tenant: Tenant;
};

/**
 * Payload for setVendorOverride. The step_run is returned with the new
 * vendor_override_id so clients can update their cache without a refetch.
 */
export type SetVendorOverridePayload = {
  __typename?: 'SetVendorOverridePayload';
  stepRun: StepRun;
};

/**
 * Share one of the caller's own dashboards with a principal. Re-sharing the same
 * principal updates the existing grant's permission.
 */
export type ShareAnalyticsDashboardInput = {
  dashboardId: Scalars['ID']['input'];
  /** `view` (default) or `edit`. */
  permission?: InputMaybe<Scalars['String']['input']>;
  sharedWithUserId: Scalars['ID']['input'];
};

/**
 * Share link row echoed by Phase 6 createShareLink — never includes the
 * token after the initial response.
 */
export type ShareLink = {
  __typename?: 'ShareLink';
  createdAt: Scalars['AWSDateTime']['output'];
  createdByUserId: Scalars['ID']['output'];
  expiresAt: Scalars['AWSDateTime']['output'];
  id: Scalars['ID']['output'];
  label?: Maybe<Scalars['String']['output']>;
  maxUses?: Maybe<Scalars['Int']['output']>;
  organizationId: Scalars['ID']['output'];
  permissions: Array<ShareLinkAction>;
  revokedAt?: Maybe<Scalars['AWSDateTime']['output']>;
  scope: ShareLinkScope;
  useCount: Scalars['Int']['output'];
};

/** Phase 6 share-link scoped action — strict whitelist. */
export enum ShareLinkAction {
  AddComment = 'ADD_COMMENT',
  MarkComplete = 'MARK_COMPLETE',
  UploadFiles = 'UPLOAD_FILES',
  ViewFiles = 'VIEW_FILES'
}

/** Payload for Phase 6 createShareLink — JWT returned ONCE. */
export type ShareLinkPayload = {
  __typename?: 'ShareLinkPayload';
  sharedAccessLink: ShareLink;
  /** The bearer JWT. SHOWN ONCE — never returned again. */
  token: Scalars['String']['output'];
  /** Full URL wrapping the token. */
  url: Scalars['AWSURL']['output'];
};

/** Phase 6 share-link resource type. */
export enum ShareLinkResourceType {
  Order = 'ORDER',
  StepRun = 'STEP_RUN'
}

/** Scope slice echoed in the Phase 6 share link payload. */
export type ShareLinkScope = {
  __typename?: 'ShareLinkScope';
  resourceId: Scalars['ID']['output'];
  resourceType: ShareLinkResourceType;
};

/**
 * Scoped tokens for vendor mode B share links.
 * Per docs/clearpath-architecture/03-data-model.md tier 6 (`shared_access_links`).
 */
export type SharedAccessLink = {
  __typename?: 'SharedAccessLink';
  createdAt: Scalars['AWSDateTime']['output'];
  createdBy?: Maybe<User>;
  /** User who created the link. */
  createdByUserId: Scalars['ID']['output'];
  expiresAt?: Maybe<Scalars['AWSDateTime']['output']>;
  id: Scalars['ID']['output'];
  /** Display name for the link. */
  label: Scalars['String']['output'];
  maxUses?: Maybe<Scalars['Int']['output']>;
  organizationId: Scalars['ID']['output'];
  /** Permissions JSONB (view_files, upload_files, mark_complete, add_comment). */
  permissions: Scalars['AWSJSON']['output'];
  revokedAt?: Maybe<Scalars['AWSDateTime']['output']>;
  revokedByUserId?: Maybe<Scalars['ID']['output']>;
  /** Scope JSONB (orderIds, stepDefinitionIds). */
  scope: Scalars['AWSJSON']['output'];
  tenantId: Scalars['ID']['output'];
  useCount: Scalars['Int']['output'];
};

export type SharedAccessLinkPayload = {
  __typename?: 'SharedAccessLinkPayload';
  sharedAccessLink: SharedAccessLink;
};

/**
 * Session introspection for Mode-B share-link tokens. Returned by the
 * `sharedAccessScope` query, which the resolver narrows to the inbound
 * JWT's permissions + scope. TODO: needs resolver impl (MOL-574).
 */
export type SharedAccessScope = {
  __typename?: 'SharedAccessScope';
  expiresAt?: Maybe<Scalars['AWSDateTime']['output']>;
  /** Share-link id. */
  linkId: Scalars['ID']['output'];
  /** Orders the link grants access to (already scope-narrowed). */
  orders: Array<Order>;
  /**
   * Permission keys granted by the token (view_files, upload_files,
   * mark_complete, add_comment).
   */
  permissions: Array<Scalars['String']['output']>;
  /** Decoded scope slice from the JWT. */
  scope: SharedAccessScopeSlice;
  tenantId: Scalars['ID']['output'];
};

/**
 * Scope slice of a SharedAccessLink — orderIds / stepDefinitionIds /
 * stepRunIds the JWT grants access to. Mirrors the `scope` JSONB field on
 * SharedAccessLink as a strongly-typed projection for the Mode-B share-link
 * UI (apps/shared-access). TODO: needs resolver impl.
 */
export type SharedAccessScopeSlice = {
  __typename?: 'SharedAccessScopeSlice';
  orderIds: Array<Scalars['ID']['output']>;
  stepDefinitionIds: Array<Scalars['ID']['output']>;
  stepRunIds: Array<Scalars['ID']['output']>;
};

export type Shift = {
  __typename?: 'Shift';
  active: Scalars['Boolean']['output'];
  days: Array<Scalars['String']['output']>;
  defaultKey?: Maybe<Scalars['String']['output']>;
  endMinutes: Scalars['Int']['output'];
  endTime: Scalars['String']['output'];
  /** True when endMinutes < startMinutes (overnight shift). */
  endsNextDay: Scalars['Boolean']['output'];
  id: Scalars['ID']['output'];
  name: Scalars['String']['output'];
  organizationId: Scalars['ID']['output'];
  staff: Array<ShiftStaffMember>;
  startMinutes: Scalars['Int']['output'];
  /** "HH:MM" 24-hour start/end derived from the minute offsets. */
  startTime: Scalars['String']['output'];
};

export type ShiftAssignmentResult = {
  __typename?: 'ShiftAssignmentResult';
  assigned: Scalars['Int']['output'];
  conflicts: Array<ShiftConflict>;
};

export type ShiftConflict = {
  __typename?: 'ShiftConflict';
  dayOfWeek: Scalars['String']['output'];
  existingShiftName: Scalars['String']['output'];
  fullName?: Maybe<Scalars['String']['output']>;
  userId: Scalars['ID']['output'];
};

export type ShiftDeletedPayload = {
  __typename?: 'ShiftDeletedPayload';
  deletedId: Scalars['ID']['output'];
};

export type ShiftManagementPayload = {
  __typename?: 'ShiftManagementPayload';
  canManageLabWide: Scalars['Boolean']['output'];
  shifts: Array<Shift>;
  timeZone: Scalars['String']['output'];
};

export type ShiftPayload = {
  __typename?: 'ShiftPayload';
  shift: Shift;
};

export type ShiftStaffMember = {
  __typename?: 'ShiftStaffMember';
  email?: Maybe<Scalars['String']['output']>;
  fullName?: Maybe<Scalars['String']['output']>;
  userId: Scalars['ID']['output'];
};

/**
 * The party whose addresses are all restricted to billing, so none can be
 * used to ship or tax.
 */
export type ShipToAddressRestriction = {
  __typename?: 'ShipToAddressRestriction';
  /** True when the restricted addresses are an ancestor's. */
  inherited: Scalars['Boolean']['output'];
  partyId: Scalars['ID']['output'];
  partyName: Scalars['String']['output'];
};

export type ShipTogetherCandidate = {
  __typename?: 'ShipTogetherCandidate';
  activeTrackingNumber?: Maybe<Scalars['String']['output']>;
  barcode?: Maybe<Scalars['String']['output']>;
  /** The ship-to's shipping address, for display; null when none is on file. */
  destination?: Maybe<Scalars['String']['output']>;
  /** Normalized destination; equal keys are the same address. */
  destinationKey?: Maybe<Scalars['String']['output']>;
  /** Why it cannot go on one label with the order asked about; null when it can. */
  ineligibleReason?: Maybe<ShipTogetherIneligibleReason>;
  orderId: Scalars['ID']['output'];
  /** Same destination as the order asked about. */
  sameDestination: Scalars['Boolean']['output'];
};

/** The same customer's active orders, the one asked about first (at most 100). */
export type ShipTogetherCandidates = {
  __typename?: 'ShipTogetherCandidates';
  orders: Array<ShipTogetherCandidate>;
};

export enum ShipTogetherIneligibleReason {
  DifferentBilling = 'DIFFERENT_BILLING',
  DifferentTerms = 'DIFFERENT_TERMS',
  HasActiveLabel = 'HAS_ACTIVE_LABEL'
}

/** Org-scoped shipping label row (ENG-211 `shipments` table). */
export type Shipment = {
  __typename?: 'Shipment';
  amountUsd?: Maybe<Scalars['Float']['output']>;
  carrier: ShippingCarrier;
  /** Every order the label covers, the primary (orderId) first. Set by Query.shipments. */
  coveredOrders?: Maybe<Array<ShipmentOrderRef>>;
  createdAt: Scalars['AWSDateTime']['output'];
  estimatedDeliveryDate?: Maybe<Scalars['AWSDate']['output']>;
  id: Scalars['ID']['output'];
  /** Order file for the carrier label PDF/GIF, when persisted. */
  labelFileId?: Maybe<Scalars['ID']['output']>;
  /** Options the label was bought with; null for labels bought before they were recorded. */
  labelOptions?: Maybe<ShipmentLabelOptions>;
  /** Order barcode for display / deep-link context. Null if the order has none. */
  orderBarcode?: Maybe<Scalars['String']['output']>;
  orderId: Scalars['ID']['output'];
  /** Parent order lifecycle (active / shipped / delivered / …). */
  orderLifecycleStatus: OrderLifecycleStatus;
  referenceNumber?: Maybe<Scalars['String']['output']>;
  serviceLevel?: Maybe<Scalars['String']['output']>;
  /** ENG-3286 — the outside account this label shipped on; null = the lab's own. */
  shippingAccountId?: Maybe<Scalars['ID']['output']>;
  /** ENG-3286 — last 4 digits of the outside account; null for the lab's own. */
  shippingAccountLast4?: Maybe<Scalars['String']['output']>;
  /** ENG-3286 — the shipper account name at label time. */
  shippingDisplayName?: Maybe<Scalars['String']['output']>;
  /** ENG-3286 — the order's shipping terms. */
  shippingTerms?: Maybe<ShippingTerms>;
  status: ShipmentStatus;
  trackingNumber: Scalars['String']['output'];
  updatedAt: Scalars['AWSDateTime']['output'];
};

export type ShipmentConnection = {
  __typename?: 'ShipmentConnection';
  edges: Array<ShipmentEdge>;
  pageInfo: PageInfo;
  totalCount: Scalars['Int']['output'];
};

export type ShipmentEdge = {
  __typename?: 'ShipmentEdge';
  cursor: Scalars['String']['output'];
  node: Shipment;
};

export type ShipmentFilterInput = {
  carrier?: InputMaybe<Array<ShippingCarrier>>;
  orderId?: InputMaybe<Scalars['ID']['input']>;
  /** ILIKE match against tracking number, reference, or order barcode. */
  search?: InputMaybe<Scalars['String']['input']>;
  status?: InputMaybe<Array<ShipmentStatus>>;
};

/** The options a label was bought with, as the carrier adapter sent them. */
export type ShipmentLabelOptions = {
  __typename?: 'ShipmentLabelOptions';
  /** Compared with the lab defaults at purchase, server-side. */
  changedFromDefaults: Array<ShipmentOptionName>;
  declaredValueUsd?: Maybe<Scalars['Float']['output']>;
  /** Null for a carrier package (no dimensions sent). */
  dimensionsIn?: Maybe<ShippingPackageDimensions>;
  oneRate: Scalars['Boolean']['output'];
  packageType: ShippingPackageType;
  pickupType: ShippingPickupType;
  /** The quoted ACCOUNT amount the rate was chosen at, as the client reported it. */
  quotedAmountUsd?: Maybe<Scalars['Float']['output']>;
  rateKey?: Maybe<Scalars['String']['output']>;
  residential: Scalars['Boolean']['output'];
  saturdayDelivery: Scalars['Boolean']['output'];
  shipDate: Scalars['AWSDate']['output'];
  signatureOption: ShippingSignatureOption;
  weightOz: Scalars['Float']['output'];
};

/**
 * A label option that has a lab default, named for "changed from lab defaults".
 * Package type, weight and dimensions are entered on every label and have none.
 */
export enum ShipmentOptionName {
  DeclaredValue = 'DECLARED_VALUE',
  OneRate = 'ONE_RATE',
  PickupType = 'PICKUP_TYPE',
  Residential = 'RESIDENTIAL',
  SaturdayDelivery = 'SATURDAY_DELIVERY',
  ShipDate = 'SHIP_DATE',
  SignatureOption = 'SIGNATURE_OPTION'
}

/**
 * Price-influencing options, chosen before requestShippingRates and sent
 * identically on the rate and the label. Residential is toAddress.residential;
 * declared value is insuredValueUsd.
 */
export type ShipmentOptionsInput = {
  /** Only with a carrier package. */
  oneRate: Scalars['Boolean']['input'];
  packageType: ShippingPackageType;
  pickupType: ShippingPickupType;
  /** Rate: include Saturday variants. Label: must equal the chosen rate's saturdayDelivery. */
  saturdayDelivery: Scalars['Boolean']['input'];
  /** Today through today + 10 days, in the lab's time zone. */
  shipDate: Scalars['AWSDate']['input'];
  signatureOption: ShippingSignatureOption;
};

export type ShipmentOrderRef = {
  __typename?: 'ShipmentOrderRef';
  barcode?: Maybe<Scalars['String']['output']>;
  lifecycleStatus: OrderLifecycleStatus;
  orderId: Scalars['ID']['output'];
};

/** Persisted label lifecycle on `shipments.status` (lowercase in DB). */
export enum ShipmentStatus {
  Created = 'CREATED',
  Voided = 'VOIDED'
}

/**
 * ENG-3286 — an outside carrier account linked into the lab's own carrier API
 * project. The number is write-only: only its last 4 digits are readable.
 */
export type ShippingAccount = {
  __typename?: 'ShippingAccount';
  accountNumberLast4: Scalars['String']['output'];
  carrier: ShippingCarrier;
  createdAt: Scalars['AWSDateTime']['output'];
  id: Scalars['ID']['output'];
  /** Shipper company printed on a label bought on this account. */
  labelCompanyName?: Maybe<Scalars['String']['output']>;
  /** Sender address printed on its labels; null = the site's. Rating still uses the site. */
  labelSenderAddress?: Maybe<ShippingLabelSenderAddress>;
  lastValidatedAt?: Maybe<Scalars['AWSDateTime']['output']>;
  nickname: Scalars['String']['output'];
  status: ShippingAccountStatus;
  updatedAt: Scalars['AWSDateTime']['output'];
  /**
   * ENG-3286 — which customers ship on it. Null outside an internal session
   * with clearpath.integrations.read.
   */
  usage?: Maybe<ShippingAccountUsage>;
};

/**
 * ENG-3286 — offices under the chosen customers that would inherit the
 * account: not ones with shipping terms of their own. Each office counts once,
 * under its nearest chosen customer, so `parties` sums to the total.
 */
export type ShippingAccountAssignmentPreview = {
  __typename?: 'ShippingAccountAssignmentPreview';
  inheritingOfficeCount: Scalars['Int']['output'];
  parties: Array<ShippingAccountAssignmentPreviewParty>;
};

export type ShippingAccountAssignmentPreviewParty = {
  __typename?: 'ShippingAccountAssignmentPreviewParty';
  inheritingOfficeCount: Scalars['Int']['output'];
  partyId: Scalars['ID']['output'];
};

/** Label settings of an outside account. Replace semantics: null clears. */
export type ShippingAccountLabelSettingsInput = {
  /** 1-35 characters, printed as the shipper company on the label; null = none. */
  labelCompanyName?: InputMaybe<Scalars['String']['input']>;
  /** Printed instead of the site's address; needs labelCompanyName. Null = print the site. */
  labelSenderAddress?: InputMaybe<ShippingLabelSenderAddressInput>;
};

/** ENG-3286 — a shipping account as referenced from terms. */
export type ShippingAccountRef = {
  __typename?: 'ShippingAccountRef';
  id: Scalars['ID']['output'];
  nickname: Scalars['String']['output'];
  status: ShippingAccountStatus;
};

export enum ShippingAccountStatus {
  Active = 'ACTIVE',
  Archived = 'ARCHIVED'
}

/**
 * ENG-3286 — the customers whose own terms name the account, and how many
 * offices under them inherit it.
 */
export type ShippingAccountUsage = {
  __typename?: 'ShippingAccountUsage';
  /** At most 50, by name. Null without clearpath.pricing.read (counts only). */
  directParties?: Maybe<Array<ShippingAccountUsageParty>>;
  /** Every customer whose own terms name the account. */
  directPartyCount: Scalars['Int']['output'];
  inheritedPartyCount: Scalars['Int']['output'];
};

export type ShippingAccountUsageParty = {
  __typename?: 'ShippingAccountUsageParty';
  id: Scalars['ID']['output'];
  name: Scalars['String']['output'];
};

export type ShippingAddressInput = {
  city: Scalars['String']['input'];
  company?: InputMaybe<Scalars['String']['input']>;
  /** ISO-3166-1 alpha-2 country code. */
  countryCode: Scalars['String']['input'];
  email?: InputMaybe<Scalars['String']['input']>;
  name: Scalars['String']['input'];
  phone?: InputMaybe<Scalars['String']['input']>;
  postalCode: Scalars['String']['input'];
  residential?: InputMaybe<Scalars['Boolean']['input']>;
  /** 2-letter for US; ISO-3166-2 subdivision code otherwise. */
  state: Scalars['String']['input'];
  street1: Scalars['String']['input'];
  street2?: InputMaybe<Scalars['String']['input']>;
};

/** A box size: inside dimensions in inches. */
export type ShippingBoxPreset = {
  __typename?: 'ShippingBoxPreset';
  heightIn: Scalars['Float']['output'];
  id: Scalars['ID']['output'];
  lengthIn: Scalars['Float']['output'];
  name: Scalars['String']['output'];
  status: ShippingAccountStatus;
  widthIn: Scalars['Float']['output'];
};

export type ShippingBoxPresetInput = {
  heightIn: Scalars['Float']['input'];
  /** Each above 0 and at most 108; stored to 0.01 in. */
  lengthIn: Scalars['Float']['input'];
  /** 1-40 characters. */
  name: Scalars['String']['input'];
  widthIn: Scalars['Float']['input'];
};

/** Carrier supported by the shipping-adapter family. */
export enum ShippingCarrier {
  Fedex = 'FEDEX',
  Ups = 'UPS'
}

export enum ShippingChargeMarkupKind {
  Flat = 'FLAT',
  Percent = 'PERCENT'
}

export type ShippingChargeSettings = {
  __typename?: 'ShippingChargeSettings';
  defaultTerms: ShippingTerms;
  markupKind: ShippingChargeMarkupKind;
  markupValue: Scalars['Float']['output'];
};

export type ShippingChargeSettingsInput = {
  /** LAB_ABSORBS or CUSTOMER_INVOICED; an outside account is per customer. */
  defaultTerms?: InputMaybe<ShippingTerms>;
  markupKind?: InputMaybe<ShippingChargeMarkupKind>;
  markupValue?: InputMaybe<Scalars['Float']['input']>;
};

/** A site holding some of an order's lines. */
export type ShippingCustodySite = {
  __typename?: 'ShippingCustodySite';
  name: Scalars['String']['output'];
  /** Active with a complete ship-from address, so a label can ship from it. */
  shipFromReady: Scalars['Boolean']['output'];
  siteId: Scalars['ID']['output'];
};

/** Result of a label purchase — PDF base64 + tracking number. */
export type ShippingLabel = {
  __typename?: 'ShippingLabel';
  /** @deprecated 0 when the carrier reported no charge. Use chargedAmountUsd. */
  amountUsd: Scalars['Float']['output'];
  carrier: ShippingCarrier;
  /** ENG-3286 — the charge to the shipping account, or null when unknown. */
  chargedAmountUsd?: Maybe<Scalars['Float']['output']>;
  estimatedDeliveryDate?: Maybe<Scalars['AWSDate']['output']>;
  /** Order Files row created for this label (null if persist failed). */
  labelFileId?: Maybe<Scalars['ID']['output']>;
  /** Base64-encoded PDF label (or GIF for UPS; see adapter header). */
  labelPdfBase64: Scalars['String']['output'];
  /**
   * Base64-encoded ZPL for Zebra thermal printers. May be null when the
   * carrier API does not provide it.
   */
  labelZplBase64?: Maybe<Scalars['String']['output']>;
  trackingNumber: Scalars['String']['output'];
};

/**
 * The lab-wide defaults for the rarely changed label options. Package type,
 * weight and dimensions are entered on every label.
 */
export type ShippingLabelDefaults = {
  __typename?: 'ShippingLabelDefaults';
  declaredValueUsd?: Maybe<Scalars['Float']['output']>;
  /** Applies only when the label's package is a carrier package. */
  oneRate: Scalars['Boolean']['output'];
  pickupType: ShippingPickupType;
  residential: Scalars['Boolean']['output'];
  saturdayDelivery: Scalars['Boolean']['output'];
  /** Default ship-from site; null when none (or the site is no longer usable). */
  shipFromSiteId?: Maybe<Scalars['ID']['output']>;
  signatureOption: ShippingSignatureOption;
  /**
   * The lab's today in its own time zone: every label's default ship date, and
   * the first day the server accepts.
   */
  today: Scalars['AWSDate']['output'];
};

/** Replaces every default. Ship date is not stored: each label defaults to today. */
export type ShippingLabelDefaultsInput = {
  declaredValueUsd?: InputMaybe<Scalars['Float']['input']>;
  oneRate: Scalars['Boolean']['input'];
  pickupType: ShippingPickupType;
  residential: Scalars['Boolean']['input'];
  saturdayDelivery: Scalars['Boolean']['input'];
  shipFromSiteId?: InputMaybe<Scalars['ID']['input']>;
  signatureOption: ShippingSignatureOption;
};

/** Stored carrier label bytes (ENG-211 `shipments.label_file_id`). */
export type ShippingLabelDocument = {
  __typename?: 'ShippingLabelDocument';
  /** Base64 file bytes. Decode to a Blob and open an object URL. */
  contentBase64: Scalars['String']['output'];
  filename: Scalars['String']['output'];
  /** application/pdf or image/gif. */
  mimeType: Scalars['String']['output'];
  shipmentId: Scalars['ID']['output'];
  trackingNumber: Scalars['String']['output'];
};

/** Why the caller wants the stored label bytes; recorded in the audit. */
export enum ShippingLabelDocumentPurpose {
  /** Save a copy. Allowed for a voided label. */
  Download = 'DOWNLOAD',
  /** Open to print. Refused for a voided label. */
  Open = 'OPEN'
}

/**
 * The sender address printed on labels of an outside account. The ship-from
 * site stays the rating and pickup origin.
 */
export type ShippingLabelSenderAddress = {
  __typename?: 'ShippingLabelSenderAddress';
  city: Scalars['String']['output'];
  /** ISO-3166-1 alpha-2. */
  country: Scalars['String']['output'];
  line1: Scalars['String']['output'];
  line2?: Maybe<Scalars['String']['output']>;
  phone?: Maybe<Scalars['String']['output']>;
  postalCode: Scalars['String']['output'];
  region: Scalars['String']['output'];
};

export type ShippingLabelSenderAddressInput = {
  city: Scalars['String']['input'];
  country: Scalars['String']['input'];
  line1: Scalars['String']['input'];
  line2?: InputMaybe<Scalars['String']['input']>;
  phone?: InputMaybe<Scalars['String']['input']>;
  postalCode: Scalars['String']['input'];
  region: Scalars['String']['input'];
};

/** One level's package defaults. Null fields inherit. */
export type ShippingPackageDefaults = {
  __typename?: 'ShippingPackageDefaults';
  /** Null when unset or archived. Applies only to your own packaging. */
  boxPreset?: Maybe<ShippingBoxPreset>;
  id: Scalars['ID']['output'];
  packageType?: Maybe<ShippingPackageType>;
  /** Set on a sender identity's row. */
  senderIdentityId?: Maybe<Scalars['ID']['output']>;
  /** Set on an outside account's row. */
  shippingAccountId?: Maybe<Scalars['ID']['output']>;
  weightOz?: Maybe<Scalars['Float']['output']>;
};

/** Replace semantics: null = inherit. */
export type ShippingPackageDefaultsInput = {
  /** Refused with a carrier package type. */
  boxPresetId?: InputMaybe<Scalars['ID']['input']>;
  packageType?: InputMaybe<ShippingPackageType>;
  /** Above 0 and at most 2400. */
  weightOz?: InputMaybe<Scalars['Float']['input']>;
};

export type ShippingPackageDefaultsTarget = {
  senderIdentityId?: InputMaybe<Scalars['ID']['input']>;
  shippingAccountId?: InputMaybe<Scalars['ID']['input']>;
};

export type ShippingPackageDimensions = {
  __typename?: 'ShippingPackageDimensions';
  heightIn: Scalars['Float']['output'];
  lengthIn: Scalars['Float']['output'];
  widthIn: Scalars['Float']['output'];
};

export type ShippingPackageDimensionsInput = {
  heightIn: Scalars['Float']['input'];
  lengthIn: Scalars['Float']['input'];
  widthIn: Scalars['Float']['input'];
};

/**
 * Package the label ships in. Carrier packages (all but CUSTOMER_PACKAGING)
 * send no dimensions.
 */
export enum ShippingPackageType {
  CustomerPackaging = 'CUSTOMER_PACKAGING',
  Envelope = 'ENVELOPE',
  ExtraLargeBox = 'EXTRA_LARGE_BOX',
  LargeBox = 'LARGE_BOX',
  MediumBox = 'MEDIUM_BOX',
  Pak = 'PAK',
  SmallBox = 'SMALL_BOX',
  Tube = 'TUBE'
}

/** How the carrier gets the package. */
export enum ShippingPickupType {
  CallToSchedule = 'CALL_TO_SCHEDULE',
  DropOff = 'DROP_OFF',
  ScheduledPickup = 'SCHEDULED_PICKUP'
}

/** Single quoted rate — one per service level offered by the carrier. */
export type ShippingRate = {
  __typename?: 'ShippingRate';
  /** @deprecated 0 when the carrier quoted no account rate. Use quotedAmountUsd. */
  amountUsd: Scalars['Float']['output'];
  carrier: ShippingCarrier;
  currency: Scalars['String']['output'];
  /**
   * Date the carrier expects to deliver by, as `YYYY-MM-DD`.
   *
   * NULLABLE ON PURPOSE (ENG-657): carriers do not always quote an ETA — UPS
   * omits `GuaranteedDelivery` on non-guaranteed services and FedEx omits
   * `commit` on some rate replies — so "no estimate available" is a normal
   * outcome that has to be representable. While this was `AWSDate!` a rate with
   * no ETA could not be nulled in isolation: the non-null violation propagated
   * out of `[ShippingRate!]!` and killed the whole quote list, so the lab could
   * not ship at all. Render this as "—"/"not quoted", never as a missing rate.
   */
  estimatedDeliveryDate?: Maybe<Scalars['AWSDate']['output']>;
  /**
   * False when ClearPath cannot buy a label on this service yet (the picker
   * hides it and counts it). Null from an older api: treat as supported.
   */
  labelSupported?: Maybe<Scalars['Boolean']['output']>;
  /** Carrier list price; equal to quotedAmountUsd means no negotiated discount. */
  listAmountUsd?: Maybe<Scalars['Float']['output']>;
  /** ENG-3286 — the account rate, or null when the carrier quoted none. */
  quotedAmountUsd?: Maybe<Scalars['Float']['output']>;
  /**
   * Identity of this quote: serviceLevel plus delivery variant. Select by
   * this, never by serviceLevel, which repeats for a Saturday variant.
   */
  rateKey: Scalars['String']['output'];
  /** The carrier's Saturday-delivery variant of serviceLevel. */
  saturdayDelivery: Scalars['Boolean']['output'];
  /** Carrier-specific service code (pass back verbatim in createShippingLabel). */
  serviceLevel: Scalars['String']['output'];
  /**
   * The carrier's own name for the service (FedEx "FedEx Ground®"); null when
   * the carrier gave none (UPS) or from an older integrations service.
   */
  serviceName?: Maybe<Scalars['String']['output']>;
  /**
   * Business days the carrier expects the package to be in transit.
   *
   * NULLABLE ON PURPOSE (ENG-1060), for the same reason as
   * `estimatedDeliveryDate` above. While this was `Int!` the adapters had
   * nowhere to put "the carrier quoted no transit time", so both of them fell
   * back to `0` — and the UI rendered that as a confident `0d` on every option
   * of every route. The absence was real: neither adapter ASKED for transit
   * times (UPS needs the `Ratetimeintransit` request option plus a
   * `DeliveryTimeInformation` container, FedEx needs `returnTransitTimes`), so
   * no quote ever carried one. Render null as "—"/"not quoted"; `0` now means
   * only what it says.
   */
  transitDays?: Maybe<Scalars['Int']['output']>;
};

/**
 * A sender printed on FedEx labels as the shipper (company, contact, address,
 * phone). Print-only: the ship-from site stays the rating and pickup origin.
 */
export type ShippingSenderIdentity = {
  __typename?: 'ShippingSenderIdentity';
  address: ShippingLabelSenderAddress;
  companyName: Scalars['String']['output'];
  contactName: Scalars['String']['output'];
  /** Customers whose own sender this is; set on shippingSenderIdentities and create only. */
  directPartyCount?: Maybe<Scalars['Int']['output']>;
  id: Scalars['ID']['output'];
  /** This sender's own package defaults; set on shippingSenderIdentities and update. */
  packageDefaults?: Maybe<ShippingPackageDefaults>;
  /** Null = the lab's own account. */
  shippingAccountId?: Maybe<Scalars['ID']['output']>;
  status: ShippingAccountStatus;
};

export type ShippingSenderIdentityInput = {
  /** 1-35 characters, printed as the shipper company. */
  companyName: Scalars['String']['input'];
  /** 1-35 characters, printed as the shipper contact. */
  contactName: Scalars['String']['input'];
  /** Phone required. Not `address`: that name is AWSJSON on external-party inputs and the client link serializes by key. */
  senderAddress: ShippingLabelSenderAddressInput;
  /** Create only (null = the lab's own account); on update it must be omitted or unchanged. */
  shippingAccountId?: InputMaybe<Scalars['ID']['input']>;
};

/**
 * Logical service level — the adapter maps to carrier-specific codes
 * (FedEx FEDEX_GROUND / STANDARD_OVERNIGHT / ..., UPS 03/02/01/14).
 */
export enum ShippingServiceLevel {
  Express = 'EXPRESS',
  Ground = 'GROUND',
  Overnight = 'OVERNIGHT',
  PriorityOvernight = 'PRIORITY_OVERNIGHT'
}

export enum ShippingSignatureOption {
  Adult = 'ADULT',
  Direct = 'DIRECT',
  Indirect = 'INDIRECT',
  None = 'NONE',
  ServiceDefault = 'SERVICE_DEFAULT'
}

/** ENG-3286 — who pays for an order's shipping. */
export enum ShippingTerms {
  /** Ships on the lab's account; the label quote plus markup is invoiced. */
  CustomerInvoiced = 'CUSTOMER_INVOICED',
  /** Ships on the customer's linked outside account; never invoiced. */
  ExternalAccount = 'EXTERNAL_ACCOUNT',
  /** Ships on the lab's account; nothing invoiced. */
  LabAbsorbs = 'LAB_ABSORBS'
}

export type ShippingTermsChoice = {
  __typename?: 'ShippingTermsChoice';
  displayName: Scalars['String']['output'];
  shippingAccount?: Maybe<ShippingAccountRef>;
  terms: ShippingTerms;
};

export type ShippingTermsOptions = {
  __typename?: 'ShippingTermsOptions';
  /** Empty unless canOverride. */
  alternatives: Array<ShippingTermsChoice>;
  canOverride: Scalars['Boolean']['output'];
  resolved: ResolvedShippingTerms;
};

/** ENG-3286 — where an order's shipping terms came from. */
export enum ShippingTermsSource {
  Legacy = 'LEGACY',
  OrderOverride = 'ORDER_OVERRIDE',
  PartyDirect = 'PARTY_DIRECT',
  PartyInherited = 'PARTY_INHERITED',
  SourceOrder = 'SOURCE_ORDER',
  TenantDefault = 'TENANT_DEFAULT'
}

/** Tracking snapshot — status + last event description. */
export type ShippingTrackingSnapshot = {
  __typename?: 'ShippingTrackingSnapshot';
  deliveredAt?: Maybe<Scalars['AWSDateTime']['output']>;
  lastEventAt: Scalars['AWSDateTime']['output'];
  lastEventDescription: Scalars['String']['output'];
  status: ShippingTrackingStatus;
};

/**
 * Normalized tracking status, collapsed from ~40 carrier codes to 8 buckets
 * so the Portal's status badge + pipeline engine don't need per-carrier logic.
 */
export enum ShippingTrackingStatus {
  Delivered = 'DELIVERED',
  Exception = 'EXCEPTION',
  InTransit = 'IN_TRANSIT',
  LabelCreated = 'LABEL_CREATED',
  OutForDelivery = 'OUT_FOR_DELIVERY',
  PickedUp = 'PICKED_UP',
  Returned = 'RETURNED',
  Unknown = 'UNKNOWN'
}

/**
 * ENG-1036 — a physical location the org operates from (intake and/or
 * manufacturing). A site IS an org_unit of unit_type='site' extended 1:1 by the
 * `sites` table; `id` is that org_unit id (the value stored in
 * VendorRoutingRule.destinationSiteId).
 *
 * Query.sites(includePlants: true) also returns plants (unitType PLANT): a plant
 * carries only a ship-from address, never intake or manufacturing, and is never a
 * routing site.
 */
export type Site = {
  __typename?: 'Site';
  /** TRUE when new work may be received at this site. */
  acceptsIntake: Scalars['Boolean']['output'];
  /** Stable SCREAMING_SNAKE reference, unique per org (e.g. SITE_TX). */
  code: Scalars['String']['output'];
  createdAt: Scalars['AWSDateTime']['output'];
  /** org_unit id (sites.org_unit_id) — the FK target of destination_site_id. */
  id: Scalars['ID']['output'];
  /** Soft-availability flag; inactive sites are hidden from new-work pickers. */
  isActive: Scalars['Boolean']['output'];
  /** Human-readable site name (from the underlying org_unit). */
  name: Scalars['String']['output'];
  organizationId: Scalars['ID']['output'];
  /** TRUE when production steps may execute at this site. */
  performsManufacturing: Scalars['Boolean']['output'];
  shipFromCity?: Maybe<Scalars['String']['output']>;
  shipFromCountry?: Maybe<Scalars['String']['output']>;
  shipFromLine1?: Maybe<Scalars['String']['output']>;
  shipFromLine2?: Maybe<Scalars['String']['output']>;
  /**
   * ENG-1043 — structured ship-from address (all nullable; a manufacturing-only
   * site that never ships need not carry one).
   */
  shipFromName?: Maybe<Scalars['String']['output']>;
  shipFromPhone?: Maybe<Scalars['String']['output']>;
  shipFromPostalCode?: Maybe<Scalars['String']['output']>;
  /**
   * TRUE when a label can ship from here: active, a complete ship-from
   * address, and a live site or plant. The server's single definition.
   */
  shipFromReady: Scalars['Boolean']['output'];
  shipFromRegion?: Maybe<Scalars['String']['output']>;
  /** SITE, or PLANT for a ship-from-only plant (includePlants). */
  unitType: OrgUnitType;
  updatedAt?: Maybe<Scalars['AWSDateTime']['output']>;
};

/** ENG-1043 — result for createSite / updateSite. */
export type SitePayload = {
  __typename?: 'SitePayload';
  site: Site;
};

export type SkillDefinition = {
  __typename?: 'SkillDefinition';
  category: Scalars['String']['output'];
  description?: Maybe<Scalars['String']['output']>;
  id: Scalars['ID']['output'];
  name: Scalars['String']['output'];
};

export type SkillDefinitionDeletedPayload = {
  __typename?: 'SkillDefinitionDeletedPayload';
  deletedId: Scalars['ID']['output'];
};

export type SkillDefinitionPayload = {
  __typename?: 'SkillDefinitionPayload';
  skillDefinition: SkillDefinition;
};

/**
 * Input for skipStepRun - ENG-2554 "this step will not be worked".
 *
 * The counterpart to `sendStepRunBack` on the other axis: send-back moves a unit
 * BACKWARD to redo work, skip moves it FORWARD past work that will not be done.
 * The resolver appends a terminal attempt (`status: COMPLETED`,
 * `outcome: SKIPPED`) and the engine activates the step's successors exactly as
 * a real completion would, so the unit keeps flowing.
 *
 * Gated on `clearpath.steps.skip`, which is deliberately NOT one of the
 * `clearpath.steps.*` action keys the execution tier holds: waiving a production
 * step is a supervisory decision, and it is granted beside
 * `clearpath.assignments.write`.
 */
export type SkipStepRunInput = {
  /**
   * Why the step is not being worked. REQUIRED, and the resolver enforces the
   * same min length 10 its loopback / send-back siblings do.
   *
   * Free text, not a code: a skip is a one-off judgement about THIS unit, so
   * there is no tenant taxonomy to pick from. Stored on the attempt as
   * `StepRun.skipReason` and repeated in the audit record.
   */
  reason: Scalars['String']['input'];
  /**
   * The pending / ready / in_progress step_run to skip. A terminal attempt is
   * refused - there is nothing left to skip.
   */
  stepRunId: Scalars['ID']['input'];
};

/** Single (YYYY-MM-DD) aggregate point used by BreachRateChart. */
export type SlaBreachRatePoint = {
  __typename?: 'SlaBreachRatePoint';
  breachRate: Scalars['Float']['output'];
  breached: Scalars['Int']['output'];
  date: Scalars['String']['output'];
  evaluated: Scalars['Int']['output'];
};

/**
 * Shared filter for slaOverview + slaDrillDown. All fields optional — any
 * present condition narrows the result set (AND semantics).
 */
export type SlaDashboardFilterInput = {
  /** Explicit override for `period`. When set, period is ignored. */
  customRange?: InputMaybe<DateRangeInput>;
  orgUnitId?: InputMaybe<Scalars['ID']['input']>;
  /** Rolling window; defaults to LAST_30D server-side when omitted. */
  period?: InputMaybe<SlaDashboardPeriod>;
  productTypeId?: InputMaybe<Scalars['ID']['input']>;
  stepDefinitionId?: InputMaybe<Scalars['ID']['input']>;
};

/**
 * Discrete rolling-window selector for the SLA dashboard. Kept as an enum
 * (rather than raw DateRangeInput) so portal + UI can share one vocabulary
 * without date-math drift. Convert to absolute bounds at resolver entry.
 * Enum names avoid underscores before digits because codegen pascalCase
 * produces inconsistent symbols across toolchains for `LAST_24H`.
 */
export enum SlaDashboardPeriod {
  Last7D = 'LAST7D',
  Last24H = 'LAST24H',
  Last30D = 'LAST30D',
  Last90D = 'LAST90D'
}

/** SLA drill-down payload returned by Query.slaDrillDown. */
export type SlaDrillDown = {
  __typename?: 'SlaDrillDown';
  rows: Array<SlaDrillDownRow>;
};

/** Drill-down grouping axis — picks which dimension sorts first. */
export enum SlaDrillDownDimension {
  OrgUnit = 'ORG_UNIT',
  ProductType = 'PRODUCT_TYPE',
  Step = 'STEP'
}

/** Single drill-down row — one (step × product_type × org_unit) tuple. */
export type SlaDrillDownRow = {
  __typename?: 'SlaDrillDownRow';
  avgPercentage: Scalars['Float']['output'];
  breachCount: Scalars['Int']['output'];
  breachRate: Scalars['Float']['output'];
  orgUnitId?: Maybe<Scalars['ID']['output']>;
  p95Percentage: Scalars['Float']['output'];
  /** Primary policy id for the tuple — drives the drill-in link. */
  primarySlaPolicyId?: Maybe<Scalars['ID']['output']>;
  productTypeId?: Maybe<Scalars['ID']['output']>;
  stepDefinitionId?: Maybe<Scalars['ID']['output']>;
  trend7d: Array<Scalars['Float']['output']>;
};

/** SLA overview payload returned by Query.slaOverview. */
export type SlaOverview = {
  __typename?: 'SlaOverview';
  breachRateByDay: Array<SlaBreachRatePoint>;
  counts: SlaOverviewCounts;
  topBreachingOrgUnits: Array<SlaTopOrgUnitRow>;
  topBreachingSteps: Array<SlaTopStepRow>;
};

/** Count bucket for the overview header cards. */
export type SlaOverviewCounts = {
  __typename?: 'SlaOverviewCounts';
  atRisk: Scalars['Int']['output'];
  breached: Scalars['Int']['output'];
  onTrack: Scalars['Int']['output'];
  total: Scalars['Int']['output'];
};

/** A "top breaching" row grouped by org_unit (department). */
export type SlaTopOrgUnitRow = {
  __typename?: 'SlaTopOrgUnitRow';
  avgPercentage: Scalars['Float']['output'];
  breachCount: Scalars['Int']['output'];
  breachRate: Scalars['Float']['output'];
  orgUnitId?: Maybe<Scalars['ID']['output']>;
};

/** A "top breaching" row grouped by step_definition. */
export type SlaTopStepRow = {
  __typename?: 'SlaTopStepRow';
  avgPercentage: Scalars['Float']['output'];
  breachCount: Scalars['Int']['output'];
  breachRate: Scalars['Float']['output'];
  stepDefinitionId?: Maybe<Scalars['ID']['output']>;
};

/**
 * ENG-2547 — a rendered work slip, delivered as BYTES over the authenticated
 * channel rather than as a link.
 *
 * Deliberately NOT a URL. A presigned S3 GET is a shareable bearer credential: for
 * its whole TTL anyone holding the string fetches the PDF with no ClearPath auth,
 * no Cedar check and no tenant check. Returning bytes means the caller must
 * already hold an authenticated session, and nothing shareable ever exists — a
 * copy-pasted value is useless to an unauthenticated stranger.
 *
 * The PDF is still persisted to S3 (`s3Key`) so a later reprint can re-serve it
 * without re-rendering, and so the canonical-vs-ad-hoc distinction (ENG-2511)
 * still decides what the next person retrieves.
 */
export type SlipDocument = {
  __typename?: 'SlipDocument';
  /**
   * Base64-encoded PDF. Clients decode this to a Blob and open an object URL;
   * a `blob:` URL is origin- and session-scoped, so it does not resolve if it is
   * pasted anywhere else.
   */
  pdfBase64: Scalars['String']['output'];
  /** Deterministic S3 key the bytes were also written to. */
  s3Key: Scalars['String']['output'];
};

/**
 * ENG-2511 — which sheet a slip is rendered for. The slip's CONTENT is always
 * A5-sized; only the sheet around it changes, so a slip cut from an A4 sheet is
 * physically identical to one printed on A5 (half of A4-landscape is 420.94pt
 * against A5's 419.53pt, at identical heights).
 */
export enum SlipPaperSize {
  /** Landscape A4 with the slip on the left half. */
  A4Half = 'A4_HALF',
  /** A true A5 page. The default. */
  A5 = 'A5',
  /** Landscape Letter with the slip on the left half. */
  LetterHalf = 'LETTER_HALF'
}

/** ENG-2511 — the full catalog behind the "Print slip" dialog. */
export type SlipPrintCatalog = {
  __typename?: 'SlipPrintCatalog';
  orderFields: Array<SlipPrintableField>;
  products: Array<SlipPrintableProduct>;
};

/**
 * ENG-2511 — which fields to print on one slip. Selection is by field NAME,
 * scoped per form, because that is the only stable identity an answer blob has.
 *
 * An OMITTED list means "use that form's own flags"; a PRESENT but empty list
 * means "print none of this form's fields". The distinction is deliberate: it
 * lets the dialog express "I unticked everything here" without that being
 * confused with "I did not touch this form".
 */
export type SlipPrintOptionsInput = {
  /** Order-level intake field names. */
  orderFields?: InputMaybe<Array<Scalars['String']['input']>>;
  /**
   * ENG-2511 — the physical sheet to render for. Defaults to A5.
   *
   * A browser's print dialog defaults to the PRINTER's paper, not to the PDF's
   * page size, and a PDF cannot force that selection — so an A5 document sent to
   * an A4 printer lands in the top-left of a mostly blank sheet. Rather than fight
   * the dialog, the renderer emits the geometry the lab wants: a true A5 page, or a
   * landscape A4/Letter sheet carrying the SAME A5-sized slip on its left half with
   * a cut guide down the middle.
   */
  paperSize?: InputMaybe<SlipPaperSize>;
  /** Per-product intake and step-form field names. */
  products?: InputMaybe<Array<SlipProductPrintOptionsInput>>;
};

/**
 * ENG-2511 — one field the slip could print, and how the dialog should show
 * it.
 */
export type SlipPrintableField = {
  __typename?: 'SlipPrintableField';
  /**
   * Whether this order has an answer that would actually render. A ticked
   * field with no answer prints nothing; the dialog still lists it so the
   * absence is visible rather than mysterious.
   */
  hasAnswer: Scalars['Boolean']['output'];
  label: Scalars['String']['output'];
  /** Answer-blob key; what a print selection names. */
  name: Scalars['String']['output'];
  /** The form's own "Print on order slip" flag — the dialog's initial tick. */
  printByDefault: Scalars['Boolean']['output'];
};

/**
 * One product's printable fields — its intake form, plus one group per
 * completed step form.
 */
export type SlipPrintableProduct = {
  __typename?: 'SlipPrintableProduct';
  intakeFields: Array<SlipPrintableField>;
  orderProductId: Scalars['ID']['output'];
  steps: Array<SlipPrintableStep>;
};

/** One COMPLETED step's printable fields. */
export type SlipPrintableStep = {
  __typename?: 'SlipPrintableStep';
  fields: Array<SlipPrintableField>;
  stepDefinitionId: Scalars['ID']['output'];
  stepName: Scalars['String']['output'];
};

/** One product's print selection. */
export type SlipProductPrintOptionsInput = {
  /** Field names from this product's intake form. */
  intakeFields?: InputMaybe<Array<Scalars['String']['input']>>;
  orderProductId: Scalars['ID']['input'];
  /** Field names from the forms bound to this product's pipeline steps. */
  steps?: InputMaybe<Array<SlipStepPrintOptionsInput>>;
};

/** One completed step's print selection, keyed by the pipeline step. */
export type SlipStepPrintOptionsInput = {
  fields?: InputMaybe<Array<Scalars['String']['input']>>;
  stepDefinitionId: Scalars['ID']['input'];
};

export type SmartAssignConfirmInput = {
  assignments: Array<SmartAssignConfirmRowInput>;
  stepRunIds?: InputMaybe<Array<Scalars['ID']['input']>>;
};

export type SmartAssignConfirmResult = {
  __typename?: 'SmartAssignConfirmResult';
  assigned: Array<AssignmentWorkItem>;
  assignedCount: Scalars['Int']['output'];
  skippedStepRunIds: Array<Scalars['ID']['output']>;
};

export type SmartAssignConfirmRowInput = {
  acknowledgeUnavailable?: InputMaybe<Scalars['Boolean']['input']>;
  stepRunId: Scalars['ID']['input'];
  userId: Scalars['ID']['input'];
};

export type SmartAssignPreviewInput = {
  stepRunIds: Array<Scalars['ID']['input']>;
};

export type SmartAssignmentPreviewResult = {
  __typename?: 'SmartAssignmentPreviewResult';
  assignmentDate: Scalars['String']['output'];
  recommendations: Array<SmartAssignmentRecommendation>;
  summary: SmartAssignmentSummary;
};

export type SmartAssignmentRecommendation = {
  __typename?: 'SmartAssignmentRecommendation';
  candidate?: Maybe<AssignmentCandidate>;
  candidates: Array<AssignmentCandidate>;
  needsReview: Scalars['Boolean']['output'];
  projectedLoad?: Maybe<Scalars['Int']['output']>;
  reason?: Maybe<Scalars['String']['output']>;
  status: Scalars['String']['output'];
  stepRunId: Scalars['ID']['output'];
  workItem?: Maybe<AssignmentWorkItem>;
};

export type SmartAssignmentSummary = {
  __typename?: 'SmartAssignmentSummary';
  needsReview: Scalars['Int']['output'];
  readyToAssign: Scalars['Int']['output'];
  selected: Scalars['Int']['output'];
};

/** Sort direction for ordered list queries. */
export enum SortDirection {
  Asc = 'ASC',
  Desc = 'DESC'
}

/** Input for completeStepRun. */
export type StartStepRunInput = {
  stepRunId: Scalars['ID']['input'];
};

/**
 * Statement-billing record.
 * Per docs/clearpath-architecture/03-data-model.md tier 7 (`statements`).
 */
export type Statement = {
  __typename?: 'Statement';
  billingParty?: Maybe<ExternalParty>;
  billingPartyId: Scalars['ID']['output'];
  createdAt: Scalars['AWSDateTime']['output'];
  currency: Scalars['String']['output'];
  id: Scalars['ID']['output'];
  issuedAt?: Maybe<Scalars['AWSDateTime']['output']>;
  lines: Array<StatementLine>;
  organizationId: Scalars['ID']['output'];
  periodEnd: Scalars['AWSDateTime']['output'];
  periodStart: Scalars['AWSDateTime']['output'];
  statementNumber: Scalars['String']['output'];
  status: StatementStatus;
  tenantId: Scalars['ID']['output'];
  total: Scalars['Float']['output'];
};

/** Filter for statements. MOL-616. */
export type StatementFilterInput = {
  billingPartyIds?: InputMaybe<Array<Scalars['ID']['input']>>;
  periodEnd?: InputMaybe<Scalars['AWSDateTime']['input']>;
  periodStart?: InputMaybe<Scalars['AWSDateTime']['input']>;
  status?: InputMaybe<Array<StatementStatus>>;
};

/**
 * Line item on a statement.
 * Per docs/clearpath-architecture/03-data-model.md tier 7 (`statement_lines`).
 */
export type StatementLine = {
  __typename?: 'StatementLine';
  amount: Scalars['Float']['output'];
  description: Scalars['String']['output'];
  id: Scalars['ID']['output'];
  invoiceId?: Maybe<Scalars['ID']['output']>;
  position: Scalars['Int']['output'];
  statementId: Scalars['ID']['output'];
};

/** Statement lifecycle status. */
export enum StatementStatus {
  Closed = 'CLOSED',
  Draft = 'DRAFT',
  Issued = 'ISSUED'
}

/**
 * Round 5 — single status column on a tenant's kanban. Sourced from
 * `tenant_settings.status_states_json`.
 */
export type StatusState = {
  __typename?: 'StatusState';
  key: Scalars['String']['output'];
  label: Scalars['String']['output'];
  position: Scalars['Int']['output'];
  /** Design-system color token: `primary`, `success`, `warning`, `danger`, `muted`. */
  tone: Scalars['String']['output'];
};

/**
 * Round 5 — single status state in `UpsertTenantSettingsInput.statusStates`.
 * Mirrors the `StatusState` output type.
 */
export type StatusStateInput = {
  key: Scalars['String']['input'];
  label: Scalars['String']['input'];
  position: Scalars['Int']['input'];
  tone: Scalars['String']['input'];
};

/**
 * ENG-2416 — the completer's choice about a reworked step's price-modifier
 * charges. See `CompleteStepRunInput.stepChargeDecision`.
 */
export enum StepChargeDecision {
  /**
   * Bill both. Refused when this attempt selected nothing the prior attempt
   * did not already charge for — identical selections collapse onto one charge by
   * design (db/migrations/0402), and billing a customer twice for the lab redoing
   * its own work is what `fee_definitions` exists for.
   */
  ChargeBoth = 'CHARGE_BOTH',
  /** Keep the prior charges; bill nothing new. */
  KeepPrior = 'KEEP_PRIOR',
  /** Supersede everything; bill nothing for this step. */
  None = 'NONE',
  /** Supersede the prior attempt's charges; bill this attempt's selections. */
  UseNew = 'USE_NEW'
}

/** Wall-clock cycle time for one pipeline step (bottleneck row). */
export type StepCycleTime = {
  __typename?: 'StepCycleTime';
  avgHours: Scalars['Float']['output'];
  medianHours: Scalars['Float']['output'];
  p90Hours: Scalars['Float']['output'];
  /** Completed/failed step_runs with a real duration counted. */
  runCount: Scalars['Int']['output'];
  stepDefinitionId: Scalars['ID']['output'];
  stepName?: Maybe<Scalars['String']['output']>;
  /** Sum of all run durations in hours (total dwell contribution). */
  totalHours: Scalars['Float']['output'];
};

/**
 * DAG edge between steps.
 * Per docs/clearpath-architecture/03-data-model.md tier 3 (`step_dependencies`).
 */
export type StepDependency = {
  __typename?: 'StepDependency';
  /** Barrier target identifier. */
  barrierTarget?: Maybe<Scalars['String']['output']>;
  /** Branch condition (JSONB). */
  branchCondition?: Maybe<Scalars['AWSJSON']['output']>;
  dependencyType: DependencyType;
  /** Failure rules (JSONB array). */
  failureRules?: Maybe<Scalars['AWSJSON']['output']>;
  fromStep?: Maybe<PipelineStep>;
  fromStepId: Scalars['ID']['output'];
  id: Scalars['ID']['output'];
  pipelineId?: Maybe<Scalars['ID']['output']>;
  toStep?: Maybe<PipelineStep>;
  toStepId: Scalars['ID']['output'];
};

export type StepDependencyDeletedPayload = {
  __typename?: 'StepDependencyDeletedPayload';
  deletedId: Scalars['ID']['output'];
};

export type StepDependencyPayload = {
  __typename?: 'StepDependencyPayload';
  stepDependency: StepDependency;
};

/**
 * ENG-1577 — the set of failure reasons a step OFFERS when work on it fails.
 * A step with a set offers exactly that set; a step with none offers the library
 * (every reason attached to no step). Replaces the whole set, because an author
 * who unticks a reason and saves has said "stop offering this one" and a
 * merge-only endpoint cannot express it.
 */
export type StepFailureReasonCodesPayload = {
  __typename?: 'StepFailureReasonCodesPayload';
  failureReasonCodeIds: Array<Scalars['ID']['output']>;
  pipelineStepId: Scalars['ID']['output'];
};

/**
 * ENG-1554 — which category of completion gate is blocking a step run.
 * `CHECKLIST` and `STEP_FORM` both come from the single checklist-requirements
 * resolution, partitioned by whether the missing field's binding is the step's own
 * form (`pipeline_steps.form_definition_id`). `MATERIAL` (ENG-1689) is blocking
 * when the step has one or more configured consumed-inventory items and not every
 * item has a captured lot for the attempt (SAVED state only — a live attempt with
 * no persisted lots reads MATERIAL-blocked here).
 */
export enum StepGateKind {
  Checklist = 'CHECKLIST',
  Material = 'MATERIAL',
  StepForm = 'STEP_FORM'
}

/**
 * Reusable step definition per tenant (referenced by pipeline_steps).
 * Per docs/clearpath-architecture/03-data-model.md tier 3 (`step_library`).
 */
export type StepLibraryEntry = {
  __typename?: 'StepLibraryEntry';
  createdAt: Scalars['AWSDateTime']['output'];
  /**
   * Default auto-assignment policy inherited by new pipeline_steps built
   * from this library entry. Can be overridden per pipeline_step via the
   * pipeline graph editor.
   */
  defaultAssignmentPolicy: AssignmentPolicy;
  defaultConfig?: Maybe<Scalars['AWSJSON']['output']>;
  defaultDurationMinutes?: Maybe<Scalars['Int']['output']>;
  defaultStepType: StepType;
  description?: Maybe<Scalars['String']['output']>;
  id: Scalars['ID']['output'];
  key: Scalars['String']['output'];
  name: Scalars['String']['output'];
  organizationId: Scalars['ID']['output'];
  tenantId: Scalars['ID']['output'];
  updatedAt?: Maybe<Scalars['AWSDateTime']['output']>;
};

export type StepLibraryEntryDeletedPayload = {
  __typename?: 'StepLibraryEntryDeletedPayload';
  deletedId: Scalars['ID']['output'];
};

export type StepLibraryEntryPayload = {
  __typename?: 'StepLibraryEntryPayload';
  stepLibraryEntry: StepLibraryEntry;
};

/**
 * Append-only execution record for a step.
 * Per docs/clearpath-architecture/03-data-model.md tier 3 (`step_runs`).
 */
export type StepRun = {
  __typename?: 'StepRun';
  assignee?: Maybe<User>;
  /** Currently assigned user. */
  assigneeId?: Maybe<Scalars['ID']['output']>;
  /** Active assignments for this step run. */
  assignments: Array<Assignment>;
  /** Attempt number (1-based). */
  attemptNumber: Scalars['Int']['output'];
  /**
   * WHO last recorded this attempt's checklist, and when.
   *
   * `step_run_custom_fields.updated_by_user_id` has been captured since 0085 and
   * was exposed nowhere, so the answers a technician attested to were readable
   * (`customFields` above) while the attestor was not. That is the half an audit
   * needs: reviewing work that passed a step and later proved defective, the
   * question is who signed it off, not only what the boxes said.
   *
   * Keyed on the ATTEMPT, like `customFields` — step_runs is append-only and mints
   * a row per transition, so a checklist filled while the step was `ready` lives on
   * a row a step_run_id-keyed read would never see. Reports the LAST writer, which
   * is the row whose values won the response merge.
   *
   * Identity and timestamp ONLY. The answers have their own field; keeping them
   * apart lets an activity feed name the attestor without widening what carries
   * free-text operator input. Null when nothing is saved.
   */
  checklistAuthorship?: Maybe<ChecklistAuthorship>;
  /**
   * ENG-3844 — the files a checklist FILE field on this step may pick from: the
   * CURRENT files of the step's owning order (registered, not superseded), newest
   * first, at most 200, both standard and internal. Exactly the set the server
   * accepts when the answer is saved or the step completed — the picker and the
   * validator share one definition.
   *
   * STAFF ONLY: refuses every non-internal caller, then requires
   * clearpath.files.read. Null when the step run resolves to no order in the
   * caller's organization, and on a mutation payload that did not resolve it
   * (field-resolver hop, nullable for the same reason as checklistRequirements).
   */
  checklistFileCandidates?: Maybe<ChecklistFileCandidates>;
  /**
   * ENG-27 — the checklists that WILL gate completion once the server-side
   * gate is wired (PR 3/5), resolved for THIS order (product-type and
   * customer-party scoped bindings merged) rather than guessed from the step's
   * name.
   *
   * This is the authoritative RESOLUTION of "what does the technician have to
   * fill in" — `requiredFieldNames` is the UNION across every applicable required
   * binding, and once PR 3 lands it is exactly the set the server enforces on
   * completion (so a UI that renders these and blocks locally will agree with
   * the gate). In this slice (PR 1/5) the resolver returns the same shape but no
   * completion path consults it yet.
   *
   * NULLABLE by necessity, not preference. This is a field-resolver hop AppSync
   * dispatches as its own Lambda invocation, so a mutation payload returning a
   * StepRun never populates it. Declaring it non-null would make every
   * StepRun-returning mutation responsible for a field it does not compute — which
   * the SDL conformance guard correctly flags, and which reaches production as
   * `Cannot return null for non-nullable type`. The resolver always returns a
   * value, so a client selecting it gets a well-formed object; it is absent only
   * when nothing resolved it.
   */
  checklistRequirements?: Maybe<StepRunChecklistRequirements>;
  /** Comments on this step run. */
  comments: CommentConnection;
  /**
   * ENG-1554 — the completion VERDICT for this step run: is completion currently
   * blocked, and by WHICH gate categories. The safe-to-list sibling of
   * `checklistRequirements`: it carries NO answer values, only the boolean verdict +
   * the blocking kinds, so it CAN be selected on list/board queries (My Work) where
   * the full checklist fields are deliberately withheld to keep answers off the
   * board.
   *
   * Reuses the SAME server gate logic the completion path enforces — the checklist
   * half runs `assertChecklistSatisfied` (catching its refusal to read the verdict
   * without throwing to the client) — so the compact-card button and the server can
   * never disagree.
   *
   * LOAD-BEARING: reflects SAVED server state only. It cannot see a client's unsaved
   * checklist edits. That is correct for the collapsed board button — the expanded
   * panel composes the LIVE gate client-side from `checklistRequirements` + the
   * operator's local edits (fetched per-step only on expand).
   *
   * NULLABLE by necessity, not preference — same as `checklistRequirements`. This is
   * a field-resolver hop AppSync dispatches as its own Lambda invocation, so a
   * StepRun-returning MUTATION payload never populates it. Declaring it non-null
   * would make every StepRun-returning mutation responsible for a field it does not
   * compute (the SDL conformance guard flags it, and it reaches production as
   * `Cannot return null for non-nullable type`). The resolver always returns a
   * well-formed object, so a client selecting it gets a safe value; it is absent
   * only when nothing resolved it.
   *
   * LEAST-DISCLOSURE: resolved ONLY for the VIEWER'S OWN steps (assignee === the
   * caller); for every other step (a teammate's, or the unassigned pool) it returns
   * the content-free default (isBlocked: false, no kinds) WITHOUT computing the
   * gate. A surface needing another user's blocked-reason must read
   * checklistRequirements directly, not this field.
   */
  completionReadiness?: Maybe<StepRunCompletionReadiness>;
  /**
   * ENG-1689 — the per-unit material items THIS step attempt is configured to
   * consume (from `pipeline_step_consumed_inventory`, any trigger mode),
   * version-pinned to the attempt's own step version so a later config change does
   * not alter what an in-flight attempt shows. Each rule carries the lot number
   * read back from `step_run_material_lots` once the attempt is COMPLETED
   * (`capturedLot`); a live attempt lists the config with a null `capturedLot`. An
   * empty list means the step consumes nothing (the UI renders no material
   * section).
   *
   * INTERNAL ONLY: lot numbers and item codes are the lab's own supply-chain data,
   * so a non-internal (portal/vendor) caller always receives an empty list.
   *
   * NULLABLE by necessity, not preference — same as `timeEntries` / `vendorDelay` /
   * `completionReadiness`. This is a field-resolver hop AppSync dispatches as its
   * own Lambda invocation, so a StepRun-returning MUTATION payload never populates
   * it. Declaring the list non-null would make every StepRun-returning mutation
   * responsible for a field it does not compute (the SDL conformance guard flags
   * it, and it reaches production as `Cannot return null for non-nullable type`).
   * The resolver always returns a list, so a client selecting it gets a well-formed
   * array; it is absent only when nothing resolved it.
   */
  consumptionRules?: Maybe<Array<StepRunConsumptionRule>>;
  createdAt: Scalars['AWSDateTime']['output'];
  /**
   * QC / form checklist responses recorded at this step (mutable side table
   * step_run_custom_fields; keeps step_runs append-only). Null until first
   * saved via updateStepRunCustomFields.
   */
  customFields?: Maybe<Scalars['AWSJSON']['output']>;
  endedAt?: Maybe<Scalars['AWSDateTime']['output']>;
  failureDetails?: Maybe<Scalars['AWSJSON']['output']>;
  failureReasonCode?: Maybe<FailureReasonCode>;
  /** Failure code (set on failed/loopback runs). */
  failureReasonCodeId?: Maybe<Scalars['ID']['output']>;
  id: Scalars['ID']['output'];
  /** Files consumed by this step run. */
  inputFiles: Array<File>;
  loopbackFromStepRun?: Maybe<StepRun>;
  /** Originating step run if this run was created via loopback. */
  loopbackFromStepRunId?: Maybe<Scalars['ID']['output']>;
  /** Human-readable loopback context. */
  loopbackReasonSummary?: Maybe<Scalars['String']['output']>;
  /**
   * Material lots consumed on THIS attempt (ENG-1550).
   *
   * `material_consumption` (db/migrations/0242) has been write-only from the graph:
   * MaterialConsumptionInput went in on completion and nothing read it back, so the
   * lot a technician recorded at Sinter was captured for traceability and visible
   * nowhere — useless for the one question it exists to answer, which is "a crown
   * came back defective, which lot was it?".
   *
   * Keyed on the ATTEMPT, like `customFields` and `checklistAuthorship`. step_runs
   * is append-only, so a reworked step has one set of rows per attempt, and the lot
   * used on the attempt that FAILED stays distinguishable from the one that passed.
   *
   * INTERNAL ONLY: a lot number is supply-chain data about the lab's own inventory.
   * Non-internal callers get an empty list rather than an error, so a portal
   * selection set still renders.
   */
  materialConsumption: Array<MaterialConsumptionRecord>;
  /**
   * ENG-3292 — the OPTIONAL, user-selected material lots captured on THIS
   * attempt (the is_optional = true lane of step_run_material_lots): ad-hoc lots for items that are
   * NOT part of the step's configured consumed-inventory (those read back through
   * consumptionRules.capturedLot). Keyed on the attempt like materialConsumption,
   * so the optional lot used on a failed attempt stays distinguishable.
   *
   * INTERNAL ONLY: a lot number is supply-chain data about the lab's own inventory.
   * Non-internal callers get an empty list rather than an error, so a portal
   * selection set still renders. Reuses StepRunMaterialLot; the human-readable item
   * name/code is resolved client-side from inventoryItemId.
   */
  optionalMaterialLots: Array<StepRunMaterialLot>;
  order?: Maybe<Order>;
  /** Owning order (always set, for order-level + product-level convenience). */
  orderId: Scalars['ID']['output'];
  organizationId: Scalars['ID']['output'];
  /** Final outcome (set when run is terminal). */
  outcome?: Maybe<StepRunOutcome>;
  /** Files produced by this step run. */
  outputFiles: Array<File>;
  pipelineRun?: Maybe<PipelineRun>;
  /** Owning pipeline run (null for order-level steps). */
  pipelineRunId?: Maybe<Scalars['ID']['output']>;
  /** The superseded prior attempt when this run was created by reassigning (or self-releasing) an in-progress step — the ENG-148 fresh-attempt lineage. Null for initial assignments and ready-step reassignments. */
  reassignedFromStepRunId?: Maybe<Scalars['ID']['output']>;
  /**
   * ENG-2293 — where reporting a defect on this step will actually send the
   * unit, and who will get it. Null when the step run is out of the caller's org.
   *
   * Resolved from the SAME matcher the engine routes with
   * (@clearpath/pipeline-routing `matchFailureRule`), so the preview and the
   * routing cannot disagree. `failureReasonCodeId` is optional: with none, the
   * answer is what an UNCLASSIFIED failure would do — the step's catch-all rework
   * if it has one. An id that resolves to nothing in the caller's org returns
   * `matches: false` rather than falling back to the catch-all, because showing a
   * destination for a reason that does not exist is worse than showing none.
   *
   * `conditional: true` means the matching rework is GUARDED, so whether it fires
   * depends on order data the engine reads at failure time; the destination and
   * policy are still that rule's. Evaluating the guard here would require a second
   * copy of the engine's predicate-context builder, so this reports rather than
   * asserts.
   */
  reworkPreview?: Maybe<ReworkPreview>;
  /**
   * ENG-2554 - why this attempt was SKIPPED, in the skipper's own words.
   *
   * Set only on an attempt with `outcome: SKIPPED`; null everywhere else. The
   * mutation requires it (10-character floor, the same as its loopback /
   * send-back siblings), so a skipped attempt always carries one: a step that was
   * not performed has to say why, or the unit's record is a step short with no
   * explanation.
   *
   * Free text rather than a code, deliberately. A defect picks from the tenant's
   * `failure_reason_codes` taxonomy because defects recur and get counted; a skip
   * is a one-off judgement about THIS unit, and a dropdown would either sit empty
   * or quietly turn skipping into a routine.
   */
  skipReason?: Maybe<Scalars['String']['output']>;
  startedAt?: Maybe<Scalars['AWSDateTime']['output']>;
  /** Current run status. */
  status: StepRunStatus;
  stepDefinition?: Maybe<PipelineStep>;
  /** Step definition this run executes. */
  stepDefinitionId: Scalars['ID']['output'];
  tenantId: Scalars['ID']['output'];
  /**
   * ENG-587 — operator time entries (scan-in / scan-out labor) recorded on this
   * step run, newest scan-in first. Each entry is one operator's session: who
   * worked it, when they scanned in (`scannedInAt`), and when they scanned out
   * (`scannedOutAt`, null while still on the clock). Lives in the mutable side
   * table step_run_time_entries (step_runs stays append-only).
   *
   * NULLABLE by necessity, not preference — same as `vendorDelay` /
   * `checklistRequirements`. This is a field-resolver hop AppSync dispatches as its
   * own Lambda invocation, so a StepRun-returning MUTATION payload never populates
   * it. Declaring the list non-null would make every StepRun-returning mutation
   * responsible for a field it does not compute (the SDL conformance guard flags
   * it, and it reaches production as `Cannot return null for non-nullable type`).
   * The resolver always returns a list, so a client selecting it gets a well-formed
   * array; it is absent only when nothing resolved it.
   */
  timeEntries?: Maybe<Array<StepRunTimeEntry>>;
  updatedAt?: Maybe<Scalars['AWSDateTime']['output']>;
  /**
   * ENG-40 — the ACTIVE (unresolved) vendor/outsourcer delay flag on this step,
   * or null when the outsourced step is not currently flagged as delayed. Resolved
   * flags are history and do not appear here. Populated only for outsourced steps
   * (dispatched to a vendor, `pending_vendor`, or an external-vendor step type).
   */
  vendorDelay?: Maybe<StepRunVendorDelay>;
  vendorExternalRef?: Maybe<Scalars['String']['output']>;
  vendorFederationLink?: Maybe<FederationLink>;
  /** Federation link if dispatched to a vendor. */
  vendorFederationLinkId?: Maybe<Scalars['ID']['output']>;
  vendorOverrideId?: Maybe<Scalars['ID']['output']>;
};


/**
 * Append-only execution record for a step.
 * Per docs/clearpath-architecture/03-data-model.md tier 3 (`step_runs`).
 */
export type StepRunCommentsArgs = {
  page?: InputMaybe<PageInput>;
};


/**
 * Append-only execution record for a step.
 * Per docs/clearpath-architecture/03-data-model.md tier 3 (`step_runs`).
 */
export type StepRunReworkPreviewArgs = {
  failureReasonCodeId?: InputMaybe<Scalars['ID']['input']>;
};

/** Generic step run action payload. */
export type StepRunActionPayload = {
  __typename?: 'StepRunActionPayload';
  order?: Maybe<Order>;
  stepRun: StepRun;
};

/** ENG-3922 — per-step capability flags (Query.stepRunCapabilities). */
export type StepRunCapabilities = {
  __typename?: 'StepRunCapabilities';
  /**
   * Assign or reassign to someone (manualAssignStep): assignments.write, and
   * the step's unit in your scope — one of the units your role applies in or a
   * unit below them; a whole-lab role passes for every unit.
   */
  assign: Scalars['Boolean']['output'];
  /** Complete (and its forms): steps.complete here, and yours or you hold the override. */
  complete: Scalars['Boolean']['output'];
  /** Report defect: steps.fail here, and yours or the override. */
  fail: Scalars['Boolean']['output'];
  /** Rework: steps.loopback here, and yours or the override (the target may narrow it). */
  loopback: Scalars['Boolean']['output'];
  /** Flag or resolve a vendor delay: steps.reassign here. */
  manageVendorDelay: Scalars['Boolean']['output'];
  /** Release to pool: your own step, its unit in your scope. */
  releaseToPool: Scalars['Boolean']['output'];
  /** Skip: steps.skip here, and yours or the override. */
  skip: Scalars['Boolean']['output'];
  stepRunId: Scalars['ID']['output'];
  /**
   * Assign to me: live, ready, unassigned, not automatic, not held, and all
   * three of: clearpath.steps.assign on the step (permission); a member of the
   * step's department, by a department-bound assignment — whole lab never counts
   * (membership); and holding the step's required skills, machine
   * authorizations and unexpired certifications (skills). Same answer as
   * Query.selfAssignableStepRuns.
   */
  take: Scalars['Boolean']['output'];
};

/** One checklist applying to a step run (ENG-27). */
export type StepRunChecklist = {
  __typename?: 'StepRunChecklist';
  /** The binding row that attached this checklist to the step. */
  bindingId: Scalars['ID']['output'];
  formDefinitionId: Scalars['ID']['output'];
  /** Form name, for labelling the checklist in the UI. */
  formName: Scalars['String']['output'];
  /** Whether this checklist gates completion. */
  isRequired: Scalars['Boolean']['output'];
  /**
   * Required field names contributed by THIS checklist. The step-level union is
   * `StepRunChecklistRequirements.requiredFieldNames`.
   */
  requiredFieldNames: Array<Scalars['String']['output']>;
  /**
   * ENG-1554 — the bound form's published schema (live version, else the parent
   * shadow), so the STAFF-ONLY completion-gate panel can evaluate this checklist's
   * form RULES (range/minItems, error/forbid effects) pre-click with
   * @clearpath/form-rules — the SAME rules the server's `checklistRuleViolations`
   * enforces over every required binding, so a rule-violating-but-fields-filled
   * checklist blocks Complete on the client too.
   *
   * INTERNAL-ONLY: nulled for portal (customer) and vendor actors at the resolver
   * (`ctx.userType !== 'internal'`), preserving ENG-216's rule that internal QC/step
   * form DEFINITIONS are not exposed to non-internal actors. Only `schema` is gated
   * — `formName` / `requiredFieldNames` / `isRequired` / `bindingId` are unchanged
   * and still reach portal via Order.stepRuns. Also null when the binding's form or
   * its schema could not be resolved.
   */
  schema?: Maybe<Scalars['AWSJSON']['output']>;
};

/**
 * The resolved checklist obligation for one step run (ENG-27).
 *
 * Resolved per-order: a binding scoped to a product type and/or customer party
 * only appears here when it applies to the order this step run belongs to. When
 * several apply, `requiredFieldNames` is their union — a field required by any
 * applicable checklist is required.
 */
export type StepRunChecklistRequirements = {
  __typename?: 'StepRunChecklistRequirements';
  /** The applicable checklists, required ones first. */
  checklists: Array<StepRunChecklist>;
  /**
   * True when at least one applicable checklist would gate completion under
   * the PR 3/5 gate. Cheaper for a client than testing `requiredFieldNames` for
   * emptiness, and unambiguous: a required checklist whose form happens to mark
   * no field required is still a checklist, but it does not gate.
   */
  isGated: Scalars['Boolean']['output'];
  /**
   * Union of required field names across every applicable REQUIRED checklist —
   * precisely the set the server-side completion gate WILL enforce once PR 3/5
   * wires it in. Empty means the step is not gated by any binding. In this slice
   * the union is computed and returned but no completion path reads it.
   */
  requiredFieldNames: Array<Scalars['String']['output']>;
  /** The step run these requirements were resolved for. */
  stepRunId: Scalars['ID']['output'];
};

/**
 * ENG-1554 — the completion VERDICT for one step run, safe to select on
 * list/board queries because it carries NO answer values or lot numbers. Derived
 * from the SAME server gate logic the completion path enforces (see
 * StepRun.completionReadiness for the full caveat about saved-vs-live state).
 */
export type StepRunCompletionReadiness = {
  __typename?: 'StepRunCompletionReadiness';
  /**
   * Which gate categories are blocking, for a compact button reason. Empty when
   * not blocked.
   */
  blockingKinds: Array<StepGateKind>;
  /**
   * True when a PASSED completion would currently be refused by the server gate
   * (checklist/step-form incomplete). Reflects SAVED state only — see the
   * StepRun.completionReadiness caveat.
   */
  isBlocked: Scalars['Boolean']['output'];
  /** The step run this verdict was resolved for. */
  stepRunId: Scalars['ID']['output'];
};

export type StepRunConnection = {
  __typename?: 'StepRunConnection';
  edges: Array<StepRunEdge>;
  pageInfo: PageInfo;
  totalCount: Scalars['Int']['output'];
};

/**
 * ENG-1689 — one inventory item a step attempt is configured to consume, with
 * the lot read back once the attempt is completed. The per-StepRun, version-pinned
 * read analogue of PipelineConsumptionRule (the pipeline-editor config read).
 * Config from `pipeline_step_consumed_inventory` (migration 0252); `capturedLot`
 * from `step_run_material_lots` (migration 0396).
 */
export type StepRunConsumptionRule = {
  __typename?: 'StepRunConsumptionRule';
  /**
   * The lot captured for this item on the COMPLETED attempt, or null on a live
   * (not-yet-completed) attempt. When the material was split across several lots
   * this is the first; read `capturedLots` for all of them.
   */
  capturedLot?: Maybe<StepRunMaterialLot>;
  /**
   * Every lot captured for this item on the COMPLETED attempt — more than one
   * when a line of identical units ran through several lots of the material.
   * Empty on a live attempt.
   */
  capturedLots: Array<StepRunMaterialLot>;
  /** The inventory item this rule consumes. */
  inventoryItemId: Scalars['ID']['output'];
  /** The item's SKU / code (from inventory_items). */
  itemCode: Scalars['String']['output'];
  /** The item's display name (from inventory_items). */
  itemName: Scalars['String']['output'];
  /**
   * quantityPerUnit × order_products.quantity for this run, as a NUMERIC(18,4)
   * decimal string. quantity is computed from `order_products.quantity` on the row,
   * not assumed; ROW=Unit is enforced at the app layer.
   */
  quantityForRun: Scalars['String']['output'];
  /**
   * Canonical NUMERIC(18,4) decimal string — quantity consumed per produced
   * unit, matching PipelineConsumptionRule.quantityPerUnit.
   */
  quantityPerUnit: Scalars['String']['output'];
  /** When this rule's consumption fires (ON_COMPLETE / ON_SCAN_PICK). */
  triggerMode: ConsumptionTriggerMode;
};

export type StepRunEdge = {
  __typename?: 'StepRunEdge';
  cursor: Scalars['String']['output'];
  node: StepRun;
};

/** Filter for step runs. */
export type StepRunFilterInput = {
  assigneeIds?: InputMaybe<Array<Scalars['ID']['input']>>;
  endedAt?: InputMaybe<DateRangeInput>;
  /**
   * ENG-40 — when true, restrict to outsourced steps that currently carry an
   * ACTIVE (unresolved) vendor delay flag. This is the delayed-outsourced-work
   * list surface.
   */
  hasActiveVendorDelay?: InputMaybe<Scalars['Boolean']['input']>;
  hasFailure?: InputMaybe<Scalars['Boolean']['input']>;
  orderIds?: InputMaybe<Array<Scalars['ID']['input']>>;
  orgUnitIds?: InputMaybe<Array<Scalars['ID']['input']>>;
  outcome?: InputMaybe<Array<StepRunOutcome>>;
  pipelineRunIds?: InputMaybe<Array<Scalars['ID']['input']>>;
  startedAt?: InputMaybe<DateRangeInput>;
  status?: InputMaybe<Array<StepRunStatus>>;
  stepDefinitionIds?: InputMaybe<Array<Scalars['ID']['input']>>;
};

/**
 * ENG-1689 — a per-unit material lot captured at a step completion (migration
 * 0396 `step_run_material_lots`). Recall-grade traceability: which concrete
 * inventory item + lot went into the produced unit. `quantity` is informational
 * only and never affects on-hand stock (inventory_transactions owns balances).
 * Deliberately does not expose organization_id.
 */
export type StepRunMaterialLot = {
  __typename?: 'StepRunMaterialLot';
  /** When the lot was captured (at completeStepRun). */
  capturedAt: Scalars['AWSDateTime']['output'];
  /**
   * The completing user who captured the lot; null if unknown or later
   * removed.
   */
  capturedByUserId?: Maybe<Scalars['ID']['output']>;
  id: Scalars['ID']['output'];
  inventoryItemId: Scalars['ID']['output'];
  /** The captured lot number (non-blank). */
  lotNumber: Scalars['String']['output'];
  /**
   * Canonical NUMERIC(18,4) decimal string; null when the capture omitted it.
   * Informational only — never affects on-hand stock.
   */
  quantity?: Maybe<Scalars['String']['output']>;
  /** The completed step_run row this lot was captured against. */
  stepRunId: Scalars['ID']['output'];
};

/**
 * Step run terminal outcomes.
 *
 * ENG-171: REJECTED was removed 2026-07 — the DB CHECK constraint on
 * step_runs.outcome (db/migrations/0003) never allowed it, so completeStepRun
 * 500'd for any caller that sent it. Grepped every client (apps/main,
 * apps/portal) and found zero producers/consumers of REJECTED — it was
 * SDL-only, dead surface, not a real outcome the product uses. Removed rather
 * than widening the CHECK constraint, to avoid inventing new business
 * semantics (when a step is "rejected" vs "failed") with no product decision
 * behind it.
 *
 * ENG-2554: SKIPPED added, with the matching DB CHECK widening in
 * db/migrations/0454_step_runs_skip_outcome.sql - the two are independently
 * closed lists and outcome-sdl-db-parity.test.ts fails the build if they drift,
 * which is the whole point of the REJECTED story above.
 *
 * SKIPPED is the outcome of `skipStepRun`: the attempt is terminal
 * (`status: COMPLETED`, so every "is this step still in flight" reader stays
 * right by construction) but the work was deliberately NOT performed. Read
 * `outcome`, not `status`, to tell a step that was done from one that was
 * waived - and `skipReason` for why.
 */
export enum StepRunOutcome {
  Cancelled = 'CANCELLED',
  Failed = 'FAILED',
  Passed = 'PASSED',
  Skipped = 'SKIPPED',
  Superseded = 'SUPERSEDED'
}

/** Step run lifecycle states. */
export enum StepRunStatus {
  Cancelled = 'CANCELLED',
  Completed = 'COMPLETED',
  Failed = 'FAILED',
  InProgress = 'IN_PROGRESS',
  OnHold = 'ON_HOLD',
  Pending = 'PENDING',
  PendingVendor = 'PENDING_VENDOR',
  Ready = 'READY',
  Superseded = 'SUPERSEDED'
}

/**
 * ENG-587 — one operator time entry (scan-in / scan-out) on a step run. Lives
 * in the mutable side table `step_run_time_entries` (step_runs stays append-only).
 * `isOpen` is true while `scannedOutAt` is null; `durationSeconds` is null until
 * the entry is closed.
 */
export type StepRunTimeEntry = {
  __typename?: 'StepRunTimeEntry';
  createdAt: Scalars['AWSDateTime']['output'];
  /** Labor time in whole seconds (scannedOutAt - scannedInAt); null while open. */
  durationSeconds?: Maybe<Scalars['Int']['output']>;
  id: Scalars['ID']['output'];
  /** True while the operator is still on the clock (scannedOutAt is null). */
  isOpen: Scalars['Boolean']['output'];
  /** Display name of the operator, for labelling; null if unknown. */
  operatorName?: Maybe<Scalars['String']['output']>;
  /** The operator this labor time is attributed to. */
  operatorUserId: Scalars['ID']['output'];
  organizationId: Scalars['ID']['output'];
  /** When the operator scanned in / started work on this step. */
  scannedInAt: Scalars['AWSDateTime']['output'];
  /** When the operator scanned out / the step was completed; null while open. */
  scannedOutAt?: Maybe<Scalars['AWSDateTime']['output']>;
  /** How the entry was captured: 'scan' (barcode-wedge) or 'board'. */
  source: Scalars['String']['output'];
  stepRunId: Scalars['ID']['output'];
  updatedAt: Scalars['AWSDateTime']['output'];
};

/**
 * ENG-40 — a vendor/outsourcer delay flag on an outsourced step run. Lives in
 * the mutable side table `step_run_vendor_delays` (step_runs stays append-only).
 * `isActive` is true while `resolvedAt` is null.
 */
export type StepRunVendorDelay = {
  __typename?: 'StepRunVendorDelay';
  createdAt: Scalars['AWSDateTime']['output'];
  /** Optional revised expected-resolution time the vendor gave; null if unknown. */
  expectedResolutionAt?: Maybe<Scalars['AWSDateTime']['output']>;
  id: Scalars['ID']['output'];
  /** True while unresolved (resolvedAt is null). */
  isActive: Scalars['Boolean']['output'];
  organizationId: Scalars['ID']['output'];
  raisedAt: Scalars['AWSDateTime']['output'];
  /** Display name of the raiser, for labelling; null if unknown. */
  raisedByName?: Maybe<Scalars['String']['output']>;
  /** User who raised the flag (null if that user was later removed). */
  raisedByUserId?: Maybe<Scalars['ID']['output']>;
  /** Why the outsourced step is delayed (free-form; required). */
  reason: Scalars['String']['output'];
  /** Optional note captured at resolution. */
  resolutionNote?: Maybe<Scalars['String']['output']>;
  /** When the flag was resolved; null while active. */
  resolvedAt?: Maybe<Scalars['AWSDateTime']['output']>;
  resolvedByName?: Maybe<Scalars['String']['output']>;
  resolvedByUserId?: Maybe<Scalars['ID']['output']>;
  stepRunId: Scalars['ID']['output'];
  updatedAt: Scalars['AWSDateTime']['output'];
};

/** Step scope discriminator. */
export enum StepScope {
  Order = 'ORDER',
  Product = 'PRODUCT'
}

/**
 * API-01 — one assignee's completed-step-run throughput over the requested
 * window. `completedCount` is the number of step_runs that reached the
 * `completed` state and were attributed to this assignee (step_runs.assignee_id)
 * within the range, keyed on each run's completion timestamp.
 */
export type StepThroughputByAssignee = {
  __typename?: 'StepThroughputByAssignee';
  /** users.id of the assignee (a step_runs.assignee_id). */
  assigneeId: Scalars['ID']['output'];
  /** Display name (users.full_name); null if the assignee row was removed. */
  assigneeName?: Maybe<Scalars['String']['output']>;
  /** Completed step_runs attributed to this assignee in the window. */
  completedCount: Scalars['Int']['output'];
};

/**
 * API-01 — connection of per-assignee completed-step-run throughput, most
 * productive first. Bounded page size + offset (millions-scale mandate).
 */
export type StepThroughputByAssigneeConnection = {
  __typename?: 'StepThroughputByAssigneeConnection';
  edges: Array<StepThroughputByAssigneeEdge>;
  pageInfo: PageInfo;
  /** Distinct assignees with at least one completed step_run in the window. */
  totalCount: Scalars['Int']['output'];
};

export type StepThroughputByAssigneeEdge = {
  __typename?: 'StepThroughputByAssigneeEdge';
  cursor: Scalars['String']['output'];
  node: StepThroughputByAssignee;
};

/** Step execution kind discriminator. */
export enum StepType {
  Automatic = 'AUTOMATIC',
  ExternalHardware = 'EXTERNAL_HARDWARE',
  ExternalVendor = 'EXTERNAL_VENDOR',
  Internal = 'INTERNAL'
}

export type StockMovementPayload = {
  __typename?: 'StockMovementPayload';
  /**
   * True only when this movement took on-hand TO OR BELOW the reorder point.
   * Inclusive at the boundary: an item with a reorder point of 10 reports true on
   * the movement that lands it on 10, not only below it. Edge-triggered — a later
   * movement while already at or below reports false.
   */
  crossedReorderPoint: Scalars['Boolean']['output'];
  item?: Maybe<InventoryItem>;
  newQuantity: Scalars['String']['output'];
  previousQuantity: Scalars['String']['output'];
};

/** Input for submitApprovalDecision. */
export type SubmitApprovalDecisionInput = {
  decision: ApprovalDecision;
  /** The open `AWAITING_CUSTOMER_APPROVAL` hold being actioned. */
  holdId: Scalars['ID']['input'];
  /**
   * Required for REQUEST_CHANGES (>= 10 chars — the change routed back to the
   * lab, may carry a review annotation deep-link); optional note for APPROVE.
   */
  note?: InputMaybe<Scalars['String']['input']>;
};

/** Input for submitOrder. */
export type SubmitOrderInput = {
  billingPartyId?: InputMaybe<Scalars['ID']['input']>;
  customFields?: InputMaybe<Scalars['AWSJSON']['input']>;
  customerPartyId: Scalars['ID']['input'];
  /**
   * ENG-1750 — the server-stored draft (order_drafts) this submit is being
   * promoted from, if any. The draft is deleted (self + org scoped) on the SAME
   * transaction as the order+products insert, so the cleanup commits or rolls back
   * atomically with the order. Two distinct outcomes, deliberately different:
   * a draftId that matches zero owned rows (malformed / stale / already-gone /
   * foreign / non-owned) is a silent no-op — the order still commits and that
   * draft is left for the 30-day TTL sweep; a genuine DB error on the delete
   * PROPAGATES and rolls the whole order transaction back (fail-closed, never a
   * half-written order). Stripped from the idempotency fingerprint alongside
   * idempotencyKey, so two retries that differ only in draftId still hash
   * identically.
   */
  draftId?: InputMaybe<Scalars['ID']['input']>;
  /**
   * ENG-5 C4 — client-supplied idempotency key. A repeat submitOrder with the
   * same key returns the original order instead of creating a second one.
   */
  idempotencyKey?: InputMaybe<Scalars['ID']['input']>;
  intakeFiles?: InputMaybe<Array<Scalars['ID']['input']>>;
  /**
   * Track 2 (2026-04-30) — answers to the order-level intake form
   * (tenant_settings.orderIntakeFormDefinition.schema). Empty / omitted
   * when the tenant has not configured an order-level form, in which case
   * the wizard skipped Step 1. Persisted to orders.intake_form_response.
   */
  intakeFormResponse?: InputMaybe<Scalars['AWSJSON']['input']>;
  notes?: InputMaybe<Scalars['String']['input']>;
  orderPipelineId?: InputMaybe<Scalars['ID']['input']>;
  orderType?: InputMaybe<OrderType>;
  /**
   * ENG-461 — the specific ordering practitioner sub-party this order is placed
   * FOR, distinct from the (often shared) customerPartyId. Server-validated (exists
   * in-org, practitioner party_type, a descendant of customerPartyId in the party
   * closure). REQUIRED when a staff/admin portal session submits on behalf; implicit
   * (self) for a leaf-practitioner portal session; NULL for internal/scanner
   * intakes. Drives per-practitioner portal case visibility (db/rls/0058).
   */
  orderingPractitionerPartyId?: InputMaybe<Scalars['ID']['input']>;
  organizationId: Scalars['ID']['input'];
  /**
   * ENG-1263 — the org_unit id of the SITE this order is physically received at
   * (the ORIGIN of route-to-site). Server-validated: must be an ACTIVE site in-org
   * that accepts intake, else the submit is refused. Stamped onto
   * orders.origin_site_id. Omitted for a single-site org — the intake executor's
   * resolveOriginSite() then falls back to the org's sole active intake site.
   */
  originSiteId?: InputMaybe<Scalars['ID']['input']>;
  parentOrderId?: InputMaybe<Scalars['ID']['input']>;
  /**
   * ENG-658 — set directly on the wizard's Details step, using the same
   * OrderPriority enum updateOrder/bulkSetOrderPriority already use. Persisted
   * to orders.customFields.priority. Omitted defaults to no explicit priority
   * (the order detail page's inline picker still shows/edits it afterward).
   */
  priority?: InputMaybe<OrderPriority>;
  products: Array<SubmitOrderProductInput>;
  relatedOrderId?: InputMaybe<Scalars['ID']['input']>;
  /**
   * ENG-2350 — the customer-requested due date chosen on the wizard's Details
   * step. Additive/optional at the schema level for back-compat, but the wizard
   * enforces it as REQUIRED: without it the order stalls at intake because the
   * first step's due-date gate (ENG-991) refuses to complete. On a non-scanner
   * submit this is normalized (parseIntakeDate) and promoted to
   * orders.customFields.dueDate (and estimated_completion_at); on a scanner
   * handoff (scannerIngestId set) it is ignored — the lab confirms the iTero
   * requested date on the created order instead.
   */
  requestedDueDate?: InputMaybe<Scalars['AWSDate']['input']>;
  /**
   * Internal IOS Intake handoff. When set, submitOrder locks this staged
   * scanner case, validates the explicitly selected customer/practitioner
   * relationship, and links the ingest plus stored scanner files atomically.
   * A saved identity mapping is only an editable suggestion; this never selects
   * a product or bypasses canonical form/catalog validation.
   */
  scannerIngestId?: InputMaybe<Scalars['ID']['input']>;
  shipToPartyId?: InputMaybe<Scalars['ID']['input']>;
  /**
   * ENG-3286 — omit to take the customer's default shipping terms. Lab staff
   * only; a value different from the default needs
   * clearpath.orders.shipping_terms.override.
   */
  shippingTerms?: InputMaybe<OrderShippingTermsInput>;
};

/** Payload for submitOrder. */
export type SubmitOrderPayload = {
  __typename?: 'SubmitOrderPayload';
  order: Order;
  warnings: Array<Scalars['String']['output']>;
};

/** Line item input for submitOrder. */
export type SubmitOrderProductInput = {
  bundleComponentId?: InputMaybe<Scalars['ID']['input']>;
  /**
   * ENG-8 — bundle provenance. Set (all three together) only on lines the wizard
   * expanded from a product bundle. The server groups submitted lines by
   * bundleInstanceId and validates each group against the LIVE bundle definition
   * (fail-closed anti-tamper); omitting all three leaves the line an ordinary
   * independent item.
   */
  bundleId?: InputMaybe<Scalars['ID']['input']>;
  bundleInstanceId?: InputMaybe<Scalars['ID']['input']>;
  customFields?: InputMaybe<Scalars['AWSJSON']['input']>;
  intakeFormResponse?: InputMaybe<Scalars['AWSJSON']['input']>;
  productTypeId: Scalars['ID']['input'];
  quantity: Scalars['Int']['input'];
};

export type Subscription = {
  __typename?: 'Subscription';
  onAssignWorkInvalidated?: Maybe<WorkInvalidationSignal>;
  onDepartmentWorkInvalidated?: Maybe<WorkInvalidationSignal>;
  onMyWorkInvalidated?: Maybe<WorkInvalidationSignal>;
  /**
   * Notification creation stream. ENG-2184: the recipient filter is derived
   * server-side from the verified session (tenant + org + user), never from an
   * argument — the api Lambda resolver refuses if any argument is supplied.
   * Field-level @aws_oidc (parity with onMyWorkInvalidated) excludes Cognito
   * user-pool subscribers at the AppSync layer, so the direct-OIDC staff contract
   * is the only admission — a second guard behind the fail-closed resolver template
   * if a binding/template is ever absent.
   */
  onNotificationCreated?: Maybe<NotificationPayload>;
};


export type SubscriptionOnAssignWorkInvalidatedArgs = {
  routes: Array<Scalars['String']['input']>;
};


export type SubscriptionOnDepartmentWorkInvalidatedArgs = {
  routes: Array<Scalars['String']['input']>;
};

export type SuggestImprovementResult = {
  __typename?: 'SuggestImprovementResult';
  suggestions: Array<Suggestion>;
  tokensUsed: TokenUsage;
};

export type Suggestion = {
  __typename?: 'Suggestion';
  category: Scalars['String']['output'];
  description: Scalars['String']['output'];
  severity: Scalars['String']['output'];
  suggestedYamlDiff?: Maybe<Scalars['String']['output']>;
  title: Scalars['String']['output'];
};

/**
 * The card surcharge the payer was shown before paying (ENG-3881). The
 * server recomputes it from the amount being charged and refuses a mismatch with
 * `ValidationError` before contacting the provider.
 */
export type SurchargeDisclosureInput = {
  /** Surcharge amount shown (decimal string, at most 2 places). */
  amount: Scalars['String']['input'];
  /** Rate shown, in basis points. */
  rateBps: Scalars['Int']['input'];
};

export type SuspendTenantInput = {
  id: Scalars['ID']['input'];
  reason: Scalars['String']['input'];
};

/**
 * Per-currency totals over the whole window.
 *
 * Computed by their OWN aggregate rather than by re-adding the customer list, so
 * they stay correct when that list is truncated.
 */
export type TaxAnalyticsTotal = {
  __typename?: 'TaxAnalyticsTotal';
  currency: Scalars['String']['output'];
  invoiceCount: Scalars['Int']['output'];
  taxBilled: Scalars['String']['output'];
  taxableBase: Scalars['String']['output'];
  taxedInvoiceCount: Scalars['Int']['output'];
};

/** Tax billed per customer over a window. ENG-2497 slice 8. */
export type TaxByCustomerReport = {
  __typename?: 'TaxByCustomerReport';
  asOf: Scalars['AWSDateTime']['output'];
  /**
   * Largest tax contributor first. Empty is a real state — a lab with no nexus
   * anywhere collects nothing from anyone.
   */
  customers: Array<TaxCustomerTax>;
  /**
   * Always LIVE until FIN-M6 slice 5 wires the period-close snapshot. LIVE
   * means PROVISIONAL — a credit note raised after a period ends changes what that
   * period's tax should have been.
   */
  periodStatus: RevenuePeriodStatus;
  rangeEnd: Scalars['AWSDateTime']['output'];
  /** Echo of the requested window, inclusive both ends. */
  rangeStart: Scalars['AWSDateTime']['output'];
  totals: Array<TaxAnalyticsTotal>;
  /** True when more customers exist than were returned. Never a silent cut. */
  truncated: Scalars['Boolean']['output'];
};

/** One row per invoice over a window, newest first. ENG-2497 slice 8. */
export type TaxByInvoiceReport = {
  __typename?: 'TaxByInvoiceReport';
  asOf: Scalars['AWSDateTime']['output'];
  invoices: Array<TaxInvoiceTax>;
  periodStatus: RevenuePeriodStatus;
  rangeEnd: Scalars['AWSDateTime']['output'];
  rangeStart: Scalars['AWSDateTime']['output'];
  /**
   * True when more invoices exist than were returned. Never a silent cut — a
   * truncated list of under-collecting invoices reads as a complete one.
   */
  truncated: Scalars['Boolean']['output'];
};

/**
 * One reference-data defect found TODAY, independent of any invoice.
 *
 * This is the half that catches the inversion trap BEFORE it bills, and it is the
 * half to trust when the two disagree: it depends on no message format and needs
 * nothing to have been billed to be true.
 */
export type TaxConfigurationAlert = {
  __typename?: 'TaxConfigurationAlert';
  /**
   * How many offending things. The UNIT differs per key and the headline says
   * which: areas for `NEXUS_GAP`, rate generations for `RATE_OVERLAP`, billing
   * events for `MIXED_TAX_EXEMPT`.
   */
  count: Scalars['Int']['output'];
  /** What it COSTS and what to do about it. */
  detail: Scalars['String']['output'];
  /** What is wrong, already pluralised for `count`. */
  headline: Scalars['String']['output'];
  /** Stable machine key, e.g. `NEXUS_GAP`. */
  key: Scalars['String']['output'];
  /**
   * Up to five examples — codes, or names, depending on the check. Deduped, so
   * fewer samples than `count` is normal and not a bug.
   */
  samples: Array<Scalars['String']['output']>;
  severity: TaxHealthSeverity;
};

/**
 * *"96 ZIPs covered; everything else at 7.25%."* One row per region the
 * organization can resolve addresses in.
 *
 * ⚠️ A ROLL-UP, NOT AN ALERT. `postalBoundaryCount` attributes a postal
 * jurisdiction to a region by its CODE, because no column records that link — a
 * postal boundary cannot carry a region code (the schema makes the two shapes
 * mutually exclusive) and the write path does not populate a parent. A lab whose
 * rows arrived by another route with another code shape shows 0 here rather than a
 * wrong number. Nothing branches on this.
 */
export type TaxCoverageSummary = {
  __typename?: 'TaxCoverageSummary';
  country: Scalars['String']['output'];
  /**
   * The rate an address in this region gets when it is NOT listed, as a
   * six-place fraction. NULL means an unlisted address here collects NOTHING —
   * which is a different and worse statement than 0%.
   */
  fallbackRate?: Maybe<Scalars['String']['output']>;
  maxRate?: Maybe<Scalars['String']['output']>;
  /**
   * Lowest / highest GENERAL rate across this organization's own listed postal
   * codes in this region. NULL when it has listed none.
   */
  minRate?: Maybe<Scalars['String']['output']>;
  /** How many postal codes this organization has listed in this region. */
  postalBoundaryCount: Scalars['Int']['output'];
  regionCode: Scalars['String']['output'];
};

/**
 * One (billable party, currency) — who the tax came from.
 *
 * ⚠️ THE PARTY IS THE **BILLABLE** PARTY, NOT THE SHIP-TO PARTY. The engine
 * anchors exemptions on `invoices.billable_party_id`, and so does this grouping,
 * so a practice shipping under a group's billing account appears under the GROUP.
 * That is the same anchor `CustomerTaxExemption` warns about, and the two must
 * agree or the exemption list and the analytics list would name different
 * customers for the same money.
 */
export type TaxCustomerTax = {
  __typename?: 'TaxCustomerTax';
  billablePartyId: Scalars['ID']['output'];
  currency: Scalars['String']['output'];
  /**
   * `taxBilled / taxableBase` as a six-place fraction. NULL when the base is
   * zero: 0/0 has no rate, and reporting 0 would claim this customer is taxed at
   * zero rather than that nothing was taxable.
   */
  effectiveRate?: Maybe<Scalars['String']['output']>;
  /**
   * Detail rows a CERTIFICATE relieved. Not rows that were exempt for some
   * other reason — a zero-rated category and a certificate have completely
   * different repairs, and only one of them is a thing the lab chose.
   */
  exemptLineCount: Scalars['Int']['output'];
  /**
   * Invoices from this customer in the window — INCLUDING the ones that
   * carried no tax at all. The difference between this and `taxedInvoiceCount` is
   * the question "how many transactions carried tax".
   */
  invoiceCount: Scalars['Int']['output'];
  /**
   * The customer's name, or a labelled placeholder when the party row is not
   * readable. Never blank — a blank cell reads as a rendering fault, where
   * "(customer not readable)" is a fact worth seeing.
   */
  partyName: Scalars['String']['output'];
  /**
   * Decimal string, two places. Tax BILLED — invoice basis, not reconciled to
   * cash received.
   */
  taxBilled: Scalars['String']['output'];
  /** Decimal string, two places. The base tax was charged on. */
  taxableBase: Scalars['String']['output'];
  /** How many of those carried a non-zero tax figure. */
  taxedInvoiceCount: Scalars['Int']['output'];
};

/**
 * One address field the tax engine resolves a destination from. All three are
 * required; any one of them missing blocks the invoice.
 */
export enum TaxDestinationField {
  Country = 'COUNTRY',
  PostalCode = 'POSTAL_CODE',
  Region = 'REGION'
}

/**
 * One open order the invoice destination gate will refuse, and why.
 *
 * `missingFields` is decided by the GATE'S OWN validator
 * (services/pricing/src/invoicing/ship_to_tax_address.ts), not by a second rule
 * written for this report — so what is listed here is exactly what the refusal
 * will say. In particular a postal code is judged by the engine's normaliser and
 * not for non-emptiness: a US code with fewer than five digits resolves to no
 * postal tier at all and is reported as missing even though the column is
 * populated.
 */
export type TaxDestinationGap = {
  __typename?: 'TaxDestinationGap';
  /**
   * The address the engine picked, so the repair completes that row in place
   * instead of adding one. Null when nothing resolved.
   */
  addressId?: Maybe<Scalars['ID']['output']>;
  /**
   * False when the party holds no address row at all — a different repair from
   * a row that exists and is incomplete, which is why it is reported
   * separately.
   */
  addressOnFile: Scalars['Boolean']['output'];
  /**
   * Whose row it is: the ship-to party, or the ancestor whose address it
   * inherits. An upsert must name this party, not the ship-to party.
   */
  addressPartyId?: Maybe<Scalars['ID']['output']>;
  city?: Maybe<Scalars['String']['output']>;
  /**
   * The values on the address the engine would have used, for context while
   * repairing.
   */
  country?: Maybe<Scalars['String']['output']>;
  missingFields: Array<TaxDestinationField>;
  orderBarcode?: Maybe<Scalars['String']['output']>;
  orderId: Scalars['ID']['output'];
  orderLifecycleStatus: Scalars['String']['output'];
  orderStatus: Scalars['String']['output'];
  /**
   * Pending, uninvoiced billing events already captured for this order. Above
   * zero means the block is IMMINENT rather than hypothetical: the next automated
   * run will refuse this order.
   */
  pendingBillingEventCount: Scalars['Int']['output'];
  postalCode?: Maybe<Scalars['String']['output']>;
  region?: Maybe<Scalars['String']['output']>;
  shipToPartyId: Scalars['ID']['output'];
  shipToPartyName: Scalars['String']['output'];
  street1?: Maybe<Scalars['String']['output']>;
  street2?: Maybe<Scalars['String']['output']>;
};

export type TaxDestinationGapsPayload = {
  __typename?: 'TaxDestinationGapsPayload';
  gaps: Array<TaxDestinationGap>;
  /**
   * Orders reported in `gaps` — always `gaps.length`.
   *
   * ⚠️ READ IT WITH `truncated`, NEVER ALONE. It is the number of blocked orders
   * only when `truncated` is false; when true it is a FLOOR, short by exactly the
   * orders the row cap cut off, so rendering it as "N orders are blocked" would
   * understate the problem at precisely the scale that made the cap bite.
   *
   * There is deliberately no exact total: counting one would mean running the
   * destination verdict over every open order, which is the unbounded read the
   * cap exists to prevent.
   */
  orderCount: Scalars['Int']['output'];
  /**
   * Distinct ship-to parties across `gaps` — the backfill is per PARTY, not
   * per order, so this is the number of addresses somebody has to enter.
   *
   * A FLOOR when `truncated`, like `orderCount`.
   */
  partyCount: Scalars['Int']['output'];
  /**
   * True when more refusable orders exist than were returned. Never a silent
   * cut.
   */
  truncated: Scalars['Boolean']['output'];
};

/** Customer-safe signal for whether tax was configured at issue time (ENG-1256). */
export enum TaxDisclosure {
  Configured = 'CONFIGURED',
  NotConfigured = 'NOT_CONFIGURED'
}

/**
 * Why a customer is relieved of tax. Mirrors the `exemption_reason` CHECK on
 * `customer_tax_exemptions` (db/migrations/0306).
 *
 * `OTHER` is the DEFAULT rather than an error case: it means "recorded, not
 * classified". Requiring a classification the person entering a certificate may
 * not know pushes them to guess, and a wrong reason on an audited certificate is
 * worse than an honest `OTHER`.
 */
export enum TaxExemptionReason {
  Government = 'GOVERNMENT',
  MedicalDevice = 'MEDICAL_DEVICE',
  Nonprofit = 'NONPROFIT',
  Other = 'OTHER',
  Resale = 'RESALE'
}

/**
 * Whether the engine honours this certificate. Mirrors the `status` CHECK on
 * `customer_tax_exemptions` (db/migrations/0306).
 *
 * ⚠️ ONLY `ACTIVE` SUPPRESSES TAX, and the default is `PENDING` on purpose.
 * `PENDING` means "collected but not yet validated — KEEP CHARGING", because an
 * unverified certificate is the lab's liability and not the customer's.
 *
 * ⚠️ `valid_to` PASSING DOES NOT FLIP THIS FIELD. Nothing sweeps the table, so a
 * stale `ACTIVE` row past its end date still reads `ACTIVE` here — the engine
 * checks BOTH the status and the dates (`compute.ts#findExemption`), so it stops
 * relieving tax while this column still says `ACTIVE`.
 */
export enum TaxExemptionStatus {
  Active = 'ACTIVE',
  Expired = 'EXPIRED',
  Pending = 'PENDING',
  Revoked = 'REVOKED'
}

/**
 * Is this organization's tax collection healthy, and if not, what does it cost?
 * ENG-2497 slice 8.
 *
 * TWO HALVES THAT ANSWER DIFFERENT QUESTIONS:
 *
 *   * `outcomes` — what the engine SAID while billing the invoices in this
 *     window, replayed from the frozen snapshots. Evidence of what already
 *     happened; it can only report a problem AFTER it has cost something.
 *   * `configuration` — what the reference data says TODAY. No invoice required,
 *     so it catches the trap BEFORE it bills. **Trust this half when the two
 *     disagree.**
 */
export type TaxHealthReport = {
  __typename?: 'TaxHealthReport';
  asOf: Scalars['AWSDateTime']['output'];
  configuration: Array<TaxConfigurationAlert>;
  coverage: Array<TaxCoverageSummary>;
  /** Invoices examined in the window — void excluded. */
  invoiceCount: Scalars['Int']['output'];
  outcomes: Array<TaxOutcomeAlert>;
  /**
   * Always LIVE. The outcome half is computed from invoices that can still be
   * credited, so it moves.
   */
  periodStatus: RevenuePeriodStatus;
  rangeEnd: Scalars['AWSDateTime']['output'];
  rangeStart: Scalars['AWSDateTime']['output'];
  /**
   * ⚠️ HOW MANY OF THOSE YIELDED NO READABLE TAX-ENGINE NOTES. Invoices issued
   * before the engine shipped or while it was switched off carry no snapshot at
   * all; a malformed `custom_fields` blob carries one that cannot be read. Both
   * were assessed by nothing, so zero alerts over a window made of these means
   * NOTHING WAS CHECKED, and the page must lead with that rather than with a tick.
   *
   * ⚠️ IT IS NOT THE SAME QUESTION AS `MIXED_TAX_EXEMPT_UNKNOWN`. This counts
   * invoices the OUTCOME half could not read at all. That configuration alert
   * counts invoices that were fully assessed by the engine of their day but
   * predate one specific field. An invoice can be in the second and not the
   * first.
   */
  snapshotMissingCount: Scalars['Int']['output'];
  /**
   * The worst severity across BOTH lists, or `OK` when both are empty. Read
   * `snapshotMissingCount` before believing `OK`.
   */
  status: TaxHealthStatus;
};

/**
 * How much an item on the tax health panel costs.
 *
 * `CRITICAL` on this panel has ONE meaning: **you are silently collecting nothing
 * you are obliged to collect.** It is reserved for that, so it keeps meaning it.
 * `WARN` is a real wrong number that is bounded or recoverable. `INFO` is working
 * as intended, reported so the volume is visible.
 */
export enum TaxHealthSeverity {
  Critical = 'CRITICAL',
  Info = 'INFO',
  Warn = 'WARN'
}

/**
 * The single roll-up the page leads with.
 *
 * A SEPARATE enum from `TaxHealthSeverity` on purpose: an individual alert can
 * never be `OK`, and folding the two would let a caller render "OK" beside a
 * finding.
 *
 * ⚠️ `OK` IS NOT THE SAME AS SAFE. Read `TaxHealthReport.snapshotMissingCount`
 * first: invoices carrying no tax snapshot were never checked by anything, and a
 * window made entirely of those returns `OK` having examined nothing.
 */
export enum TaxHealthStatus {
  Critical = 'CRITICAL',
  Info = 'INFO',
  Ok = 'OK',
  Warn = 'WARN'
}

/** Which uploaded column feeds which field. */
export type TaxImportColumnMapInput = {
  /**
   * Header of a display label column. Optional, and NEVER used to match an
   * address — the source file's own city column is wrong on 9 of its 96 rows and
   * it costs nothing precisely because it is not a key.
   */
  label?: InputMaybe<Scalars['String']['input']>;
  /** Header of the column holding the postal code. */
  postalCode: Scalars['String']['input'];
  /** Header of the column holding the rate. */
  rate: Scalars['String']['input'];
  rateUnit: TaxRateColumnUnit;
};

export type TaxImportCommitPayload = {
  __typename?: 'TaxImportCommitPayload';
  batchId: Scalars['ID']['output'];
  jurisdictionIds: Array<Scalars['ID']['output']>;
  rowCount: Scalars['Int']['output'];
};

/**
 * One thing a human should know before committing an import.
 *
 * `BLOCK` refuses the commit. `WARN` does not — a rate that is not a multiple of
 * an eighth of a percent is usually a typo and is sometimes real (ZIP 94306 in the
 * lab's own file), and refusing it would make the product reject the customer's
 * actual data. `INFO` is an observation.
 */
export type TaxImportNotice = {
  __typename?: 'TaxImportNotice';
  code: Scalars['String']['output'];
  level: Scalars['String']['output'];
  message: Scalars['String']['output'];
  /**
   * 1-based row number as it appears in the spreadsheet; the header is row 1,
   * so the first data row is 2. Null for a whole-file notice.
   */
  row?: Maybe<Scalars['Int']['output']>;
};

/** What an import WOULD do. Writes nothing — this is a Query for that reason. */
export type TaxImportPreview = {
  __typename?: 'TaxImportPreview';
  /** False when any notice is BLOCK, or the file has no rows. */
  canCommit: Scalars['Boolean']['output'];
  /**
   * The coverage consequence in plain English, computed from the REAL fallback
   * rather than written as fixed copy: "96 postal codes will be taxed at their
   * listed rate. Every other CA address continues at the 7.25% regional default."
   * That sentence is what makes the inversion trap visible to a human.
   */
  coverageSentence: Scalars['String']['output'];
  /**
   * SHA-256 over the canonicalised normalised rows, the effective date and the
   * column map. Echo it back on commit: the server recomputes it from the rows it
   * is about to write and refuses a mismatch, so a commit can only ever write the
   * import the human was shown.
   */
  digest: Scalars['String']['output'];
  effectiveFrom: Scalars['AWSDate']['output'];
  /**
   * The regional default these rates sit above, as a six-decimal fraction.
   * Null when the region has none — which BLOCKS, because the rate floor is the
   * only check that catches a fraction entered where a percentage was expected.
   */
  fallbackRate?: Maybe<Scalars['String']['output']>;
  headers: Array<Scalars['String']['output']>;
  notices: Array<TaxImportNotice>;
  regionCode: Scalars['String']['output'];
  rowCount: Scalars['Int']['output'];
  rows: Array<TaxImportPreviewRow>;
};

export type TaxImportPreviewRow = {
  __typename?: 'TaxImportPreviewRow';
  label: Scalars['String']['output'];
  postalCode: Scalars['String']['output'];
  /** The stored six-decimal fraction the cell became. */
  rate: Scalars['String']['output'];
  row: Scalars['Int']['output'];
};

/**
 * One invoice, with its money beside the engine notes frozen onto it.
 *
 * THIS TYPE IS THE JOIN BETWEEN THE MONEY VIEW AND THE HEALTH VIEW. `taxBilled`
 * comes from `invoice_line_taxes`; `noteCodes` is REPLAYED from
 * `invoices.custom_fields -> 'tax_snapshot' -> 'notes'`, the record the engine
 * froze at issue time. Nothing is recomputed — a re-render replays stored JSON,
 * which is why a pre-ticket invoice keeps its own wording forever.
 */
export type TaxInvoiceTax = {
  __typename?: 'TaxInvoiceTax';
  billablePartyId: Scalars['ID']['output'];
  currency: Scalars['String']['output'];
  /**
   * The engine that produced the snapshot, e.g. `phase0.2`.
   *
   * ⚠️ BRANCH ON THIS BEFORE READING `jurisdictionCoverage`. Every `phase0.1`
   * snapshot says `STATE_ONLY` whether or not it was true, because that engine
   * hardcoded it. Null when there is no snapshot.
   */
  engineVersion?: Maybe<Scalars['String']['output']>;
  /**
   * FALSE means this invoice carries NO tax snapshot at all — it was issued
   * before the engine shipped, or while it was switched off. Its empty
   * `noteCodes` therefore means NOTHING WAS CHECKED, not "clean". Render the
   * difference.
   */
  hasSnapshot: Scalars['Boolean']['output'];
  /**
   * ENG-3881 — the org-calendar day the invoice is dated (`invoices.invoice_date`):
   * the anchor `revenueReport` and `taxCollectedByJurisdiction` use, so all three
   * cover the same population for the same month.
   */
  invoiceDate: Scalars['AWSDate']['output'];
  invoiceId: Scalars['ID']['output'];
  invoiceNumber: Scalars['String']['output'];
  /**
   * The issue instant, `COALESCE(sent_at, created_at)`. Display only: the
   * window selects on `invoiceDate`.
   */
  issuedAt: Scalars['AWSDateTime']['output'];
  jurisdictionCodes: Array<Scalars['String']['output']>;
  /**
   * Distinct jurisdictions that charged something on this invoice. Zero with a
   * non-empty `noteCodes` is the shape worth looking at.
   */
  jurisdictionCount: Scalars['Int']['output'];
  jurisdictionCoverage?: Maybe<Scalars['String']['output']>;
  /**
   * The engine's own note codes, distinct, as frozen. A plain String list and
   * deliberately NOT an enum: the engine's vocabulary grows (three codes were
   * added in `phase0.2` alone) and an unknown-but-correct code must not become an
   * AppSync serialisation failure — the page would break precisely when the engine
   * gained something new to say.
   */
  noteCodes: Array<Scalars['String']['output']>;
  partyName: Scalars['String']['output'];
  /**
   * Never `VOIDED`: a voided invoice is EXCLUDED from this report, not zeroed,
   * because tax on an invoice that was never revenue was never billed.
   */
  status: InvoiceStatus;
  subtotal: Scalars['String']['output'];
  taxBilled: Scalars['String']['output'];
};

/**
 * How completely an address was resolved — `tax_snapshot.jurisdictionCoverage`.
 *
 * **Branch on the engine version before reading this.** Every `phase0.1` snapshot
 * says `STATE_ONLY` whether or not it was true, because that engine hardcoded it.
 * Only `phase0.2` and later derive it.
 */
export enum TaxJurisdictionCoverage {
  /**
   * Every matched boundary was postal-shaped — a rate entered for this exact
   * postal code, which is the complete figure for it.
   */
  FullStack = 'FULL_STACK',
  /**
   * Both shapes appear across one calculation: some addresses are listed,
   * others fell back to the regional default.
   */
  MixedCoverage = 'MIXED_COVERAGE',
  /**
   * Every matched boundary was region-shaped, so local/district taxes are NOT
   * included and the figure is a FLOOR.
   */
  StateOnly = 'STATE_ONLY'
}

/**
 * Which collecting body a tax bucket belongs to. Mirrors
 * `tax_jurisdictions.level` (db/migrations/0305). Phase 0 seeds STATE only; the
 * deeper levels exist because Phase 1 stacks local rates without a schema change.
 */
export enum TaxJurisdictionLevel {
  City = 'CITY',
  Country = 'COUNTRY',
  County = 'COUNTY',
  District = 'DISTRICT',
  Special = 'SPECIAL',
  State = 'STATE'
}

/**
 * This organization's own tax jurisdictions. Shared PLATFORM reference rows are
 * deliberately excluded: they are readable by everyone and writable by nobody, so
 * listing them here would offer an Edit affordance that can only ever refuse. The
 * regional default an address falls back to is visible on `taxRateLookup`, where
 * it is part of the answer.
 */
export type TaxJurisdictionList = {
  __typename?: 'TaxJurisdictionList';
  jurisdictions: Array<TaxJurisdictionSummary>;
  /** True when more rows exist than were returned. Never silently truncated. */
  truncated: Scalars['Boolean']['output'];
};

export type TaxJurisdictionPayload = {
  __typename?: 'TaxJurisdictionPayload';
  jurisdiction: TaxJurisdictionWritten;
};

/** What was closed, and when. */
export type TaxJurisdictionRetired = {
  __typename?: 'TaxJurisdictionRetired';
  /**
   * How many boundary generations were closed. Zero is legitimate — a
   * jurisdiction whose boundaries already ended before this date.
   */
  boundariesClosed: Scalars['Int']['output'];
  code: Scalars['String']['output'];
  country: Scalars['String']['output'];
  effectiveFrom: Scalars['AWSDate']['output'];
  /** The date it stops applying. Exclusive. */
  effectiveTo: Scalars['AWSDate']['output'];
  id: Scalars['ID']['output'];
  level: TaxJurisdictionLevel;
  name: Scalars['String']['output'];
  /** Set on a postal-keyed jurisdiction, null on a regional default. */
  postalCode?: Maybe<Scalars['String']['output']>;
  /**
   * How many rate generations were closed. A generation that STARTS on or after
   * the retirement date is left alone rather than closed: it is unreachable once
   * the jurisdiction itself has ended, and it records a rate change somebody
   * scheduled.
   */
  ratesClosed: Scalars['Int']['output'];
  /** Set on a regional default, null on a postal-keyed jurisdiction. */
  regionCode?: Maybe<Scalars['String']['output']>;
  /**
   * How many registrations had their collecting window closed. `status` is
   * deliberately left `active`: it is not date-bounded, so closing it would remove
   * nexus for PAST dates too and a lookup for a day the lab really was registered
   * would start answering NO_NEXUS.
   */
  registrationsClosed: Scalars['Int']['output'];
  /** The token AFTER the close. Pass it back on any later write. */
  version: Scalars['String']['output'];
};

export type TaxJurisdictionRetiredPayload = {
  __typename?: 'TaxJurisdictionRetiredPayload';
  jurisdiction: TaxJurisdictionRetired;
};

/** One tax jurisdiction this organization has defined for itself. */
export type TaxJurisdictionSummary = {
  __typename?: 'TaxJurisdictionSummary';
  /**
   * How many categories carry an effective rate. Below the regional default's
   * count is an under-collection waiting to happen.
   */
  categoryCount: Scalars['Int']['output'];
  code: Scalars['String']['output'];
  country: Scalars['String']['output'];
  effectiveFrom: Scalars['AWSDate']['output'];
  effectiveTo?: Maybe<Scalars['AWSDate']['output']>;
  /**
   * The `GENERAL` rate on this row's effective date, as a six-place fraction.
   * Null means NO general rate — every unclassified line here bills zero.
   */
  generalRate?: Maybe<Scalars['String']['output']>;
  /**
   * False means every address this jurisdiction covers is taxed at NOTHING,
   * while unlisted addresses nearby keep collecting.
   */
  hasActiveRegistration: Scalars['Boolean']['output'];
  id: Scalars['ID']['output'];
  level: TaxJurisdictionLevel;
  name: Scalars['String']['output'];
  /** Set on a postal-keyed jurisdiction; the key the resolver matches on. */
  postalCode?: Maybe<Scalars['String']['output']>;
  /**
   * `COMBINED` = the rate is the whole figure for an address and REPLACES the
   * wider tier. `INCREMENTAL` = it stacks on one.
   */
  rateBasis?: Maybe<Scalars['String']['output']>;
  /** Set on a region-keyed jurisdiction (a regional default). */
  regionCode?: Maybe<Scalars['String']['output']>;
  /**
   * The permit number, when one was recorded. Null on a registration this
   * console created — those assert authority that has not been verified and are
   * counted as such in tax health.
   */
  registrationNumber?: Maybe<Scalars['String']['output']>;
  /** Opaque optimistic-concurrency token. Pass it back as `expectedVersion`. */
  version: Scalars['String']['output'];
};

/**
 * One jurisdiction's tax for one period in one currency. FIN-M6 slice 3
 * (ENG-1479). Decimal amounts and rates are STRINGS, matching the money-as-string
 * convention the billing SDL already uses, so a figure that goes on a government
 * form cannot pick up a binary-float artefact in transit.
 */
export type TaxJurisdictionTax = {
  __typename?: 'TaxJurisdictionTax';
  /** ISO-4217 code, from the INVOICE. Part of the grouping key. */
  currency: Scalars['String']['output'];
  /**
   * `taxBilled / taxableBase` to 6dp — the blended rate this bucket actually
   * achieved, for reconciling against a jurisdiction's published rate. NULL when
   * `taxableBase` is zero: 0/0 has no rate, and returning 0 would wrongly imply the
   * jurisdiction taxes at zero.
   */
  effectiveRate?: Maybe<Scalars['String']['output']>;
  /**
   * DISTINCT invoices behind this bucket. Lower than `lineCount` whenever an
   * invoice has several taxed lines in the same jurisdiction.
   */
  invoiceCount: Scalars['Int']['output'];
  /**
   * Stable external identifier, ISO-3166-2 shaped where one exists (`US-CA`).
   * This, not the name, is what a filing keys on — the name is editable prose.
   */
  jurisdictionCode: Scalars['String']['output'];
  /** ISO-3166-1 alpha-2 of the collecting country. */
  jurisdictionCountry: Scalars['String']['output'];
  jurisdictionId: Scalars['ID']['output'];
  jurisdictionLevel: TaxJurisdictionLevel;
  jurisdictionName: Scalars['String']['output'];
  /** `invoice_line_taxes` rows behind this bucket. */
  lineCount: Scalars['Int']['output'];
  /**
   * Lines in this bucket whose `tax_dataset_version_id` is NULL — tax with no
   * recorded provenance. The engine always populates it, so this is normally 0;
   * anything else is unsourced evidence and should be resolved before filing.
   */
  linesMissingProvenance: Scalars['Int']['output'];
  /** Last day of the period, inclusive (UTC). */
  periodEnd: Scalars['AWSDate']['output'];
  /**
   * First day of the period (UTC calendar period, so reproducible regardless of
   * session time zone).
   */
  periodStart: Scalars['AWSDate']['output'];
  /**
   * Every DISTINCT rate that actually applied in this bucket, ascending. More
   * than one entry means a rate change inside the period or mixed taxability —
   * surfaced rather than averaged away, because a filing needs the rate it was
   * billed at.
   */
  rates: Array<Scalars['String']['output']>;
  /**
   * Tax BILLED in this bucket — `SUM(invoice_line_taxes.tax_amount)`. Invoice
   * basis, not cash. See the note on `TaxRemittanceReport`.
   */
  taxBilled: Scalars['String']['output'];
  /**
   * The human-readable codes for the same versions (e.g.
   * `MANUAL_US_CA_PHASE0`), so a filing note can cite them without a second
   * lookup.
   */
  taxDatasetVersionCodes: Array<Scalars['String']['output']>;
  /**
   * PROVENANCE — which `tax_dataset_versions` rows the rates came from. This is
   * what makes the figure defensible years later: it pins the published dataset the
   * lab billed against. More than one entry means the window spans a dataset
   * change.
   */
  taxDatasetVersionIds: Array<Scalars['ID']['output']>;
  /**
   * The bucket split by how each line was treated. These rows sum to
   * `taxableBase` and `taxBilled` exactly — both are computed from these same
   * rounded sub-totals. A jurisdiction that taxed some lines and exempted others
   * shows both here, which is what a filing has to declare.
   */
  taxability: Array<TaxabilityBreakdown>;
  /**
   * The portion of invoiced value that entered this jurisdiction's tax base.
   * NOT the invoice subtotal: an exempt or non-taxable line contributes 0.
   */
  taxableBase: Scalars['String']['output'];
};

/**
 * What was written. `rates` lists a row per tax category, because a jurisdiction
 * covering fewer categories than its regional default bills those categories at
 * ZERO.
 */
export type TaxJurisdictionWritten = {
  __typename?: 'TaxJurisdictionWritten';
  code: Scalars['String']['output'];
  country: Scalars['String']['output'];
  effectiveFrom: Scalars['AWSDate']['output'];
  id: Scalars['ID']['output'];
  level: TaxJurisdictionLevel;
  name: Scalars['String']['output'];
  /**
   * NULL on a REGION-shaped claim, which is the whole of upsertTaxStateDefault.
   *
   * It was declared non-null and it is not: `validate()` sets `postalKey = null`
   * for a region claim — the region code IS its key — and refuses a postal code
   * supplied alongside one. So a state-default save COMMITTED the row and THEN
   * failed serialisation, nulling the entire `jurisdiction` object and reporting
   * failure for a write that had already landed. `regionCode` stays non-null
   * because validate() requires it on BOTH shapes.
   */
  postalCode?: Maybe<Scalars['String']['output']>;
  /** The stored six-place fraction the percentage became. */
  rate: Scalars['String']['output'];
  rateBasis: Scalars['String']['output'];
  ratePercent: Scalars['String']['output'];
  rates: Array<TaxJurisdictionWrittenRate>;
  regionCode: Scalars['String']['output'];
  sourcing: Scalars['String']['output'];
  version: Scalars['String']['output'];
};

export type TaxJurisdictionWrittenRate = {
  __typename?: 'TaxJurisdictionWrittenRate';
  categoryCode: Scalars['String']['output'];
  rate: Scalars['String']['output'];
  taxability: Scalars['String']['output'];
};

/**
 * One note the tax engine emitted while answering a lookup — the "why" half of
 * `taxRateLookup`.
 *
 * `code` is a plain String and deliberately NOT an enum: the engine's note
 * vocabulary grows (three codes were added in `phase0.2` alone), and an enum would
 * make an unknown-but-correct code an AppSync serialisation failure — the panel
 * would break precisely when the engine gained something new to say.
 */
export type TaxLookupNote = {
  __typename?: 'TaxLookupNote';
  code: Scalars['String']['output'];
  /** Human-readable, safe to show lab staff, never contains PHI. */
  detail: Scalars['String']['output'];
};

/**
 * One engine note code seen across the invoices in the window, counted.
 *
 * REPLAYED from the frozen snapshots, never recomputed.
 */
export type TaxOutcomeAlert = {
  __typename?: 'TaxOutcomeAlert';
  /**
   * The engine's code, verbatim. A String and not an enum, for the same reason
   * `TaxInvoiceTax.noteCodes` is.
   */
  code: Scalars['String']['output'];
  /**
   * Distinct invoices carrying at least one note with this code. An invoice
   * with four lines all missing a rate is ONE invoice to go and look at.
   */
  invoiceCount: Scalars['Int']['output'];
  /** Individual notes, which is lines rather than invoices. */
  noteCount: Scalars['Int']['output'];
  /**
   * One of the engine's own sentences, verbatim. It names the jurisdiction and
   * the category; a paraphrase would be a second place for the vocabulary to
   * drift. Safe to show lab staff, never contains PHI.
   */
  sampleDetail?: Maybe<Scalars['String']['output']>;
  sampleInvoiceIds: Array<Scalars['ID']['output']>;
  /**
   * The same invoices as `sampleInvoiceIds`, in the same order, by number —
   * ordered by invoice NUMBER and never by id, because the ids are random and an
   * id-ordered sample is a coin flip.
   */
  sampleInvoiceNumbers: Array<Scalars['String']['output']>;
  severity: TaxHealthSeverity;
  /**
   * True when the jurisdiction behind this note is one THIS organization
   * defined for itself.
   *
   * ⚠️ IT IS WHAT ESCALATES `NO_NEXUS` FROM INFO TO CRITICAL, and that is the
   * whole of finding N9: a tenant boundary with no registration bills every
   * address it covers at ZERO, while every address the lab did NOT list keeps
   * collecting. `NO_NEXUS` against a PLATFORM jurisdiction is the ordinary "we do
   * not collect there" and stays INFO.
   */
  tenantOwned: Scalars['Boolean']['output'];
};

/** What the sweep did. */
export type TaxPermitRecordedPayload = {
  __typename?: 'TaxPermitRecordedPayload';
  /**
   * How many were flipped `PENDING` -> `ACTIVE`. Zero when `activate` was
   * false.
   */
  activated: Scalars['Int']['output'];
  country: Scalars['String']['output'];
  /** The jurisdiction codes touched, e.g. `["US-CA", "US-CA-90210"]`. */
  jurisdictionCodes: Array<Scalars['String']['output']>;
  regionCode: Scalars['String']['output'];
  registrationNumber: Scalars['String']['output'];
  /**
   * The rows this sweep did NOT activate, split by reason.
   *
   * ⚠️ THREE NAMED COUNTS RATHER THAN ONE TOTAL, because a total lied in the
   * normal case: with `activate: false` every open `PENDING` row is left pending by
   * design, so a sweep over a state with 40 of them reported `0` — which reads as
   * "nothing needs attention" while 40 registrations collect nothing.
   *
   * Rows that were already `ACTIVE` with an open window are in NO count: nothing
   * about them was skipped and nothing about them changed. So `stamped` is at
   * least `activated + closed + windowEnded + leftPending`, never equal to it.
   */
  skipped: TaxPermitSkipped;
  /**
   * How many registrations the number was written onto — every selected row,
   * whatever its status. Recording which permit a closed registration was held
   * under is a true statement about the past.
   */
  stamped: Scalars['Int']['output'];
};

/** Why a selected registration was not switched on. */
export type TaxPermitSkipped = {
  __typename?: 'TaxPermitSkipped';
  /** Deregistered. Re-opening one would erase a decision somebody made. */
  closed: Scalars['Int']['output'];
  /**
   * Open `PENDING` rows the caller chose not to switch on (`activate: false`).
   * Always zero when `activate` was true. These are not a fault — they are the
   * rows still waiting for a decision.
   */
  leftPending: Scalars['Int']['output'];
  /**
   * The collecting window has already ended — WHATEVER the row's status.
   *
   * ⚠️ INCLUDES AN `ACTIVE` ROW, and that is the steady state after a retirement
   * rather than an anomaly: `retireTaxJurisdiction` sets `collectsTo` and
   * deliberately LEAVES the status `ACTIVE`, because flipping it would remove nexus
   * for every PAST date too.
   */
  windowEnded: Scalars['Int']['output'];
};

/**
 * How the rate column in an uploaded file is expressed. NEVER inferred: the
 * lab's own spreadsheet is a percentage (`7.25`) and a published authority file is
 * already a decimal fraction (`0.0725`), and guessing wrong is a hundredfold
 * error in one direction or the other.
 */
export enum TaxRateColumnUnit {
  Fraction = 'FRACTION',
  Percent = 'PERCENT'
}

/**
 * What an address would be taxed at, and why — run through the REAL resolver.
 *
 * This is not a projection of stored rows: it loads the organization's tax
 * reference data and executes the same `resolveJurisdictions` / `computeTax` an
 * invoice is billed through, against one synthetic line. It is therefore the live
 * acceptance test for the rate someone just entered.
 *
 * **It has no customer and no product**, so a blanket exemption certificate and
 * the product tax-exempt flag are NOT reflected. It answers "what does this
 * ADDRESS attract", not "what will this customer be charged".
 */
export type TaxRateLookup = {
  __typename?: 'TaxRateLookup';
  /**
   * The day this answer is for — AUTHORITATIVE. When `asOf:` was not supplied
   * this is today on YOUR ORGANIZATION's calendar, decided server-side by the same
   * function that decides an invoice's calculation date. Render this rather than
   * the viewer's own date: a contractor in London would otherwise be shown
   * tomorrow's rates.
   */
  asOf: Scalars['AWSDate']['output'];
  /** The notional line amount the figures below are computed on (`100.0000`). */
  base: Scalars['String']['output'];
  categoryCode: Scalars['String']['output'];
  /**
   * Sum of every applied rate, as a six-place fraction. `"0.000000"` means this
   * address currently collects NOTHING.
   */
  combinedRate: Scalars['String']['output'];
  country: Scalars['String']['output'];
  coverage: TaxJurisdictionCoverage;
  /** The tax engine that produced this answer, e.g. `phase0.2`. */
  engineVersion: Scalars['String']['output'];
  jurisdictions: Array<TaxRateLookupJurisdiction>;
  notes: Array<TaxLookupNote>;
  /**
   * The key the resolver actually matched on, AFTER normalisation — a US ZIP+4
   * is reduced to its five-digit prefix. Shown so an operator can see why a row
   * they entered did or did not match.
   */
  postalKey?: Maybe<Scalars['String']['output']>;
  region?: Maybe<Scalars['String']['output']>;
  /**
   * Platform jurisdictions a tenant-owned boundary displaced at the same tier.
   * Non-empty means the lab's own row replaced a published one.
   */
  shadowedPlatformCodes: Array<Scalars['String']['output']>;
  taxOnBase: Scalars['String']['output'];
  /** The IANA zone whose calendar decided `asOf`, e.g. `America/Los_Angeles`. */
  timeZone: Scalars['String']['output'];
  /**
   * Where that zone came from: `tenant_settings`, `custom_fields`, or `default`.
   *
   * `default` means nobody configured one and this lab is billing tax on an ASSUMED
   * zone. Surface it beside the date rather than hiding it — see the matching
   * `ASSUMED_TIME_ZONE` row on `taxHealth`.
   */
  timeZoneSource: Scalars['String']['output'];
};

/** One jurisdiction that claimed an address, and what it did with it. */
export type TaxRateLookupJurisdiction = {
  __typename?: 'TaxRateLookupJurisdiction';
  code: Scalars['String']['output'];
  /**
   * Whether an active registration covers `asOf`. Answered by the tax engine's own
   * nexus predicate, NOT inferred from whether a rate was applied — a jurisdiction
   * can be registered and still collect nothing because no rate exists for the
   * category, and the two failures have different repairs.
   */
  hasNexus: Scalars['Boolean']['output'];
  jurisdictionId: Scalars['ID']['output'];
  level: TaxJurisdictionLevel;
  matchedBy: TaxResolutionTier;
  name: Scalars['String']['output'];
  /**
   * True when this organization defined the jurisdiction itself. A tenant row
   * beats a platform row WITHIN a tier and never across one.
   */
  ownedByTenant: Scalars['Boolean']['output'];
  /**
   * Decimal FRACTION as a string, six places (`"0.097500"`). Null when nothing
   * was applied — see `notes` for which of nexus, rate or sourcing was missing.
   */
  rate?: Maybe<Scalars['String']['output']>;
  /**
   * `effective_from` of the rate row that was used — which version of the law
   * applied.
   */
  rateEffectiveFrom?: Maybe<Scalars['AWSDate']['output']>;
  /**
   * WHY this jurisdiction collected nothing, when it collected nothing. Null when
   * it did collect. One of `NO_SOURCING_SUPPORT`, `NO_NEXUS`, `NO_RATE_FOUND`,
   * `CATEGORY_NOT_TAXED`, `CONFLICTING_TENANT_JURISDICTIONS`, derived in the
   * engine's own branch order so the value names the thing to fix FIRST.
   *
   * `CONFLICTING_TENANT_JURISDICTIONS` is the one that is NOT a gap: this
   * jurisdiction had a rate and had nexus, and another of your own jurisdictions
   * claimed the same address with a HIGHER rate, so the engine kept that one.
   * Both are listed here because the fix is to retire whichever is wrong.
   *
   * A String rather than an enum, for the same reason `TaxLookupNote.code` is: the
   * engine's vocabulary grows, and an unknown-but-correct value must not become an
   * AppSync serialisation failure.
   */
  reason?: Maybe<Scalars['String']['output']>;
  /** Tax this jurisdiction would charge on `TaxRateLookup.base`. */
  taxOnBase?: Maybe<Scalars['String']['output']>;
  taxability?: Maybe<TaxabilityOutcome>;
};

/**
 * One jurisdiction this organization says it is registered to collect in.
 *
 * ⚠️ THE PERMIT NUMBER IS RECORDED AND ATTESTED, NEVER VERIFIED. ClearPath
 * performs NO lookup against CDTFA or any other tax authority — none exists in
 * this product. The number is what somebody typed from the registration document,
 * and the audit trail of who attested it is the only evidence there is.
 */
export type TaxRegistration = {
  __typename?: 'TaxRegistration';
  /**
   * True when this row was created automatically to satisfy the nexus gate
   * while listing a jurisdiction (`nexus_basis: ASSUMED_NOT_VERIFIED`), rather
   * than entered from a registration document. It asserts the organization is
   * registered somewhere it has not shown it is registered — an accepted legal
   * exposure that stays visible until a human records the real permit.
   */
  autoCreated: Scalars['Boolean']['output'];
  /**
   * First date the obligation to collect applies. The engine compares the
   * invoice calculation date against `[collectsFrom, collectsTo)`.
   */
  collectsFrom: Scalars['AWSDate']['output'];
  /** EXCLUSIVE. Null = no end date. Set by `retireTaxJurisdiction`. */
  collectsTo?: Maybe<Scalars['AWSDate']['output']>;
  id: Scalars['ID']['output'];
  /**
   * The jurisdiction's code (e.g. `US-CA`). Null means the jurisdiction this
   * registration points at is neither shared platform reference data nor this
   * organization's — the row is still listed rather than silently dropped,
   * because the tax engine joins nothing and would still see it. Treat a null
   * here as a defect to report, not as an empty cell.
   */
  jurisdictionCode?: Maybe<Scalars['String']['output']>;
  /** The jurisdiction's name. Null for the same reason as `jurisdictionCode`. */
  jurisdictionName?: Maybe<Scalars['String']['output']>;
  /**
   * False when the registration points at SHARED PLATFORM reference data,
   * which is the NORMAL case — db/seeds/0004 points this organization's California
   * registration at the platform `US-CA` row. True when it points at a
   * jurisdiction this organization listed itself.
   */
  jurisdictionOwnedByTenant: Scalars['Boolean']['output'];
  /**
   * The permit / licence number, as recorded. Null while a registration is in
   * progress — the column is nullable by design so an application can be tracked
   * before the number arrives. A registration cannot be ACTIVATED without one.
   */
  registrationNumber?: Maybe<Scalars['String']['output']>;
  status: TaxRegistrationStatus;
  taxJurisdictionId: Scalars['ID']['output'];
  /** Opaque optimistic-concurrency token. Pass it back as `expectedVersion`. */
  version: Scalars['String']['output'];
};

/** Where this organization says it is registered to collect. */
export type TaxRegistrationList = {
  __typename?: 'TaxRegistrationList';
  registrations: Array<TaxRegistration>;
  /** True when more rows exist than were returned. Never silently truncated. */
  truncated: Scalars['Boolean']['output'];
};

export type TaxRegistrationPayload = {
  __typename?: 'TaxRegistrationPayload';
  registration: TaxRegistration;
};

/**
 * Whether the engine collects under this registration. Mirrors the `status`
 * CHECK on `tax_registrations` (db/migrations/0306).
 *
 * ⚠️ ONLY `ACTIVE` COLLECTS. `compute.ts#hasNexus` DROPS every jurisdiction a
 * destination address resolves to that has no active registration — no row, no
 * tax. `PENDING` means "applied for, do NOT collect yet", which is the fail-safe
 * direction and the state db/seeds/0004 ships.
 *
 * ⚠️ `CLOSED` IS NOT HOW COLLECTION IS STOPPED. `status` carries no date, so
 * closing it would remove nexus for every PAST date too and a lookup for a day the
 * lab really was registered would begin answering `NO_NEXUS`. Stopping collection
 * sets `collectsTo` — see `retireTaxJurisdiction`.
 */
export enum TaxRegistrationStatus {
  Active = 'ACTIVE',
  Closed = 'CLOSED',
  Pending = 'PENDING'
}

/**
 * A (period, currency, jurisdiction) group withheld from
 * `TaxRemittanceReport.buckets` because it contradicts itself. FIN-M6 slice 3
 * (ENG-1479).
 *
 * Carries the same identifying and money fields as a bucket so the offending rows
 * can be found and corrected, but it is NOT part of any total anywhere in the
 * payload. The provenance and rate detail is deliberately omitted: the number is
 * not trustworthy, so the report does not dress it up as filing evidence.
 */
export type TaxRemittanceException = {
  __typename?: 'TaxRemittanceException';
  /** The currency actually on the invoices. */
  currency: Scalars['String']['output'];
  /** The currency this jurisdiction's country can legitimately be taxed in. */
  expectedCurrency: Scalars['String']['output'];
  invoiceCount: Scalars['Int']['output'];
  jurisdictionCode: Scalars['String']['output'];
  jurisdictionCountry: Scalars['String']['output'];
  jurisdictionId: Scalars['ID']['output'];
  jurisdictionLevel: TaxJurisdictionLevel;
  jurisdictionName: Scalars['String']['output'];
  lineCount: Scalars['Int']['output'];
  periodEnd: Scalars['AWSDate']['output'];
  periodStart: Scalars['AWSDate']['output'];
  reason: TaxRemittanceExceptionReason;
  taxBilled: Scalars['String']['output'];
  taxableBase: Scalars['String']['output'];
};

/**
 * Why a group was routed to `TaxRemittanceReport.currencyExceptions` instead of
 * `buckets`. One member today; the field is an enum so a future integrity check
 * adds a value rather than a parallel list.
 */
export enum TaxRemittanceExceptionReason {
  /**
   * The invoice currency contradicts the jurisdiction's country — e.g. a CAD
   * invoice taxed under a US jurisdiction. Nothing can be remitted to California
   * in Canadian dollars, so the group is reported as an error to fix rather than
   * added to any total.
   */
  CurrencyJurisdictionMismatch = 'CURRENCY_JURISDICTION_MISMATCH'
}

/**
 * Tax BILLED per jurisdiction over a filing window — the evidence behind what
 * the lab remits. FIN-M6 slice 3 (ENG-1479).
 *
 * **Read the name of the money field.** Every figure here is `taxBilled`:
 * INVOICE-BASIS tax taken from `invoice_line_taxes`, the append-only per-line
 * per-jurisdiction record the tax engine writes. It is NOT reconciled to cash
 * received — an invoice issued in the window and never paid contributes in full.
 * There is deliberately NO field named for "collected" tax, because whether a given
 * jurisdiction wants a billed or a cash basis is a tax-advisor decision that has
 * not been made, and putting a billed figure on a form that wanted cash is a
 * filing error.
 *
 * Four invariants a consumer may rely on:
 *
 *   1. **Nothing is summed across currencies.** Currency is part of the key on
 *      every bucket and there is no grand-total field, matching `revenueReport`,
 *      `arAging` and `myBillingSummary`.
 *   2. **A currency that contradicts its jurisdiction is NOT in `buckets`.** It is
 *      in `currencyExceptions`, so no total in this payload can contain it.
 *   3. **Voided invoices are excluded** — absent, not zeroed. Tax on an invoice
 *      that was never revenue was never billed.
 *   4. **The taxability breakdown adds up to its bucket**, to the cent: bucket
 *      totals are summed from the same rounded sub-totals the breakdown shows.
 *
 * Periods are anchored on the invoice ISSUE date,
 * `COALESCE(invoices.sent_at, invoices.created_at)` — the same anchor
 * `revenueReport` uses on its ACCRUAL_BILLED basis, so the two reports cover the
 * same population of invoices for the same month.
 *
 * Computed on read; no rollup table and no materialized view, so the figures cannot
 * drift from the `invoice_line_taxes` rows an auditor would count by hand.
 */
export type TaxRemittanceReport = {
  __typename?: 'TaxRemittanceReport';
  /**
   * Instant the aggregate was computed. Two reports over the same window with
   * different `asOf` values may legitimately disagree.
   */
  asOf: Scalars['AWSDateTime']['output'];
  /**
   * One row per (period, currency, jurisdiction). Empty is a real, expected
   * state — a lab with no nexus anywhere collects no tax.
   */
  buckets: Array<TaxJurisdictionTax>;
  /**
   * Groups withheld from `buckets` because they are self-contradictory. Normally
   * empty; a non-empty list is a DATA ERROR to fix on the underlying invoices, not
   * a rounding difference to ignore.
   */
  currencyExceptions: Array<TaxRemittanceException>;
  /** The period length the buckets use, as requested. */
  grain: RevenuePeriodGrain;
  /**
   * Always LIVE until FIN-M6 slice 5 wires the M5 period-close snapshot. A LIVE
   * report is PROVISIONAL — a credit note raised after a month ends changes what
   * that month's tax should have been. Do not file from a LIVE report without
   * saying so.
   */
  periodStatus: RevenuePeriodStatus;
  rangeEnd: Scalars['AWSDateTime']['output'];
  /**
   * Echo of the requested window (inclusive both ends). Always present: a
   * remittance report covers a filing period, never an open-ended window.
   */
  rangeStart: Scalars['AWSDateTime']['output'];
};

/**
 * Which specificity tier matched an address. `POSTAL` beats `REGION`: if any
 * postal-shaped boundary matches, region-shaped rows are ignored ENTIRELY rather
 * than unioned in, because unioning would double-count the state the moment ZIP
 * rows land beside region rows.
 */
export enum TaxResolutionTier {
  Postal = 'POSTAL',
  Region = 'REGION'
}

/**
 * One taxability outcome within a jurisdiction bucket. FIN-M6 slice 3
 * (ENG-1479).
 */
export type TaxabilityBreakdown = {
  __typename?: 'TaxabilityBreakdown';
  lineCount: Scalars['Int']['output'];
  /**
   * Tax billed under this outcome. Always "0.00" for EXEMPT and ZERO_RATED,
   * enforced at the database by `chk_invoice_line_taxes_zero_when_not_collected`.
   */
  taxBilled: Scalars['String']['output'];
  taxability: TaxabilityOutcome;
  /**
   * Base attributed to this outcome. Zero for EXEMPT / ZERO_RATED lines only if
   * the line itself carried no base — an exempt line usually has a base and no
   * tax, which is exactly what a filing must report.
   */
  taxableBase: Scalars['String']['output'];
};

/**
 * How a line was treated for tax. Mirrors `invoice_line_taxes.taxability`.
 *
 * `EXEMPT` and `ZERO_RATED` both carry zero tax and are enforced to do so by
 * `chk_invoice_line_taxes_zero_when_not_collected`; they are distinct because a
 * filing reports them differently — an exempt sale is outside the tax, a
 * zero-rated one is inside it at 0%.
 */
export enum TaxabilityOutcome {
  Exempt = 'EXEMPT',
  Reduced = 'REDUCED',
  Taxable = 'TAXABLE',
  ZeroRated = 'ZERO_RATED'
}

export type TeamMemberSkills = {
  __typename?: 'TeamMemberSkills';
  certifications: Array<UserCertification>;
  machines: Array<MachineAuthorization>;
  skills: Array<UserSkill>;
  userId: Scalars['ID']['output'];
};

export type TechnicianCapacityRow = {
  __typename?: 'TechnicianCapacityRow';
  activeStepRunCount: Scalars['Int']['output'];
  available: Scalars['Boolean']['output'];
  blockingTimeOffRequestIds: Array<Scalars['ID']['output']>;
  capacitySource: Scalars['String']['output'];
  capacityUnitsPerDay: Scalars['Int']['output'];
  email?: Maybe<Scalars['String']['output']>;
  name?: Maybe<Scalars['String']['output']>;
  orgRole?: Maybe<Scalars['String']['output']>;
  userId: Scalars['ID']['output'];
};

/** Tenant row in the control-plane registry. Per db/migrations/control-plane/0001. */
export type Tenant = {
  __typename?: 'Tenant';
  billingEmail?: Maybe<Scalars['String']['output']>;
  /**
   * ENG-88 Level A white-label — the lab's display name shown in
   * transactional-email copy. NULL = Molaris/ClearPath default.
   */
  brandingDisplayName?: Maybe<Scalars['String']['output']>;
  /**
   * ENG-88 Level A white-label — friendly Cognito From display name for the
   * tenant-staff pool. Still noreply@molaris.ai (Level A = zero per-tenant
   * DNS). NULL = "Molaris".
   */
  brandingFromName?: Maybe<Scalars['String']['output']>;
  /**
   * ENG-88 Level A white-label — HTTPS logo URL rendered in the
   * transactional-email header. NULL = Molaris wordmark default.
   */
  brandingLogoUrl?: Maybe<Scalars['String']['output']>;
  /**
   * ENG-88 Level A white-label — 6-digit hex brand color for the email CTA.
   * NULL = ClearPath default blue (#2563eb).
   */
  brandingPrimaryColor?: Maybe<Scalars['String']['output']>;
  /**
   * ENG-88 Level A white-label — optionally replaces "ClearPath" in
   * transactional-email copy. NULL = "ClearPath".
   */
  brandingProductName?: Maybe<Scalars['String']['output']>;
  createdAt: Scalars['AWSDateTime']['output'];
  customFields: Scalars['AWSJSON']['output'];
  /** Saved database placement, independent of tier. Null means legacy inventory has not been verified. */
  databasePlacement?: Maybe<TenantDatabasePlacement>;
  id: Scalars['ID']['output'];
  name: Scalars['String']['output'];
  primaryIndustry?: Maybe<Scalars['String']['output']>;
  schemaVersion: Scalars['Int']['output'];
  slug: Scalars['String']['output'];
  status: TenantStatus;
  tenantAwsAccountId: Scalars['String']['output'];
  tenantAwsRegion: Scalars['String']['output'];
  tenantCognitoPoolId?: Maybe<Scalars['String']['output']>;
  tenantDbClusterArn?: Maybe<Scalars['String']['output']>;
  tenantDbName?: Maybe<Scalars['String']['output']>;
  tenantDbProxyEndpoint?: Maybe<Scalars['String']['output']>;
  tenantDbSecretArn?: Maybe<Scalars['String']['output']>;
  tenantEventbridgeBusArn?: Maybe<Scalars['String']['output']>;
  tenantKmsKeyArn?: Maybe<Scalars['String']['output']>;
  tenantS3Bucket?: Maybe<Scalars['String']['output']>;
  /**
   * Saved provisioning compute tier, separate from product entitlements.
   * Production uses a dedicated cluster/proxy for every tier: Starter 0.5–2,
   * Growth 0.5–4, Enterprise 1–16 ACUs; one writer, zero readers.
   * Nullable for compatibility with older resolver projections.
   */
  tier?: Maybe<TenantTier>;
  updatedAt: Scalars['AWSDateTime']['output'];
};

/** Per-tenant billing detail across a date range. */
export type TenantBillingDetail = {
  __typename?: 'TenantBillingDetail';
  periods: Array<BillingRollupRow>;
  tenantId: Scalars['ID']['output'];
  tenantName: Scalars['String']['output'];
  totals: BillingTotals;
};

/**
 * Versioned tenant configuration document (YAML + parsed JSON).
 * Per db/migrations/control-plane/0007_tenant_config_documents.sql.
 */
export type TenantConfigDocument = {
  __typename?: 'TenantConfigDocument';
  appliedAt?: Maybe<Scalars['AWSDateTime']['output']>;
  author?: Maybe<Scalars['String']['output']>;
  commitMessage?: Maybe<Scalars['String']['output']>;
  createdAt: Scalars['AWSDateTime']['output'];
  id: Scalars['ID']['output'];
  jsonSchema?: Maybe<Scalars['AWSJSON']['output']>;
  status: TenantConfigDocumentStatus;
  tenantId: Scalars['ID']['output'];
  version: Scalars['Int']['output'];
  yamlContent: Scalars['String']['output'];
};

export enum TenantConfigDocumentStatus {
  Applied = 'APPLIED',
  Applying = 'APPLYING',
  Draft = 'DRAFT',
  RolledBack = 'ROLLED_BACK',
  Superseded = 'SUPERSEDED',
  Validated = 'VALIDATED'
}

export type TenantConfigHistoryFilter = {
  author?: InputMaybe<Scalars['String']['input']>;
  fromDate?: InputMaybe<Scalars['AWSDateTime']['input']>;
  status?: InputMaybe<TenantConfigDocumentStatus>;
  toDate?: InputMaybe<Scalars['AWSDateTime']['input']>;
};

/** Paginated history result for tenantConfigHistory. */
export type TenantConfigHistoryResult = {
  __typename?: 'TenantConfigHistoryResult';
  documents: Array<TenantConfigDocument>;
  pageInfo: PageInfo;
  totalCount: Scalars['Int']['output'];
};

export type TenantDatabaseClusterResource = {
  __typename?: 'TenantDatabaseClusterResource';
  arn?: Maybe<Scalars['String']['output']>;
  dedicatedToTenant: Scalars['Boolean']['output'];
  deletionProtection: Scalars['Boolean']['output'];
  identifier: Scalars['String']['output'];
  maxCapacity?: Maybe<Scalars['Float']['output']>;
  minCapacity?: Maybe<Scalars['Float']['output']>;
  /** Scope=tenant and TenantId tags match this tenant in the AWS observation. */
  ownershipVerified: Scalars['Boolean']['output'];
  readerCount?: Maybe<Scalars['Int']['output']>;
  status?: Maybe<Scalars['String']['output']>;
  storedReference: Scalars['Boolean']['output'];
  /** Observed AWS Tier tag, never inferred from registry tier or capacity. */
  tierTag?: Maybe<Scalars['String']['output']>;
  /** Observed membership counts; null when AWS omitted membership evidence. */
  writerCount?: Maybe<Scalars['Int']['output']>;
};

export type TenantDatabaseInventory = {
  __typename?: 'TenantDatabaseInventory';
  /** True when a dedicated Aurora cluster or RDS Proxy was observed live; null when AWS could not be checked. */
  billableResourcesPresent?: Maybe<Scalars['Boolean']['output']>;
  capturedAt: Scalars['AWSDateTime']['output'];
  liveClusters: Array<TenantDatabaseClusterResource>;
  liveProxies: Array<TenantDatabaseProxyResource>;
  state: TenantDatabaseInventoryState;
  storedClusterArn?: Maybe<Scalars['String']['output']>;
  storedProxyEndpoint?: Maybe<Scalars['String']['output']>;
  tenantId: Scalars['ID']['output'];
  tenantStatus: TenantStatus;
};

export enum TenantDatabaseInventoryState {
  Absent = 'ABSENT',
  Ambiguous = 'AMBIGUOUS',
  CleanupRequired = 'CLEANUP_REQUIRED',
  Live = 'LIVE',
  Shared = 'SHARED',
  StaleReference = 'STALE_REFERENCE',
  Unavailable = 'UNAVAILABLE'
}

export enum TenantDatabasePlacement {
  Dedicated = 'DEDICATED',
  Shared = 'SHARED'
}

export type TenantDatabaseProvisioningPolicy = {
  __typename?: 'TenantDatabaseProvisioningPolicy';
  availablePlacements: Array<TenantDatabasePlacement>;
  defaultPlacement?: Maybe<TenantDatabasePlacement>;
  environment: Scalars['String']['output'];
  message: Scalars['String']['output'];
};

export type TenantDatabaseProxyResource = {
  __typename?: 'TenantDatabaseProxyResource';
  arn?: Maybe<Scalars['String']['output']>;
  dedicatedToTenant: Scalars['Boolean']['output'];
  name: Scalars['String']['output'];
  status?: Maybe<Scalars['String']['output']>;
  storedReference: Scalars['Boolean']['output'];
};

/** Billing summary — open invoices, outstanding balance (USD). */
export type TenantHealthBilling = {
  __typename?: 'TenantHealthBilling';
  /** ISO-8601 of the oldest open invoice, if any. */
  oldestOpenInvoiceAt?: Maybe<Scalars['AWSDateTime']['output']>;
  openInvoiceCount?: Maybe<Scalars['Int']['output']>;
  outstandingBalanceUsd?: Maybe<Scalars['String']['output']>;
};

/** Aurora capacity + connection metrics. */
export type TenantHealthDb = {
  __typename?: 'TenantHealthDb';
  /** Active backend connections (tenants.cluster_identifier). */
  activeConnections?: Maybe<Scalars['Int']['output']>;
  /** 24-hour ACU series (oldest first) — one point per 5 minutes. */
  acu24h: Array<TenantHealthMetricPoint>;
  /** Current ACU reading. Null if the CloudWatch probe failed or is unwired. */
  acuCurrent?: Maybe<Scalars['Float']['output']>;
  /** Count of queries in pg_stat_statements exceeding slow-query threshold. */
  slowQueryCount24h?: Maybe<Scalars['Int']['output']>;
};

/** Event volume summary. */
export type TenantHealthEvents = {
  __typename?: 'TenantHealthEvents';
  /** Ratio of 24h volume vs prior 24h. 1.0 = flat, 2.0 = doubled, null = no prior data. */
  rateOfChange?: Maybe<Scalars['Float']['output']>;
  volume24h?: Maybe<Scalars['Int']['output']>;
};

/** Federation activity summary. */
export type TenantHealthFederation = {
  __typename?: 'TenantHealthFederation';
  activeLinkCount?: Maybe<Scalars['Int']['output']>;
  messageVolume24h?: Maybe<Scalars['Int']['output']>;
  /** Count of federation messages that breached their per-link SLA window. */
  slaBreaches24h?: Maybe<Scalars['Int']['output']>;
};

/**
 * Per-function error counts + cold-start p95 for the tenant's key Lambdas.
 * Right now this is the read-api handler + outbox-publisher + webhook-dispatcher.
 */
export type TenantHealthLambda = {
  __typename?: 'TenantHealthLambda';
  perFunction: Array<TenantHealthLambdaEntry>;
};

export type TenantHealthLambdaEntry = {
  __typename?: 'TenantHealthLambdaEntry';
  coldStartP95Ms?: Maybe<Scalars['Float']['output']>;
  errorCount24h?: Maybe<Scalars['Int']['output']>;
  functionName: Scalars['String']['output'];
  invocations24h?: Maybe<Scalars['Int']['output']>;
};

/** Generic (t, value) point for any tenant-health time-series. */
export type TenantHealthMetricPoint = {
  __typename?: 'TenantHealthMetricPoint';
  at: Scalars['AWSDateTime']['output'];
  value?: Maybe<Scalars['Float']['output']>;
};

/** Backlog depth for the outbox + webhook queues. */
export type TenantHealthQueues = {
  __typename?: 'TenantHealthQueues';
  outboxPublisherBacklog?: Maybe<Scalars['Int']['output']>;
  webhookDispatcherBacklog?: Maybe<Scalars['Int']['output']>;
  webhookDlqCount?: Maybe<Scalars['Int']['output']>;
};

/**
 * Aggregated tenant health snapshot. Returned by MolarisAdminQuery.tenantHealth.
 * partial=true when any backing metric source is unwired; `unwiredMetrics` lists
 * the field paths that stubbed out.
 */
export type TenantHealthReport = {
  __typename?: 'TenantHealthReport';
  billing: TenantHealthBilling;
  capturedAt: Scalars['AWSDateTime']['output'];
  /** Live AWS probe that distinguishes billable resources from stale ARNs. */
  databaseInventory: TenantDatabaseInventory;
  db: TenantHealthDb;
  events: TenantHealthEvents;
  federation: TenantHealthFederation;
  lambda: TenantHealthLambda;
  partial: Scalars['Boolean']['output'];
  queues: TenantHealthQueues;
  tenantId: Scalars['ID']['output'];
  /**
   * Dot-paths of metrics that are not yet wired (e.g. 'db.acuCurrent').
   * Rendered as badges in the UI so ops can see which integrations are
   * pending. Permanent TODO markers while Phase 3.3 backfills resolvers.
   */
  unwiredMetrics: Array<Scalars['String']['output']>;
  userActivity: TenantHealthUserActivity;
};

/** User-activity summary. */
export type TenantHealthUserActivity = {
  __typename?: 'TenantHealthUserActivity';
  activeUsers7d?: Maybe<Scalars['Int']['output']>;
  activeUsers24h?: Maybe<Scalars['Int']['output']>;
  /** Sessions currently alive (not revoked, not expired). */
  liveSessions?: Maybe<Scalars['Int']['output']>;
};

/**
 * Tenant-membership row from customer_tenant_memberships joined against
 * tenants + tenant_product_entitlements for display name + entitlements.
 */
export type TenantMembership = {
  __typename?: 'TenantMembership';
  grantedAt: Scalars['AWSDateTime']['output'];
  id: Scalars['ID']['output'];
  /**
   * `clearpath`, `orovia`, etc. — keys from the products registry that the
   * tenant has ACTIVE or TRIAL entitlements for.
   */
  productEntitlements: Array<Scalars['String']['output']>;
  revokedAt?: Maybe<Scalars['AWSDateTime']['output']>;
  status: TenantMembershipStatus;
  tenantId: Scalars['ID']['output'];
  tenantName: Scalars['String']['output'];
};

export enum TenantMembershipStatus {
  Active = 'ACTIVE',
  Revoked = 'REVOKED',
  Suspended = 'SUSPENDED'
}

export type TenantPayload = {
  __typename?: 'TenantPayload';
  tenant: Tenant;
};

/** Per-tenant product entitlement (tier + status + expiry + seat limit). */
export type TenantProductEntitlement = {
  __typename?: 'TenantProductEntitlement';
  createdAt: Scalars['AWSDateTime']['output'];
  customFields: Scalars['AWSJSON']['output'];
  entitledAt: Scalars['AWSDateTime']['output'];
  expiresAt?: Maybe<Scalars['AWSDateTime']['output']>;
  id: Scalars['ID']['output'];
  productId: Scalars['ID']['output'];
  seatLimit?: Maybe<Scalars['Int']['output']>;
  status: EntitlementStatus;
  tenantId: Scalars['ID']['output'];
  tier: Scalars['String']['output'];
  updatedAt: Scalars['AWSDateTime']['output'];
};

export type TenantProductEntitlementPayload = {
  __typename?: 'TenantProductEntitlementPayload';
  entitlement: TenantProductEntitlement;
};

/** Payload returned by tenant role CRUD mutations. */
export type TenantRolePayload = {
  __typename?: 'TenantRolePayload';
  role: Role;
};

/**
 * Input for addPermissionToRole / removePermissionFromRole on the tenant
 * Mutation root. Caller's tenantId is implicit from the request context.
 */
export type TenantRolePermissionInput = {
  permissionKey: Scalars['String']['input'];
  reason?: InputMaybe<Scalars['String']['input']>;
  roleId: Scalars['ID']['input'];
};

/**
 * Tenant-wide settings.
 * Per docs/clearpath-architecture/03-data-model.md tier 8 (`tenant_settings`).
 */
export type TenantSettings = {
  __typename?: 'TenantSettings';
  /** Business calendar (JSONB). */
  businessCalendar?: Maybe<Scalars['AWSJSON']['output']>;
  /**
   * ENG-6 (migration 0125) — per-tenant case-number scheme (JSONB) governing
   * how orders.barcode_value is generated at submit. Object
   * `{version, prefix, width, start, resetCadence}`; null when the tenant uses
   * the default random id. Validated server-side in the upsert handler; every
   * field is editable anytime and existing case numbers are never renumbered.
   */
  caseNumberScheme?: Maybe<Scalars['AWSJSON']['output']>;
  /**
   * Round 6 (2026-04-30) — N-level catalog taxonomy. Drives the lab-side
   * catalog browser. Empty when not configured. Sourced from
   * `tenant_settings.catalog_taxonomy_json` (migration 0057).
   */
  catalogTaxonomy: CatalogTaxonomy;
  /**
   * ENG-2523 (migration 0421) — per-tenant customer-number scheme (JSONB)
   * governing how external_parties.customer_number is generated. Object
   * `{version, prefix, width, start}`; null when the tenant has not configured
   * one, in which case nothing is auto-allocated. There is deliberately NO
   * `resetCadence`: a customer number is permanent identity, not a dated
   * document number, so it never resets per period. Validated server-side in the
   * upsert handler.
   */
  customerNumberScheme?: Maybe<Scalars['AWSJSON']['output']>;
  /** Default barcode format for new orders. */
  defaultBarcodeFormat: BarcodeFormat;
  /** Default locale. */
  defaultLocale?: Maybe<Scalars['String']['output']>;
  /** Default timezone. */
  defaultTimezone?: Maybe<Scalars['String']['output']>;
  /**
   * Tenant label overrides for the canonical field-label keys (orders.order,
   * team.member, etc.). Portal's FieldLabelsProvider merges this over the
   * platform defaults at session-start. Empty / null object → no overrides.
   * P1.5.3 (2026-04-24) — replaces the pre-existing misuse of
   * `businessCalendar` as a field-label map.
   */
  fieldLabels?: Maybe<Scalars['AWSJSON']['output']>;
  /**
   * Idle timeout in minutes. Deprecated alias for
   * `idleTimeoutMinutesMainApp` — kept for backward-compat with the
   * apps/main admin-tenant-settings surface. New callers should pick
   * the audience-specific field below.
   */
  idleTimeoutMinutes?: Maybe<Scalars['Int']['output']>;
  /** Idle timeout enforced by apps/admin (Molaris super-admin SPA). */
  idleTimeoutMinutesAdmin?: Maybe<Scalars['Int']['output']>;
  /** Idle timeout enforced by apps/main (lab staff SPA). */
  idleTimeoutMinutesMainApp?: Maybe<Scalars['Int']['output']>;
  /**
   * Idle timeout enforced by apps/portal (customer-facing SPA). Often
   * tighter than main-app when the tenant is HIPAA-hardening the portal.
   */
  idleTimeoutMinutesPortal?: Maybe<Scalars['Int']['output']>;
  /** Tenant-facing plural label for order-groups. Default `cases`. */
  orderGroupLabelPlural: Scalars['String']['output'];
  /**
   * Tenant-facing singular label for an order-group (a parent order with
   * child orders). Default `case`. Tenants can rename to `Project`, `Job`,
   * `Treatment Plan`, etc. The grouping primitive is `orders.parent_order_id`
   * (Bible §05:9) — this column only controls display copy.
   */
  orderGroupLabelSingular: Scalars['String']['output'];
  /**
   * Track 2 (2026-04-30) — hydrated FormDefinition for
   * `orderIntakeFormDefinitionId`. Null when the column is null OR the
   * referenced row has been archived. Resolved via the same path as
   * ProductType.intakeFormDefinition.
   */
  orderIntakeFormDefinition?: Maybe<FormDefinition>;
  /**
   * Track 2 (2026-04-30) — FK to the order-level intake FormDefinition.
   * When set, portal NewOrderPage Step 1 renders this form (industry-neutral
   * per bible §00). Null means the tenant has not configured one and the
   * wizard skips Step 1. Sourced from
   * `tenant_settings.order_intake_form_definition_id` (migration 0060).
   */
  orderIntakeFormDefinitionId?: Maybe<Scalars['ID']['output']>;
  organizationId?: Maybe<Scalars['ID']['output']>;
  /**
   * ENG-1581 — bounded per-form scanner field mappings. V1 accepts only
   * iTero Patient.FirstName, Patient.LastName, Patient.DateOfBirth and
   * Order.DueDate as configurable targets. This is configuration metadata only
   * and never contains values received for a case.
   */
  scannerIntakeFieldMappings?: Maybe<Scalars['AWSJSON']['output']>;
  /** Security policy (JSONB). */
  securityPolicy?: Maybe<Scalars['AWSJSON']['output']>;
  /**
   * Round 5a (2026-04-30) — tenant-customizable status states for the
   * kanban dashboard. Array of `{key, label, tone, position}` sorted by
   * `position`. Sourced from `tenant_settings.status_states_json`
   * (migration 0056). Defaults to a 5-state pipeline; tenants can rename /
   * reorder / extend without a schema change.
   */
  statusStates: Array<StatusState>;
  tenantId: Scalars['ID']['output'];
  updatedAt?: Maybe<Scalars['AWSDateTime']['output']>;
};

export type TenantSettingsPayload = {
  __typename?: 'TenantSettingsPayload';
  tenantSettings: TenantSettings;
};

/** Tenant lifecycle status. Mirrors tenants.status CHECK constraint. */
export enum TenantStatus {
  Active = 'ACTIVE',
  Deprovisioning = 'DEPROVISIONING',
  Provisioning = 'PROVISIONING',
  Suspended = 'SUSPENDED',
  Terminated = 'TERMINATED'
}

/** P1.6 (2026-04-25) — service tier. Mirrors tenants.tier CHECK constraint. */
export enum TenantTier {
  Enterprise = 'ENTERPRISE',
  Growth = 'GROWTH',
  Starter = 'STARTER'
}

/** Filter for Query.molarisAdmin.tenants. All fields are ANDed. */
export type TenantsFilterInput = {
  createdAfter?: InputMaybe<Scalars['AWSDateTime']['input']>;
  createdBefore?: InputMaybe<Scalars['AWSDateTime']['input']>;
  limit?: InputMaybe<Scalars['Int']['input']>;
  offset?: InputMaybe<Scalars['Int']['input']>;
  /** Case-insensitive substring match against the tenant's primary industry. */
  primaryIndustry?: InputMaybe<Scalars['String']['input']>;
  status?: InputMaybe<TenantStatus>;
};

/** Paginated tenant list with total count. */
export type TenantsPage = {
  __typename?: 'TenantsPage';
  databaseProvisioningPolicy?: Maybe<TenantDatabaseProvisioningPolicy>;
  items: Array<Tenant>;
  totalCount: Scalars['Int']['output'];
};

/** Mock event body for testAutomationRule. */
export type TestAutomationRuleEventInput = {
  eventType: Scalars['String']['input'];
  payload?: InputMaybe<Scalars['AWSJSON']['input']>;
};

/**
 * Input for testAutomationRule — dry-runs a rule body against a mock event.
 * No side effects; returns the action list that WOULD fire. Drives the admin
 * rule-editor Dry-run button. Per Phase 2.6 / MOL-566.
 */
export type TestAutomationRuleInput = {
  /** Inline actions array (JSONB). */
  action?: InputMaybe<Scalars['AWSJSON']['input']>;
  /** Inline condition (JSONLogic). Null = always-fire when trigger matches. */
  condition?: InputMaybe<Scalars['AWSJSON']['input']>;
  /** Mock event to replay against the rule. */
  event: TestAutomationRuleEventInput;
  /**
   * ID of an existing rule. When supplied, the rule's stored trigger /
   * condition / actions are loaded; the caller only supplies `event`. Mutually
   * exclusive with the inline fields below.
   */
  id?: InputMaybe<Scalars['ID']['input']>;
  /** Inline trigger pattern (used for rules being authored but not yet saved). */
  triggerPattern?: InputMaybe<Scalars['AWSJSON']['input']>;
};

/** Input for testing an integration's connection without persisting changes. */
export type TestExternalIntegrationConnectionInput = {
  id: Scalars['ID']['input'];
};

/**
 * Result of a connection test — thin wrapper around the adapter's
 * `testConnection` AdapterResult.
 */
export type TestExternalIntegrationConnectionPayload = {
  __typename?: 'TestExternalIntegrationConnectionPayload';
  /**
   * Provider-specific diagnostic payload (auth status, upstream version,
   * clinic name, etc.). JSON.
   */
  diagnostics?: Maybe<Scalars['AWSJSON']['output']>;
  errorCode?: Maybe<Scalars['String']['output']>;
  errorMessage?: Maybe<Scalars['String']['output']>;
  ok: Scalars['Boolean']['output'];
};

/** One throughput bucket (period start is UTC-truncated to the granularity). */
export type ThroughputBucket = {
  __typename?: 'ThroughputBucket';
  /** Orders that reached a done state in this bucket. */
  completedCount: Scalars['Int']['output'];
  /** Orders created in this bucket. */
  createdCount: Scalars['Int']['output'];
  periodStart: Scalars['AWSDateTime']['output'];
};

/** Throughput + work-in-progress payload. */
export type ThroughputMetrics = {
  __typename?: 'ThroughputMetrics';
  buckets: Array<ThroughputBucket>;
  /**
   * Orders currently in-flight (not terminal) as of now — point-in-time,
   * independent of the range window.
   */
  currentWip: Scalars['Int']['output'];
  granularity: AnalyticsGranularity;
};

/**
 * Tenant-scoped internal support / workflow ticket.
 * Per docs/clearpath-architecture/03-data-model.md tier 3 (Phase 12).
 */
export type Ticket = {
  __typename?: 'Ticket';
  /** Who the ticket is assigned to for active work. Null = unassigned queue. */
  assigneeUserId?: Maybe<Scalars['ID']['output']>;
  body: Scalars['String']['output'];
  category: TicketCategory;
  closedAt?: Maybe<Scalars['AWSDateTime']['output']>;
  createdAt: Scalars['AWSDateTime']['output'];
  customFields?: Maybe<Scalars['AWSJSON']['output']>;
  dueAt?: Maybe<Scalars['AWSDateTime']['output']>;
  id: Scalars['ID']['output'];
  organizationId: Scalars['ID']['output'];
  priority: TicketPriority;
  /** Optional reference to an order this ticket is about. */
  relatedOrderId?: Maybe<Scalars['ID']['output']>;
  /**
   * User who filed the ticket. Usually ctx.userId at create-time; nullable so
   * admin-filed tickets on behalf of unknown staff still persist.
   */
  requesterUserId?: Maybe<Scalars['ID']['output']>;
  resolvedAt?: Maybe<Scalars['AWSDateTime']['output']>;
  status: TicketStatus;
  subject: Scalars['String']['output'];
  tenantId: Scalars['ID']['output'];
  updatedAt: Scalars['AWSDateTime']['output'];
};

/** Category a ticket falls under. Drives default routing + iconography. */
export enum TicketCategory {
  Billing = 'BILLING',
  Bug = 'BUG',
  FeatureRequest = 'FEATURE_REQUEST',
  HowTo = 'HOW_TO',
  Other = 'OTHER',
  Security = 'SECURITY'
}

/** Relay-style connection for Query.tickets. */
export type TicketConnection = {
  __typename?: 'TicketConnection';
  edges: Array<TicketEdge>;
  pageInfo: PageInfo;
  totalCount: Scalars['Int']['output'];
};

/** Relay-style edge. */
export type TicketEdge = {
  __typename?: 'TicketEdge';
  cursor: Scalars['String']['output'];
  node: Ticket;
};

export type TicketFilterInput = {
  assigneeUserId?: InputMaybe<Scalars['ID']['input']>;
  category?: InputMaybe<Array<TicketCategory>>;
  priority?: InputMaybe<Array<TicketPriority>>;
  requesterUserId?: InputMaybe<Scalars['ID']['input']>;
  search?: InputMaybe<Scalars['String']['input']>;
  status?: InputMaybe<Array<TicketStatus>>;
};

/** Envelope for ticket write mutations. */
export type TicketPayload = {
  __typename?: 'TicketPayload';
  ticket: Ticket;
};

/** Priority pill on the ticket. Drives list ordering + colour. */
export enum TicketPriority {
  High = 'HIGH',
  Low = 'LOW',
  Normal = 'NORMAL',
  Rush = 'RUSH'
}

/**
 * Lifecycle status of a ticket.
 * new → in_progress → (waiting) → resolved → closed.
 */
export enum TicketStatus {
  Closed = 'CLOSED',
  InProgress = 'IN_PROGRESS',
  New = 'NEW',
  Resolved = 'RESOLVED',
  Waiting = 'WAITING'
}

export type TimeOffPayload = {
  __typename?: 'TimeOffPayload';
  request: TimeOffRequest;
};

export type TimeOffRequest = {
  __typename?: 'TimeOffRequest';
  decisionNote?: Maybe<Scalars['String']['output']>;
  endDate: Scalars['AWSDate']['output'];
  id: Scalars['ID']['output'];
  notes?: Maybe<Scalars['String']['output']>;
  organizationId: Scalars['ID']['output'];
  portion: Scalars['String']['output'];
  redistributionPolicy: Scalars['String']['output'];
  requesterUserId: Scalars['ID']['output'];
  reviewedAt?: Maybe<Scalars['AWSDateTime']['output']>;
  reviewedByUserId?: Maybe<Scalars['ID']['output']>;
  startDate: Scalars['AWSDate']['output'];
  status: Scalars['String']['output'];
};

export type TimeOffReviewRow = {
  __typename?: 'TimeOffReviewRow';
  activeStepRunCount: Scalars['Int']['output'];
  endDate: Scalars['AWSDate']['output'];
  id: Scalars['ID']['output'];
  notes?: Maybe<Scalars['String']['output']>;
  organizationId: Scalars['ID']['output'];
  portion: Scalars['String']['output'];
  redistributionPolicy: Scalars['String']['output'];
  requesterEmail?: Maybe<Scalars['String']['output']>;
  requesterName?: Maybe<Scalars['String']['output']>;
  requesterUserId: Scalars['ID']['output'];
  startDate: Scalars['AWSDate']['output'];
  status: Scalars['String']['output'];
};

/**
 * LLM token usage breakdown — per-call accounting includes Anthropic
 * prompt-cache hit/miss counters.
 */
export type TokenUsage = {
  __typename?: 'TokenUsage';
  cacheCreationInputTokens: Scalars['Int']['output'];
  cacheReadInputTokens: Scalars['Int']['output'];
  inputTokens: Scalars['Int']['output'];
  outputTokens: Scalars['Int']['output'];
};

export type TransitionDsarRequestInput = {
  bundleExpiresAt?: InputMaybe<Scalars['AWSDateTime']['input']>;
  bundleKey?: InputMaybe<Scalars['String']['input']>;
  error?: InputMaybe<Scalars['String']['input']>;
  id: Scalars['ID']['input'];
  tenantId: Scalars['ID']['input'];
  to: DsarState;
};

/**
 * Trusted device record per user.
 * Per docs/clearpath-architecture/03-data-model.md tier 8 (`trusted_devices`).
 */
export type TrustedDevice = {
  __typename?: 'TrustedDevice';
  createdAt: Scalars['AWSDateTime']['output'];
  deviceFingerprint: Scalars['String']['output'];
  deviceLabel: Scalars['String']['output'];
  id: Scalars['ID']['output'];
  /**
   * P2.1.5 (2026-04-24) — surface the columns that already existed on
   * trusted_devices but were omitted from the resolver's SELECT. Empowers
   * the account SPA to show "last seen from Chrome on macOS, 24.131.x.x"
   * without a follow-up fetch.
   */
  lastIp?: Maybe<Scalars['String']['output']>;
  lastSeenAt?: Maybe<Scalars['AWSDateTime']['output']>;
  lastUserAgent?: Maybe<Scalars['String']['output']>;
  revokedAt?: Maybe<Scalars['AWSDateTime']['output']>;
  trustedUntil?: Maybe<Scalars['AWSDateTime']['output']>;
  userId: Scalars['ID']['output'];
};

export type TrustedDevicePayload = {
  __typename?: 'TrustedDevicePayload';
  device: TrustedDevice;
};

export type UnassignShiftInput = {
  shiftId: Scalars['ID']['input'];
  userId: Scalars['ID']['input'];
};

/**
 * Currently-billable charges that belong to a PAYER rather than to an order —
 * `billing_events.order_id IS NULL` (manual charges, some fees).
 *
 * They cannot be rows in an order-grain list without either lying about which
 * order they belong to or blanking it, so they are their own bucket: one per
 * (payer, currency), selectable as a unit, contributing `billingEventIds` to that
 * payer's generate call. A combined run can include them; a one-invoice-per-order
 * run has no order to pair them with, and a client must say so rather than leaving
 * them silently unbilled.
 */
export type UnattachedChargeBucket = {
  __typename?: 'UnattachedChargeBucket';
  /**
   * Every charge in the bucket, for `generateInvoiceManual.billingEventIds`.
   * Uncapped, and bounded in practice because an invoice run clears them; a cap
   * here would silently truncate a payer's book, which is the failure this whole
   * surface exists to remove.
   */
  billingEventIds: Array<Scalars['ID']['output']>;
  /** The payer. Always a party the caller's own filter named or expanded into. */
  billingPartyId: Scalars['ID']['output'];
  /**
   * One bucket per currency — `generateInvoice` issues one invoice per currency
   * per call, so a mixed-currency payer is genuinely two jobs, not one.
   */
  currency: Scalars['String']['output'];
  /** Exact sum of this bucket's charges. */
  totalAmount: Scalars['Float']['output'];
};

/**
 * ENG-1559 — whether a Unit runs a pipeline. HAS_PIPELINE carries a coarse
 * stage rail; NO_PIPELINE carries none (empty `stages`, null `currentStageKey`) so
 * the portal can distinguish "no pipeline / not yet started" from a real rail
 * without a fake step.
 */
export enum UnitStageKind {
  HasPipeline = 'HAS_PIPELINE',
  NoPipeline = 'NO_PIPELINE'
}

/**
 * ENG-1559 — one coarse, customer-facing stage node on a Unit's rail. Carries
 * ONLY the config-driven milestone key, its customer label, its state, and (when
 * known) the reached date. NEVER an internal step name, assignee, org-unit,
 * routing detail, raw step count or hold reason.
 */
export type UnitStageNode = {
  __typename?: 'UnitStageNode';
  /**
   * Reached date. Null when no trustworthy timestamp exists (always null in the
   * initial server slice; reached dates arrive with a later slice).
   */
  at?: Maybe<Scalars['AWSDateTime']['output']>;
  /**
   * Stable milestone config key (e.g. 'received'). Config identifier, never a
   * step name.
   */
  key: Scalars['String']['output'];
  /** Customer-facing milestone label. */
  label: Scalars['String']['output'];
  state: UnitStageState;
};

/** ENG-1559 — a Unit's rendered position on a coarse stage node. */
export enum UnitStageState {
  Current = 'CURRENT',
  Done = 'DONE',
  Upcoming = 'UPCOMING'
}

/**
 * ENG-1559 — the coarse, customer-safe per-Unit lifecycle stage rail. A PURE
 * FOLD of the single per-Unit derivation (F2): it reads F2's already-resolved step
 * states and can never contradict them. Data-minimised by construction — it
 * exposes no internal step names, assignees, org-units, department/routing, raw
 * step counts or hold reasons.
 */
export type UnitStageSummary = {
  __typename?: 'UnitStageSummary';
  /**
   * Per-unit identity barcode (mirrors OrderProduct.barcodeValue). Nullable —
   * backfills lazily.
   */
  barcodeValue?: Maybe<Scalars['String']['output']>;
  /**
   * The `key` of the node the Unit is currently at, or null when the Unit is
   * fully done or has no pipeline.
   */
  currentStageKey?: Maybe<Scalars['String']['output']>;
  kind: UnitStageKind;
  /** The ordered coarse rail. Empty for a NO_PIPELINE Unit. */
  stages: Array<UnitStageNode>;
};

/** Revoke a principal's access to one of the caller's own dashboards. */
export type UnshareAnalyticsDashboardInput = {
  dashboardId: Scalars['ID']['input'];
  sharedWithUserId: Scalars['ID']['input'];
};

/**
 * Update a dashboard the caller can edit (its owner OR a same-org editor-sharee,
 * T6/ENG-2117). Omitted fields are left unchanged.
 */
export type UpdateAnalyticsDashboardInput = {
  definition?: InputMaybe<Scalars['AWSJSON']['input']>;
  /**
   * Optional optimistic-concurrency token: the dashboard's last-seen updatedAt.
   * When supplied, the save is rejected with a Conflict (never silently
   * last-writer-wins) if the stored updatedAt has since changed — the client then
   * refetches and replays. Concurrent editors make this real; omit it to force the
   * write.
   */
  expectedUpdatedAt?: InputMaybe<Scalars['AWSDateTime']['input']>;
  id: Scalars['ID']['input'];
  name?: InputMaybe<Scalars['String']['input']>;
};

/**
 * Update a widget on a dashboard the caller can edit (its owner OR a same-org
 * editor-sharee, T6/ENG-2117). Omitted fields are left unchanged.
 */
export type UpdateAnalyticsWidgetInput = {
  config?: InputMaybe<Scalars['AWSJSON']['input']>;
  id: Scalars['ID']['input'];
  position?: InputMaybe<Scalars['Int']['input']>;
};

/**
 * Update the caller's org's calculated measure. Omitted fields are left
 * unchanged; the output `key` is immutable. A re-supplied `formula` is re-parsed
 * through the same grammar fail-closed. NotFound if the id is not a live measure in
 * the caller's org.
 */
export type UpdateCalculatedMeasureInput = {
  /**
   * Optional human-authored note (free text, staff-visible, not PHI). Omitted =
   * unchanged; a supplied blank/whitespace value clears it to null.
   */
  description?: InputMaybe<Scalars['String']['input']>;
  format?: InputMaybe<CalculatedMeasureFormat>;
  formula?: InputMaybe<Scalars['AWSJSON']['input']>;
  id: Scalars['ID']['input'];
  label?: InputMaybe<Scalars['String']['input']>;
};

/** Input for updateEscalationChain — PATCH semantics (null = unchanged). */
export type UpdateEscalationChainInput = {
  code?: InputMaybe<Scalars['String']['input']>;
  isActive?: InputMaybe<Scalars['Boolean']['input']>;
  name?: InputMaybe<Scalars['String']['input']>;
  steps?: InputMaybe<Array<EscalationChainStepInput>>;
  targetStepDefinitionId?: InputMaybe<Scalars['ID']['input']>;
};

/**
 * Input for updateExternalParty. Every typed field is tri-state: omit it to
 * keep the stored value, send null or "" to clear it, send a value to set it.
 *
 * `customFieldsSet` (a JSON object) sets keys, values stored exactly as sent;
 * `customFieldsUnset` removes keys. A key may not appear in both. Built-in keys
 * (slug, contact_email, contact_phone, contact_phone_ext,
 * contact_phone_unverified, address, tax_ids, is_active), ClearPath-managed keys
 * and __proto__/constructor/prototype are refused in both: use the typed field.
 * A NEW key must match `^[a-z][a-z0-9_]{0,63}$` and may not resemble a built-in
 * key after case and Unicode folding; keys that already exist stay editable.
 * Limits: 100 keys set and 100 removed per request, 200 keys and 64 KB in total,
 * 8 KB and 8 levels of nesting per value. statement_close_day must be 1-31,
 * default_currency a 3-letter capital ISO 4217 code, billing_address text.
 */
export type UpdateExternalPartyInput = {
  /**
   * Clear-only: null or "" removes the legacy address blob. Any other value is
   * refused; addresses are edited through upsertExternalPartyAddress.
   */
  address?: InputMaybe<Scalars['AWSJSON']['input']>;
  contactEmail?: InputMaybe<Scalars['AWSEmail']['input']>;
  contactPhone?: InputMaybe<Scalars['AWSPhone']['input']>;
  /** See ExternalParty.contactPhoneExtension. */
  contactPhoneExtension?: InputMaybe<Scalars['String']['input']>;
  /**
   * The quarantined phone db/migrations/0171 could not normalise (ENG-647),
   * read from customFields.contact_phone_unverified. Free text, not a phone.
   */
  contactPhoneUnverified?: InputMaybe<Scalars['String']['input']>;
  customFieldsSet?: InputMaybe<Scalars['AWSJSON']['input']>;
  customFieldsUnset?: InputMaybe<Array<Scalars['String']['input']>>;
  id: Scalars['ID']['input'];
  /** Writes the is_active column. Null is treated as omitted. */
  isActive?: InputMaybe<Scalars['Boolean']['input']>;
  slug?: InputMaybe<Scalars['String']['input']>;
  taxIds?: InputMaybe<Scalars['AWSJSON']['input']>;
};

/** Input for updateFeatureFlag. `key` and `flagType` are immutable. */
export type UpdateFeatureFlagInput = {
  archived?: InputMaybe<Scalars['Boolean']['input']>;
  defaultValue?: InputMaybe<Scalars['AWSJSON']['input']>;
  description?: InputMaybe<Scalars['String']['input']>;
  id: Scalars['ID']['input'];
  name?: InputMaybe<Scalars['String']['input']>;
};

/**
 * Input for updateFederationVisibilityConfig. Carries the link id plus a
 * partial patch of the per-link policy (event_visibility_config JSONB and/or
 * allowed_work_types). Null fields are left unchanged. Phase 1.1 (MOL-603).
 */
export type UpdateFederationVisibilityConfigInput = {
  /** Full replacement of allowed_work_types. Null = no change. */
  allowedWorkTypes?: InputMaybe<Array<Scalars['String']['input']>>;
  /** Full replacement of event_visibility_config (JSON). Null = no change. */
  eventVisibilityConfig?: InputMaybe<Scalars['AWSJSON']['input']>;
  /** federation_links.id to update. */
  id: Scalars['ID']['input'];
};

/**
 * Input for updateHoldReasonCode (ENG-687). Every field but `id` is optional;
 * an omitted field is left unchanged (PATCH semantics). Setting `isActive` false
 * retires the code — but prefer deactivateHoldReasonCode for that, which names the
 * action in the audit trail. `code` is editable (holds reference a reason by id,
 * not by code string) but stays unique per organization.
 */
export type UpdateHoldReasonCodeInput = {
  code?: InputMaybe<Scalars['String']['input']>;
  /**
   * The customer-safe portal message. Omitted = unchanged; explicit null or ""
   * clears it (falling back to generic copy).
   */
  customerFacingText?: InputMaybe<Scalars['String']['input']>;
  description?: InputMaybe<Scalars['String']['input']>;
  excludesFromSla?: InputMaybe<Scalars['Boolean']['input']>;
  id: Scalars['ID']['input'];
  isActive?: InputMaybe<Scalars['Boolean']['input']>;
  /**
   * Whether holds under this reason are shown to the customer on the portal.
   * Omitted = unchanged.
   */
  isCustomerVisible?: InputMaybe<Scalars['Boolean']['input']>;
  label?: InputMaybe<Scalars['String']['input']>;
  position?: InputMaybe<Scalars['Int']['input']>;
  responsibleParty?: InputMaybe<HoldResponsibleParty>;
};

export type UpdateInventoryItemInput = {
  code?: InputMaybe<Scalars['String']['input']>;
  customFields?: InputMaybe<Scalars['AWSJSON']['input']>;
  description?: InputMaybe<Scalars['String']['input']>;
  isActive?: InputMaybe<Scalars['Boolean']['input']>;
  name?: InputMaybe<Scalars['String']['input']>;
  productTypeId?: InputMaybe<Scalars['ID']['input']>;
  reorderPoint?: InputMaybe<Scalars['String']['input']>;
  storageLocation?: InputMaybe<Scalars['String']['input']>;
  unitOfMeasure?: InputMaybe<Scalars['String']['input']>;
};

export type UpdateInventoryItemPayload = {
  __typename?: 'UpdateInventoryItemPayload';
  /**
   * How many pipeline-step consumption rules this edit just disabled by
   * deactivating the item. Zero unless the update flipped isActive to false.
   */
  affectedConsumptionRules: Scalars['Int']['output'];
  item: InventoryItem;
};

/**
 * Input for updateMe — self-profile update. No userId (always the
 * caller's own id from the request context); no isActive (deactivation
 * is an admin action on updateUser). Phase P1.9.1 / 2026-04-24.
 */
export type UpdateMeInput = {
  avatarUrl?: InputMaybe<Scalars['AWSURL']['input']>;
  customFields?: InputMaybe<Scalars['AWSJSON']['input']>;
  email?: InputMaybe<Scalars['AWSEmail']['input']>;
  fullName?: InputMaybe<Scalars['String']['input']>;
  locale?: InputMaybe<Scalars['String']['input']>;
  phone?: InputMaybe<Scalars['AWSPhone']['input']>;
  timezone?: InputMaybe<Scalars['String']['input']>;
};

/** ENG-3853 — input for Mutation.updateNasFormatPolicy (the whole list). */
export type UpdateNasFormatPolicyInput = {
  /** Every allowed format key; empty = send no formats. */
  allowedFormats: Array<Scalars['String']['input']>;
};

/** ENG-3975 — input for Mutation.updateNasHoldingGrace. */
export type UpdateNasHoldingGraceInput = {
  /**
   * 10 to 10080, or null for the default. Required: send null explicitly to
   * reset. Applies to holdings released after the change.
   */
  minutes?: InputMaybe<Scalars['Int']['input']>;
};

/**
 * Patch input for updateNotificationPreferences. All fields optional; the
 * server merges into the existing preferences. Per MOL-596.
 */
export type UpdateNotificationPreferencesInput = {
  channels?: InputMaybe<NotificationChannelTogglesInput>;
  dnd?: InputMaybe<NotificationDndInput>;
  types?: InputMaybe<Scalars['AWSJSON']['input']>;
};

/**
 * Input for updateOrder — Round 2b inline pickers / inline metadata edits.
 *
 * Every field is optional; the resolver patches only the keys present on the
 * input. `customFields` deep-merges over the existing `orders.custom_fields`
 * JSONB so a partial patch (e.g. `{ "priority": "rush" }`) does not blow away
 * sibling keys. Use `setOrderStatus` for `status` (separate audit action) and
 * `cancelOrder` for cancellation. The dedicated `updateOrder` field exists so
 * the inline-picker / inline-edit UX has one mutation to bind to.
 *
 * Cedar permission: `clearpath.orders.update` (already in the registry).
 */
export type UpdateOrderInput = {
  /**
   * Partial patch into `orders.custom_fields`. Deep-merges with whatever
   * is already on the row. Pass `null` for a key to delete it.
   */
  customFields?: InputMaybe<Scalars['AWSJSON']['input']>;
  /** Promised completion / due date. */
  estimatedCompletionAt?: InputMaybe<Scalars['AWSDateTime']['input']>;
  /**
   * Optional human-friendly name (stored on `custom_fields.name` for the
   * industry-neutral barcode-only base table).
   */
  name?: InputMaybe<Scalars['String']['input']>;
  /** Free-form operator notes (stored on `custom_fields.notes`). */
  notes?: InputMaybe<Scalars['String']['input']>;
  orderId: Scalars['ID']['input'];
  /** Industry-neutral priority. Persisted to `custom_fields.priority`. */
  priority?: InputMaybe<OrderPriority>;
};

/**
 * Input for updateOrderIntake — patches the ORDER-level intake form
 * response (`orders.intake_form_response`), the order-level twin of
 * updateOrderProductIntake. A 4-product case fills the order-level form
 * ONCE; without this mutation a typo in it is uncorrectable — this is what
 * makes "store once, display everywhere" safe. Validated against the
 * tenant's order-level intake form the same way submitOrder validates it
 * (fails closed on a bound-but-unpublished form). Every change writes an
 * audit_log row visible on the Activity tab.
 */
export type UpdateOrderIntakeInput = {
  intakeFormResponse: Scalars['AWSJSON']['input'];
  orderId: Scalars['ID']['input'];
};

/**
 * Input for updateOrderProductIntake — patches the intake form response
 * on a single order_products row. Lab staff use this to correct doctor-
 * submitted RX answers (typo in shade, missing tooth number, etc) without
 * requiring the doctor to re-submit. Every change writes an audit_log
 * row visible on the Activity tab.
 */
export type UpdateOrderProductIntakeInput = {
  intakeFormResponse: Scalars['AWSJSON']['input'];
  orderProductId: Scalars['ID']['input'];
};

/**
 * Update locally-owned payment method metadata (nickname). Card/bank
 * details are provider-tokenized and cannot be edited here.
 */
export type UpdatePaymentMethodInput = {
  id: Scalars['ID']['input'];
  /** Customer nickname. Pass null or empty string to clear. */
  label?: InputMaybe<Scalars['String']['input']>;
};

export type UpdatePaymentRailSettingsInput = {
  /**
   * Rails to accept for NEW payment methods and NEW charges. MUST be non-empty
   * — an empty list is refused with `PaymentRailsEmpty` rather than stored,
   * because `[]` is read as UNRESTRICTED by the gate and would silently re-enable
   * everything. Turning online payments off entirely is a support request, not a
   * zero-selection save. Values outside CARD / ACH are refused with
   * `PaymentRailUnsupported`.
   */
  enabledRails: Array<PaymentMethodType>;
  /**
   * The PaymentRailSettings.version the operator was shown. A mismatch, or an
   * UPDATE that matches zero rows, returns `Conflict` and writes nothing.
   */
  expectedVersion: Scalars['String']['input'];
};

/** Input for updatePriceBook. Omitted fields are left unchanged. */
export type UpdatePriceBookInput = {
  currency?: InputMaybe<Scalars['String']['input']>;
  description?: InputMaybe<Scalars['String']['input']>;
  id: Scalars['ID']['input'];
  /**
   * Setting false is refused when this is the Org's only remaining default
   * book — one catalog always has to price new orders.
   */
  isDefault?: InputMaybe<Scalars['Boolean']['input']>;
  name?: InputMaybe<Scalars['String']['input']>;
};

export type UpdateRedistributionPolicyInput = {
  id: Scalars['ID']['input'];
  /** none | when_active */
  redistributionPolicy: Scalars['String']['input'];
};

/** Input for updateRole — PATCH semantics (null fields unchanged). */
export type UpdateRoleInput = {
  /**
   * Reclassify the role. Permitted only while every live holder would remain
   * compatible afterwards and no child role carries a different audience —
   * otherwise the database raises (roles_audience_immutable /
   * roles_parent_audience_match). This is the supported remediation path for a
   * role the 0137 backfill left misclassified.
   */
  audience?: InputMaybe<RoleAudience>;
  description?: InputMaybe<Scalars['String']['input']>;
  id: Scalars['ID']['input'];
  name?: InputMaybe<Scalars['String']['input']>;
  parentRoleId?: InputMaybe<Scalars['ID']['input']>;
};

export type UpdateShiftInput = {
  daysOfWeek?: InputMaybe<Array<Scalars['String']['input']>>;
  endTime?: InputMaybe<Scalars['String']['input']>;
  id: Scalars['ID']['input'];
  name?: InputMaybe<Scalars['String']['input']>;
  startTime?: InputMaybe<Scalars['String']['input']>;
};

/**
 * ENG-1043 — input for Mutation.updateSite. `id` is the site's org_unit id.
 * Partial: only the fields present are written (an explicit null clears an address
 * line; an omitted field is preserved). For a plant only the shipFrom* fields are
 * accepted (any other field is a BadRequest naming it); the first save creates
 * the plant's ship-from record.
 */
export type UpdateSiteInput = {
  acceptsIntake?: InputMaybe<Scalars['Boolean']['input']>;
  code?: InputMaybe<Scalars['String']['input']>;
  id: Scalars['ID']['input'];
  isActive?: InputMaybe<Scalars['Boolean']['input']>;
  name?: InputMaybe<Scalars['String']['input']>;
  organizationId: Scalars['ID']['input'];
  performsManufacturing?: InputMaybe<Scalars['Boolean']['input']>;
  shipFromCity?: InputMaybe<Scalars['String']['input']>;
  shipFromCountry?: InputMaybe<Scalars['String']['input']>;
  shipFromLine1?: InputMaybe<Scalars['String']['input']>;
  shipFromLine2?: InputMaybe<Scalars['String']['input']>;
  shipFromName?: InputMaybe<Scalars['String']['input']>;
  shipFromPhone?: InputMaybe<Scalars['String']['input']>;
  shipFromPostalCode?: InputMaybe<Scalars['String']['input']>;
  shipFromRegion?: InputMaybe<Scalars['String']['input']>;
};

export type UpdateSkillDefinitionInput = {
  category?: InputMaybe<Scalars['String']['input']>;
  description?: InputMaybe<Scalars['String']['input']>;
  id: Scalars['ID']['input'];
  name?: InputMaybe<Scalars['String']['input']>;
};

/**
 * Input for updateStepRunCustomFields — records the QC/form checklist
 * responses for a step run. `customFields` is the whole response object (AWSJSON)
 * and replaces any prior saved value for this step run.
 */
export type UpdateStepRunCustomFieldsInput = {
  customFields: Scalars['AWSJSON']['input'];
  stepRunId: Scalars['ID']['input'];
};

export type UpdateTechCapacityInput = {
  capacityUnitsPerDay: Scalars['Int']['input'];
  userId: Scalars['ID']['input'];
};

export type UpdateTenantStatusInput = {
  id: Scalars['ID']['input'];
  reason: Scalars['String']['input'];
  status: TenantStatus;
};

export type UpdateTenantTierInput = {
  newTier: Scalars['String']['input'];
  productId: Scalars['ID']['input'];
  reason: Scalars['String']['input'];
  tenantId: Scalars['ID']['input'];
};

export type UpdateTicketInput = {
  body?: InputMaybe<Scalars['String']['input']>;
  category?: InputMaybe<TicketCategory>;
  customFields?: InputMaybe<Scalars['AWSJSON']['input']>;
  dueAt?: InputMaybe<Scalars['AWSDateTime']['input']>;
  priority?: InputMaybe<TicketPriority>;
  relatedOrderId?: InputMaybe<Scalars['ID']['input']>;
  status?: InputMaybe<TicketStatus>;
  subject?: InputMaybe<Scalars['String']['input']>;
  ticketId: Scalars['ID']['input'];
};

/** Input for updateUser. */
export type UpdateUserInput = {
  avatarUrl?: InputMaybe<Scalars['AWSURL']['input']>;
  customFields?: InputMaybe<Scalars['AWSJSON']['input']>;
  email?: InputMaybe<Scalars['AWSEmail']['input']>;
  fullName?: InputMaybe<Scalars['String']['input']>;
  /**
   * Change the user's home party (`users.home_party_id`). Only applied when
   * a value is provided (same convention as every other field on this input —
   * there is no "clear the party" mutation path). Rejected when the target
   * user isn't CUSTOMER-type, when the id doesn't belong to this organization,
   * or when the party type can't own orders (ENG-user-invite-home-party).
   */
  homePartyId?: InputMaybe<Scalars['ID']['input']>;
  isActive?: InputMaybe<Scalars['Boolean']['input']>;
  locale?: InputMaybe<Scalars['String']['input']>;
  phone?: InputMaybe<Scalars['AWSPhone']['input']>;
  /**
   * Change the user's portal role (ENG-56). The resolver rejects this
   * when userId is the caller's own id — self role-change is blocked
   * server-side to prevent self-lockout; use another admin's account.
   */
  portalRole?: InputMaybe<PortalRole>;
  timezone?: InputMaybe<Scalars['String']['input']>;
  userId: Scalars['ID']['input'];
};

export type UpdateUserSkillInput = {
  certifiedOn?: InputMaybe<Scalars['AWSDateTime']['input']>;
  id: Scalars['ID']['input'];
  lastAssessed?: InputMaybe<Scalars['AWSDateTime']['input']>;
  level?: InputMaybe<Scalars['String']['input']>;
};

/**
 * Input for uploadSharedFile — request a presigned PUT URL scoped to the
 * share-link's step_run. TODO: needs resolver impl (MOL-574). Implementation
 * can delegate to the existing requestFileUpload pathway with the share-link
 * audience gating the file_locations / audit_log writes.
 */
export type UploadSharedFileInput = {
  /** Original filename (display + stored in files.filename). */
  displayName: Scalars['String']['input'];
  mimeType: Scalars['String']['input'];
  sizeBytes: Scalars['Float']['input'];
  stepRunId: Scalars['ID']['input'];
};

/** Payload for uploadSharedFile. */
export type UploadSharedFilePayload = {
  __typename?: 'UploadSharedFilePayload';
  /** When the URL expires. */
  expiresAt?: Maybe<Scalars['AWSDateTime']['output']>;
  /** Pre-allocated file row. */
  file: File;
  /** Required headers for the PUT. */
  uploadHeaders?: Maybe<Scalars['AWSJSON']['output']>;
  /** Presigned PUT URL — single-use, short TTL. */
  uploadUrl: Scalars['AWSURL']['output'];
};

/** Input for upsertAutomationRule. */
export type UpsertAutomationRuleInput = {
  action: Scalars['AWSJSON']['input'];
  /**
   * T12/ENG-370 — attach this rule to a specific pipeline step (attach-by-
   * step_id). Null clears it. Resolver validates same-org ownership (R2).
   */
  attachedStepId?: InputMaybe<Scalars['ID']['input']>;
  condition?: InputMaybe<Scalars['AWSJSON']['input']>;
  description?: InputMaybe<Scalars['String']['input']>;
  id?: InputMaybe<Scalars['ID']['input']>;
  intent?: InputMaybe<Scalars['String']['input']>;
  isActive?: InputMaybe<Scalars['Boolean']['input']>;
  name: Scalars['String']['input'];
  organizationId: Scalars['ID']['input'];
  /**
   * T12/ENG-370 — attach this rule to a pipeline for canvas rendering. Null
   * clears the attachment (org-wide). Resolver validates same-org ownership (R2).
   */
  pipelineId?: InputMaybe<Scalars['ID']['input']>;
  triggerPattern: Scalars['AWSJSON']['input'];
};

/**
 * Input for upsertCommentCategory. `id` null = create; non-null = update.
 * Sort orders default to the next slot in the existing list when omitted.
 */
export type UpsertCommentCategoryInput = {
  color?: InputMaybe<Scalars['String']['input']>;
  id?: InputMaybe<Scalars['ID']['input']>;
  name: Scalars['String']['input'];
  sortOrder?: InputMaybe<Scalars['Int']['input']>;
};

/**
 * Record or correct ONE exemption certificate.
 *
 * A BLANKET certificate — the per-customer "charge this customer no tax" shape —
 * is `taxJurisdictionId: null` with `taxCategoryCode: null` and
 * `status: ACTIVE`.
 */
export type UpsertCustomerTaxExemptionInput = {
  /**
   * Required and non-blank. The column is NOT NULL and this slice deliberately
   * does NOT migrate it nullable.
   */
  certificateNumber: Scalars['String']['input'];
  /** Defaults to `OTHER` — "recorded, not classified". */
  exemptionReason?: InputMaybe<TaxExemptionReason>;
  /**
   * Required when `id` is supplied. Without it two concurrent saves both report
   * success and one is silently lost.
   */
  expectedVersion?: InputMaybe<Scalars['String']['input']>;
  /**
   * The customer to relieve. Must be a party THIS organization bills — a party
   * belonging to another organization is refused by name
   * (`TaxExemptionPartyNotFound`). ⚠️ This is the BILLABLE party on the invoice,
   * not the ship-to.
   */
  externalPartyId: Scalars['ID']['input'];
  /**
   * Null creates. Supplied edits an existing certificate THIS organization
   * holds; another organization's row is refused by name
   * (`TaxExemptionNotFound`), never silently ignored.
   */
  id?: InputMaybe<Scalars['ID']['input']>;
  /**
   * Defaults to `PENDING`, which KEEPS CHARGING TAX. `REVOKED` is not settable
   * here; use `revokeCustomerTaxExemption`, which records it under its own audit
   * action.
   */
  status?: InputMaybe<TaxExemptionStatus>;
  /** Null = every tax category. */
  taxCategoryCode?: InputMaybe<Scalars['String']['input']>;
  /**
   * Null = every jurisdiction. May be a shared PLATFORM jurisdiction or one
   * this organization owns; another organization's is refused by name.
   */
  taxJurisdictionId?: InputMaybe<Scalars['ID']['input']>;
  validFrom: Scalars['AWSDate']['input'];
  /** EXCLUSIVE, and must be after `validFrom`. */
  validTo?: InputMaybe<Scalars['AWSDate']['input']>;
};

/** Input for upserting an external integration row. */
export type UpsertExternalIntegrationInput = {
  /** Provider-specific configuration JSON (apiBaseUrl, clinicId, etc.). */
  config: Scalars['AWSJSON']['input'];
  /**
   * Full Secrets Manager ARN (or local sentinel such as local/fedex) to store.
   * On update, null/omit/blank keeps the existing ARN; do not send the masked
   * value returned on ExternalIntegration reads. Null on create means no secret.
   */
  credentialsSecretArn?: InputMaybe<Scalars['String']['input']>;
  /** Omit on create, supply on update. */
  id?: InputMaybe<Scalars['ID']['input']>;
  integrationType: ExternalIntegrationType;
  /** Whether the row is active and eligible for polling/webhooks. */
  isActive?: InputMaybe<Scalars['Boolean']['input']>;
  name: Scalars['String']['input'];
  /**
   * Provider id — must match a registered adapter (e.g., `3shape`, `itero`,
   * `medit`, `roland`, `imes_icore`, `generic_nesting`, `manual_upload`).
   */
  provider: Scalars['String']['input'];
};

/**
 * Input for upsertExternalPartyAddress. `id` null = insert; non-null = update.
 * Update is a PATCH: an omitted field keeps its stored value; an explicit null
 * clears it.
 */
export type UpsertExternalPartyAddressInput = {
  city?: InputMaybe<Scalars['String']['input']>;
  country?: InputMaybe<Scalars['String']['input']>;
  customFields?: InputMaybe<Scalars['AWSJSON']['input']>;
  externalPartyId: Scalars['ID']['input'];
  id?: InputMaybe<Scalars['ID']['input']>;
  isPrimary?: InputMaybe<Scalars['Boolean']['input']>;
  kind?: InputMaybe<Scalars['String']['input']>;
  label?: InputMaybe<Scalars['String']['input']>;
  lat?: InputMaybe<Scalars['Float']['input']>;
  lng?: InputMaybe<Scalars['Float']['input']>;
  metadata?: InputMaybe<Scalars['AWSJSON']['input']>;
  postalCode?: InputMaybe<Scalars['String']['input']>;
  region?: InputMaybe<Scalars['String']['input']>;
  street1?: InputMaybe<Scalars['String']['input']>;
  street2?: InputMaybe<Scalars['String']['input']>;
  /** Omitted keeps the stored tags; [] or null = every purpose. */
  tags?: InputMaybe<Array<AddressPurpose>>;
};

/**
 * Input for upsertExternalParty — tenant customer/vendor graph. `id` null
 * = insert; non-null = update. `parentPartyId` is the hierarchical parent
 * (DSO above clinic, vendor parent, etc); omitting it on an update clears it.
 *
 * slug, contactEmail, contactPhone, contactPhoneExtension,
 * contactPhoneUnverified, address and taxIds are tri-state on an update: omit to
 * keep, null or "" to clear, a value to set. `customFields` is merged over the
 * stored map under the same key and size rules as
 * UpdateExternalPartyInput.customFieldsSet (use updateExternalParty to remove a
 * custom key).
 */
export type UpsertExternalPartyInput = {
  address?: InputMaybe<Scalars['AWSJSON']['input']>;
  contactEmail?: InputMaybe<Scalars['AWSEmail']['input']>;
  contactPhone?: InputMaybe<Scalars['AWSPhone']['input']>;
  /** See ExternalParty.contactPhoneExtension. ENG-458. */
  contactPhoneExtension?: InputMaybe<Scalars['String']['input']>;
  /** See UpdateExternalPartyInput.contactPhoneUnverified. */
  contactPhoneUnverified?: InputMaybe<Scalars['String']['input']>;
  customFields?: InputMaybe<Scalars['AWSJSON']['input']>;
  /**
   * Customer number to store on this party (ENG-2524).
   *
   * Omit it and the server decides: on a CREATE, if the tenant has configured a
   * `customerNumberScheme`, one is ALLOCATED from the tenant sequence and
   * `customerNumberSource` becomes `generated`; with no scheme configured the
   * party simply has no number. Supply it and the value is stored verbatim with
   * source `manual`.
   *
   * There is no counterpart for `imported` here — that source is written by the
   * ABS backfill (ENG-2527), not through this mutation.
   *
   * A SYSTEM-MANAGED NUMBER IS IMMUTABLE — both `generated` and `imported`
   * sources (a `manual` or absent number stays editable). Passing a different
   * `customerNumber` is REFUSED, and `customerNumberSource` is frozen with it,
   * so you cannot relabel to `manual` to sidestep the lock. Enforcement is
   * layered: this mutation rejects the change AND a BEFORE UPDATE trigger
   * rejects it in the database (ENG-3048). The point is provenance and
   * collision-safety — a system-assigned identifier keeps its origin, and a
   * minted number cannot be rewritten into, or made to collide with, a real ABS
   * account number before that account is imported. This is NOT the ABS
   * crosswalk join: that binds on `custom_fields.account_number(s)`, not
   * `customerNumber`, and its binding integrity is enforced separately
   * (ENG-3046).
   */
  customerNumber?: InputMaybe<Scalars['String']['input']>;
  /**
   * ENG-3738 — default bill-to. Omit to leave unchanged, null to clear, or an
   * id to set: this party or an active ancestor in the same organization
   * (validated after any re-parent in the same call). Refused otherwise. Sending
   * this field (any value) also requires clearpath.billing.invoice.
   */
  defaultBillingPartyId?: InputMaybe<Scalars['ID']['input']>;
  id?: InputMaybe<Scalars['ID']['input']>;
  isActive?: InputMaybe<Scalars['Boolean']['input']>;
  name: Scalars['String']['input'];
  organizationId: Scalars['ID']['input'];
  parentPartyId?: InputMaybe<Scalars['ID']['input']>;
  partyType: Scalars['String']['input'];
  slug?: InputMaybe<Scalars['String']['input']>;
  taxIds?: InputMaybe<Scalars['AWSJSON']['input']>;
};

/**
 * Input for upsertFailureReasonCode. `stepDefinitionId` null = org-wide
 * code; non-null = scoped to one pipeline step (and its substeps by
 * convention). `defaultLoopbackStepId` is stored in custom_fields.
 * severity defaults to MEDIUM when omitted.
 */
export type UpsertFailureReasonCodeInput = {
  code: Scalars['String']['input'];
  defaultLoopbackStepId?: InputMaybe<Scalars['ID']['input']>;
  description?: InputMaybe<Scalars['String']['input']>;
  id?: InputMaybe<Scalars['ID']['input']>;
  isActive?: InputMaybe<Scalars['Boolean']['input']>;
  label: Scalars['String']['input'];
  severity?: InputMaybe<FailureReasonCodeSeverity>;
  stepDefinitionId?: InputMaybe<Scalars['ID']['input']>;
};

/**
 * Input for upsertFieldLabel.
 * `fieldName` is the internal DB field key (e.g. 'orders.customer_party_id'
 * or 'order_products.shade'). `locale` is an ISO code; default 'en'. The
 * primary key is (organizationId, fieldName, locale) — repeated calls
 * update the `displayLabel` in place.
 */
export type UpsertFieldLabelInput = {
  displayLabel: Scalars['String']['input'];
  fieldName: Scalars['String']['input'];
  locale?: InputMaybe<Scalars['String']['input']>;
  organizationId: Scalars['ID']['input'];
};

/** Input for upsertFormDefinition. */
export type UpsertFormDefinitionInput = {
  entityDefinitionId?: InputMaybe<Scalars['ID']['input']>;
  formKind: FormKind;
  id?: InputMaybe<Scalars['ID']['input']>;
  name: Scalars['String']['input'];
  organizationId: Scalars['ID']['input'];
  schema: Scalars['AWSJSON']['input'];
  uiSchema?: InputMaybe<Scalars['AWSJSON']['input']>;
};

/** ENG-658 input for upsertFormField. */
export type UpsertFormFieldInput = {
  code: Scalars['String']['input'];
  fieldType: FieldType;
  helpText?: InputMaybe<Scalars['String']['input']>;
  id?: InputMaybe<Scalars['ID']['input']>;
  /**
   * ENG-658 Part D — see `FormField.isInventoryTracked`. Omitted leaves the
   * current value; false does NOT clear the per-option links, so re-ticking the
   * box restores the work rather than making an admin redo it.
   */
  isInventoryTracked?: InputMaybe<Scalars['Boolean']['input']>;
  name: Scalars['String']['input'];
  /** Full option set — delete+reinsert convention. */
  options?: InputMaybe<Array<UpsertFormFieldOptionInput>>;
  /** See `FormField.placeholder`. Null/omitted clears it. */
  placeholder?: InputMaybe<Scalars['String']['input']>;
};

export type UpsertFormFieldOptionInput = {
  id?: InputMaybe<Scalars['ID']['input']>;
  /**
   * ENG-658 Part D — the stock item this option corresponds to. Null clears
   * the link. When updating an existing field, omission preserves the link of the
   * option matched by id, then exact value; a new unmatched option remains
   * unlinked. Validated same-org before the write; the DB trigger
   * trg_form_field_options_enforce_inventory_org is the backstop.
   */
  inventoryItemId?: InputMaybe<Scalars['ID']['input']>;
  label: Scalars['String']['input'];
  position: Scalars['Int']['input'];
  value: Scalars['String']['input'];
};

export type UpsertLedgerAccountInput = {
  /** Frozen once an invoice allocation references the account. */
  accountNumber: Scalars['String']['input'];
  /**
   * Frozen once an invoice allocation references the account; must stay
   * allowed for every mapping and class already pointing at it.
   */
  accountType: LedgerAccountType;
  /** True removes the parent. Refused together with `parentId`. */
  clearParent?: InputMaybe<Scalars['Boolean']['input']>;
  /** Omit to create. */
  id?: InputMaybe<Scalars['ID']['input']>;
  /** Default true on create; omitted on update = unchanged. */
  isActive?: InputMaybe<Scalars['Boolean']['input']>;
  name: Scalars['String']['input'];
  /**
   * Same organization; may not make the account its own ancestor. Omitted or
   * null on update = parent unchanged; use `clearParent` to remove it.
   */
  parentId?: InputMaybe<Scalars['ID']['input']>;
};

export type UpsertLedgerCustomerClassInput = {
  accountId: Scalars['ID']['input'];
  /** Upper-case letters, digits and underscore; 1 to 64. */
  code: Scalars['String']['input'];
  /** Omit to create. */
  id?: InputMaybe<Scalars['ID']['input']>;
  name: Scalars['String']['input'];
};

/**
 * Per MOL-595 — create or update a branded template. Unique by
 * (organizationId, notificationType, channel, locale).
 */
export type UpsertNotificationTemplateInput = {
  bodyTemplate: Scalars['String']['input'];
  channel: NotificationChannel;
  id?: InputMaybe<Scalars['ID']['input']>;
  isActive?: InputMaybe<Scalars['Boolean']['input']>;
  locale: Scalars['String']['input'];
  notificationType: Scalars['String']['input'];
  subjectTemplate?: InputMaybe<Scalars['String']['input']>;
};

/** Input for upsertOrgUnit. */
export type UpsertOrgUnitInput = {
  /** Tenant-defined extension blob. */
  customFields?: InputMaybe<Scalars['AWSJSON']['input']>;
  /** Existing row id when updating; null when creating. */
  id?: InputMaybe<Scalars['ID']['input']>;
  /** Display name. */
  name: Scalars['String']['input'];
  /** Parent org_unit. Null for top-level (department / plant). */
  parentOrgUnitId?: InputMaybe<Scalars['ID']['input']>;
  /** Sort order within the parent. Defaults to 0. */
  position?: InputMaybe<Scalars['Int']['input']>;
  /** Optional URL-safe slug. Derived from name when omitted. */
  slug?: InputMaybe<Scalars['String']['input']>;
  /** Discriminator: department / team / plant / division / other. */
  unitType: OrgUnitType;
};

/** Input for upsertPartyPricingAgreement. */
export type UpsertPartyPricingAgreementInput = {
  agreementType: PartyAgreementType;
  id?: InputMaybe<Scalars['ID']['input']>;
  isActive?: InputMaybe<Scalars['Boolean']['input']>;
  lines?: InputMaybe<Array<UpsertPartyPricingLineInput>>;
  organizationId: Scalars['ID']['input'];
  partyId: Scalars['ID']['input'];
  resolutionMode: PricingResolutionMode;
  scope?: InputMaybe<Scalars['AWSJSON']['input']>;
  /** @deprecated ENG-2478: use validFromDay — the server anchors it in the organization's timezone. A raw instant is stored as-is with no day provenance. */
  validFrom?: InputMaybe<Scalars['AWSDateTime']['input']>;
  /**
   * ENG-2478. Calendar day for validFrom, e.g. from a date input. Supplying
   * both this field and its instant counterpart for the same bound is
   * rejected. When only the day is supplied the server anchors it in the
   * organization's timezone. When only the instant is supplied it is stored
   * as-is with a null day (the legacy path).
   */
  validFromDay?: InputMaybe<Scalars['AWSDate']['input']>;
  /** @deprecated ENG-2478: use validUntilDay — the server anchors it in the organization's timezone. A raw instant is stored as-is with no day provenance. */
  validUntil?: InputMaybe<Scalars['AWSDateTime']['input']>;
  /** ENG-2478. Calendar day for validUntil — same rule as validFromDay. */
  validUntilDay?: InputMaybe<Scalars['AWSDate']['input']>;
};

export type UpsertPartyPricingLineInput = {
  /**
   * Trimmed; blank = the standard name. At most 200 characters, no control,
   * bidi or zero-width characters. Refused on a line with no productTypeId.
   */
  contractedName?: InputMaybe<Scalars['String']['input']>;
  discountPercent?: InputMaybe<Scalars['Float']['input']>;
  id?: InputMaybe<Scalars['ID']['input']>;
  overridePrice?: InputMaybe<Scalars['Float']['input']>;
  position: Scalars['Int']['input'];
  productCategoryId?: InputMaybe<Scalars['ID']['input']>;
  productTypeId?: InputMaybe<Scalars['ID']['input']>;
  /**
   * ENG-2564 — the canonical variant token. COMPUTED server-side from
   * `variantSelections` (resolveVariantKeysForLines) and NEVER trusted off the
   * wire: any client-supplied value is overwritten before a line is written, so a
   * caller cannot desync the token from the one an order carries. Present on the
   * type because the shared line shape carries it once resolved. NULL = the
   * SKU-level line.
   */
  variantKey?: InputMaybe<Scalars['String']['input']>;
  /**
   * ENG-2564 — the admin's per-variant-defining-field selections for this line,
   * as a JSON object { fieldCode: value | value[] } of OPTION CODES. The server
   * canonicalizes it into variant_key via the SAME @clearpath/form-rules
   * computeVariantKey an order submit uses, so the authored token provably equals
   * the order-carried one. Omitted / empty object = the SKU-level line
   * (variant_key NULL). Refused on a line with no productTypeId.
   */
  variantSelections?: InputMaybe<Scalars['AWSJSON']['input']>;
  volumeTiers?: InputMaybe<Scalars['AWSJSON']['input']>;
};

export type UpsertPipelineConsumptionRuleInput = {
  consumeOnFailure?: InputMaybe<Scalars['Boolean']['input']>;
  inventoryItemId: Scalars['ID']['input'];
  pipelineStepId: Scalars['ID']['input'];
  /** Positive NUMERIC(18,4) decimal string. */
  quantityPerUnit: Scalars['String']['input'];
  triggerMode?: InputMaybe<ConsumptionTriggerMode>;
};

/**
 * Round 4 — input for upsertPipelineGraph. Used by both the YAML editor and
 * the reactflow node-graph editor; both serialize to YAML before save and
 * round-trip through the same mutation.
 */
export type UpsertPipelineGraphInput = {
  /**
   * Canonical YAML — the graph editor serializes graph state to YAML before
   * save. Round-trip with the YAML editor is lossless for the modelled subset.
   */
  definitionYaml: Scalars['String']['input'];
  /** Reactflow node positions + viewport. Saved verbatim. */
  graphMetadata: Scalars['AWSJSON']['input'];
  pipelineId: Scalars['ID']['input'];
};

/** Input for upsertPipeline. */
export type UpsertPipelineInput = {
  description?: InputMaybe<Scalars['String']['input']>;
  id?: InputMaybe<Scalars['ID']['input']>;
  name: Scalars['String']['input'];
  organizationId: Scalars['ID']['input'];
  /**
   * ENG-1579 — what KIND of pipeline to create. Defaults to PRODUCT, which is
   * every pre-existing caller's behaviour.
   *
   * ORDER creates the organization's order-step host: the pipeline carrying the
   * order-level DAG. At most one may exist per organization
   * (uq_pipelines_one_order_scope_per_org, db/migrations/0326), so a second
   * attempt is refused rather than silently creating a rival host — two would
   * cross-wire orders onto the wrong barrier.
   *
   * Ignored on UPDATE (when `id` is set): a pipeline's scope is not something a
   * rename should be able to change, and flipping it would orphan every step it
   * already owns from the readers that filter on scope.
   */
  pipelineScope?: InputMaybe<PipelineScope>;
  productTypeId?: InputMaybe<Scalars['ID']['input']>;
};

/** Input for upsertPipelineStep. */
export type UpsertPipelineStepInput = {
  customFields?: InputMaybe<Scalars['AWSJSON']['input']>;
  description?: InputMaybe<Scalars['String']['input']>;
  estimatedDurationMinutes?: InputMaybe<Scalars['Int']['input']>;
  id?: InputMaybe<Scalars['ID']['input']>;
  isMandatory: Scalars['Boolean']['input'];
  name: Scalars['String']['input'];
  orderPipelineId?: InputMaybe<Scalars['ID']['input']>;
  orgUnitId?: InputMaybe<Scalars['ID']['input']>;
  parentStepId?: InputMaybe<Scalars['ID']['input']>;
  pipelineId?: InputMaybe<Scalars['ID']['input']>;
  position: Scalars['Int']['input'];
  scope: StepScope;
  stepLibraryId?: InputMaybe<Scalars['ID']['input']>;
  stepType: StepType;
  vendorConfig?: InputMaybe<Scalars['AWSJSON']['input']>;
};

/** Version-checked organization-level authoring input for one portal surface. */
export type UpsertPortalViewConfigInput = {
  expectedVersion: Scalars['Int']['input'];
  organizationId: Scalars['ID']['input'];
  overrides: Array<PortalViewSlotOverrideInput>;
  surface: Scalars['String']['input'];
};

/**
 * Input for upsertPriceBookEntry. Creates a new priced SKU on the book, or
 * updates the currently-open entry for that (priceBookId, productTypeId) pair.
 * Omit `validFrom` to target/extend the currently-open entry rather than
 * appending a duplicate — the underlying uniqueness is (price_book_id,
 * product_type_id, valid_from), which defaults to now() on every insert.
 */
export type UpsertPriceBookEntryInput = {
  currency?: InputMaybe<Scalars['String']['input']>;
  priceBookId: Scalars['ID']['input'];
  productTypeId: Scalars['ID']['input'];
  unitPrice: Scalars['Float']['input'];
  /** @deprecated ENG-2478: use validFromDay — the server anchors it in the organization's timezone. A raw instant is stored as-is with no day provenance. */
  validFrom?: InputMaybe<Scalars['AWSDateTime']['input']>;
  /**
   * ENG-2478. Calendar day for validFrom, e.g. from a date input. Supplying
   * both this field and its instant counterpart for the same bound is
   * rejected. When only the day is supplied the server anchors it in the
   * organization's timezone. When only the instant is supplied it is stored
   * as-is with a null day (the legacy path).
   */
  validFromDay?: InputMaybe<Scalars['AWSDate']['input']>;
  /** @deprecated ENG-2478: use validUntilDay — the server anchors it in the organization's timezone. A raw instant is stored as-is with no day provenance. */
  validUntil?: InputMaybe<Scalars['AWSDateTime']['input']>;
  /** ENG-2478. Calendar day for validUntil — same rule as validFromDay. */
  validUntilDay?: InputMaybe<Scalars['AWSDate']['input']>;
};

export type UpsertProductBundleComponentInput = {
  defaultIntakeFormResponse?: InputMaybe<Scalars['AWSJSON']['input']>;
  id?: InputMaybe<Scalars['ID']['input']>;
  position: Scalars['Int']['input'];
  productTypeId: Scalars['ID']['input'];
  quantity: Scalars['Int']['input'];
  required: Scalars['Boolean']['input'];
};

/** Input for upsertProductBundle. */
export type UpsertProductBundleInput = {
  /**
   * Short unique identifier. OPTIONAL on create — omit it and the server
   * generates a unique code from the name (so a non-technical admin never has to
   * invent one or resolve a collision); required when updating an existing bundle.
   */
  code?: InputMaybe<Scalars['String']['input']>;
  /**
   * Full component set — replaces the existing components when provided; omit to
   * leave them untouched.
   */
  components?: InputMaybe<Array<UpsertProductBundleComponentInput>>;
  customFields?: InputMaybe<Scalars['AWSJSON']['input']>;
  description?: InputMaybe<Scalars['String']['input']>;
  id?: InputMaybe<Scalars['ID']['input']>;
  name: Scalars['String']['input'];
  organizationId: Scalars['ID']['input'];
};

/**
 * Input for upsertProductType. ENG-658 Part C — no `attributes` field
 * anymore: removed outright (decision #3), not kept as a compat wrapper. A
 * product type's fields are now managed through two separate actions:
 * `formDefinitionId` below (CONNECT — pick or create a form via
 * product_type_forms) and upsertFormDefinition (EDIT — that form's own
 * fields), mirroring how `pipelineId` already connects a pipeline here while
 * the pipeline's own steps are edited through a separate mutation.
 */
export type UpsertProductTypeInput = {
  code: Scalars['String']['input'];
  currency?: InputMaybe<Scalars['String']['input']>;
  customFields?: InputMaybe<Scalars['AWSJSON']['input']>;
  /**
   * ENG-658 Part C — (re)connect this product type's intake form via
   * product_type_forms. Omitted leaves the existing connection untouched
   * (same convention as pipelineId above) — there is no way to disconnect
   * through this mutation.
   */
  formDefinitionId?: InputMaybe<Scalars['ID']['input']>;
  id?: InputMaybe<Scalars['ID']['input']>;
  /**
   * Out of the tax base EVERYWHERE, always — `product_types.is_tax_exempt`.
   *
   * **This is a UNIVERSAL assertion and is not the same thing as a category.**
   * Categories model taxability that VARIES by jurisdiction, which is why the rate
   * key is (jurisdiction, category). "Never taxed anywhere" expressed as rate rows
   * would need an exempt row in every jurisdiction — the inversion trap and the
   * matrix explosion at once. So it is a boolean, checked BEFORE any address is
   * resolved, and a line carrying it gets exactly one note
   * (`PRODUCT_NOT_TAXABLE`) instead of also collecting the
   * `NO_NEXUS` / `NO_RATE_FOUND` noise that means "you are probably
   * under-collecting".
   *
   * ⚠️ FOR A BILLING EVENT COVERING SEVERAL PRODUCTS THE FLAG IS `AND`-ed, NOT
   * `OR`-ed: the line is relieved only if EVERY product on it is exempt. A mixed
   * event is therefore taxed IN FULL, which over-collects on the exempt portion —
   * a wrong number in the SAFE direction, recorded rather than hidden. `OR` would
   * relieve the whole line because one product on it was exempt.
   *
   * Requires `taxExemptReason` to be non-blank; the database refuses the pair
   * otherwise.
   */
  isTaxExempt?: InputMaybe<Scalars['Boolean']['input']>;
  listPrice?: InputMaybe<Scalars['Float']['input']>;
  name: Scalars['String']['input'];
  organizationId: Scalars['ID']['input'];
  /**
   * ENG-2987 — `product_types.pipeline_exempt`: this product type's
   * org-wide slot intentionally has no in-house pipeline (a shipping or fee line,
   * an outsourced part). It publishes and is ordered without a pipeline; nothing
   * is produced or tracked for its lines, and an order made only of such lines is
   * refused. Price, invoicing, SLA, shipping and entitlement are unaffected.
   *
   * Moving it requires an organization-wide internal administrator (not only
   * `clearpath.product_types.write`) and is audited. Refused while the product
   * type is LIVE, while it has an org-wide pipeline, and — when clearing it —
   * while open orders hold its lines with no production run. Omitted leaves the
   * current value.
   */
  pipelineExempt?: InputMaybe<Scalars['Boolean']['input']>;
  pipelineId?: InputMaybe<Scalars['ID']['input']>;
  productCategoryId?: InputMaybe<Scalars['ID']['input']>;
  /**
   * Which KIND of thing this is for tax — `product_types.tax_category_code`,
   * a key into the closed `tax_categories` vocabulary (`GENERAL`,
   * `MEDICAL_DEVICE`, `SERVICE`, `SHIPPING`).
   *
   * It is the second axis of every rate lookup: the same jurisdiction taxes a
   * fabricated device, a pure service and a shipping charge differently, and in
   * some states differently enough to flip taxable/exempt outright. An
   * unclassified SKU falls back to `GENERAL` and the invoice records
   * `CATEGORY_FALLBACK`.
   *
   * **This field had no GraphQL or frontend reader at all before slice 4** — the
   * column shipped in Phase 0 and was only ever set by a seed.
   */
  taxCategoryCode?: InputMaybe<Scalars['String']['input']>;
  /**
   * WHY this product is out of the tax base. REQUIRED whenever
   * `isTaxExempt` is true and refused as blank by
   * `chk_product_types_tax_exempt_reason`.
   *
   * Not documentation: an unexplained exemption is the lab's liability at audit,
   * and this is the only place the justification is recorded.
   */
  taxExemptReason?: InputMaybe<Scalars['String']['input']>;
  warrantyPeriodDays?: InputMaybe<Scalars['Int']['input']>;
};

/** Input for upsertSlaPolicy (reuses the canonical SLACalculationMode enum). */
export type UpsertSlaPolicyInput = {
  appliesToOrgUnitId?: InputMaybe<Scalars['ID']['input']>;
  appliesToProductTypeId?: InputMaybe<Scalars['ID']['input']>;
  appliesToStepDefinitionId?: InputMaybe<Scalars['ID']['input']>;
  breachWarnAtPct?: InputMaybe<Scalars['Int']['input']>;
  businessCalendar?: InputMaybe<Scalars['AWSJSON']['input']>;
  calculationMode?: InputMaybe<SlaCalculationMode>;
  code: Scalars['String']['input'];
  id?: InputMaybe<Scalars['ID']['input']>;
  isActive?: InputMaybe<Scalars['Boolean']['input']>;
  name: Scalars['String']['input'];
  organizationId: Scalars['ID']['input'];
  targetDurationMinutes: Scalars['Int']['input'];
};

/**
 * Input for upsertStepDependency. `id` null = insert; non-null = update.
 * `pipelineId` is the authoritative scope — both step ids must belong to
 * this pipeline AND the pipeline must belong to the viewer's org.
 */
export type UpsertStepDependencyInput = {
  barrierTarget?: InputMaybe<Scalars['String']['input']>;
  branchCondition?: InputMaybe<Scalars['AWSJSON']['input']>;
  dependencyType: DependencyType;
  failureRules?: InputMaybe<Scalars['AWSJSON']['input']>;
  fromStepId: Scalars['ID']['input'];
  id?: InputMaybe<Scalars['ID']['input']>;
  pipelineId: Scalars['ID']['input'];
  toStepId: Scalars['ID']['input'];
};

/**
 * Input for upsertStepLibraryEntry — reusable step templates. `id` null =
 * insert; non-null = update. `key` is the stable snake_case reference used by
 * pipelines to bind to a library entry (unique per tenant).
 */
export type UpsertStepLibraryEntryInput = {
  defaultAssignmentPolicy?: InputMaybe<AssignmentPolicy>;
  defaultConfig?: InputMaybe<Scalars['AWSJSON']['input']>;
  defaultDurationMinutes?: InputMaybe<Scalars['Int']['input']>;
  defaultStepType: StepType;
  description?: InputMaybe<Scalars['String']['input']>;
  id?: InputMaybe<Scalars['ID']['input']>;
  key: Scalars['String']['input'];
  name: Scalars['String']['input'];
  organizationId: Scalars['ID']['input'];
};

/**
 * Create or correct ONE tax jurisdiction, with its boundary, its rates and its
 * registration, in a single transaction.
 */
export type UpsertTaxJurisdictionInput = {
  /** ISO-3166-1 alpha-2, e.g. `US`. */
  country: Scalars['String']['input'];
  /**
   * The date this rate takes effect. May be in the future — that is how the
   * quarterly rate cadence is survivable — and may be in the past only while no
   * invoice has been billed against this jurisdiction.
   */
  effectiveFrom: Scalars['AWSDate']['input'];
  /**
   * The `version` the operator was shown. REQUIRED when `id` is supplied; a
   * mismatch returns `Conflict` and writes nothing.
   */
  expectedVersion?: InputMaybe<Scalars['String']['input']>;
  /**
   * Null creates. Supplied edits an existing jurisdiction THIS organization
   * owns — a shared platform row is refused by name (`TaxPlatformRowImmutable`),
   * never silently ignored.
   */
  id?: InputMaybe<Scalars['ID']['input']>;
  /**
   * Display label. `Beverly Hills 90210`. Never used as a key — the source
   * city column in a published rate sheet is routinely wrong.
   */
  name: Scalars['String']['input'];
  /**
   * Normalised by the tax engine's OWN postal normaliser before storage: a US
   * ZIP+4 is reduced to its five-digit prefix.
   *
   * REQUIRED by `upsertTaxJurisdiction` and REFUSED by `upsertTaxStateDefault`,
   * which covers a whole region and has no postal key — hence nullable here, on
   * the input both share. The server enforces the rule per field rather than the
   * SDL enforcing it for one caller and not the other.
   */
  postalCode?: InputMaybe<Scalars['String']['input']>;
  /**
   * The rate AS A PERCENTAGE, exactly as an operator types it: `"9.75"`.
   *
   * **Never a fraction.** The server does the division by 100, so there is one
   * conversion in the system rather than one per caller. `"0.0975"` here is read
   * as 0.0975% and refused by the plausibility floor, which is the point: a
   * fraction entered where a percentage was expected is a silent 100x
   * under-collection that passes every database constraint.
   */
  ratePercent: Scalars['String']['input'];
  /**
   * The state/province whose default rate this postal code overrides. It is
   * what the fallback and floor checks are made against, so it is required even
   * though the boundary itself is postal-keyed.
   */
  regionCode: Scalars['String']['input'];
};

/**
 * Input for upsertTenantSettings — there is exactly one tenant_settings row
 * per organization; this upsert targets by organizationId and inserts the row
 * the first time, then updates it thereafter.
 */
export type UpsertTenantSettingsInput = {
  businessCalendar?: InputMaybe<Scalars['AWSJSON']['input']>;
  /**
   * ENG-6 — set/replace the case-number scheme (object
   * `{version, prefix, width, start, resetCadence}`), pass null to disable
   * (→ default random id), or omit to leave unchanged. Server-validated; every
   * field editable anytime and existing case numbers are never renumbered.
   */
  caseNumberScheme?: InputMaybe<Scalars['AWSJSON']['input']>;
  /**
   * ENG-2523 — set/replace the customer-number scheme (object
   * `{version, prefix, width, start}`, all four required), pass null to disable
   * (→ nothing auto-allocated), or omit to leave unchanged. Server-validated. An
   * input carrying `resetCadence` is REJECTED rather than silently stripped —
   * see the field doc on TenantSettings.
   */
  customerNumberScheme?: InputMaybe<Scalars['AWSJSON']['input']>;
  defaultBarcodeFormat?: InputMaybe<BarcodeFormat>;
  defaultLocale?: InputMaybe<Scalars['String']['input']>;
  defaultTimezone?: InputMaybe<Scalars['String']['input']>;
  /**
   * Deprecated alias. Writes idle_timeout_minutes_main_app. Kept for
   * back-compat with admin-tenant-settings; new callers should use the
   * audience-specific fields below.
   */
  idleTimeoutMinutes?: InputMaybe<Scalars['Int']['input']>;
  idleTimeoutMinutesAdmin?: InputMaybe<Scalars['Int']['input']>;
  idleTimeoutMinutesMainApp?: InputMaybe<Scalars['Int']['input']>;
  idleTimeoutMinutesPortal?: InputMaybe<Scalars['Int']['input']>;
  /** Tenant-facing plural label for order-groups. Default `cases`. */
  orderGroupLabelPlural?: InputMaybe<Scalars['String']['input']>;
  /** Tenant-facing singular label for an order-group. Default `case`. */
  orderGroupLabelSingular?: InputMaybe<Scalars['String']['input']>;
  /**
   * Track 2 (2026-04-30) — set/clear the order-level intake
   * FormDefinition link. Pass a FormDefinition id to bind, an empty string
   * to clear, or omit to leave unchanged. The form must be `formType:
   * INTAKE` with no productTypeId; the resolver enforces both invariants.
   */
  orderIntakeFormDefinitionId?: InputMaybe<Scalars['ID']['input']>;
  organizationId: Scalars['ID']['input'];
  /**
   * Replace the bounded scanner-to-order-form mapping document. The server
   * validates every target against a LIVE ORDER_INTAKE form and rejects unknown
   * provider paths, incompatible field types and duplicate targets.
   */
  scannerIntakeFieldMappings?: InputMaybe<Scalars['AWSJSON']['input']>;
  securityPolicy?: InputMaybe<Scalars['AWSJSON']['input']>;
  /**
   * Round 5 — full replacement of the kanban status states. When provided
   * the tenant_settings.status_states_json column is overwritten with this
   * array verbatim; when omitted the existing column is preserved. Pair
   * with the Round 5c admin page (apps/main/src/admin/pages/StatusStatesPage).
   */
  statusStates?: InputMaybe<Array<StatusStateInput>>;
};

/**
 * Input for upsertVendorRoutingRule. Mirrors the vendor_routing_rules
 * DB columns. id present = update, id null = create.
 */
export type UpsertVendorRoutingRuleInput = {
  code: Scalars['String']['input'];
  conditions?: InputMaybe<Scalars['AWSJSON']['input']>;
  /**
   * ENG-1038 — target class. Omit or VENDOR = vendor route (destinationSiteId
   * must be null); SITE = internal-site route (destinationSiteId required).
   */
  destinationKind?: InputMaybe<RoutingDestinationKind>;
  /** ENG-1038 — target site (org_units.id) when destinationKind = SITE. */
  destinationSiteId?: InputMaybe<Scalars['ID']['input']>;
  eligibleFederationLinkIds: Array<Scalars['ID']['input']>;
  id?: InputMaybe<Scalars['ID']['input']>;
  isActive?: InputMaybe<Scalars['Boolean']['input']>;
  /** ENG-1037 — optional origin-site filter (org_units.id); null = any origin. */
  matchOriginSiteId?: InputMaybe<Scalars['ID']['input']>;
  name?: InputMaybe<Scalars['String']['input']>;
  organizationId: Scalars['ID']['input'];
  priority?: InputMaybe<Scalars['Int']['input']>;
  strategy: VendorSelectionStrategy;
  targetProductTypeId?: InputMaybe<Scalars['ID']['input']>;
  targetStepDefinitionId?: InputMaybe<Scalars['ID']['input']>;
};

/**
 * Input for upsertWorkLocation. `id` null = insert; non-null = update. `code`
 * is the stable snake_case reference used to bind steps / scanners to a location
 * (unique per tenant).
 */
export type UpsertWorkLocationInput = {
  code: Scalars['String']['input'];
  customFields?: InputMaybe<Scalars['AWSJSON']['input']>;
  description?: InputMaybe<Scalars['String']['input']>;
  id?: InputMaybe<Scalars['ID']['input']>;
  isActive?: InputMaybe<Scalars['Boolean']['input']>;
  name: Scalars['String']['input'];
  orgUnitId?: InputMaybe<Scalars['ID']['input']>;
  organizationId: Scalars['ID']['input'];
};

/**
 * Tenant user (internal staff, customer, service account, system).
 * Per docs/clearpath-architecture/03-data-model.md tier 1.
 */
export type User = {
  __typename?: 'User';
  /** Profile avatar URL. */
  avatarUrl?: Maybe<Scalars['AWSURL']['output']>;
  /**
   * ENG-3303 (AD-6) — a UI HINT ONLY: whether this viewer may target the
   * INDEPENDENT customer group in the Product customer-access surface (the pure
   * server Scope rule, catalog/entitlements/scope_rule.ts). Populated ONLY by
   * `Query.me`; null on every other User read, exactly like
   * `effectivePermissionKeys`. The client hides the independent-group toggle when
   * it is false/null (`useCanTargetIndependent` reads `?? false`, failing closed).
   * It is NOT an authorization control: the server refuses a scoped caller with
   * `scoped_caller` regardless of what the UI shows.
   */
  canTargetIndependentCustomers?: Maybe<Scalars['Boolean']['output']>;
  /** Cognito sub claim, if Cognito-backed. */
  cognitoSub?: Maybe<Scalars['String']['output']>;
  createdAt: Scalars['AWSDateTime']['output'];
  /** Custom-fields blob. */
  customFields?: Maybe<Scalars['AWSJSON']['output']>;
  /**
   * The viewer's flat, chain-resolved EFFECTIVE permission-key set — the union
   * across every held role of its inherited keys with each role's
   * `permission_overrides` applied (same computation as
   * `roleAssignments[].role.permissionKeys`, flattened). With one role per person
   * (ENG-3498) that is the one role and every role below it; only pre-0498
   * multi-role users the backfill could not collapse still union several.
   * Populated ONLY by `Query.me`; null on every other User read
   * (Query.user/users, mutation payloads) because those surfaces have no viewer
   * identity to compute it for.
   *
   * It is a PAGE-level gate (may this viewer open the page at all). It cannot say
   * whether the viewer may act on a particular order or step, because that also
   * depends on where the item is and on the viewer's scope: per-item action
   * buttons read Query.orderCapabilities / Query.stepRunCapabilities (ENG-3922).
   *
   * Exists so the client permission gate (`useCanI`) reads ONE list off the
   * `User:<id>` cache entity instead of iterating `roleAssignments[].role
   * .permissionKeys`. The Role entity is shared: `Query.roles`/`Query.role`
   * return each role's OWN (direct-only) keys onto the SAME normalized
   * `Role:<id>`, so fetching the role-editor list would otherwise overwrite the
   * viewer's effective keys in the Apollo cache and shrink their own UI
   * affordances until the next full reload (the admin-access flap). This field
   * lands on `User:<id>`, which no role-editor query touches, so it cannot be
   * poisoned by a Role read.
   */
  effectivePermissionKeys?: Maybe<Array<Scalars['String']['output']>>;
  /** Email address (unique per tenant). */
  email?: Maybe<Scalars['AWSEmail']['output']>;
  /** Display name. */
  fullName: Scalars['String']['output'];
  /**
   * The ExternalParty (`external_parties.id`, `users.home_party_id` —
   * migration 0031) this user is anchored to. Drives
   * `ctx.customerPartyId` / `ctx.partyScopeIds` (services/api/src/context.ts),
   * which order/file/billing scoping key off — required in practice for
   * CUSTOMER-type users; null for internal/service/system/vendor users, which
   * have no party scope.
   */
  homePartyId?: Maybe<Scalars['ID']['output']>;
  id: Scalars['ID']['output'];
  /** Whether the user is active (false = disabled). */
  isActive: Scalars['Boolean']['output'];
  /**
   * Whether this viewer's home party's `party_type` is configured as a
   * BILLING PRINCIPAL for their organization
   * (`external_party_type_definitions.billing_principal`, migration 0449).
   *
   * When true the server derives `clearpath.invoices.view`,
   * `clearpath.billing.read` and `clearpath.payment_methods.manage` onto the
   * caller's billing requests, so a practitioner may see and pay their OWN
   * invoices. It grants nothing about WHICH rows they reach — party scope is
   * unchanged, and every billing predicate still fences on the caller's own
   * party.
   *
   * Populated only by `Query.me` (it is the viewer's own answer); false on every
   * other User read. Clients must treat this as the server's answer and never
   * derive it from `portalRole` — see apps/portal/src/org-scope/capabilities.ts.
   */
  isBillingPrincipal: Scalars['Boolean']['output'];
  /** Last successful sign-in timestamp. */
  lastSignedInAt?: Maybe<Scalars['AWSDateTime']['output']>;
  /** ISO locale (e.g. en-US, fr-CA). */
  locale?: Maybe<Scalars['String']['output']>;
  /** Owning Organization. */
  organizationId?: Maybe<Scalars['ID']['output']>;
  /** Optional phone. */
  phone?: Maybe<Scalars['AWSPhone']['output']>;
  /**
   * Portal-customer role (dso_admin / clinic_admin / doctor / clinic_staff
   * in the DB `portal_role_enum`). Null for internal/service/system users.
   * This is the authoritative role for Cedar + RLS scoping
   * (`current_portal_role()`, db/rls/0001_rls_baseline.sql) — distinct from
   * the tenant RBAC `roleAssignments` above. (ENG-55)
   */
  portalRole?: Maybe<PortalRole>;
  /**
   * All role assignments held by this user: ONE role (ENG-3498), one row per
   * place it applies — a row per department or team, or a single whole-lab row
   * (orgUnitId null). See UserRole for what each kind of row grants.
   */
  roleAssignments: Array<UserRole>;
  /**
   * Tenant the user belongs to. Null for a platform-admin / control-plane
   * identity (molaris-admin / molaris-account audience) — those callers are
   * tenant-less by design (ENG-82); every other user always has one.
   */
  tenantId?: Maybe<Scalars['ID']['output']>;
  /** IANA timezone (e.g. America/Los_Angeles). */
  timezone?: Maybe<Scalars['String']['output']>;
  updatedAt?: Maybe<Scalars['AWSDateTime']['output']>;
  /** Discriminator: internal, customer, service, system. */
  userType: UserType;
};

export type UserCertification = {
  __typename?: 'UserCertification';
  /** Catalog reference (ENG-148). name/issuer are derived from it. */
  certificationDefinitionId: Scalars['ID']['output'];
  earnedDate?: Maybe<Scalars['AWSDate']['output']>;
  expiringSoon: Scalars['Boolean']['output'];
  expiryDate?: Maybe<Scalars['AWSDate']['output']>;
  id: Scalars['ID']['output'];
  issuer: Scalars['String']['output'];
  name: Scalars['String']['output'];
  userId: Scalars['ID']['output'];
};

export type UserCertificationDeletedPayload = {
  __typename?: 'UserCertificationDeletedPayload';
  deletedId: Scalars['ID']['output'];
};

export type UserCertificationPayload = {
  __typename?: 'UserCertificationPayload';
  certification: UserCertification;
};

export type UserConnection = {
  __typename?: 'UserConnection';
  edges: Array<UserEdge>;
  pageInfo: PageInfo;
  totalCount: Scalars['Int']['output'];
};

/** Mutation result for deleteUser (ENG-679). */
export type UserDeletedPayload = {
  __typename?: 'UserDeletedPayload';
  deletedId: Scalars['ID']['output'];
};

export type UserEdge = {
  __typename?: 'UserEdge';
  cursor: Scalars['String']['output'];
  node: User;
};

/** Filter for users. */
export type UserFilterInput = {
  /**
   * Resolve a KNOWN set of users by id. Added for the role audit log, which has
   * at most ~100 distinct user ids on screen and previously had to page the whole
   * directory to label them — silently missing anyone outside the first page.
   * Bounded by the same page clamp as any other users() call.
   */
  ids?: InputMaybe<Array<Scalars['ID']['input']>>;
  isActive?: InputMaybe<Scalars['Boolean']['input']>;
  orgUnitIds?: InputMaybe<Array<Scalars['ID']['input']>>;
  roleIds?: InputMaybe<Array<Scalars['ID']['input']>>;
  search?: InputMaybe<Scalars['String']['input']>;
  userType?: InputMaybe<Array<UserType>>;
};

/**
 * User mutation payload. `tempPassword` is populated ONLY by inviteUser for
 * CUSTOMER-type invites (ENG-customer-invite-membership) — Cognito never
 * echoes an auto-generated TemporaryPassword back in its AdminCreateUser
 * response, so the resolver generates its own and returns it here as a
 * fallback hand-off channel alongside the normal invite email (mirrors
 * InviteStaffUserPayload.tempPassword). Null for createUser, for non-CUSTOMER
 * invites, and for updateUser/updateMe.
 */
export type UserPayload = {
  __typename?: 'UserPayload';
  tempPassword?: Maybe<Scalars['String']['output']>;
  user: User;
};

/**
 * User ↔ role ↔ org_unit scoped assignment.
 * Per docs/clearpath-architecture/03-data-model.md tier 1 (`user_roles`).
 *
 * THE RULES (ENG-3919 decision 2026-10-21; recorded by ENG-3925 in
 * docs/project-notes/ENG-3925-one-role-scope-membership-2026-10-23.md):
 *
 *   * ONE ROLE PER PERSON (ENG-3498). Every row a user holds names the same
 *     role; rows differ only in where it applies. A second, different role is
 *     refused anywhere — by assignRole (reason role_change_not_allowed), by the
 *     invites (reason multiple_roles) and by the database
 *     (trg_user_roles_single_role, db/migrations/0498). The role is changed only
 *     with changeUserRole, which moves every row at once.
 *   * WHERE IT APPLIES: either the WHOLE LAB (one row, orgUnitId null) or the
 *     departments and teams the person is placed in (one row per unit). For
 *     access, a unit row covers that unit and every unit below it
 *     (org_unit_closure); a whole-lab row covers everything. Whole lab is an
 *     explicit choice: a staff row with no unit needs `wholeLab: true`
 *     (ENG-3923, see AssignRoleInput).
 *   * MEMBERSHIP: only unit rows place someone in a department
 *     (org_unit_memberships(), db/migrations/0574, ENG-3920) — in that unit, the
 *     units below it and the units above it. A whole-lab row is authority, not
 *     membership: it places nobody, so a whole-lab person is never an assignment
 *     candidate, never auto-assigned and cannot take department work by
 *     membership (see assignStepRun).
 *   * THE STAFF BASELINE (ENG-3922, db/rls/0176) does not depend on these rows:
 *     every staff session reads every order in its organization, and commenting
 *     on one needs clearpath.comments.create but no org-unit scope. It is off
 *     for account managers (they keep to their book) and for ClearPath Light
 *     sessions. What someone may DO to an order or step stays scoped by these
 *     rows; the per-item answer is Query.orderCapabilities /
 *     Query.stepRunCapabilities.
 *
 * The server does not refuse a person holding a whole-lab row AND unit rows at
 * once: such rows exist from before ENG-3923, and assignRole does not refuse
 * adding one kind to the other. While both exist, the whole-lab row decides
 * access (rbac-engine resolveUserRoles: any null row means unrestricted) and the
 * unit rows still count as membership. The Roles page resolves it to one choice
 * (runbook docs/runbooks/staff-without-department.md).
 */
export type UserRole = {
  __typename?: 'UserRole';
  expiresAt?: Maybe<Scalars['AWSDateTime']['output']>;
  grantedAt: Scalars['AWSDateTime']['output'];
  grantedByUserId?: Maybe<Scalars['ID']['output']>;
  id: Scalars['ID']['output'];
  isActive: Scalars['Boolean']['output'];
  orgUnit?: Maybe<OrgUnit>;
  /**
   * Where the role applies: this org unit and every unit below it. Null =
   * the whole lab (authority everywhere, membership nowhere). The server accepts
   * any org unit here (department, team, plant, site).
   */
  orgUnitId?: Maybe<Scalars['ID']['output']>;
  role?: Maybe<Role>;
  roleId: Scalars['ID']['output'];
  user?: Maybe<User>;
  userId: Scalars['ID']['output'];
};

/** Role assignment mutation payload. */
export type UserRolePayload = {
  __typename?: 'UserRolePayload';
  userRole: UserRole;
};

/**
 * Round 5 — one scope a user has visibility for. Drives the topbar
 * scope picker.
 */
export type UserScope = {
  __typename?: 'UserScope';
  cardCount: Scalars['Int']['output'];
  id: Scalars['ID']['output'];
  kind: DashboardScopeKind;
  label: Scalars['String']['output'];
};

/**
 * Active session row from the control-plane `sessions` table. The
 * `isCurrent` flag is derived from the caller's molaris.session_id claim
 * so the UI can grey-out the row the user is signing from.
 */
export type UserSession = {
  __typename?: 'UserSession';
  /**
   * Browser brand parsed from userAgent (e.g. "Chrome", "Safari"). Null when
   * the UA is absent or unrecognized.
   */
  browser?: Maybe<Scalars['String']['output']>;
  createdAt: Scalars['AWSDateTime']['output'];
  /**
   * Form factor parsed from userAgent: "desktop" | "mobile" | "tablet". Null
   * when the UA is absent.
   */
  deviceType?: Maybe<Scalars['String']['output']>;
  id: Scalars['ID']['output'];
  ipOrigin?: Maybe<Scalars['String']['output']>;
  isCurrent: Scalars['Boolean']['output'];
  lastActiveAt: Scalars['AWSDateTime']['output'];
  mfaMethod?: Maybe<Scalars['String']['output']>;
  mfaVerifiedAt?: Maybe<Scalars['AWSDateTime']['output']>;
  /**
   * Operating system parsed from userAgent (e.g. "macOS", "iOS", "Windows
   * 10/11"). Null when unknown.
   */
  os?: Maybe<Scalars['String']['output']>;
  revokedAt?: Maybe<Scalars['AWSDateTime']['output']>;
  /** Same as id — retained for UI compat with the legacy REST shape. */
  sessionId: Scalars['ID']['output'];
  /**
   * Raw User-Agent captured at session creation (Wave 5). Null for sessions
   * created before the capture landed and for non-browser (api) sessions.
   */
  userAgent?: Maybe<Scalars['String']['output']>;
  /**
   * Coarse classifier (web/mobile/api). Kept for back-compat; prefer the
   * parsed browser/os/deviceType below for distinguishing sessions.
   */
  userAgentClass?: Maybe<Scalars['String']['output']>;
};

export type UserSkill = {
  __typename?: 'UserSkill';
  certifiedOn?: Maybe<Scalars['AWSDateTime']['output']>;
  id: Scalars['ID']['output'];
  lastAssessed?: Maybe<Scalars['AWSDateTime']['output']>;
  level: Scalars['String']['output'];
  skillCategory?: Maybe<Scalars['String']['output']>;
  skillDefinitionId: Scalars['ID']['output'];
  skillName?: Maybe<Scalars['String']['output']>;
  userId: Scalars['ID']['output'];
};

export type UserSkillDeletedPayload = {
  __typename?: 'UserSkillDeletedPayload';
  deletedId: Scalars['ID']['output'];
};

export type UserSkillPayload = {
  __typename?: 'UserSkillPayload';
  userSkill: UserSkill;
};

/** User category. */
export enum UserType {
  Customer = 'CUSTOMER',
  Internal = 'INTERNAL',
  Service = 'SERVICE',
  System = 'SYSTEM',
  Vendor = 'VENDOR'
}

export type ValidateAndExplainInput = {
  errors: Array<ValidationErrorInput>;
  yaml: Scalars['String']['input'];
};

export type ValidateAndExplainResult = {
  __typename?: 'ValidateAndExplainResult';
  explanation: Scalars['String']['output'];
  suggestedFix?: Maybe<Scalars['String']['output']>;
  tokensUsed: TokenUsage;
};

/**
 * Input for the validateIntake dry-run (form-rules). Product lines reuse
 * SubmitOrderProductInput. customerPartyId selects the same effective clinic
 * order form submitOrder will enforce (direct, inherited, then tenant default).
 */
export type ValidateIntakeInput = {
  customerPartyId?: InputMaybe<Scalars['ID']['input']>;
  /** Order-level intake answers (same shape as SubmitOrderInput.intakeFormResponse). */
  intakeFormResponse?: InputMaybe<Scalars['AWSJSON']['input']>;
  organizationId: Scalars['ID']['input'];
  products: Array<SubmitOrderProductInput>;
};

/** Output type used by validateTenantConfig errors[]. */
export type ValidationError = {
  __typename?: 'ValidationError';
  keyword?: Maybe<Scalars['String']['output']>;
  line?: Maybe<Scalars['Int']['output']>;
  message: Scalars['String']['output'];
  path: Scalars['String']['output'];
};

export type ValidationErrorInput = {
  keyword?: InputMaybe<Scalars['String']['input']>;
  line?: InputMaybe<Scalars['Int']['input']>;
  message: Scalars['String']['input'];
  path: Scalars['String']['input'];
};

export type ValidationResult = {
  __typename?: 'ValidationResult';
  errors: Array<ValidationError>;
  valid: Scalars['Boolean']['output'];
};

/**
 * Selection logic for dynamic vendor assignment.
 * Per docs/clearpath-architecture/03-data-model.md tier 6 (`vendor_routing_rules`).
 */
export type VendorRoutingRule = {
  __typename?: 'VendorRoutingRule';
  /** Stable identifier within the organization (DB column code). */
  code: Scalars['String']['output'];
  /** Conditional logic (JSONB). */
  conditions?: Maybe<Scalars['AWSJSON']['output']>;
  createdAt: Scalars['AWSDateTime']['output'];
  /**
   * ENG-1037/ENG-1038 — target class this rule dispatches to: an internal
   * SITE (org_units.unit_type='site') or an external VENDOR (federation_link).
   * Defaults to VENDOR (every pre-1037 rule is a vendor route).
   */
  destinationKind: RoutingDestinationKind;
  /** Resolved target site (name/code), when destinationKind = SITE. */
  destinationSite?: Maybe<Site>;
  /**
   * ENG-1038 — target site (org_units.id) when destinationKind = SITE; null
   * for vendor routes.
   */
  destinationSiteId?: Maybe<Scalars['ID']['output']>;
  /** Eligibility criteria (JSONB). */
  eligibility?: Maybe<Scalars['AWSJSON']['output']>;
  /** Federation links this rule may dispatch to. */
  eligibleFederationLinkIds: Array<Scalars['ID']['output']>;
  id: Scalars['ID']['output'];
  isActive: Scalars['Boolean']['output'];
  /** Resolved origin-filter site (name/code), when matchOriginSiteId is set. */
  matchOriginSite?: Maybe<Site>;
  /**
   * ENG-1037 — optional origin-site filter: when set, the rule matches only
   * work originating at this site. Null matches any origin.
   */
  matchOriginSiteId?: Maybe<Scalars['ID']['output']>;
  name?: Maybe<Scalars['String']['output']>;
  organizationId: Scalars['ID']['output'];
  /** Evaluation order — lower numbers win. */
  priority: Scalars['Int']['output'];
  productType?: Maybe<ProductType>;
  /** Legacy alias for strategy. */
  selectionStrategy: VendorSelectionStrategy;
  stepDefinition?: Maybe<PipelineStep>;
  /** Selection strategy (DB column strategy). */
  strategy: VendorSelectionStrategy;
  /** Strategy-specific config (JSONB). */
  strategyConfig?: Maybe<Scalars['AWSJSON']['output']>;
  /** Product type this rule applies to (FK target_product_type_id). */
  targetProductTypeId?: Maybe<Scalars['ID']['output']>;
  /** Pipeline step this rule applies to (FK target_step_definition_id). */
  targetStepDefinitionId?: Maybe<Scalars['ID']['output']>;
  tenantId: Scalars['ID']['output'];
  updatedAt?: Maybe<Scalars['AWSDateTime']['output']>;
};

export type VendorRoutingRulePayload = {
  __typename?: 'VendorRoutingRulePayload';
  vendorRoutingRule: VendorRoutingRule;
};

/** Vendor selection strategy. */
export enum VendorSelectionStrategy {
  CustomerPreference = 'CUSTOMER_PREFERENCE',
  LoadBased = 'LOAD_BASED',
  ManualOnly = 'MANUAL_ONLY',
  RoundRobin = 'ROUND_ROBIN',
  SpecialtyBased = 'SPECIALTY_BASED',
  Static = 'STATIC'
}

/** Input for voidBillingAdjustment. MOL-620. */
export type VoidBillingAdjustmentInput = {
  adjustmentId: Scalars['ID']['input'];
  reason: Scalars['String']['input'];
};

/** Input for voidInvoice. */
export type VoidInvoiceInput = {
  invoiceId: Scalars['ID']['input'];
  reason: Scalars['String']['input'];
};

/** Result of voiding a label — true when the carrier accepted the refund. */
export type VoidShippingLabelResult = {
  __typename?: 'VoidShippingLabelResult';
  refunded: Scalars['Boolean']['output'];
};

/** Line-item input for requestWarrantyReturn. */
export type WarrantyProductLineInput = {
  /** Free-form notes about the defect / return reason for this specific line. */
  notes?: InputMaybe<Scalars['String']['input']>;
  /** productType id from the source order. Quantity copies from the source line if omitted. */
  productTypeId: Scalars['ID']['input'];
  /** Override quantity (defaults to the source line quantity). */
  quantity?: InputMaybe<Scalars['Int']['input']>;
};

/**
 * Outbound webhook registration.
 * Per docs/clearpath-architecture/03-data-model.md tier 6 (`webhooks`).
 */
export type Webhook = {
  __typename?: 'Webhook';
  createdAt: Scalars['AWSDateTime']['output'];
  /** Event types this webhook subscribes to. */
  eventTypes: Array<Scalars['String']['output']>;
  id: Scalars['ID']['output'];
  /** Active state. */
  isActive: Scalars['Boolean']['output'];
  /** Last successful delivery. */
  lastDeliveredAt?: Maybe<Scalars['AWSDateTime']['output']>;
  /** Most recent error message. */
  lastErrorMessage?: Maybe<Scalars['String']['output']>;
  organizationId: Scalars['ID']['output'];
  tenantId: Scalars['ID']['output'];
  updatedAt?: Maybe<Scalars['AWSDateTime']['output']>;
  url: Scalars['AWSURL']['output'];
};

/**
 * P1 #53 — one message peeked from the webhook DLQ. Read-only — calling
 * the query does NOT remove the message; only replayWebhookDlq drains
 * the queue.
 */
export type WebhookDlqMessage = {
  __typename?: 'WebhookDlqMessage';
  /**
   * Receive-count from SQS metadata (how many times the dispatcher has
   * re-tried the message before it landed in DLQ).
   */
  approximateReceiveCount?: Maybe<Scalars['Int']['output']>;
  /**
   * SQS message body — the original DispatchJob JSON. Bounded to 256 KiB
   * by SQS; the UI truncates display to 4 KiB.
   */
  body: Scalars['AWSJSON']['output'];
  /** Event type, if extractable. */
  eventType?: Maybe<Scalars['String']['output']>;
  messageId: Scalars['String']['output'];
  /** Webhook ID the message targets, if extractable from the body. */
  webhookId?: Maybe<Scalars['String']['output']>;
};

/** P1 #53 — return shape for replayWebhookDlq. */
export type WebhookDlqReplayResult = {
  __typename?: 'WebhookDlqReplayResult';
  /** Number of messages re-enqueued onto the dispatch queue. */
  replayed: Scalars['Int']['output'];
};

/** Minimal invalidation only. Clients refetch authoritative authorized work queries. */
export enum WorkInvalidationLane {
  Assign = 'ASSIGN',
  Department = 'DEPARTMENT',
  MyWork = 'MY_WORK'
}

export type WorkInvalidationPlan = {
  __typename?: 'WorkInvalidationPlan';
  lane: WorkInvalidationLane;
  leaseExpiresAtEpochMs: Scalars['Float']['output'];
  shards: Array<WorkInvalidationShard>;
};

export type WorkInvalidationShard = {
  __typename?: 'WorkInvalidationShard';
  routes: Array<Scalars['String']['output']>;
};

export type WorkInvalidationSignal = {
  __typename?: 'WorkInvalidationSignal';
  lane: WorkInvalidationLane;
  organizationId: Scalars['ID']['output'];
  publishedAtEpochMs: Scalars['Float']['output'];
  signalId: Scalars['ID']['output'];
  tenantId: Scalars['ID']['output'];
  trustedRoutes: Array<Scalars['String']['output']>;
};

/**
 * Reusable station / location in a tenant's work-location registry (ENG-589).
 * The physical place — bench, mill, QC bay, scanner post — where a step is
 * performed / a scan is captured / an operator works. Org-scoped master data,
 * optionally nested under an org_unit. Backed by `work_locations`
 * (db/migrations/0264, RLS db/rls/0059).
 */
export type WorkLocation = {
  __typename?: 'WorkLocation';
  /** Stable snake_case reference, unique per organization. */
  code: Scalars['String']['output'];
  createdAt: Scalars['AWSDateTime']['output'];
  customFields?: Maybe<Scalars['AWSJSON']['output']>;
  description?: Maybe<Scalars['String']['output']>;
  id: Scalars['ID']['output'];
  /** Inactive locations are retained but hidden from new-work pickers. */
  isActive: Scalars['Boolean']['output'];
  name: Scalars['String']['output'];
  /** Optional org_unit (department/team) this location belongs to. */
  orgUnitId?: Maybe<Scalars['ID']['output']>;
  organizationId: Scalars['ID']['output'];
  tenantId: Scalars['ID']['output'];
  updatedAt?: Maybe<Scalars['AWSDateTime']['output']>;
};

/** Mutation result for deleteWorkLocation. */
export type WorkLocationDeletedPayload = {
  __typename?: 'WorkLocationDeletedPayload';
  deletedId: Scalars['ID']['output'];
};

/** Mutation result for upsertWorkLocation. */
export type WorkLocationPayload = {
  __typename?: 'WorkLocationPayload';
  workLocation: WorkLocation;
};

export type YamlDiffBlock = {
  __typename?: 'YamlDiffBlock';
  action: Scalars['String']['output'];
  description: Scalars['String']['output'];
  path: Scalars['String']['output'];
  yamlContent: Scalars['String']['output'];
};
